// DispatchDeck - Roster Module
// Manage people, trucks, and trailers

import {
  db,
  auth,
  storage,
  ref,
  uploadBytes,
  getDownloadURL,
  collection,
  doc,
  getDocs,
  getDoc,
  setDoc,
  addDoc,
  updateDoc,
  deleteDoc,
  writeBatch,
  query,
  orderBy,
  where,
  onSnapshot,
  arrayRemove,
  arrayUnion,
  Timestamp,
  increment,
  deleteObject,
  functions,
  httpsCallable
} from '../firebase-config.js?v=1868';
import { runGuardedSave, withWriteTimeout } from '../saveGuard.js?v=2';

import { processPhoto } from '../utils/imageProcessor.js?v=1955';
// TASK-9378 — HEIC/HEIF → JPEG before preview + upload (lazy-loads the vendored converter).
import { normalizePhotoForUpload, sniffFileKind, canBrowserDecode } from '../utils/heicConvert.js?v=3';
// TASK-1141 — per-unit equipment notes thread. Imported ONCE here (app.js
// static graph) so `window.EquipmentNotes` exists before any handler runs;
// maintenance.js reads it off window at call time (Pitfall #1 — never a
// cross-module ?v= import).
import '../equipmentNotes.js?v=1511703';
// TASK-9267 — Permit Readiness quick fill: the pure parse/validate rules live on
// window.PermitQuickFill. Imported ONLY here (Pitfall #1 — one importer).
import '../utils/permitQuickFill.js?v=1';
// TASK-9844 — the crew-skills rule (`window.CrewSkills`), a GENERATED side-effect
// module (scripts/build-shared.cjs). dispatch.js imports the SAME url — keep the
// two `?v=` identical or they load twice (Pitfall #7).
import '../shared/crew-skills.js?v=9911004';
// TASK-9715 — the ONE truck / trailer spec ranges (GENERATED from
// functions/src/shared/vehicleSpecRanges.js). The Claude connector refuses a
// year outside the same bounds these modals warn on. Only roster.js imports it.
import { VEHICLE_SPEC_RANGES } from '../shared/vehicle-spec-ranges.js?v=9715';
// TASK-9375 S2 — the ONE asset stock / per-yard rollup math (GENERATED from
// functions/src/shared/assetRollup.js; the organization rollup runs the same
// file, so Group-by-branch and the Organization view cannot disagree).
// roster.js is its ONLY browser importer — keep it that way (Pitfall #1).
import { assetStockIsNegative, rentalStockView, computeAssetBranchRollups, _assetTransferableQty, findYardTransferTarget } from '../shared/asset-rollup.js?v=9375002';
// TASK-9935 — "Doesn't expire" heavy-unit confirm (window.RegNoExpiryGuard).
// roster.js is the ONE main-app importer (Pitfall #1); compliance.js reads the
// window global at call time.
import '../regNoExpiryGuard.js?v=9935002';
// TASK-9267 COD (Code Reviewer) — the quick-fill stale guard must be atomic with
// its write. runTransaction is not re-exported from firebase-config.js; same
// SDK URL/version, same pattern as app.js / ap.js.
import { runTransaction, serverTimestamp } from 'https://www.gstatic.com/firebasejs/10.8.0/firebase-firestore.js';

// ============================================================================
// TASK-512 — "Permit Pete" OS/OW permit platform — DATA CONTRACT (Slice 0)
// ----------------------------------------------------------------------------
// No behavior here. This documents the Firestore doc shapes that Slice 1
// (Permit Pete chat + askPermitPete fn) and Slice 2 (Brian's desk) will read
// and write, so they're built against one agreed contract. The collections
// are rules-gated in firestore.rules (tenants/{tid}/permitRequests,
// /jurisdictions, /equipmentTemplates). The OS/OW vehicle dimension fields
// added to the truck/trailer modals below (length/width/height/axleCount/
// axleSpacing) are what Pete pulls from the fleet to prefill a request.
//
// ── permitRequest doc: tenants/{tid}/permitRequests/{id} ──
// {
//   // who/what
//   carrier:        { name, dotNumber, contactName, contactPhone, contactEmail },
//   unitNumber:     string,            // truck unit pulled from / typed against the fleet
//   truckId:        string|null,       // ref to tenants/{tid}/trucks/{id}, server-re-validated (never trust AI/client id)
//   trailerId:      string|null,       // ref to tenants/{tid}/trailers/{id}, server-re-validated
//   // the move
//   travel: {
//     date:             string,        // YYYY-MM-DD, travel/start date
//     origin:           string,
//     dest:             string,
//     routeDescription: string,        // ⚠️ REQUIRED — ND OS/OW permits are issued PER APPROVED ROUTE,
//                                       //    not just origin/dest. Pete MUST explicitly ask; NEVER default null.
//                                       //    (Enforced in Slice 1.)
//     loadDesc:         string,        // what's being hauled (e.g. "frac sand silo")
//     notes:            string,
//   },
//   // dimensions — overall + load + overhang (decimal feet)
//   dims: {
//     overallL: number, overallW: number, overallH: number,  // full rig as configured for this move
//     trailerLen: number,                                     // deck length
//     loadL: number, loadW: number, loadH: number,            // the cargo itself
//     frontOverhang: number, rearOverhang: number,            // load beyond the deck
//     stretch: number,                                        // extendable-trailer stretch length
//     selfPropelled: boolean,                                 // self-propelled unit (e.g. crane) vs hauled load
//     nonDivisible: boolean,    // ⚠️ HUMAN-ONLY — legal attestation under 23 CFR 658.5 ("this load cannot
//                               //    be reduced/disassembled w/o…"). NEVER AI-populated; carrier checkbox only.
//                               //    AI-setting it = false attestation on a state filing. (Enforced Slice 1.)
//     bridgeSpacing: number,    // ⚠️ HUMAN-ONLY — Bridge Formula input (23 USC 127). Must come from the
//                               //    equipment's actual axle config (the net-new axleSpacing field below)
//                               //    or be left blank + flagged "Required — Brian to verify". NEVER AI-guessed.
//                               //    (Enforced Slice 1.)
//   },
//   permitAxles: [ { tires, tireWidth, trackWidth, spacingToNext, groupBreakAfter } ],
//                                       // ⚠️ TASK-525: per-axle PERMIT config, SERVER-RE-FETCHED from the
//                                       //    validated truckId/trailerId at submit — NEVER trusted from the
//                                       //    client packet. This is the assembled continuous axle sequence the
//                                       //    desk files. Per-axle WEIGHT is intentionally absent here: it's
//                                       //    rated capacity (stable, on the vehicle) vs actual LOADED (move-
//                                       //    specific), and loaded weight is collected per-request, not stored.
//                                       //    (Replaces the old `axleConfig:[{position,weight,spacingToNext}]`.)
//   // workflow
//   status:      'NEW' | 'IN_PROGRESS' | 'ISSUED' | 'DELIVERED',
//   assignedTo:  string|null,          // permit creator (Brian) handling it
//   jurisdictionId: string|null,       // ref to tenants/{tid}/jurisdictions/{id} (see below) — drives required-field set
//   // provenance — EVERY substantive field carries a source tag so Brian can see at a glance what to double-check.
//   // Human-verify alone is necessary but NOT sufficient without these tags.
//   sources:     { '<fieldPath>': 'truck-record' | 'carrier-stated' | 'AI-inferred', ... },
//   confidence:  { '<fieldPath>': number, ... },              // AI-inferred field confidence (0–1)
//   createdAt:   Timestamp, createdBy: string,
// }
// Pete DRAFTS only — never finalizes/auto-submits to a state. "Mark ready to file" (Slice 2)
// hard-blocks while any AI-inferred field is unconfirmed. Do NOT log the assembled packet (PII).
//
// ── jurisdiction doc: tenants/{tid}/jurisdictions/{id} ── (🌎 #1 national-scale seam)
// Jurisdictions are DATA, never hardcoded. Adding a state/county later = adding a row, not recoding.
// ZERO "ND" (or any state) literals belong in permit LOGIC — thresholds live here.
// {
//   level:       'state' | 'county' | 'township' | 'city',
//   state:       string,               // e.g. "ND" — a DATA value on the row, never a code branch
//   county:      string|null,
//   name:        string,               // display name, e.g. "North Dakota DOT OS/OW"
//   portal:      { url, loginNotes },   // where/how the permit is filed
//   fees:        [ { label, amount, basis } ],
//   rules:       string,               // the playbook prose: limits, escort thresholds, curfews, restrictions
//   contact:     { name, phone, email, hours },
//   quirks:      string,               // gotchas a permit creator must know
//   // Pete's required-field set is DRIVEN by this row, not a fixed schema — keep permitRequest extensible
//   // so a state's extra fields don't break the model. "Add a state" = teach Pete data, not code.
//   requiredFields: [ string ],
// }
//
// ── equipmentTemplate doc: tenants/{tid}/equipmentTemplates/{id} ── (BUILT: TASK-1443)
// Saved reusable rig configs ("15 + 104 + booster") for the "2-min reorder."
// Mirrors the dims/permitAxles shape above so Pete can prefill from a template.
// ⚠️ AXLE-OWNERSHIP (23 USC 127 / 49 CFR 658.17): the tractor↔trailer COUPLING gap
//    (last drive axle → first trailer axle) is a property of the COMBINATION, not
//    either vehicle. It lives HERE (the rig combo), computed at assembly time —
//    NEVER on truck.permitAxles or trailer.permitAxles (those hold each unit's OWN
//    internal axles only). A coupling gap baked onto a vehicle = wrong Bridge Formula
//    inputs the moment that tractor pulls a different trailer.
// {
//   name:        string,                 // "15 + 104 + booster"
//   kind:        string,                 // slug from RIG_KINDS (data-ish; add a slug, not a code branch)
//   truckId:     string,                 // tenants/{tid}/trucks/{id} — the power unit
//   trailerIds:  [ string ],             // ORDERED, front→rear. ARRAY, never a single id:
//                                        //   TITS runs trailer+booster combos (104 / 104A / 104B) — that's
//                                        //   what produces the 9-axle configs. Length N ⇒ N couplings.
//   // ── the whole point of the task: one entry per JOINT, front→rear ──
//   couplings: [ {
//     fromType, fromId, toType, toId,    // which two units this joint is between
//     gap:    number|null,               // as-configured distance, last axle of `from` → first axle of `to` (ft)
//     gapMin: number|null,               // sliding fifth wheel / sliding tandems: the ACHIEVABLE range.
//     gapMax: number|null,               //   A single fixed number silently lies on half the moves (Sean's point).
//   } ],
//   // ── scalar mirrors of couplings[0] — THE FIFTH WHEEL JOINT, AND ONLY THAT ──
//   // These three are the tractor↔first-trailer fifth-wheel setting, mirrored out of
//   // couplings[0] for a reader that wants just that one number. They are NOT "the
//   // rig's gap." A rig has ONE GAP PER JOINT (N trailers ⇒ N couplings): on a
//   // tractor + trailer + booster, couplings[0] is the fifth wheel and couplings[1]
//   // is the trailer↔booster joint, which these fields cannot express at all.
//   // 🔴 A CONSUMER THAT WANTS THE WHOLE RIG MUST READ couplings[] (or the assembled
//   //    permitAxles[] below, where every inter-unit gap is tagged isCouplingGap).
//   //    Putting one joint of N onto a permit filing is a wrong axle-spacing
//   //    declaration — permit-revocation / weigh-station-citation surface, not a
//   //    cosmetic bug. Named `fifthWheelGap*` (NOT `couplingGap*`) precisely so the
//   //    field name cannot invite that misread; the old scalar names were renamed
//   //    before TASK-1445's server reader was built against them, while the
//   //    contract still had zero readers and zero stored documents.
//   fifthWheelGap: number|null, fifthWheelGapMin: number|null, fifthWheelGapMax: number|null,
//
//   permitAxles: [ { tires, tireWidth, trackWidth, spacingToNext, groupBreakAfter,
//                    // + provenance (additive; the 5 keys above are the canonical contract):
//                    unitType, unitId, unitLabel, axleIndexOnUnit,
//                    isCouplingGap, spacingToNextMin, spacingToNextMax } ],
//        // ⚠️ DERIVED — the continuous assembled sequence: truck.permitAxles → couplings[0].gap →
//        //    trailer[0].permitAxles → couplings[1].gap → trailer[1].permitAxles → … Recomputed from the
//        //    SOURCE VEHICLES on every save (never hand-edited, never allowed to drift).
//        //    `assembly.fingerprint` lets a reader detect that a source vehicle changed after
//        //    the last save; the UI shows a "source changed — re-save" badge when it differs.
//        //    Server-side consumers (Pete / the desk) MUST still re-derive from the validated
//        //    vehicle ids — this array is a client-computed convenience cache, not an authority.
//   assembly: { derivedBy:'client', fingerprint, truckId, trailerIds, computedAt,
//               axleCount, extremeSpacing, extremeSpacingMin, extremeSpacingMax },
//        // ⚠️ `incomplete` / `warnings` / `permitAxles` are CLIENT-computed. Rules gate
//        //    WHO writes this doc, never WHAT the numbers say — a roster-module member
//        //    with the raw SDK can write incomplete:false over a doctored sequence.
//        //    Treat `incomplete:true` as authoritative (fail closed) and `false` as
//        //    unverified until a server re-derive agrees. Never file off this doc alone.
//        // ⚠️ extremeSpacing = first→last axle distance, COMPUTED for display/sanity only.
//        //    Deliberately NOT written as `bridgeSpacing` — that field is a HUMAN attestation
//        //    (23 USC 127) and must never be silently populated by a derived number.
//   incomplete: boolean, warnings: [ string ],   // missing axle tables / missing gaps — downstream "ready to file" gate
//   dims: { overallL, overallW, overallH },      // as-configured EMPTY rig, hand-entered (never summed — fifth-wheel
//                                                //   overlap makes a computed length a fabricated regulatory number)
//   notes: string, createdAt, createdBy, updatedAt, updatedBy,
// }
// ============================================================================

// ============================================
// DEFAULT TYPE CONSTANTS
// ============================================
const DEFAULT_TRUCK_TYPES = [
  { slug: 'semi', label: 'Semi' },
  { slug: 'vac', label: 'Vac Truck' },
  { slug: 'truck', label: 'Truck' },
  { slug: 'van', label: 'Van' },
  { slug: 'pickup', label: 'Pickup' },
  { slug: 'other', label: 'Other Vehicle' }
];
const DEFAULT_TRAILER_TYPES = [
  { slug: 'flatbed', label: 'Flatbed' },
  { slug: 'enclosed', label: 'Enclosed' },
  { slug: 'utility', label: 'Utility' },
  { slug: 'other', label: 'Other' }
];
const DEFAULT_ASSET_CATEGORIES = [
  { slug: 'equipment', label: 'Equipment' },
  { slug: 'tool', label: 'Tool' },
  { slug: 'other', label: 'Other' }
];
// TASK-1443 — rig "kind" options. Kept as a plain list (not a tenant custom-type
// collection) because a rig kind is descriptive only — nothing branches on it.
const RIG_KINDS = [
  { slug: 'tractor-trailer', label: 'Tractor + Trailer' },
  { slug: 'heavy-haul', label: 'Heavy Haul' },
  { slug: 'frac', label: 'Frac' },
  { slug: 'workover', label: 'Workover Rig' },
  { slug: 'coil-tubing', label: 'Coil Tubing' },
  { slug: 'other', label: 'Other' }
];
// Sanity ceilings for the coupling gap (ft). Not a legal limit — a typo guard.
const RIG_GAP_MAX_FT = 200;
const RIG_MAX_TRAILERS = 10;

const DEFAULT_CREW_TYPES = [
  { slug: 'owner', label: 'Owner' },
  { slug: 'cdl_driver', label: 'CDL Driver' },
  { slug: 'laborer', label: 'Laborer' },
  { slug: 'foreman', label: 'Foreman' },
  { slug: 'operator', label: 'Operator' },
  // 'swamper' removed — too niche (daylighting-specific), users can add custom
  { slug: 'mechanic', label: 'Mechanic' },
  { slug: 'dispatcher', label: 'Dispatcher' },
  { slug: 'admin', label: 'Admin' }
];

// ============================================================================
// TASK-1476 — ROLE-CONFLICT DETECTOR (read-only; DETECT, never DERIVE)
// ============================================================================
// `crewType` (this module, operational — drives dispatch eligibility, the
// CREW_TYPE_ITEM_CODE_MAP billing rate lookup in clock.js, and the
// /driver|cdl/i driver-detection used by compliance + the driver board) and
// `hrPosition` (hr.js ORG_CHART — an HR career ladder WITH LEVELS, tenant
// extensible) are two DIFFERENT concepts, not two copies of one. Nothing in
// firestore.rules constrains either, so they rot apart silently: a shop manager
// carrying crewType `cdl_driver` gets scheduled, billed and performance-flagged
// as a driver.
//
// 🔴 COD REJECTED deriving one from the other (3 ✅ / 5 ⚠️ / 1 ❌): coupling an
// extensible HR ladder to a money enum silently repriced customer lines and
// changed who is dispatchable. So this code ONLY COMPARES AND REPORTS.
//   • It NEVER writes crewType, hrPosition, or anything else.
//   • It is NOT called from savePerson / any save path — editing one field can
//     never move the other. (That regression IS the rejected proposal.)
//   • A tenant-authored custom position or custom crew type maps to NO work
//     area, so it produces NO signal — free-form input never reaches a verdict.
//
// The comparison is deliberately COARSE. Raw slug equality is worthless here:
// measured live 2026-08-25, 42 of the 43 people company-wide who carry both
// fields have different slugs, because the two vocabularies simply don't share
// spellings (`crew_lead_driver` vs `cdl_driver`, `entry_swamper` vs `laborer`
// are both CORRECT). What actually bites is the WORK AREA disagreeing: does
// DispatchDeck treat this person as field crew (dispatchable, billed on a crew
// line, counted as a driver) when HR says their job is a support role, or vice
// versa. That predicate flags 6 people company-wide — the exact set a human
// picked out by hand — instead of 42.
const HR_POSITION_WORK_AREA = {
  // Field Operations — on a crew, on location, on equipment.
  supervisor_foreman: 'field',
  crew_lead_driver: 'field',
  advanced_operator: 'field',
  operator: 'field',
  entry_operator: 'field',
  veteran_swamper: 'field',
  entry_swamper: 'field',
  // Management / dispatch / shop / office — NOT billed or scheduled as crew.
  // field_manager + division_manager sit in the Field Operations department but
  // run it; coding them as crew is the reported bug, not an edge case —
  // EXCEPT a CDL holder coded as a driver, which getRoleConflict exempts
  // (TASK-9179: at a small carrier the managers drive).
  field_manager: 'support',
  division_manager: 'support',
  dispatch_manager: 'support',
  dispatcher: 'support',
  sales_account_manager: 'support',
  shop_manager: 'support',
  mechanic: 'support',
  shop_hand: 'support',
  office_manager: 'support',
  billing_ar: 'support',
  payroll_hr_admin: 'support'
};

// Built-in crew types only (DEFAULT_CREW_TYPES + the legacy spellings that are
// live in prod). `owner` is deliberately ABSENT — an owner legitimately wears
// any hat, so they are exempt rather than perpetually flagged. Anything not
// listed (a tenant's custom crew type) is exempt for the same reason.
const CREW_TYPE_WORK_AREA = {
  cdl_driver: 'field',
  driver: 'field',        // legacy pre-v1.12.71 spelling, still live
  laborer: 'field',
  foreman: 'field',
  operator: 'field',
  swamper: 'field',       // removed from DEFAULT_CREW_TYPES, still on live docs
  mechanic: 'support',
  dispatcher: 'support',
  admin: 'support'
};

// Which crew slugs actually HAVE a crew billing rate. MIRRORS the keys of
// `CREW_TYPE_ITEM_CODE_MAP` in clock.js (grep it before editing this) — roster
// does not own that file and nothing here writes to it. `admin` and
// `dispatcher` are NOT in it: `getRateForPerson` (clock.js) leaves
// `effectiveCrewType` null for them and drops out of the crew-rate branch
// entirely, so "their hours price at the Admin rate" would be a lie. Used for
// CONSEQUENCE COPY ONLY — never for a rate, a lookup, or a verdict, so drift
// here can misword a sentence but can never misprice a line.
const CREW_TYPES_WITH_A_CREW_RATE = new Set([
  'cdl_driver', 'driver', 'laborer', 'foreman', 'operator', 'swamper', 'supervisor', 'mechanic'
]);

// Live prod carries case + separator variants of the same slug ('Admin',
// 'cdl-driver', 'Driver' — see the TASK-1466 note on populateCrewTypeDropdown),
// so normalize before lookup rather than listing every spelling.
function _normalizeCrewSlug(slug) {
  return String(slug || '').trim().toLowerCase().replace(/[\s-]+/g, '_');
}

// The SAME predicate the consumers use (dispatch.js CDL-eligibility filter,
// compliance/driver-board driver detection). Kept literal so a future change to
// those consumers is greppable against this one.
function _crewSlugIsDriverish(slug) {
  return /driver|cdl/i.test(String(slug || ''));
}

// Display title for an hrPosition id. Prefers hr.js's resolver (via `window`,
// never a cross-import — Pitfall #1) so a tenant's CUSTOM position title wins;
// falls back to a prettified id when hr.js isn't loaded in this session.
function _hrPositionTitle(positionId) {
  if (!positionId) return '';
  const viaHr = window.HRModule?.getPositionTitle?.(positionId);
  if (viaHr && viaHr !== positionId) return viaHr;
  return String(positionId).replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

/**
 * Does this person's operational crew type disagree with their HR job title?
 * Pure + read-only. Returns null (no signal) unless BOTH fields are set AND
 * both resolve to a known work area AND those areas disagree.
 *
 * @returns {null | {hrId, hrTitle, crewSlug, crewLabel, hrArea, crewArea, kind, summary}}
 *   kind: 'hr-support-crew-field' | 'hr-field-crew-support' | 'hr-driver-crew-not'
 */
function getRoleConflict(person) {
  if (!person) return null;
  const hrId = String(person.hrPosition || '').trim();
  if (!hrId) return null;                                   // no HR title on file → nothing to compare
  const crewSlug = String(person.crewType || person.role || (person.roles && person.roles[0]) || '').trim();
  if (!crewSlug) return null;

  const hrArea = HR_POSITION_WORK_AREA[hrId];
  const crewArea = CREW_TYPE_WORK_AREA[_normalizeCrewSlug(crewSlug)];
  if (!hrArea || !crewArea) return null;                    // custom/unknown on either side → stay silent

  let kind = null;
  if (hrArea !== crewArea) {
    kind = hrArea === 'support' ? 'hr-support-crew-field' : 'hr-field-crew-support';
    // TASK-9179 — a manager / sales / office title held by someone who ALSO
    // drives is normal at a small carrier ("the owners, managers, etc are also
    // drivers" — Chris), not a data error. When they hold a CDL and their crew
    // type is a driver role, the record is correct: stay silent. Same rule as
    // the one-directional `hr-driver-crew-not` branch below, one rung up.
    // Requires BOTH: a CDL-less "driver" under a manager title, or a CDL holder
    // coded as a laborer/operator under one, still flags.
    // Known limit (COD 2026-09-28): savePerson DERIVES cdlLicensed=true from a
    // driver crew type when it was never set, so for a never-classified person
    // this reduces to "support title + driver crew type". Accepted with the
    // spec — Chris's call is that this shape is normal, and a stale-CDL / no-
    // dispatch detector is the right tool for a manager who left the truck.
    if (kind === 'hr-support-crew-field' && person.cdlLicensed === true && _crewSlugIsDriverish(crewSlug)) {
      kind = null;
    }
  } else if (hrArea === 'field' && _crewSlugIsDriverish(hrId) && !_crewSlugIsDriverish(crewSlug)) {
    // One-directional on purpose. HR title literally says "driver" but the crew
    // type isn't driver-ish → dispatch will never offer them as a driver.
    // The REVERSE (crewType cdl_driver under an Operator / Swamper HR title) is
    // normal in the field — operators hold CDLs — so it must never flag.
    kind = 'hr-driver-crew-not';
  }
  if (!kind) return null;

  const hrTitle = _hrPositionTitle(hrId);
  const crewLabel = (window.getTypeLabel ? window.getTypeLabel(crewSlug, 'crewTypes') : '') || capitalizeFirst(crewSlug) || crewSlug;

  // 🔴 CONSEQUENCE COPY MUST STAY TRUE TO THE ACTUAL CONSUMERS. Verified against
  // the code, not assumed (COD Code Reviewer caught the first draft overstating
  // this — do not loosen it back):
  //   • `CREW_TYPE_ITEM_CODE_MAP` (clock.js) maps cdl_driver/laborer/foreman/
  //     operator/swamper/supervisor AND `mechanic` (→ CREW-NONCDL) to crew item
  //     codes. So "they won't be billed on a crew line" is FALSE for a mechanic —
  //     say which RATE they bill at, never that they don't bill.
  //   • `populateJobCrewSections` (dispatch.js) offers EVERY showInDispatch
  //     person for crew assignment regardless of crew type. So "they won't be
  //     offered for crew assignment" is FALSE for everyone — never say it.
  //   • The narrower DRIVER picker filters on
  //     `cdlLicensed === true || /cdl|driver/i.test(crewType)`. A CDL-licensed
  //     person is offered as a driver even when their crew type isn't driver-ish,
  //     so that claim is conditioned on `cdlLicensed` below.
  // Roster does not own clock.js or dispatch.js; nothing here edits them.
  const crewIsDriverish = _crewSlugIsDriverish(crewSlug);
  const cdlOverrides = person.cdlLicensed === true;
  const crewHasRate = CREW_TYPES_WITH_A_CREW_RATE.has(_normalizeCrewSlug(crewSlug));
  const summary = kind === 'hr-support-crew-field'
    ? `HR title is ${hrTitle}, but their crew type is ${crewLabel} — tickets price their hours at the ${crewLabel} crew rate and reports count them as field crew${crewIsDriverish ? ', and they show up in the driver picker on a job' : ''}.`
    : kind === 'hr-field-crew-support'
      ? (crewHasRate
        ? `HR title is ${hrTitle} (field crew), but their crew type is ${crewLabel} — tickets price their hours at the ${crewLabel} rate, not a field-crew rate.`
        : `HR title is ${hrTitle} (field crew), but their crew type is ${crewLabel}, which has no crew rate of its own — their hours don't price at a field-crew rate.`)
      : cdlOverrides
        ? `HR title is ${hrTitle}, but their crew type is ${crewLabel}, which isn't a driver role — they still show in the driver picker because CDL Licensed is on, but their hours price as ${crewLabel}.`
        : `HR title is ${hrTitle}, but their crew type is ${crewLabel}, which isn't a driver role — they won't show in the driver picker when a job is assigned.`;

  return { hrId, hrTitle, crewSlug, crewLabel, hrArea, crewArea, kind, crewIsDriverish, cdlOverrides, crewHasRate, summary };
}

/** How many people currently in state have a role conflict (optionally scoped). */
function countRoleConflicts(list) {
  return (list || RosterState.people || []).filter(p => !!getRoleConflict(p)).length;
}

// Sentinel value for the People "Role" filter. Prefixed + suffixed with double
// underscores so it can't collide with a slugified tenant crew type; checked
// BEFORE the slug-equality branch in renderPeopleList so even a freak collision
// degrades to "show conflicts", never to a crash.
const ROLE_CONFLICT_FILTER = '__role_conflict__';

// ============================================================================
// TASK-4728 — SHARED PHONE: two live people on one number breaks PIN self-reset
// ============================================================================
// `forgotDriverPin` resolves a driver by PHONE
// (functions/src/index.js `_resolveDriverByPhone`) with
//   collectionGroup('people').where('normalizedPhones','array-contains', e164)
// and REFUSES on `matches.length !== 1`. That refusal is correct — TASK-4682 put
// it there because collapsing two identities into one on an UNAUTHENTICATED
// callable is a privilege escalation — and nothing here weakens it. The problem
// is only that nobody in the building learns about the collision: the refusal
// breadcrumb lands in `errorLogs`, which is SUPER-ADMIN read. The fleet manager
// who can actually fix it (by giving one of them their own number) finds out
// when a driver phones in from the gate at 4am.
//
// So this is VISIBILITY, not a repair. Nothing below merges, clears, rewrites or
// refuses a phone number — family crews and owner-operators legitimately share a
// handset, and TASK-4730 (Paul + Mitch Barker at Wolverine, two real people on
// one real phone) is a product decision for Chris, not something a badge decides.
//
// MEASURED POPULATION (read-only admin-SDK census, all 93 tenants, 2026-09-10):
//   455 people · 423 live · 2 colliding numbers, 2 pairs, 2 tenants —
//     ml71j5u3v1p1eucih (Expertool llc)      Lance Powell / Daryl — 0 PIN holders
//     moc4nl9n1u7vs2sc4 (Wolverine Hydro-Ex) Paul / Mitch Barker  — BOTH hold PINs
//   Same count TASK-4728 recorded on 2026-09-03, so a chip is the right size and
//   a validation subsystem is not.
//
// NORMALIZATION — chosen to be the resolver's key, not a prettier one.
// `_rosterNormalizePhoneE164` is a byte-for-byte mirror of
// functions/src/services/notifications.js `normalizePhoneNumber`: strip every
// non-digit (so formatting, dashes, parens, spaces and a leading + all collapse),
// then 10 digits -> +1XXXXXXXXXX, 11 leading 1 -> +1…, >10 -> +digits. That is
// the SAME derivation `computeNormalizedPhones` feeds the index the resolver
// queries, so a badge here means recovery is broken there — no independent
// "smart" matching that would invent collisions the server never sees.
//   • An EXTENSION is deliberately NOT split off. `normalizePhoneNumber` doesn't,
//     so "7015551234 x12" normalizes to +170155512341234 server-side and does not
//     collide with +17015551234 there either. Splitting it here would flag a pair
//     the resolver treats as distinct — a warning about a failure that cannot happen.
//   • All EIGHT phone fields are read (phone/mobile/cell/altPhone/phoneNumber/
//     cellPhone/mobilePhone/personalPhone), because the index is built from all
//     eight. Reading only `phone` would miss a collision that really does refuse.
// The mirror is duplicated rather than imported: client code cannot import from
// functions/, and a cross-`?v=` import is Pitfall #1.
// `tests/task4728-shared-phone.unit.cjs` pins the two copies together on the
// exact strings the census normalized.
//
// TERMINATED / INACTIVE PEOPLE DO NOT COUNT. `_resolveDriverByPhone` skips
// `active === false || terminated === true || terminationDate` (the same gate
// `classifyDriverPinMatch` applies), so a number shared with a terminated worker
// resolves to exactly one live person and recovery WORKS. Flagging it would be
// pure noise — `_personCountsForPinRecovery` below is that gate, verbatim.
//
// NO NEW FIELD is written to any person doc, and nothing here is rendered to a
// driver: this is admin-facing Roster chrome, reading fields the roster already
// broadcasts.
const SHARED_PHONE_FIELDS = [
  'phone', 'mobile', 'cell', 'altPhone',
  'phoneNumber', 'cellPhone', 'mobilePhone', 'personalPhone',
];

/** Mirror of notifications.normalizePhoneNumber. Returns E.164 or null. */
function _rosterNormalizePhoneE164(phone) {
  if (!phone) return null;
  const phoneStr = String(phone);
  if (phoneStr.startsWith('#')) return null;          // spreadsheet formula error
  const digits = phoneStr.replace(/[^0-9]/g, '');
  if (/^0+$/.test(digits)) return null;
  if (/^(\d)\1{9,}$/.test(digits)) return null;
  if (digits.length === 10 && digits.startsWith('000')) return null;
  if (digits.length === 10) return `+1${digits}`;
  if (digits.length === 11 && digits[0] === '1') return `+${digits}`;
  if (digits.length > 10) return `+${digits}`;
  return null;
}

/**
 * The E.164 keys a person would answer to in `_resolveDriverByPhone`.
 *
 * `overridePhone` (the value being TYPED into the person modal) replaces the
 * `phone` field so the warning tracks the edit instead of the saved doc. On that
 * path the STORED `normalizedPhones` array is deliberately ignored — it still
 * describes the pre-edit number and would keep warning about a collision the
 * save is in the middle of clearing.
 *
 * With no override, stored `normalizedPhones` entries are UNIONED in. The stored
 * array is literally what the server queries, so an entry there breaks recovery
 * right now even if the field it came from has since been cleared and the
 * onPersonWrite trigger hasn't caught up. (Census 2026-09-10: stored === computed
 * on all 423 live people, so today the union is a no-op safety net.)
 */
function _personPhoneKeys(person, overridePhone) {
  const p = person || {};
  const keys = new Set();
  for (const field of SHARED_PHONE_FIELDS) {
    const raw = (field === 'phone' && overridePhone !== undefined) ? overridePhone : p[field];
    const e164 = _rosterNormalizePhoneE164(raw);
    if (e164) keys.add(e164);
  }
  if (overridePhone === undefined && Array.isArray(p.normalizedPhones)) {
    p.normalizedPhones.forEach(v => { if (typeof v === 'string' && v) keys.add(v); });
  }
  return keys;
}

/**
 * The resolver's own liveness gate, verbatim (`_resolveDriverByPhone`, and the
 * same one `classifyDriverPinMatch` applies). A terminated or deactivated person
 * is invisible to PIN recovery, so they cannot be half of a collision.
 */
function _personCountsForPinRecovery(person) {
  const p = person || {};
  if (p.active === false) return false;
  if (p.terminated === true) return false;
  if (p.terminationDate) return false;
  return true;
}

/**
 * phone(E.164) -> [person, …] over the LIVE roster. Built once per render so the
 * card loop stays linear instead of O(n squared) — and built over ALL of
 * RosterState.people, never the filtered list, because the person you collide
 * with may be filtered off screen.
 */
function buildSharedPhoneIndex(list) {
  const index = new Map();
  (list || RosterState.people || []).forEach(p => {
    if (!p || !_personCountsForPinRecovery(p)) return;
    _personPhoneKeys(p).forEach(key => {
      if (!index.has(key)) index.set(key, []);
      index.get(key).push(p);
    });
  });
  return index;
}

/**
 * Does this person share a number with another LIVE person on this roster?
 * Returns null (the overwhelmingly common case) or { phone, others, summary }.
 */
function getSharedPhoneConflict(person, index) {
  if (!person || !_personCountsForPinRecovery(person)) return null;
  const idx = index || buildSharedPhoneIndex();
  const seen = new Set();
  const others = [];
  let phone = null;
  _personPhoneKeys(person).forEach(key => {
    const bucket = idx.get(key);
    if (!bucket || bucket.length < 2) return;
    bucket.forEach(other => {
      if (!other || other.id === person.id || seen.has(other.id)) return;
      seen.add(other.id);
      others.push(other);
      if (!phone) phone = key;
    });
  });
  if (!others.length) return null;
  return { phone, others, summary: _sharedPhoneSummary(phone, others) };
}

/**
 * TASK-4728 — the data-entry half. Paints the advisory under the Phone field
 * against the number being TYPED, so a collision is caught while the admin is
 * still holding the keyboard rather than at 4am at the gate.
 *
 * Advisory only: it never disables the Save button and `savePerson` is not
 * touched, because a shared handset is a legitimate arrangement on a family
 * crew and refusing it would break real customers (TASK-4728's own "do NOT
 * block saving" note). It also never proposes a merge or edits the other
 * person — which of the two moves is the customer's decision (TASK-4730).
 */
function _syncPersonPhoneCollisionWarning() {
  const el = document.getElementById('person-phone-shared-warning');
  if (!el) return;
  const hide = () => { el.innerHTML = ''; el.style.display = 'none'; };

  const input = document.getElementById('person-phone');
  const editingId = String(document.getElementById('person-id')?.value || '').trim();
  const self = editingId ? (RosterState.people || []).find(p => p.id === editingId) : null;

  // A terminated/deactivated person is invisible to `_resolveDriverByPhone`, so
  // their number cannot break anyone's recovery — warning there would be noise.
  if (self && !_personCountsForPinRecovery(self)) return hide();

  const keys = _personPhoneKeys(self || {}, input ? input.value : '');
  if (!keys.size) return hide();

  const others = [];
  const seen = new Set();
  (RosterState.people || []).forEach(p => {
    if (!p || p.id === editingId || seen.has(p.id)) return;
    if (!_personCountsForPinRecovery(p)) return;
    for (const key of _personPhoneKeys(p)) {
      if (keys.has(key)) { seen.add(p.id); others.push(p); return; }
    }
  });
  if (!others.length) return hide();

  const hitKey = Array.from(_personPhoneKeys(others[0])).find(k => keys.has(k)) || null;
  // Dark amber on pale amber — an explicit high-contrast pair, never
  // var(--text-muted) on a light surface.
  el.innerHTML = `
    <div style="display:flex;gap:8px;align-items:flex-start;margin-top:8px;padding:10px 12px;background:#FFFBEB;border:1px solid #FDE68A;border-radius:var(--radius-sm);font-size:0.8125rem;line-height:1.45;color:#92400E;">
      <span class="material-icons" aria-hidden="true" style="font-size:18px;flex:0 0 auto;color:#B45309;">phone_forwarded</span>
      <span>${escapeHtml(_sharedPhoneSummary(hitKey, others))} You can still save it.</span>
    </div>`;
  el.style.display = 'block';
}

/** Shared consequence copy — one wording for the card chip and the modal. */
function _sharedPhoneSummary(phone, others) {
  const pretty = formatPhoneForDisplay(phone) || phone || 'This number';
  const names = (others || []).map(o => (o && o.name) ? o.name : 'another person');
  const nameList = names.length === 1
    ? names[0]
    : names.length === 2
      ? `${names[0]} and ${names[1]}`
      : `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
  // 🔴 CONSEQUENCE COPY MUST STAY TRUE TO THE SERVER. Verified against
  // `_resolveDriverByPhone`, not assumed:
  //   • It refuses on >1 LIVE match, so BOTH people lose self-reset — not one.
  //   • It never clears driverPinHash, so a PIN they still REMEMBER keeps
  //     working. Do not say they are locked out; say self-reset is off.
  //   • Roster › Driver Code (send/text a setup code) is unaffected and has no
  //     phone-uniqueness requirement — that is the working way out today.
  return `${pretty} is also on ${nameList}'s record. Forgot-PIN looks a driver up by phone and refuses when two live people share one number, so PIN self-reset is off for all of them (a PIN they still remember keeps working). Give each their own number, or send them a code from Driver Code.`;
}

// Notification tag definitions — used to build tag cards in person modal
const NOTIFY_TAG_DEFS = [
  { value: 'ALL', label: 'Select All', desc: 'Receive every notification type', gate: null },
  { value: 'JOBALERTS', label: 'Job Alerts', desc: 'Mass text schedule broadcasts from dispatchers', gate: null },
  { value: 'DISPATCH_ADMIN', label: 'Dispatch Admin', desc: 'Job created/updated/deleted alerts — AND the escalation when someone is still clocked in long past the cutoff', gate: null },
  { value: 'PRETRIP', label: 'All Pre-Trip/DVIR Alerts (supervisor)', desc: 'Every pre-trip/DVIR submission tenant-wide — for a safety manager, not each driver', gate: null },
  { value: 'MAINTENANCE', label: 'Maintenance', desc: 'New tasks + annual inspection due reminders', gate: null },
  { value: 'COMPLIANCE', label: 'Compliance', desc: 'Insurance & registration expiration warnings', gate: null },
  { value: 'TIMEOFF', label: 'Time Off', desc: 'Request submissions & approval updates', gate: 'hr' },
  // HIRING — parent tag covering hiring + BG-check return events (background
  // check completes/canceled, MVR results, applicant status changes). Lets
  // safety leads / hiring managers get pinged without being a tenant admin.
  // Server fanout: union'd into checkrWebhook report.completed + report.canceled
  // + _notifyMvrComplete alongside admin members and tenant.compliance.notifiers[].
  // Compliance-only tenants don't have Roster (sidebar locked), so this tag is
  // paying-tenant-only in practice.
  { value: 'HIRING', label: 'Hiring & BG Alerts', desc: 'Background check returns, MVR results, applicant updates', gate: 'hr' },
  { value: 'ASSETS', label: 'Orders / Assets', desc: 'Order created & status change alerts', gate: 'clockbilling' },
  // TASK-9722 — stock alerts for counted supplies (PPE "Track stock"): below
  // zero, below the reorder level, or a shelf count that didn't match. One
  // email a day at most, never a text. Delivered only to people whose login
  // holds Roster or HR (functions/src/services/supplyAlerts.js).
  { value: 'INVENTORY', label: 'Inventory / Supplies', desc: 'Daily email when counted supplies (gloves, glasses…) go below zero, below the reorder level, or a shelf count is off. Needs a Roster or HR login. No texts.', gate: null },
  // TASK-9652 S2 — what a customer sends in on a project portal: uploaded
  // documents (notice + the waited-a-day reminder) and, since fix/1018, change
  // requests too. One card: the server's old PROJECTS type (change requests)
  // never had a card here, so both notices reached only people on Select All;
  // that type is gone and both senders resolve from this one. The value is the
  // server's NOTIFICATION_TAGS key (services/notifications.js). Edit Project,
  // the Docs tab and the Changes tab warn when no active person holds this (or ALL).
  { value: 'CUSTOMER_DOCS', label: 'Customer documents & requests', desc: 'A customer uploaded a document or sent a change request on a project portal (email + text; uploads at most one per project per 10 min), plus a reminder when a document has waited a day for review', gate: null },
  // AR/AP — parent tag covering all billing events (driver expense submissions,
  // reimbursement-needed alerts, future invoice-paid-late / vendor-bill-approval /
  // payment-received notifications). Collapsed v1.1 (2026-05-12) from two
  // sub-event-typed tags (DRIVER_EXPENSE_SUBMITTED + REIMBURSEMENT_REQUIRED).
  // Server fanout (functions/src/index.js _fireDriverExpenseNotifications) still
  // accepts the old strings for backward-compat until migration runs.
  // TASK-9688 — also the QuickBooks paid sync exceptions digest (qboPaidSyncAlert.js).
  { value: 'AR_AP', label: 'AR / AP', desc: 'Billing alerts — driver expenses, reimbursements owed, payment issues, vendor bills, QuickBooks paid sync exceptions (digest or immediate depending on event)', gate: null }
];

// Starter custom types seeded for new accounts (deletable by user)
const STARTER_CUSTOM_TYPES = {
  truckTypes: [
    { slug: 'semi', label: 'Semi' },
    { slug: 'box_truck', label: 'Box Truck' }
  ],
  trailerTypes: [
    { slug: 'dry_van', label: 'Dry Van' },
    { slug: 'tanker', label: 'Tanker' }
  ],
  assetCategories: [
    { slug: 'parts', label: 'Parts' },
    { slug: 'safety_gear', label: 'Safety Gear' }
  ],
  crewTypes: []
};

// ============================================
// YARD HELPERS
// ============================================

// TASK-1142 — optional facility `type` tag on yard entries. Canonical yard
// shape is {slug, label, address?, type?} — type is ADDITIVE and optional;
// every reader must tolerate its absence (pre-existing yards have none).
const YARD_TYPES = [
  { slug: 'yard', label: 'Yard' },
  { slug: 'plant', label: 'Plant' },
  { slug: 'shop', label: 'Shop' },
  { slug: 'terminal', label: 'Terminal' },
  // TASK-9369 — a "branch" is a yard with a different label (Split Rock /
  // Sunbelt run one yard per branch). Same {slug,label,address?,type?} shape,
  // same settings.yards array, no new collection. In the HELD org design
  // (TASK-9375) a branch becomes its own tenant and a yard stays a location
  // INSIDE it — so this tag is display copy only; never let code branch on it.
  { slug: 'branch', label: 'Branch' }
];

// TASK-9369 — Assets "Group by branch" constants. Declared here, not beside the
// rollup code, because RosterState's initializer reads the localStorage key at
// module load (see _loadAssetsGroupBy).
const ASSETS_GROUPBY_LS_KEY = 'dd_roster_assets_groupby_v1';
/** Sentinel yard filter for "slug no longer in settings.yards" (deleteYard leaves the tag on the asset). */
const ASSETS_FORMER_YARD_KEY = '__former__';

/** Human label for a yard/facility type slug ('' for unknown/missing). */
function getYardTypeLabel(type) {
  if (!type) return '';
  const found = YARD_TYPES.find(t => t.slug === type);
  return found ? found.label : '';
}

/** Returns the tenant's yards list from settings */
function getYardsList() {
  return window.AppState?.settings?.yards || [];
}

/**
 * Populate a <select> with yard options. Includes "No Yard" as first option.
 * If includeAll is true, prepends "All Yards" for filter dropdowns.
 */
// TASK-607 — populateReportsToDropdown removed from roster.js. "Reports to"
// is now edited in the HR Compliance editor, which builds its own supervisor
// picker from HRState.people (see hr.js editComplianceTab / saveComplianceFields).

function populateYardDropdown(selectId, includeAll) {
  const select = document.getElementById(selectId);
  if (!select) return;
  const yards = getYardsList();
  // TASK-9369 — keep the current selection across a rebuild. refreshYardFilters
  // now also runs on a live settings snapshot (another session adds a yard), and
  // a rebuild that reset the <select> to 'all' while RosterState still held a
  // slug would desync DOM from state until the next filterX() read the DOM.
  const prevValue = select.value;
  // TASK-9548 slice 2 — "All Yards" / "Unassigned" / "No Yard" stay flat on
  // top; the real facilities sit under a labeled <optgroup> (window.PickerGroups,
  // Pitfall #1: never a ?v= import) so the pinned rows read as what they are
  // instead of as facilities that sort wrong. Values unchanged ('all', '',
  // slug) — every filterX()/save path reads `.value`. A "Former facility"
  // sentinel the assets drill appended (ASSETS_FORMER_YARD_KEY) is re-added by
  // drillAssetsBranch, not here.
  const pinned = [];
  if (includeAll) pinned.push({ value: 'all', label: 'All Yards' });
  pinned.push({ value: '', label: includeAll ? 'Unassigned' : 'No Yard' });
  select.innerHTML = window.PickerGroups.groupedOptionsHtml([
    { label: '', items: pinned },
    { label: 'Facilities', items: yards.map(y => {
      // TASK-1142 — surface the facility type in dropdowns/filters. 'yard' is
      // the implied default, so only non-yard types get the suffix.
      const typeLabel = getYardTypeLabel(y.type);
      return { value: y.slug, label: typeLabel && y.type !== 'yard' ? `${y.label} · ${typeLabel}` : y.label };
    }) },
  ], { alwaysGroup: true });
  if (prevValue && Array.from(select.options).some(o => o.value === prevValue)) select.value = prevValue;
}

/** Look up yard label from slug */
function getYardLabel(slug) {
  if (!slug) return '';
  const yards = getYardsList();
  const found = yards.find(y => y.slug === slug);
  return found ? found.label : slug.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

/**
 * TASK-1142 (Your Facilities) — live label lookup: returns the CURRENT label
 * for a facility slug, or '' when the facility no longer exists in settings.
 * Renderers should prefer this over the denormalized `yardName` snapshot so a
 * renamed facility updates everywhere immediately; fall back to `yardName`
 * (then getYardLabel's prettified slug) for facilities that were deleted.
 */
function getLiveYardLabel(slug) {
  if (!slug) return '';
  const found = getYardsList().find(y => y.slug === slug);
  return found ? found.label : '';
}

/** Material icon for a facility type ('yard' default). */
function getYardTypeIcon(type) {
  return { yard: 'warehouse', plant: 'factory', shop: 'home_repair_service', terminal: 'local_shipping', branch: 'business' }[type] || 'warehouse';
}

/**
 * Show add/edit facility modal — name + type + address with autocomplete.
 * TASK-1142 — pass editSlug to edit an existing facility (slug stays STABLE
 * on rename; it's the stored key on people/vehicles/trailers/assets yardId).
 */
function addYardInline(selectId, editSlug, presetType) {
  const editing = editSlug ? getYardsList().find(y => y.slug === editSlug) : null;
  // TASK-9369 — "Add Branch" on the Assets tab opens this same modal with the
  // type preselected. Only honoured on ADD (an edit keeps the stored type).
  const preselect = !editing && YARD_TYPES.some(t => t.slug === presetType) ? presetType : null;
  let overlay = document.getElementById('modal-add-yard-overlay');
  if (!overlay) {
    overlay = document.createElement('div');
    overlay.id = 'modal-add-yard-overlay';
    overlay.className = 'modal';
  }
  // (Re-)append so this overlay stacks ABOVE the Facilities manager when
  // editing from it (same z-index — DOM order decides).
  document.body.appendChild(overlay);
  overlay.onclick = (e) => { if (e.target === overlay) overlay.classList.add('hidden'); };
  overlay.classList.remove('hidden');
  overlay.innerHTML = `
    <div class="modal-content modal-small" style="max-width:420px;margin:auto;border-radius:var(--radius-xl);overflow:visible;">
      <div style="padding:16px 20px;background:linear-gradient(135deg, var(--brand-blue) 0%, #2563EB 100%);color:#fff;border-radius:var(--radius-xl) var(--radius-xl) 0 0;">
        <div style="display:flex;justify-content:space-between;align-items:center;">
          <h3 style="margin:0;font-size:1.1rem;font-weight:700;"><span class="material-icons" style="font-size:20px;vertical-align:text-bottom;">warehouse</span> ${editing ? 'Edit Facility' : 'Add Facility'}</h3>
          <button class="btn-icon" onclick="document.getElementById('modal-add-yard-overlay').classList.add('hidden')" style="color:#fff;"><span class="material-icons">close</span></button>
        </div>
      </div>
      <div style="padding:20px;background:#fff;border-radius:0 0 var(--radius-xl) var(--radius-xl);">
        <div class="form-group">
          <label>Name *</label>
          <input type="text" id="add-yard-name" class="form-control" placeholder="e.g. Main Yard, Williston Shop" maxlength="100" autofocus value="${editing ? escapeHtml(editing.label) : ''}">
        </div>
        <div class="form-group">
          <label>Type</label>
          <select id="add-yard-type" class="form-control">
            ${YARD_TYPES.map(t => `<option value="${t.slug}"${(editing ? editing.type === t.slug : preselect === t.slug) ? ' selected' : ''}>${t.label}</option>`).join('')}
          </select>
        </div>
        <div class="form-group" style="position:relative;">
          <label>Address</label>
          <input type="text" id="add-yard-address" class="form-control" placeholder="Start typing to search..." value="${editing && editing.address ? escapeHtml(editing.address) : ''}">
          <p style="font-size:0.75rem;color:var(--text-muted);margin-top:4px;">Shown under "Your Facilities" in the Dispatch job-location picker.</p>
        </div>
        <div style="display:flex;gap:var(--spacing-md);justify-content:flex-end;align-items:center;margin-top:var(--spacing-lg);">
          ${editing ? '' : `<button class="btn btn-secondary" style="margin-right:auto;padding:6px 10px;font-size:0.8rem;" onclick="document.getElementById('modal-add-yard-overlay').classList.add('hidden'); window.RosterModule.manageFacilities();" title="Rename, re-address, or remove facilities"><span class="material-icons" style="font-size:15px;vertical-align:text-bottom;">tune</span> Manage</button>`}
          <button class="btn btn-secondary" onclick="document.getElementById('modal-add-yard-overlay').classList.add('hidden')">Cancel</button>
          <button class="btn btn-primary" id="btn-save-yard" onclick="window.RosterModule.saveNewYard('${selectId || ''}', '${editing ? editing.slug : ''}')"><span class="material-icons" style="font-size:16px;">${editing ? 'check' : 'add'}</span> ${editing ? 'Save Changes' : 'Add Facility'}</button>
        </div>
      </div>
    </div>
  `;

  // Address autocomplete via shared helper
  const addrInput = document.getElementById('add-yard-address');
  if (addrInput && window.attachAddressAutocomplete) window.attachAddressAutocomplete(addrInput);

  // Focus name input
  setTimeout(() => document.getElementById('add-yard-name')?.focus(), 100);
}

async function saveNewYard(selectId, editSlug) {
  const nameEl = document.getElementById('add-yard-name');
  const addrEl = document.getElementById('add-yard-address');
  const name = nameEl?.value.trim();
  if (!name) { window.showToast?.('Enter a facility name', 'error'); nameEl?.focus(); return; }

  const label = name;
  const yards = getYardsList();
  const address = addrEl?.value.trim();
  const typeVal = document.getElementById('add-yard-type')?.value || '';

  let newYards;
  let selectValue;
  if (editSlug) {
    // TASK-1142 — edit in place. Slug NEVER changes (stored key on roster
    // items + the value in every yard dropdown); only label/address/type do.
    const idx = yards.findIndex(y => y.slug === editSlug);
    if (idx === -1) { window.showToast?.('Facility not found', 'error'); return; }
    const updated = { ...yards[idx], label };
    if (address) updated.address = address; else delete updated.address;
    if (YARD_TYPES.some(t => t.slug === typeVal)) updated.type = typeVal; else delete updated.type;
    newYards = yards.map((y, i) => (i === idx ? updated : y));
    selectValue = editSlug;
  } else {
    const slug = label.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '');
    if (!slug) return;
    // TASK-9369 (COD Edge Case #4) — every yard filter reads the slug 'all' as
    // "no filter", so a facility literally named "All" would be a card that
    // shows the whole company. '__former__' is the Branches-view bucket for
    // slugs no longer in settings.yards (the slug regex can't produce it, but
    // the guard is cheap and the two sentinels belong next to each other).
    if (slug === 'all' || slug === '__former__') {
      window.showToast?.('That name is reserved — pick another facility name', 'error');
      nameEl?.focus();
      return;
    }
    if (yards.find(y => y.slug === slug)) {
      window.showToast?.('Facility already exists', 'error');
      return;
    }
    if (yards.length >= 50) {
      window.showToast?.('Maximum 50 facilities', 'error');
      return;
    }
    const yard = { slug, label };
    if (address) yard.address = address;
    // TASK-1142 — optional facility type tag (yard | plant | shop | terminal)
    if (YARD_TYPES.some(t => t.slug === typeVal)) yard.type = typeVal;
    newYards = [...yards, yard];
    selectValue = slug;
  }

  const tenant = window.AppState?.tenant || 'demo';
  try {
    await updateDoc(doc(db, 'tenants', tenant), { 'settings.yards': newYards });
    if (!window.AppState.settings) window.AppState.settings = {};
    window.AppState.settings.yards = newYards;
    if (selectId) {
      const select = document.getElementById(selectId);
      if (select) {
        const prev = editSlug ? select.value : selectValue;
        populateYardDropdown(selectId, false);
        select.value = prev;
      }
    }
    document.getElementById('modal-add-yard-overlay')?.classList.add('hidden');
    window.showToast?.(editSlug ? `Facility "${label}" updated` : `Facility "${label}" added`, 'success');
    refreshFacilitySurfaces();
  } catch (e) {
    console.error('[Roster] Error saving facility:', e);
    window.showToast?.('Error saving facility', 'error');
  }
}

/**
 * TASK-1142 — "Your Facilities" manager: list/rename/re-address/retype/delete
 * the company's yards, plants, shops & terminals in one place. Opened from the
 * Roster header (Facilities button) and the add-facility modal's Manage link.
 */
function manageFacilities() {
  let overlay = document.getElementById('modal-manage-facilities');
  if (!overlay) {
    overlay = document.createElement('div');
    overlay.id = 'modal-manage-facilities';
    overlay.className = 'modal';
  }
  // (Re-)append so the manager stacks above whatever opened it.
  document.body.appendChild(overlay);
  overlay.onclick = (e) => { if (e.target === overlay) overlay.classList.add('hidden'); };
  overlay.classList.remove('hidden');
  overlay.innerHTML = `
    <div class="modal-content modal-small" style="max-width:520px;margin:auto;border-radius:var(--radius-xl);display:flex;flex-direction:column;max-height:85dvh;overflow:hidden;">
      <div style="padding:16px 20px;background:linear-gradient(135deg, var(--brand-blue) 0%, #2563EB 100%);color:#fff;border-radius:var(--radius-xl) var(--radius-xl) 0 0;flex-shrink:0;">
        <div style="display:flex;justify-content:space-between;align-items:center;">
          <h3 style="margin:0;font-size:1.1rem;font-weight:700;"><span class="material-icons" style="font-size:20px;vertical-align:text-bottom;">warehouse</span> Your Facilities</h3>
          <button class="btn-icon" onclick="document.getElementById('modal-manage-facilities').classList.add('hidden')" style="color:#fff;"><span class="material-icons">close</span></button>
        </div>
        <p style="margin:6px 0 0;font-size:0.8rem;opacity:0.9;">Yards, plants, shops &amp; terminals — used across Roster and as job locations in Dispatch.</p>
      </div>
      <div id="manage-facilities-list" style="flex:1;min-height:0;overflow-y:auto;-webkit-overflow-scrolling:touch;padding:12px 16px;background:#fff;"></div>
      <div style="display:flex;justify-content:flex-end;gap:var(--spacing-md);padding:12px 16px;border-top:1px solid var(--border-color);background:#fff;border-radius:0 0 var(--radius-xl) var(--radius-xl);flex-shrink:0;">
        <button class="btn btn-primary" onclick="document.getElementById('modal-manage-facilities').classList.add('hidden'); window.RosterModule.addYardInline('');"><span class="material-icons" style="font-size:16px;">add</span> Add Facility</button>
      </div>
    </div>
  `;
  renderFacilitiesList();
}

/** Count roster items assigned to a facility slug (people + vehicles + trailers + assets). */
function countFacilityAssignments(slug) {
  const count = (list) => (list || []).filter(x => x.yardId === slug).length;
  return count(RosterState.people) + count(RosterState.trucks) + count(RosterState.trailers) + count(RosterState.assets);
}

function renderFacilitiesList() {
  const container = document.getElementById('manage-facilities-list');
  if (!container) return;
  const yards = getYardsList();
  if (yards.length === 0) {
    container.innerHTML = `
      <div style="text-align:center;padding:32px 16px;color:var(--text-secondary);">
        <span class="material-icons" style="font-size:40px;color:var(--text-muted);">warehouse</span>
        <p style="margin:8px 0 0;font-weight:600;color:var(--text-primary);">No facilities yet</p>
        <p style="margin:4px 0 0;font-size:0.85rem;">Add your first yard, plant, shop, or terminal — assign people and equipment to it, filter Roster by it, and pick it as a job location in Dispatch.</p>
      </div>`;
    return;
  }
  container.innerHTML = yards.map(y => {
    const typeLabel = getYardTypeLabel(y.type);
    const assigned = countFacilityAssignments(y.slug);
    return `
      <div style="display:flex;align-items:center;gap:12px;padding:10px 8px;border-bottom:1px solid var(--border-color);">
        <span class="material-icons" style="font-size:22px;color:var(--brand-blue);flex-shrink:0;">${getYardTypeIcon(y.type)}</span>
        <div style="flex:1;min-width:0;">
          <div style="display:flex;align-items:center;gap:8px;flex-wrap:wrap;">
            <span style="font-weight:600;color:var(--text-primary);">${escapeHtml(y.label)}</span>
            ${typeLabel && y.type !== 'yard' ? `<span style="font-size:0.7rem;font-weight:700;text-transform:uppercase;letter-spacing:0.04em;padding:2px 8px;border-radius:999px;background:var(--bg-secondary);color:var(--text-secondary);border:1px solid var(--border-color);">${typeLabel}</span>` : ''}
          </div>
          <div style="font-size:0.8rem;color:${y.address ? 'var(--text-secondary)' : 'var(--warning, #B45309)'};margin-top:2px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;">${y.address ? escapeHtml(y.address) : 'No address — add one so drivers get directions'}</div>
          ${assigned > 0 ? `<div style="font-size:0.75rem;color:var(--text-secondary);margin-top:2px;">${assigned} roster item${assigned === 1 ? '' : 's'} assigned</div>` : ''}
        </div>
        <button class="btn-icon" onclick="window.RosterModule.addYardInline('', '${y.slug}')" title="Edit facility"><span class="material-icons" style="font-size:19px;">edit</span></button>
        <button class="btn-icon" onclick="window.RosterModule.deleteYard('${y.slug}')" title="Remove facility" style="color:var(--danger, #DC2626);"><span class="material-icons" style="font-size:19px;">delete_outline</span></button>
      </div>`;
  }).join('');
}

/** TASK-1142 — remove a facility from settings (roster items keep their last-known tag; dispatched jobs keep their address snapshots). */
async function deleteYard(slug) {
  const yards = getYardsList();
  const yard = yards.find(y => y.slug === slug);
  if (!yard) return;
  const assigned = countFacilityAssignments(slug);
  const msg = assigned > 0
    ? `Remove "${yard.label}"? ${assigned} roster item${assigned === 1 ? ' is' : 's are'} assigned to it — they keep their last-known facility tag until reassigned, but this facility disappears from pickers and filters. Jobs already dispatched keep their address.`
    : `Remove "${yard.label}"? It disappears from pickers and filters. Jobs already dispatched keep their address.`;
  const confirmed = window.showConfirm
    ? await window.showConfirm('Remove facility', msg, { confirmText: 'Remove', cancelText: 'Cancel', danger: true })
    : confirm(msg);
  if (!confirmed) return;

  const newYards = yards.filter(y => y.slug !== slug);
  const tenant = window.AppState?.tenant || 'demo';
  try {
    await updateDoc(doc(db, 'tenants', tenant), { 'settings.yards': newYards });
    if (!window.AppState.settings) window.AppState.settings = {};
    window.AppState.settings.yards = newYards;
    window.showToast?.(`Facility "${yard.label}" removed`, 'success');
    refreshFacilitySurfaces();
  } catch (e) {
    console.error('[Roster] Error removing facility:', e);
    window.showToast?.('Error removing facility', 'error');
  }
}

/** Re-sync every surface that renders facilities after an add/edit/delete. */
function refreshFacilitySurfaces() {
  refreshYardFilters();
  renderFacilitiesList();
  // Card lists re-render so renamed labels show immediately. Guard on the
  // roster view actually being in the DOM — filterX() reads filter selects
  // from the DOM and would reset saved filter state if called off-view.
  if (document.getElementById('list-people')) {
    try { filterPeople(); filterTrucks(); filterTrailers(); filterAssets(); } catch (_) { /* partial render */ }
  }
}

/**
 * Seeds starter custom types for new accounts that have never set up customTypes.
 * Only runs once — when settings.customTypes is undefined (not empty array).
 */
async function seedStarterTypesIfNeeded() {
  const settings = window.AppState?.settings;
  if (!settings || settings.customTypes) return; // already initialized or no settings

  const tenant = window.AppState?.tenant;
  if (!tenant || tenant === 'demo') return;

  try {
    if (!settings) window.AppState.settings = {};
    window.AppState.settings.customTypes = { ...STARTER_CUSTOM_TYPES };

    await updateDoc(doc(db, 'tenants', tenant), {
      'settings.customTypes': { ...STARTER_CUSTOM_TYPES }
    });
    console.log('[Roster] Seeded starter custom types for new account');
  } catch (e) {
    console.warn('[Roster] Could not seed starter types:', e);
  }
}

// ============================================
// SHARED TYPE HELPERS (exposed on window)
// ============================================

/**
 * Returns merged list of default + custom types for a given kind.
 * Custom types are inserted before "Other" (which always stays last).
 * Duplicate slugs are skipped.
 */
function getTypeList(kind) {
  const defaults = kind === 'truckTypes' ? DEFAULT_TRUCK_TYPES
    : kind === 'trailerTypes' ? DEFAULT_TRAILER_TYPES
    : kind === 'crewTypes' ? DEFAULT_CREW_TYPES
    : DEFAULT_ASSET_CATEGORIES;

  const custom = window.AppState?.settings?.customTypes?.[kind] || [];
  const slugSet = new Set(defaults.map(d => d.slug));

  // Separate "other" from the rest
  const base = defaults.filter(d => d.slug !== 'other');
  const other = defaults.find(d => d.slug === 'other');

  // Add custom types that aren't duplicates
  const extras = custom.filter(c => !slugSet.has(c.slug));

  // TASK-9548 slice 2 — each entry says which bucket it came from (`group`:
  // 'standard' built-in · 'custom' tenant-added · 'other' the catch-all) so the
  // dropdown builders can label the buckets instead of silently concatenating
  // them. Additive: slug/label are unchanged and every existing reader ignores
  // the extra key. Fresh objects, never the DEFAULT_* constants.
  return [
    ...base.map(t => ({ ...t, group: 'standard' })),
    ...extras.map(t => ({ ...t, group: 'custom' })),
    ...(other ? [{ ...other, group: 'other' }] : []),
  ];
}

// TASK-9548 slice 2 — the one grouped builder for every type <select>:
// "Standard types" (built-in) → "Custom types" (settings.customTypes) → "In use,
// not on type list" (TASK-9339 extras, still suffixed per option) → "Other"
// flat at the end, exactly the order the flat list already had. Values are
// the slugs, unchanged. opts.leading = flat options rendered first
// ([{value,label}]), e.g. "All Types" / "None".
function _typeOptionsHtml(list, kind, opts = {}) {
  const noun = kind === 'assetCategories' ? 'categories' : (kind === 'crewTypes' ? 'crew types' : 'types');
  const byGroup = { standard: [], custom: [], inuse: [], other: [] };
  (list || []).forEach(item => {
    const key = item.unregistered ? 'inuse' : (byGroup[item.group] ? item.group : 'standard');
    byGroup[key].push({ value: item.slug, label: _typeOptionText(item) });
  });
  return window.PickerGroups.groupedOptionsHtml([
    { label: '', items: opts.leading || [] },
    { label: `Standard ${noun}`, items: byGroup.standard },
    { label: `Custom ${noun}`, items: byGroup.custom },
    { label: 'In use, not on type list', items: byGroup.inuse },
    { label: '', items: byGroup.other },
  ], { alwaysGroup: true });
}

/**
 * TASK-9339 — the registered list (getTypeList) PLUS every type value actually
 * stored on `items` that the list doesn't carry. The registered list alone never
 * looks at the vehicles, so a type written by an import or an older type list
 * (measured 2026-09-23: 12 of 28 tenants with vehicles, e.g. `hydrovac`,
 * `trucking`, `hot shot`, `vac-truck`, `dry_van` trailers) could not be picked
 * in a filter, a vehicle form, or a PM override — those vehicles silently fell
 * to the catch-all.
 *
 * Returns fresh objects (never the DEFAULT_* constants): registered entries as
 * `{ slug, label, count }`, in-use extras as `{ slug, label, count,
 * unregistered: true }`, extras sorted by label and inserted before "Other".
 *
 * `opts.caseInsensitive` — match and key by lower-case. Use it ONLY where the
 * consumer itself lower-cases the stored value (PM interval lookup does
 * `(truck.type || '').toLowerCase()`); filters and forms need the exact stored
 * value or the saved record would change.
 */
function getTypeListWithInUse(kind, items, opts = {}) {
  const field = kind === 'assetCategories' ? 'category' : (kind === 'crewTypes' ? 'crewType' : 'type');
  const ci = !!opts.caseInsensitive;
  const norm = s => (ci ? String(s).toLowerCase() : String(s));
  const list = getTypeList(kind).map(t => ({ slug: ci ? norm(t.slug) : t.slug, label: t.label, count: 0, group: t.group }));
  const byKey = new Map();
  list.forEach(t => { if (!byKey.has(norm(t.slug))) byKey.set(norm(t.slug), t); });

  const extras = [];
  (Array.isArray(items) ? items : []).forEach(it => {
    const raw = it ? it[field] : null;
    if (typeof raw !== 'string' || !raw.trim()) return;
    const key = norm(raw);
    let entry = byKey.get(key);
    if (!entry) {
      entry = { slug: ci ? key : raw, label: getTypeLabel(raw, kind), count: 0, unregistered: true };
      byKey.set(key, entry);
      extras.push(entry);
    }
    entry.count++;
  });
  if (!extras.length) return list;
  extras.sort((a, b) => a.label.localeCompare(b.label));
  const otherIdx = list.findIndex(t => t.slug === 'other');
  return otherIdx >= 0
    ? [...list.slice(0, otherIdx), ...extras, ...list.slice(otherIdx)]
    : [...list, ...extras];
}

// Option text for an in-use type the tenant never registered — visible AS such.
function _typeOptionText(item) {
  return item.unregistered ? `${item.label} (not on type list)` : item.label;
}

// The records whose type values count as "in use" for a kind on the roster page.
function _inUseItemsFor(kind) {
  if (kind === 'truckTypes') return RosterState.trucks || [];
  if (kind === 'trailerTypes') return RosterState.trailers || [];
  if (kind === 'assetCategories') return RosterState.assets || [];
  return [];
}

// Append an <option> for `slug` unless the select already offers it. Iterates
// options rather than querySelector — a slug is free-form tenant data and a
// quote in it would throw on an attribute selector.
function _ensureTypeOption(select, slug, kind) {
  if (!select || !slug) return;
  if (Array.from(select.options).some(o => o.value === slug)) return;
  const opt = document.createElement('option');
  opt.value = slug;
  opt.textContent = getTypeLabel(slug, kind);
  select.appendChild(opt);
}

/**
 * Populate a <select> element with type options from getTypeList().
 * If includeAll is true, prepends "All Types" / "All Categories" option.
 *
 * TASK-9339 — `opts.inUse` (array of records) also offers every type those
 * records actually carry (see getTypeListWithInUse), so a vehicle form can put
 * a truck on a type the fleet already uses. `opts.keep` re-adds that slug if the
 * rebuilt list lacks it (the record's own legacy type, or the value the select
 * held before a custom-type add/remove rebuilt it).
 */
function populateTypeDropdown(selectId, kind, includeAll, opts = {}) {
  const select = document.getElementById(selectId);
  if (!select) return;

  const list = opts.inUse ? getTypeListWithInUse(kind, opts.inUse) : getTypeList(kind);
  const allLabel = kind === 'assetCategories' ? 'All Categories' : kind === 'crewTypes' ? 'Select Crew Type' : 'All Types';

  // TASK-9548 slice 2 — labeled buckets (see _typeOptionsHtml); values unchanged.
  select.innerHTML = _typeOptionsHtml(list, kind, { leading: includeAll ? [{ value: 'all', label: allLabel }] : [] });
  if (opts.keep && opts.keep !== 'all') _ensureTypeOption(select, opts.keep, kind);
}

/**
 * Look up the display label for a type slug.
 * Falls back to capitalizing the slug with underscores / hyphens replaced by spaces.
 */
function getTypeLabel(slug, kind) {
  if (!slug) return '';
  const list = getTypeList(kind);
  const found = list.find(item => item.slug === slug);
  if (found) return found.label;
  // Fallback: capitalize slug (TASK-9339: `vac-truck` reads "Vac Truck", not "Vac-Truck")
  return String(slug).replace(/[_-]+/g, ' ').trim().replace(/\b\w/g, c => c.toUpperCase());
}

/**
 * Populate crew type dropdown — includes blank "None" option + all crew types + custom types.
 *
 * TASK-1466 — `ensureSlug` injects a temporary <option> for a role the person
 * already holds that isn't in the offerable list, mirroring what the truck /
 * trailer / asset edit forms have always done for unknown type slugs (see
 * openTruckModal, openTrailerModal, openAssetModal). WITHOUT it the select
 * silently falls back to blank, and savePerson()'s `if (!roleVal) return`
 * guard then refuses to save until the user picks a DIFFERENT role — i.e. the
 * form forces a mislabel on any legacy slug. Measured live: 32 people across
 * 15 tenants sit on a slug the dropdown cannot offer ('Admin' with a capital
 * A — written by the signup owner-person path — 'cdl-driver', 'Driver',
 * 'staff'), so this is not a trippin-trucking-only problem.
 *
 * This does NOT read the orphaned top-level `customTypes` map. The canonical
 * source stays `settings.customTypes` via getTypeList(); we only tolerate a
 * value already stored on the record being edited.
 */
function populateCrewTypeDropdown(selectId, ensureSlug) {
  const select = document.getElementById(selectId);
  if (!select) return;
  const list = getTypeList('crewTypes');
  const current = select.value;
  // TASK-9548 slice 2 — labeled buckets (see _typeOptionsHtml); values unchanged.
  select.innerHTML = _typeOptionsHtml(list, 'crewTypes', { leading: [{ value: '', label: 'None' }] });
  // Preserve an unknown/legacy slug so it can be selected and re-saved as-is.
  // Iterate options rather than querySelector — a slug is free-form tenant data
  // and could contain a quote, which would throw on an attribute selector.
  // Deliberately keyed off the EXPLICIT ensureSlug argument, not `current` —
  // `current` can be a stale value left in the select by a previously-opened
  // person, and injecting that into the ADD form would let a user create a
  // brand-new person on a legacy slug. Only the record being edited gets the
  // tolerance.
  const keep = ensureSlug;
  if (keep && !Array.from(select.options).some(o => o.value === keep)) {
    const opt = document.createElement('option');
    opt.value = keep;
    opt.textContent = getTypeLabel(keep, 'crewTypes');
    select.appendChild(opt);
  }
  select.value = current || '';
}

/**
 * Populate the role filter dropdown with all known roles (defaults + custom + in-use).
 * Preserves the current selection.
 */
function populateRoleFilterDropdown() {
  const select = document.getElementById('filter-people-role');
  if (!select) return;
  const current = select.value;

  // Merge type list with roles actually used by people
  const typeList = getTypeList('crewTypes');
  const slugSet = new Set(typeList.map(t => t.slug));
  const extras = [];
  (RosterState.people || []).forEach(p => {
    const r = p.crewType || p.role || (p.roles && p.roles[0]) || '';
    if (r && !slugSet.has(r)) {
      slugSet.add(r);
      const label = r.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
      extras.push({ slug: r, label });
    }
  });

  select.innerHTML = '<option value="all">All Roles</option>';
  [...typeList, ...extras].forEach(item => {
    const opt = document.createElement('option');
    opt.value = item.slug;
    opt.textContent = item.label;
    select.appendChild(opt);
  });

  // TASK-1476 — the drift detector's "filterable list" half. Only offered when
  // there is at least one conflict, so it's never a dead option that returns
  // nothing. Kept in the dropdown (not a separate control) per the COD UX
  // condition: one place to reconcile, not a second parallel filter UI.
  const conflictCount = countRoleConflicts();
  if (conflictCount > 0) {
    const opt = document.createElement('option');
    opt.value = ROLE_CONFLICT_FILTER;
    opt.textContent = `⚠ Role conflicts (${conflictCount})`;
    select.appendChild(opt);
  }

  // Don't strand the select on the conflict sentinel after the last conflict is
  // resolved — the option is gone, so fall back to All Roles.
  if (current === ROLE_CONFLICT_FILTER && conflictCount === 0) {
    select.value = 'all';
    if (RosterState.filters?.people) RosterState.filters.people.role = 'all';
    return;
  }
  select.value = current || 'all';
}

/**
 * Populate a filter dropdown (trucks/trailers/assets) with defaults + custom types
 * + any in-use type slug found in the current data. Self-heals when settings
 * haven't loaded yet or when imported data has types not registered in customTypes.
 * Preserves the current selection.
 *
 * TASK-9339 — built on getTypeListWithInUse so the in-use extras are marked
 * "(not on type list)" instead of passing as registered types, and `items` can
 * be passed in so maintenance.js filters its OWN vehicle snapshot (it has no
 * RosterState). A selection whose type is no longer carried by any record is
 * kept as an option rather than dropped — otherwise the dropdown would read
 * "All Types" while the list stayed filtered to the vanished type.
 */
function populateTypeFilterDropdown(selectId, kind, items) {
  const select = document.getElementById(selectId);
  if (!select) return;
  const current = select.value;
  const finalList = getTypeListWithInUse(kind, Array.isArray(items) ? items : _inUseItemsFor(kind));

  const allLabel = kind === 'assetCategories' ? 'All Categories' : 'All Types';
  // TASK-9548 slice 2 — labeled buckets (see _typeOptionsHtml); values unchanged.
  select.innerHTML = _typeOptionsHtml(finalList, kind, { leading: [{ value: 'all', label: allLabel }] });
  if (current && current !== 'all') _ensureTypeOption(select, current, kind);
  select.value = current || 'all';
  if (!items) _typeDrift(kind); // TASK-10198 (roster's own lists only)
}
// TASK-10198 — near-duplicate type warning, LAZY views/rosterTypeDrift.js (perf budget).
let _tdP = null;
const _tdLoad = () => (_tdP ||= __ddChunkWrap(import('./rosterTypeDrift.js?v=10198001')).catch(() => (_tdP = null)));
function _typeDrift(kind) {
  _tdLoad().then((m) => m?.renderTypeDrift(kind, { RosterState, db, doc, runTransaction, getTypeList, getTypeLabel, escapeHtml }));
}

// Expose shared helpers on window for cross-module use (maintenance.js, dispatch.js)
window.getTypeList = getTypeList;
window.getTypeListWithInUse = getTypeListWithInUse;
window.populateTypeFilterDropdown = populateTypeFilterDropdown;
window.getTypeLabel = getTypeLabel;
window.populateTypeDropdown = populateTypeDropdown;

// ============================================
// ROSTER STATE
// ============================================
const RosterState = {
  people: [],
  trucks: [],
  trailers: [],
  assets: [],
  // TASK-1443 — equipmentTemplates ("rigs"): tractor+trailer(s) COMBINATIONS.
  rigs: [],
  orders: [],
  customers: [],
  activeTab: 'people',
  unsubscribers: [],
  // Filter state
  filters: {
    people: { search: '', role: 'all', yard: 'all', status: 'active' },
    trucks: { search: '', type: 'all', yard: 'all', showInactive: false, sort: 'unit' },
    trailers: { search: '', type: 'all', yard: 'all', showInactive: false, sort: 'unit' },
    // TASK-9369 — groupBy: 'items' (today's grid) | 'branches' (one rollup card
    // per yard). Persisted per device; only EFFECTIVE when the tenant has yards
    // (see _assetsGroupByEffective) so a zero-yard tenant renders today's grid
    // byte-for-byte whatever this device last chose.
    assets: { search: '', category: 'all', yard: 'all', showInactive: false, groupBy: _loadAssetsGroupBy() },
    rigs: { search: '' },
    orders: { search: '', customer: 'all', status: 'all' }
  },
  // Order management overlay state
  orderManagementOpen: false,
  // Document uploads - trucks (each type: { file, previewUrl } for pending, { url, name, uploadedAt } for existing)
  // TASK-259 — `lease` doc type holds Part 376 lease agreement PDFs for
  // non-owned vehicles. Retention is end-of-lease + 1yr per §379 App A.
  truckDocs: {
    inspection: { pending: [], existing: [] },
    registration: { pending: [], existing: [] },
    insurance: { pending: [], existing: [] },
    lease: { pending: [], existing: [] },
    irp: { pending: [], existing: [] } // TASK-9813 — IRP cab card
  },
  // Document uploads - trailers
  trailerDocs: {
    inspection: { pending: [], existing: [] },
    registration: { pending: [], existing: [] },
    insurance: { pending: [], existing: [] }
  },
  // General documents (additional docs beyond inspection/registration/insurance)
  truckGeneralDocs: { pending: [], existing: [] },
  trailerGeneralDocs: { pending: [], existing: [] },
  // TASK-1297 — generic categorized documents on equipment/assets (mirrors
  // truck/trailer generalDocs; uploads to tenants/{tid}/asset-docs/).
  assetGeneralDocs: { pending: [], existing: [] },
  // Asset photo upload
  assetPhoto: { pending: null, existing: null },
  // TASK-340 Slice A (Lacey 2026-05-20) — Truck + trailer photo upload (mirrors
  // assetPhoto). Lets dispatchers swap the generic local_shipping / rv_hookup
  // icon for an actual photo of the rig. Same pending/existing shape.
  truckPhoto: { pending: null, existing: null },
  trailerPhoto: { pending: null, existing: null },
  // TASK-340 Slice B (Lacey 2026-05-20) — Person profile photo. **Separate
  // from DL image** (privacy: DL contains DOB/license #/address). Admin uploads
  // a non-DL profile photo; render replaces the colored-initials avatar.
  // COD-locked: 3-1 vote, Architect "strictly mandatory" sidesteps PII leak.
  personPhoto: { pending: null, existing: null },
  // DVIR Accountability flags
  dvirFlags: [],
  // JSA Accountability flags
  jsaFlags: [],
  // OOS Citations (active, not completed)
  oosCitations: [],
  driverOosCitations: [],
  _peopleLoaded: false,
  // TASK-368 pattern (2026-05-28) — distinguish "loading" from "empty" for
  // each tab's onSnapshot. Pre-fix, opening Roster on a fresh tab flashed
  // "No vehicles yet / No trailers yet / No assets yet" before the first
  // snapshot landed. Same shape as billing tickets + HR Hiring fixes.
  _trucksLoaded: false,
  _trailersLoaded: false,
  _assetsLoaded: false,
  _rigsLoaded: false
};

// ============================================
// PHONE NUMBER FORMATTING
// ============================================
function formatPhoneForDisplay(phone) {
  if (!phone) return null;
  const digits = String(phone).replace(/[^0-9]/g, '');

  if (digits.length === 10) {
    return `(${digits.slice(0,3)}) ${digits.slice(3,6)}-${digits.slice(6)}`;
  }
  if (digits.length === 11 && digits[0] === '1') {
    return `+1 (${digits.slice(1,4)}) ${digits.slice(4,7)}-${digits.slice(7)}`;
  }
  if (digits.length > 11) {
    // International - just add + prefix
    return `+${digits}`;
  }
  // Not enough digits for a valid phone
  return null;
}

function updatePhonePreview(inputId, previewId) {
  const input = document.getElementById(inputId);
  const preview = document.getElementById(previewId);
  if (!input || !preview) return;

  const formatted = formatPhoneForDisplay(input.value);
  if (formatted && input.value.trim()) {
    preview.innerHTML = `<span class="material-icons" style="font-size: 14px; vertical-align: middle;">check_circle</span> ${formatted}`;
    preview.className = 'phone-preview valid';
  } else if (input.value.trim()) {
    const digits = input.value.replace(/[^0-9]/g, '').length;
    preview.innerHTML = `<span class="material-icons" style="font-size: 14px; vertical-align: middle;">error</span> Need ${10 - digits} more digits`;
    preview.className = 'phone-preview invalid';
  } else {
    preview.textContent = '';
    preview.className = 'phone-preview';
  }
}

// ============================================
// UNIT DISPLAY HELPER
// ============================================
function getUnitDisplay(item) {
  if (!item) return '';
  const unit = item.unit || '';
  const nick = item.nickname || '';
  if (nick && unit) return `${nick} — #${unit}`;
  if (nick) return nick;
  if (unit) return unit;
  return item.name || '';
}
// Expose globally for other modules
window.getUnitDisplay = getUnitDisplay;

// ============================================
// INITIALIZE ROSTER VIEW
// ============================================
export function initRoster(container, tenantId) {
  console.log('[Roster] Initializing for tenant:', tenantId);

  // Guard against null tenant
  if (!tenantId) {
    console.error('[Roster] No tenant ID provided');
    container.innerHTML = `
      <div class="empty-state">
        <div class="empty-state-icon">⚠️</div>
        <div class="empty-state-title">Account Setup Issue</div>
        <div class="empty-state-text">
          Unable to load roster. Please try logging out and back in.
        </div>
        <button class="btn btn-primary" onclick="window.handleLogout()">
          <span class="material-icons">logout</span> Log Out
        </button>
      </div>
    `;
    return () => {};
  }

  RosterState._peopleLoaded = false;
  RosterState._trucksLoaded = false;
  RosterState._trailersLoaded = false;
  RosterState._assetsLoaded = false;
  RosterState.filters.assets.belowZero = false; // TASK-9985 — never persists across view entries

  // Seed starter custom types for new accounts
  seedStarterTypesIfNeeded();

  // Render the roster UI
  container.innerHTML = getRosterHTML();

  // TASK-1489 — reveal "Invite User" only for owner/admin/legacy-single-login.
  _syncInviteUserButtonVisibility();
  _syncCrewSkillsButton(); // TASK-9844
  _syncDriverPinOffice(); // TASK-9941 F S3
  _syncSetupAnswersBanner(); // TASK-9768 slice 2b

  // Populate dynamic filter dropdowns (self-heal with in-use slugs once data arrives)
  populateTypeFilterDropdown('filter-trucks-type', 'truckTypes');
  populateTypeFilterDropdown('filter-trailers-type', 'trailerTypes');
  populateTypeFilterDropdown('filter-assets-category', 'assetCategories');
  refreshYardFilters();

  // Set up tab switching
  setupTabs();

  // Mount modals (to document.body so Compliance module can reuse them)
  ensureModalsMounted();

  // Subscribe to real-time data
  subscribeToData(tenantId);
  _drainRosterOpenHint(); // TASK-9717 — a Claude file-handoff link (vehicle upload / photo reader)

  // Return cleanup function
  return () => {
    RosterState.unsubscribers.forEach(unsub => unsub());
    RosterState.unsubscribers = [];
    ['person-name','person-email','person-personal-email','person-phone','person-terminated-reason',
     'truck-unit','truck-nickname','truck-make','truck-model','truck-ins-company','truck-ins-phone','truck-inspection-location','truck-engine-make','truck-engine-model','truck-notes',
     'trailer-unit','trailer-nickname','trailer-make','trailer-model','trailer-ins-company','trailer-ins-phone','trailer-inspection-location','trailer-notes',
     'asset-name','asset-manufacturer','asset-notes']
      .forEach(id => window.SmartAssistant?.unregister(id));
    ['.inv-name']
      .forEach(s => window.SmartAssistant?.unregisterClass(s));
  };
}

// ============================================
// HTML TEMPLATE
// ============================================
function getRosterHTML() {
  return `
    <div class="roster-view">
      <!-- Tabs -->
      <div style="display:flex;align-items:center;gap:var(--spacing-sm);justify-content:center;">
        <div class="roster-tabs" style="flex:0 1 auto;">
          <button class="roster-tab active" data-tab="people">
            <span class="material-icons">people</span>
            <span>People</span>
            <span class="tab-count count-zero" id="people-count">0</span>
          </button>
          <button class="roster-tab" data-tab="trucks">
            <span class="material-icons">local_shipping</span>
            <span>Vehicles</span>
            <span class="tab-count count-zero" id="trucks-count">0</span>
          </button>
          <button class="roster-tab" data-tab="trailers">
            <span class="material-icons">rv_hookup</span>
            <span>Trailers</span>
            <span class="tab-count count-zero" id="trailers-count">0</span>
          </button>
          <button class="roster-tab" data-tab="assets">
            <span class="material-icons">inventory_2</span>
            <span>Assets</span>
            <span class="tab-count count-zero" id="assets-count">0</span>
            <span id="assets-alert-badge" class="nav-alert-badge hidden"></span>
          </button>
          <!-- TASK-1443 — Rigs = tractor+trailer(s) COMBINATIONS. Last tab: a rig is
               built FROM vehicles + trailers, so it reads left-to-right as a pipeline. -->
          <button class="roster-tab" data-tab="rigs">
            <span class="material-icons">link</span>
            <span>Rigs</span>
            <span class="tab-count count-zero" id="rigs-count">0</span>
          </button>
        </div>
        <button class="btn btn-secondary btn-sm" id="btn-manage-facilities" onclick="window.RosterModule.manageFacilities()" title="Your Facilities — yards, plants, shops & terminals" style="padding:6px 12px;font-size:0.8rem;display:flex;align-items:center;gap:4px;white-space:nowrap;">
          <span class="material-icons" style="font-size:16px;">warehouse</span><span class="print-label"> Facilities</span>
        </button>
        <button class="btn btn-secondary btn-sm" onclick="window.RosterModule.showPrintListModal()" title="Print roster list" style="padding:6px 12px;font-size:0.8rem;display:flex;align-items:center;gap:4px;white-space:nowrap;">
          <span class="material-icons" style="font-size:16px;">print</span><span class="print-label"> Print List</span>
        </button>
      </div>

      <!-- Tab Content -->
      <div class="roster-content">
        <!-- People Tab -->
        <div class="roster-panel active" id="panel-people">
          <div class="panel-header">
            <h2>People</h2>
            <div style="display:flex;gap:var(--spacing-sm);flex-wrap:wrap;align-items:center;">
              <!-- TASK-1489 / TASK-1500 — scoped invite for someone who is NOT an
                   employee (outside compliance manager, bookkeeper, insurance
                   agent). Deliberately generic per Chris ("something closer to
                   'invite a user'") with a Compliance Manager quick-fill inside,
                   rather than a Crown-specific button. Admin-only; hidden for
                   non-admins by _syncInviteUserButtonVisibility() (the server's
                   requireTenantAdmin is the real gate). -->
              <button class="btn btn-secondary" id="btn-roster-invite-user" style="display:none;" onclick="window.openAddTeamMember()" title="Give a roster person or an outside user (compliance manager, bookkeeper, agent) their own login with scoped module access">
                <span class="material-icons">person_add_alt</span> Add team member
              </button>
              <!-- TASK-9844 — the company's crew skills list + what each job type /
                   vehicle type needs. Hidden for anyone who cannot edit it
                   (_syncCrewSkillsButton). -->
              <button class="btn btn-secondary" id="btn-roster-skills" style="display:none;" onclick="window.RosterModule.showCrewSkills()" title="Skills & certifications: your skills list (hydro-vac operator, confined space, a union class...) with who has each one, and your certifications (cards on file)">
                <span class="material-icons">workspace_premium</span> Skills
              </button>
              <!-- TASK-9941 F S3 — who still has no private PIN + per-company
                   "Send setup codes" + the owner's tablet cutover date. Replaces
                   TASK-4692's "Text setup codes". Owner or the Roster / HR module
                   (_syncDriverPinOffice); the server re-checks. -->
              <button class="btn btn-secondary" id="btn-roster-pin-setup" style="display:none;" onclick="window.RosterModule.openDriverPins()" title="See who still has no private PIN and send them setup codes">
                <span class="material-icons">pin</span> Driver PINs
              </button>
              <button class="btn btn-primary" onclick="window.RosterModule.showAddPerson()">
                <span class="material-icons">add</span> Add Person
              </button>
            </div>
          </div>
          <!-- TASK-9768 slice 2b — "setup answers are waiting" (filled by _syncSetupAnswersBanner). -->
          <div id="roster-setupq-banner" style="display:none;"></div>
          <!-- TASK-9941 F S3 — the owner's tablet cutover countdown, when a date is set. -->
          <div id="roster-pin-cutover-banner" style="display:none;"></div>
          <div class="roster-filter-bar">
            <div class="roster-search">
              <span class="material-icons">search</span>
              <input type="search" id="search-people" placeholder="Search people..." autocomplete="off" oninput="window.RosterModule.filterPeople()">
            </div>
            <select id="filter-people-role" onchange="window.RosterModule.filterPeople()">
              <option value="all">All Roles</option>
            </select>
            <select id="filter-people-yard" onchange="window.RosterModule.filterPeople()" style="display:none;">
            </select>
            <select id="filter-people-status" onchange="window.RosterModule.filterPeople()" data-tooltip="Filter by employment status. Inactive = still employed, temporarily not working. Use No Longer Employed (Terminated) when employment ends." aria-label="Filter by employment status. Inactive means still employed, temporarily not working. Use No Longer Employed when employment ends.">
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
              <option value="laidoff">Laid off</option>
              <option value="terminated">Terminated</option>
              <option value="stop">Texted STOP</option>
              <option value="all">All</option>
            </select>
            <button id="btn-mute-toggle" class="btn-toggle-inactive" onclick="window.RosterModule.toggleMuteAll()" data-tooltip="Mute/unmute notifications for everyone" aria-label="Mute/unmute notifications for everyone">
              <span class="material-icons">notifications_off</span>
              <span class="toggle-label">Mute</span>
            </button>
            <!-- TASK-1532 — always-visible answer to "did that work?": how many
                 active people are currently muted. Hidden when zero. -->
            <!-- TASK-1476 — role-conflict chip: makes the rot VISIBLE instead of
                 silent. Click = filter the list to just those people. Hidden at
                 zero. Read-only signal; nothing is repaired automatically. -->
            <button type="button" id="roster-role-conflict-chip" onclick="window.RosterModule.showRoleConflicts()" style="display:none;align-items:center;gap:4px;padding:4px 10px;border-radius:999px;background:#FEF3C7;border:1px solid #FDE68A;color:#92400E;font-size:0.8rem;font-weight:600;white-space:nowrap;cursor:pointer;" data-tooltip="These people have an HR job title that describes different work than their crew type. Crew type drives dispatch assignment, billing rate lookup and driver reporting — so whichever one is stale is steering the wrong thing. Click to review them." aria-label="Show people whose HR title disagrees with their crew type"><span class="material-icons" style="font-size:15px;">rule</span><span id="roster-role-conflict-chip-count"></span></button>
            <!-- TASK-9941 F S3 — how many active people have no private PIN yet
                 (server-computed; never a PIN). Click = show just them. -->
            <button type="button" id="roster-nopin-chip" onclick="window.RosterModule.toggleNoPinFilter()" style="display:none;align-items:center;gap:4px;padding:4px 10px;border-radius:999px;background:#FEF3C7;border:1px solid #FDE68A;color:#92400E;font-size:0.8rem;font-weight:600;white-space:nowrap;cursor:pointer;" aria-label="Show people who have no private PIN yet"><span class="material-icons" style="font-size:15px;">pin</span><span id="roster-nopin-chip-count"></span></button>
            <span id="roster-muted-chip" style="display:none;align-items:center;gap:4px;padding:4px 10px;border-radius:999px;background:#FEF2F2;border:1px solid #FECACA;color:#DC2626;font-size:0.8rem;font-weight:600;white-space:nowrap;" title="Active people with notifications muted"><span class="material-icons" style="font-size:15px;">notifications_off</span><span id="roster-muted-chip-count"></span></span>
            <button id="btn-team-directory-toggle" class="btn-toggle-inactive" onclick="window.RosterModule.toggleDriverDirectory()" data-tooltip="Show/hide the driver-side Team contact directory. Only people with 'Show on Driver Card' on appear." aria-label="Show/hide the driver-side Team contact directory. Only people with 'Show on Driver Card' on appear.">
              <span class="material-icons">groups</span>
              <span class="toggle-label">Team Directory: Off</span>
            </button>
          </div>
          <!-- TASK-9894 — shown only for a view & upload only login (updateMuteButton). -->
          <div id="roster-people-viewonly-note" role="note" style="display:none;margin:0 0 10px;padding:8px 12px;border-radius:8px;background:#FEF3C7;border:1px solid #FDE68A;color:#92400E;font-size:0.85rem;line-height:1.4;">Your login is view &amp; upload only — you can look people up and add new people, but not change or remove anyone already here. Ask the owner or an admin to change this.</div>
          <div class="roster-list" id="list-people">
            <div class="loading-state"><div class="loading-spinner"></div><p>Loading people...</p></div>
          </div>
        </div>

        <!-- Vehicles Tab -->
        <div class="roster-panel" id="panel-trucks">
          <div class="panel-header">
            <h2>Vehicles</h2>
            <div class="panel-header-actions">
              <!-- TASK-4876 S2 — the fleet permit checklist, also reachable where the
                   axle data is typed (it used to live only on the Rigs tab). -->
              <button class="btn btn-secondary" onclick="window.RosterModule.showPermitReadiness()" title="Which vehicles and trailers still need permit specs">
                <span class="material-icons">fact_check</span> Permit Readiness
              </button>
              <!-- TASK-9716 — read the shop whiteboard into TASK-9715's spec request (approval card). -->
              <button class="btn btn-secondary ai-entry" onclick="window.FleetPhotoIntake && window.FleetPhotoIntake.open({ surface: 'roster' })" title="Snap the shop whiteboard; you approve every change">
                <span class="material-icons">photo_camera</span> Update from photo
              </button>
              <button class="btn btn-primary" onclick="window.RosterModule.showAddTruck()">
                <span class="material-icons">add</span> Add Vehicle
              </button>
            </div>
          </div>
          <div class="roster-filter-bar">
            <div class="roster-search">
              <span class="material-icons">search</span>
              <input type="search" id="search-trucks" placeholder="Search vehicles or VIN..." autocomplete="off" oninput="window.RosterModule.filterTrucks()">
            </div>
            <select id="filter-trucks-type" onchange="window.RosterModule.filterTrucks()">
            </select>
            <select id="filter-trucks-yard" onchange="window.RosterModule.filterTrucks()" style="display:none;">
            </select>
            <select id="sort-trucks" onchange="window.RosterModule.filterTrucks()">
              <option value="unit">Sort: Unit #</option>
              <option value="name">Sort: Name A-Z</option>
            </select>
            <button id="toggle-inactive-trucks" class="btn-toggle-inactive" onclick="window.RosterModule.toggleInactive('trucks')" data-tooltip="Show/hide inactive" aria-label="Show/hide inactive">
              <span class="material-icons">local_shipping</span>
              <span class="toggle-label">Show Inactive</span>
            </button>
          </div>
          <div class="roster-list" id="list-trucks">
            <div class="loading-state"><div class="loading-spinner"></div><p>Loading vehicles...</p></div>
          </div>
        </div>

        <!-- Trailers Tab -->
        <div class="roster-panel" id="panel-trailers">
          <div class="panel-header">
            <h2>Trailers</h2>
            <div class="panel-header-actions">
              <!-- TASK-4876 S2 — the fleet permit checklist, also reachable where the
                   axle data is typed (it used to live only on the Rigs tab). -->
              <button class="btn btn-secondary" onclick="window.RosterModule.showPermitReadiness()" title="Which vehicles and trailers still need permit specs">
                <span class="material-icons">fact_check</span> Permit Readiness
              </button>
              <!-- TASK-9716 — read the shop whiteboard into TASK-9715's spec request (approval card). -->
              <button class="btn btn-secondary ai-entry" onclick="window.FleetPhotoIntake && window.FleetPhotoIntake.open({ surface: 'roster' })" title="Snap the shop whiteboard; you approve every change">
                <span class="material-icons">photo_camera</span> Update from photo
              </button>
              <button class="btn btn-primary" onclick="window.RosterModule.showAddTrailer()">
                <span class="material-icons">add</span> Add Trailer
              </button>
            </div>
          </div>
          <div class="roster-filter-bar">
            <div class="roster-search">
              <span class="material-icons">search</span>
              <input type="search" id="search-trailers" placeholder="Search trailers or VIN..." autocomplete="off" oninput="window.RosterModule.filterTrailers()">
            </div>
            <select id="filter-trailers-type" onchange="window.RosterModule.filterTrailers()">
            </select>
            <select id="filter-trailers-yard" onchange="window.RosterModule.filterTrailers()" style="display:none;">
            </select>
            <select id="sort-trailers" onchange="window.RosterModule.filterTrailers()">
              <option value="unit">Sort: Unit #</option>
              <option value="name">Sort: Name A-Z</option>
            </select>
            <button id="toggle-inactive-trailers" class="btn-toggle-inactive" onclick="window.RosterModule.toggleInactive('trailers')" data-tooltip="Show/hide inactive" aria-label="Show/hide inactive">
              <span class="material-icons">rv_hookup</span>
              <span class="toggle-label">Show Inactive</span>
            </button>
          </div>
          <div class="roster-list" id="list-trailers">
            <div class="loading-state"><div class="loading-spinner"></div><p>Loading trailers...</p></div>
          </div>
        </div>

        <!-- Assets Tab -->
        <div class="roster-panel" id="panel-assets">
          <div class="panel-header">
            <h2>Assets/Inventory</h2>
            <div class="panel-header-actions">
              <!-- TASK-9369 — "Add Branch" = add a yard (settings.yards) with type
                   preselected. Shown only in Branches mode, or always while the
                   tenant has zero yards (so the first branch can be made here).
                   Two primary buttons overflow 375px (COD UX), so it is secondary. -->
              <button class="btn btn-secondary" id="btn-add-branch" onclick="window.RosterModule.addBranchFromAssets()" style="display:none;" title="Add a branch (a yard tagged Branch)">
                <span class="material-icons">add_business</span> Add Branch
              </button>
              <!-- TASK-9723 — receive a delivery of counted (stock-tracked)
                   items in one form. Shown only when at least one item is counted. -->
              <button class="btn btn-secondary" id="btn-receive-stock" onclick="window.RosterModule.openFastReceive()" style="display:none;" title="Receive a delivery of counted supplies">
                <span class="material-icons">move_to_inbox</span> Receive<span class="receive-low-count"></span>
              </button>
              <button class="btn btn-primary" onclick="window.RosterModule.showAddAsset()">
                <span class="material-icons">add</span> Add Asset
              </button>
            </div>
          </div>
          <div class="roster-filter-bar">
            <div class="roster-search">
              <span class="material-icons">search</span>
              <input type="search" id="search-assets" placeholder="Search assets..." autocomplete="off" oninput="window.RosterModule.filterAssets()">
            </div>
            <!-- TASK-9369 — Items | Branches segmented control. Hidden until the
                 tenant has ≥1 yard (same gate as #filter-assets-yard, wired in
                 refreshYardFilters). Grouping is by branch / yard: one card per
                 settings.yards entry. -->
            <div id="assets-groupby" role="group" aria-label="Group assets by" style="display:none;align-items:stretch;border:1px solid var(--border-medium, #CBD5E1);border-radius:var(--radius-md, 8px);overflow:hidden;flex-shrink:0;">
              <button type="button" id="assets-groupby-items" class="btn-toggle-inactive active" style="border:0;border-radius:0;" onclick="window.RosterModule.setAssetsGroupBy('items')" title="Every asset as its own card">
                <span class="material-icons">grid_view</span><span class="toggle-label">Items</span>
              </button>
              <button type="button" id="assets-groupby-branches" class="btn-toggle-inactive" style="border:0;border-radius:0;border-left:1px solid var(--border-medium, #CBD5E1);" onclick="window.RosterModule.setAssetsGroupBy('branches')" title="One card per branch / yard with totals and % out">
                <span class="material-icons">business</span><span class="toggle-label">Branches</span>
              </button>
            </div>
            <!-- TASK-4800 S3 — "On Site": which jobs hold your rental equipment
                 and for how long, oldest (stranded) first. A VIEW switch, so it
                 sits beside Items | Branches (COD UX lane) — but its own button,
                 because that control is hidden on a zero-yard tenant and a
                 tenant with mats and no yards still needs this. Shown once the
                 tenant has a rental asset (_syncAssetsOnSiteUi). -->
            <button type="button" id="toggle-assets-onsite" class="btn-toggle-inactive" style="display:none;flex-shrink:0;" onclick="window.RosterModule.toggleAssetsOnSite()" aria-pressed="false" title="Where your rental equipment is sitting, and for how long — oldest first">
              <span class="material-icons">pin_drop</span>
              <span class="toggle-label">On Site</span>
            </button>
            <select id="filter-assets-category" onchange="window.RosterModule.filterAssets()">
            </select>
            <select id="filter-assets-yard" onchange="window.RosterModule.filterAssets()" style="display:none;">
            </select>
            <button id="toggle-inactive-assets" class="btn-toggle-inactive" onclick="window.RosterModule.toggleInactive('assets')" data-tooltip="Show/hide inactive" aria-label="Show/hide inactive">
              <span class="material-icons">inventory_2</span>
              <span class="toggle-label">Show Inactive</span>
            </button>
          </div>
          <div class="roster-list asset-grid" id="list-assets">
            <div class="loading-state"><div class="loading-spinner"></div><p>Loading assets...</p></div>
          </div>
        </div>

        <!-- Rigs Tab (TASK-1443) — saved tractor+trailer(s) combinations. The
             coupling gap lives on the COMBINATION, never on a vehicle. -->
        <div class="roster-panel" id="panel-rigs">
          <div class="panel-header">
            <h2>Rigs</h2>
            <div class="panel-header-actions">
              <!-- TASK-1444 — the fleet gap checklist. Lives here because a rig is
                   only as good as the axle tables on the units under it. -->
              <button class="btn btn-secondary" onclick="window.RosterModule.showPermitReadiness()" title="Which vehicles and trailers still need permit specs">
                <span class="material-icons">fact_check</span> Permit Readiness
              </button>
              <button class="btn btn-primary" id="btn-add-rig" onclick="window.RosterModule.openRigEditor()">
                <span class="material-icons">add</span> Add Rig
              </button>
            </div>
          </div>
          <div class="roster-filter-bar">
            <div class="roster-search">
              <span class="material-icons">search</span>
              <input type="search" id="search-rigs" placeholder="Search rigs..." autocomplete="off" oninput="window.RosterModule.filterRigs()">
            </div>
          </div>
          <div class="roster-list" id="list-rigs">
            <div class="loading-state"><div class="loading-spinner"></div><p>Loading rigs...</p></div>
          </div>
        </div>
      </div>
    </div>

    <!-- Order Management Overlay -->
    <div class="order-management-overlay hidden" id="order-management-overlay">
      <div class="order-management-view">
        <div class="order-management-header">
          <h2><span class="material-icons">factory</span> Order Management</h2>
          <button class="btn-icon" onclick="window.RosterModule.closeOrderManagement()">
            <span class="material-icons">close</span>
          </button>
        </div>
        <div class="order-management-toolbar">
          <button class="btn btn-secondary" onclick="window.RosterModule.showCustomerList()">
            <span class="material-icons">business</span> Customers
          </button>
          <button class="btn btn-primary" onclick="window.RosterModule.showGenerateOrderLink()">
            <span class="material-icons">link</span> Generate Request Link
          </button>
        </div>
        <div class="order-management-filters">
          <h3>Orders</h3>
          <div class="roster-filter-bar">
            <div class="roster-search">
              <span class="material-icons">search</span>
              <input type="search" id="search-orders" placeholder="Search orders..." autocomplete="off" oninput="window.RosterModule.filterOrders()">
            </div>
            <select id="filter-orders-customer" onchange="window.RosterModule.filterOrders()">
              <option value="all">All Customers</option>
            </select>
            <select id="filter-orders-status" onchange="window.RosterModule.filterOrders()">
              <option value="all">All Statuses</option>
              <option value="pending">Pending</option>
              <option value="pick">Pick</option>
              <option value="prep">Prep</option>
              <option value="load">Load</option>
              <option value="shipping">Shipping</option>
              <option value="delivered">Delivered</option>
            </select>
          </div>
        </div>
        <div class="orders-list" id="list-orders">
          <div class="empty-state">
            <div class="empty-state-icon">📋</div>
            <div class="empty-state-title">No orders yet</div>
            <div class="empty-state-text">Generate a request link and share it with customers.</div>
          </div>
        </div>
      </div>
    </div>
  `;
}

// ============================================
// MODAL HTML (mounted to document.body so Compliance module can reuse)
// ============================================
function getRosterModalsHTML() {
  return `
    <!-- Add/Edit Person Modal -->
    <div class="modal hidden" id="modal-person">
      <div class="modal-content modal-form-wide">
        <div class="modal-header">
          <h3 id="modal-person-title">Add Person</h3>
          <button class="btn-icon" onclick="window.RosterModule.closeModal('person')">
            <span class="material-icons">close</span>
          </button>
        </div>
        <form id="form-person" class="modal-form-scrollable" novalidate onsubmit="window.RosterModule.savePerson(event)">
          <div class="modal-form-scroll">
          <!-- TASK-9686 — the Edit Person modal is one long scroll on a phone; every
               topic is an always-open titled section (COD: no accordions — a closed
               section would skip validateRequiredFields and hide the Driver Card PII
               note). These links only scroll; section visibility is never access control. -->
          <nav class="person-section-jump" id="person-section-jump" aria-label="Jump to section">
            <button type="button" class="person-section-jump-link" data-person-jump="contact" onclick="window.RosterModule.jumpToPersonSection('contact')">Contact</button>
            <button type="button" class="person-section-jump-link" data-person-jump="notifications" onclick="window.RosterModule.jumpToPersonSection('notifications')">Alerts</button>
            <button type="button" class="person-section-jump-link" data-person-jump="access" onclick="window.RosterModule.jumpToPersonSection('access')">Login</button>
            <button type="button" class="person-section-jump-link" data-person-jump="license" onclick="window.RosterModule.jumpToPersonSection('license')">License</button>
            <button type="button" class="person-section-jump-link" data-person-jump="employment" onclick="window.RosterModule.jumpToPersonSection('employment')">Status</button>
          </nav>

          <section class="person-section" id="person-section-contact" data-person-section="contact" aria-labelledby="person-section-contact-title">
            <div class="person-section-header">
              <span class="material-icons" aria-hidden="true">contact_phone</span>
              <h4 class="person-section-title" id="person-section-contact-title" tabindex="-1">Contact</h4>
            </div>
          <input type="hidden" id="person-id">
          <div class="form-group">
            <label for="person-name">Name *</label>
            <input type="text" id="person-name" required placeholder="John Smith" data-maxlen="100">
          </div>
          <!-- TASK-9682 — crew nicknames. Shown by _syncPersonAliasesField (Roster module or owner). -->
          <div class="form-group" id="person-aliases-group" style="display:none;">
            <label for="person-aliases">Also goes by <span class="form-hint-inline">(optional)</span></label>
            <input type="text" id="person-aliases" placeholder="Paddy, Patty" maxlength="440" autocomplete="off">
            <p class="form-hint">Nicknames the crew uses in texts, separated by commas (up to 10). Used by search, text dispatch and the AI assistant. Visible to all crew, so nothing private (no phone numbers or emails).</p>
          </div>
          <!-- TASK-340 Slice B — profile photo. NOT the driver's license image
               (that's compliance-only). Renders as the roster card avatar in
               place of colored initials. -->
          <div class="form-group">
            <label>Profile Photo <span class="form-hint-inline">(optional — shows on the roster card)</span></label>
            <div class="asset-photo-upload" style="display:flex;align-items:center;gap:8px;flex-wrap:wrap;">
              <input type="file" id="person-photo-input" accept="image/*,.heic,.heif" style="display:none"
                onchange="window.RosterModule.handlePersonPhoto(event)">
              <div id="person-photo-preview" class="asset-photo-preview hidden"></div>
              <button type="button" class="btn btn-secondary btn-sm"
                onclick="document.getElementById('person-photo-input').click()" style="white-space:nowrap;">
                <span class="material-icons" style="font-size:16px;">add_a_photo</span> Photo
              </button>
            </div>
          </div>
          <div class="form-group">
            <label for="person-email">Email</label>
            <input type="email" id="person-email" placeholder="john@example.com">
          </div>
          <div class="form-group" id="person-personal-email-group" style="display:none;">
            <label for="person-personal-email">Personal Email</label>
            <input type="email" id="person-personal-email" placeholder="personal@gmail.com">
          </div>
          <div class="form-group">
            <label for="person-phone">Phone</label>
            <input type="tel" id="person-phone" placeholder="303-555-1234">
            <div id="person-phone-preview" class="phone-preview"></div>
            <!-- TASK-4728 — shared-phone advisory. Populated by
                 _syncPersonPhoneCollisionWarning() as the number is typed, so a
                 duplicate is caught at data entry instead of at the gate. It
                 NEVER blocks the save (a shared handset is legitimate). -->
            <div id="person-phone-shared-warning" role="status" style="display:none;"></div>
          </div>
          </section><!-- end person-section-contact -->

          <section class="person-section" id="person-section-job" data-person-section="job" aria-labelledby="person-section-job-title">
            <div class="person-section-header">
              <span class="material-icons" aria-hidden="true">work</span>
              <h4 class="person-section-title" id="person-section-job-title" tabindex="-1">Job &amp; Pay</h4>
            </div>
          <!-- Role dropdown (moved up, right after phone) -->
          <div id="person-role-select-group" class="form-group" style="display:none;">
            <label for="person-role-select">Role *</label>
            <div style="display:flex;gap:8px;align-items:center;">
              <select id="person-role-select" class="form-control" style="flex:1;">
                <option value="">Select role...</option>
              </select>
              <button type="button" class="btn btn-secondary btn-sm" id="btn-add-role"
                onclick="window.RosterModule.showAddTypeInline('crewTypes', 'person-role-select')" title="Add custom role"
                style="padding:6px 10px;">
                <span class="material-icons" style="font-size:18px;">add</span>
              </button>
            </div>
            <p class="form-hint">Used for dispatch assignment, billing rate lookup, and compliance.</p>
          </div>

          <!-- TASK-1476 — the ONE reconciliation prompt (COD UX condition:
               "unify UI controls; do not expose backend duality"). Rendered
               only when this person's HR job title and their crew type describe
               different kinds of work. Read-only: it states the disagreement
               and what it is steering, and offers NO auto-fix button — deriving
               one field from the other was rejected by COD because it silently
               reprices customer lines and changes who is dispatchable. -->
          <div id="person-role-conflict-notice" style="display:none;"></div>

          <!-- TASK-1122 — compliance classification at creation. Optional; a chosen
               value is applied AFTER the person save via the setDriverClassification
               callable (positionType is SERVER-OWNED, firestore.rules TASK-934 —
               it must never ride the person updateDoc/addDoc). -->
          <div class="form-group" id="person-position-type-group">
            <label for="person-position-type">Compliance Classification</label>
            <select id="person-position-type" class="form-control">
              <option value="">Not classified yet</option>
              <option value="cdl">CDL Driver</option>
              <option value="non_cdl_driver">Non-CDL Driver (box truck / hot-shot 10,001–26,000 lb — med card + driver file, no CDL)</option>
              <option value="drives_no_cmv">Drives, no CMV (pickup / car, 10,000 lb or less, no placarded hazmat, under 9 seats — no DOT driver file)</option>
              <option value="non_driver">Non-Driver (office/shop)</option>
            </select>
            <p class="form-hint">Controls which DOT driver-qualification items apply. Until classified, the full CDL checklist applies.</p>
          </div>

          <!-- Hidden legacy groups (unchanged) -->
          <div id="person-roles-group" class="form-group" style="display:none;">
            <label>Roles * <span class="form-hint-inline">(select at least one)</span></label>
            <div class="checkbox-grid role-checkboxes" id="person-roles">
              <label class="checkbox-label"><input type="checkbox" value="driver" id="role-driver"> Driver</label>
              <label class="checkbox-label"><input type="checkbox" value="laborer" id="role-laborer"> Laborer</label>
              <label class="checkbox-label"><input type="checkbox" value="mechanic" id="role-mechanic"> Mechanic</label>
              <label class="checkbox-label"><input type="checkbox" value="dispatcher" id="role-dispatcher"> Dispatcher</label>
              <label class="checkbox-label"><input type="checkbox" value="admin" id="role-admin"> Admin</label>
            </div>
          </div>

          <div id="crew-type-group" class="form-group" style="display:none;">
            <label for="person-crew-type">Crew Type</label>
            <div style="display:flex;gap:8px;align-items:center;">
              <select id="person-crew-type" class="form-control" style="flex:1;">
                <option value="">None</option>
              </select>
              <button type="button" class="btn btn-secondary btn-sm" id="btn-add-crew-type" onclick="window.RosterModule.showAddTypeInline('crewTypes', 'person-crew-type')" title="Add custom crew type" style="padding:6px 10px;">
                <span class="material-icons" style="font-size:18px;">add</span>
              </button>
            </div>
            <p class="form-hint">Used for billing rate lookup when building tickets from clock entries.</p>
          </div>

          <div id="person-cdl-group" class="form-group" style="display:none;">
            <!-- CDL auto-derived from crewType (cdl_driver) — no manual checkbox needed -->
          </div>

          <div id="person-cost-rate-group" class="form-group" style="display:none;">
            <label for="person-cost-rate">Hourly Wage ($)</label>
            <input type="number" id="person-cost-rate" step="0.01" min="0" placeholder="0.00" class="form-control" style="max-width:160px;">
            <p class="form-hint">What you pay this person per hour. Used in BI Profitability to calculate labor costs.</p>
          </div>

          <!-- Yard / Location -->
          <div class="form-group" id="person-yard-group">
            <label for="person-yard">Yard / Location</label>
            <div style="display:flex;gap:8px;align-items:center;">
              <select id="person-yard" class="form-control" style="flex:1;"></select>
              <button type="button" class="btn-add-type" onclick="window.RosterModule.addYardInline('person-yard')" title="Add yard">
                <span class="material-icons" style="font-size:18px;">add</span>
              </button>
            </div>
          </div>

          <!-- TASK-607 — "Reports to" (direct supervisor) moved to the HR
               employee Compliance editor (next to Supervisor Role). It's an
               org-hierarchy/HR concept, not an operational roster field. The
               value still persists on people/{pid}.reportsTo — it's just
               edited from HR now, not here. -->
          </section><!-- end person-section-job -->

          <section class="person-section" id="person-section-notifications" data-person-section="notifications" aria-labelledby="person-section-notifications-title">
            <div class="person-section-header">
              <span class="material-icons" aria-hidden="true">notifications</span>
              <h4 class="person-section-title" id="person-section-notifications-title" tabindex="-1">Notifications</h4>
            </div>
          <div class="form-group" id="person-notify-tags"></div>

          <div class="form-group radio-group" id="person-preferred-channel">
            <label style="font-weight:600;margin-bottom:4px;display:block;font-size:0.85rem;color:var(--text-secondary);">Preferred Channel</label>
            <label class="radio-label"><input type="radio" name="preferred-channel" value="email"> Email</label>
            <label class="radio-label"><input type="radio" name="preferred-channel" value="sms"> SMS</label>
            <label class="radio-label"><input type="radio" name="preferred-channel" value="both" checked> Both</label>
          </div>

          <div class="form-group sms-optin-group" style="margin-top: 12px;">
            <label class="checkbox-switch">
              <input type="checkbox" id="person-sms-optin">
              <span class="switch-slider"></span>
              <span class="switch-label">SMS Notifications</span>
            </label>
            <p class="form-hint">Allow dispatch alerts and signature requests via text message</p>
            <div id="person-sms-stop-warning" style="display:none;margin-top:8px;padding:8px 10px;background:#fef2f2;border:1px solid #fecaca;border-radius:6px;font-size:0.8rem;color:#b91c1c;line-height:1.4;">
              <strong>⛔ Opted out of SMS (texted STOP).</strong>
              <span id="person-sms-stop-detail"></span>
              By law (TCPA) we can't text this person — reach them by phone or email. SMS can't be re-enabled here; they must text START from their phone.
            </div>
            <div id="person-sms-reply-status" style="display:none;margin-top:8px;font-size:0.8rem;color:var(--text-secondary);line-height:1.4;"></div>
          </div>

          <div class="mute-notifications-box">
            <label class="checkbox-switch">
              <input type="checkbox" id="person-muted">
              <span class="switch-slider switch-slider-danger"></span>
              <span class="switch-label">Mute All Notifications</span>
            </label>
            <p class="mute-hint">Stops ALL notifications for this person</p>
          </div>
          </section><!-- end person-section-notifications -->

          <section class="person-section" id="person-section-access" data-person-section="access" aria-labelledby="person-section-access-title">
            <div class="person-section-header">
              <span class="material-icons" aria-hidden="true">vpn_key</span>
              <h4 class="person-section-title" id="person-section-access-title" tabindex="-1">Driver Login &amp; Access</h4>
            </div>

          <!-- Driver Code -->
          <div class="form-section-header">
            <h4>Driver Code</h4>
          </div>
          <div class="form-group">
            <div id="person-driver-pin" class="driver-pin-display"></div>
            <p class="form-hint">Drivers enter this code on the login page to view their schedule.</p>
          </div>

          <!-- DVIR Signature (founder-only) -->
          <div id="person-signature-section" style="display:none;">
            <div class="form-section-header">
              <h4>DVIR Signature</h4>
            </div>
            <div class="form-group">
              <div id="person-signature-status"></div>
              <button type="button" class="btn btn-secondary" id="btn-request-signature" onclick="window.RosterModule.showSignatureRequestModal()">
                <span class="material-icons">draw</span> Request Signature
              </button>
            </div>
          </div>

          <!-- Staff Login (TASK-319 slice 5) -->
          <div class="form-section-header" id="person-staff-login-section" style="display:none;">
            <h4>Staff Login</h4>
          </div>
          <div class="form-group" id="person-staff-login-group" style="display:none;">
            <div id="person-staff-login-status" class="staff-login-status"></div>
            <button type="button" class="btn btn-secondary" id="btn-setup-staff-login" onclick="window.RosterModule.showSetupStaffLoginModal()">
              <span class="material-icons">person_add</span> Set up login
            </button>
            <p class="form-hint">Send this person an invite to create their own email + password login with module-level access.</p>
          </div>

          <!-- Show All Jobs toggle (driver PIN visibility) -->
          <div class="form-group sms-optin-group">
            <label class="checkbox-switch">
              <input type="checkbox" id="person-show-all-jobs" checked>
              <span class="switch-slider"></span>
              <span class="switch-label">Show all jobs on driver board</span>
            </label>
            <p class="form-hint">When off, this person only sees jobs they're assigned to when using Driver PIN. Use for 3rd party drivers or contractors.</p>
          </div>

          <!-- Allow Maintenance Access (driver PIN) -->
          <div class="form-group sms-optin-group">
            <label class="checkbox-switch">
              <input type="checkbox" id="person-allow-maintenance">
              <span class="switch-slider"></span>
              <span class="switch-label">Allow maintenance access</span>
            </label>
            <p class="form-hint">When on, this person can view and create maintenance tasks via Driver PIN login</p>
          </div>

          <!-- TASK-9710 — per-driver OFF switch for driver Add/Edit Job. Shown only
               while Dispatch → Settings → "Drivers can add and edit jobs" is on.
               Stored on the TENANT settings (driverJobEdit.blockedPersonIds), never
               on the people doc: handing out job-write power is a Dispatch call,
               so only a Dispatch-module holder can flip it; others see it read-only. -->
          <div class="form-group sms-optin-group" id="person-driver-job-edit-group" style="display:none;">
            <label class="checkbox-switch">
              <input type="checkbox" id="person-driver-job-edit" checked>
              <span class="switch-slider"></span>
              <span class="switch-label">Can add and edit jobs from the driver board</span>
            </label>
            <p class="form-hint" id="person-driver-job-edit-hint">Your company lets drivers add and edit jobs. Turn this off to keep this person to just driving.</p>
          </div>

          <!-- TASK-10039 — per-person grant: edit / delete their own tickets after
               clock-out on the driver timesheet. Off by default. Shown only to a
               Billing holder or the owner; saved by the setTicketEditGrant callable
               (server-only grant doc + audit row), never on the people doc. -->
          <div class="form-group sms-optin-group" id="person-ticket-edit-group" style="display:none;">
            <label class="checkbox-switch">
              <input type="checkbox" id="person-ticket-edit" disabled>
              <span class="switch-slider"></span>
              <span class="switch-label">Can edit their own tickets after clock-out</span>
            </label>
            <p class="form-hint" id="person-ticket-edit-hint">When off, this person builds their ticket at clock-out, then only the office can change it.</p>
          </div>

          <!-- Show on Driver Card — team contact directory (TASK-459) -->
          <div class="form-group sms-optin-group">
            <label class="checkbox-switch">
              <input type="checkbox" id="person-show-on-driver-card">
              <span class="switch-slider"></span>
              <span class="switch-label">Show on Driver Card</span>
            </label>
            <p class="form-hint">When on, this person's name, title, phone &amp; email appear in the driver-side Team contact directory (visible to all PIN drivers). Requires the Team directory to be enabled in Roster settings.</p>
            <!-- TASK-4846 — a pre-checked PII-publish box must ANNOUNCE itself, not
                 hide behind the hint text (COD Adversary lane). Shown only by
                 showAddPerson() when it auto-checks the box, and cleared the moment
                 the user touches the switch. Never shown on the edit path. -->
            <p class="form-hint" id="person-show-on-driver-card-default-note" style="display:none;color:var(--brand-blue-dark);font-weight:600;">On by default because your Team Directory is turned on. Uncheck it to keep this person's phone &amp; email off every driver's phone.</p>
          </div>
          </section><!-- end person-section-access -->

          <section class="person-section" id="person-section-license" data-person-section="license" aria-labelledby="person-section-license-title">
            <div class="person-section-header">
              <span class="material-icons" aria-hidden="true">badge</span>
              <h4 class="person-section-title" id="person-section-license-title" tabindex="-1">License &amp; DQ</h4>
            </div>

          <!-- TASK-812 follow-on — Driver Qualification (DL & CDL) at creation.
               Mirrors the Compliance editDriverDetails / HR saveComplianceFields
               edit forms EXACTLY: same person-doc keys (dob, dlState, dlNumber,
               dlExpiryDate, cdlClass, cdlEndorsements, cdlRestrictions [TASK-9300 (5)],
               cdlIssueDate, cdlExpiryDate), same labels/placeholders. Lets a person be added
               via Roster OR Compliance "Add Person" and be immediately MVR/DQ-
               ready instead of forcing a second Edit pass to type DL/MVR data.
               All optional; native inputs reset via form.reset() on add. -->
          <details id="person-dq-section" class="form-collapsible">
            <summary class="form-collapsible-header">
              <span class="material-icons">badge</span>
              <span>Driver Qualification <span style="color:var(--text-muted);font-weight:400;font-size:0.78rem;">DL &amp; CDL — optional</span></span>
              <span class="material-icons collapse-icon">expand_more</span>
            </summary>
            <div class="form-collapsible-content">
              <div class="form-group">
                <label for="person-dq-dob">Date of Birth</label>
                <input type="date" id="person-dq-dob" class="form-control">
              </div>
              <div class="form-group">
                <label for="person-dq-dl-state">DL State</label>
                <input type="text" id="person-dq-dl-state" class="form-control" maxlength="2" placeholder="ND" style="text-transform:uppercase;">
              </div>
              <div class="form-group">
                <label for="person-dq-dl-number">DL Number</label>
                <input type="text" id="person-dq-dl-number" class="form-control" placeholder="D123456789">
              </div>
              <div class="form-group">
                <label for="person-dq-dl-expiry">DL Expiry Date</label>
                <input type="date" id="person-dq-dl-expiry" class="form-control">
              </div>
              <div class="form-group">
                <label for="person-dq-cdl-class">CDL Class</label>
                <select id="person-dq-cdl-class" class="form-control">
                  <option value="">None</option>
                  <option value="A">Class A</option>
                  <option value="B">Class B</option>
                  <option value="C">Class C</option>
                </select>
              </div>
              <div class="form-group">
                <label for="person-dq-cdl-endorsements">Endorsements</label>
                <input type="text" id="person-dq-cdl-endorsements" class="form-control" placeholder="H, N, T, X">
              </div>
              <div class="form-group">
                <label for="person-dq-cdl-restrictions">Restrictions</label>
                <input type="text" id="person-dq-cdl-restrictions" class="form-control" placeholder="e.g. L, Z">
              </div>
              <div class="form-group">
                <label for="person-dq-cdl-issue">CDL Issue Date</label>
                <input type="date" id="person-dq-cdl-issue" class="form-control">
              </div>
              <div class="form-group">
                <label for="person-dq-cdl-expiry">CDL Expiry Date</label>
                <input type="date" id="person-dq-cdl-expiry" class="form-control">
              </div>
              <p class="form-hint">Needed to run an MVR and build the driver's qualification (DQ) file. Editable later from the Compliance or HR person view.</p>
            </div>
          </details>

          <!-- TASK-258 §396.19 Inspector Qualifications mini-section. Tracks
               in-house annual-vehicle / brake inspector credentials per the
               FMCSA retention rule (employer must retain proof for employment + 1yr).
               Doc upload is a follow-on; v1 captures the metadata so the cert
               type + training date are queryable for audit prep. -->
          <details id="person-inspector-qual-section" class="form-collapsible">
            <summary class="form-collapsible-header">
              <span class="material-icons">verified_user</span>
              <span>Inspector Qualifications <span style="color:var(--text-muted);font-weight:400;font-size:0.78rem;">§396.19 — optional</span></span>
              <span class="material-icons collapse-icon">expand_more</span>
            </summary>
            <div class="form-collapsible-content">
              <div class="form-group">
                <label for="person-inspector-cert-type">Certification Type</label>
                <select id="person-inspector-cert-type" class="form-control">
                  <option value="">— None / not an inspector —</option>
                  <option value="annual_396_17">Annual Vehicle Inspector (§396.17)</option>
                  <option value="brake_inspector_396_25">Brake Inspector (§396.25)</option>
                  <option value="both">Both Annual + Brake</option>
                  <option value="state_certified">State-Certified (state-issued)</option>
                  <option value="other">Other (note in field below)</option>
                </select>
                <p class="form-hint">Federal motor carrier rule §396.19 — annual-inspector and brake-inspector qualifications must be on file at the employer. Retention = employment + 1 year.</p>
              </div>
              <div class="form-group">
                <label for="person-inspector-training-date">Training / Qualification Date</label>
                <input type="date" id="person-inspector-training-date" class="form-control">
              </div>
              <div class="form-group">
                <label for="person-inspector-notes">Notes <span style="color:var(--text-muted);font-weight:400;font-size:0.78rem;">(cert number, state, training provider)</span></label>
                <input type="text" id="person-inspector-notes" class="form-control" maxlength="200" placeholder="e.g. ATA Inspector Course, Cert #ABC-12345">
              </div>
              <div class="form-group">
                <label>Cert / Training Document <span style="color:var(--text-muted);font-weight:400;font-size:0.78rem;">(PDF or photo, max 10MB)</span></label>
                <div id="person-inspector-doc-existing" style="display:none;font-size:0.85rem;margin-bottom:6px;">
                  <a id="person-inspector-doc-link" href="#" target="_blank" rel="noopener" style="color:var(--brand-blue);text-decoration:none;">
                    <span class="material-icons" style="font-size:16px;vertical-align:middle;">description</span>
                    <span id="person-inspector-doc-name">View on file</span>
                  </a>
                  <button type="button" id="person-inspector-doc-remove" class="btn-link" style="margin-left:8px;color:#dc2626;font-size:0.8rem;" onclick="window.RosterModule._clearInspectorDoc()">Remove</button>
                </div>
                <input type="file" id="person-inspector-doc-file" accept="application/pdf,image/*" class="form-control">
                <p class="form-hint">Per §396.19 retention = employment + 1 year. Optional but recommended for audit prep.</p>
              </div>
            </div>
          </details>

          <!-- Custom Reminders TASK-262 Slice 1B. Populated by editPerson
               or showAddPerson based on tenant.compliance.customReminders
               from Slice 1A. Hidden when no active items. Date inputs
               carry data-custom-reminder-id. Backticks removed - same v1.15.518
               trap (HTML comment inside JS template literal). -->
          <!-- TASK-9844 / TASK-9911 M2 — Skills and certifications. Populated by
               _populatePersonSkills; hidden while the company keeps neither. -->
          <details id="person-skills-section" class="form-collapsible" style="display:none;" open>
            <summary class="form-collapsible-header">
              <span class="material-icons">workspace_premium</span>
              <span>Skills &amp; certifications <span id="person-skills-count" style="color:var(--text-secondary);font-weight:400;"></span></span>
              <span class="material-icons collapse-icon">expand_more</span>
            </summary>
            <div class="form-collapsible-content" id="person-skills-fields"></div>
          </details>

          <details id="person-custom-reminders-section" class="form-collapsible" style="display:none;" open>
            <summary class="form-collapsible-header">
              <span class="material-icons">event_available</span>
              <span>Custom Reminders <span id="person-custom-reminders-count" style="color:var(--text-muted);font-weight:400;"></span></span>
              <span class="material-icons collapse-icon">expand_more</span>
            </summary>
            <div class="form-collapsible-content" id="person-custom-reminders-fields">
              <!-- date inputs injected dynamically -->
            </div>
          </details>
          </section><!-- end person-section-license -->

          <section class="person-section" id="person-section-employment" data-person-section="employment" aria-labelledby="person-section-employment-title">
            <div class="person-section-header">
              <span class="material-icons" aria-hidden="true">work_history</span>
              <h4 class="person-section-title" id="person-section-employment-title" tabindex="-1">Employment Status</h4>
            </div>

          <!-- Active / Inactive toggle (controls showInDispatch too) -->
          <div class="form-group sms-optin-group">
            <label class="checkbox-switch">
              <input type="checkbox" id="person-active" checked>
              <span class="switch-slider"></span>
              <span class="switch-label">Active</span>
            </label>
            <p class="form-hint">Inactive people won't appear in dispatch, DVIR forms, job assignments, or notifications. Inactive = still employed, temporarily not working. Use No Longer Employed when employment ends.</p>
          </div>
          <!-- TASK-10183 — regular days off (weekly rotation); painted by rotationDays.js for existing people, hidden when this login can't see it -->
          <div class="form-group form-span-full" id="person-rotation" style="display:none;"></div>

          <!-- Terminated (founder-only) -->
          <div id="person-terminated-section" style="display:none;">
            <div class="form-group sms-optin-group">
              <label class="checkbox-switch">
                <input type="checkbox" id="person-terminated">
                <span class="switch-slider-danger"></span>
                <span class="switch-label">No Longer Employed</span>
              </label>
              <p class="form-hint">Mark former employees who resigned or were terminated</p>
            </div>
            <div id="person-terminated-fields" class="hidden" style="padding-left:8px;border-left:3px solid #ef4444;margin-top:-4px;">
              <div id="person-nle-leftovers" style="display:none;margin:4px 0 12px;"></div>
              <div class="form-group">
                <label for="person-terminated-date">Last Day</label>
                <input type="date" id="person-terminated-date" class="form-control">
              </div>
              <div class="form-group">
                <label for="person-terminated-reason">Reason</label>
                <input type="text" id="person-terminated-reason" class="form-control" placeholder="Resigned, Terminated, etc.">
              </div>

              <!--
                TASK-633 — FCRA adverse-action trigger. A termination decided
                "based in whole or in part on a consumer report" (background
                check, MVR, or PSP) is an adverse action under the federal Fair
                Credit Reporting Act: the driver is owed a Pre-Adverse notice, a
                wait window to dispute, then a Final notice — BEFORE the firing
                is final. Default UNCHECKED = today's immediate one-click
                termination (resignation, no-show, layoff, misconduct unrelated
                to a report). CHECKED holds the termination and starts the FCRA
                sequence.
              -->
              <div class="form-group" id="person-term-fcra-group" style="margin-top:6px;padding:10px 12px;background:#FFF7ED;border:1px solid #FED7AA;border-radius:8px;">
                <label class="checkbox-switch" style="display:flex;align-items:flex-start;gap:8px;">
                  <input type="checkbox" id="person-term-fcra">
                  <span style="font-size:0.85rem;color:#7C2D12;line-height:1.4;">
                    This termination is based <strong>in whole or in part</strong> on a background check, MVR, or PSP result
                  </span>
                </label>
                <p class="form-hint" style="color:#9A3412;margin:6px 0 0;">
                  Checking this starts the FCRA process: we email the driver the required Pre-Adverse Action notice and hold the termination open through the dispute window. The driver stays active until you complete it. Leave unchecked for resignations, no-shows, layoffs, or any reason not based on a report.
                </p>
                <div id="person-term-fcra-report-group" style="display:none;margin-top:8px;">
                  <label for="person-term-fcra-report" style="font-size:0.8rem;font-weight:600;color:#7C2D12;">Where is the report on file?</label>
                  <input type="text" id="person-term-fcra-report" class="form-control" placeholder="e.g. State MVR pulled 4/2026 — on file in DQ folder; or a link">
                  <p class="form-hint" style="color:#9A3412;margin:4px 0 0;">No background check is on file in DispatchDeck for this person. FCRA requires we provide a copy of the report — tell us where it lives so the notice can reference it.</p>
                </div>
              </div>

              <!-- TASK-633 — Pending-separation panel (shown only when an FCRA
                   termination hold is in flight for this person). -->
              <div id="person-pending-separation-panel" style="display:none;margin-top:10px;padding:12px 14px;background:#FEF3C7;border:1px solid #FDE68A;border-radius:8px;">
                <div style="font-weight:700;font-size:0.85rem;color:#78350F;display:flex;align-items:center;gap:6px;margin-bottom:6px;">
                  <span class="material-icons" style="font-size:18px;">hourglass_top</span> Termination on hold (FCRA)
                </div>
                <p id="person-pending-separation-msg" class="form-hint" style="color:#78350F;margin:0 0 10px;"></p>
                <div style="display:flex;gap:8px;flex-wrap:wrap;">
                  <button type="button" class="btn" id="btn-complete-separation" style="background:#DC2626;color:#fff;" onclick="window.RosterModule.completeSeparation()">Complete termination</button>
                  <button type="button" class="btn btn-secondary" id="btn-cancel-separation" onclick="window.RosterModule.cancelSeparation()">Cancel separation</button>
                </div>
                <label class="checkbox-switch" id="person-separation-override-wrap" style="display:none;align-items:flex-start;gap:8px;margin-top:10px;">
                  <input type="checkbox" id="person-separation-override">
                  <span style="font-size:0.8rem;color:#7C2D12;line-height:1.4;">Override the dispute-wait and finalize now — I attest the wait window is satisfied or waived (audit-logged).</span>
                </label>
              </div>

              <!--
                49 CFR 391.23(d) Safety Performance History questions.
                Required to retain for 3 years after the date of termination.
                When a future employer requests SPH on this driver, these
                answers form the response. Without them on file, the carrier
                can't legally respond to the inquiry.
              -->
              <div style="margin-top:14px;padding:10px 12px;background:#FEF3C7;border:1px solid #FDE68A;border-radius:8px;">
                <div style="font-weight:700;font-size:0.82rem;color:#78350F;margin-bottom:6px;display:flex;align-items:center;gap:6px;">
                  <span class="material-icons" style="font-size:16px;">gavel</span> Safety Performance History (49 CFR 391.23)
                </div>
                <p class="form-hint" style="color:#78350F;margin:0 0 10px;">
                  Required to keep on file for 3 years. Future employers can request these answers under FMCSA Safety Performance History rules.
                </p>

                <div class="form-group">
                  <label for="person-sph-voluntary">Termination Type</label>
                  <select id="person-sph-voluntary" class="form-control">
                    <option value="">Select…</option>
                    <option value="voluntary">Voluntary (driver resigned)</option>
                    <option value="involuntary">Involuntary (carrier-initiated)</option>
                    <option value="layoff">Layoff / position eliminated</option>
                    <option value="other">Other</option>
                  </select>
                </div>

                <div class="form-group">
                  <label for="person-sph-rehire">Eligible for rehire?</label>
                  <select id="person-sph-rehire" class="form-control">
                    <option value="">Select…</option>
                    <option value="yes">Yes</option>
                    <option value="no">No</option>
                    <option value="conditional">Conditional</option>
                  </select>
                </div>

                <div class="form-group">
                  <label for="person-sph-accidents">Any DOT-recordable accidents in their tenure here?</label>
                  <select id="person-sph-accidents" class="form-control" onchange="document.getElementById('person-sph-accidents-detail').style.display = this.value === 'yes' ? '' : 'none';">
                    <option value="">Select…</option>
                    <option value="no">No</option>
                    <option value="yes">Yes</option>
                  </select>
                  <textarea id="person-sph-accidents-detail" class="form-control" rows="2" placeholder="Date(s), location(s), fatalities or injuries (per 49 CFR 390.5 accident definition)" style="display:none;margin-top:6px;"></textarea>
                </div>

                <div class="form-group">
                  <label for="person-sph-drug-alcohol">Any drug/alcohol violations under 49 CFR Part 382 / Part 40?</label>
                  <select id="person-sph-drug-alcohol" class="form-control" onchange="document.getElementById('person-sph-drug-alcohol-detail').style.display = this.value === 'yes' ? '' : 'none';">
                    <option value="">Select…</option>
                    <option value="no">No</option>
                    <option value="yes">Yes</option>
                  </select>
                  <textarea id="person-sph-drug-alcohol-detail" class="form-control" rows="2" placeholder="Type of violation, date, whether SAP/return-to-duty process was completed" style="display:none;margin-top:6px;"></textarea>
                </div>

                <div class="form-group">
                  <label for="person-sph-rtd-completed">If a violation occurred, was the SAP / return-to-duty process completed?</label>
                  <select id="person-sph-rtd-completed" class="form-control">
                    <option value="">N/A</option>
                    <option value="yes">Yes — completed</option>
                    <option value="no">No — not completed</option>
                    <option value="in_progress">In progress</option>
                  </select>
                </div>

                <div class="form-group">
                  <label for="person-sph-performance">Overall performance summary (optional)</label>
                  <textarea id="person-sph-performance" class="form-control" rows="2" placeholder="Brief notes for the future-employer SPH response."></textarea>
                </div>
              </div>
            </div>
          </div>
          </section><!-- end person-section-employment -->

          <section class="person-section" id="person-section-reminders" data-person-section="reminders" aria-labelledby="person-section-reminders-title">
            <div class="person-section-header">
              <span class="material-icons" aria-hidden="true">alarm</span>
              <h4 class="person-section-title" id="person-section-reminders-title" tabindex="-1">My Reminders</h4>
            </div>
          <!-- TASK-1550 — the signed-in user's own reminders on this person (existing people only) -->
          <div class="form-group form-span-full" id="person-reminders"></div>
          </section><!-- end person-section-reminders -->
          </div><!-- end modal-form-scroll -->

          <div class="modal-form-footer">
            <span id="person-viewonly-note" role="note" style="display:none;flex:1 1 auto;min-width:0;align-self:center;font-size:0.85rem;color:#92400E;background:#FEF3C7;border:1px solid #FDE68A;border-radius:6px;padding:6px 10px;line-height:1.35;">Your login is view &amp; upload only — ask the owner or an admin to change this.</span>
            <button type="button" class="btn btn-secondary" onclick="window.RosterModule.closeModal('person')">Cancel</button>
            <button type="submit" class="btn btn-primary" id="btn-save-person">Save</button>
          </div>
        </form>
      </div>
    </div>

    <!-- Add/Edit Truck Modal -->
    <div class="modal hidden" id="modal-truck">
      <!-- TASK-4629 — modal-form-xl: desktop-wide form tier (min(92vw,920px) + 2-col
           grid at >=769px). ONLY this modal moved off modal-form-wide; the person /
           trailer / rig / asset / permit modals stay on the 540px tier on purpose. -->
      <div class="modal-content modal-form-xl">
        <div class="modal-header">
          <h3 id="modal-truck-title">Add Vehicle</h3>
          <button class="btn-icon" onclick="window.RosterModule.closeModal('truck')">
            <span class="material-icons">close</span>
          </button>
        </div>
        <form id="form-truck" class="modal-form-scrollable" novalidate onsubmit="window.RosterModule.saveTruck(event)">
          <div class="modal-form-scroll">
          <input type="hidden" id="truck-id">
          <div class="form-group">
            <label for="truck-unit">Unit # *</label>
            <input type="text" id="truck-unit" required placeholder="101" data-maxlen="100">
          </div>
          <div class="form-group">
            <label for="truck-nickname">Name <span class="form-hint-inline">(optional)</span></label>
            <input type="text" id="truck-nickname" placeholder="e.g. Pete, Big Red" data-maxlen="100">
          </div>
          <!-- TASK-340 Slice A — Photo upload. Mirrors asset photo pattern.
               Renders on the roster card in place of the local_shipping icon. -->
          <div class="form-group">
            <label>Photo <span class="form-hint-inline">(optional — shows on the roster card)</span></label>
            <div class="asset-photo-upload" style="display:flex;align-items:center;gap:8px;flex-wrap:wrap;">
              <input type="file" id="truck-photo-input" accept="image/*,.heic,.heif" style="display:none"
                onchange="window.RosterModule.handleTruckPhoto(event)">
              <div id="truck-photo-preview" class="asset-photo-preview hidden"></div>
              <button type="button" class="btn btn-secondary btn-sm"
                onclick="document.getElementById('truck-photo-input').click()" style="white-space:nowrap;">
                <span class="material-icons" style="font-size:16px;">add_a_photo</span> Photo
              </button>
            </div>
          </div>
          <div class="form-group">
            <label for="truck-type">Type *</label>
            <div class="select-with-add">
              <select id="truck-type" required>
              </select>
              <button type="button" class="btn-add-type" onclick="window.RosterModule.showAddTypeInline('truckTypes', 'truck-type')" title="Add custom type">
                <span class="material-icons">add</span>
              </button>
            </div>
          </div>
          <div class="form-group sms-optin-group">
            <label class="checkbox-switch">
              <input type="checkbox" id="truck-show-in-dispatch" checked>
              <span class="switch-slider"></span>
              <span class="switch-label">Show in Dispatch</span>
            </label>
            <p class="form-hint">Uncheck to hide from dispatch job assignments</p>
          </div>
          <div class="form-group">
            <label for="truck-make">Make</label>
            <input type="text" id="truck-make" placeholder="Kenworth">
          </div>
          <div class="form-group">
            <label for="truck-model">Model</label>
            <input type="text" id="truck-model" placeholder="T880">
          </div>
          <div class="form-group">
            <label for="truck-year">Year</label>
            <input type="number" id="truck-year" placeholder="${new Date().getFullYear()}" min="1900" max="${new Date().getFullYear() + 2}">
          </div>
          <div class="form-group form-span-full">
            <label>VIN</label>
            <div id="truck-vins-container" class="axles-container">
              <!-- VIN inputs will be added here dynamically -->
            </div>
            <button type="button" class="btn btn-secondary btn-sm" id="btn-add-truck-vin" onclick="window.RosterModule.addTruckVin()" style="font-size:0.75rem;color:var(--text-secondary);border:1px dashed var(--border-light);background:transparent;">
              <span class="material-icons" style="font-size:14px;">add</span> Add Another VIN <span style="font-weight:400;opacity:0.7;">(combo units)</span>
            </button>
          </div>

          <details class="form-collapsible">
            <summary class="form-collapsible-header">
              <span class="material-icons">scale</span>
              <span>Weight Ratings</span>
              <span class="material-icons collapse-icon">expand_more</span>
            </summary>
            <div class="form-collapsible-content">
              <div class="form-row">
                <div class="form-group">
                  <label for="truck-gvwr">GVWR (lbs)</label>
                  <input type="number" id="truck-gvwr" placeholder="33000" min="0" step="100">
                  <p class="form-hint">Gross Vehicle Weight Rating</p>
                </div>
                <div class="form-group">
                  <label for="truck-gcwr">GCWR (lbs)</label>
                  <input type="number" id="truck-gcwr" placeholder="80000" min="0" step="100">
                  <p class="form-hint">Gross Combined Weight Rating</p>
                </div>
              </div>

              <div class="form-row">
                <div class="form-group">
                  <label for="truck-curb-weight">Curb Weight (lbs)</label>
                  <input type="number" id="truck-curb-weight" placeholder="18000" min="0" step="100">
                  <p class="form-hint">Empty vehicle weight</p>
                </div>
                <div class="form-group">
                  <label for="truck-payload-capacity">Payload Capacity (lbs)</label>
                  <input type="number" id="truck-payload-capacity" placeholder="19200" min="0" step="100">
                  <p class="form-hint">Max cargo weight per load. Used by Load Calculator.</p>
                </div>
              </div>

              <div class="form-row">
                <div class="form-group">
                  <label for="truck-gawr-front">GAWR Front (lbs)</label>
                  <input type="number" id="truck-gawr-front" placeholder="12000" min="0" step="100">
                  <p class="form-hint">Gross Axle Weight Rating - Front</p>
                </div>
                <div class="form-group">
                  <label for="truck-gawr-rear">GAWR Rear (lbs)</label>
                  <input type="number" id="truck-gawr-rear" placeholder="21000" min="0" step="100">
                  <p class="form-hint">Gross Axle Weight Rating - Rear</p>
                </div>
              </div>
            </div>
          </details>

          <!-- TASK-512 (Permit Pete, Slice 0) — net-new OS/OW dimension fields.
               Overall vehicle dimensions + axle config feed the oversize/overweight
               permit-request packet (and can later prefill HERE routing). Additive,
               no migration; empty by default. Bridge Formula (23 USC 127) needs
               axle count + spacing — these are equipment facts captured here, never
               AI-guessed at permit time. -->
          <details class="form-collapsible" id="truck-dimensions-section">
            <summary class="form-collapsible-header">
              <span class="material-icons">straighten</span>
              <span>Dimensions (OS/OW Permits)</span>
              <span class="paxle-meta-review" id="truck-dims-missing-badge" style="display:none;"></span>
              <span class="material-icons collapse-icon">expand_more</span>
            </summary>
            <div class="form-collapsible-content">
              <div class="form-row">
                <div class="form-group">
                  <label for="truck-length">Overall Length (ft)</label>
                  <input type="number" id="truck-length" placeholder="24" min="0" step="0.1">
                  <p class="form-hint">Bumper to back of tractor</p>
                </div>
                <div class="form-group">
                  <label for="truck-width">Overall Width (ft)</label>
                  <input type="number" id="truck-width" placeholder="8.5" min="0" step="0.1">
                  <p class="form-hint">Widest point (mirrors excluded)</p>
                </div>
              </div>
              <div class="form-row">
                <div class="form-group">
                  <label for="truck-height">Overall Height (ft)</label>
                  <input type="number" id="truck-height" placeholder="13.5" min="0" step="0.1">
                  <p class="form-hint">Ground to highest point</p>
                </div>
                <div class="form-group">
                  <label for="truck-axle-count">Axle Count</label>
                  <input type="number" id="truck-axle-count" placeholder="3" min="0" step="1">
                  <p class="form-hint">Number of axles on this unit</p>
                </div>
              </div>
              <div class="form-group">
                <label for="truck-axle-spacing">Axle Spacing (ft)</label>
                <input type="text" id="truck-axle-spacing" placeholder="e.g. 12-4-4">
                <p class="form-hint">Axle-to-axle spacings, front to rear (for Bridge Formula weight limits)</p>
              </div>
            </div>
          </details>

          <!-- TASK-525 — Heavy Haul / Permit Specs. Collapsed by default so the
               95% who don't run superloads never see the per-axle table. This unit's
               OWN internal axles only (steer→drives for a tractor); the tractor↔trailer
               coupling gap is combination-specific and lives on the rig template, NOT here. -->
          <details class="form-collapsible" id="truck-heavyhaul-section">
            <summary class="form-collapsible-header">
              <span class="material-icons">scale</span>
              <span>Heavy Haul / Permit Specs</span>
              <span class="paxle-meta-review" id="truck-hh-missing-badge" style="display:none;"></span>
              <span class="material-icons collapse-icon">expand_more</span>
            </summary>
            <div class="form-collapsible-content">
              <div class="form-group">
                <label for="truck-registered-weight">Registered / Licensed Weight (lbs)</label>
                <input type="number" id="truck-registered-weight" placeholder="105500" min="0" step="1">
                <p class="form-hint">The weight your plate is licensed for (e.g. 105,500 lbs). Plate state is set under Registration below.</p>
              </div>
              <!-- TASK-1444 — spreadsheet-style capture. Tab moves cell to cell,
                   Enter drops to the next row (and makes one at the end), Ctrl/Cmd+D
                   copies the axle above, and a block pasted from Excel/Sheets fills
                   from whichever cell has focus. Every one of those routes writes to
                   these INPUTS only — getPermitAxles() stays the single write path. -->
              <div class="form-group">
                <label>Per-Axle Permit Configuration</label>
                <p class="form-hint" style="margin-top:0;">This unit's own axles, front to rear. Used to assemble the permit packet. Do not enter loaded axle weight here — loaded weights are collected per-move.</p>
                <div class="paxle-toolbar">
                  <button type="button" class="btn btn-secondary btn-sm" onclick="window.RosterModule.addTruckPermitAxle()">
                    <span class="material-icons" style="font-size:14px;">add</span> Add axle
                  </button>
                  <button type="button" class="btn btn-secondary btn-sm" onclick="window.RosterModule._paxleDuplicateLast('truck')" title="Copy the last axle again — drive groups repeat">
                    <span class="material-icons" style="font-size:14px;">content_copy</span> Duplicate last
                  </button>
                  <select id="truck-paxle-preset" class="paxle-preset" onchange="window.RosterModule.applyAxlePreset('truck', this.value)" aria-label="Add a common axle group">
                    <option value="">Add a common group…</option>
                  </select>
                  <button type="button" class="btn btn-secondary btn-sm" onclick="window.RosterModule.showPaxleImport('truck')" title="Paste a block from a spreadsheet, or import a permit you already filed">
                    <span class="material-icons" style="font-size:14px;">content_paste</span> Paste / import…
                  </button>
                  <span class="paxle-meta" id="truck-paxle-count"></span>
                  <span class="paxle-meta paxle-meta-review" id="truck-paxle-review" style="display:none;"></span>
                </div>
                <div class="paxle-grid-head" id="truck-paxle-head" style="display:none;">
                  <span></span><span>Tires</span><span>Tire W</span><span>Track W</span><span>To next (ft)</span><span>Brk</span><span></span>
                </div>
                <div id="truck-permit-axles-container" class="axles-container paxle-grid"></div>
                <div class="paxle-empty" id="truck-paxle-empty">No axles yet. Add one, apply a common group, or paste a block straight out of a spreadsheet.</div>
                <div class="paxle-banner" id="truck-paxle-banner" style="display:none;"></div>
                <p class="form-hint">Spacing is the distance to the <strong>next</strong> axle, in feet — the last axle leaves it blank. Feet-and-inches (14' 6") is converted to decimal feet and flagged. Anything we can't read is left blank and outlined red rather than guessed: this goes on a permit filing.</p>
              </div>
              <!-- TASK-1443 — READ-ONLY. Which saved rigs this unit belongs to. Editing
                   a rig happens on the Rigs tab; nothing here writes to the rig. -->
              <div class="form-group" id="truck-rigs-summary-group" style="display:none;">
                <label>Rigs using this vehicle</label>
                <div id="truck-rigs-summary"></div>
              </div>
              <div id="truck-permit-geometry"></div>
            </div>
          </details>

          <details class="form-collapsible" id="truck-registration-section">
            <summary class="form-collapsible-header">
              <span class="material-icons">badge</span>
              <span>Registration & Compliance</span>
              <span class="material-icons collapse-icon">expand_more</span>
            </summary>
            <div class="form-collapsible-content">
              <div class="form-group">
                <label>License Plates</label>
                <div id="truck-plates-container" class="axles-container">
                  <!-- Plate inputs will be added here dynamically -->
                </div>
                <button type="button" class="btn btn-secondary btn-sm" id="btn-add-truck-plate" onclick="window.RosterModule.addTruckPlate()" style="font-size:0.75rem;color:var(--text-secondary);border:1px dashed var(--border-light);background:transparent;">
                  <span class="material-icons" style="font-size:14px;">add</span> Add Another Plate
                </button>
              </div>
              <div class="form-row">
                <div class="form-group">
                  <label for="truck-reg-number">Registration #</label>
                  <input type="text" id="truck-reg-number" placeholder="REG123456">
                </div>
                <div class="form-group">
                  <label for="truck-reg-expiry">Registration Expiry</label>
                  <input type="date" id="truck-reg-expiry" oninput="window.RosterModule.regPromptHide('truck')">
                  <label class="checkbox-label" style="margin-top:6px;font-size:0.85rem;">
                    <input type="checkbox" id="truck-reg-no-expiry" onchange="window.RosterModule.regNoExpiryToggle('truck')" data-reg-noexp-guard="roster-truck">
                    Doesn't expire
                  </label>
                </div>
              </div>
              <!-- TASK-9814 — the registration card upload sits right under its expiry date
                   (the old separate "Registration Documents" section is gone; ids unchanged). -->
              <div id="truck-reg-expiry-prompt" role="alert" hidden style="display:none;align-items:flex-start;gap:8px;flex-wrap:wrap;padding:10px 12px;margin:4px 0 8px;background:#FFFBEB;color:#92400E;border:1px solid #FDE68A;border-radius:var(--radius-md);font-size:0.85rem;line-height:1.4;">
                <span class="material-icons" style="font-size:18px;">event</span>
                <span style="flex:1;min-width:180px;">Enter the expiration date from the registration card so we can warn you before it expires.</span>
                <button type="button" class="btn btn-secondary btn-sm" id="truck-reg-save-anyway-btn" onclick="window.RosterModule.saveAnywayRegPrompt('truck')">Save anyway</button>
              </div>
              <div class="form-group">
                <label>Registration Card <span class="doc-count-label" id="truck-registration-count">(0/5)</span></label>
                <div class="doc-upload-area">
                  <input type="file" id="truck-registration-doc-input" accept="image/*,.pdf" multiple style="display:none" onchange="window.RosterModule.handleDocUpload(event, 'truck', 'registration')">
                  <div class="doc-list" id="truck-registration-doc-list"></div>
                  <button type="button" class="btn btn-secondary btn-sm" id="truck-registration-upload-btn" onclick="document.getElementById('truck-registration-doc-input').click()">
                    <span class="material-icons">add</span> Add File
                  </button>
                  <p class="form-hint">Vehicle registration card/sticker (max 5)</p>
                </div>
              </div>
              <!-- TASK-9813 — IRP (apportioned) cab card: its own expiry + upload. Blank = not apportioned. -->
              <div class="form-row">
                <div class="form-group">
                  <label for="truck-irp-expiry">IRP Cab Card Expires</label>
                  <input type="date" id="truck-irp-expiry">
                  <p class="form-hint">Apportioned (IRP) trucks only. Leave blank if this truck isn't apportioned.</p>
                </div>
              </div>
              <div class="form-group">
                <label>IRP Cab Card <span class="doc-count-label" id="truck-irp-count">(0/5)</span></label>
                <div class="doc-upload-area">
                  <input type="file" id="truck-irp-doc-input" accept="image/*,.pdf" multiple style="display:none" onchange="window.RosterModule.handleDocUpload(event, 'truck', 'irp')">
                  <div class="doc-list" id="truck-irp-doc-list"></div>
                  <button type="button" class="btn btn-secondary btn-sm" id="truck-irp-upload-btn" onclick="document.getElementById('truck-irp-doc-input').click()">
                    <span class="material-icons">add</span> Add File
                  </button>
                  <p class="form-hint">IRP cab card (max 5)</p>
                </div>
              </div>
              <div style="padding:12px;background:#F1F5F9;border:1px solid var(--border-light);border-radius:var(--radius-md);margin-top:8px;">
                <div style="display:flex;align-items:flex-start;gap:10px;">
                  <input type="checkbox" id="truck-ifta-registered" checked style="width:18px;height:18px;cursor:pointer;margin-top:2px;">
                  <div style="flex:1;">
                    <label for="truck-ifta-registered" style="margin:0;cursor:pointer;font-weight:600;">IFTA Licensed</label>
                    <div style="font-size:0.75rem;color:var(--text-secondary);margin-top:4px;line-height:1.4;">
                      When checked, fuel purchases and miles for this vehicle are included in your IFTA quarterly reports (Compliance → IFTA). Uncheck for service pickups, intrastate-only trucks, or any vehicle without an IFTA decal.
                    </div>
                    <div style="margin-top:10px;max-width:200px;">
                      <label for="truck-ifta-decal-year" style="font-size:0.75rem;font-weight:600;color:var(--text-secondary);">IFTA Decal Year (optional)</label>
                      <input type="number" id="truck-ifta-decal-year" min="2000" max="2100" placeholder="e.g. 2026" style="margin-top:4px;">
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </details>

          <details class="form-collapsible">
            <summary class="form-collapsible-header">
              <span class="material-icons">shield</span>
              <span>Insurance</span>
              <span class="material-icons collapse-icon">expand_more</span>
            </summary>
            <div class="form-collapsible-content">
              <div class="form-row">
                <div class="form-group">
                  <label for="truck-ins-company">Insurance Company</label>
                  <input type="text" id="truck-ins-company" placeholder="Progressive Commercial">
                </div>
                <div class="form-group">
                  <label for="truck-ins-policy">Policy Number</label>
                  <input type="text" id="truck-ins-policy" placeholder="POL-123456789">
                </div>
              </div>
              <div class="form-row">
                <div class="form-group">
                  <label for="truck-ins-expiry">Insurance Expiry</label>
                  <input type="date" id="truck-ins-expiry">
                </div>
                <div class="form-group">
                  <label for="truck-ins-phone">Claims Phone</label>
                  <input type="tel" id="truck-ins-phone" placeholder="1-800-555-0123">
                </div>
              </div>
            </div>
          </details>

          <!-- TASK-259 — Part 376 ownership + lease section.
               Per 49 CFR §376.11(a): every non-owned vehicle used in
               authorized transportation requires a written lease meeting
               §376.12. Per §379 App A item A.5(f): retain the lease for
               its expiration/termination + 1 year. Per §376.11(c)(2):
               either a copy of the lease OR a §376.12-compliant statement
               rides in the cab during the lease period. -->
          <details class="form-collapsible" id="truck-ownership-section">
            <summary class="form-collapsible-header">
              <span class="material-icons">gavel</span>
              <span>Ownership &amp; Lease (Part 376)</span>
              <span class="material-icons collapse-icon">expand_more</span>
            </summary>
            <div class="form-collapsible-content">
              <div class="form-group">
                <label for="truck-ownership">Ownership Type</label>
                <select id="truck-ownership" onchange="window.RosterModule.onTruckOwnershipChange()">
                  <option value="owned">Carrier-owned</option>
                  <option value="leased">Leased (third-party owner-operator)</option>
                  <option value="owner_op">Owner-operator (driver-owned)</option>
                  <option value="sister_co">Sister company / related entity (titled to another LLC)</option>
                </select>
                <p class="form-hint">Anything other than "Carrier-owned" must have a written lease on file per 49 CFR &sect;376.11.</p>
              </div>
              <div id="truck-lease-fields" style="display:none;">
                <div class="form-row">
                  <div class="form-group">
                    <label for="truck-lease-start">Lease Start Date</label>
                    <input type="date" id="truck-lease-start" onchange="window.RosterModule.recalcLeaseRetainUntil()">
                  </div>
                  <div class="form-group">
                    <label for="truck-lease-end">Lease End Date</label>
                    <input type="date" id="truck-lease-end" onchange="window.RosterModule.recalcLeaseRetainUntil()">
                  </div>
                </div>
                <div class="form-row">
                  <div class="form-group">
                    <label for="truck-lease-owner">Equipment Owner (Lessor) Name</label>
                    <input type="text" id="truck-lease-owner" placeholder="Smith Trucking LLC">
                  </div>
                  <div class="form-group">
                    <label for="truck-lease-retain-until">Retain Until (auto: end + 1 year)</label>
                    <input type="date" id="truck-lease-retain-until" readonly style="background:var(--bg-elevated);color:var(--text-secondary);">
                  </div>
                </div>
                <div class="form-group">
                  <label for="truck-lease-commodity-restrictions">Commodity Restrictions (if any, per &sect;376.11(c)(2))</label>
                  <input type="text" id="truck-lease-commodity-restrictions" placeholder="e.g. No hazmat; flatbed only">
                </div>
                <div class="form-group">
                  <label for="truck-lease-original-address">Address Where Original Lease Is Kept</label>
                  <input type="text" id="truck-lease-original-address" placeholder="1109 6th St E, Williston ND 58801">
                  <p class="form-hint">Required on the in-cab statement per &sect;376.11(c)(2).</p>
                </div>
                <div class="form-group">
                  <label>Lease Document(s) <span class="doc-count-label" id="truck-lease-count">(0/5)</span></label>
                  <div class="doc-upload-area">
                    <input type="file" id="truck-lease-doc-input" accept=".pdf,image/*" multiple style="display:none" onchange="window.RosterModule.handleDocUpload(event, 'truck', 'lease')">
                    <div class="doc-list" id="truck-lease-doc-list"></div>
                    <button type="button" class="btn btn-secondary btn-sm" id="truck-lease-upload-btn" onclick="document.getElementById('truck-lease-doc-input').click()">
                      <span class="material-icons">add</span> Add Lease PDF
                    </button>
                    <p class="form-hint">PDF preferred. Retain until end + 1 yr per &sect;379 App A.</p>
                  </div>
                </div>
                <div class="form-group" id="truck-lease-statement-block" style="border:1px solid var(--border);border-radius:var(--radius-sm);padding:12px;background:var(--bg-elevated);">
                  <label style="display:flex;align-items:center;gap:6px;margin-bottom:6px;">
                    <span class="material-icons" style="font-size:18px;color:var(--accent);">picture_as_pdf</span>
                    <span>In-Cab &sect;376.12 Statement</span>
                  </label>
                  <p class="form-hint" style="margin:0 0 8px;">Per &sect;376.11(c)(2), a copy of the lease OR a certifying statement must ride in the cab. Generate a PDF with the verbatim &sect;376.12 disclosures + your carrier name, DOT#, equipment ID, and lease dates. The PDF is stored on this vehicle for download by drivers (Job Board &rarr; Vehicle Docs).</p>
                  <div id="truck-lease-statement-current" style="font-size:0.8rem;color:var(--text-secondary);margin-bottom:8px;"></div>
                  <label class="checkbox-label" style="margin-bottom:8px;">
                    <input type="checkbox" id="truck-lease-summary-mode">
                    <span style="font-size:0.85rem;">Cab copy: hide compensation &amp; payment-period specifics (&sect;376.12(d), (f)). Full lease still applies; permitted under the &sect;376.11(c)(2) statement-in-lieu-of-lease path.</span>
                  </label>
                  <button type="button" class="btn btn-secondary btn-sm" id="truck-lease-statement-generate-btn" onclick="window.RosterModule.generatePart376Statement()">
                    <span class="material-icons">picture_as_pdf</span> Generate &sect;376.12 Statement
                  </button>
                  <p class="form-hint" id="truck-lease-statement-generate-hint" style="margin-top:6px;display:none;">Save the vehicle first to enable PDF generation.</p>
                </div>
              </div>
            </div>
          </details>

          <details class="form-collapsible">
            <summary class="form-collapsible-header">
              <span class="material-icons">fact_check</span>
              <span>Annual Inspection (DOT)</span>
              <span class="material-icons collapse-icon">expand_more</span>
            </summary>
            <div class="form-collapsible-content">
              <div class="form-row">
                <div class="form-group">
                  <label for="truck-inspection-date">Last Inspection Date</label>
                  <input type="date" id="truck-inspection-date">
                </div>
                <div class="form-group">
                  <label for="truck-inspection-expiry">Inspection Expires</label>
                  <input type="date" id="truck-inspection-expiry">
                </div>
              </div>
              <div class="form-group">
                <label for="truck-inspection-location">Inspection Location</label>
                <input type="text" id="truck-inspection-location" placeholder="ABC Truck Service, Denver CO">
              </div>
              <div class="form-group">
                <label>Inspection Documents <span class="doc-count-label" id="truck-inspection-count">(0/5)</span></label>
                <div class="doc-upload-area">
                  <input type="file" id="truck-inspection-doc-input" accept="image/*,.pdf" multiple style="display:none" onchange="window.RosterModule.handleDocUpload(event, 'truck', 'inspection')">
                  <div class="doc-list" id="truck-inspection-doc-list"></div>
                  <button type="button" class="btn btn-secondary btn-sm" id="truck-inspection-upload-btn" onclick="document.getElementById('truck-inspection-doc-input').click()">
                    <span class="material-icons">add</span> Add File
                  </button>
                  <p class="form-hint">DOT inspection sticker/certificate (max 5)</p>
                </div>
              </div>
              <div class="form-group">
                <label class="checkbox-label">
                  <input type="checkbox" id="truck-show-inspection-flag" onchange="window.RosterModule.toggleFlagOptions('truck')">
                  Show flag when inspection is due or missing
                </label>
                <div class="form-hint" style="margin-top:2px;font-size:0.8rem;color:var(--text-muted);">Flag appears 30 days before expiry (configurable below) and if no inspection date is entered.</div>
              </div>
              <div class="form-row flag-options" id="truck-flag-options" style="display:none">
                <div class="form-group">
                  <label for="truck-flag-days">Days before expiry to show flag</label>
                  <select id="truck-flag-days">
                    <option value="30">30 days</option>
                    <option value="60">60 days</option>
                    <option value="90">90 days</option>
                  </select>
                </div>
                <div class="form-group" style="display:flex;align-items:flex-end;">
                  <button type="button" class="btn btn-secondary btn-sm" id="truck-reset-flag-btn" onclick="window.RosterModule.resetInspectionFlag('truck')" style="display:none">
                    <span class="material-icons">refresh</span> Reset Flag
                  </button>
                </div>
              </div>
            </div>
          </details>

          <details class="form-collapsible">
            <summary class="form-collapsible-header">
              <span class="material-icons">folder_open</span>
              <span>Insurance Documents</span>
              <span class="material-icons collapse-icon">expand_more</span>
            </summary>
            <div class="form-collapsible-content">
              <div class="form-group">
                <label>Insurance Documents <span class="doc-count-label" id="truck-insurance-count">(0/5)</span></label>
                <div class="doc-upload-area">
                  <input type="file" id="truck-insurance-doc-input" accept="image/*,.pdf" multiple style="display:none" onchange="window.RosterModule.handleDocUpload(event, 'truck', 'insurance')">
                  <div class="doc-list" id="truck-insurance-doc-list"></div>
                  <button type="button" class="btn btn-secondary btn-sm" id="truck-insurance-upload-btn" onclick="document.getElementById('truck-insurance-doc-input').click()">
                    <span class="material-icons">add</span> Add File
                  </button>
                  <p class="form-hint">Insurance card/certificate (max 5)</p>
                </div>
              </div>
            </div>
          </details>

          <details class="form-collapsible">
            <summary class="form-collapsible-header">
              <span class="material-icons">folder_open</span>
              <span>Documents <span class="doc-count-label" id="truck-general-doc-count" style="font-weight:normal;"></span></span>
              <span class="material-icons collapse-icon">expand_more</span>
            </summary>
            <div class="form-collapsible-content">
              <div id="truck-general-doc-list" class="general-doc-list"></div>
              <button type="button" class="btn btn-secondary btn-sm" id="truck-add-general-doc-btn" onclick="window.RosterModule.showAddGeneralDoc('truck')" style="margin-top:8px;">
                <span class="material-icons">add</span> Add Document
              </button>
              <p class="form-hint">TERO, service agreements, permits, or any other documents (max 20)</p>
            </div>
          </details>

          <details class="form-collapsible" id="truck-citations-section">
            <summary class="form-collapsible-header" onclick="window.RosterModule.loadVehicleCitations && window.RosterModule.loadVehicleCitations()">
              <span class="material-icons" style="color:#DC2626;">gavel</span>
              <span>Vehicle Exam Reports</span>
              <span class="material-icons collapse-icon">expand_more</span>
            </summary>
            <div class="form-collapsible-content">
              <div id="truck-citations-list" style="font-size:0.8rem;color:var(--text-muted);padding:4px 0;">Click to load...</div>
            </div>
          </details>

          <details class="form-collapsible">
            <summary class="form-collapsible-header">
              <span class="material-icons">build</span>
              <span>Engine/Drivetrain</span>
              <span class="material-icons collapse-icon">expand_more</span>
            </summary>
            <div class="form-collapsible-content">
              <div class="form-row">
                <div class="form-group">
                  <label for="truck-engine-make">Engine Make</label>
                  <input type="text" id="truck-engine-make" placeholder="Cummins">
                </div>
                <div class="form-group">
                  <label for="truck-engine-model">Engine Model</label>
                  <input type="text" id="truck-engine-model" placeholder="X15">
                </div>
              </div>
              <div class="form-group">
                <label for="truck-engine-serial">Engine Serial</label>
                <input type="text" id="truck-engine-serial" placeholder="79876543">
              </div>
              <div class="form-row">
                <div class="form-group">
                  <label for="truck-transmission">Transmission Type</label>
                  <select id="truck-transmission">
                    <option value="">Select type</option>
                    <option value="Manual">Manual</option>
                    <option value="Automatic">Automatic</option>
                    <option value="Automated Manual">Automated Manual</option>
                  </select>
                </div>
                <div class="form-group">
                  <label for="truck-fuel">Fuel Type</label>
                  <select id="truck-fuel">
                    <option value="Diesel">Diesel</option>
                    <option value="Gasoline">Gasoline</option>
                    <option value="CNG">CNG</option>
                    <option value="Electric">Electric</option>
                    <option value="Hybrid">Hybrid</option>
                  </select>
                </div>
              </div>
            </div>
          </details>

          <details class="form-collapsible">
            <summary class="form-collapsible-header">
              <span class="material-icons">settings</span>
              <span>Axles</span>
              <span class="material-icons collapse-icon">expand_more</span>
            </summary>
            <div class="form-collapsible-content">
              <div class="form-group">
                <label>Axle Serial Numbers</label>
                <div id="truck-axles-container" class="axles-container">
                  <!-- Axle inputs will be added here dynamically -->
                </div>
                <button type="button" class="btn btn-secondary btn-sm" onclick="window.RosterModule.addTruckAxle()">
                  <span class="material-icons">add</span> Add Axle
                </button>
              </div>
            </div>
          </details>

          <details class="form-collapsible">
            <summary class="form-collapsible-header">
              <span class="material-icons">tire_repair</span>
              <span>Tires</span>
              <span class="material-icons collapse-icon">expand_more</span>
            </summary>
            <div class="form-collapsible-content">
              <div class="form-row">
                <div class="form-group">
                  <label for="truck-tire-size-front">Front Tire Size</label>
                  <input type="text" id="truck-tire-size-front" placeholder="11R22.5">
                </div>
                <div class="form-group">
                  <label for="truck-tire-size-rear">Rear Tire Size</label>
                  <input type="text" id="truck-tire-size-rear" placeholder="11R22.5">
                </div>
              </div>
            </div>
          </details>

          <!-- Vehicle Inventory Section (visible when feature enabled) -->
          <div id="truck-inventory-section" class="form-section-collapsible hidden">
            <div class="form-section-header collapsible" onclick="window.RosterModule.toggleInventorySection()">
              <h4><span class="material-icons">inventory_2</span> Inventory List</h4>
              <span class="material-icons section-chevron">expand_more</span>
            </div>
            <div id="truck-inventory-content" class="form-section-content">
              <div class="form-group">
                <label style="display:flex;align-items:center;gap:var(--spacing-sm);cursor:pointer;">
                  <input type="checkbox" id="truck-inventory-enabled" style="width:18px;height:18px;">
                  <span>Enable inventory tracking for this vehicle</span>
                </label>
                <p style="font-size:0.75rem;color:var(--text-muted);margin-top:4px;">
                  When enabled, you can track equipment/tools in the Maintenance module.
                </p>
              </div>

              <div id="truck-inventory-items" class="inventory-items-container">
                <!-- Inventory items added here dynamically -->
              </div>

              <button type="button" class="btn btn-secondary btn-sm" onclick="window.RosterModule.addTruckInventoryItem()">
                <span class="material-icons">add</span> Add Item
              </button>
            </div>
          </div>

          <div class="form-group form-span-full">
            <label for="truck-notes">Notes</label>
            <textarea id="truck-notes" rows="2" placeholder="Additional notes about this vehicle..."></textarea>
          </div>

          <!-- TASK-1141 — per-unit notes thread (existing vehicles only) -->
          <div class="form-group form-span-full" id="truck-notes-thread"></div>
          <!-- TASK-1550 — the signed-in user's own reminders on this vehicle (existing vehicles only) -->
          <div class="form-group form-span-full" id="truck-reminders"></div>

          <!-- Yard / Location -->
          <div class="form-group" id="truck-yard-group">
            <label for="truck-yard">Yard / Location</label>
            <div style="display:flex;gap:8px;align-items:center;">
              <select id="truck-yard" class="form-control" style="flex:1;"></select>
              <button type="button" class="btn-add-type" onclick="window.RosterModule.addYardInline('truck-yard')" title="Add yard">
                <span class="material-icons" style="font-size:18px;">add</span>
              </button>
            </div>
          </div>

${_rentalSectionHtml('truck')}

          <div class="form-group sms-optin-group">
            <label class="checkbox-switch">
              <input type="checkbox" id="truck-active" checked>
              <span class="switch-slider"></span>
              <span class="switch-label">Active</span>
            </label>
            <p class="form-hint">Inactive vehicles won't appear in job assignments or DVIR forms</p>
          </div>
          </div><!-- end modal-form-scroll -->

          <div class="modal-form-footer">
            <button type="button" class="btn btn-secondary" onclick="window.RosterModule.closeModal('truck')">Cancel</button>
            <button type="button" class="btn btn-primary" id="btn-save-truck" onclick="window.RosterModule.saveTruck(event)">Save</button>
          </div>
        </form>
      </div>
    </div>

    <!-- Add/Edit Trailer Modal -->
    <div class="modal hidden" id="modal-trailer">
      <div class="modal-content modal-form-wide">
        <div class="modal-header">
          <h3 id="modal-trailer-title">Add Trailer</h3>
          <button class="btn-icon" onclick="window.RosterModule.closeModal('trailer')">
            <span class="material-icons">close</span>
          </button>
        </div>
        <form id="form-trailer" class="modal-form-scrollable" novalidate onsubmit="window.RosterModule.saveTrailer(event)">
          <div class="modal-form-scroll">
          <input type="hidden" id="trailer-id">
          <div class="form-group">
            <label for="trailer-unit">Unit # *</label>
            <input type="text" id="trailer-unit" required placeholder="T-01" data-maxlen="100">
          </div>
          <div class="form-group">
            <label for="trailer-nickname">Name <span class="form-hint-inline">(optional)</span></label>
            <input type="text" id="trailer-nickname" placeholder="e.g. Flatbed, Red Stepdeck" data-maxlen="100">
          </div>
          <!-- TASK-340 Slice A — Photo upload. Mirrors truck photo + asset pattern. -->
          <div class="form-group">
            <label>Photo <span class="form-hint-inline">(optional — shows on the roster card)</span></label>
            <div class="asset-photo-upload" style="display:flex;align-items:center;gap:8px;flex-wrap:wrap;">
              <input type="file" id="trailer-photo-input" accept="image/*,.heic,.heif" style="display:none"
                onchange="window.RosterModule.handleTrailerPhoto(event)">
              <div id="trailer-photo-preview" class="asset-photo-preview hidden"></div>
              <button type="button" class="btn btn-secondary btn-sm"
                onclick="document.getElementById('trailer-photo-input').click()" style="white-space:nowrap;">
                <span class="material-icons" style="font-size:16px;">add_a_photo</span> Photo
              </button>
            </div>
          </div>
          <div class="form-group">
            <label for="trailer-type">Type *</label>
            <div class="select-with-add">
              <select id="trailer-type" required>
              </select>
              <button type="button" class="btn-add-type" onclick="window.RosterModule.showAddTypeInline('trailerTypes', 'trailer-type')" title="Add custom type">
                <span class="material-icons">add</span>
              </button>
            </div>
          </div>
          <div class="form-group sms-optin-group">
            <label class="checkbox-switch">
              <input type="checkbox" id="trailer-show-in-dispatch" checked>
              <span class="switch-slider"></span>
              <span class="switch-label">Show in Dispatch</span>
            </label>
            <p class="form-hint">Uncheck to hide from dispatch job assignments</p>
          </div>
          <div class="form-group">
            <label for="trailer-make">Make</label>
            <input type="text" id="trailer-make" placeholder="Wabash">
          </div>
          <div class="form-group">
            <label for="trailer-model">Model</label>
            <input type="text" id="trailer-model" placeholder="DuraPlate">
          </div>
          <div class="form-group">
            <label for="trailer-year">Year</label>
            <input type="number" id="trailer-year" placeholder="${new Date().getFullYear()}" min="1900" max="${new Date().getFullYear() + 2}">
          </div>
          <div class="form-group">
            <label>VIN</label>
            <div id="trailer-vins-container" class="axles-container">
              <!-- VIN inputs will be added here dynamically -->
            </div>
            <button type="button" class="btn btn-secondary btn-sm" id="btn-add-trailer-vin" onclick="window.RosterModule.addTrailerVin()" style="font-size:0.75rem;color:var(--text-secondary);border:1px dashed var(--border-light);background:transparent;">
              <span class="material-icons" style="font-size:14px;">add</span> Add Another VIN <span style="font-weight:400;opacity:0.7;">(combo units)</span>
            </button>
          </div>

          <details class="form-collapsible">
            <summary class="form-collapsible-header">
              <span class="material-icons">scale</span>
              <span>Weight Ratings</span>
              <span class="material-icons collapse-icon">expand_more</span>
            </summary>
            <div class="form-collapsible-content">
              <div class="form-row">
                <div class="form-group">
                  <label for="trailer-gvwr">GVWR (lbs)</label>
                  <input type="number" id="trailer-gvwr" placeholder="48000" min="0" step="100">
                  <p class="form-hint">Gross Vehicle Weight Rating</p>
                </div>
                <div class="form-group">
                  <label for="trailer-unladen-weight">Unladen Weight (lbs)</label>
                  <input type="number" id="trailer-unladen-weight" placeholder="15000" min="0" step="100">
                  <p class="form-hint">Empty trailer weight</p>
                </div>
              </div>

              <div class="form-group">
                <label for="trailer-gawr">GAWR (lbs)</label>
                <input type="number" id="trailer-gawr" placeholder="20000" min="0" step="100">
                <p class="form-hint">Gross Axle Weight Rating (per axle)</p>
              </div>
            </div>
          </details>

          <!-- TASK-512 (Permit Pete, Slice 0) — net-new OS/OW dimension fields.
               Overall trailer dimensions + axle config feed the oversize/overweight
               permit-request packet (and can later prefill HERE routing). Additive,
               no migration; empty by default. Bridge Formula (23 USC 127) needs
               axle count + spacing — equipment facts captured here, never AI-guessed. -->
          <details class="form-collapsible" id="trailer-dimensions-section">
            <summary class="form-collapsible-header">
              <span class="material-icons">straighten</span>
              <span>Dimensions (OS/OW Permits)</span>
              <span class="paxle-meta-review" id="trailer-dims-missing-badge" style="display:none;"></span>
              <span class="material-icons collapse-icon">expand_more</span>
            </summary>
            <div class="form-collapsible-content">
              <div class="form-row">
                <div class="form-group">
                  <label for="trailer-length">Overall Length (ft)</label>
                  <input type="number" id="trailer-length" placeholder="48" min="0" step="0.1">
                  <p class="form-hint">Kingpin to rear, deck length</p>
                </div>
                <div class="form-group">
                  <label for="trailer-width">Overall Width (ft)</label>
                  <input type="number" id="trailer-width" placeholder="8.5" min="0" step="0.1">
                  <p class="form-hint">Widest point</p>
                </div>
              </div>
              <div class="form-row">
                <div class="form-group">
                  <label for="trailer-height">Overall Height (ft)</label>
                  <input type="number" id="trailer-height" placeholder="11" min="0" step="0.1">
                  <p class="form-hint">Empty deck height (loaded height set per move)</p>
                </div>
                <div class="form-group">
                  <label for="trailer-axle-count">Axle Count</label>
                  <input type="number" id="trailer-axle-count" placeholder="2" min="0" step="1">
                  <p class="form-hint">Number of axles on this trailer</p>
                </div>
              </div>
              <div class="form-group">
                <label for="trailer-axle-spacing">Axle Spacing (ft)</label>
                <input type="text" id="trailer-axle-spacing" placeholder="e.g. 10-4">
                <p class="form-hint">Axle-to-axle spacings, front to rear (for Bridge Formula weight limits)</p>
              </div>
            </div>
          </details>

          <!-- TASK-525 — Heavy Haul / Permit Specs (trailer). This trailer's OWN axles only;
               the tractor↔trailer coupling gap is combination-specific (rig template), NOT here. -->
          <details class="form-collapsible" id="trailer-heavyhaul-section">
            <summary class="form-collapsible-header">
              <span class="material-icons">scale</span>
              <span>Heavy Haul / Permit Specs</span>
              <span class="paxle-meta-review" id="trailer-hh-missing-badge" style="display:none;"></span>
              <span class="material-icons collapse-icon">expand_more</span>
            </summary>
            <div class="form-collapsible-content">
              <div class="form-group">
                <label for="trailer-registered-weight">Registered / Licensed Weight (lbs)</label>
                <input type="number" id="trailer-registered-weight" placeholder="80000" min="0" step="1">
                <p class="form-hint">The weight this trailer's plate is licensed for. Plate state is set under Registration below.</p>
              </div>
              <!-- TASK-1444 — same grid as the vehicle form (see there for the rules). -->
              <div class="form-group">
                <label>Per-Axle Permit Configuration</label>
                <p class="form-hint" style="margin-top:0;">This trailer's own axles, front to rear. Do not enter loaded axle weight here — loaded weights are collected per-move.</p>
                <div class="paxle-toolbar">
                  <button type="button" class="btn btn-secondary btn-sm" onclick="window.RosterModule.addTrailerPermitAxle()">
                    <span class="material-icons" style="font-size:14px;">add</span> Add axle
                  </button>
                  <button type="button" class="btn btn-secondary btn-sm" onclick="window.RosterModule._paxleDuplicateLast('trailer')" title="Copy the last axle again — trailer groups repeat">
                    <span class="material-icons" style="font-size:14px;">content_copy</span> Duplicate last
                  </button>
                  <select id="trailer-paxle-preset" class="paxle-preset" onchange="window.RosterModule.applyAxlePreset('trailer', this.value)" aria-label="Add a common axle group">
                    <option value="">Add a common group…</option>
                  </select>
                  <button type="button" class="btn btn-secondary btn-sm" onclick="window.RosterModule.showPaxleImport('trailer')" title="Paste a block from a spreadsheet, or import a permit you already filed">
                    <span class="material-icons" style="font-size:14px;">content_paste</span> Paste / import…
                  </button>
                  <span class="paxle-meta" id="trailer-paxle-count"></span>
                  <span class="paxle-meta paxle-meta-review" id="trailer-paxle-review" style="display:none;"></span>
                </div>
                <div class="paxle-grid-head" id="trailer-paxle-head" style="display:none;">
                  <span></span><span>Tires</span><span>Tire W</span><span>Track W</span><span>To next (ft)</span><span>Brk</span><span></span>
                </div>
                <div id="trailer-permit-axles-container" class="axles-container paxle-grid"></div>
                <div class="paxle-empty" id="trailer-paxle-empty">No axles yet. Add one, apply a common group, or paste a block straight out of a spreadsheet.</div>
                <div class="paxle-banner" id="trailer-paxle-banner" style="display:none;"></div>
                <p class="form-hint">Spacing is the distance to the <strong>next</strong> axle, in feet — the last axle leaves it blank. Feet-and-inches (14' 6") is converted to decimal feet and flagged. Anything we can't read is left blank and outlined red rather than guessed: this goes on a permit filing.</p>
              </div>
              <!-- TASK-1443 — READ-ONLY rig membership (see truck modal for rationale). -->
              <div class="form-group" id="trailer-rigs-summary-group" style="display:none;">
                <label>Rigs using this trailer</label>
                <div id="trailer-rigs-summary"></div>
              </div>
              <div id="trailer-permit-geometry"></div>
            </div>
          </details>

          <details class="form-collapsible" id="trailer-registration-section">
            <summary class="form-collapsible-header">
              <span class="material-icons">badge</span>
              <span>Registration & Compliance</span>
              <span class="material-icons collapse-icon">expand_more</span>
            </summary>
            <div class="form-collapsible-content">
              <div class="form-group">
                <label>License Plates</label>
                <div id="trailer-plates-container" class="axles-container">
                  <!-- Plate inputs will be added here dynamically -->
                </div>
                <button type="button" class="btn btn-secondary btn-sm" id="btn-add-trailer-plate" onclick="window.RosterModule.addTrailerPlate()" style="font-size:0.75rem;color:var(--text-secondary);border:1px dashed var(--border-light);background:transparent;">
                  <span class="material-icons" style="font-size:14px;">add</span> Add Another Plate
                </button>
              </div>
              <div class="form-row">
                <div class="form-group">
                  <label for="trailer-reg-number">Registration #</label>
                  <input type="text" id="trailer-reg-number" placeholder="REG123456">
                </div>
                <div class="form-group">
                  <label for="trailer-reg-expiry">Registration Expiry</label>
                  <input type="date" id="trailer-reg-expiry" oninput="window.RosterModule.regPromptHide('trailer')">
                  <label class="checkbox-label" style="margin-top:6px;font-size:0.85rem;">
                    <input type="checkbox" id="trailer-reg-no-expiry" onchange="window.RosterModule.regNoExpiryToggle('trailer')" data-reg-noexp-guard="roster-trailer">
                    Doesn't expire
                  </label>
                </div>
              </div>
              <!-- TASK-9814 — the registration card upload sits right under its expiry date
                   (the old separate "Registration Documents" section is gone; ids unchanged). -->
              <div id="trailer-reg-expiry-prompt" role="alert" hidden style="display:none;align-items:flex-start;gap:8px;flex-wrap:wrap;padding:10px 12px;margin:4px 0 8px;background:#FFFBEB;color:#92400E;border:1px solid #FDE68A;border-radius:var(--radius-md);font-size:0.85rem;line-height:1.4;">
                <span class="material-icons" style="font-size:18px;">event</span>
                <span style="flex:1;min-width:180px;">Enter the expiration date from the registration card so we can warn you before it expires.</span>
                <button type="button" class="btn btn-secondary btn-sm" id="trailer-reg-save-anyway-btn" onclick="window.RosterModule.saveAnywayRegPrompt('trailer')">Save anyway</button>
              </div>
              <div class="form-group">
                <label>Registration Card <span class="doc-count-label" id="trailer-registration-count">(0/5)</span></label>
                <div class="doc-upload-area">
                  <input type="file" id="trailer-registration-doc-input" accept="image/*,.pdf" multiple style="display:none" onchange="window.RosterModule.handleDocUpload(event, 'trailer', 'registration')">
                  <div class="doc-list" id="trailer-registration-doc-list"></div>
                  <button type="button" class="btn btn-secondary btn-sm" id="trailer-registration-upload-btn" onclick="document.getElementById('trailer-registration-doc-input').click()">
                    <span class="material-icons">add</span> Add File
                  </button>
                  <p class="form-hint">Trailer registration card/sticker (max 5)</p>
                </div>
              </div>
              <div style="padding:12px;background:#F1F5F9;border:1px solid var(--border-light);border-radius:var(--radius-md);margin-top:8px;">
                <div style="display:flex;align-items:flex-start;gap:10px;">
                  <input type="checkbox" id="trailer-ifta-registered" style="width:18px;height:18px;cursor:pointer;margin-top:2px;">
                  <div style="flex:1;">
                    <label for="trailer-ifta-registered" style="margin:0;cursor:pointer;font-weight:600;">IFTA Licensed</label>
                    <div style="font-size:0.75rem;color:var(--text-secondary);margin-top:4px;line-height:1.4;">
                      Most trailers don't need this. Check only if this trailer has its own IFTA decal and tracked fuel (typically reefer trailers). When checked, fuel purchases for this trailer are included in your IFTA quarterly reports (Compliance → IFTA).
                    </div>
                    <div style="margin-top:10px;max-width:200px;">
                      <label for="trailer-ifta-decal-year" style="font-size:0.75rem;font-weight:600;color:var(--text-secondary);">IFTA Decal Year (optional)</label>
                      <input type="number" id="trailer-ifta-decal-year" min="2000" max="2100" placeholder="e.g. 2026" style="margin-top:4px;">
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </details>

          <details class="form-collapsible">
            <summary class="form-collapsible-header">
              <span class="material-icons">shield</span>
              <span>Insurance</span>
              <span class="material-icons collapse-icon">expand_more</span>
            </summary>
            <div class="form-collapsible-content">
              <div class="form-row">
                <div class="form-group">
                  <label for="trailer-ins-company">Insurance Company</label>
                  <input type="text" id="trailer-ins-company" placeholder="Progressive Commercial">
                </div>
                <div class="form-group">
                  <label for="trailer-ins-policy">Policy Number</label>
                  <input type="text" id="trailer-ins-policy" placeholder="POL-123456789">
                </div>
              </div>
              <div class="form-row">
                <div class="form-group">
                  <label for="trailer-ins-expiry">Insurance Expiry</label>
                  <input type="date" id="trailer-ins-expiry">
                </div>
                <div class="form-group">
                  <label for="trailer-ins-phone">Claims Phone</label>
                  <input type="tel" id="trailer-ins-phone" placeholder="1-800-555-0123">
                </div>
              </div>
            </div>
          </details>

          <details class="form-collapsible">
            <summary class="form-collapsible-header">
              <span class="material-icons">fact_check</span>
              <span>Annual Inspection (DOT)</span>
              <span class="material-icons collapse-icon">expand_more</span>
            </summary>
            <div class="form-collapsible-content">
              <div class="form-row">
                <div class="form-group">
                  <label for="trailer-inspection-date">Last Inspection Date</label>
                  <input type="date" id="trailer-inspection-date">
                </div>
                <div class="form-group">
                  <label for="trailer-inspection-expiry">Inspection Expires</label>
                  <input type="date" id="trailer-inspection-expiry">
                </div>
              </div>
              <div class="form-group">
                <label for="trailer-inspection-location">Inspection Location</label>
                <input type="text" id="trailer-inspection-location" placeholder="ABC Truck Service, Denver CO">
              </div>
              <div class="form-group">
                <label>Inspection Documents <span class="doc-count-label" id="trailer-inspection-count">(0/5)</span></label>
                <div class="doc-upload-area">
                  <input type="file" id="trailer-inspection-doc-input" accept="image/*,.pdf" multiple style="display:none" onchange="window.RosterModule.handleDocUpload(event, 'trailer', 'inspection')">
                  <div class="doc-list" id="trailer-inspection-doc-list"></div>
                  <button type="button" class="btn btn-secondary btn-sm" id="trailer-inspection-upload-btn" onclick="document.getElementById('trailer-inspection-doc-input').click()">
                    <span class="material-icons">add</span> Add File
                  </button>
                  <p class="form-hint">DOT inspection sticker/certificate (max 5)</p>
                </div>
              </div>
              <div class="form-group">
                <label class="checkbox-label">
                  <input type="checkbox" id="trailer-show-inspection-flag" onchange="window.RosterModule.toggleFlagOptions('trailer')">
                  Show flag when inspection is due or missing
                </label>
                <div class="form-hint" style="margin-top:2px;font-size:0.8rem;color:var(--text-muted);">Flag appears 30 days before expiry (configurable below) and if no inspection date is entered.</div>
              </div>
              <div class="form-row flag-options" id="trailer-flag-options" style="display:none">
                <div class="form-group">
                  <label for="trailer-flag-days">Days before expiry to show flag</label>
                  <select id="trailer-flag-days">
                    <option value="30">30 days</option>
                    <option value="60">60 days</option>
                    <option value="90">90 days</option>
                  </select>
                </div>
                <div class="form-group" style="display:flex;align-items:flex-end;">
                  <button type="button" class="btn btn-secondary btn-sm" id="trailer-reset-flag-btn" onclick="window.RosterModule.resetInspectionFlag('trailer')" style="display:none">
                    <span class="material-icons">refresh</span> Reset Flag
                  </button>
                </div>
              </div>
            </div>
          </details>

          <details class="form-collapsible">
            <summary class="form-collapsible-header">
              <span class="material-icons">folder_open</span>
              <span>Insurance Documents</span>
              <span class="material-icons collapse-icon">expand_more</span>
            </summary>
            <div class="form-collapsible-content">
              <div class="form-group">
                <label>Insurance Documents <span class="doc-count-label" id="trailer-insurance-count">(0/5)</span></label>
                <div class="doc-upload-area">
                  <input type="file" id="trailer-insurance-doc-input" accept="image/*,.pdf" multiple style="display:none" onchange="window.RosterModule.handleDocUpload(event, 'trailer', 'insurance')">
                  <div class="doc-list" id="trailer-insurance-doc-list"></div>
                  <button type="button" class="btn btn-secondary btn-sm" id="trailer-insurance-upload-btn" onclick="document.getElementById('trailer-insurance-doc-input').click()">
                    <span class="material-icons">add</span> Add File
                  </button>
                  <p class="form-hint">Insurance card/certificate (max 5)</p>
                </div>
              </div>
            </div>
          </details>

          <details class="form-collapsible">
            <summary class="form-collapsible-header">
              <span class="material-icons">folder_open</span>
              <span>Documents <span class="doc-count-label" id="trailer-general-doc-count" style="font-weight:normal;"></span></span>
              <span class="material-icons collapse-icon">expand_more</span>
            </summary>
            <div class="form-collapsible-content">
              <div id="trailer-general-doc-list" class="general-doc-list"></div>
              <button type="button" class="btn btn-secondary btn-sm" id="trailer-add-general-doc-btn" onclick="window.RosterModule.showAddGeneralDoc('trailer')" style="margin-top:8px;">
                <span class="material-icons">add</span> Add Document
              </button>
              <p class="form-hint">TERO, service agreements, permits, or any other documents (max 20)</p>
            </div>
          </details>

          <details class="form-collapsible">
            <summary class="form-collapsible-header">
              <span class="material-icons">settings</span>
              <span>Axles</span>
              <span class="material-icons collapse-icon">expand_more</span>
            </summary>
            <div class="form-collapsible-content">
              <div class="form-group">
                <label>Axle Serial Numbers</label>
                <div id="trailer-axles-container" class="axles-container">
                  <!-- Axle inputs will be added here dynamically -->
                </div>
                <button type="button" class="btn btn-secondary btn-sm" onclick="window.RosterModule.addTrailerAxle()">
                  <span class="material-icons">add</span> Add Axle
                </button>
              </div>
            </div>
          </details>

          <!-- TASK-9731 — optional tire size (tire orders, shop work, DVIR).
               NOT a permit field: Permit Pete reads permitAxles[].tireWidth. -->
          <details class="form-collapsible">
            <summary class="form-collapsible-header">
              <span class="material-icons">tire_repair</span>
              <span>Tires</span>
              <span class="material-icons collapse-icon">expand_more</span>
            </summary>
            <div class="form-collapsible-content">
              <div class="form-group">
                <label for="trailer-tire-size">Tire Size</label>
                <input type="text" id="trailer-tire-size" placeholder="275/70R22.5" maxlength="40">
                <p class="form-hint">Optional. Not used on permits (the axle table's tire width is).</p>
              </div>
            </div>
          </details>

          <div class="form-group">
            <label for="trailer-notes">Notes</label>
            <textarea id="trailer-notes" rows="2" placeholder="Additional notes about this trailer..."></textarea>
          </div>

          <!-- TASK-1141 — per-unit notes thread (existing trailers only) -->
          <div class="form-group" id="trailer-notes-thread"></div>

          <!-- Yard / Location -->
          <div class="form-group" id="trailer-yard-group">
            <label for="trailer-yard">Yard / Location</label>
            <div style="display:flex;gap:8px;align-items:center;">
              <select id="trailer-yard" class="form-control" style="flex:1;"></select>
              <button type="button" class="btn-add-type" onclick="window.RosterModule.addYardInline('trailer-yard')" title="Add yard">
                <span class="material-icons" style="font-size:18px;">add</span>
              </button>
            </div>
          </div>

${_rentalSectionHtml('trailer')}

          <div class="form-group sms-optin-group">
            <label class="checkbox-switch">
              <input type="checkbox" id="trailer-active" checked>
              <span class="switch-slider"></span>
              <span class="switch-label">Active</span>
            </label>
            <p class="form-hint">Inactive trailers won't appear in job assignments or DVIR forms</p>
          </div>
          </div><!-- end modal-form-scroll -->

          <div class="modal-form-footer">
            <button type="button" class="btn btn-secondary" onclick="window.RosterModule.closeModal('trailer')">Cancel</button>
            <button type="submit" class="btn btn-primary" id="btn-save-trailer">Save</button>
          </div>
        </form>
      </div>
    </div>

    <!-- Add/Edit Rig Modal (TASK-1443) — the tractor+trailer(s) COMBINATION.
         Everything in here is combination-scoped: the coupling gap(s), the
         assembled continuous axle sequence, the as-configured overall dims.
         NOTHING in this modal writes back to a truck or trailer record. -->
    <div class="modal hidden" id="modal-rig">
      <div class="modal-content modal-form-wide">
        <div class="modal-header">
          <h3 id="modal-rig-title">Add Rig</h3>
          <button class="btn-icon" onclick="window.RosterModule.closeModal('rig')">
            <span class="material-icons">close</span>
          </button>
        </div>
        <form id="form-rig" class="modal-form-scrollable" novalidate onsubmit="window.RosterModule.saveRig(event)">
          <div class="modal-form-scroll">
          <input type="hidden" id="rig-id">
          <div id="rig-suggest"></div>

          <div class="form-row">
            <div class="form-group">
              <label for="rig-name">Rig Name *</label>
              <input type="text" id="rig-name" required maxlength="100" placeholder="15 + 104 + booster">
              <p class="form-hint">Filled in from the units you pick. Change it to what the yard calls it.</p>
            </div>
            <div class="form-group">
              <label for="rig-kind">Kind</label>
              <select id="rig-kind">
                ${RIG_KINDS.map(k => `<option value="${k.slug}">${k.label}</option>`).join('')}
              </select>
            </div>
          </div>

          <div class="form-group">
            <label for="rig-truck">Power Unit *</label>
            <select id="rig-truck" required onchange="window.RosterModule.refreshRigAssembly()"></select>
            <p class="form-hint">Contributes its own axles (steer → drives) to the front of the sequence.</p>
          </div>

          <div class="form-group">
            <label>Trailers — front to rear</label>
            <p class="form-hint" style="margin-top:0;">Add the trailer, then any booster or jeep behind it, in the order they actually run. Each row captures the gap from the unit in <strong>front</strong> of it.</p>
            <div id="rig-diagram" style="margin:0 0 8px;"></div>
            <div id="rig-units-container"></div>
            <button type="button" class="btn btn-secondary btn-sm" id="btn-add-rig-trailer" onclick="window.RosterModule.addRigTrailer()" style="font-size:0.75rem;color:var(--text-secondary);border:1px dashed var(--border-light);background:transparent;">
              <span class="material-icons" style="font-size:14px;">add</span> Add Trailer / Booster
            </button>
          </div>

          <div class="form-group">
            <label>Assembled Axle Sequence</label>
            <p class="form-hint" style="margin-top:0;">Derived from each unit's own permit axles joined by the coupling gaps. Recomputed on every save — never hand-edited, so it can't drift from the vehicles.</p>
            <div id="rig-assembly-preview"></div>
          </div>

          <details class="form-collapsible" id="rig-dims-section">
            <summary class="form-collapsible-header">
              <span class="material-icons">straighten</span>
              <span>Overall Dimensions (as configured, empty)</span>
              <span class="material-icons collapse-icon">expand_more</span>
            </summary>
            <div class="form-collapsible-content">
              <p class="form-hint" style="margin-top:0;">Measured on the assembled rig with no load. Deliberately not auto-summed from the units — fifth-wheel overlap makes an added-up length a made-up number, and this one goes on a filing. Load dimensions are per-move and are collected on the permit request, not here.</p>
              <div class="form-row">
                <div class="form-group">
                  <label for="rig-overall-length">Overall Length (ft)</label>
                  <input type="number" id="rig-overall-length" placeholder="75" min="0" step="0.1">
                </div>
                <div class="form-group">
                  <label for="rig-overall-width">Overall Width (ft)</label>
                  <input type="number" id="rig-overall-width" placeholder="11" min="0" step="0.1">
                </div>
              </div>
              <div class="form-group">
                <label for="rig-overall-height">Overall Height (ft)</label>
                <input type="number" id="rig-overall-height" placeholder="15" min="0" step="0.1">
              </div>
              <div id="rig-dims-reference" class="form-hint"></div>
            </div>
          </details>

          <div class="form-group">
            <label for="rig-notes">Notes</label>
            <textarea id="rig-notes" rows="2" maxlength="1000" placeholder="Fifth wheel runs all the way back for the Zeus pad..."></textarea>
          </div>
          </div><!-- end modal-form-scroll -->

          <div class="modal-form-footer">
            <button type="button" class="btn btn-secondary" id="btn-delete-rig" style="margin-right:auto;color:var(--danger, #DC2626);display:none;" onclick="window.RosterModule.deleteRig()">Delete</button>
            <button type="button" class="btn btn-secondary" onclick="window.RosterModule.closeModal('rig')">Cancel</button>
            <button type="submit" class="btn btn-primary" id="btn-save-rig">Save</button>
          </div>
        </form>
      </div>
    </div>

    <!-- Add/Edit Asset Modal -->
    <div class="modal hidden" id="modal-asset">
      <div class="modal-content modal-form-wide">
        <div class="modal-header">
          <h3 id="modal-asset-title">Add Asset</h3>
          <button class="btn-icon" onclick="window.RosterModule.closeModal('asset')">
            <span class="material-icons">close</span>
          </button>
        </div>
        <form id="form-asset" class="modal-form-scrollable" novalidate onsubmit="window.RosterModule.saveAsset(event)">
          <div class="modal-form-scroll">
          <input type="hidden" id="asset-id">

          <!-- Identity -->
          <div class="form-row">
            <div class="form-group">
              <label for="asset-part-number">Part/Serial # *</label>
              <input type="text" id="asset-part-number" required placeholder="DP-4500">
            </div>
            <div class="form-group">
              <label for="asset-category">Category *</label>
              <div class="select-with-add">
                <select id="asset-category" required>
                </select>
                <button type="button" class="btn-add-type" onclick="window.RosterModule.showAddTypeInline('assetCategories', 'asset-category')" title="Add custom category">
                  <span class="material-icons">add</span>
                </button>
              </div>
            </div>
          </div>
          <div class="form-group">
            <label for="asset-name">Name/Description *</label>
            <input type="text" id="asset-name" required placeholder="4.5&quot; Drill Pipe" data-maxlen="100">
          </div>
          <div class="form-row">
            <div class="form-group">
              <label for="asset-manufacturer">Manufacturer</label>
              <input type="text" id="asset-manufacturer" placeholder="Smith Tools">
            </div>
            <div class="form-group" style="display:flex;align-items:flex-end;">
              <div class="asset-photo-upload" id="asset-photo-area" style="display:flex;align-items:center;gap:8px;flex-wrap:wrap;">
                <input type="file" id="asset-photo-input" accept="image/*,.heic,.heif" style="display:none"
                  onchange="window.RosterModule.handleAssetPhoto(event)">
                <div id="asset-photo-preview" class="asset-photo-preview hidden"></div>
                <button type="button" class="btn btn-secondary btn-sm" id="asset-photo-btn"
                  onclick="document.getElementById('asset-photo-input').click()" style="white-space:nowrap;">
                  <span class="material-icons" style="font-size:16px;">add_a_photo</span> Photo
                </button>
              </div>
            </div>
          </div>

          <!-- Tracking -->
          <div style="margin-top:var(--spacing-sm);padding-top:var(--spacing-sm);border-top:1px solid var(--border-light);">
            <div class="form-row">
              <div class="form-group">
                <label for="asset-tracking-mode">Tracking Mode *</label>
                <select id="asset-tracking-mode" required onchange="window.RosterModule.toggleTrackingMode()">
                  <option value="quantity">Quantity-based (e.g., 50 pipes)</option>
                  <option value="individual">Individual (serial # tracked)</option>
                </select>
              </div>
              <div class="form-group" id="asset-quantity-group">
                <label for="asset-quantity">Quantity *</label>
                <input type="number" id="asset-quantity" min="0" placeholder="50">
              </div>
              <div class="form-group hidden" id="asset-serial-group">
                <label for="asset-serial-number">Serial Number *</label>
                <input type="text" id="asset-serial-number" placeholder="SN-12345">
              </div>
            </div>
            <div class="form-group" id="asset-quantity-type-group">
              <label for="asset-quantity-type">Quantity Type</label>
              <select id="asset-quantity-type" onchange="window.RosterModule.updateAssetHoursVisibility()">
                <option value="standard">Standard (tracked, not deducted)</option>
                <option value="consumable">Consumable (deducts when dispatched)</option>
                <option value="rental">Rental (tracks deployed vs available)</option>
              </select>
            </div>
            <div class="form-group" id="asset-unit-weight-group">
              <label for="asset-unit-weight">Unit Weight (lbs)</label>
              <input type="number" id="asset-unit-weight" min="0" step="1" placeholder="e.g. 2400">
              <p class="form-hint">Weight per unit. Used by Load Calculator.</p>
            </div>
            <div class="form-group hidden" id="asset-individual-rental-group">
              <label class="checkbox-switch">
                <input type="checkbox" id="asset-is-rental" onchange="window.RosterModule.updateAssetHoursVisibility()">
                <span class="switch-slider"></span>
                <span class="switch-label">Rental Asset (track deployment)</span>
              </label>
              <p class="form-hint">Enables deploy/return tracking in Dispatch.</p>
            </div>
          </div>

          <!-- Rental Options (contextual — shown for rental quantityType or isRental individual) -->
          <div id="asset-rental-options" class="hidden" style="margin-top:var(--spacing-sm);padding-top:var(--spacing-sm);border-top:1px solid var(--border-light);">
            <div class="form-group">
              <label class="checkbox-switch">
                <input type="checkbox" id="asset-require-return-inspection">
                <span class="switch-slider"></span>
                <span class="switch-label">Require Inspection on Return</span>
              </label>
              <p class="form-hint">When returned from a job without inspection, flags in Maintenance & Compliance.</p>
            </div>
          </div>

          <!-- Hours Tracking (contextual — hidden for consumables) -->
          <div id="asset-hours-section" style="margin-top:var(--spacing-sm);padding-top:var(--spacing-sm);border-top:1px solid var(--border-light);">
            <div class="form-group">
              <label class="checkbox-switch">
                <input type="checkbox" id="asset-hours-enabled" onchange="window.RosterModule.toggleAssetHoursFields()">
                <span class="switch-slider"></span>
                <span class="switch-label">Hours Tracking</span>
              </label>
              <p class="form-hint">For equipment with hour meters (generators, pumps, etc.)</p>
            </div>
            <div id="asset-hours-fields" class="hidden">
              <div class="form-row">
                <div class="form-group">
                  <label for="asset-current-hours">Current Hours</label>
                  <input type="number" id="asset-current-hours" step="0.1" min="0" placeholder="0">
                </div>
                <div class="form-group">
                  <label for="asset-pm-interval">PM Interval (hours)</label>
                  <input type="number" id="asset-pm-interval" step="1" min="1" placeholder="250">
                </div>
              </div>
              <div class="form-group">
                <label for="asset-last-pm-hours">Last PM at (hours)</label>
                <input type="number" id="asset-last-pm-hours" step="0.1" min="0" placeholder="0">
              </div>
            </div>
          </div>

          <!-- Yard / Location -->
          <div class="form-group" id="asset-yard-group">
            <label for="asset-yard">Yard / Location</label>
            <div style="display:flex;gap:8px;align-items:center;">
              <select id="asset-yard" class="form-control" style="flex:1;"></select>
              <button type="button" class="btn-add-type" onclick="window.RosterModule.addYardInline('asset-yard')" title="Add yard">
                <span class="material-icons" style="font-size:18px;">add</span>
              </button>
            </div>
          </div>

          <!-- Valuation — TASK-9575 slice D: shown only to the owner / BI / Billing
               (read from the server-only cost home, saved by setAssetValuation) -->
          <div id="asset-valuation-block" style="display:none;margin-top:var(--spacing-sm);padding-top:var(--spacing-sm);border-top:1px solid var(--border-light);">
            <div id="asset-cost-summary" style="font-size:13px;color:var(--text-secondary);margin-bottom:6px;"></div>
            <div class="form-row">
              <div class="form-group">
                <label for="asset-purchase-price">Purchase Price ($)</label>
                <input type="number" id="asset-purchase-price" step="0.01" min="0" placeholder="0.00">
              </div>
              <div class="form-group">
                <label for="asset-current-value">Current Value ($)</label>
                <input type="number" id="asset-current-value" step="0.01" min="0" placeholder="0.00">
              </div>
            </div>
            <!-- TASK-9724: shown only while the owner's "Track supply cost" switch is on -->
            <div class="form-group" id="asset-avg-cost-group" style="display:none;">
              <label for="asset-avg-unit-cost">Average unit cost ($) &mdash; what each sign-out is costed at</label>
              <input type="number" id="asset-avg-unit-cost" step="0.01" min="0" inputmode="decimal" placeholder="unknown">
            </div>
          </div>

          <!-- Billing — TASK-9725 S2 UI: its own section, not a Valuation field.
               Supply (quantity) items only; owner / Billing edit, BI reads
               (_assetCostAccess — the setting lives in the cost home, saved by
               setSupplyBillMode). Needs Track stock ON: an untracked item has no
               ledger rows, so nothing would ever bill. The code is picked from
               Billing > Item Codes (datalist); free text stays allowed. -->
          <div id="asset-billing-block" style="display:none;margin-top:var(--spacing-sm);padding-top:var(--spacing-sm);border-top:1px solid var(--border-light);">
            <div style="font-size:0.8rem;font-weight:600;color:var(--text-secondary);margin-bottom:6px;display:flex;align-items:center;gap:6px;">
              <span class="material-icons" aria-hidden="true" style="font-size:16px;">request_quote</span> Billing
            </div>
            <div class="form-row" id="asset-bill-mode-group">
              <div class="form-group">
                <label for="asset-bill-mode">When signed out on a job</label>
                <select id="asset-bill-mode" onchange="window.RosterModule.onAssetBillModeChange()">
                  <option value="company">Company cost</option>
                  <option value="customer">Bill to customer</option>
                </select>
              </div>
              <div class="form-group" id="asset-bill-code-group">
                <label for="asset-bill-item-code">Bills as Item Code</label>
                <input type="text" id="asset-bill-item-code" list="asset-bill-item-codes" maxlength="40" autocomplete="off" placeholder="Pick an Item Code" oninput="window.RosterModule.onAssetBillCodeInput()" onchange="window.RosterModule.onAssetBillCodeChange()">
                <datalist id="asset-bill-item-codes"></datalist>
              </div>
            </div>
            <div id="asset-bill-hint" style="font-size:0.75rem;color:var(--text-secondary);line-height:1.45;"></div>
          </div>

          <!-- Documents (TASK-1297) — generic categorized uploader, mirrors truck/trailer -->
          <div style="margin-top:var(--spacing-sm);padding-top:var(--spacing-sm);border-top:1px solid var(--border-light);">
            <details class="form-collapsible">
              <summary class="form-collapsible-header">
                <span class="material-icons">folder_open</span>
                <span>Documents <span class="doc-count-label" id="asset-general-doc-count" style="font-weight:normal;"></span></span>
                <span class="material-icons collapse-icon">expand_more</span>
              </summary>
              <div class="form-collapsible-content">
                <div id="asset-general-doc-list" class="general-doc-list"></div>
                <button type="button" class="btn btn-secondary btn-sm" id="asset-add-general-doc-btn" onclick="window.RosterModule.showAddGeneralDoc('asset')" style="margin-top:8px;">
                  <span class="material-icons">add</span> Add Document
                </button>
                <p class="form-hint">Warranties, service agreements, leases, permits, or any other documents (max 20)</p>
              </div>
            </details>
          </div>

${_rentalSectionHtml('asset')}

          <!-- Footer fields -->
          <div style="margin-top:var(--spacing-sm);padding-top:var(--spacing-sm);border-top:1px solid var(--border-light);">
            <div class="form-group">
              <label for="asset-notes">Notes</label>
              <textarea id="asset-notes" rows="2" placeholder="Stored in yard B..."></textarea>
            </div>
            <!-- TASK-1141 — per-unit notes thread (existing assets only) -->
            <div class="form-group" id="asset-notes-thread"></div>
            <div class="form-row">
              <div class="form-group sms-optin-group">
                <label class="checkbox-switch">
                  <input type="checkbox" id="asset-show-in-dispatch" checked>
                  <span class="switch-slider"></span>
                  <span class="switch-label">Show in Dispatch</span>
                </label>
              </div>
              <div class="form-group sms-optin-group">
                <label class="checkbox-switch">
                  <input type="checkbox" id="asset-active" checked>
                  <span class="switch-slider"></span>
                  <span class="switch-label">Active</span>
                </label>
              </div>
            </div>
          </div>
          </div><!-- end modal-form-scroll -->

          <div class="modal-form-footer">
            <button type="button" id="btn-asset-transfer" class="btn btn-secondary" style="display:none;margin-right:auto;" onclick="window.RosterModule.showYardTransferModal()">
              <span class="material-icons" style="font-size:16px;vertical-align:middle;">swap_horiz</span> Transfer
            </button>
            <button type="button" class="btn btn-secondary" onclick="window.RosterModule.closeModal('asset')">Cancel</button>
            <button type="submit" class="btn btn-primary" id="btn-save-asset">Save</button>
          </div>
        </form>
      </div>
    </div>

    <!-- Generate Order Link Modal -->
    <div class="modal hidden" id="modal-order-link">
      <div class="modal-content">
        <div class="modal-header">
          <h3>Generate Request Link</h3>
          <button class="btn-icon" onclick="window.RosterModule.closeModal('order-link')">
            <span class="material-icons">close</span>
          </button>
        </div>
        <div class="modal-body" style="padding: var(--spacing-lg);">
          <div class="form-group">
            <label for="order-link-customer">Customer (optional)</label>
            <select id="order-link-customer">
              <option value="">Any customer can use this link</option>
            </select>
            <p class="form-hint">Pre-select a customer or leave blank for a generic link</p>
          </div>
          <div class="form-group">
            <label for="order-link-expiry">Link Expiry</label>
            <select id="order-link-expiry">
              <option value="never">Never expires</option>
              <option value="24h">24 hours</option>
              <option value="7d">7 days</option>
              <option value="30d">30 days</option>
            </select>
          </div>
          <div class="form-group hidden" id="order-link-result-group">
            <label>Request Link</label>
            <div class="input-with-button">
              <input type="text" id="order-link-url" readonly>
              <button type="button" class="btn btn-secondary" onclick="window.RosterModule.copyOrderLink()">
                <span class="material-icons">content_copy</span>
              </button>
            </div>
            <p class="form-hint">Share this link with customers to let them request equipment</p>
          </div>
          <div class="form-actions">
            <button type="button" class="btn btn-secondary" onclick="window.RosterModule.closeModal('order-link')">Close</button>
            <button type="button" class="btn btn-primary" id="btn-generate-link" onclick="window.RosterModule.generateOrderRequestLink()">
              <span class="material-icons">link</span> Generate Link
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Order Details Modal -->
    <div class="modal hidden" id="modal-order-details">
      <div class="modal-content modal-large">
        <div class="modal-header">
          <h3 id="modal-order-title">Order Details</h3>
          <button class="btn-icon" onclick="window.RosterModule.closeModal('order-details')">
            <span class="material-icons">close</span>
          </button>
        </div>
        <div class="modal-body" style="padding: var(--spacing-lg);">
          <div id="order-details-content">
            <!-- Populated dynamically -->
          </div>

          <!-- Pizza Tracker -->
          <div class="pizza-tracker-section">
            <h4>Order Status</h4>
            <div class="pizza-tracker" id="order-pizza-tracker">
              <!-- Populated dynamically -->
            </div>
            <div class="pizza-tracker-actions" id="order-status-actions">
              <!-- Status update buttons populated dynamically -->
            </div>
          </div>

          <!-- Status History -->
          <div class="status-history-section">
            <h4>Status History</h4>
            <div id="order-status-history">
              <!-- Populated dynamically -->
            </div>
          </div>
        </div>

          <!-- Delivery Ticket Section (shown when delivered) -->
          <div class="delivery-ticket-section hidden" id="order-delivery-ticket-section">
            <h4>Delivery Ticket</h4>
            <div class="delivery-ticket-status" id="delivery-ticket-status">
              <!-- Populated dynamically -->
            </div>
          </div>
        </div>
        <div class="form-actions">
          <button type="button" class="btn btn-secondary" onclick="window.RosterModule.closeModal('order-details')">Close</button>
          <button type="button" class="btn btn-primary hidden" id="btn-view-delivery-ticket" onclick="window.RosterModule.viewDeliveryTicket()">
            <span class="material-icons">receipt</span> View Ticket
          </button>
        </div>
      </div>
    </div>

    <!-- Customer List Modal -->
    <div class="modal hidden" id="modal-customer-list">
      <div class="modal-content modal-large">
        <div class="modal-header">
          <h3>Customers</h3>
          <button class="btn-icon" onclick="window.RosterModule.closeModal('customer-list')">
            <span class="material-icons">close</span>
          </button>
        </div>
        <div class="modal-body" style="padding: var(--spacing-lg);">
          <p class="form-hint" style="margin-bottom: var(--spacing-lg);">
            Customers are managed in the Dispatch module. This is a read-only list for reference.
          </p>
          <div id="customer-list-content">
            <!-- Populated dynamically -->
          </div>
        </div>
        <div class="form-actions">
          <button type="button" class="btn btn-secondary" onclick="window.RosterModule.closeModal('customer-list')">Close</button>
        </div>
      </div>
    </div>

    <!-- Delete Confirmation Modal -->
    <div class="modal hidden" id="modal-delete">
      <div class="modal-content modal-small" style="text-align:center;padding:var(--spacing-xl);">
        <div style="display:flex;flex-direction:column;align-items:center;gap:var(--spacing-sm);margin-bottom:var(--spacing-md);">
          <span class="material-icons" style="font-size:48px;color:#dc2626;">delete_forever</span>
          <h3 style="font-size:1.25rem;font-weight:600;margin:0;">Confirm Delete</h3>
        </div>
        <div id="delete-message" style="color:var(--text-secondary);margin-bottom:var(--spacing-lg);line-height:1.5;">
          <p>Are you sure you want to delete this item?</p>
        </div>
        <div style="display:flex;gap:var(--spacing-md);justify-content:center;">
          <button class="btn btn-secondary" style="flex:1;max-width:140px;" onclick="window.RosterModule.closeModal('delete')">Cancel</button>
          <button class="btn btn-danger" style="flex:1;max-width:140px;" id="btn-confirm-delete">Delete</button>
        </div>
      </div>
    </div>

    <!-- Delivery Ticket Modal -->
    <div class="modal hidden" id="modal-delivery-ticket">
      <div class="modal-content modal-large">
        <div class="modal-header">
          <h3>Delivery Ticket</h3>
          <button class="btn-icon" onclick="window.RosterModule.closeModal('delivery-ticket')">
            <span class="material-icons">close</span>
          </button>
        </div>
        <div class="modal-body" style="padding: var(--spacing-lg);">
          <div id="delivery-ticket-content">
            <!-- Populated dynamically -->
          </div>
        </div>
        <div class="form-actions" id="delivery-ticket-actions">
          <button type="button" class="btn btn-secondary" onclick="window.RosterModule.closeModal('delivery-ticket')">Close</button>
          <button type="button" class="btn btn-primary" id="btn-print-ticket" onclick="window.RosterModule.printDeliveryTicket()">
            <span class="material-icons">print</span> Print
          </button>
        </div>
      </div>
    </div>

    <!-- Signature Request Modal -->
    <div class="modal hidden" id="modal-signature-request">
      <div class="modal-content modal-small">
        <div class="modal-header">
          <h3>Request Signature</h3>
          <button class="btn-icon" onclick="window.RosterModule.closeModal('signature-request')">
            <span class="material-icons">close</span>
          </button>
        </div>
        <div class="modal-body" style="padding: var(--spacing-lg);">
          <p class="form-hint">Send the customer a link to sign the delivery ticket.</p>
          <div class="form-group" style="margin-top: var(--spacing-md);">
            <label>Signature Link</label>
            <div class="input-with-copy">
              <input type="text" id="signature-link" class="form-control" readonly>
              <button class="btn btn-icon" onclick="window.RosterModule.copySignatureLink()" title="Copy link">
                <span class="material-icons">content_copy</span>
              </button>
            </div>
          </div>
        </div>
        <div class="form-actions">
          <button type="button" class="btn btn-secondary" onclick="window.RosterModule.closeModal('signature-request')">Close</button>
        </div>
      </div>
    </div>

    <!-- Inventory Receipt Modal -->
    <div class="modal hidden" id="modal-inventory-receipt">
      <div class="modal-content modal-form-wide">
        <div class="modal-header">
          <h3 id="receipt-modal-title">Add Inventory</h3>
          <button class="btn-icon" onclick="window.RosterModule.closeModal('inventory-receipt')">
            <span class="material-icons">close</span>
          </button>
        </div>
        <form id="form-inventory-receipt" class="modal-form-scrollable" novalidate onsubmit="window.RosterModule.saveReceipt(event)">
          <div class="modal-form-scroll">
            <input type="hidden" id="receipt-asset-id">
            <div class="form-group">
              <label>Asset</label>
              <input type="text" id="receipt-asset-name" class="form-control" readonly>
            </div>
            <div style="display:grid;grid-template-columns:1fr 1fr;gap:12px;">
              <div class="form-group">
                <label>Quantity Received *</label>
                <input type="number" id="receipt-qty" class="form-control" min="1" required oninput="window.RosterModule.calcReceiptTotal()">
              </div>
              <div class="form-group">
                <label>Date Received</label>
                <input type="date" id="receipt-date" class="form-control">
              </div>
            </div>
            <div id="receipt-pro-fields">
              <div style="display:grid;grid-template-columns:1fr 1fr 1fr;gap:12px;">
                <div class="form-group">
                  <label>Unit Cost ($)</label>
                  <input type="number" id="receipt-unit-cost" class="form-control" step="0.01" min="0" oninput="window.RosterModule.calcReceiptTotal()">
                </div>
                <div class="form-group">
                  <label>Tax ($)</label>
                  <input type="number" id="receipt-tax" class="form-control" step="0.01" min="0" value="0" oninput="window.RosterModule.calcReceiptTotal()">
                </div>
                <div class="form-group">
                  <label>Total</label>
                  <input type="text" id="receipt-total" class="form-control" readonly style="font-weight:700;background:var(--bg-secondary);">
                </div>
              </div>
              <div class="form-group">
                <label>Vendor</label>
                <select id="receipt-vendor" class="form-control">
                  <option value="">— None —</option>
                </select>
              </div>
              <div class="form-group">
                <label>PO / Invoice #</label>
                <input type="text" id="receipt-po" class="form-control" placeholder="Optional reference">
              </div>
              <div class="form-group">
                <label>Receipt / Invoice Upload</label>
                <input type="file" id="receipt-file" class="form-control" accept="image/*,.pdf">
              </div>
            </div>
            <div class="form-group">
              <label>Notes</label>
              <textarea id="receipt-notes" class="form-control" rows="2" placeholder="Optional notes"></textarea>
            </div>
          </div>
          <div class="form-actions">
            <button type="button" class="btn btn-secondary" onclick="window.RosterModule.closeModal('inventory-receipt')">Cancel</button>
            <button type="submit" class="btn btn-primary" id="btn-save-receipt">
              <span class="material-icons">add_circle</span> Add Inventory
            </button>
          </div>
        </form>
      </div>
    </div>

    <!-- Inventory Adjustment Modal -->
    <div class="modal hidden" id="modal-inventory-adjustment">
      <div class="modal-content modal-small" style="max-width:440px;">
        <div class="modal-header">
          <h3>Adjust Inventory</h3>
          <button class="btn-icon" onclick="window.RosterModule.closeModal('inventory-adjustment')">
            <span class="material-icons">close</span>
          </button>
        </div>
        <form id="form-inventory-adjustment" novalidate onsubmit="window.RosterModule.saveAdjustment(event)">
          <div style="padding:16px 24px;">
            <input type="hidden" id="adjust-asset-id">
            <div class="form-group">
              <label>Asset</label>
              <input type="text" id="adjust-asset-name" class="form-control" readonly>
            </div>
            <div style="display:grid;grid-template-columns:1fr 1fr;gap:12px;">
              <div class="form-group">
                <label>Quantity (negative = remove)</label>
                <input type="number" id="adjust-qty" class="form-control" required>
              </div>
              <div class="form-group">
                <label>Reason</label>
                <select id="adjust-reason" class="form-control" required>
                  <option value="">Select...</option>
                  <option value="damaged">Damaged</option>
                  <option value="lost">Lost</option>
                  <option value="returned">Returned</option>
                  <option value="count-correction">Count Correction</option>
                  <option value="other">Other</option>
                </select>
              </div>
            </div>
            <div class="form-group">
              <label>Notes</label>
              <textarea id="adjust-notes" class="form-control" rows="2" placeholder="Optional details"></textarea>
            </div>
          </div>
          <div class="form-actions">
            <button type="button" class="btn btn-secondary" onclick="window.RosterModule.closeModal('inventory-adjustment')">Cancel</button>
            <button type="submit" class="btn btn-primary" id="btn-save-adjustment">
              <span class="material-icons">tune</span> Apply Adjustment
            </button>
          </div>
        </form>
      </div>
    </div>

    <!-- Yard Transfer Modal -->
    <div class="modal hidden" id="modal-yard-transfer">
      <div class="modal-content modal-small" style="max-width:440px;">
        <div class="modal-header">
          <h3><span class="material-icons" style="vertical-align:middle;margin-right:4px;">swap_horiz</span> Transfer to Yard</h3>
          <button class="btn-icon" onclick="window.RosterModule.closeModal('yard-transfer')">
            <span class="material-icons">close</span>
          </button>
        </div>
        <form id="form-yard-transfer" novalidate onsubmit="window.RosterModule.executeYardTransfer(event)">
          <div style="padding:16px 24px;">
            <input type="hidden" id="transfer-asset-id">
            <div class="form-group">
              <label>Asset</label>
              <input type="text" id="transfer-asset-name" class="form-control" readonly>
            </div>
            <div class="form-group">
              <label>From</label>
              <input type="text" id="transfer-from-yard" class="form-control" readonly style="background:var(--bg-secondary);">
            </div>
            <div style="display:grid;grid-template-columns:1fr 1fr;gap:12px;">
              <div class="form-group">
                <label>Destination Yard *</label>
                <select id="transfer-to-yard" class="form-control" required>
                  <option value="">Select...</option>
                </select>
              </div>
              <div class="form-group">
                <label>Quantity *</label>
                <input type="number" id="transfer-qty" class="form-control" min="1" required>
              </div>
            </div>
            <p id="transfer-available" style="font-size:0.8rem;color:var(--text-muted);margin-top:-8px;"></p>
            <div class="form-group">
              <label>Notes</label>
              <textarea id="transfer-notes" class="form-control" rows="2" placeholder="Optional — reason for transfer"></textarea>
            </div>
          </div>
          <div class="form-actions">
            <button type="button" class="btn btn-secondary" onclick="window.RosterModule.closeModal('yard-transfer')">Cancel</button>
            <button type="submit" class="btn btn-primary" id="btn-execute-transfer">
              <span class="material-icons">swap_horiz</span> Transfer
            </button>
          </div>
        </form>
      </div>
    </div>

    <!-- Staff Login Invite Modal (TASK-319 slice 5) -->
    <div class="modal hidden" id="modal-staff-login-invite">
      <div class="modal-content modal-form-narrow">
        <div class="modal-header">
          <h3 id="staff-login-invite-title">Set up login</h3>
          <button class="btn-icon" onclick="window.RosterModule.closeStaffLoginInviteModal()">
            <span class="material-icons">close</span>
          </button>
        </div>
        <form id="form-staff-login-invite" class="modal-form-scrollable" novalidate onsubmit="window.RosterModule.submitStaffLoginInvite(event)">
          <div class="modal-form-scroll">
            <input type="hidden" id="staff-login-invite-person-id">
            <!-- TASK-1489 — '1' when this is a PERSONLESS invite (outside user, no
                 roster/employee record). Read by submitStaffLoginInvite to pick
                 which contract to send. Always reset by both open paths. -->
            <input type="hidden" id="staff-login-invite-personless" value="">

            <div id="staff-login-invite-form-body">
              <div id="staff-login-cap-banner" style="display:none;padding:8px 12px;border-radius:6px;font-size:0.85rem;margin-bottom:12px;"></div>

              <p id="staff-login-invite-intro-person" style="font-size:0.9rem;color:var(--text-secondary);margin:0 0 var(--spacing-md) 0;">
                Sending an invite to <strong id="staff-login-invite-person-name"></strong>. They'll get a link to create their own password and access to the modules you grant below.
              </p>

              <!-- TASK-1489 / TASK-1500 — personless intro. Says the quiet part out
                   loud, because "will this add them to my roster?" is the exact
                   question the carrier is asking when they click this button. -->
              <div id="staff-login-invite-intro-personless" style="display:none;padding:10px 12px;margin:0 0 var(--spacing-md) 0;border:1px solid var(--brand-blue,#3B82F6);border-radius:6px;background:#eff6ff;color:var(--text-primary);font-size:0.85rem;line-height:1.45;">
                <strong>Outside user — no employee record.</strong><br>
                They get a link to create their own login with only the modules you grant below. They will <strong>not</strong> appear on your roster, in HR, on the schedule, or in payroll — and sensitive employee data (SSN, W-4, I-9, pay) stays locked to admins. Revoke any time from Settings &rarr; Team Access.
              </div>

              <!-- TASK-1489 — personless only: there is no person doc to read a
                   name from, so the operator types one. Optional. -->
              <div class="form-group" id="staff-login-invite-name-group" style="display:none;">
                <label for="staff-login-invite-name">Their name</label>
                <input type="text" id="staff-login-invite-name" maxlength="80" placeholder="Jane Smith" autocomplete="off">
                <p class="form-hint">Used in the invite email and shown in Settings &rarr; Team Access. Optional.</p>
              </div>

              <div class="form-group">
                <label for="staff-login-invite-email">Email *</label>
                <input type="email" id="staff-login-invite-email" required placeholder="name@company.com">
                <!-- TASK-4840 — this field is prefilled from person.email (the WORK
                     address) and never from person.personalEmail, but the person card
                     shows both and nothing used to say which one becomes the login.
                     Alyse Hymer spent a morning trying to sign in with the personal
                     one, getting "wrong password" from Firebase because no account
                     exists on it at all. Say it out loud. -->
                <p class="form-hint">This becomes their sign-in address — not their personal email.</p>
              </div>

              <div class="form-group" id="staff-login-invite-delivery-group">
                <label>Delivery</label>
                <!-- TASK-10027 muted-tenant UI hints — what this invite will really send (app.js paintTenantSendNotices). -->
                <div data-tenant-send-notice="invite" style="display:none;"></div>
                <div style="display:flex;gap:16px;flex-wrap:wrap;">
                  <label style="display:flex;align-items:center;gap:6px;cursor:pointer;font-weight:400;">
                    <input type="radio" name="staff-login-delivery" value="email" checked> Email only
                  </label>
                  <label style="display:flex;align-items:center;gap:6px;cursor:pointer;font-weight:400;" id="staff-login-delivery-both-label">
                    <input type="radio" name="staff-login-delivery" value="both" id="staff-login-delivery-both"> Email + Text
                  </label>
                </div>
                <!-- TASK-4840 — "Text only" was removed on purpose. The invite is a
                     long opaque token link; an iPhone will not make it tappable for a
                     number the person has never replied to, and there is nothing to
                     type instead. The email address is REQUIRED on this form, so an
                     email always exists — a text-only invite could never be the right
                     answer, it just looked like a choice. sendUserInvite coerces any
                     leftover 'sms' payload to 'both' at the trust boundary. -->
                <p class="form-hint" id="staff-login-delivery-pref-hint" style="display:none;"></p>
                <p class="form-hint" id="staff-login-phone-hint" style="display:none;">The text is a heads-up — the working link is always in the email. Text goes to <span id="staff-login-invite-phone-display"></span>.</p>
                <p class="form-hint" id="staff-login-phone-missing-hint" style="display:none;color:var(--text-muted);">No phone on file — add one to enable SMS delivery.</p>
              </div>

              ${_staffLoginRoleAndModuleGridHtml('invite')}

              <div id="staff-login-invite-error" style="display:none;padding:8px 12px;border-radius:6px;background:#fee2e2;color:#991b1b;font-size:0.85rem;margin-top:8px;"></div>
            </div>

            <!-- Success state (hidden until send succeeds) -->
            <div id="staff-login-invite-success" style="display:none;text-align:center;padding:var(--spacing-md) 0;">
              <span class="material-icons" style="font-size:48px;color:#10b981;">check_circle</span>
              <h4 style="margin:var(--spacing-sm) 0;color:var(--text-primary);">Invite sent</h4>
              <p id="staff-login-invite-success-detail" style="color:var(--text-secondary);margin:0 0 var(--spacing-md) 0;"></p>
              <div id="staff-login-invite-success-existing" style="display:none;padding:8px 12px;border-radius:6px;background:#dbeafe;color:#1e40af;font-size:0.85rem;margin-bottom:var(--spacing-md);">
                This person already has a DispatchDeck account — accepting will add this tenant to their existing login.
              </div>
              <p id="staff-login-invite-success-length" style="font-size:0.8rem;color:var(--text-secondary);">The invite link works for 7 days unless the account owner changed it in Settings → Security. You can resend or revoke from Settings → Team Access.</p>
            </div>
          </div>
          <div class="form-actions" id="staff-login-invite-actions">
            <button type="button" class="btn btn-secondary" onclick="window.RosterModule.closeStaffLoginInviteModal()">Cancel</button>
            <button type="submit" class="btn btn-primary" id="btn-staff-login-invite-send">
              <span class="material-icons">send</span> Send invite
            </button>
          </div>
          <div class="form-actions" id="staff-login-invite-success-actions" style="display:none;">
            <button type="button" class="btn btn-primary" onclick="window.RosterModule.closeStaffLoginInviteModal()" style="width:100%;">Done</button>
          </div>
        </form>
      </div>
    </div>

    <!-- TASK-366 — Edit Access Modal (sibling to invite modal above) -->
    <!-- Surfaces from the Active-login card on the Roster person edit panel.
         Re-uses the role-preset rail + custom-module grid via the shared
         _staffLoginRoleAndModuleGridHtml('edit') helper. Distinct radio names
         (-edit suffix) so it can coexist with the invite modal in the DOM. -->
    <div class="modal hidden" id="modal-staff-login-edit">
      <div class="modal-content modal-form-narrow">
        <div class="modal-header">
          <h3 id="staff-login-edit-title">Edit access</h3>
          <button class="btn-icon" onclick="window.RosterModule.closeStaffLoginEditModal()">
            <span class="material-icons">close</span>
          </button>
        </div>
        <form id="form-staff-login-edit" class="modal-form-scrollable" novalidate onsubmit="window.RosterModule.submitStaffLoginEdit(event)">
          <div class="modal-form-scroll">
            <input type="hidden" id="staff-login-edit-uid">

            <p style="font-size:0.9rem;color:var(--text-secondary);margin:0 0 var(--spacing-md) 0;">
              Updating access for <strong id="staff-login-edit-person-name"></strong>
              (<span id="staff-login-edit-email" style="word-break:break-all;"></span>).
              They keep their existing login — only the modules + role change.
              To remove all access entirely, use <strong>Revoke access</strong> on the person card instead.
            </p>

            ${_staffLoginRoleAndModuleGridHtml('edit')}

            <!-- TASK-9369 — Home branch: a DEFAULT filter for Roster → Assets
                 (the tab opens on this branch; they can switch to any other).
                 Not a read boundary — every branch stays visible to everyone
                 with the module. Shown only when the tenant has facilities;
                 populated by editStaffLoginAccess. -->
            <div class="form-group" id="staff-login-homebranch-wrap-edit" style="display:none;">
              <label for="staff-login-homebranch-edit">Home branch <span style="font-weight:400;color:var(--text-muted);font-size:0.85rem;">— where Assets opens for them</span></label>
              <select id="staff-login-homebranch-edit" class="form-control"></select>
              <p id="staff-login-homebranch-note-edit" style="display:none;margin:6px 0 0;font-size:0.8rem;color:#92400e;"></p>
              <p style="margin:6px 0 0;font-size:0.78rem;color:var(--text-secondary);">A starting filter, not a restriction — they can still look at every branch.</p>
            </div>

            <div id="staff-login-edit-empty-hint" style="display:none;padding:8px 12px;border-radius:6px;background:#fef3c7;color:#92400e;font-size:0.82rem;margin-top:8px;">
              <span class="material-icons" style="font-size:14px;vertical-align:-3px;">info</span>
              To remove all access, use <strong>Revoke access</strong> instead.
            </div>
            <div id="staff-login-edit-error" style="display:none;padding:8px 12px;border-radius:6px;background:#fee2e2;color:#991b1b;font-size:0.85rem;margin-top:8px;"></div>
          </div>
          <div class="form-actions">
            <button type="button" class="btn btn-secondary" onclick="window.RosterModule.closeStaffLoginEditModal()">Cancel</button>
            <button type="submit" class="btn btn-primary" id="btn-staff-login-edit-save">
              <span class="material-icons">check</span> Update access
            </button>
          </div>
        </form>
      </div>
    </div>

    <!-- TASK-1444 — Paste / import axle rows. Takes a spreadsheet block OR a
         permit you already filed (the LoadPass/ND export shape). Everything is
         previewed and ticked BEFORE it reaches the grid, and the grid is still
         only the form — nothing is written until the vehicle is saved. -->
    <div class="modal hidden" id="modal-paxle-import">
      <div class="modal-content modal-form-wide">
        <div class="modal-header">
          <h3>Paste or import axle rows</h3>
          <button class="btn-icon" onclick="window.RosterModule.closeModal('paxle-import')">
            <span class="material-icons">close</span>
          </button>
        </div>
        <div class="modal-form-scrollable">
          <div class="modal-form-scroll">
            <input type="hidden" id="paxle-import-kind">
            <div class="form-group">
              <label for="paxle-import-text">Paste here</label>
              <p class="form-hint" style="margin-top:0;">
                Either <strong>rows copied out of Excel or Google Sheets</strong> — columns in the order
                Tires, Tire&nbsp;W, Track&nbsp;W, spacing to the next axle, group break —
                or the <strong>contents of a permit you already filed</strong>, if you have the file.
                A header row is detected and skipped.
              </p>
              <textarea id="paxle-import-text" rows="7" class="paxle-import-text" spellcheck="false"
                placeholder="2&#9;385 mm&#9;8' 6&quot;&#9;14' 6&quot;&#9;y&#10;4&#9;275 mm&#9;8' 6&quot;&#9;5' 0&quot;&#10;4&#9;275 mm&#9;8' 6&quot;&#9;"></textarea>
              <button type="button" class="btn btn-secondary btn-sm" onclick="window.RosterModule.readPaxleImport()">
                <span class="material-icons" style="font-size:14px;">search</span> Read it
              </button>
            </div>
            <div id="paxle-import-preview"></div>
          </div>
          <div class="form-actions" id="paxle-import-actions" style="display:none;">
            <button type="button" class="btn btn-secondary" onclick="window.RosterModule.closeModal('paxle-import')">Cancel</button>
            <button type="button" class="btn btn-secondary" onclick="window.RosterModule.applyPaxleImport('replace')">
              Replace the table
            </button>
            <button type="button" class="btn btn-primary" onclick="window.RosterModule.applyPaxleImport('append')">
              <span class="material-icons">playlist_add</span> Add ticked rows
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- TASK-1444 — Fleet permit readiness. Read-only checklist: what each unit
         is still missing before its half of a permit packet can be assembled. -->
    <div class="modal hidden" id="modal-permit-readiness">
      <div class="modal-content modal-form-wide">
        <div class="modal-header">
          <h3>Permit readiness</h3>
          <button class="btn-icon" onclick="window.RosterModule.closeModal('permit-readiness')">
            <span class="material-icons">close</span>
          </button>
        </div>
        <div class="modal-form-scrollable">
          <div class="modal-form-scroll" id="permit-readiness-body"></div>
          <div class="form-actions">
            <button type="button" class="btn btn-secondary" onclick="window.RosterModule.closeModal('permit-readiness')">Close</button>
          </div>
        </div>
      </div>
    </div>
  `;
}

let _rosterModalsMounted = false;

// Mounts Roster's Add/Edit modals to document.body so they're reachable
// from any view (Roster itself or the Compliance partner portal).
function ensureModalsMounted() {
  if (_rosterModalsMounted || document.getElementById('modal-person')) {
    _rosterModalsMounted = true;
    return;
  }
  const wrapper = document.createElement('div');
  wrapper.innerHTML = getRosterModalsHTML();
  while (wrapper.firstChild) {
    document.body.appendChild(wrapper.firstChild);
  }
  setupModals();
  _rosterModalsMounted = true;
}

// ============================================
// PENDING EDIT (from Maintenance "Edit in Roster")
// ============================================
function consumePendingEdit(dataType) {
  const pending = window._rosterPendingEdit;
  if (!pending) return;
  if (pending.type === 'truck' && dataType === 'truck') {
    const found = RosterState.trucks.find(t => t.id === pending.id);
    if (found) {
      window._rosterPendingEdit = null;
      switchTab('trucks');
      setTimeout(() => editTruck(pending.id), 50);
    }
  } else if (pending.type === 'trailer' && dataType === 'trailer') {
    const found = RosterState.trailers.find(t => t.id === pending.id);
    if (found) {
      window._rosterPendingEdit = null;
      switchTab('trailers');
      setTimeout(() => editTrailer(pending.id), 50);
    }
  } else if (pending.type === 'asset' && dataType === 'asset') {
    const found = RosterState.assets.find(a => a.id === pending.id);
    if (found) {
      window._rosterPendingEdit = null;
      switchTab('assets');
      setTimeout(() => editAsset(pending.id), 50);
    }
  }
}

// TASK-9717 — #/roster?dd_open=vehicle_registration|vehicle_insurance|vehicle_photo&vehicle=<id>
// or roster_photo_intake (app.js parks it as AppState.pendingFocus, window.OpenLinks).
// takeHint consumes it once (stale / other company = null). Session checks
// (driver / link / PIN), the access re-check, the wait for this mount's live
// data, the unit read-back and the open: the LAZY rosterOpenLink.js.
function _drainRosterOpenHint() {
  const hint = window.AppState?.pendingFocus?.view === 'roster' && window.OpenLinks?.takeHint('roster');
  if (!hint) return;
  const tenant = window.AppState.tenant || null;
  const host = document.querySelector('.roster-view');
  // COD r2 — ready only once THIS mount's snapshots replaced the lists (an
  // error handler sets the loaded flags but keeps the old company's arrays).
  const t0 = RosterState.trucks, l0 = RosterState.trailers;
  __ddChunkWrap(import('./rosterOpenLink.js?v=1'))
    .then((m) => m.openRosterHint(hint, {
      tenant, host,
      ready: () => RosterState._trucksLoaded && RosterState._trailersLoaded && RosterState.trucks !== t0 && RosterState.trailers !== l0,
      trucks: () => RosterState.trucks, trailers: () => RosterState.trailers,
      editTruck, editTrailer, switchTab,
    }))
    .catch(() => window.showToast?.('Could not open that from the link. Open the unit in Roster.', 'error'));
}

// ============================================
// TAB SWITCHING
// ============================================
function setupTabs() {
  document.querySelectorAll('.roster-tab').forEach(tab => {
    tab.addEventListener('click', () => {
      const tabName = tab.dataset.tab;
      switchTab(tabName);
    });
  });
}

function switchTab(tabName) {
  RosterState.activeTab = tabName;

  // Update tab buttons
  document.querySelectorAll('.roster-tab').forEach(tab => {
    tab.classList.toggle('active', tab.dataset.tab === tabName);
  });

  // Update panels
  document.querySelectorAll('.roster-panel').forEach(panel => {
    panel.classList.toggle('active', panel.id === `panel-${tabName}`);
  });

  // TASK-9369 — first open of Assets this session → the member's home branch.
  if (tabName === 'assets') _applyAssetsHomeBranchDefault();
}

// ============================================
// TASK-9369 — MEMBER HOME BRANCH (default Assets filter)
// ============================================
// members/{uid}.yardId (carried on AppState.memberAccess.yardId by BOTH writers
// in app.js) is a CONVENIENCE DEFAULT: the Assets tab opens filtered to that
// branch, in Items mode with the breadcrumb, once per session, and only if the
// user has not touched the yard filter yet. It is NOT a read boundary — every
// branch stays one click away — and no code may ever treat it as one
// (Chris 2026-09-24; TASK-9375 pins the meaning). Not the same field as
// people/{id}.yardId (the person's assigned facility, hr.js).
//
// Scope CUT per COD Skeptic: Assets only. No people/trucks/trailers cascade.

let _assetsHomeBranchApplied = false;

/**
 * PURE. The slug to preselect, or null for "all" (no memberAccess, no yardId,
 * or a slug no longer in settings.yards — deleteYard leaves dangling tags).
 * An owner has no members doc (memberAccess null) so they land on "all" by
 * construction; any member with a valid slug gets it, whatever their role.
 */
function resolveAssetsHomeBranch(memberAccess, yards) {
  const slug = memberAccess && typeof memberAccess.yardId === 'string' ? memberAccess.yardId : '';
  if (!slug || slug === 'all') return null;
  return (Array.isArray(yards) && yards.some(y => y && y.slug === slug)) ? slug : null;
}

function _applyAssetsHomeBranchDefault() {
  if (_assetsHomeBranchApplied) return;
  // memberAccess still loading (cold start) → not decided yet; try again on the
  // next tab switch rather than locking in "all" against a doc we haven't read.
  if (window.AppState?.memberAccessPending) return;
  const ma = window.AppState?.memberAccess;
  // No member doc (owner / PIN session), or a member with no home branch →
  // definitive "all"; mark done so a later snapshot never yanks the filter.
  if (!ma || !ma.yardId) { _assetsHomeBranchApplied = true; return; }
  // Settings (yards) not here yet → try again on the next tab switch.
  if (!window.AppState?.settings) return;
  const slug = resolveAssetsHomeBranch(ma, getYardsList());
  if (!slug) { _assetsHomeBranchApplied = true; return; }       // dangling slug → all, silently
  // Untouched filter only — never override a choice the user already made.
  if (RosterState.filters.assets.yard !== 'all' || RosterState._assetsBranchDrill) { _assetsHomeBranchApplied = true; return; }
  const select = document.getElementById('filter-assets-yard');
  // The dropdown is (re)built by refreshYardFilters; without the option the DOM
  // and state would disagree and the next keystroke would wipe the default.
  if (!select || !Array.from(select.options).some(o => o.value === slug)) return;
  _assetsHomeBranchApplied = true;
  drillAssetsBranch(slug);
}

// ============================================
// MODAL HANDLERS
// ============================================
function setupModals() {
  // Close modal on backdrop click (but not for form modals to prevent data loss)
  document.querySelectorAll('.modal').forEach(modal => {
    modal.addEventListener('click', (e) => {
      if (e.target === modal) {
        // Don't close modals with form inputs on backdrop click (prevents data loss)
        const hasFormInputs = modal.querySelector('input, select, textarea');
        if (hasFormInputs) return;

        modal.classList.add('hidden');
      }
    });
  });

  // Phone number preview - shows formatted number as user types
  const phoneInput = document.getElementById('person-phone');
  if (phoneInput) {
    phoneInput.addEventListener('input', () => {
      updatePhonePreview('person-phone', 'person-phone-preview');
      // TASK-4728 — re-evaluate the shared-phone advisory on every keystroke,
      // against the typed value rather than the saved doc.
      _syncPersonPhoneCollisionWarning();
    });
  }
}

// ============================================
// DATA SUBSCRIPTIONS (Real-time)
// ============================================
function subscribeToData(tenantId) {
  // For demo mode, use a fixed demo tenant
  const tenant = tenantId === 'demo-tenant' ? 'demo' : tenantId;

  // TASK-9359 — DOB / licence number / state / expiry / CDL number live in the
  // peopleIdentity vault, not on the broadcast people doc. Overlay them onto
  // RosterState.people in place (vault-over-legacy) so the DQ section, expiry
  // badges and every other reader keep reading person.dob / person.dlExpiryDate.
  window.PersonIdentity?.subscribe?.(tenant, {
    key: 'roster',
    onUpdate: () => {
      try {
        window.PersonIdentity.overlayList(RosterState.people);
        if (RosterState._peopleLoaded) renderPeopleList();
        // An edit form opened while the vault was still loading rendered its
        // identity inputs locked; unlock + fill them now (only while still
        // locked, so typed input is never clobbered).
        const _openId = document.getElementById('person-id')?.value;
        const _dobEl = document.getElementById('person-dq-dob');
        if (_openId && _dobEl && _dobEl.disabled) {
          const _p = RosterState.people.find(x => x.id === _openId);
          if (_p) {
            _rosterApplyIdentityLock(_p);
            if (!_dobEl.disabled) {
              _dobEl.value = _p.dob || '';
              const _v = (id, val) => { const el = document.getElementById(id); if (el) el.value = val || ''; };
              _v('person-dq-dl-state', _p.dlState); _v('person-dq-dl-number', _p.dlNumber); _v('person-dq-dl-expiry', _p.dlExpiryDate);
            }
          }
        }
      } catch (_) { /* view may be mid-teardown */ }
    },
  });

  // Subscribe to people
  const peopleQuery = query(
    collection(db, 'tenants', tenant, 'people'),
    orderBy('name')
  );
  const unsubPeople = onSnapshot(peopleQuery, (snapshot) => {
    RosterState.people = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    try { window.PersonIdentity?.overlayList?.(RosterState.people); } catch (_) { /* legacy values stay */ } // TASK-9359
    RosterState._peopleLoaded = true;
    populateRoleFilterDropdown();
    renderPeopleList();
    updateCount('people', RosterState.people.length);
  }, (error) => {
    console.error('[Roster] Error loading people:', error);
  });
  RosterState.unsubscribers.push(unsubPeople);

  // TASK-633-FF — the in-flight SEPARATION adverse-action hold (FCRA-gated
  // termination) is now DENORMALIZED onto the person doc as `pendingSeparation`
  // (server-stamped on initiate, cleared on complete/cancel). The roster reads it
  // from the EXISTING `people` subscription above, so the old unbounded
  // peopleBackgroundChecks onSnapshot listener (which scaled with ALL bg checks,
  // flagged twice by COD Architect) is gone. The badge + terminate modal + the
  // re-init guard all read `person.pendingSeparation` via _sepAaFromPerson().

  // Subscribe to trucks
  const trucksQuery = query(
    collection(db, 'tenants', tenant, 'trucks'),
    orderBy('unit')
  );
  const unsubTrucks = onSnapshot(trucksQuery, (snapshot) => {
    RosterState.trucks = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    RosterState._trucksLoaded = true;
    populateTypeFilterDropdown('filter-trucks-type', 'truckTypes');
    renderTrucksList();
    // TASK-1443 (blast-radius) — a rig's stored assembly is DERIVED from these
    // vehicles, so a truck edit is exactly when the 'source vehicle changed -
    // re-save' badge must appear. renderRigsList() was only called from the rigs
    // snapshot and filterRigs(), i.e. only when the RIG doc changed - the one
    // event that makes it NOT stale. This also fixes a first-paint race: the tiny
    // equipmentTemplates snapshot usually resolves first, rendering every card as
    // 'Missing vehicle' with nothing to re-render it.
    renderRigsList();
    updateCount('trucks', RosterState.trucks.length);
    consumePendingEdit('truck');
  }, (error) => {
    console.error('[Roster] Error loading trucks:', error);
    RosterState._trucksLoaded = true;
    renderTrucksList();
  });
  RosterState.unsubscribers.push(unsubTrucks);

  // Subscribe to trailers
  const trailersQuery = query(
    collection(db, 'tenants', tenant, 'trailers'),
    orderBy('unit')
  );
  const unsubTrailers = onSnapshot(trailersQuery, (snapshot) => {
    RosterState.trailers = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    RosterState._trailersLoaded = true;
    populateTypeFilterDropdown('filter-trailers-type', 'trailerTypes');
    renderTrailersList();
    // TASK-1443 (blast-radius) — a rig's stored assembly is DERIVED from these
    // vehicles, so a trailer edit is exactly when the 'source vehicle changed -
    // re-save' badge must appear. renderRigsList() was only called from the rigs
    // snapshot and filterRigs(), i.e. only when the RIG doc changed - the one
    // event that makes it NOT stale. This also fixes a first-paint race: the tiny
    // equipmentTemplates snapshot usually resolves first, rendering every card as
    // 'Missing vehicle' with nothing to re-render it.
    renderRigsList();
    updateCount('trailers', RosterState.trailers.length);
    consumePendingEdit('trailer');
  }, (error) => {
    console.error('[Roster] Error loading trailers:', error);
    RosterState._trailersLoaded = true;
    renderTrailersList();
  });
  RosterState.unsubscribers.push(unsubTrailers);

  // Subscribe to assets — the WHOLE collection, sorted client-side.
  // TASK-4800 follow-up: this used orderBy('partNumber'), and Firestore leaves
  // out every doc that has no value for the ordered field. Measured 2026-10-03:
  // 53 of 200 live asset docs (no UI-created asset lacks the field — saveAsset
  // always writes it — they come from seeds/imports) never reached the Items
  // grid, the banners, the counts or the edit modal. Never re-add an orderBy
  // here on a field that is not on every doc.
  const assetsQuery = collection(db, 'tenants', tenant, 'assets');
  const unsubAssets = onSnapshot(assetsQuery, (snapshot) => {
    RosterState.assets = sortRosterAssets(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    RosterState._assetsLoaded = true;
    populateTypeFilterDropdown('filter-assets-category', 'assetCategories');
    renderAssetsList();
    updateCount('assets', RosterState.assets.length);
    updateAssetAlertBadges();
    consumePendingEdit('asset');
    consumePendingBelowZero(); // TASK-9985
  }, (error) => {
    console.error('[Roster] Error loading assets:', error);
    RosterState._assetsLoaded = true;
    renderAssetsList();
  });
  RosterState.unsubscribers.push(unsubAssets);

  // Subscribe to rigs (TASK-1443 — equipmentTemplates). Renders the Rigs tab AND
  // the read-only "rigs using this unit" block on the truck/trailer modals, so it
  // also re-renders those lists when a source vehicle changes (drift badge).
  const rigsQuery = query(
    collection(db, 'tenants', tenant, 'equipmentTemplates'),
    orderBy('name')
  );
  const unsubRigs = onSnapshot(rigsQuery, (snapshot) => {
    RosterState.rigs = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
    RosterState._rigsLoaded = true;
    renderRigsList();
    updateCount('rigs', RosterState.rigs.length);
  }, (error) => {
    // A rules lag (frontend deployed before firestore.rules) lands here. Fail to
    // an empty tab, never a broken Roster.
    console.error('[Roster] Error loading rigs:', error);
    RosterState._rigsLoaded = true;
    renderRigsList();
  });
  RosterState.unsubscribers.push(unsubRigs);

  // Subscribe to orders
  const ordersQuery = query(
    collection(db, 'tenants', tenant, 'orders'),
    orderBy('createdAt', 'desc')
  );
  const unsubOrders = onSnapshot(ordersQuery, (snapshot) => {
    RosterState.orders = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    renderOrdersList();
    populateOrderCustomerFilter();
  }, (error) => {
    console.error('[Roster] Error loading orders:', error);
  });
  RosterState.unsubscribers.push(unsubOrders);

  // Subscribe to customers (reuse from Dispatch)
  const customersQuery = query(
    collection(db, 'tenants', tenant, 'customers'),
    orderBy('name')
  );
  const unsubCustomers = onSnapshot(customersQuery, (snapshot) => {
    RosterState.customers = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    RosterState.customersErr = false;
    populateOrderLinkCustomerDropdown();
    populateOrderCustomerFilter();
  }, (error) => {
    console.error('[Roster] Error loading customers:', error);
    RosterState.customersErr = true; // TASK-10212 — the vendor picker says so, never an empty list
  });
  RosterState.unsubscribers.push(unsubCustomers);

  // Register fields with Smart Assistant for data quality warnings
  // TASK-4834 — IDENTIFIERS ARE NOT NAMES. SmartAssistant's default and
  // formatOnly modes title-case ALL-CAPS text behind an "Auto-fix" button and
  // (default mode) offer a levenshtein "Did you mean" Apply between two REAL
  // values. On an identifier both are corruption: cleanFormat('1HGBH41JXMN109186')
  // lower-cases a VIN, and a plate / policy / serial gets the same. Measured live
  // 2026-10-03 (scripts/identifier-field-audit.cjs): 244 of 262 distinct truck
  // VINs were offered the Auto-fix, 12 VINs are stored lowercase, and TITS has a
  // trailer policy number stored beside its own title-cased twin. So VIN, plate,
  // policy, engine/asset serial, tire size, axle serial and part number are NOT
  // registered (same move as TASK-4782 job-po). Unit numbers keep ONLY the
  // exact-duplicate warning via `identifier: true` (207 of 270 live truck units
  // were being offered a different real unit, e.g. 104 -> 105). Don't re-add.
  // Follow-up (task-smartassist-names-2): make / model / engine make / engine
  // model / asset manufacturer are PRODUCT CODES and the inspection location is
  // an ADDRESS, so they use identifier mode (whitespace-only fix, never case):
  // cleanFormat turns 'GMC' into 'Gmc', '4900SA' into '4900sa', 'WILLISTON ND'
  // into 'Williston Nd'. Nicknames keep whitespace cleanup but get `noFuzzy`
  // (live 2026-10-03: 'HR-1' was offered 'HR-2', 'Shower 1' -> 'Shower 2') and
  // `skipAllCaps` (cleanFormat('HR-1') === 'Hr-1').
  if (window.SmartAssistant) {
    const SA = window.SmartAssistant;
    // People — duplicate + fuzzy
    SA.register('person-name', () => RosterState.people.map(p => ({ value: p.name || '', id: p.id })), 'Roster', { excludeIdField: 'person-id' });
    SA.register('person-email', () => [], 'Roster', { emailFormat: true });
    SA.register('person-personal-email', () => [], 'Roster', { emailFormat: true });
    SA.register('person-phone', () => [], 'Roster', { phoneFormat: true });
    SA.register('person-terminated-reason', () => [], 'Roster', { formatOnly: true });
    // Vehicles — duplicate + fuzzy + format-only fields
    SA.register('truck-unit', () => RosterState.trucks.map(t => ({ value: t.unit || '', id: t.id })), 'Vehicles', { identifier: true, excludeIdField: 'truck-id' });
    SA.register('truck-nickname', () => RosterState.trucks.filter(t => t.nickname).map(t => ({ value: t.nickname, id: t.id })), 'Vehicles', { noFuzzy: true, skipAllCaps: true, excludeIdField: 'truck-id' });
    SA.register('truck-make', () => [], 'Vehicles', { identifier: true });
    SA.register('truck-model', () => [], 'Vehicles', { identifier: true });
    SA.register('truck-ins-company', () => [], 'Vehicles', { formatOnly: true });
    SA.register('truck-ins-phone', () => [], 'Vehicles', { phoneFormat: true });
    SA.register('truck-inspection-location', () => [], 'Vehicles', { identifier: true });
    SA.register('truck-engine-make', () => [], 'Vehicles', { identifier: true });
    SA.register('truck-engine-model', () => [], 'Vehicles', { identifier: true });
    SA.register('truck-notes', () => [], 'Vehicles', { formatOnly: true });
    // Trailers — duplicate + fuzzy + format-only fields
    SA.register('trailer-unit', () => RosterState.trailers.map(t => ({ value: t.unit || '', id: t.id })), 'Trailers', { identifier: true, excludeIdField: 'trailer-id' });
    SA.register('trailer-nickname', () => RosterState.trailers.filter(t => t.nickname).map(t => ({ value: t.nickname, id: t.id })), 'Trailers', { noFuzzy: true, skipAllCaps: true, excludeIdField: 'trailer-id' });
    SA.register('trailer-make', () => [], 'Trailers', { identifier: true });
    SA.register('trailer-model', () => [], 'Trailers', { identifier: true });
    SA.register('trailer-ins-company', () => [], 'Trailers', { formatOnly: true });
    SA.register('trailer-ins-phone', () => [], 'Trailers', { phoneFormat: true });
    SA.register('trailer-inspection-location', () => [], 'Trailers', { identifier: true });
    SA.register('trailer-notes', () => [], 'Trailers', { formatOnly: true });
    // Assets — duplicate + fuzzy + format-only fields
    SA.register('asset-name', () => RosterState.assets.map(a => ({ value: a.name || '', id: a.id })), 'Assets', { excludeIdField: 'asset-id' });
    SA.register('asset-manufacturer', () => [], 'Assets', { identifier: true });
    SA.register('asset-notes', () => [], 'Assets', { formatOnly: true });
    // Dynamic class-based fields
    SA.registerClass('.inv-name', { formatOnly: true });
  }

  // Subscribe to DVIR accountability flags (only flagged status)
  if (window.AppState?.settings?.dvirAccountability) {
    const dvirFlagsQuery = query(
      collection(db, 'tenants', tenant, 'dvirAccountability'),
      where('status', '==', 'flagged')
    );
    const unsubDvirFlags = onSnapshot(dvirFlagsQuery, (snapshot) => {
      RosterState.dvirFlags = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      renderPeopleList();
      renderTrucksList();
    }, (error) => {
      console.error('[Roster] Error loading DVIR flags:', error);
    });
    RosterState.unsubscribers.push(unsubDvirFlags);
  }

  // Subscribe to JSA accountability flags (only flagged status)
  if (window.AppState?.settings?.jsaAccountability) {
    const jsaFlagsQuery = query(
      collection(db, 'tenants', tenant, 'jsaAccountability'),
      where('status', '==', 'flagged')
    );
    const unsubJsaFlags = onSnapshot(jsaFlagsQuery, (snapshot) => {
      RosterState.jsaFlags = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      renderPeopleList();
    }, (error) => {
      console.error('[Roster] Error loading JSA flags:', error);
    });
    RosterState.unsubscribers.push(unsubJsaFlags);
  }

  // Subscribe to active OOS citations
  const oosCitQuery = query(
    collection(db, 'tenants', tenant, 'citations'),
    where('vehicleOOS', '==', true)
  );
  const unsubOosCitations = onSnapshot(oosCitQuery, (snapshot) => {
    RosterState.oosCitations = snapshot.docs
      .map(doc => ({ id: doc.id, ...doc.data() }))
      .filter(c => c.repairStatus !== 'completed');
    renderTrucksList();
    renderPeopleList();
  }, (error) => {
    console.error('[Roster] Error loading OOS citations:', error);
  });
  RosterState.unsubscribers.push(unsubOosCitations);

  // Also track driver OOS citations
  const driverOosCitQuery = query(
    collection(db, 'tenants', tenant, 'citations'),
    where('driverOOS', '==', true)
  );
  const unsubDriverOosCitations = onSnapshot(driverOosCitQuery, (snapshot) => {
    RosterState.driverOosCitations = snapshot.docs
      .map(doc => ({ id: doc.id, ...doc.data() }))
      .filter(c => c.repairStatus !== 'completed');
    renderPeopleList();
  }, (error) => {
    console.error('[Roster] Error loading driver OOS citations:', error);
  });
  RosterState.unsubscribers.push(unsubDriverOosCitations);
}

function updateCount(type, count) {
  if (window.setCountBadge) {
    window.setCountBadge(`${type}-count`, count);
  } else {
    const el = document.getElementById(`${type}-count`);
    if (el) el.textContent = count;
  }
}

// ============================================
// RENDER LISTS
// ============================================
function getExpiredDocsRoster(person) {
  const today = new Date().toISOString().split('T')[0];
  const expired = [];
  // TASK-1285 — the three DOT driver credentials (CDL / Med Card / DL) only
  // apply to drivers. Suppress them for a CONFIDENTLY-classified non_driver
  // (office/shop) so a stray lapsed medCardExpiryDate on a swamper doesn't fire
  // a red "EXPIRED" flag the DQ matrix itself says is N/A (dqMatrix.js:83-88).
  // Gate ONLY on an EXPLICIT non_driver classification — a blank/unknown
  // positionType defaults to the full CDL checklist (never hide a real driver's
  // lapsed credential). complianceDocs / isnetTrainings are NEVER gated (a
  // swamper's H2S / training expiry is real, non-role-gated). window-global per
  // Pitfall #1 (no cross-?v= import); failsafe: if DqMatrix is absent the check
  // is false → chips still show.
  // TASK-9696 — drives_no_cmv has no CDL / med-card requirement either, but the
  // licence itself is still tracked (they drive).
  const _explicitTier = window.DqMatrix?.explicitPositionType?.(person) || null;
  const isNonDriver = _explicitTier === 'non_driver' || _explicitTier === 'drives_no_cmv';
  if (!isNonDriver) {
    if (person.cdlExpiryDate && person.cdlExpiryDate < today) expired.push('CDL');
    if (person.medCardExpiryDate && person.medCardExpiryDate < today) expired.push('Med Card');
  }
  if (!isNonDriver || _explicitTier === 'drives_no_cmv') {
    if (person.dlExpiryDate && person.dlExpiryDate < today) expired.push('DL');
  }
  if (person.complianceDocs) {
    for (const d of person.complianceDocs) {
      if (d.expiresAt && d.expiresAt < today) expired.push(d.name);
    }
  }
  if (person.isnetTrainings) {
    for (const t of person.isnetTrainings) {
      if (t.expiryDate && t.expiryDate < today) expired.push(t.trainingName || 'Training');
    }
  }
  return expired;
}

// TASK-1138 — expiring-soon tier for the Roster people-card. getExpiredDocsRoster
// above only surfaces docs ALREADY past expiry (red "X EXPIRED"). This companion
// collector returns docs / trainings / credentials whose expiry is within
// EXPIRING_SOON_DAYS but NOT yet past — so admins get the 30-day runway, not the
// autopsy (Ivan's H2S showed nothing until it lapsed → he couldn't go on
// location). Scans the SAME five field groups getExpiredDocsRoster does, using
// the SAME UTC date string for the expired boundary so any item is EITHER
// expired OR expiring-soon, never both (no double-render). Borrows TASK-262's
// strict YYYY-MM-DD guard so a garbage date can't NaN-poison a chip. Returns
// [{ label, days }] sorted nearest-expiry first.
const EXPIRING_SOON_DAYS = 30;
function getExpiringSoonDocsRoster(person) {
  if (!person) return [];
  const todayStr = new Date().toISOString().split('T')[0];
  const [ty, tm, td] = todayStr.split('-').map(Number);
  const todayMs = Date.UTC(ty, tm - 1, td);
  const MS_PER_DAY = 86400000;
  const out = [];
  const consider = (ymd, label) => {
    if (!ymd || !label) return;
    const s = String(ymd);
    // Strict YYYY-MM-DD only — rejects '', null, legacy ISO timestamps, and
    // roll-over dates (2023-02-30) before they NaN-poison the chip (TASK-262).
    if (!/^\d{4}-\d{2}-\d{2}$/.test(s)) return;
    const [y, m, d] = s.split('-').map(Number);
    if (m < 1 || m > 12 || d < 1 || d > 31) return;
    const expMs = Date.UTC(y, m - 1, d);
    const check = new Date(expMs);
    // Reject roll-over: parsed components must match the inputs exactly.
    if (check.getUTCFullYear() !== y || check.getUTCMonth() !== m - 1 || check.getUTCDate() !== d) return;
    const days = Math.round((expMs - todayMs) / MS_PER_DAY);
    if (days < 0) return;                    // already expired → red chip via getExpiredDocsRoster
    if (days > EXPIRING_SOON_DAYS) return;   // outside the runway window
    out.push({ label, days });
  };
  // TASK-1285 — same DQ-applicability gate as getExpiredDocsRoster: skip the
  // three DOT driver credentials for a confidently-classified non_driver; keep
  // complianceDocs / isnetTrainings always shown; blank/unknown → not suppressed.
  const _explicitTier = window.DqMatrix?.explicitPositionType?.(person) || null; // TASK-9696
  const isNonDriver = _explicitTier === 'non_driver' || _explicitTier === 'drives_no_cmv';
  if (!isNonDriver) {
    consider(person.cdlExpiryDate, 'CDL');
    consider(person.medCardExpiryDate, 'Med Card');
  }
  if (!isNonDriver || _explicitTier === 'drives_no_cmv') {
    consider(person.dlExpiryDate, 'DL');
  }
  if (person.complianceDocs) {
    for (const doc of person.complianceDocs) consider(doc.expiresAt, doc.name);
  }
  if (person.isnetTrainings) {
    for (const t of person.isnetTrainings) consider(t.expiryDate, t.trainingName || 'Training');
  }
  out.sort((a, b) => a.days - b.days);
  return out;
}

// TASK-262 Slice 1B — render countdown badges for tenant custom reminders
// whose expiration falls within 30 days (or already past). Cap at 3 visible
// badges per card; surface overflow as "+N more". Buckets:
//   <0 days  → red    (expired)
//   <=7 days → amber  (urgent)
//   <=30 days→ blue   (heads-up)
// Returns an HTML string; '' when nothing to show. Archived custom-reminder
// items are skipped — once archived at tenant level, badges stop displaying.
function renderCustomReminderBadgesRoster(person) {
  const expirations = (person && person.customReminderExpirations) || {};
  if (Object.keys(expirations).length === 0) return '';

  const getCurrent = window._crGetCurrent;
  if (typeof getCurrent !== 'function') return '';
  const all = getCurrent();
  const active = all.filter(i => i && i.archived !== true);
  if (active.length === 0) return '';

  const today = new Date(); today.setHours(0,0,0,0);
  const todayMs = today.getTime();
  const MS_PER_DAY = 86400000;

  const candidates = [];
  for (const item of active) {
    const ymd = expirations[item.itemId];
    if (!ymd) continue;
    // Strict YYYY-MM-DD only — rejects 2023-02-30 (Date roll-over), '', null,
    // legacy ISO timestamps, etc. before they NaN-poison badge rendering.
    if (!/^\d{4}-\d{2}-\d{2}$/.test(String(ymd))) continue;
    const [y, m, d] = String(ymd).split('-').map(Number);
    if (m < 1 || m > 12 || d < 1 || d > 31) continue;
    const expDate = new Date(y, m - 1, d);
    // Reject roll-over (e.g. 2023-02-30 → 2023-03-02): the parsed year /
    // month / day must match the inputs exactly.
    if (expDate.getFullYear() !== y || expDate.getMonth() !== m - 1 || expDate.getDate() !== d) continue;
    if (Number.isNaN(expDate.getTime())) continue;
    const daysOut = Math.round((expDate.getTime() - todayMs) / MS_PER_DAY);
    if (daysOut > 30) continue; // not yet in the 30-day window
    candidates.push({ item, daysOut, ymd });
  }
  if (candidates.length === 0) return '';

  // Sort by urgency: most past (most negative) first, then nearest-future.
  candidates.sort((a, b) => a.daysOut - b.daysOut);

  const styleFor = (daysOut) => {
    if (daysOut < 0)  return 'background:#fef2f2;color:#dc2626;border:1px solid #fecaca;';
    if (daysOut <= 7) return 'background:#FFF7ED;color:#C2410C;border:1px solid #FED7AA;';
    return                 'background:#EFF6FF;color:#1D4ED8;border:1px solid #BFDBFE;';
  };
  const labelFor = (c) => {
    const base = escapeHtml(c.item.label);
    if (c.daysOut < 0)   return `${base} EXPIRED`;
    if (c.daysOut === 0) return `${base} due today`;
    if (c.daysOut === 1) return `${base} due tomorrow`;
    return `${base} ${c.daysOut}d`;
  };

  const visible = candidates.slice(0, 3);
  const overflow = candidates.length - visible.length;
  const badges = visible.map(c =>
    `<span class="cr-badge" title="Expires ${escapeHtml(c.ymd)}" style="${styleFor(c.daysOut)}padding:2px 8px;border-radius:999px;font-size:0.72rem;font-weight:600;margin-left:6px;display:inline-block;line-height:1.4;">${labelFor(c)}</span>`
  ).join('');
  const more = overflow > 0
    ? `<span class="cr-badge-more" title="${overflow} more reminder${overflow === 1 ? '' : 's'} expiring soon" style="background:var(--bg-page);color:var(--text-secondary);border:1px solid var(--border-light);padding:2px 8px;border-radius:999px;font-size:0.72rem;font-weight:600;margin-left:6px;display:inline-block;line-height:1.4;">+${overflow} more</span>`
    : '';
  return badges + more;
}

// TASK-1138 — render the expiring-soon doc / training / credential chips.
// Reuses TASK-262's EXACT bucket colors + "Nd" / "due today" / "due tomorrow"
// format and its cap-3 + "+N more" overflow so the card speaks ONE visual
// language and a long-tenured driver's card can't fill with chips. No red
// bucket here — already-expired stays red via getExpiredDocsRoster's "X EXPIRED"
// chips. Buckets: <=7d → amber (dark amber on light, readable), <=30d → blue.
function renderExpiringSoonBadgesRoster(person) {
  const items = getExpiringSoonDocsRoster(person);
  if (items.length === 0) return '';

  const styleFor = (days) =>
    days <= 7 ? 'background:#FFF7ED;color:#C2410C;border:1px solid #FED7AA;'
              : 'background:#EFF6FF;color:#1D4ED8;border:1px solid #BFDBFE;';
  const labelFor = (it) => {
    const base = escapeHtml(it.label);
    if (it.days === 0) return `${base} due today`;
    if (it.days === 1) return `${base} due tomorrow`;
    return `${base} ${it.days}d`;
  };

  const visible = items.slice(0, 3);
  const overflow = items.length - visible.length;
  const badges = visible.map(it =>
    `<span class="doc-expiring-badge" title="Expires in ${it.days} day${it.days === 1 ? '' : 's'}" style="${styleFor(it.days)}padding:2px 8px;border-radius:999px;font-size:0.72rem;font-weight:600;margin-left:6px;display:inline-block;line-height:1.4;">${labelFor(it)}</span>`
  ).join('');
  const more = overflow > 0
    ? `<span class="doc-expiring-badge-more" title="${overflow} more credential${overflow === 1 ? '' : 's'} expiring soon" style="background:var(--bg-page);color:var(--text-secondary);border:1px solid var(--border-light);padding:2px 8px;border-radius:999px;font-size:0.72rem;font-weight:600;margin-left:6px;display:inline-block;line-height:1.4;">+${overflow} more</span>`
    : '';
  return badges + more;
}

// TASK-9893 — "has this person ever texted the company's DD number?"
// smsRepliedAt is SERVER-OWNED (twilioIncomingSms stamps it; firestore.rules
// deny client writes). Until it exists an iPhone shows links in our texts as
// plain text. A subtle grey marker, not a warning: Android users are fine and
// nothing is broken on our side. Mirrors services/knownSender.js hasReplied.
function _rosterHasTextablePhone(p) {
  return String((p && p.phone) || '').replace(/\D/g, '').length >= 10;
}
// TASK-9893 COD — replied FROM a phone still on the record (twin of knownSender.hasReplied).
function _smsReplied(p) {
  if (!p || !p.smsRepliedAt) return false;
  const last10 = (v) => String(v == null ? '' : v).replace(/\D/g, '').slice(-10);
  const from = last10(p.smsRepliedFrom);
  return !from || ['phone', 'cellPhone', 'mobilePhone'].some((k) => last10(p[k]) === from);
}
function _smsRepliedDate(p) {
  const v = _smsReplied(p) ? p.smsRepliedAt : null;
  if (!v) return null;
  const d = typeof v.toDate === 'function' ? v.toDate() : new Date(v.seconds ? v.seconds * 1000 : v);
  return isNaN(d) ? null : d;
}
function _smsAwaitingReplyIcon(p) {
  if (!p || p.smsOptOutReason || _smsReplied(p) || !_rosterHasTextablePhone(p)) return '';
  const tip = "Hasn't texted your number yet. On an iPhone, links in your texts won't open until they reply START.";
  return ` <span class="material-icons" style="font-size:15px;vertical-align:-3px;color:var(--text-secondary);cursor:help;" title="${tip}" aria-label="${tip}" role="img">link_off</span>`;
}

// TASK-9569 — the reminder bell on a person / vehicle card, placed by the name
// (never beside the delete icon). window.Reminders owns the module gate, the
// per-login hide and the live state (Pitfall #1: never imported). '' when absent.
function _reminderBell(type, id, label) {
  try { return window.Reminders?.bellHtml ? window.Reminders.bellHtml(type, id, label) : ''; } catch (_) { return ''; }
}

function renderPeopleList() {
  const container = document.getElementById('list-people');
  if (!container) return;
  _syncCrewSkillsButton(); // TASK-9844 — access state can land after the first paint
  _syncDriverPinOffice(); // TASK-9941 F S3 (cached after the first read)
  // Skip render if person edit modal is open to preserve unsaved input
  const modal = document.getElementById('modal-person');
  if (modal && !modal.classList.contains('hidden')) return;

  // TASK-1476 — synced HERE (not at the bottom next to updateMuteButton) so the
  // count is still right on the paths that return early: no people yet, or the
  // current filter matched nobody.
  _syncRoleConflictChip();
  _syncPeopleViewOnlyNote(); // TASK-9894 — same early-return reason
  const canEditPeople = _rosterCanEditPeople(); // TASK-9894 — row Transfer / Deactivate

  // Apply filters
  const { search, role, status } = RosterState.filters.people;

  // Apply the NON-status filters (search/role/yard) first, into a base set, so
  // the employment-status filter is the only thing separating shown from hidden.
  // This lets us count exactly how many people the "Active" filter is hiding
  // (TASK-788) — only people who would otherwise be on screen given the current
  // search/role/yard, not the whole terminated/inactive population company-wide.
  const yardFilter = RosterState.filters.people.yard;
  const _noPinIds = _noPinFilterIds(); // TASK-9941 F S3 — null unless the chip filter is on
  const searchLower = search ? search.trim().toLowerCase() : '';
  const base = RosterState.people.filter(p => {
    if (search && !(p.name || '').toLowerCase().includes(searchLower)) return false;
    // TASK-1476 — the role filter doubles as the "show me the role conflicts"
    // list. Checked BEFORE slug equality so the sentinel can never be read as a
    // crew-type slug.
    if (role === ROLE_CONFLICT_FILTER) {
      if (!getRoleConflict(p)) return false;
    } else if (role !== 'all') {
      const ct = p.crewType || p.role || (p.roles && p.roles[0]) || '';
      if (ct !== role) return false;
    }
    if (yardFilter !== 'all') {
      if (yardFilter === '' ? p.yardId : p.yardId !== yardFilter) return false;
    }
    // TASK-9941 F S3 — "no private PIN" chip filter (server status words).
    if (_noPinIds && !_noPinIds.has(p.id)) return false;
    return true;
  });

  // Filter by employment status.
  //   active     → not terminated AND active !== false (the legacy default)
  //   inactive   → not terminated AND active === false
  //   terminated → terminated === true
  //   all        → no status filter
  const matchesStatus = (p) => {
    if (status === 'active') return p.terminated !== true && p.active !== false;
    if (status === 'inactive') return p.terminated !== true && p.active === false;
    if (status === 'terminated') return p.terminated === true;
    // TASK-10211 — Laid off = an Inactive person with the layoff label; Texted
    // STOP = the SMS STOP chip's own test (no new field, no reason shown).
    if (status === 'laidoff') return p.terminated !== true && p.active === false && p.inactiveReason === 'laid_off';
    if (status === 'stop') return !!p.smsOptOutReason;
    return true; // all
  };
  let filtered = base.filter(matchesStatus);

  // TASK-788 — when the default "Active" filter is on and it's hiding ≥1 person
  // who'd otherwise be visible, surface a one-tap hint so terminated/inactive
  // people aren't silently invisible ("where did my guy go?"). UI-only — does
  // not change WHO is active, just makes the hiding visible + reversible.
  let hiddenHintHtml = '';
  if (status === 'active') {
    let inactiveCount = 0;
    let terminatedCount = 0;
    base.forEach(p => {
      if (p.terminated === true) terminatedCount++;
      else if (p.active === false) inactiveCount++;
    });
    const hiddenTotal = inactiveCount + terminatedCount;
    if (hiddenTotal > 0) {
      const parts = [];
      if (inactiveCount > 0) parts.push(`${inactiveCount} inactive`);
      if (terminatedCount > 0) parts.push(`${terminatedCount} terminated`);
      hiddenHintHtml = `
        <div class="roster-hidden-hint" role="status" style="display:flex;align-items:center;gap:8px;flex-wrap:wrap;padding:10px 14px;margin-bottom:12px;background:var(--bg-page);border:1px solid var(--border-light);border-radius:var(--radius-md);font-size:0.8125rem;color:var(--text-secondary);">
          <span class="material-icons" aria-hidden="true" style="font-size:18px;color:var(--text-muted);">visibility_off</span>
          <span>${parts.join(' + ')} hidden by the <strong>Active</strong> filter.</span>
          <button type="button" onclick="window.RosterModule.showAllPeople()" style="margin-left:auto;background:var(--brand-blue);color:#fff;border:none;border-radius:var(--radius-sm);padding:5px 12px;font-size:0.8125rem;font-weight:600;cursor:pointer;">Show all</button>
        </div>`;
    }
  }

  if (RosterState.people.length === 0) {
    if (!RosterState._peopleLoaded) {
      container.innerHTML = `
        <div style="text-align:center;padding:var(--spacing-xl);">
          <div class="btn-spinner" style="width:32px;height:32px;margin:0 auto var(--spacing-md);"></div>
          <div style="color:var(--text-secondary);font-size:0.875rem;">Loading...</div>
        </div>
      `;
    } else {
      container.innerHTML = `
        <div class="empty-state">
          <div class="empty-state-icon">👥</div>
          <div class="empty-state-title">No people yet</div>
          <div class="empty-state-text">Add your first team member to get started.</div>
        </div>
      `;
    }
    return;
  }

  if (filtered.length === 0) {
    // Still show the hidden-people hint above the empty state — if the Active
    // filter hid everyone (e.g. the whole crew is terminated), "No matches" with
    // a one-tap "Show all" is exactly the discoverability fix (TASK-788).
    // TASK-1476 — a conflict filter that lands on zero needs to say WHY. The
    // chip counts everyone; the Active/Inactive/Terminated filter can still hide
    // them all, and "No matches" alone reads as a broken chip.
    const conflictEmpty = role === ROLE_CONFLICT_FILTER;
    container.innerHTML = hiddenHintHtml + `
      <div class="empty-state">
        <div class="empty-state-icon">${conflictEmpty ? '✅' : '🔍'}</div>
        <div class="empty-state-title">${conflictEmpty ? 'No role conflicts here' : 'No matches'}</div>
        <div class="empty-state-text">${conflictEmpty
          ? 'Nobody matching the current search and employment-status filter has an HR title that disagrees with their crew type.'
          : 'Try adjusting your search or filter.'}</div>
      </div>
    `;
    return;
  }

  // TASK-4728 — built ONCE, over the WHOLE roster (not `filtered`): the person
  // you share a number with may be hidden by the current search/role/status
  // filter, and a collision that only shows when both halves happen to be on
  // screen is exactly the kind of half-warning that trains people to ignore it.
  const sharedPhoneIndex = buildSharedPhoneIndex();

  container.innerHTML = hiddenHintHtml + filtered.map(person => {
    const expiredDocs = getExpiredDocsRoster(person);
    const hasDriverOos = (RosterState.driverOosCitations || []).some(c => c.driverId === person.id);
    // TASK-262 Slice 1B — countdown badges for custom reminders within 30 days
    const customReminderBadges = renderCustomReminderBadgesRoster(person);
    // TASK-1138 — expiring-soon (≤30d) doc/training/credential chips: the
    // 30-day runway BEFORE the red "X EXPIRED" chip, so admins don't first learn
    // of a lapse when the worker can't go on location.
    const expiringSoonBadges = renderExpiringSoonBadgesRoster(person);
    // TASK-258 §396.19 — surface inspector qualification as a small chip on
    // the people list so admins see at-a-glance who's a cert-holding
    // annual / brake inspector when scanning the roster.
    const iqLabels = {
      annual_396_17: '§396.17 Annual Inspector',
      brake_inspector_396_25: '§396.25 Brake Inspector',
      both: 'Annual + Brake Inspector',
      state_certified: 'State Inspector',
      other: 'Inspector',
    };
    const iqType = person.inspectorQualification?.type;
    const inspectorBadge = iqType
      ? ` <span class="dvir-flag-label" style="background:#ECFEFF;color:#0E7490;border-color:#A5F3FC;" title="§396.19 in-house inspector qualification on file">${escapeHtml(iqLabels[iqType] || 'Inspector')}</span>`
      : '';
    // TASK-628 — §391.51 DQ-file retention. For terminated drivers, surface a
    // "RETENTION ENDED" badge once the 3-yr retention clock (retentionExpiresAt,
    // = last day worked + 3yr) has passed → the archived DQ file is eligible for
    // disposal. FLAG ONLY — never an auto-purge; disposal is a human decision.
    let retentionBadge = '';
    if (person.terminated === true && person.retentionExpiresAt) {
      try {
        const exp = person.retentionExpiresAt?.toDate?.() || new Date(person.retentionExpiresAt);
        if (exp && !isNaN(exp.getTime()) && exp.getTime() <= Date.now()) {
          retentionBadge = ` <span class="dvir-flag-label" style="background:#F1F5F9;color:#475569;border-color:#CBD5E1;" title="§391.51 3-year DQ-file retention has ended — this archived file is eligible for disposal (admin decision; not auto-deleted)">RETENTION ENDED — DISPOSAL OK</span>`;
        }
      } catch (_) {}
    }
    // TASK-633 — PENDING SEPARATION badge: a report-based (FCRA) termination is
    // held during the Pre-Adverse-Action dispute window. The driver stays active
    // and dispatchable; this badge + countdown signal the hold. TASK-633-FF —
    // source is now the DENORMALIZED person.pendingSeparation (set server-side on
    // initiate, cleared on complete/cancel), read straight from the people
    // subscription — no separate bg listener.
    let pendingSeparationBadge = '';
    const sepAa = _sepAaFromPerson(person);
    if (sepAa && sepAa.preNoticeAt && !sepAa.finalNoticeAt) {
      try {
        const dd = sepAa.disputeDeadline?.toDate?.() || (sepAa.disputeDeadline ? new Date(sepAa.disputeDeadline) : null);
        let when = '';
        if (dd && !isNaN(dd.getTime())) {
          const days = Math.ceil((dd.getTime() - Date.now()) / 86400000);
          when = days > 0 ? ` — ${days}d left` : ' — wait elapsed';
        }
        pendingSeparationBadge = ` <span class="dvir-flag-label" style="background:#FEF3C7;color:#92400E;border-color:#FDE68A;" title="FCRA termination on hold — Pre-Adverse Action notice sent; the dispute window must close before the termination is finalized. The driver stays active during this window.">PENDING SEPARATION${when}</span>`;
      } catch (_) {
        pendingSeparationBadge = ` <span class="dvir-flag-label" style="background:#FEF3C7;color:#92400E;border-color:#FDE68A;">PENDING SEPARATION</span>`;
      }
    }
    // TASK-1476 — ROLE CONFLICT chip. Read-only signal: this person's HR job
    // title and their operational crew type describe different kinds of work,
    // so whichever one is stale is quietly steering dispatch eligibility,
    // billing rate lookup and driver reporting. Amber/advisory — never a block,
    // never an auto-fix (COD rejected deriving one field from the other).
    const roleConflict = getRoleConflict(person);
    const roleConflictBadge = roleConflict
      ? ` <span class="dvir-flag-label" style="background:#FEF3C7;color:#92400E;border-color:#FDE68A;" title="${window.escAttr(roleConflict.summary + ' Open this person to review; nothing is changed automatically.')}">ROLE CONFLICT</span>`
      : '';
    // TASK-4728 — SHARED PHONE chip. Two LIVE people on one number make
    // `forgotDriverPin` refuse (it resolves by phone and requires exactly one
    // live match), so PIN self-reset is off for both and today the only party
    // told about it is a super-admin reading errorLogs. Amber/advisory, exactly
    // like ROLE CONFLICT: never a block, never an auto-merge, never a rewrite of
    // anyone's number — a shared handset is legitimate on a family crew, and
    // which of the two should move is the customer's call (TASK-4730).
    const sharedPhone = getSharedPhoneConflict(person, sharedPhoneIndex);
    const sharedPhoneBadge = sharedPhone
      ? ` <span class="dvir-flag-label" style="background:#FEF3C7;color:#92400E;border-color:#FDE68A;" title="${window.escAttr(sharedPhone.summary + ' Nothing is changed automatically.')}">SHARED PHONE</span>`
      : '';
    // TASK-1170 — e-sign signature chips REMOVED from the Roster card (moved to the
    // HR employee card). Signature status is an HR-specific alert, not a Roster
    // concern (same call as TASK-536's "No application on file" removal). The
    // pendingSignatureCount / lastSignedAt denorm fields + onSignDocumentWrite
    // trigger stay; the HR card now consumes pendingSignatureCount.
    // TASK-470/479 — advisory "No application on file" chip for hand-added
    // drivers (no digital application AND no paper file marked on-file). The
    // §391.21 DQ application is the carrier's responsibility; this surfaces the
    // gap so it can be self-corrected. Advisory amber, NOT a block (COD rejected
    // the hard-block as heavy-handed). Source of truth: candidateId (set by
    // confirmApproveToRoster on promote) OR dqApplicationOnFile/applicationDate
    // (paper file marked on-file — same signal as the DQ checklist). Gated to
    // TASK-536 — removed the amber "No application on file" Roster chip (Chris:
    // it's an HR concern, not a Roster one; too flashy for the list view). The
    // §391.21 DQ-application signal still lives in its correct home — the HR DQ
    // checklist item `application` (hr.js ~:20800, `dqApplicationOnFile`/
    // `applicationDate`) + the hiring/candidate pipeline. No Roster badge.
    // TASK-1531 — the MUTED badge/label must agree with what the senders check.
    // Was flat-only `person.notifyMuted`: a nested-only mute got no badge while
    // the (fixed) senders skip them. One predicate everywhere.
    const personIsMuted = window.isNotificationMuted(person);
    return `
    <div class="roster-card roster-card-clickable ${personIsMuted ? 'muted' : ''} ${person.active === false ? 'inactive' : ''} ${expiredDocs.length > 0 ? 'compliance-alert' : ''}" data-id="${person.id}" onclick="window.RosterModule.editPerson('${person.id}')" ${hasDriverOos ? 'style="border-left:4px solid #DC2626;"' : ''}>
      <div class="roster-card-avatar" style="background: ${hasDriverOos ? '#DC2626' : person.color || '#2563EB'}">
        ${personAvatarInnerHtml(person, 'roster-card-photo', getInitials(person.name))}
        ${personIsMuted ? '<span class="muted-badge" title="Notifications muted"><span class="material-icons">notifications_off</span></span>' : ''}
        ${person.active === false ? '<span class="inactive-badge" title="Inactive"><span class="material-icons">person_off</span></span>' : ''}
      </div>
      <div class="roster-card-info">
        <div class="roster-card-name">${escapeHtml(person.name)}${_reminderBell('person', person.id, person.name)}${person.active === false ? ` <span class="inactive-label"${person.inactiveReason === 'laid_off' && !person.terminated ? (canEditPeople ? ` role="button" style="cursor:pointer" onclick="event.stopPropagation();RosterModule.recallPerson('${window.escJsAttr(person.id)}')">LAID OFF · RECALL` : '>LAID OFF') : '>INACTIVE'}</span>` : ''}${personIsMuted && person.active !== false ? ' <span class="muted-label">MUTED</span>' : ''}${hasDriverOos ? ' <span class="dvir-flag-label" style="background:#fef2f2;color:#dc2626;border-color:#fecaca;">DRIVER OOS</span>' : ''}${person.smsOptOutReason ? ' <span class="dvir-flag-label" style="background:#fef2f2;color:#dc2626;border-color:#fecaca;" title="This person texted STOP and cannot be sent SMS (TCPA). Contact by phone/email.">SMS STOP</span>' : ''}${_smsAwaitingReplyIcon(person)}${expiredDocs.map(d => ` <span class="dvir-flag-label" style="background:#fef2f2;color:#dc2626;border-color:#fecaca;">${escapeHtml(d)} EXPIRED</span>`).join('')}${expiringSoonBadges}${customReminderBadges}${inspectorBadge}${retentionBadge}${pendingSeparationBadge}${roleConflictBadge}${sharedPhoneBadge}${_bisInactiveChip(person)}</div>
        <div class="roster-card-meta">
          ${formatRoleBadges(person)}
          ${person.yardId ? `<span class="badge badge-yard">${escapeHtml(getLiveYardLabel(person.yardId) || person.yardName || getYardLabel(person.yardId))}</span>` : ''}
          ${_personSkillChips(person)}
          ${person.phone ? `<span>${escapeHtml(person.phone)}</span>` : ''}
        </div>
      </div>
      <div class="roster-card-actions">
        ${canEditPeople && getYardsList().length > 0 ? `<button class="btn-icon" onclick="event.stopPropagation(); window.RosterModule.transferToYard('people','${person.id}')" title="Transfer Yard"><span class="material-icons">swap_horiz</span></button>` : ''}
        ${canEditPeople ? `<button class="btn-icon btn-icon-danger" onclick="event.stopPropagation(); window.RosterModule.deletePerson('${person.id}')" title="Deactivate">
          <span class="material-icons">delete</span>
        </button>` : ''}
      </div>
      <span class="roster-card-chevron material-icons">chevron_right</span>
    </div>
  `;
  }).join('');

  // TASK-9234 — avatars paint from the storage path, resolved after the cards
  // are in the DOM. Fire-and-forget: it never throws, and a card whose photo
  // can't resolve just keeps its initials.
  hydratePersonPhotos(container);

  updateMuteButton();
  _syncDriverDirectoryButton();
}

// TASK-10138 (Wolverine / Steffani 10/9) — the Vehicles and Trailers search box
// also matches VIN: full or partial, any case, spaces ignored. VINs live in
// `vins[]` (current form) with a legacy single `vin` string; check both.
function _vinSearchMatch(item, query) {
  const q = String(query || '').replace(/\s+/g, '').toLowerCase();
  if (!q || !item) return false;
  const list = Array.isArray(item.vins) ? item.vins.slice() : [];
  if (item.vin) list.push(item.vin);
  return list.some(v => typeof v === 'string' && v.replace(/\s+/g, '').toLowerCase().includes(q));
}

function renderTrucksList() {
  const container = document.getElementById('list-trucks');
  if (!container) return;
  const modal = document.getElementById('modal-truck');
  if (modal && !modal.classList.contains('hidden')) return;

  // Apply filters
  const { search, type, showInactive } = RosterState.filters.trucks;
  let filtered = RosterState.trucks;

  // Filter inactive unless showInactive is true
  if (!showInactive) {
    filtered = filtered.filter(t => t.active !== false);
  }

  if (search) {
    const searchLower = search.trim().toLowerCase();
    filtered = filtered.filter(t =>
      (t.unit || '').toLowerCase().includes(searchLower) ||
      (t.nickname && t.nickname.toLowerCase().includes(searchLower)) ||
      (t.make && t.make.toLowerCase().includes(searchLower)) ||
      (t.model && t.model.toLowerCase().includes(searchLower)) ||
      _vinSearchMatch(t, search)
    );
  }
  if (type !== 'all') {
    filtered = filtered.filter(t => t.type === type);
  }
  const truckYardFilter = RosterState.filters.trucks.yard;
  if (truckYardFilter !== 'all') {
    filtered = filtered.filter(t => truckYardFilter === '' ? !t.yardId : t.yardId === truckYardFilter);
  }

  // Sort
  const truckSort = RosterState.filters.trucks.sort || 'unit';
  if (truckSort === 'name') {
    filtered.sort((a, b) => (a.nickname || a.unit || '').localeCompare(b.nickname || b.unit || ''));
  } else {
    filtered.sort((a, b) => {
      const aNum = parseInt((a.unit || '').replace(/\D/g, '')) || 0;
      const bNum = parseInt((b.unit || '').replace(/\D/g, '')) || 0;
      return aNum - bNum || (a.unit || '').localeCompare(b.unit || '');
    });
  }

  if (RosterState.trucks.length === 0) {
    if (!RosterState._trucksLoaded) {
      container.innerHTML = `
        <div style="text-align:center;padding:var(--spacing-xl);">
          <div class="btn-spinner" style="width:32px;height:32px;margin:0 auto var(--spacing-md);"></div>
          <div style="color:var(--text-secondary);font-size:0.875rem;">Loading vehicles…</div>
        </div>
      `;
    } else {
      container.innerHTML = `
        <div class="empty-state">
          <div class="empty-state-icon">🚛</div>
          <div class="empty-state-title">No vehicles yet</div>
          <div class="empty-state-text">Add your first vehicle to get started.</div>
        </div>
      `;
    }
    return;
  }

  if (filtered.length === 0) {
    container.innerHTML = `
      <div class="empty-state">
        <div class="empty-state-icon">🔍</div>
        <div class="empty-state-title">No matches</div>
        <div class="empty-state-text">Try adjusting your search or filter.</div>
      </div>
    `;
    return;
  }

  container.innerHTML = filtered.map(truck => {
    const showFlag = shouldShowInspectionFlag(truck);
    const daysUntil = truck.inspectionExpiry ? Math.ceil((new Date(truck.inspectionExpiry) - new Date()) / (1000 * 60 * 60 * 24)) : null;
    const inspFlagTitle = !truck.inspectionExpiry ? 'No inspection date entered' : daysUntil < 0 ? `Inspection expired ${Math.abs(daysUntil)} days ago` : `Inspection due in ${daysUntil} days`;
    const truckDvirFlags = RosterState.dvirFlags.filter(f => f.truckId === truck.id);
    const hasDvirFlag = truckDvirFlags.length > 0;
    const hasOosCitation = (RosterState.oosCitations || []).some(c => c.vehicleId === truck.id);
    return `
    <div class="roster-card roster-card-clickable ${truck.active === false ? 'inactive' : ''}" data-id="${truck.id}" onclick="window.RosterModule.editTruck('${truck.id}')" ${hasOosCitation ? 'style="border-left:4px solid #DC2626;"' : ''}>
      <div class="roster-card-icon roster-card-icon-photo${truck.photoUrl ? ' has-photo' : ''}">
        ${truck.photoUrl
          ? `<img src="${escapeHtml(truck.photoUrl)}" alt="${escapeHtml(getUnitDisplay(truck))}" class="roster-card-photo" loading="lazy">`
          : `<span class="material-icons">${hasOosCitation ? 'gavel' : 'local_shipping'}</span>`}
        ${truck.active === false ? '<span class="inactive-badge" title="Inactive"><span class="material-icons">block</span></span>' : ''}
        ${hasDvirFlag ? `<span class="dvir-flag-badge" title="Missing DVIR" onclick="event.stopPropagation(); window.RosterModule.showDvirFlags('${truck.id}', 'truck')"><span class="material-icons">warning</span></span>` : ''}
      </div>
      <div class="roster-card-info">
        <div class="roster-card-name">
          ${escapeHtml(getUnitDisplay(truck))}${_reminderBell('vehicle', truck.id, getUnitDisplay(truck))}
          ${truck.active === false ? ' <span class="inactive-label">INACTIVE</span>' : ''}
          ${showFlag ? `<span class="inspection-due-flag" title="${inspFlagTitle}"><span class="material-icons">flag</span></span>` : ''}
          ${hasDvirFlag ? ' <span class="dvir-flag-label">MISSING DVIR</span>' : ''}
          ${hasOosCitation ? ' <span class="dvir-flag-label" style="background:#fef2f2;color:#dc2626;border-color:#fecaca;">OUT OF SERVICE</span>' : ''}
          ${_noRegExpiryBadge(truck)}
          ${_rentalBadge(truck)}
        </div>
        <div class="roster-card-meta">
          <span class="badge badge-${escapeHtml(truck.type)}">${escapeHtml(getTypeLabel(truck.type, 'truckTypes'))}</span>
          ${truck.yardId ? `<span class="badge badge-yard">${escapeHtml(getLiveYardLabel(truck.yardId) || truck.yardName || getYardLabel(truck.yardId))}</span>` : ''}
          ${truck.make ? `<span>${escapeHtml(truck.make)} ${escapeHtml(truck.model || '')}</span>` : ''}
          ${truck.year ? `<span>${escapeHtml(truck.year)}</span>` : ''}
          ${truck.ownership && truck.ownership !== 'owned' ? `<span class="badge badge-yard" title="Part 376 lease required" style="background:#fef3c7;color:#92400e;border-color:#fde68a;">${truck.ownership === 'leased' ? 'Leased' : truck.ownership === 'owner_op' ? 'Owner-op' : truck.ownership === 'sister_co' ? 'Sister-co' : 'Non-owned'}</span>` : ''}
        </div>
      </div>
      <div class="roster-card-actions">
        ${getYardsList().length > 0 ? `<button class="btn-icon" onclick="event.stopPropagation(); window.RosterModule.transferToYard('trucks','${truck.id}')" title="Transfer Yard"><span class="material-icons">swap_horiz</span></button>` : ''}
        <button class="btn-icon btn-icon-danger" onclick="event.stopPropagation(); window.RosterModule.deleteTruck('${truck.id}')" title="Deactivate">
          <span class="material-icons">delete</span>
        </button>
      </div>
      <span class="roster-card-chevron material-icons">chevron_right</span>
    </div>
  `;
  }).join('');
}

function renderTrailersList() {
  const container = document.getElementById('list-trailers');
  if (!container) return;
  const modal = document.getElementById('modal-trailer');
  if (modal && !modal.classList.contains('hidden')) return;

  // Apply filters
  const { search, type, showInactive } = RosterState.filters.trailers;
  let filtered = RosterState.trailers;

  // Filter inactive unless showInactive is true
  if (!showInactive) {
    filtered = filtered.filter(t => t.active !== false);
  }

  if (search) {
    const searchLower = search.trim().toLowerCase();
    filtered = filtered.filter(t =>
      (t.unit || '').toLowerCase().includes(searchLower) ||
      (t.nickname && t.nickname.toLowerCase().includes(searchLower)) ||
      (t.make && t.make.toLowerCase().includes(searchLower)) ||
      _vinSearchMatch(t, search)
    );
  }
  if (type !== 'all') {
    filtered = filtered.filter(t => t.type === type);
  }
  const trailerYardFilter = RosterState.filters.trailers.yard;
  if (trailerYardFilter !== 'all') {
    filtered = filtered.filter(t => trailerYardFilter === '' ? !t.yardId : t.yardId === trailerYardFilter);
  }

  // Sort
  const trailerSort = RosterState.filters.trailers.sort || 'unit';
  if (trailerSort === 'name') {
    filtered.sort((a, b) => (a.nickname || a.unit || '').localeCompare(b.nickname || b.unit || ''));
  } else {
    filtered.sort((a, b) => {
      const aNum = parseInt((a.unit || '').replace(/\D/g, '')) || 0;
      const bNum = parseInt((b.unit || '').replace(/\D/g, '')) || 0;
      return aNum - bNum || (a.unit || '').localeCompare(b.unit || '');
    });
  }

  if (RosterState.trailers.length === 0) {
    if (!RosterState._trailersLoaded) {
      container.innerHTML = `
        <div style="text-align:center;padding:var(--spacing-xl);">
          <div class="btn-spinner" style="width:32px;height:32px;margin:0 auto var(--spacing-md);"></div>
          <div style="color:var(--text-secondary);font-size:0.875rem;">Loading trailers…</div>
        </div>
      `;
    } else {
      container.innerHTML = `
        <div class="empty-state">
          <div class="empty-state-icon">🚚</div>
          <div class="empty-state-title">No trailers yet</div>
          <div class="empty-state-text">Add your first trailer to get started.</div>
        </div>
      `;
    }
    return;
  }

  if (filtered.length === 0) {
    container.innerHTML = `
      <div class="empty-state">
        <div class="empty-state-icon">🔍</div>
        <div class="empty-state-title">No matches</div>
        <div class="empty-state-text">Try adjusting your search or filter.</div>
      </div>
    `;
    return;
  }

  container.innerHTML = filtered.map(trailer => {
    const showFlag = shouldShowInspectionFlag(trailer);
    const daysUntil = trailer.inspectionExpiry ? Math.ceil((new Date(trailer.inspectionExpiry) - new Date()) / (1000 * 60 * 60 * 24)) : null;
    const inspFlagTitle = !trailer.inspectionExpiry ? 'No inspection date entered' : daysUntil < 0 ? `Inspection expired ${Math.abs(daysUntil)} days ago` : `Inspection due in ${daysUntil} days`;
    return `
    <div class="roster-card roster-card-clickable ${trailer.active === false ? 'inactive' : ''}" data-id="${trailer.id}" onclick="window.RosterModule.editTrailer('${trailer.id}')">
      <div class="roster-card-icon roster-card-icon-photo${trailer.photoUrl ? ' has-photo' : ''}">
        ${trailer.photoUrl
          ? `<img src="${escapeHtml(trailer.photoUrl)}" alt="${escapeHtml(getUnitDisplay(trailer))}" class="roster-card-photo" loading="lazy">`
          : `<span class="material-icons">rv_hookup</span>`}
        ${trailer.active === false ? '<span class="inactive-badge" title="Inactive"><span class="material-icons">block</span></span>' : ''}
      </div>
      <div class="roster-card-info">
        <div class="roster-card-name">
          ${escapeHtml(getUnitDisplay(trailer))}
          ${trailer.active === false ? ' <span class="inactive-label">INACTIVE</span>' : ''}
          ${showFlag ? `<span class="inspection-due-flag" title="${inspFlagTitle}"><span class="material-icons">flag</span></span>` : ''}
          ${_noRegExpiryBadge(trailer)}
          ${_rentalBadge(trailer)}
        </div>
        <div class="roster-card-meta">
          <span class="badge badge-${escapeHtml(trailer.type)}">${escapeHtml(getTypeLabel(trailer.type, 'trailerTypes'))}</span>
          ${trailer.yardId ? `<span class="badge badge-yard">${escapeHtml(getLiveYardLabel(trailer.yardId) || trailer.yardName || getYardLabel(trailer.yardId))}</span>` : ''}
          ${trailer.make ? `<span>${escapeHtml(trailer.make)}</span>` : ''}
          ${trailer.year ? `<span>${escapeHtml(trailer.year)}</span>` : ''}
        </div>
      </div>
      <div class="roster-card-actions">
        ${getYardsList().length > 0 ? `<button class="btn-icon" onclick="event.stopPropagation(); window.RosterModule.transferToYard('trailers','${trailer.id}')" title="Transfer Yard"><span class="material-icons">swap_horiz</span></button>` : ''}
        <button class="btn-icon btn-icon-danger" onclick="event.stopPropagation(); window.RosterModule.deleteTrailer('${trailer.id}')" title="Deactivate">
          <span class="material-icons">delete</span>
        </button>
      </div>
      <span class="roster-card-chevron material-icons">chevron_right</span>
    </div>
  `;
  }).join('');
}

// ============================================
// NOTIFICATION TAG CARDS
// ============================================

// TASK-865 — extended person-form fields (personal email; plus the terminated
// section via editPerson's tail block, whose later write wins) generalized from
// the trippin-trucking hardcode ("build for everybody"). Flag absent/false =
// hidden — the pre-existing non-TITS behavior. TITS is backfilled true via
// scripts/backfill-task865-tits-flags.cjs.
function rosterExtendedFields() {
  return window.AppState?.settings?.rosterExtendedFields === true;
}

/** Build notification tag cards into #person-notify-tags, gated by module access */
function buildNotifyTagCards() {
  const container = document.getElementById('person-notify-tags');
  if (!container) return;
  // TASK-932 — the tenant-literal founder arm is gone: hasAddOnPackage() (app.js)
  // is the ONE place the founder/VIP/trial comp lives, and already returns true there.
  const visibleTags = NOTIFY_TAG_DEFS.filter(t => {
    if (t.value === 'ALL') return false; // handled via Select All button
    if (!t.gate) return true;
    return !!window.hasAddOnPackage?.(t.gate);
  });

  container.innerHTML = `
    <label style="font-weight:600;margin-bottom:4px;display:block;font-size:0.85rem;color:var(--text-secondary);">Notifications this person receives</label>
    <div style="font-size:0.72rem;color:var(--text-muted);margin-bottom:6px;line-height:1.35;">
      These control what alerts <strong>this person gets</strong>. Most drivers only need Job Alerts — anything else just adds noise to their phone.
    </div>
    <div class="notify-multi-select" id="notify-multi-select">
      <div class="notify-multi-toggle" onclick="window.RosterModule.toggleNotifyDropdown()">
        <span class="notify-multi-summary" id="notify-multi-summary">None selected</span>
        <span class="material-icons" style="font-size:20px;color:var(--text-muted);">expand_more</span>
      </div>
      <div class="notify-multi-dropdown hidden" id="notify-multi-dropdown">
        <label class="notify-multi-option notify-multi-all">
          <input type="checkbox" value="ALL" id="notify-tag-all">
          <span><strong>Select All</strong> — usually too much for drivers</span>
        </label>
        ${visibleTags.map(t => `
          <label class="notify-multi-option" style="display:flex;align-items:flex-start;gap:8px;padding:6px 4px;">
            <input type="checkbox" value="${t.value}" style="margin-top:3px;flex-shrink:0;">
            <span style="display:flex;flex-direction:column;gap:2px;">
              <span style="font-weight:600;">${t.label}</span>
              <span style="font-size:0.72rem;color:var(--text-muted);font-weight:400;line-height:1.3;">${t.desc}</span>
            </span>
          </label>
        `).join('')}
      </div>
    </div>
    <div id="notify-tags-described" style="margin-top:8px;font-size:0.78rem;line-height:1.45;"></div>
  `;

  // Wire ALL toggle
  const allCb = container.querySelector('#notify-tag-all');
  if (allCb) {
    allCb.addEventListener('change', () => {
      container.querySelectorAll('.notify-multi-dropdown input[type="checkbox"]').forEach(cb => {
        if (cb !== allCb) cb.checked = allCb.checked;
      });
      updateNotifySummary();
      updateNotifyDescribed();
    });
  }
  // Wire individual checkboxes
  container.querySelectorAll('.notify-multi-dropdown input[type="checkbox"]').forEach(cb => {
    if (cb === allCb) return;
    cb.addEventListener('change', () => {
      syncAllCheckbox();
      updateNotifySummary();
      updateNotifyDescribed();
    });
  });

  // Close dropdown on outside click
  document.addEventListener('click', (e) => {
    const dd = document.getElementById('notify-multi-dropdown');
    const ms = document.getElementById('notify-multi-select');
    if (dd && ms && !ms.contains(e.target)) {
      dd.classList.add('hidden');
    }
  });

  updateNotifySummary();
}

function toggleNotifyDropdown() {
  const dd = document.getElementById('notify-multi-dropdown');
  if (dd) dd.classList.toggle('hidden');
}

function updateNotifySummary() {
  const container = document.getElementById('person-notify-tags');
  if (!container) return;
  const summary = document.getElementById('notify-multi-summary');
  if (!summary) return;
  const checked = Array.from(container.querySelectorAll('.notify-multi-dropdown input[type="checkbox"]:checked'))
    .filter(cb => cb.value !== 'ALL');
  const allCb = container.querySelector('#notify-tag-all');
  const totalIndividual = Array.from(container.querySelectorAll('.notify-multi-dropdown input[type="checkbox"]'))
    .filter(cb => cb.value !== 'ALL').length;

  if (checked.length === 0) {
    summary.textContent = 'None selected';
    summary.style.color = 'var(--text-muted)';
  } else if (allCb?.checked || checked.length === totalIndividual) {
    summary.textContent = 'All notifications';
    summary.style.color = 'var(--text-primary)';
  } else if (checked.length <= 2) {
    const tag = (val) => NOTIFY_TAG_DEFS.find(t => t.value === val)?.label || val;
    summary.textContent = checked.map(cb => tag(cb.value)).join(', ');
    summary.style.color = 'var(--text-primary)';
  } else {
    summary.textContent = `${checked.length} selected`;
    summary.style.color = 'var(--text-primary)';
  }
}

// Plain-language consequence-of-save text — composes the running list of
// what THIS PERSON will actually receive based on currently-checked tags.
// Lives below the dropdown so users see the impact of their selections at
// a glance before saving. Empty when nothing's selected.
const NOTIFY_TAG_CONSEQUENCES = {
  JOBALERTS: 'mass-text schedule broadcasts when dispatch sends them out',
  // TASK-1116 — this tag is ALSO the missed-clock-out escalation channel, and the
  // copy said nothing about it, so the one thing you had to do to receive that
  // escalation was undiscoverable. Naming both consequences here rather than
  // splitting the tag: it is one audience (office/dispatch), and a new tag would
  // leave everyone currently carrying this one silently un-enrolled.
  DISPATCH_ADMIN: 'an alert every time a job is created, updated, or deleted (high volume — typically only for office staff), PLUS a heads-up when a crew member is still clocked in long past the cutoff',
  PRETRIP: 'an SMS/email for EVERY pre-trip/DVIR submission tenant-wide — intended for a safety manager, not each driver',
  MAINTENANCE: 'alerts when maintenance tasks are created and when annual inspections are due',
  COMPLIANCE: 'alerts when insurance or registration is approaching expiration',
  TIMEOFF: 'alerts when time-off requests are submitted or approval status changes',
  ASSETS: 'alerts when orders are created or change status',
  CUSTOMER_DOCS: 'an email + text when a customer uploads a document (at most one per project every 10 minutes) or sends a change request on a project portal, and a reminder once a document has waited a day for review', // TASK-9652 S2 + fix/1018 change requests
  AR_AP: 'billing alerts — driver expense submissions (digest by default), out-of-pocket reimbursements (immediate email + SMS), a daily email when the QuickBooks paid sync finds tickets to review, and future AR/AP events as we ship them',
};
function updateNotifyDescribed() {
  const container = document.getElementById('person-notify-tags');
  const out = container?.querySelector('#notify-tags-described');
  if (!out) return;
  const checked = Array.from(container.querySelectorAll('.notify-multi-dropdown input[type="checkbox"]:checked'))
    .filter(cb => cb.value !== 'ALL');
  if (checked.length === 0) {
    out.innerHTML = '';
    return;
  }
  const personName = (document.getElementById('person-name')?.value?.trim()) || 'This person';
  const items = checked
    .map(cb => NOTIFY_TAG_CONSEQUENCES[cb.value])
    .filter(Boolean);
  if (items.length === 0) { out.innerHTML = ''; return; }
  out.innerHTML = `
    <div style="background:rgba(59,130,246,0.05);border-left:3px solid var(--brand-blue);border-radius:6px;padding:8px 12px;">
      <div style="font-weight:600;color:var(--text-primary);margin-bottom:4px;">${escapeHtml(personName)} will receive:</div>
      <ul style="margin:0;padding-left:18px;color:var(--text-secondary);">
        ${items.map(line => `<li style="margin-bottom:2px;">${escapeHtml(line)}</li>`).join('')}
      </ul>
    </div>`;
}

/** If every individual tag is checked, auto-check ALL; otherwise uncheck ALL */
function syncAllCheckbox() {
  const container = document.getElementById('person-notify-tags');
  if (!container) return;
  const allCb = container.querySelector('#notify-tag-all');
  if (!allCb) return;
  const individuals = Array.from(container.querySelectorAll('.notify-multi-dropdown input[type="checkbox"]')).filter(cb => cb !== allCb);
  allCb.checked = individuals.length > 0 && individuals.every(cb => cb.checked);
}

/** Legacy compat — no-op, cards are gone */
function updateTagCardHighlights() {}

// Crew roles that should have the JOBALERTS tag set by default on new-person creation.
// Office/management roles (owner, dispatcher, admin) intentionally excluded — they can opt in.
const CREW_ROLES_FOR_JOBALERTS_DEFAULT = ['cdl_driver', 'driver', 'laborer', 'foreman', 'operator', 'mechanic', 'helper', 'lead'];

function roleImpliesJobAlerts(role) {
  return CREW_ROLES_FOR_JOBALERTS_DEFAULT.includes(String(role || '').toLowerCase().trim());
}

function setJobAlertsCheckbox(checked) {
  const container = document.getElementById('person-notify-tags');
  if (!container) return;
  const cb = container.querySelector('input[type="checkbox"][value="JOBALERTS"]');
  if (!cb) return;
  cb.checked = !!checked;
  syncAllCheckbox();
  updateNotifySummary();
  updateNotifyDescribed();
}

// ============================================
// CRUD OPERATIONS - PEOPLE
// ============================================

// TASK-262 Slice 1B — populate the Custom Reminders section of the person
// modal from `tenant.compliance.customReminders[]` (Slice 1A) and the
// person's existing `customReminderExpirations` map. Hides the section
// entirely when there are no active items defined for the tenant.
//
// Only ACTIVE items render an input. Expirations for ARCHIVED items are
// preserved on save (see savePerson) — archiving a tenant-level reminder
// hides it from the UI but doesn't delete per-driver history.
function _populatePersonCustomReminders(person) {
  const section = document.getElementById('person-custom-reminders-section');
  const fields = document.getElementById('person-custom-reminders-fields');
  const countEl = document.getElementById('person-custom-reminders-count');
  if (!section || !fields) return;

  const getCurrent = window._crGetCurrent;
  if (typeof getCurrent !== 'function') {
    section.style.display = 'none';
    return;
  }
  const all = getCurrent();
  const active = all.filter(i => i && i.archived !== true);
  if (active.length === 0) {
    section.style.display = 'none';
    fields.innerHTML = '';
    if (countEl) countEl.textContent = '';
    return;
  }

  const expirations = (person && person.customReminderExpirations) || {};

  section.style.display = '';
  if (countEl) countEl.textContent = `(${active.length})`;

  fields.innerHTML = active.map(item => {
    const safeId = escapeHtml(item.itemId);
    const safeLabel = escapeHtml(item.label);
    const value = expirations[item.itemId] || '';
    return `
      <div class="form-group" style="margin-bottom:12px;">
        <label for="person-cr-${safeId}">${safeLabel} <span style="color:var(--text-muted);font-weight:400;font-size:0.75rem;">expires</span></label>
        <input type="date" id="person-cr-${safeId}" data-custom-reminder-id="${safeId}" value="${escapeHtml(value)}" class="form-control" style="max-width:200px;">
      </div>
    `;
  }).join('');
}

// ============================================
// TASK-9844 — Crew skills (company-defined skills on people + required skills
// per job type / vehicle type). The rule lives in functions/src/shared/
// crewSkills.js; this view reads the generated browser copy through
// `window.CrewSkills` at call time (Pitfall #1 — no cross ?v= import).
//
// Stored on the tenant doc as settings.crewSkills (one map, see crewSkills.js)
// and on each person as `skills: [{ id, expiresOn }]`. People docs reach every
// session (drivers too) — a skill name + an expiry date only, nothing else.
// TASK-9911 — two switches. People carry skills (person section, card chips,
// the modal's "Who has it" checklist) as soon as the company keeps one active
// skill. The "Warn dispatch" switch gates only the dispatch warnings
// (dispatch.js _crewSkillWarnings, which still reads CS.isOn) and what a job /
// vehicle type requires. Dispatch only ever WARNS.
// ============================================
function _skillsCfg() {
  const CS = window.CrewSkills;
  return CS ? CS.config(window.AppState?.settings || {}) : { enabled: false, list: [], byJobType: {}, byVehicleType: {} };
}
function _skillsExist() {
  const CS = window.CrewSkills;
  if (!CS) return false;
  const cfg = _skillsCfg();
  return !!(typeof CS.hasSkills === 'function' ? CS.hasSkills(cfg) : CS.isOn(cfg));
}
// Whether this login may change who holds a skill. Mirrors the people update
// rule (dispatcher+, or the roster module without viewUploadOnly) on top of
// the modal's own gate; a view-only login sees the checklist read-only.
function _canEditPeopleSkills() {
  if (!_canManageCrewSkills()) return false;
  const m = window.AppState?.memberAccess;
  return !(m && (m.viewUploadOnly === true || m.readOnly === true));
}
// TASK-9911 M2 — the Skills & certifications screen can be opened from HR,
// where Roster's people listener may not be running (it stops when Roster is
// left) and HR's list can be partial (COD r1: Compliance seeds one subject; a
// first open lands before HR's snapshot). So opened outside Roster the screen
// reads the tenant's people ONCE itself (_loadSkillPeople) and uses that;
// opened from Roster, Roster's live list.
let _cskPeopleSource = null; // null = Roster's list; else an array this screen loaded
let _cskPeopleLoading = false;
// "Who has it (N)" — no number while the screen's own crew read is in flight.
function _cskWhoLabel(n) { return _cskPeopleLoading ? 'Who has it (…)' : `Who has it (${n})`; }
function _skillPeopleAll() {
  return Array.isArray(_cskPeopleSource) ? _cskPeopleSource : (RosterState.people || []);
}
async function _loadSkillPeople() {
  const tid = window.AppState?.tenant;
  if (!tid) return;
  try {
    const snap = await getDocs(collection(db, 'tenants', tid, 'people'));
    if (!document.getElementById('modal-crew-skills')) return; // closed meanwhile
    _cskPeopleSource = snap.docs.map(d => ({ id: d.id, ...d.data() }));
    _cskPeopleLoading = false;
    _refreshCrewSkillsUI();
  } catch (e) {
    _cskPeopleLoading = false;
    console.error('[Roster] skills screen people load failed', e);
    const note = document.getElementById('csk-modal-body');
    if (note) note.insertAdjacentHTML('afterbegin', '<p class="form-hint" style="color:#b91c1c;margin:0 0 6px;">Could not load the crew list — close and open this screen again.</p>');
  }
}
// Active crew for the "Who has it" checklist, by name.
function _skillChecklistPeople() {
  return _skillPeopleAll()
    .filter(p => p && p.active !== false && p.terminated !== true && !p.terminationDate)
    .slice()
    .sort((a, b) => String(a.name || '').localeCompare(String(b.name || '')));
}
// TASK-10211 BREAK IN SERVICE chip: Inactive driver past 30 days per PersonSections (never copy the 30).
function _bisInactiveChip(p) {
  const R = window.PersonSections?.resolveBreakInService, t = p.deactivatedAt;
  // COD Domain: CDL tier only (unclassified = CDL, as the DQ checklist does); a 'Non-CDL' / 'Non-Driver' role is not one.
  if (!R || !t || p.active !== false || p.terminated === true || (p.positionType && p.positionType !== 'cdl') || !/^(?!non).*(driver|cdl)/i.test(p.crewType || p.role || p.roles?.[0] || '')) return '';
  try {
    // COD Code Reviewer: a YYYY-MM-DD string is a local day already (new Date() would read it as UTC).
    if (R({ breakInServiceReturnAt: _todayKeyLocal(), breakInServiceDepartureAt: typeof t === 'string' ? t.slice(0, 10) : _todayKeyLocal(t.toDate ? t.toDate() : new Date(t.seconds * 1000)) }).state !== 'required') return '';
  } catch (_) { return ''; }
  return ' <span class="dvir-flag-label" style="background:#FEF3C7;color:#92400E;border-color:#FDE68A;" title="Inactive over 30 days: 3 return items due before they drive">BREAK IN SERVICE</span>';
}
function _todayKeyLocal(d = new Date()) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

// MODULE access, never a role (Chris 2026-09-24): the tenant OWNER, a legacy
// admin with no module list, or a member holding 'roster'. Mirrors
// firestore.rules (settings.crewSkills is on the roster module's settings
// allowlist, t9337SettingsOk). Driver sessions never see the button.
function _canManageCrewSkills() {
  const S = window.AppState || {};
  if (S.driverMode || S.isPublicAccess || S.driverModePin) return false;
  const uid = S.user?.uid || null;
  const ownerId = S.tenantData?.ownerId || null;
  if (uid && ownerId && uid === ownerId) return true;
  const m = S.memberAccess;
  if (!m) return S.isDemo === true || S.tenant === 'demo';
  if (['suspended', 'removed', 'revoked', 'inactive', 'pending'].includes(m.status)) return false;
  if (m.role === 'owner') return true; // an owner member holds every module (modules: [] on owner docs)
  if (!Array.isArray(m.modules)) return m.role === 'admin';
  return m.modules.includes('roster');
}

function _syncCrewSkillsButton() {
  const btn = document.getElementById('btn-roster-skills');
  if (btn) btn.style.display = _canManageCrewSkills() ? '' : 'none';
}

// Up to 3 chips on the person card; expired ones in red. Nothing when off.
function _personSkillChips(person) {
  const CS = window.CrewSkills;
  if (!CS || !_skillsExist()) return '';
  const cfg = _skillsCfg();
  const today = _todayKeyLocal();
  const held = CS.personSkills(person)
    .map(s => ({ s, sk: CS.skillById(cfg, s.id) }))
    .filter(x => x.sk && !x.sk.archived);
  if (!held.length) return '';
  const chip = (x) => {
    const expired = x.s.expiresOn && x.s.expiresOn < today;
    const title = expired ? `${x.sk.label} — expired ${x.s.expiresOn}` : (x.s.expiresOn ? `${x.sk.label} — expires ${x.s.expiresOn}` : x.sk.label);
    return `<span class="badge" data-skill-chip="${escapeHtml(x.s.id)}" title="${escapeHtml(title)}" style="${expired ? 'background:#fef2f2;color:#b91c1c;border-color:#fecaca;' : 'background:#eef2ff;color:#3730a3;border-color:#c7d2fe;'}">${escapeHtml(x.sk.label)}${expired ? ' (expired)' : ''}</span>`;
  };
  const more = held.length > 3 ? `<span class="badge" title="${escapeHtml(held.slice(3).map(x => x.sk.label).join(', '))}">+${held.length - 3}</span>` : '';
  return held.slice(0, 3).map(chip).join('') + more;
}

// Person modal section — TASK-9911 M2 "Skills & certifications", rendered by
// the shared window.PersonSections.renderSkillsAndCerts (roster / hr /
// compliance). Skills: active skills + any archived skill this person already
// holds (so a save never drops it), editable. Certifications: the required-
// training list with this person's state, READ-ONLY here (cards are uploaded
// in HR). RosterState._skillsShown records whether the user could SEE the
// skill rows — a hidden skills half never writes `skills`.
function _personCertRows(person) {
  if (!person) return [];
  const list = (window._stGetCurrent?.() || []).filter(i => i && i.archived !== true);
  const calc = window.HRModule?.computeRequiredTrainingState;
  if (!list.length || typeof calc !== 'function') return [];
  const rows = [];
  for (const c of list) {
    try { rows.push({ itemId: c.itemId, label: c.label, ...calc(person, c) }); }
    catch (_) { /* one bad item never hides the rest */ }
  }
  return rows;
}
function _populatePersonSkills(person) {
  const section = document.getElementById('person-skills-section');
  const fields = document.getElementById('person-skills-fields');
  const countEl = document.getElementById('person-skills-count');
  RosterState._skillsShown = false;
  RosterState._skillsAtOpen = new Map();
  if (!section || !fields) return;
  const CS = window.CrewSkills;
  const PS = window.PersonSections;
  const skillsOn = !!(CS && _skillsExist());
  const certRows = _personCertRows(person);
  if (!PS || typeof PS.renderSkillsAndCerts !== 'function' || (!skillsOn && !certRows.length)) {
    section.style.display = 'none';
    fields.innerHTML = '';
    if (countEl) countEl.textContent = '';
    return;
  }
  let skillRows = [];
  let cfg = null;
  if (skillsOn) {
    cfg = _skillsCfg();
    const mine = new Map(CS.personSkills(person || {}).map(s => [s.id, s]));
    skillRows = cfg.list.filter(sk => !sk.archived || mine.has(sk.id)).map(sk => ({
      id: sk.id, label: sk.label, archived: sk.archived === true,
      held: mine.has(sk.id), expiresOn: mine.has(sk.id) ? (mine.get(sk.id).expiresOn || '') : '',
    }));
    // TASK-9911 (COD r1) — what each row showed at open, so Save writes only the
    // rows the user changed (merged onto the person's LIVE skills) and a form
    // left open never reverts a "Who has it" tick made meanwhile.
    RosterState._skillsAtOpen = new Map(skillRows.map(r => [r.id, r.held ? (r.expiresOn || '') : null]));
    RosterState._skillsShown = skillRows.length > 0;
  }
  const heldShown = skillRows.filter(r => r.held).length;
  if (countEl) countEl.textContent = heldShown ? `(${heldShown})` : '';
  section.style.display = '';
  fields.innerHTML = PS.renderSkillsAndCerts({
    skills: skillRows.length ? {
      rows: skillRows, editable: true, today: _todayKeyLocal(),
      note: `Things this person can do, no card needed. Tick what they have. An expiry date is optional${cfg && cfg.enabled ? ' — dispatch warns once it has passed' : ''}.`,
    } : null,
    certs: certRows.length ? {
      rows: certRows,
      note: 'Cards on file. Upload or renew a card in HR, on this person\'s Compliance tab.',
    } : null,
  });
}

// TASK-9911 (COD r1/r2) — the Skills rows the user CHANGED since the form
// opened: [{ id, on, expiresOn }], or undefined when the section was not shown
// or nothing in it changed (savePerson then leaves `skills` alone).
function _collectPersonSkillChanges() {
  if (!RosterState._skillsShown) return undefined;
  const CS = window.CrewSkills;
  if (!CS) return undefined;
  const atOpen = RosterState._skillsAtOpen instanceof Map ? RosterState._skillsAtOpen : new Map();
  const changes = [];
  document.querySelectorAll('#person-skills-fields .person-skill-row').forEach(row => {
    const id = row.dataset.skillId;
    const on = !!row.querySelector('.person-skill-check')?.checked;
    const exp = CS.dateKey(row.querySelector('.person-skill-exp')?.value || '');
    const was = atOpen.has(id) ? atOpen.get(id) : null; // null = not held at open
    if (on === (was !== null) && (!on || exp === was)) return; // row untouched
    changes.push({ id, on, expiresOn: on ? (exp || null) : undefined });
  });
  return changes.length ? changes : undefined;
}

// Changes applied onto a person's skills; every other skill + expiry kept.
function _applySkillChanges(person, changes) {
  const CS = window.CrewSkills;
  let out = CS.personSkills(person || {});
  for (const c of changes || []) {
    out = typeof CS.setHolder === 'function'
      ? CS.setHolder(out, c.id, c.on, c.expiresOn)
      : (c.on ? out.filter(s => s.id !== c.id).concat([{ id: c.id, expiresOn: c.expiresOn || null }]) : out.filter(s => s.id !== c.id));
  }
  return out;
}

// Existing person, after the form write: one transaction on the CURRENT doc.
// A failure is said out loud (the rest of the form did save).
async function _persistPersonSkillChanges(tid, personId, changes) {
  const CS = window.CrewSkills;
  const ref = doc(db, 'tenants', tid, 'people', personId);
  let before = null;
  let after = null;
  try {
    await runTransaction(db, async (tx) => {
      const snap = await tx.get(ref);
      if (!snap.exists()) throw new Error('That person is no longer on the roster.');
      before = CS.personSkills(snap.data() || {});
      after = _applySkillChanges({ skills: before }, changes);
      tx.update(ref, { skills: after });
    });
  } catch (e) {
    console.error('[Roster] person skills save failed', e);
    return false;
  }
  const local = (RosterState.people || []).find(p => p.id === personId);
  if (local) local.skills = after;
  // One audit row per changed skill, same shape as the Who has it tick.
  const cfg = _skillsCfg();
  for (const c of changes || []) {
    const was = before.find(s => s.id === c.id) || null;
    const now = after.find(s => s.id === c.id) || null;
    window.logAudit?.('roster', 'UPDATE_PERSON_SKILL', {
      personId, personName: local?.name || null, skillId: c.id, skillLabel: (CS.skillById(cfg, c.id) || {}).label || null,
      heldFrom: !!was, heldTo: !!now, expiresFrom: was ? was.expiresOn : null, expiresTo: now ? now.expiresOn : null, via: 'person_form',
    });
  }
  return true;
}

// One edit = one transaction on the tenant doc, re-reading the CURRENT config,
// so two people editing at once never erase each other's change.
async function _persistCrewSkillsOp(op) {
  const tid = window.AppState?.tenant;
  const CS = window.CrewSkills;
  if (!tid || !CS) { window.showToast?.('Not logged in', 'error'); return null; }
  if (!_canManageCrewSkills()) { window.showToast?.('You need Roster access to edit skills.', 'error'); return null; }
  const ref = doc(db, 'tenants', tid);
  let result = null;
  let before = null;
  try {
    await runTransaction(db, async (tx) => {
      const snap = await tx.get(ref);
      before = CS.config(snap.data()?.settings || {});
      result = CS.apply(before, op);
      if (result.error) return;
      tx.update(ref, { 'settings.crewSkills': result.config });
    });
  } catch (e) {
    console.error('[Roster] crew skills save failed', e);
    window.showToast?.('Could not save skills: ' + (e?.message || 'unknown error'), 'error');
    return null;
  }
  if (!result) return null;
  if (result.error) { window.showToast?.(result.error, 'error'); return null; }
  if (!window.AppState.settings) window.AppState.settings = {};
  window.AppState.settings.crewSkills = result.config;
  // Audited old -> new for the piece that moved. TASK-9911 M3 — a cert
  // requirement ('cert:<itemId>') is named from HR's training list.
  const certLabels = new Map((typeof CS.certList === 'function' ? CS.certList(window._stGetCurrent?.() || []) : []).map(c => [c.id, c.label]));
  const label = (cfg, id) => (CS.skillById(cfg, id) || {}).label || certLabels.get(id) || (typeof CS.certIdOk === 'function' && CS.certIdOk(id) ? id : null);
  const detail = { op: op.kind };
  if (op.kind === 'enable') { detail.enabledFrom = before.enabled; detail.enabledTo = result.config.enabled; }
  if (op.kind === 'add') { detail.skillId = result.id; detail.labelTo = label(result.config, result.id); }
  if (op.kind === 'rename' || op.kind === 'archive') {
    detail.skillId = op.id; detail.labelFrom = label(before, op.id); detail.labelTo = label(result.config, op.id);
    if (op.kind === 'archive') detail.archivedTo = op.archived === true;
  }
  if (op.kind === 'require' || op.kind === 'toggleRequire') {
    const m = op.scope === 'jobType' ? 'byJobType' : 'byVehicleType';
    detail.scope = op.scope; detail.typeKey = op.key;
    detail.requiredFrom = (before[m][op.key] || []).map(id => label(before, id));
    detail.requiredTo = (result.config[m][op.key] || []).map(id => label(result.config, id));
  }
  window.logAudit?.('settings', 'SAVE_CREW_SKILLS', detail);
  return result;
}

// TASK-9911 — the modal's "Who has it" tick. ONE skill on/off for ONE person,
// merged inside a transaction into the person's CURRENT skills, so their other
// skills + expiries are never erased. (The person form, in turn, writes only the
// rows the user changed — _collectPersonSkills.)
// expiresOn: undefined keeps what is stored. Audited old -> new.
async function _persistPersonSkill(personId, skillId, on, expiresOn) {
  const tid = window.AppState?.tenant;
  const CS = window.CrewSkills;
  if (!tid || !CS || typeof CS.setHolder !== 'function') { window.showToast?.('Skills are still loading — try again in a moment.', 'error'); return false; }
  if (!_canEditPeopleSkills()) { window.showToast?.('Your login can see skills but not change them.', 'error'); return false; }
  const sk = CS.skillById(_skillsCfg(), skillId);
  if (!sk || !personId) { window.showToast?.('That skill or person is no longer on the list.', 'error'); return false; }
  const ref = doc(db, 'tenants', tid, 'people', personId);
  let before = null;
  let after = null;
  try {
    await runTransaction(db, async (tx) => {
      const snap = await tx.get(ref);
      if (!snap.exists()) throw new Error('That person is no longer on the roster.');
      before = CS.personSkills(snap.data() || {});
      after = CS.setHolder(before, skillId, on === true, expiresOn);
      tx.update(ref, { skills: after });
    });
  } catch (e) {
    console.error('[Roster] person skill save failed', e);
    const denied = e?.code === 'permission-denied' || /permission/i.test(e?.message || '');
    window.showToast?.(denied ? 'Your login cannot change people, so the skill was not saved.' : 'Could not save the skill: ' + (e?.message || 'unknown error'), 'error');
    return false;
  }
  // The checklist's list (HR's when opened from HR) and Roster's own.
  const local = _skillPeopleAll().find(p => p.id === personId) || (RosterState.people || []).find(p => p.id === personId);
  if (local) local.skills = after;
  const rosterLocal = (RosterState.people || []).find(p => p.id === personId);
  if (rosterLocal && rosterLocal !== local) rosterLocal.skills = after;
  const was = before.find(s => s.id === skillId) || null;
  const now = after.find(s => s.id === skillId) || null;
  window.logAudit?.('roster', 'UPDATE_PERSON_SKILL', {
    personId, personName: local?.name || null, skillId, skillLabel: sk.label,
    heldFrom: !!was, heldTo: !!now, expiresFrom: was ? was.expiresOn : null, expiresTo: now ? now.expiresOn : null,
  });
  return true;
}

// TASK-9911 — which skill's "Who has it" checklist is open (one at a time).
let _cskOpenSkillId = null;

// The skill-first checklist: every active person, tick = holds it, optional
// expiry. Read-only (disabled + a plain line) for a login that can't change people.
function _crewSkillHoldersHTML(skill) {
  const CS = window.CrewSkills;
  const canEdit = _canEditPeopleSkills();
  const today = _todayKeyLocal();
  const people = _skillChecklistPeople();
  const sid = escapeHtml(skill.id);
  const rows = people.map(p => {
    const held = CS.personSkills(p).find(s => s.id === skill.id) || null;
    const exp = held && held.expiresOn ? held.expiresOn : '';
    const expired = exp && exp < today;
    const name = String(p.name || '').trim() || 'Unnamed person';
    return `
        <div class="csk-holder" data-person-id="${escapeHtml(p.id)}" style="display:flex;align-items:center;gap:10px;flex-wrap:wrap;padding:6px 0;border-bottom:1px solid var(--border-light);">
          <label style="display:flex;align-items:center;gap:8px;flex:1;min-width:150px;margin:0;color:var(--text-primary);min-height:32px;">
            <input type="checkbox" class="csk-holder-check" ${held ? 'checked' : ''} ${canEdit ? '' : 'disabled'} aria-label="${escapeHtml(name)} has ${escapeHtml(skill.label)}">
            ${escapeHtml(name)}
          </label>
          <label style="display:flex;align-items:center;gap:6px;margin:0;font-size:0.8rem;color:var(--text-secondary);">
            Expires
            <input type="date" class="form-control csk-holder-exp" value="${escapeHtml(exp)}" ${canEdit ? '' : 'disabled'} style="max-width:160px;" aria-label="${escapeHtml(name)} ${escapeHtml(skill.label)} expiry date">
          </label>
          ${expired ? '<span style="font-size:0.75rem;font-weight:600;color:#b91c1c;">Expired</span>' : ''}
        </div>`;
  }).join('');
  return `
      <div class="csk-holders" data-skill-id="${sid}" style="margin:4px 0 10px;padding:8px 12px;border:1px solid var(--border-light);border-radius:8px;background:var(--bg-secondary, #f8fafc);">
        <p class="form-hint" style="margin:0 0 4px;">${canEdit ? 'Tick everyone who has it. Each tick saves on its own.' : 'Your login can see who has it but cannot change people.'}</p>
        ${rows || (_cskPeopleLoading ? '<p style="color:var(--text-secondary);font-size:0.85rem;margin:6px 0;">Loading the crew…</p>' : '<p style="color:var(--text-secondary);font-size:0.85rem;margin:6px 0;">No active people on the roster yet.</p>')}
      </div>`;
}

function _crewSkillsBodyHTML() {
  const CS = window.CrewSkills;
  const cfg = _skillsCfg();
  const active = cfg.list.filter(s => !s.archived);
  const people = _skillChecklistPeople();
  if (_cskOpenSkillId && !active.some(s => s.id === _cskOpenSkillId)) _cskOpenSkillId = null;
  // TASK-9911 M2 — opened from HR by a login without Roster access: the list
  // and who has each skill are shown, nothing is editable (settings.crewSkills
  // and people writes are Roster's), and it says so.
  const canManage = _canManageCrewSkills();
  if (!canManage) {
    const roRows = active.map(s => {
      const open = _cskOpenSkillId === s.id;
      const count = people.filter(p => CS.personSkills(p).some(x => x.id === s.id)).length;
      return `
      <div class="csk-row" data-skill-id="${escapeHtml(s.id)}" style="display:flex;align-items:center;gap:8px;flex-wrap:wrap;padding:6px 0;border-bottom:1px solid var(--border-light);">
        <span class="csk-label-ro" style="flex:1;min-width:140px;font-weight:600;color:var(--text-primary);">${escapeHtml(s.label)}</span>
        <button type="button" class="btn btn-small btn-secondary csk-who" aria-expanded="${open ? 'true' : 'false'}" title="See who has this skill">${_cskWhoLabel(count)}</button>
      </div>${open ? _crewSkillHoldersHTML(s) : ''}`;
    }).join('');
    return `
    <p class="form-hint" id="csk-readonly-note" style="margin:0 0 6px;">You need Roster access to add skills or change who has them.</p>
    <div id="csk-rows">${roRows || '<p style="color:var(--text-secondary);font-size:0.85rem;margin:6px 0;">No skills on the list yet.</p>'}</div>`;
  }
  const skillRows = cfg.list.map(s => {
    const open = !s.archived && _cskOpenSkillId === s.id;
    const count = s.archived ? 0 : people.filter(p => CS.personSkills(p).some(x => x.id === s.id)).length;
    const who = s.archived ? '' : `<button type="button" class="btn btn-small btn-secondary csk-who" aria-expanded="${open ? 'true' : 'false'}" title="Tick the people who have this skill">${_cskWhoLabel(count)}</button>`;
    return `
      <div class="csk-row" data-skill-id="${escapeHtml(s.id)}" style="display:flex;align-items:center;gap:8px;flex-wrap:wrap;padding:6px 0;border-bottom:1px solid var(--border-light);${s.archived ? 'opacity:0.6;' : ''}">
        <input type="text" class="csk-label form-control" maxlength="40" value="${escapeHtml(s.label)}" aria-label="Skill name" style="flex:1;min-width:140px;">
        ${s.archived ? '<span style="font-size:0.75rem;color:var(--text-secondary);">Archived</span>' : ''}
        ${who}
        <button type="button" class="btn btn-small btn-secondary csk-archive" data-archived="${s.archived ? '1' : '0'}">${s.archived ? 'Restore' : 'Archive'}</button>
      </div>${open ? _crewSkillHoldersHTML(s) : ''}`;
  }).join('');
  // TASK-9911 M3 — the same rows also offer HR's active certifications
  // ('cert:<itemId>' in the same maps; a person holds one when HR has a card
  // on file that is good on the job day). Skill chips first, then cards.
  const certs = typeof CS.certList === 'function' ? CS.certList(window._stGetCurrent?.() || []) : [];
  const chip = (id, text, on, isCert) => `<button type="button" class="csk-chip${isCert ? ' csk-chip-cert' : ''}" data-skill-id="${escapeHtml(id)}" aria-pressed="${on ? 'true' : 'false'}" title="${isCert ? 'Certification (card on file in HR)' : 'Skill'}" style="min-height:32px;padding:4px 10px;border-radius:999px;font-size:0.8rem;cursor:pointer;border:1px ${isCert && !on ? 'dashed' : 'solid'} ${on ? 'var(--brand-blue)' : 'var(--border-light)'};background:${on ? 'var(--brand-blue)' : '#fff'};color:${on ? '#fff' : 'var(--text-primary)'};">${on ? '&#10003; ' : ''}${isCert ? '<span class="material-icons" aria-hidden="true" style="font-size:14px;vertical-align:-2px;margin-right:2px;">badge</span>' : ''}${escapeHtml(text)}</button>`;
  const chipRow = (scope, key, label, have) => `
      <div class="csk-req" data-scope="${scope}" data-key="${escapeHtml(key)}" style="display:flex;align-items:flex-start;gap:8px;flex-wrap:wrap;padding:6px 0;border-bottom:1px solid var(--border-light);">
        <div style="flex:0 0 140px;font-weight:600;font-size:0.85rem;color:var(--text-primary);padding-top:6px;">${escapeHtml(label)}</div>
        <div style="display:flex;gap:6px;flex-wrap:wrap;flex:1;min-width:160px;">
          ${active.map(s => chip(s.id, s.label, have.includes(s.id), false)).join('')}
          ${certs.map(c => chip(c.id, c.label, have.includes(c.id), true)).join('')}
        </div>
      </div>`;
  const JT = window.JobTypes;
  const jobTypes = (JT ? JT.list(window.AppState?.settings || {}) : [{ value: 'job', label: 'Job' }]).filter(t => !t.archived);
  const vehicleTypes = getTypeList('truckTypes');
  const reqSections = (active.length || certs.length) ? `
    <h4 style="margin:14px 0 4px;font-size:0.9rem;">Needed for a job type</h4>
    <p class="form-hint" style="margin:0 0 6px;">Everyone on the day (drivers and helpers) needs these.</p>
    ${jobTypes.map(t => chipRow('jobType', t.value, t.label, cfg.byJobType[t.value] || [])).join('')}
    <h4 style="margin:14px 0 4px;font-size:0.9rem;">Needed for a vehicle type</h4>
    <p class="form-hint" style="margin:0 0 6px;">The drivers of that vehicle need these.</p>
    ${vehicleTypes.map(t => chipRow('vehicleType', t.slug, t.label, cfg.byVehicleType[t.slug] || [])).join('')}` : '';
  // TASK-9911 — skills first; the warn switch + requirements in their own section.
  return `
    ${cfg.list.length
      ? '<p class="form-hint" style="margin:0 0 6px;">Tap <strong>Who has it</strong> to tick the people who hold a skill. Rename a skill by editing its name; Archive hides it from new picks.</p>'
      : '<p class="form-hint" style="margin:0 0 6px;">Add the skills your crew can hold, then tick who has each one.</p>'}
    <div id="csk-rows">${skillRows}</div>
    <div style="display:flex;gap:8px;margin-top:10px;flex-wrap:wrap;">
      <input type="text" id="csk-new-label" class="form-control" maxlength="40" placeholder="New skill, e.g. Confined space" style="flex:1;min-width:160px;">
      <button type="button" class="btn btn-primary" id="csk-add-btn"><span class="material-icons" style="font-size:18px;">add</span> Add</button>
    </div>
    ${(active.length || certs.length) ? `
    <section id="csk-warn-section" style="margin-top:20px;padding-top:14px;border-top:2px solid var(--border-light);">
      <h4 style="margin:0 0 6px;font-size:0.95rem;">When to warn dispatch</h4>
      <label class="checkbox-switch" style="margin-bottom:4px;">
        <input type="checkbox" id="csk-enabled" ${cfg.enabled ? 'checked' : ''}>
        <span class="switch-slider"></span>
        <span class="switch-label">Warn dispatch when someone is missing a skill or certification</span>
      </label>
      <p class="form-hint" style="margin:0 0 6px;">A warning only — it never blocks a dispatch. Skills check who is ticked above; certifications (<span class="material-icons" aria-hidden="true" style="font-size:13px;vertical-align:-2px;">badge</span>) check the cards on file in HR, good on the job day. Someone HR marks not required or exempt still gets a warning that says so.</p>
      ${reqSections}
    </section>` : ''}`;
}

function _refreshCrewSkillsUI() {
  const body = document.getElementById('csk-modal-body');
  if (body) body.innerHTML = _crewSkillsBodyHTML();
  renderPeopleList(); // no-ops off the Roster page (no #list-people)
}

// ============================================
// TASK-9911 M2 — ONE door: "Skills & certifications". Roster → Skills and
// HR → Safety → Required trainings both open this screen. Two sections, two
// stores, never merged (decision 2026-10-16, COD cod-20261016T143314Z):
//   Skills         — settings.crewSkills; the carrier always owns it (the
//                    editor above, unchanged).
//   Certifications — settings.safetyTrainings; app.js's existing editor
//                    (_stRenderList / addSafetyTraining / … keyed off the same
//                    DOM ids, saved through the updateSafetyTrainings callable),
//                    partner locks untouched.
// The HR page no longer renders that editor inline, so its ids exist once.
// ============================================

// Mirror of updateSafetyTrainings' carrier gate (owner/admin, or the hr /
// compliance module and not view & upload only) on top of the partner lock
// (_stCanEdit). Deliberately one notch STRICTER: a readOnly member is shown the
// list read-only too (the callable does not check readOnly). UI only — the
// callable re-checks every save.
function _canEditCertList() {
  const S = window.AppState || {};
  if (S.driverMode || S.isPublicAccess || S.driverModePin) return false;
  if (typeof window._stCanEdit === 'function' && !window._stCanEdit()) return false;
  if (S.isDemo === true || S.tenant === 'demo') return true;
  const uid = S.user?.uid || null;
  const ownerId = S.tenantData?.ownerId || null;
  if (uid && ownerId && uid === ownerId) return true;
  const m = S.memberAccess;
  if (!m) return false;
  if (['suspended', 'removed', 'revoked', 'inactive', 'pending'].includes(m.status)) return false;
  if (m.role === 'owner' || m.role === 'admin') return true;
  if (m.viewUploadOnly === true || m.readOnly === true) return false;
  const mods = Array.isArray(m.modules) ? m.modules : [];
  return mods.includes('hr') || mods.includes('compliance');
}

// Why the certifications list is read-only, in plain words ('' = editable).
function _certListLockNote() {
  if (_canEditCertList()) return '';
  const partnerLocked = typeof window._stCanEdit === 'function' && !window._stCanEdit();
  if (partnerLocked) {
    const td = window.AppState?.tenantData || {};
    const name = (typeof td.compliancePartner === 'string' && td.compliancePartner.trim()) ? td.compliancePartner.trim() : 'your compliance partner';
    return `Managed by ${name} — they control your certification list.`;
  }
  return 'You need HR or Compliance access to change the certification list.';
}

// The Certifications section: the existing required-trainings editor markup
// (same ids app.js drives). Read-only = data-readonly on the list + no add row.
function _certSectionHTML() {
  const canEdit = _canEditCertList();
  const lock = _certListLockNote();
  return `
    <p class="form-hint" style="margin:0 0 8px;">Certifications need a <strong>card on file</strong> — H2S, OSHA 10/30, First Aid, Fit Test. Each person's expiry comes from the card uploaded to them in HR. A skill with no card goes in Skills.</p>
    ${lock ? `<div id="scert-lock-note" style="font-size:0.8rem;color:var(--text-primary);background:#F1F5F9;border:1px solid #CBD5E1;border-radius:6px;padding:8px 10px;margin-bottom:8px;display:flex;align-items:center;gap:6px;"><span class="material-icons" style="font-size:16px;">lock</span>${escapeHtml(lock)}</div>` : ''}
    <div style="display:flex;align-items:center;gap:8px;margin-bottom:6px;">
      <span style="font-weight:600;font-size:0.9rem;color:var(--text-primary);">Your certifications</span>
      <span id="safety-trainings-count" style="font-size:0.78rem;color:var(--text-secondary);font-weight:400;"></span>
    </div>
    <div id="safety-trainings-list" data-readonly="${canEdit ? '0' : '1'}" style="display:flex;flex-direction:column;gap:8px;"></div>
    <div id="safety-trainings-quickadd" style="margin-top:8px;"></div>
    ${canEdit ? `
    <div style="border-top:1px solid var(--border-light);padding-top:12px;margin-top:12px;">
      <label for="st-new-label" style="font-weight:600;font-size:0.85rem;display:block;margin-bottom:6px;">Add a certification</label>
      <div style="display:flex;gap:8px;align-items:stretch;flex-wrap:wrap;">
        <input type="text" id="st-new-label" class="form-control" placeholder="e.g. Hot Work Permit" maxlength="64" style="flex:2;min-width:160px;">
        <button type="button" class="btn btn-primary" id="scert-add-btn" style="white-space:nowrap;min-height:44px;">Add</button>
      </div>
    </div>` : ''}`;
}

function _skTab(which) {
  const modal = document.getElementById('modal-crew-skills');
  if (!modal) return;
  modal.querySelectorAll('.sc-tab').forEach(b => {
    const on = b.dataset.tab === which;
    b.setAttribute('aria-selected', on ? 'true' : 'false');
    b.tabIndex = on ? 0 : -1;
    b.style.borderBottomColor = on ? 'var(--brand-blue)' : 'transparent';
    b.style.color = on ? 'var(--text-primary)' : 'var(--text-secondary)';
  });
  const sk = document.getElementById('sc-panel-skills');
  const ce = document.getElementById('sc-panel-certs');
  if (sk) sk.hidden = which !== 'skills';
  if (ce) ce.hidden = which !== 'certs';
  if (which === 'certs') window._stRenderList?.();
}

// opts.focus: 'skills' (default) | 'certs'. opts.loadPeople: true when opened
// outside Roster — the screen reads the crew once itself (see _skillPeopleAll).
function showSkillsAndCerts(opts = {}) {
  const S = window.AppState || {};
  if (S.driverMode || S.isPublicAccess || S.driverModePin) return;
  if (!window.CrewSkills) { window.showToast?.('Skills are still loading — try again in a moment.', 'error'); return; }
  closeCrewSkills();
  _cskOpenSkillId = null;
  // Outside Roster (opts.loadPeople, or Roster's list never loaded): read the
  // crew once; until it lands the checklist shows a loading line.
  const ownList = opts.loadPeople === true || !RosterState._peopleLoaded;
  _cskPeopleSource = ownList ? [] : null;
  _cskPeopleLoading = ownList;
  const focus = opts.focus === 'certs' ? 'certs' : 'skills';
  const tabBtn = (id, label) => `<button type="button" role="tab" class="sc-tab" id="sc-tab-${id}" data-tab="${id}" aria-controls="sc-panel-${id}" style="flex:1;min-height:44px;background:none;border:none;border-bottom:3px solid transparent;font-weight:600;font-size:0.9rem;cursor:pointer;padding:8px 6px;">${label}</button>`;
  const modal = document.createElement('div');
  modal.className = 'modal';
  modal.id = 'modal-crew-skills';
  modal.innerHTML = `
    <div class="modal-content" style="max-width:640px;">
      <div class="modal-header">
        <h3>Skills &amp; certifications</h3>
        <button type="button" class="modal-close" id="csk-close-btn" aria-label="Close"><span class="material-icons">close</span></button>
      </div>
      <div role="tablist" aria-label="Skills and certifications" style="display:flex;border-bottom:1px solid var(--border-light);padding:0 16px;">
        ${tabBtn('skills', 'Skills')}${tabBtn('certs', 'Certifications')}
      </div>
      <div class="modal-body">
        <section id="sc-panel-skills" role="tabpanel" aria-labelledby="sc-tab-skills">
          <p class="form-hint" style="margin:0 0 8px;">Skills are things your crew can do with <strong>no card to upload</strong> — hydro-vac operator, OP1, TM2. Tick who has each one here. A training with a card goes in Certifications.</p>
          <div id="csk-modal-body">${_crewSkillsBodyHTML()}</div>
        </section>
        <section id="sc-panel-certs" role="tabpanel" aria-labelledby="sc-tab-certs" hidden>${_certSectionHTML()}</section>
      </div>
    </div>`;
  document.body.appendChild(modal);
  _skTab(focus);
  if (_cskPeopleLoading) _loadSkillPeople();
  let busy = false;
  // `op` = a settings edit (csApply op) or, TASK-9911, a function running one
  // person-skill save. Either way ONE save at a time, visibly locked.
  const run = async (op, ok) => {
    if (busy) return;
    busy = true;
    // Visibly locked while one save runs, so a second click is never silently dropped.
    const body = document.getElementById('csk-modal-body');
    if (body) { body.style.pointerEvents = 'none'; body.style.opacity = '0.6'; body.setAttribute('aria-busy', 'true'); }
    try {
      const r = typeof op === 'function' ? await op() : await _persistCrewSkillsOp(op);
      if (r && ok) ok(r);
    } finally {
      busy = false;
      if (body) { body.style.pointerEvents = ''; body.style.opacity = ''; body.removeAttribute('aria-busy'); }
      _refreshCrewSkillsUI();
    }
  };
  // Delegated handlers — ids ride in escaped data-* attributes, never in a JS
  // string inside an onclick.
  modal.addEventListener('click', (ev) => {
    if (ev.target === modal || ev.target.closest('#csk-close-btn')) { closeCrewSkills(); return; }
    const tab = ev.target.closest('.sc-tab');
    if (tab) { _skTab(tab.dataset.tab); return; }
    // Certifications add — app.js's own handler (the callable re-checks access).
    if (ev.target.closest('#scert-add-btn')) { window.addSafetyTraining?.(); return; }
    if (ev.target.closest('#csk-add-btn')) {
      const input = document.getElementById('csk-new-label');
      run({ kind: 'add', label: input ? input.value : '' }, () => {
        window.showToast?.('Skill added', 'success');
        setTimeout(() => document.getElementById('csk-new-label')?.focus(), 0);
      });
      return;
    }
    const whoBtn = ev.target.closest('.csk-who');
    if (whoBtn) {
      if (busy) return;
      const id = whoBtn.closest('.csk-row')?.dataset.skillId || null;
      _cskOpenSkillId = _cskOpenSkillId === id ? null : id;
      _refreshCrewSkillsUI();
      return;
    }
    const arch = ev.target.closest('.csk-archive');
    if (arch) {
      run({ kind: 'archive', id: arch.closest('.csk-row')?.dataset.skillId, archived: arch.dataset.archived !== '1' });
      return;
    }
    const chip = ev.target.closest('.csk-chip');
    if (chip) {
      const row = chip.closest('.csk-req');
      if (!row) return;
      // ONE skill flipped, merged into the CURRENT stored list inside the
      // transaction — never a whole set rebuilt from this (maybe stale) DOM.
      run({ kind: 'toggleRequire', scope: row.dataset.scope, key: row.dataset.key, id: chip.dataset.skillId, on: chip.getAttribute('aria-pressed') !== 'true' });
    }
  });
  modal.addEventListener('change', (ev) => {
    if (ev.target.id === 'csk-enabled') { run({ kind: 'enable', enabled: ev.target.checked }); return; }
    // TASK-9911 — "Who has it" checklist: a tick or an expiry = one person save.
    const holder = ev.target.closest('.csk-holder');
    if (holder) {
      const personId = holder.dataset.personId;
      const skillId = holder.closest('.csk-holders')?.dataset.skillId;
      if (ev.target.classList.contains('csk-holder-check')) {
        const on = ev.target.checked;
        // Ticking keeps a typed date; unticking drops the skill (and its date).
        const expVal = holder.querySelector('.csk-holder-exp')?.value || '';
        run(() => _persistPersonSkill(personId, skillId, on, on ? (expVal || null) : undefined), () => window.showToast?.('Saved', 'success'));
      } else if (ev.target.classList.contains('csk-holder-exp')) {
        // A date on an unticked row ticks it; clearing a date keeps the skill.
        run(() => _persistPersonSkill(personId, skillId, true, ev.target.value || null), () => window.showToast?.('Saved', 'success'));
      }
      return;
    }
    const input = ev.target.closest('.csk-label');
    if (!input) return;
    run({ kind: 'rename', id: input.closest('.csk-row')?.dataset.skillId, label: input.value });
  });
  modal.addEventListener('keydown', (ev) => {
    if (ev.key === 'Enter' && ev.target.id === 'csk-new-label') { ev.preventDefault(); document.getElementById('csk-add-btn')?.click(); }
    else if (ev.key === 'Enter' && ev.target.id === 'st-new-label') { ev.preventDefault(); document.getElementById('scert-add-btn')?.click(); }
    else if (ev.key === 'Enter' && ev.target.classList.contains('csk-label')) { ev.preventDefault(); ev.target.blur(); }
    else if ((ev.key === 'ArrowRight' || ev.key === 'ArrowLeft') && ev.target.classList.contains('sc-tab')) {
      const next = ev.target.dataset.tab === 'skills' ? 'certs' : 'skills';
      _skTab(next);
      document.getElementById('sc-tab-' + next)?.focus();
    }
    else if (ev.key === 'Escape') closeCrewSkills();
  });
}

// Roster → Skills (kept name: the Roster button, trailguide and tests call it).
function showCrewSkills() {
  showSkillsAndCerts({ focus: 'skills' });
}

function closeCrewSkills() {
  const had = document.getElementById('modal-crew-skills');
  had?.remove();
  _cskPeopleSource = null;
  _cskPeopleLoading = false;
  // A person-form section open underneath reads the lists fresh next open; the
  // HR Safety page re-reads its counts.
  if (had) { try { window.HRModule?.refreshSafetyCertDoor?.(); } catch (_) { /* HR not on screen */ } }
}

// TASK-628 — Whether THIS carrier may self-terminate drivers. Mirrors the
// server resolver (_resolveCarrierCanTerminate): a partner-managed carrier
// (tenantData.complianceManagerUid present) whose partner set
// compliance.carrierCanTerminate === false is BLOCKED — only the partner can
// off-board. Unmanaged carriers / default (absent) → allowed. This is a UI
// mirror of the authoritative server gate; the callable re-checks.
function _carrierCanTerminate() {
  const td = window.AppState?.tenantData || {};
  const hasManager = !!(td.complianceManagerUid && String(td.complianceManagerUid).trim());
  if (!hasManager) return true;
  return td.compliance?.carrierCanTerminate === false ? false : true;
}

// TASK-9828 (4) — who may mark someone No Longer Employed (Chris 2026-10-13): the
// OWNER (tenants/{tid}.ownerId — no members doc needed) or a member holding BOTH
// `roster` and `hr`, not view/upload-only, read-only, or suspended/removed.
// Roles are never read. Twin of the server's _carrierTerminateAuthorize. The
// shared demo tenant keeps its UI (the server refuses it either way, as before).
// Pure (explicit args) so the unit test executes it.
function _rosterTerminateGate({ tenantData, uid, memberAccess, tenant, isDemo } = {}) {
  if (isDemo === true || tenant === 'demo') return true;
  const td = tenantData || {};
  if (td.ownerId && uid && td.ownerId === uid) return true;
  const m = memberAccess;
  if (!m) return false;
  if (['revoked', 'inactive', 'pending', 'suspended', 'removed'].includes(m.status)) return false;
  if (m.readOnly === true || m.viewUploadOnly === true) return false;
  const mods = Array.isArray(m.modules) ? m.modules : [];
  return mods.includes('roster') && mods.includes('hr');
}
function _rosterCanTerminate() {
  const S = window.AppState || {};
  // A partner session terminates through terminateDriverFromPartner (its own gate).
  if (typeof window.isPartnerClientSession === 'function' && window.isPartnerClientSession()) return true;
  return _rosterTerminateGate({ tenantData: S.tenantData, uid: S.user?.uid, memberAccess: S.memberAccess, tenant: S.tenant, isDemo: S.isDemo });
}
const _ROSTER_NO_TERMINATE_MSG = 'Only the account owner, or someone with Roster and HR access, can mark someone No Longer Employed.';
// TASK-9828 (6) — the No Longer Employed section shows to everyone who passes the
// terminate gate and to nobody else. It used to hang off the hidden
// rosterExtendedFields setting (only TITS had it on): no TITS-special.
function _syncTerminatedSectionVisibility() {
  const section = document.getElementById('person-terminated-section');
  if (section) section.style.display = _rosterCanTerminate() ? '' : 'none';
}

// Gate the termination toggle/fields by _carrierCanTerminate(). When the partner
// has exclusive control, disable the toggle + fields and show a notice so the
// carrier knows to route off-boarding through the partner.
function _applyCarrierTerminateGate() {
  const allowed = _carrierCanTerminate();
  const toggle = document.getElementById('person-terminated');
  const fields = document.getElementById('person-terminated-fields');
  let notice = document.getElementById('person-terminated-partner-notice');
  const section = document.getElementById('person-terminated-section');
  if (toggle) toggle.disabled = !allowed;
  if (!allowed) {
    if (toggle) toggle.checked = false;
    if (fields) fields.classList.add('hidden');
    if (section && !notice) {
      notice = document.createElement('p');
      notice.id = 'person-terminated-partner-notice';
      notice.className = 'form-hint';
      notice.style.cssText = 'color:#92400E;background:#FEF3C7;border:1px solid #FDE68A;border-radius:6px;padding:8px 10px;margin-top:4px;';
      notice.textContent = 'Your compliance partner manages driver terminations for this account. Contact them to off-board a driver.';
      section.appendChild(notice);
    }
    if (notice) notice.style.display = '';
  } else if (notice) {
    notice.style.display = 'none';
  }
  _applyRosterTerminateAccessGate();
}

// TASK-9828 (4) — module gate. Only blocks MARKING someone No Longer Employed;
// an already-terminated person's toggle is left alone (rehire is not this gate).
// Never unchecks anything. Re-run by editPerson once the toggle holds the person.
function _applyRosterTerminateAccessGate() {
  const toggle = document.getElementById('person-terminated');
  const fields = document.getElementById('person-terminated-fields');
  const section = document.getElementById('person-terminated-section');
  const allowed = _carrierCanTerminate();
  let accessNote = document.getElementById('person-terminated-access-notice');
  if (allowed && toggle && !toggle.checked && !_rosterCanTerminate()) {
    toggle.disabled = true;
    if (fields) fields.classList.add('hidden');
    if (section && !accessNote) {
      accessNote = document.createElement('p');
      accessNote.id = 'person-terminated-access-notice';
      accessNote.className = 'form-hint';
      accessNote.style.cssText = 'color:var(--text-primary);background:#F1F5F9;border:1px solid #CBD5E1;border-radius:6px;padding:8px 10px;margin-top:4px;';
      accessNote.textContent = _ROSTER_NO_TERMINATE_MSG;
      section.appendChild(accessNote);
    }
    if (accessNote) accessNote.style.display = '';
  } else if (accessNote) {
    accessNote.style.display = 'none';
  }
}

// ============================================================================
// TASK-10034 — No Longer Employed: what is still open for the person, listed
// BEFORE Save, plus the payroll reminder. Every company (no TITS-special).
// WARNINGS, never blocks: Save is untouched, and nothing changes without a tap
// (the only actions are "take them off future job days" — a button — and
// "revoke their staff login when I save" — a checkbox). Shown only inside the
// No Longer Employed section, which only renders for _rosterCanTerminate()
// (owner, or Roster+HR — modules, never roles). A section this login cannot
// read says so ("couldn't check"), never "nothing open".
// ============================================================================
// The list itself (model, HTML, payroll reminder copy, the job-edit gate) lives in
// the LAZY views/rosterNleLeftovers.js — loaded by _nleEnsureMod when the toggle goes on.
let _nleMod = null;
let _nleModLoad = null;
function _nleEnsureMod() {
  if (_nleMod) return Promise.resolve(_nleMod);
  if (!_nleModLoad) {
    _nleModLoad = __ddChunkWrap(import('./rosterNleLeftovers.js?v=10034001')).then((m) => {
      m.bind({ db, collection, doc, getDoc, getDocs, query, where, orderBy, Timestamp, escapeHtml, rosterTenantTz: _rosterTenantTz, rosterDayKey: _rosterDayKey, RosterState, cascadeOffFutureJobDays: _cascadeOffFutureJobDays, httpsCallable, functions });
      _nleMod = m;
      return m;
    }).catch((e) => { _nleModLoad = null; console.warn('[Roster] rosterNleLeftovers load failed:', e?.message || e); return null; });
  }
  return _nleModLoad;
}
// Panel state lives on RosterState._nle (set by editPerson). Everything that
// paints or reads is in the lazy module; these are the thin, always-loaded hooks.
// #person-nle-leftovers sits ABOVE the Last Day / Reason fields on purpose: a
// field that grows on blur (format hints) must not shift the revoke checkbox
// out from under a click (found in Playwright: mousedown hit it, mouseup missed).
const _NLE_FAIL_HTML = '<p class="form-hint">Could not load what is still open. Turn the switch off and on to retry. You can still save.</p>';
function _nleCheckNow() { if (_nleMod) _nleMod.load(); }
function _nleOnLastDayInput() { if (!(_nleMod && _nleMod.onLastDayInput())) _nleSync(); }
function _nleSetRevoke(on) { if (RosterState._nle) RosterState._nle.revoke = !!on; }
// Called from editPerson once the toggle holds this person, and on toggle change.
// The gate: owner or Roster + HR (_rosterCanTerminate), never a partner session.
function _nleSync() {
  const toggle = document.getElementById('person-terminated');
  const el = document.getElementById('person-nle-leftovers');
  const st = RosterState._nle;
  if (!el) return;
  const partner = typeof window.isPartnerClientSession === 'function' && window.isPartnerClientSession();
  if (!st || !st.person?.id || !toggle || !toggle.checked || partner || !_rosterCanTerminate()) {
    el.style.display = 'none';
    el.innerHTML = '';
    if (st) st.revoke = false;
    return;
  }
  if (_nleMod) { _nleMod.show(); return; }
  el.style.display = '';
  el.innerHTML = '<p class="form-hint">Checking what is still open…</p>';
  _nleEnsureMod().then((m) => {
    if (RosterState._nle !== st) return;
    if (m) _nleSync(); else el.innerHTML = _NLE_FAIL_HTML;
  });
}
function _nleRemoveFutureJobs() { return _nleMod ? _nleMod.removeFutureJobs() : undefined; }
// After Save: revoke a ticked login (module: only when NLE, never on an FCRA hold).
async function _nleRevokeAfterSave(tenant, personId, opts) {
  const st = RosterState._nle;
  if (!st || !st.revoke || st.person?.id !== personId || !_nleMod) return;
  await _nleMod.revokeAfterSave(tenant, personId, opts);
}

// ============================================================================
// TASK-633 — FCRA separation adverse-action UI (pending hold panel + actions).
// ============================================================================

// TASK-633-FF — adapt the DENORMALIZED person.pendingSeparation (set server-side
// on initiate, deleted on complete/cancel) into the legacy sepAa shape the badge
// + panel already consume ({ preNoticeAt, finalNoticeAt, disputeDeadline }).
// Presence of pendingSeparation == an in-flight hold, so finalNoticeAt is always
// absent here (a finalized/cancelled hold has the field cleared). Returns null
// when no hold. Tolerates Firestore Timestamp OR ISO/Date for deadline.
function _sepAaFromPerson(person) {
  const ps = person && person.pendingSeparation;
  if (!ps || !ps.preNoticeAt) return null;
  return {
    preNoticeAt: ps.preNoticeAt,
    finalNoticeAt: null, // an in-flight hold by definition has no final notice
    disputeDeadline: ps.deadline || null,
  };
}

// Render the pending-separation panel inside the person modal. `sepAa` is the
// adverseAction.* block from peopleBackgroundChecks/{personId} (or null). When a
// hold is in flight, hide the normal terminate toggle row (you can't re-fire a
// fresh termination while one is pending) and show Complete/Cancel.
function _renderPendingSeparationPanel(personId, sepAa) {
  const panel = document.getElementById('person-pending-separation-panel');
  const msgEl = document.getElementById('person-pending-separation-msg');
  const completeBtn = document.getElementById('btn-complete-separation');
  const overrideWrap = document.getElementById('person-separation-override-wrap');
  const overrideEl = document.getElementById('person-separation-override');
  const fcraGroup = document.getElementById('person-term-fcra-group');
  if (!panel) return;
  const pending = !!(sepAa && sepAa.preNoticeAt && !sepAa.finalNoticeAt);
  panel.style.display = pending ? '' : 'none';
  // While a hold is pending, hide the "start a new FCRA termination" trigger to
  // avoid a confusing double-start.
  if (fcraGroup) fcraGroup.style.display = pending ? 'none' : '';
  if (overrideEl) overrideEl.checked = false;
  window.__pendingSeparationPersonId = pending ? personId : null;
  if (!pending) return;

  let deadlinePassed = false;
  let when = 'the dispute window';
  try {
    const dd = sepAa.disputeDeadline?.toDate?.() || (sepAa.disputeDeadline ? new Date(sepAa.disputeDeadline) : null);
    if (dd && !isNaN(dd.getTime())) {
      deadlinePassed = dd.getTime() <= Date.now();
      when = dd.toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' });
    }
  } catch (_) {}
  if (msgEl) {
    msgEl.textContent = deadlinePassed
      ? `The Pre-Adverse Action dispute window closed on ${when}. You can now send the Final notice and finalize the termination.`
      : `Pre-Adverse Action notice sent. The termination is held until the dispute window closes (${when}). The driver stays active until then.`;
  }
  if (completeBtn) {
    completeBtn.disabled = !deadlinePassed;
    completeBtn.title = deadlinePassed ? '' : 'Available after the dispute window closes (or use the override below).';
  }
  // Only offer the attestation override before the deadline passes.
  if (overrideWrap) overrideWrap.style.display = deadlinePassed ? 'none' : 'flex';
}

// Complete a held FCRA termination — sends Final-AA then fires the §391.51
// termination. Reads the held payload server-side; we just pass the override
// attestation flag if the admin opted in before the deadline.
async function completeSeparation() {
  const personId = window.__pendingSeparationPersonId;
  if (!personId) return;
  const overrideEl = document.getElementById('person-separation-override');
  const attest = overrideEl && overrideEl.checked ? 'override_5bd_wait' : undefined;
  const tenant = window.AppState?.tenant || 'demo';
  const confirmMsg = attest
    ? 'Finalize this termination now and send the Final Adverse Action notice? You are attesting the dispute wait is satisfied or waived (this is audit-logged).'
    : 'Send the Final Adverse Action notice and finalize this termination? The driver will be terminated and their DQ file archived per §391.51.';
  const proceed = await (window.showConfirm
    ? window.showConfirm('Complete termination', confirmMsg, { confirmText: 'Complete termination', cancelText: 'Cancel', danger: true })
    : Promise.resolve(confirm(confirmMsg)));
  if (!proceed) return;
  try {
    const fn = httpsCallable(functions, 'completeSeparationAdverseAction');
    const res = await fn({ tenantId: tenant, personId, attestation: attest });
    const d = res?.data || {};
    if (d.ok) {
      window.showToast?.('Termination finalized — Final notice sent, DQ file retained per §391.51', 'success');
      closeModal('person');
      renderPeopleList();
    } else {
      const reasonMap = {
        dispute_window_not_elapsed: 'The dispute window has not closed yet. Wait for the deadline or use the override attestation.',
        pre_aa_not_sent: 'No Pre-Adverse notice on file — cannot finalize.',
        no_separation_in_flight: 'No pending separation found for this driver.',
        candidate_email_missing: 'This driver has no email on file, so the Final notice could not be sent.',
      };
      await window.showAlert?.('Could not finalize', reasonMap[d.reason] || `Could not complete termination (${d.reason || 'unknown'}).`);
    }
  } catch (err) {
    console.error('[Roster] completeSeparationAdverseAction failed:', err);
    const msg = (err?.code === 'functions/permission-denied' || /permission/i.test(err?.message || ''))
      ? (err?.message || 'Your compliance partner manages terminations for this account.')
      : `Could not complete termination: ${err?.message || 'unknown error'}`;
    await window.showAlert?.('Could not finalize', msg);
  }
}

// Cancel a held FCRA termination (driver disputed successfully / carrier
// reconsidered). The driver was never terminated — this just clears the hold.
// Reuses the existing cancelAdverseActionNotice callable (~index.js).
async function cancelSeparation() {
  const personId = window.__pendingSeparationPersonId;
  if (!personId) return;
  const tenant = window.AppState?.tenant || 'demo';
  const proceed = await (window.showConfirm
    ? window.showConfirm('Cancel separation', 'Cancel this held termination? The driver stays employed. Any Pre-Adverse notice already sent stays in the audit trail.', { confirmText: 'Cancel separation', cancelText: 'Keep hold' })
    : Promise.resolve(confirm('Cancel this held termination? The driver stays employed.')));
  if (!proceed) return;
  try {
    const fn = httpsCallable(functions, 'cancelAdverseActionNotice');
    const res = await fn({ tenantId: tenant, bgRecordId: personId });
    const d = res?.data || {};
    if (d.ok) {
      window.showToast?.('Separation canceled — the driver stays employed.', 'success');
      // TASK-4687 — cancelled on a live send-claim: a Pre-Adverse notice may
      // ALREADY have been delivered, so the driver may hold a live §615 window
      // even though the termination gates just released.
      if (d.cancelledOnClaimOnly) {
        await window.showAlert?.('Notice may already be out', 'This notice may already have been delivered. Treat the candidate as notified — keep the record and do not re-send without checking.');
      }
      closeModal('person');
      renderPeopleList();
    } else {
      await window.showAlert?.('Could not cancel', `Could not cancel the hold (${d.reason || 'unknown'}).`);
    }
  } catch (err) {
    console.error('[Roster] cancelAdverseActionNotice failed:', err);
    await window.showAlert?.('Could not cancel', `Could not cancel: ${err?.message || 'unknown error'}`);
  }
}

// TASK-9359 — lock the four identity inputs when this viewer cannot read the
// peopleIdentity vault for this person (and the person doc carries no legacy
// copy): disabled + "Restricted", so the form neither shows a misleading blank
// nor offers an edit the vault write would refuse. `person` null = add mode,
// which always unlocks (a create has nothing hidden to protect).
const _ROSTER_ID_INPUTS_9359 = [
  ['person-dq-dob', ''], ['person-dq-dl-state', 'ND'],
  ['person-dq-dl-number', 'D123456789'], ['person-dq-dl-expiry', ''],
];
function _rosterApplyIdentityLock(person) {
  const locked = !!(person && window.PersonIdentity && window.PersonIdentity.identityUnknown(person));
  _ROSTER_ID_INPUTS_9359.forEach(([elId, ph]) => {
    const el = document.getElementById(elId);
    if (!el) return;
    el.disabled = locked;
    el.placeholder = locked ? 'Restricted' : ph;
    if (locked) el.value = '';
  });
}

// TASK-9710 — the Roster card's per-driver Add/Edit Job switch. Reads and writes
// settings.driverJobEdit.blockedPersonIds (an opt-OUT list; absent = everyone
// keeps the company setting). Writable only with the Dispatch module — the same
// fence firestore.rules puts on the key (t9337DispatchKeys).
function _rosterDriverJobEditCfg() {
  const cfg = window.AppState?.settings?.driverJobEdit;
  return (cfg && typeof cfg === 'object') ? cfg : {};
}
function _rosterHydrateDriverJobEdit(personId) {
  const group = document.getElementById('person-driver-job-edit-group');
  const el = document.getElementById('person-driver-job-edit');
  const hint = document.getElementById('person-driver-job-edit-hint');
  if (!group || !el) return;
  const cfg = _rosterDriverJobEditCfg();
  const on = cfg.enabled === true;
  group.style.display = on ? '' : 'none';
  const blocked = !!personId && Array.isArray(cfg.blockedPersonIds) && cfg.blockedPersonIds.includes(personId);
  el.checked = !blocked;
  el.dataset.orig = blocked ? 'off' : 'on';
  const canWrite = window.checkModuleAccess?.('dispatch', true) === true;
  el.disabled = !canWrite;
  el.dataset.canWrite = canWrite ? '1' : '';
  if (hint) {
    hint.textContent = canWrite
      ? 'Your company lets drivers add and edit jobs. Turn this off to keep this person to just driving.'
      : 'Your company lets drivers add and edit jobs. Only someone with Dispatch access can change this for a person.';
  }
}
// After the person doc is saved. Non-fatal: the person already saved; say what did not.
async function _rosterSaveDriverJobEdit(tenant, personId, personName) {
  const group = document.getElementById('person-driver-job-edit-group');
  const el = document.getElementById('person-driver-job-edit');
  if (!el || !personId || !group || group.style.display === 'none' || el.dataset.canWrite !== '1') return;
  const before = el.dataset.orig === 'off' ? 'off' : 'on';
  const after = el.checked ? 'on' : 'off';
  if (before === after) return;
  const cfg = _rosterDriverJobEditCfg();
  const list = Array.isArray(cfg.blockedPersonIds) ? cfg.blockedPersonIds : [];
  if (after === 'off' && list.length >= 500) {
    window.showToast?.('Person saved, but no more drivers can be switched off (limit 500).', 'error');
    return;
  }
  try {
    await withWriteTimeout(updateDoc(doc(db, 'tenants', tenant), {
      'settings.driverJobEdit.blockedPersonIds': after === 'off' ? arrayUnion(personId) : arrayRemove(personId),
    }), 15000, 'driver job edit switch');
    // Re-read AFTER the await (COD Code Reviewer): the settings listener may have
    // landed a newer map meanwhile; only apply this one person's change to it.
    const cur = _rosterDriverJobEditCfg();
    const curList = Array.isArray(cur.blockedPersonIds) ? cur.blockedPersonIds : [];
    const next = after === 'off' ? Array.from(new Set(curList.concat([personId]))) : curList.filter((x) => x !== personId);
    if (window.AppState?.settings) window.AppState.settings.driverJobEdit = { ...cur, blockedPersonIds: next };
    el.dataset.orig = after;
    // Ops-governing field on a shared Save — audit old->new by name.
    window.logAudit?.('settings', 'DRIVER_JOB_EDIT_PERSON', {
      personId, personName: personName || '', driverJobEditFrom: before, driverJobEditTo: after, source: 'roster-person-form',
    });
  } catch (e) {
    console.error('[Roster] TASK-9710 driver job edit switch failed', e);
    window.showToast?.(`Person saved, but the "add and edit jobs" switch was not: ${e?.message || 'unknown error'}`, 'error');
  }
}

// TASK-10039 — the person card's "Can edit their own tickets after clock-out"
// switch. MODULE access, never a role: the owner (tenants/{tid}.ownerId) or an
// active, non-read-only member holding 'billing' — the same gate the server's
// setTicketEditGrant applies (requireModuleAccess). Hidden for everyone else.
function _rosterCanGrantTicketEdit() {
  const S = window.AppState || {};
  if (S.driverMode || S.isPublicAccess || S.driverModePin) return false;
  if (S.isDemo === true || S.tenant === 'demo') return true;
  const uid = S.user?.uid || null;
  const ownerId = S.tenantData?.ownerId || null;
  if (uid && ownerId && uid === ownerId) return true;
  const m = S.memberAccess;
  if (!m || m.readOnly === true) return false;
  if (['suspended', 'removed', 'revoked', 'inactive', 'pending'].includes(String(m.status || '').trim().toLowerCase())) return false;
  return Array.isArray(m.modules) && m.modules.includes('billing');
}
let _rosterTicketEditGen = 0;
async function _rosterHydrateTicketEdit(personId) {
  const group = document.getElementById('person-ticket-edit-group');
  const el = document.getElementById('person-ticket-edit');
  const hint = document.getElementById('person-ticket-edit-hint');
  const gen = ++_rosterTicketEditGen;
  if (!group || !el) return;
  el.checked = false;
  el.disabled = true;
  el.dataset.orig = '';
  el.dataset.personId = personId || '';
  el.onchange = () => _rosterTicketEditChanged(el);
  const tenant = window.AppState?.tenant;
  if (!personId || !tenant || !_rosterCanGrantTicketEdit()) { group.style.display = 'none'; return; }
  group.style.display = '';
  if (hint) hint.textContent = 'Loading…';
  try {
    const res = await httpsCallable(functions, 'setTicketEditGrant')({ tenantId: tenant, personId });
    if (gen !== _rosterTicketEditGen) return; // another person opened meanwhile
    const on = res?.data?.enabled === true;
    el.checked = on;
    el.dataset.orig = on ? 'on' : 'off';
    el.disabled = false;
    if (hint) hint.textContent = 'When off, this person builds their ticket at clock-out, then only the office can change it.';
  } catch (e) {
    if (gen !== _rosterTicketEditGen) return;
    console.error('[Roster] TASK-10039 ticket edit grant load failed', e);
    if (hint) hint.textContent = 'Could not load this switch — close and open the person again.';
  }
}
// Saves on its OWN, the moment it is flipped (confirmed first — it is a money
// switch): a Billing holder often cannot save the person (no Roster edit), and
// the grant is not on the people doc anyway. The server writes the audit row.
async function _rosterTicketEditChanged(el) {
  const personId = el?.dataset?.personId;
  const tenant = window.AppState?.tenant;
  if (!el || !personId || !tenant || !el.dataset.orig) return;
  const want = el.checked;
  const msg = want
    ? 'Let this person edit and delete their own tickets after clock-out, from their phone?'
    : 'Stop this person editing their own tickets after clock-out? Only the office will be able to change them.';
  const ok = window.showConfirm
    ? await window.showConfirm(want ? 'Allow ticket edits' : 'Turn off ticket edits', msg, { confirmText: want ? 'Allow' : 'Turn off', icon: 'receipt_long' })
    : confirm(msg);
  const gen = _rosterTicketEditGen;
  const same = () => gen === _rosterTicketEditGen && el.dataset.personId === personId; // the card was not reopened meanwhile
  if (!ok) { if (same()) el.checked = el.dataset.orig === 'on'; return; }
  el.disabled = true;
  try {
    const res = await httpsCallable(functions, 'setTicketEditGrant')({ tenantId: tenant, personId, enabled: want });
    const on = res?.data?.enabled === true; // what the server holds now
    if (same()) { el.checked = on; el.dataset.orig = on ? 'on' : 'off'; }
    window.showToast?.(on ? 'Ticket edits allowed for this person' : 'Ticket edits turned off for this person', 'success');
  } catch (e) {
    console.error('[Roster] TASK-10039 ticket edit grant save failed', e);
    if (same()) el.checked = el.dataset.orig === 'on';
    window.showToast?.(`Not saved: ${e?.message || 'unknown error'}`, 'error');
  } finally {
    if (same()) el.disabled = false;
  }
}

// TASK-10183 — "Regular days off" on the person (lazy chunk; read/write only through the
// getRotationDays / saveRotation callables, which decide who may see or change it).
let _rosterRotLoad = null;
function _mountPersonRotation(personId) {
  if (!_rosterRotLoad) _rosterRotLoad = __ddChunkWrap(import('./rotationDays.js?v=10183001')).catch((e) => { _rosterRotLoad = null; throw e; });
  _rosterRotLoad.then(() => {
    if (document.getElementById('person-id')?.value !== personId) return; // modal moved on
    window.DDRotations?.mountPerson('person-rotation', personId);
  }).catch((e) => console.warn('[Roster] rotations chunk failed', e?.message || e));
}

function showAddPerson() {
  window.Reminders?.unmount('person-reminders'); // TASK-1550 — nothing to attach to yet
  window.DDRotations?.unmountPerson('person-rotation'); // TASK-10183
  _applyPersonViewOnlyLock(false); // TASK-9894
  document.getElementById('modal-person-title').textContent = 'Add Person';
  document.getElementById('form-person').reset();
  _syncPersonAliasesField(null); // TASK-9682
  _rosterApplyIdentityLock(null); // TASK-9359
  _rosterHydrateDriverJobEdit(null); // TASK-9710
  _rosterHydrateTicketEdit(null); // TASK-10039 — hidden until the person exists
  // TASK-1594 — clear a lock/loaded state left by the last Edit (a 'denied'
  // reason field would otherwise stay disabled for a brand-new person).
  { const _r = document.getElementById('person-terminated-reason'); if (_r) _hydrateTerminationReason(_r, null); }
  // TASK-10034 — no "still open" list for a person who does not exist yet.
  RosterState._nle = null;
  _nleSync();
  document.getElementById('person-id').value = '';
  // Legacy checkboxes — hide always (unified to single dropdown)
  document.querySelectorAll('#person-roles input[type="checkbox"]').forEach(cb => {
    cb.checked = false;
  });

  // Clear signature status and driver code for new person
  const sigStatus = document.getElementById('person-signature-status');
  if (sigStatus) sigStatus.innerHTML = '<span class="text-muted">No signature on file</span>';
  const driverPinEl = document.getElementById('person-driver-pin');
  if (driverPinEl) driverPinEl.innerHTML = '<span class="text-muted">Save person first to generate code</span>';

  // Clear phone preview
  const phonePreview = document.getElementById('person-phone-preview');
  if (phonePreview) {
    phonePreview.textContent = '';
    phonePreview.className = 'phone-preview';
  }
  // TASK-4728 — a fresh Add Person starts with no number, so the shared-phone
  // advisory must start cleared; the input listener repaints it as they type.
  _syncPersonPhoneCollisionWarning();

  // Tier-gated fields: show advanced fields only for Tier 2+ (clockbilling)
  // TASK-932 — no tenant-literal founder arm; hasAddOnPackage() carries the comp.
  const hasPro = !!window.hasAddOnPackage?.('clockbilling');

  // Founder-only fields (kept for personal-email which is sensitive). Termination
  // section was previously founder-only too, but it's the entry point for the
  // 49 CFR 391.23(g) Safety Performance History capture every carrier needs.
  // Ungated so carrier admins (and compliance partners working with them) can
  // record termination data at the time of separation.
  const founderEls = ['person-personal-email-group'];
  founderEls.forEach(id => { const el = document.getElementById(id); if (el) el.style.display = rosterExtendedFields() ? '' : 'none'; }); // TASK-865 — settings-gated (was founder-only)
  _syncTerminatedSectionVisibility(); // TASK-9828 (6) — terminate gate, not a setting
  _applyCarrierTerminateGate(); // TASK-628 — partner-exclusive-control gate

  // Signature section — removed (compliance-sketchy, replaced by document signing in HR)
  const sigSection = document.getElementById('person-signature-section');
  if (sigSection) sigSection.style.display = 'none';

  // TASK-258 §396.19 — reset Inspector Qualification fields so a fresh
  // add doesn't inherit prior edit state.
  try {
    const iqType = document.getElementById('person-inspector-cert-type'); if (iqType) iqType.value = '';
    const iqDate = document.getElementById('person-inspector-training-date'); if (iqDate) iqDate.value = '';
    const iqNotes = document.getElementById('person-inspector-notes'); if (iqNotes) iqNotes.value = '';
    const iqFile = document.getElementById('person-inspector-doc-file'); if (iqFile) iqFile.value = '';
    const iqExisting = document.getElementById('person-inspector-doc-existing'); if (iqExisting) iqExisting.style.display = 'none';
    window.__pendingInspectorDoc = { personId: null, existingUrl: null, existingPath: null, existingName: null, remove: false };
    const iqSection = document.getElementById('person-inspector-qual-section');
    if (iqSection) iqSection.removeAttribute('open');
  } catch (_) {}

  // Hourly wage — hidden (moved to HR)
  const costRateGroup = document.getElementById('person-cost-rate-group');
  if (costRateGroup) costRateGroup.style.display = 'none';

  // Unified role UI — single dropdown for all accounts
  const rolesGroup = document.getElementById('person-roles-group');
  const crewTypeGroup = document.getElementById('crew-type-group');
  const roleSelectGroup = document.getElementById('person-role-select-group');
  const cdlGroup = document.getElementById('person-cdl-group');

  if (rolesGroup) rolesGroup.style.display = 'none';      // Legacy checkboxes — always hidden
  if (crewTypeGroup) crewTypeGroup.style.display = 'none'; // Legacy crew type — always hidden
  if (roleSelectGroup) roleSelectGroup.style.display = hasPro ? '' : 'none';  // Role dropdown — Tier 2+
  if (cdlGroup) cdlGroup.style.display = 'none';           // CDL auto-derived from crewType

  // Yard dropdown — always visible so users can create their first yard
  populateYardDropdown('person-yard', false);

  // TASK-607 — "Reports to" is now edited in the HR Compliance editor, not
  // the roster person form. No dropdown to populate here.

  // Populate and default the role dropdown
  populateCrewTypeDropdown('person-role-select');
  const roleSelect = document.getElementById('person-role-select');
  if (roleSelect) roleSelect.value = 'cdl_driver';

  // TASK-1476 — a brand-new person has no HR title yet, so there is nothing to
  // reconcile. Clear the notice explicitly or it ghosts in from the last edit.
  _syncPersonRoleConflictNotice(null);

  // TASK-1122 — fresh add defaults to "Not classified yet" (no stale edit state).
  const ptSelectAdd = document.getElementById('person-position-type');
  if (ptSelectAdd) ptSelectAdd.value = '';

  // Default SMS notifications to OFF for new people — dispatcher must explicitly opt
  // them in after collecting documented employee consent per /fleet-sms.html (TF
  // verification compliance gate).
  const smsOptin = document.getElementById('person-sms-optin');
  if (smsOptin) smsOptin.checked = false;

  // TASK-4846 — Show on Driver Card defaults ON at ADD TIME when the tenant has
  // already switched the Team Directory on. form.reset() above leaves the box
  // unchecked (the markup carries no `checked` attribute), and savePerson reads
  // it straight back — which is why every hire since the directory was enabled
  // silently landed OUTSIDE it (7 of 20 at trippin-trucking).
  //
  // 🔴 WRITE DEFAULT ONLY. The read gates stay strict `showOnDriverCard === true`
  // (roster.js filter + per-person detail, dispatch.js tile). editPerson() keeps
  // reading the stored value verbatim, so opening an existing record NEVER changes
  // their setting and no existing person becomes newly visible.
  const showOnCardEl = document.getElementById('person-show-on-driver-card');
  const showOnCardNote = document.getElementById('person-show-on-driver-card-default-note');
  const directoryOn = window.AppState?.settings?.driverContactDirectory?.enabled === true;
  if (showOnCardEl) {
    showOnCardEl.checked = directoryOn;
    // COD Adversary lane: a silently pre-checked PII-publish box lets a rushed
    // admin tab past it. Say so on screen, and drop the notice the instant they
    // touch the switch (checked or not — they've seen it either way).
    if (showOnCardNote) showOnCardNote.style.display = directoryOn ? '' : 'none';
    if (!showOnCardEl.dataset.defaultNoteListenerWired) {
      showOnCardEl.addEventListener('change', () => {
        const n = document.getElementById('person-show-on-driver-card-default-note');
        if (n) n.style.display = 'none';
      });
      showOnCardEl.dataset.defaultNoteListenerWired = '1';
    }
  }

  // Build notification tag cards (module-gated)
  buildNotifyTagCards();

  // Default JOBALERTS based on role (crew gets it on, office/management doesn't).
  // Re-evaluate when the role select changes — user can still uncheck after.
  const initialRole = roleSelect ? roleSelect.value : 'cdl_driver';
  setJobAlertsCheckbox(roleImpliesJobAlerts(initialRole));
  if (roleSelect && !roleSelect.dataset.jobAlertsListenerWired) {
    roleSelect.addEventListener('change', (e) => {
      // Only auto-toggle when adding a new person (modal title is "Add Person").
      // Don't surprise edits to existing people.
      const titleEl = document.getElementById('modal-person-title');
      if (titleEl && titleEl.textContent.trim() === 'Add Person') {
        setJobAlertsCheckbox(roleImpliesJobAlerts(e.target.value));
      }
    });
    roleSelect.dataset.jobAlertsListenerWired = '1';
  }

  // TASK-262 Slice 1B — clear Custom Reminders inputs for new person
  _populatePersonCustomReminders(null);
  _populatePersonSkills(null); // TASK-9844

  // TASK-340 Slice B — reset profile photo state on new-person modal open.
  RosterState.personPhoto = { pending: null, existing: null };
  renderPersonPhotoPreview();

  // TASK-1357 — run LAST so it wins over the per-field resets above.
  _applyDotComplianceGates(null, { isAdd: true });

  const modal = document.getElementById('modal-person');
  modal.querySelectorAll('.smart-warning').forEach(el => el.remove());
  modal.classList.remove('hidden');
  // Reset scroll position to top
  const modalBody = modal.querySelector('.modal-body');
  if (modalBody) modalBody.scrollTop = 0;
}

// TASK-9686 — titled sections in the Add/Edit Person modal. LAYOUT ONLY: these
// helpers never decide what a viewer may see — every field keeps its own gate
// (rules/callables are the authority) and section visibility is derived from
// those gates' results, never the other way round.
//
// Whitelist of section keys a caller may jump to. The key is only ever used to
// look up a fixed element id — it is never rendered into HTML.
const PERSON_SECTION_KEYS = ['contact', 'job', 'notifications', 'access', 'license', 'employment', 'reminders'];

// A section is hidden only when EVERY element below its header is already
// display:none — so it can never hide a visible field (validateRequiredFields
// skips offsetParent === null). My Reminders is left to CSS (:has(:empty))
// because window.Reminders paints it asynchronously.
function _syncPersonSectionVisibility() {
  const form = document.getElementById('form-person');
  if (!form) return;
  // COD (Domain) — today every show/hide of a section's children runs before or
  // inside _applyDotComplianceGates, but a future late un-hide (async settings,
  // a new caller) must never leave DQ fields stranded in a hidden section. So
  // any style/class change on a section's direct child re-runs this sync.
  if (!form.dataset.personSectionObserver && typeof MutationObserver === 'function') {
    form.dataset.personSectionObserver = '1';
    new MutationObserver((muts) => {
      if (muts.some((m) => m.target.parentElement?.classList?.contains('person-section'))) {
        _syncPersonSectionVisibility();
      }
    }).observe(form, { subtree: true, attributes: true, attributeFilter: ['style', 'class'] });
  }
  form.querySelectorAll('.person-section[data-person-section]').forEach((sec) => {
    const key = sec.dataset.personSection;
    if (key === 'reminders') return;
    const kids = Array.from(sec.children).filter((el) => !el.classList.contains('person-section-header'));
    const anyShown = kids.some((el) => el.style.display !== 'none' && !el.classList.contains('hidden'));
    sec.style.display = anyShown ? '' : 'none';
    const link = form.querySelector(`.person-section-jump-link[data-person-jump="${key}"]`);
    if (link) link.style.display = anyShown ? '' : 'none';
  });
}

// Scroll the modal's own scroller (.modal-form-scroll — not .modal-body, not the
// page) so the section title sits at the top. Returns false for an unknown key
// or a hidden section.
function jumpToPersonSection(key) {
  if (!PERSON_SECTION_KEYS.includes(key)) return false;
  const sec = document.getElementById(`person-section-${key}`);
  const scroller = sec?.closest('.modal-form-scroll');
  if (!sec || !scroller || sec.style.display === 'none') return false;
  const delta = sec.getBoundingClientRect().top - scroller.getBoundingClientRect().top;
  scroller.scrollTop += delta - 8;
  document.getElementById(`person-section-${key}-title`)?.focus({ preventScroll: true });
  return true;
}

function editPerson(id, opts) {
  const person = RosterState.people.find(p => p.id === id);
  if (!person) return;

  document.getElementById('modal-person-title').textContent = 'Edit Person';
  document.getElementById('person-id').value = id;
  _applyPersonViewOnlyLock(true); // TASK-9894
  // TASK-1550 — reminders on this person (window.Reminders self-gates on roster|hr)
  window.Reminders?.mount('person-reminders', { type: 'person', id, label: person.name || '' });
  _mountPersonRotation(id); // TASK-10183
  document.getElementById('person-name').value = person.name || '';
  _syncPersonAliasesField(person); // TASK-9682
  // TASK-340 Slice B — seed profile photo state.
  // TASK-9234 — seed the PATH (derived from the legacy URL when the row hasn't
  // been backfilled) so a plain save re-persists the path and never the URL.
  // `url` is carried only as the fail-soft preview source.
  RosterState.personPhoto = (() => {
    const storagePath = personPhotoStoragePath(person);
    const url = person.profilePhotoUrl || null;
    return { pending: null, existing: (storagePath || url) ? { storagePath, url } : null };
  })();
  renderPersonPhotoPreview();
  document.getElementById('person-email').value = person.email || '';
  document.getElementById('person-personal-email').value = person.personalEmail || '';
  document.getElementById('person-phone').value = person.phone || '';
  updatePhonePreview('person-phone', 'person-phone-preview');
  _syncPersonPhoneCollisionWarning();   // TASK-4728
  // Handle roles (support both legacy 'role' string and new 'roles' array)
  const personRoles = person.roles || (person.role ? [person.role] : ['driver']);
  document.querySelectorAll('#person-roles input[type="checkbox"]').forEach(cb => {
    cb.checked = personRoles.includes(cb.value);
  });

  // Unified role UI — tier-gated fields
  // TASK-932 — no tenant-literal founder arm; hasAddOnPackage() carries the comp.
  const hasProEdit = !!window.hasAddOnPackage?.('clockbilling');
  const rolesGroupEdit = document.getElementById('person-roles-group');
  const roleSelectGroupEdit = document.getElementById('person-role-select-group');
  const cdlGroupEdit = document.getElementById('person-cdl-group');

  if (rolesGroupEdit) rolesGroupEdit.style.display = 'none';      // Legacy checkboxes — always hidden
  if (roleSelectGroupEdit) roleSelectGroupEdit.style.display = hasProEdit ? '' : 'none'; // Role — Tier 2+
  if (cdlGroupEdit) cdlGroupEdit.style.display = 'none';          // CDL auto-derived from crewType
  const pinSectionEdit = document.getElementById('person-pin-section');
  if (pinSectionEdit) pinSectionEdit.style.display = hasProEdit ? '' : 'none'; // PIN — Tier 2+

  // Signature section — hidden (replaced by document signing in HR)
  const sigSectionEdit = document.getElementById('person-signature-section');
  if (sigSectionEdit) sigSectionEdit.style.display = 'none';

  // Founder-only fields (personal-email only — termination is universal,
  // see addPerson() comment).
  const founderEditEls = ['person-personal-email-group'];
  founderEditEls.forEach(elId => { const el = document.getElementById(elId); if (el) el.style.display = rosterExtendedFields() ? '' : 'none'; }); // TASK-865 — settings-gated (was founder-only)
  _syncTerminatedSectionVisibility(); // TASK-9828 (6) — terminate gate, not a setting
  _applyCarrierTerminateGate(); // TASK-628 — partner-exclusive-control gate

  // Populate role dropdown and set value (map legacy 'driver' → 'cdl_driver').
  // TASK-1466 — resolve the role BEFORE populating so an unknown/legacy slug
  // gets a temporary <option> injected; otherwise the select falls back to
  // blank and savePerson()'s required-role guard forces the user to recode the
  // person just to save an unrelated field.
  const LEGACY_ROLE_MAP = { 'driver': 'cdl_driver' };
  const rawRole = person.crewType || person.role || personRoles[0] || '';
  const resolvedRole = LEGACY_ROLE_MAP[rawRole] || rawRole;
  populateCrewTypeDropdown('person-role-select', resolvedRole);
  const roleSelect = document.getElementById('person-role-select');
  if (roleSelect) roleSelect.value = resolvedRole;

  // TASK-1476 — surface an HR-title / crew-type disagreement right where the
  // crew type is edited. Display only; re-evaluates as the dropdown changes so
  // the user can see the notice clear. NEVER writes hrPosition.
  _syncPersonRoleConflictNotice(person, resolvedRole);
  if (roleSelect && !roleSelect.dataset.roleConflictListenerWired) {
    roleSelect.addEventListener('change', (e) => {
      const pid = document.getElementById('person-id')?.value;
      const cur = pid ? RosterState.people.find(pp => pp.id === pid) : null;
      _syncPersonRoleConflictNotice(cur, e.target.value);
    });
    roleSelect.dataset.roleConflictListenerWired = '1';
  }

  // TASK-1122 — show the current EXPLICIT compliance classification (blank =
  // never classified; savePerson never writes blank, so leaving it untouched
  // can't clobber an existing value).
  const ptSelectEdit = document.getElementById('person-position-type');
  if (ptSelectEdit) ptSelectEdit.value = _normalizePositionTypePick(person.positionType);

  // Yard dropdown
  populateYardDropdown('person-yard', false);
  document.getElementById('person-yard').value = person.yardId || '';

  // TASK-607 — "Reports to" is now edited in the HR Compliance editor, not
  // the roster person form. The value still lives on person.reportsTo and is
  // preserved across saves here (savePerson no longer touches it).

  // Build notification tag cards (module-gated) then load saved values
  buildNotifyTagCards();
  const notifyTags = person.notifications?.tags || person.notifyTags || [];
  document.querySelectorAll('#person-notify-tags input[type="checkbox"]').forEach(cb => {
    cb.checked = notifyTags.includes(cb.value);
  });
  syncAllCheckbox();
  updateNotifySummary();
  // Preferred channel
  const channel = person.notifications?.preferredChannel || person.preferredChannel || 'both';
  const channelRadio = document.querySelector(`input[name="preferred-channel"][value="${channel}"]`);
  if (channelRadio) channelRadio.checked = true;
  // SMS opt-in
  const smsOptin = document.getElementById('person-sms-optin');
  if (smsOptin) smsOptin.checked = !!(person.notifications?.sms || person.smsOptIn);
  // TASK-835: surface a carrier-STOP opt-out (smsOptOutReason stamped by the
  // 21610 STOP capture — distinct from SMS simply being toggled off). Serious:
  // texting them is a TCPA violation and the toggle can't legally re-enable them.
  const smsStopWarn = document.getElementById('person-sms-stop-warning');
  if (smsStopWarn) {
    if (person.smsOptOutReason) {
      smsStopWarn.style.display = 'block';
      const detail = document.getElementById('person-sms-stop-detail');
      if (detail) {
        const when = person.smsOptOutAt?.toDate ? person.smsOptOutAt.toDate()
          : (person.smsOptOutAt ? new Date(person.smsOptOutAt.seconds ? person.smsOptOutAt.seconds * 1000 : person.smsOptOutAt) : null);
        detail.textContent = when && !isNaN(when) ? ` (on ${when.toLocaleDateString()})` : '';
      }
    } else {
      smsStopWarn.style.display = 'none';
    }
  }
  // TASK-9893 — has this person ever texted the company's number? Until they
  // have, an iPhone shows links in our texts as plain text (Apple's rule).
  // smsRepliedAt is server-stamped by twilioIncomingSms; read-only here.
  const smsReplyStatus = document.getElementById('person-sms-reply-status');
  if (smsReplyStatus) {
    const replied = _smsRepliedDate(person);
    if (person.smsOptOutReason || !_rosterHasTextablePhone(person)) {
      smsReplyStatus.style.display = 'none';
    } else if (replied) {
      smsReplyStatus.style.display = 'block';
      smsReplyStatus.textContent = `Has texted your number (${replied.toLocaleDateString()}), so links in your texts open on their phone.`;
    } else {
      smsReplyStatus.style.display = 'block';
      smsReplyStatus.innerHTML = '<span class="material-icons" style="font-size:14px;vertical-align:-2px;" aria-hidden="true">link_off</span> Hasn\'t texted your number yet. On an iPhone, links in your texts won\'t open until they reply <strong>START</strong> (or save the number). Their next text with a link asks them to.';
    }
  }
  // Muted status
  const mutedEl = document.getElementById('person-muted');
  if (mutedEl) mutedEl.checked = !!(person.notifications?.muted || person.notifyMuted);
  // Active status (default to true if not set)
  const activeEl = document.getElementById('person-active');
  if (activeEl) activeEl.checked = person.active !== false;
  // Show all jobs (default to true if not set)
  const showAllJobsEl = document.getElementById('person-show-all-jobs');
  if (showAllJobsEl) showAllJobsEl.checked = person.showAllJobs !== false;
  // Show on Driver Card — team directory opt-in (TASK-459, default OFF)
  // TASK-4846 — the EDIT path reads the stored value verbatim, always. The
  // add-time default must never leak in here or it would flip existing people on
  // silently just for opening their record.
  const showOnDriverCardEl = document.getElementById('person-show-on-driver-card');
  if (showOnDriverCardEl) showOnDriverCardEl.checked = person.showOnDriverCard === true;
  const showOnCardNoteEdit = document.getElementById('person-show-on-driver-card-default-note');
  if (showOnCardNoteEdit) showOnCardNoteEdit.style.display = 'none';
  // Allow maintenance access (default false for new people)
  const allowMaintenanceEl = document.getElementById('person-allow-maintenance');
  if (allowMaintenanceEl) allowMaintenanceEl.checked = !!person.allowMaintenance;
  _rosterHydrateDriverJobEdit(id); // TASK-9710
  _rosterHydrateTicketEdit(id); // TASK-10039
  // TASK-258 §396.19 Inspector Qualifications — load existing values if any
  try {
    const iq = person.inspectorQualification || {};
    const typeEl = document.getElementById('person-inspector-cert-type');
    const dateEl = document.getElementById('person-inspector-training-date');
    const notesEl = document.getElementById('person-inspector-notes');
    if (typeEl) typeEl.value = iq.type || '';
    if (dateEl) dateEl.value = iq.trainingDate || '';
    if (notesEl) notesEl.value = iq.notes || '';
    // Stash the existing doc on a window-scoped pending object so saveTime
    // can decide whether to preserve, replace, or remove.
    window.__pendingInspectorDoc = {
      personId: person.id,
      existingUrl: iq.docUrl || null,
      existingPath: iq.docPath || null,
      existingName: iq.docName || null,
      remove: false,
    };
    const existingEl = document.getElementById('person-inspector-doc-existing');
    const linkEl = document.getElementById('person-inspector-doc-link');
    const nameEl = document.getElementById('person-inspector-doc-name');
    if (existingEl && linkEl && (iq.docUrl || iq.docPath)) {
      // TASK-9855 — open through the Storage SDK by path (rules-checked), never
      // the stored token URL.
      linkEl.href = '#';
      linkEl.removeAttribute('target');
      linkEl.onclick = (ev) => { ev.preventDefault(); window.StorageFile.open(iq, { urlKey: 'docUrl' }); };
      if (nameEl) nameEl.textContent = iq.docName || 'View on file';
      existingEl.style.display = '';
    } else if (existingEl) {
      existingEl.style.display = 'none';
    }
    const fileInput = document.getElementById('person-inspector-doc-file');
    if (fileInput) fileInput.value = '';
    // Auto-expand if anything is set so the admin sees it on edit
    const iqSection = document.getElementById('person-inspector-qual-section');
    if (iqSection && (iq.type || iq.trainingDate || iq.notes || iq.docUrl || iq.docPath)) {
      iqSection.setAttribute('open', '');
    }
  } catch (_) {}

  // TASK-812 follow-on — Driver Qualification (DL & CDL). Prefill the shared
  // person form's DQ inputs from the SAME canonical keys saveDriverDetails /
  // saveComplianceFields read, so editing from Roster shows DL/MVR data captured
  // in Compliance/HR (and savePerson won't blank it). Auto-expand if any set.
  try {
    const _setVal = (elId, v) => { const el = document.getElementById(elId); if (el) el.value = v || ''; };
    _setVal('person-dq-dob', person.dob);
    _setVal('person-dq-dl-state', person.dlState);
    _setVal('person-dq-dl-number', person.dlNumber);
    _setVal('person-dq-dl-expiry', person.dlExpiryDate);
    _setVal('person-dq-cdl-class', person.cdlClass && person.cdlClass !== 'none' ? person.cdlClass : '');
    _setVal('person-dq-cdl-endorsements', person.cdlEndorsements);
    _setVal('person-dq-cdl-restrictions', _cdlRestrText(person.cdlRestrictions)); // TASK-9300 (5): string or legacy array
    _setVal('person-dq-cdl-issue', person.cdlIssueDate);
    _setVal('person-dq-cdl-expiry', person.cdlExpiryDate);
    _rosterApplyIdentityLock(person); // TASK-9359
    const dqSection = document.getElementById('person-dq-section');
    if (dqSection && (person.dob || person.dlNumber || person.dlState || person.dlExpiryDate ||
        (person.cdlClass && person.cdlClass !== 'none') || person.cdlEndorsements ||
        _cdlRestrText(person.cdlRestrictions) ||
        person.cdlIssueDate || person.cdlExpiryDate)) {
      dqSection.setAttribute('open', '');
    }
  } catch (_) {}

  // Terminated status
  const terminatedEl = document.getElementById('person-terminated');
  const terminatedFields = document.getElementById('person-terminated-fields');
  if (terminatedEl) {
    terminatedEl.checked = !!person.terminated;
    if (terminatedFields) terminatedFields.classList.toggle('hidden', !person.terminated);
    terminatedEl.addEventListener('change', () => {
      if (terminatedFields) terminatedFields.classList.toggle('hidden', !terminatedEl.checked);
    });
    // TASK-9828 (4) — re-run the module gate now that the toggle holds THIS
    // person's state (it leaves an already-terminated person's toggle alone).
    if (_carrierCanTerminate()) { terminatedEl.disabled = false; _applyRosterTerminateAccessGate(); }
  }
  const terminatedDateEl = document.getElementById('person-terminated-date');
  if (terminatedDateEl) {
    // TASK-9662 (B) — same bounds savePerson + the server enforce: year >= 2000 and
    // not before the hire date, at most today+90. Picker-side only; typed values are
    // re-checked on save.
    const _b = _t9662LastDayBounds(person.hireDate);
    terminatedDateEl.min = _b.min;
    terminatedDateEl.max = _b.max;
    // TASK-628 — "Last Day" prefills from lastDayWorked (the §391.51 retention
    // anchor) when present, falling back to the legacy terminatedAt date.
    if (person.lastDayWorked && /^\d{4}-\d{2}-\d{2}$/.test(person.lastDayWorked)) {
      terminatedDateEl.value = person.lastDayWorked;
    } else if (person.terminatedAt) {
      const d = person.terminatedAt?.toDate?.() || new Date(person.terminatedAt);
      if (d && !isNaN(d.getTime())) terminatedDateEl.value = d.toISOString().split('T')[0];
    }
  }
  const terminatedReasonEl = document.getElementById('person-terminated-reason');
  if (terminatedReasonEl) _hydrateTerminationReason(terminatedReasonEl, person);

  // TASK-10034 — what is still open + the payroll reminder. Property handlers
  // (not addEventListener) so re-opening the modal never stacks them.
  RosterState._nle = { person: { ...person }, model: null, loading: false, token: 0, revoke: false, jobsBusy: false };
  if (terminatedEl) terminatedEl.onchange = () => _nleSync();
  if (terminatedDateEl) terminatedDateEl.oninput = () => _nleOnLastDayInput();
  _nleSync();

  // TASK-633 — FCRA termination trigger checkbox. Always reset to UNCHECKED on
  // open (default = today's immediate one-click termination). Wire it to reveal
  // the "where is the report" field only when there's no BG report on file (the
  // no-report guard). Use onchange (not addEventListener) so re-opening the
  // modal doesn't stack listeners.
  const fcraEl = document.getElementById('person-term-fcra');
  const fcraReportGroup = document.getElementById('person-term-fcra-report-group');
  const fcraReportEl = document.getElementById('person-term-fcra-report');
  const sepAa = _sepAaFromPerson(person); // TASK-633-FF — denormalized person.pendingSeparation
  if (fcraEl) {
    fcraEl.checked = false;
    if (fcraReportEl) fcraReportEl.value = '';
    if (fcraReportGroup) fcraReportGroup.style.display = 'none';
    // Does this person have a Checkr/BG report on file? We don't have the bg
    // doc's report ids in RosterState.people, so probe lazily on first check.
    fcraEl.onchange = async () => {
      if (!fcraEl.checked) {
        if (fcraReportGroup) fcraReportGroup.style.display = 'none';
        return;
      }
      // Show the report field only if no on-file report is detected.
      let hasReport = false;
      try {
        const tenant = window.AppState?.tenant || 'demo';
        const bgSnap = await getDoc(doc(db, 'tenants', tenant, 'peopleBackgroundChecks', person.id));
        if (bgSnap.exists()) {
          const bgd = bgSnap.data() || {};
          // TASK-9235 — a hire-time reference names the application's reports;
          // initiateSeparationAdverseAction attaches them from the linked record.
          hasReport = !!(bgd.latestReportId || bgd.checkrReportId
            || (Array.isArray(bgd.inheritedReportIds) && bgd.inheritedReportIds.length));
        }
      } catch (_) { /* probe failed → treat as no report, require the ref */ }
      if (fcraReportGroup) fcraReportGroup.style.display = hasReport ? 'none' : '';
    };
  }

  // TASK-633 — Pending-separation panel. When an FCRA termination hold is in
  // flight for this person, show the Complete/Cancel controls + countdown.
  _renderPendingSeparationPanel(person.id, sepAa);

  // 49 CFR 391.23(d) Safety Performance History — populate when editing a
  // previously-terminated driver who has answers on file.
  // TASK-9677 — never from an EARLIER employment stint (captured before the current hire
  // date, or undated with one on file): prefilled, it would be re-saved as this
  // separation's answers. Same rule as _t9677MergeStoredSph / the server core.
  let sph = person.safetyPerformanceHistory || {};
  {
    const _h = _t9662YmdOf(person.hireDate);
    const _c = _t9662YmdOf(sph.capturedAt);
    if (_h && _c && _c < _h) sph = {}; // undated (pre-TASK-1208) records are kept
  }
  const setVal = (id, v) => { const el = document.getElementById(id); if (el && v != null) el.value = v; };
  setVal('person-sph-voluntary', sph.voluntary);
  setVal('person-sph-rehire', sph.eligibleForRehire);
  setVal('person-sph-accidents', sph.hadAccidents);
  setVal('person-sph-accidents-detail', sph.accidentsDetail);
  setVal('person-sph-drug-alcohol', sph.hadDrugAlcoholViolation);
  setVal('person-sph-drug-alcohol-detail', sph.drugAlcoholDetail);
  setVal('person-sph-rtd-completed', sph.returnToDutyCompleted);
  setVal('person-sph-performance', sph.performanceSummary);
  // Show conditional textareas if their parent select is "yes"
  const accDetail = document.getElementById('person-sph-accidents-detail');
  if (accDetail) accDetail.style.display = sph.hadAccidents === 'yes' ? '' : 'none';
  const daDetail = document.getElementById('person-sph-drug-alcohol-detail');
  if (daDetail) daDetail.style.display = sph.hadDrugAlcoholViolation === 'yes' ? '' : 'none';
  // Signature status
  const sigStatus = document.getElementById('person-signature-status');
  if (sigStatus) {
    sigStatus.innerHTML = person.signatureUrl
      ? `<img src="${escapeHtml(person.signatureUrl)}" alt="Signature" style="max-height:60px;border:1px solid var(--border-light);border-radius:var(--radius-sm);margin-bottom:8px;display:block;">`
      : '<span class="text-muted">No signature on file</span>';
  }
  // Signature button always enabled — modal has copyable link even without phone
  const sigBtn = document.getElementById('btn-request-signature');
  if (sigBtn) {
    sigBtn.disabled = false;
    sigBtn.title = '';
  }

  // PIN status
  const pinStatus = document.getElementById('person-pin-status');
  if (pinStatus) {
    if (person.pinSetAt) {
      const setDate = person.pinSetAt?.toDate?.() || new Date(person.pinSetAt);
      pinStatus.innerHTML = `<span class="text-success"><span class="material-icons" style="font-size:16px;vertical-align:middle;">check_circle</span> PIN set on ${setDate.toLocaleDateString()}</span>`;
    } else {
      pinStatus.innerHTML = '<span class="text-muted">No PIN set</span>';
    }
  }
  // PIN button — always enabled, modal handles SMS availability
  const pinBtn = document.getElementById('btn-request-pin');
  if (pinBtn) {
    pinBtn.disabled = false;
    pinBtn.title = '';
  }

  // Driver Code (stored on person doc as driverPin)
  const driverPinEl = document.getElementById('person-driver-pin');
  if (driverPinEl) {
    renderDriverPinSection(driverPinEl, person);
  }

  // Staff Login (TASK-319 slice 5) — only for saved people
  _renderStaffLoginSection(person);

  // Extended fields (personal email) — TASK-865: settings-gated (was founder-only).
  // TASK-9828 (6) — the terminated section left this list: it follows the
  // terminate gate (owner, or active member with roster + hr) for every tenant.
  const founderEls = ['person-personal-email-group'];
  founderEls.forEach(elId => { const el = document.getElementById(elId); if (el) el.style.display = rosterExtendedFields() ? '' : 'none'; });
  _syncTerminatedSectionVisibility();

  // Hourly wage is HR-only — always hide from Roster
  const costRateGroupEdit = document.getElementById('person-cost-rate-group');
  if (costRateGroupEdit) costRateGroupEdit.style.display = 'none';

  // Crew type dropdown — always hidden, Role dropdown handles crewType
  const crewTypeGroupEdit = document.getElementById('crew-type-group');
  if (crewTypeGroupEdit) crewTypeGroupEdit.style.display = 'none';

  // TASK-262 Slice 1B — Custom Reminders date inputs (per-tenant compliance)
  _populatePersonCustomReminders(person);
  _populatePersonSkills(person); // TASK-9844

  // TASK-1357 — run LAST, after every prefill above, so the gate sees the real
  // stored values and can honour "never hide populated data".
  _applyDotComplianceGates(person, { isAdd: false });

  // HOTFIX 2026-11-04 — reused modal: drop any Smart Assist banner computed from the
  // previous record's values (J-1361's notes were offered as Auto-fix on J-1355).
  window.SmartAssistant?.clearAll?.(document.getElementById('modal-person'));
  document.getElementById('modal-person').classList.remove('hidden');

  // TASK-9686 slice 2 — editPerson(id, { section: 'notifications' }) opens
  // straight to a section (help copy: "Roster → Edit Person → Notifications").
  // Whitelisted inside jumpToPersonSection; anything else is ignored.
  const wanted = opts && typeof opts === 'object' ? opts.section : null;
  if (typeof wanted === 'string' && PERSON_SECTION_KEYS.includes(wanted)) {
    requestAnimationFrame(() => jumpToPersonSection(wanted));
  }
}

// TASK-1122 — canonicalize a positionType-ish value ('non-cdl-driver' →
// 'non_cdl_driver'); returns '' when not a recognized tier. Prefers the shared
// DqMatrix normalizer (loaded on window via hr.js's dqMatrix import — never
// import it here, pitfall #1) with an inline fallback so roster still works
// if that module ever isn't mounted.
function _normalizePositionTypePick(v) {
  if (window.DqMatrix?.normalizePositionType) return window.DqMatrix.normalizePositionType(v) || '';
  const s = String(v || '').trim().toLowerCase().replace(/-/g, '_');
  return ['cdl', 'non_cdl_driver', 'drives_no_cmv', 'non_driver'].includes(s) ? s : ''; // TASK-9696
}

// ============================================================================
// TASK-1357 — DOT/FMCSA field gating for the shared Add/Edit Person modal.
//
// The modal is mounted ONCE to <body> and reused by Roster AND the Compliance
// module (compliance.js -> openAddPerson), so the gate is evaluated at OPEN
// time, never at mount time.
//
// TWO INDEPENDENT AXES — they are not the same question:
//   1. TENANT — is this tenant a DOT-regulated motor carrier at all?
//      DispatchDeck is no longer trucking-only (GC/trades tenants are live), so
//      "every tenant keeps a DQ file" is false. Signal = tenants/{tid}.isDotCarrier.
//   2. PERSON — which DQ items apply to THIS person? That question already has a
//      canonical answer: window.DqMatrix.getApplicableDqItems().items (TASK-934).
//      We CONSUME it. We do not invent a second matrix and we never read
//      p.cdlLicensed — Part 382 / Clearinghouse items are CDL/CMV-only, which is
//      exactly what the matrix already encodes.
//
// 🔴 FAIL OPEN, ALWAYS. A missing isDotCarrier means DOT = true. Every live
// tenant today is a motor carrier; defaulting a missing flag to false would
// silently hide DQ capture from real carriers — compliance data loss, not a
// cosmetic regression. Inferring "non-DOT" from a blank tenantData.dotNumber is
// the same trap wearing a different hat (a real carrier who simply has not
// filled in Settings yet), so we deliberately do NOT derive from it.
//
// 🔴 NEVER HIDE POPULATED DATA. Every gate below is additionally conditioned on
// the section being EMPTY for the person being edited. A field that already
// holds a value stays visible and editable on every tenant and every
// classification — hiding it would strand the value: invisible in the UI and
// impossible to correct. savePerson (below) carries the matching guard so a
// hidden field can never be written back as null.
//
// window.* delegation only (pitfall #1 — never cross-import a ?v= module).
// ============================================================================

// Tenant axis. Explicit false is the ONLY non-DOT signal; everything else
// (absent, true, garbage) resolves to DOT.
function _tenantIsDotCarrier() {
  const td = window.AppState?.tenantData || {};
  if (td.isDotCarrier === false || td.isDotCarrier === 'false') return false;
  return true;
}

// Live form reads — "has the user typed something into this section RIGHT NOW?"
// The stored-doc predicates below only see the snapshot captured at modal open,
// which is enough for EDIT but blind on ADD: type a DL number on a fresh add,
// then flip the classification to Non-Driver, and a stored-doc-only check would
// hide the section and savePerson would drop the keys — the typed value gone
// with no message. These make the "never hide populated data" rule hold for
// in-session input too.
// TASK-9300 (5) — CDL restrictions arrive as a STRING from the carrier forms and as an
// ARRAY from older partner-side writes. Normalise for hydration + the "has data"
// predicates; [] / '' / null all read as "none". Twin of compliance.js _cdlRestrText.
function _cdlRestrText(v) {
  if (Array.isArray(v)) return v.filter(Boolean).join(', ');
  return v == null ? '' : String(v).trim();
}
function _dqInputsHaveValue() {
  return ['person-dq-dob', 'person-dq-dl-state', 'person-dq-dl-number', 'person-dq-dl-expiry',
    'person-dq-cdl-class', 'person-dq-cdl-endorsements', 'person-dq-cdl-restrictions', 'person-dq-cdl-issue', 'person-dq-cdl-expiry']
    .some((elId) => (document.getElementById(elId)?.value || '').trim());
}
function _cdlInputsHaveValue() {
  return ['person-dq-cdl-class', 'person-dq-cdl-endorsements', 'person-dq-cdl-restrictions',
    'person-dq-cdl-issue', 'person-dq-cdl-expiry']
    .some((elId) => (document.getElementById(elId)?.value || '').trim());
}
function _iqInputsHaveValue() {
  return ['person-inspector-cert-type', 'person-inspector-training-date', 'person-inspector-notes']
    .some((elId) => (document.getElementById(elId)?.value || '').trim());
}

// "Does this person already carry data in the section?" predicates. Keys match
// the canonical people/{id} fields savePerson / saveDriverDetails /
// saveComplianceFields all read — no shadow keys.
function _dqSectionHasData(person) {
  const p = person || {};
  return !!(p.dob || p.dlState || p.dlNumber || p.dlExpiryDate
    || (p.cdlClass && p.cdlClass !== 'none') || p.cdlEndorsements || _cdlRestrText(p.cdlRestrictions)
    || p.cdlIssueDate || p.cdlExpiryDate);
}
function _cdlFieldsHaveData(person) {
  const p = person || {};
  return !!((p.cdlClass && p.cdlClass !== 'none') || p.cdlEndorsements || _cdlRestrText(p.cdlRestrictions)
    || p.cdlIssueDate || p.cdlExpiryDate);
}
function _iqSectionHasData(person) {
  const iq = (person || {}).inspectorQualification || {};
  return !!(iq.type || iq.trainingDate || iq.notes || iq.docUrl || iq.docPath);
}

// Applies both axes to the open modal and records what it did on
// RosterState._dotGate (true = visible). savePerson reads that record rather
// than probing the DOM — a field inside a collapsed <details> reports
// offsetParent === null even when it is perfectly visible, so DOM probing would
// mis-detect "hidden" and is not usable as a data-safety signal.
//   opts.isAdd — true only from showAddPerson.
function _applyDotComplianceGates(person, opts) {
  opts = opts || {};
  const p = person || {};
  const dotCarrier = _tenantIsDotCarrier();

  const ptGroup = document.getElementById('person-position-type-group');
  const ptSelect = document.getElementById('person-position-type');
  const iqSection = document.getElementById('person-inspector-qual-section');
  const dqSection = document.getElementById('person-dq-section');

  // ── Tenant axis ──
  // Classification: a non-DOT tenant does not keep a DQ file, so the tier picker
  // is noise — UNLESS this person already carries an explicit classification, in
  // which case it stays visible so it remains correctable.
  const explicitPt = _normalizePositionTypePick(p.positionType);
  // A stored 'non_driver' does NOT bring the picker back on a non-DOT tenant:
  // non_driver IS that tenant's normal state, so honouring it here would
  // re-expose the control across the whole roster the moment anyone classified
  // anybody. A stored DRIVER tier is different — that is an anomaly on a tenant
  // claiming it runs no CMVs, and the admin needs to see and correct it.
  const showClassification = dotCarrier
    || explicitPt === 'cdl' || explicitPt === 'non_cdl_driver';

  // §396.19 Inspector Qualifications is a motor-carrier vehicle-inspection rule
  // (Part 396) — out of scope for a non-DOT tenant. It is deliberately NOT gated
  // on the person's classification: a shop mechanic is correctly classified
  // Non-Driver and is exactly who holds an annual/brake inspector credential.
  const showIq = dotCarrier || _iqSectionHasData(p) || _iqInputsHaveValue();

  // ── Person axis (only meaningful inside a DOT tenant) ──
  // The tier picked in the form RIGHT NOW wins over the stored value so the
  // section reacts live as the admin changes it.
  const picked = _normalizePositionTypePick(ptSelect?.value);
  let items = { cdlValid: true, dlExpiry: true };
  try {
    if (window.DqMatrix?.getApplicableDqItems) {
      items = window.DqMatrix.getApplicableDqItems(picked || p).items || items;
    }
  } catch (_) { /* matrix unavailable -> conservative defaults above (show all) */ }

  // DL/CDL capture is in scope when the TENANT is a DOT carrier, or when a human
  // has deliberately classified THIS person into a driver tier anyway. The second
  // clause matters: without it a non-DOT tenant that classified someone "CDL
  // Driver" would get a picker saying CDL Driver with nowhere to enter the CDL —
  // a dead end. Within scope, WHICH inputs show is the DqMatrix's call, never a
  // cdlLicensed boolean.
  const tierInPlay = picked || explicitPt;
  const dqInScope = dotCarrier || tierInPlay === 'cdl' || tierInPlay === 'non_cdl_driver' || tierInPlay === 'drives_no_cmv'; // TASK-9696 — licence still captured
  // ...and never to Non-Driver (items.dlExpiry === false).
  const showDq = (dqInScope && items.dlExpiry !== false)
    || _dqSectionHasData(p) || _dqInputsHaveValue();
  // The four CDL-only inputs inside it (§391.11 / Part 382 territory).
  const showCdlFields = showDq
    && ((dqInScope && items.cdlValid !== false)
      || _cdlFieldsHaveData(p) || _cdlInputsHaveValue());

  const _toggle = (el, show) => { if (el) el.style.display = show ? '' : 'none'; };
  _toggle(ptGroup, showClassification);
  _toggle(iqSection, showIq);
  _toggle(dqSection, showDq);
  ['person-dq-cdl-class', 'person-dq-cdl-endorsements', 'person-dq-cdl-restrictions',
    'person-dq-cdl-issue', 'person-dq-cdl-expiry'].forEach((elId) => {
    _toggle(document.getElementById(elId)?.closest('.form-group'), showCdlFields);
  });

  // ⚠️ DO NOT re-add an auto-stamp here. An earlier cut of this gate set the
  // hidden select to 'non_driver' on a non-DOT ADD so the person would not fall
  // through to DqMatrix's unclassified -> full-CDL-checklist default. It was
  // wrong twice over, and both failures are the kind that only show up on the
  // SECOND interaction:
  //   1. Self-defeating. The stamp writes an explicit positionType, and
  //      showClassification is (dotCarrier || !!explicitPt) — so on the very
  //      next edit of that person the dropdown this gate exists to hide came
  //      back, permanently, for exactly the people it had stamped.
  //   2. It fired a permission-gated callable behind an invisible control.
  //      savePerson routes the pick through setDriverClassification, whose
  //      permission-denied path toasts "changing the compliance classification
  //      needs HR or Compliance access". A roster-only dispatcher on a plumbing
  //      tenant would get a DOT compliance error naming a control they never
  //      saw — the TASK-428 shape (the denial lands on someone with no way to
  //      act on it) — plus an audit row per new person on a tenant that by
  //      definition keeps no DQ file.
  // The confirm this was working around is handled in savePerson instead, by
  // skipping it when the classification control was gated off. The residual
  // "unclassified people resolve to the full CDL checklist" cost is real but it
  // belongs to the HR badge surface, not here.

  RosterState._dotGate = {
    dotCarrier,
    classification: showClassification,
    iq: showIq,
    dq: showDq,
    cdlFields: showCdlFields,
  };
  RosterState._dotGatePerson = p;

  // TASK-9686 — a section whose every field the gates above just hid would show
  // a bare title; drop it (and its jump link). Runs here because this gate runs
  // LAST on both open paths and again on every classification change.
  _syncPersonSectionVisibility();

  // Re-run when the tier changes so the CDL inputs appear/disappear live.
  if (ptSelect && !ptSelect.dataset.dotGateWired) {
    ptSelect.addEventListener('change', () => {
      _applyDotComplianceGates(RosterState._dotGatePerson, { isAdd: false });
    });
    ptSelect.dataset.dotGateWired = '1';
  }
}

// TASK-1122 — stamp/refresh a person's compliance classification through the
// sanctioned server path. positionType is SERVER-OWNED (TASK-934: firestore.rules
// denies it on every client people create/update), so it can never ride the
// person addDoc/updateDoc — the setDriverClassification callable validates,
// stamps provenance, writes the audit row, and resolves the TASK-1087
// classify_person prompt. Non-fatal by design: the person save has already
// succeeded; on failure the classify prompt stays open as the safety net.
// Skips demo (anon demo users aren't members → callable would always deny),
// blank picks (never clobber an existing classification with blank), and
// unchanged values (no audit noise from an untouched form).
async function _applyPositionTypePick(tenant, personId, picked, currentRaw, personName) {
  if (!picked || tenant === 'demo') return;
  if (picked === _normalizePositionTypePick(currentRaw)) return;
  try {
    // TASK-9806 — "Drives, no CMV" asks the placarded-hazmat / passenger checks
    // first; a yes switches to the tier it requires. Cancel keeps the old tier.
    const pick = window.DqMatrix?.confirmClassificationPick
      ? await window.DqMatrix.confirmClassificationPick(picked, { name: personName })
      : { positionType: picked, cmvChecks: null };
    if (!pick) { window.showToast?.('Saved. The compliance classification was not changed', 'info'); return; }
    if (pick.positionType === _normalizePositionTypePick(currentRaw)) return;
    const setPt = httpsCallable(functions, 'setDriverClassification');
    await setPt({ tenantId: tenant, personId, positionType: pick.positionType, cmvChecks: pick.cmvChecks, reason: 'Set from roster person form' });
  } catch (err) {
    console.warn('[Roster] classification save failed (non-blocking):', err);
    const denied = err?.code === 'functions/permission-denied' || /permission/i.test(err?.message || '');
    window.showToast?.(denied
      ? 'Saved, but changing the compliance classification needs HR or Compliance access'
      : 'Saved, but the compliance classification could not be updated', 'warning');
  }
}

// TASK-9662 (B) — last-day-worked plausibility. A typed "0026-10-12" (Ivan, 10/01) was
// accepted because only the YYYY-MM-DD shape was checked, and it set retentionExpiresAt
// to 0029 — a file that reads as past §391.51 retention. Mirrors the server's
// _t9662LastDayError (index.js): year >= 2000, not before hire, at most today+90.
// Empty = no error (the server defaults to today).
function _t9662YmdOf(v) {
  if (!v) return '';
  if (typeof v === 'string') {
    if (/^\d{4}-\d{2}-\d{2}/.test(v)) return v.slice(0, 10);
    // TASK-9677 (4) — a legacy US-style hire date ("1/1/2026", Justin Funk) is a real
    // date; read it as one so the hire-date floor still applies instead of falling
    // back to 2000-01-01. Anything else stays unreadable ('').
    const us = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec(v.trim());
    if (us) {
      const y = +us[3], mo = +us[1], d = +us[2];
      const dt = new Date(Date.UTC(y, mo - 1, d));
      if (y >= 1000 && dt.getUTCFullYear() === y && dt.getUTCMonth() === mo - 1 && dt.getUTCDate() === d) {
        return `${us[3]}-${String(mo).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
      }
    }
    return '';
  }
  const d = (v && typeof v.toDate === 'function') ? v.toDate() : (v instanceof Date ? v : null);
  return (d && !isNaN(d.getTime())) ? d.toISOString().slice(0, 10) : '';
}
function _t9662LastDayBounds(hireDate, now) {
  const t = now instanceof Date ? now : new Date();
  const max = new Date(t.getTime() + 90 * 86400000).toISOString().slice(0, 10);
  const hire = _t9662YmdOf(hireDate);
  return { min: (hire && hire >= '2000-01-01' && hire <= max) ? hire : '2000-01-01', max };
}
function _t9662LastDayError(ymd, hireDate, now) {
  const v = String(ymd || '').trim();
  if (!v) return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(v);
  if (!m) return 'Enter the last day worked as a full date.';
  const dt = new Date(Date.UTC(+m[1], +m[2] - 1, +m[3]));
  // Year first: Date.UTC maps years 0-99 to 1900-1999, so 0026 would read as "not a real date".
  if (+m[1] < 2000) return `The year on ${v} is ${m[1]} — check it (did you mean ${'20' + m[1].slice(2)}?).`;
  if (dt.getUTCFullYear() !== +m[1] || dt.getUTCMonth() !== +m[2] - 1 || dt.getUTCDate() !== +m[3]) return `${v} is not a real date.`;
  const b = _t9662LastDayBounds(null, now);
  if (v > b.max) return `${v} is more than 90 days from today. Enter the actual last day worked.`;
  const hire = _t9662YmdOf(hireDate);
  if (hire && hire >= '2000-01-01' && v < hire) return `${v} is before the hire date (${hire}).`;
  return null;
}
// TASK-9677 (1) — the explicit allowlist for a compliance PARTNER's roster-modal save.
// 🔴 MIRRORS the partner people-update arm in firestore.rules (the isComplianceManager
// hasOnly([...]) list) — tests/task9677-crown-followups.unit.cjs parses the rules and
// fails if this set names a key the rules would refuse. It may be NARROWER than the
// rules, never wider. Termination fields (terminated, terminatedAt, terminationReason,
// terminationDate, lastDayWorked, safetyPerformanceHistory, separation*, retention*)
// are deliberately absent: a partner terminates only through terminateDriverFromPartner.
const _T9677_PARTNER_PEOPLE_KEYS = new Set([
  'cdlClass', 'cdlEndorsements', 'cdlRestrictions', 'cdlExpiryDate', 'cdlIssueDate',
  'medCardExpiryDate', 'medExamLastDate', 'medExaminerName', 'medExaminerNRCME',
  'medRestrictions', 'medCertPdfUrl', 'medExamNextDueDate',
  'medCardDocumentUrl', 'medCardDocumentPath', 'medCardDocumentUploadedAt',
  'mvrLastPulled', 'mvrLastPullDate', 'mvrLastState', 'mvrLastResult',
  'mvrViolationCount', 'mvrLatestViolations', 'mvrLatestPdfUrl', 'mvrNextDueDate',
  'clearinghouseConsentDate',
  'dqFileScore', 'dqFileLastAudit', 'dqFileMissingItems',
  'dqApplicationOnFile', 'dqRoadTestPassed', 'roadTestDate',
  'customReminders',
  'mvrPullHistory', 'mvrLastPurpose',
  'medExamType', 'medExamHistory', 'medCertPdfPath', 'medCertPdfUploadedAt',
  'medCardSelfUploadedAt', 'medCardPendingReview',
  'dlDocumentUrl', 'dlDocumentPath', 'dlDocumentBackUrl', 'dlDocumentBackPath',
  'daCategory', 'daCategoryUpdatedAt',
  'medCardNumber', 'inRandomPool',
]);
// Identity kit keys (TASK-9359). These never land on the people doc: PersonIdentity.updatePerson
// splits them off and, in a partner session, writes them through the audited
// updateDriverCompliance callable (or drops them with its own notice when the vault value
// can't be known). Pass them through so that existing routing still decides (COD 9677).
const _T9677_PARTNER_IDENTITY_KEYS = new Set(['dob', 'dateOfBirth', 'dlNumber', 'dlState', 'dlExpiryDate', 'cdlNumber']);
// Bookkeeping the form stamps on every save. Not a partner decision, so it is dropped
// silently rather than reported as "not saved".
const _T9677_PARTNER_SILENT_KEYS = new Set(['updatedAt', 'updatedBy']);
const _T9677_FIELD_LABELS = {
  name: 'name', email: 'email', personalEmail: 'personal email', phone: 'phone',
  roles: 'role', role: 'role', crewType: 'role', cdlLicensed: 'CDL holder',
  yardId: 'yard', yardName: 'yard',
  notifications: 'notification settings', notifyTags: 'notification settings',
  preferredChannel: 'notification settings', smsOptIn: 'notification settings', notifyMuted: 'notification settings',
  active: 'active status', showInDispatch: 'active status',
  showAllJobs: 'driver app settings', allowMaintenance: 'driver app settings', showOnDriverCard: 'driver app settings',
  hireDate: 'hire date', position: 'position',
  terminated: 'termination status', terminatedAt: 'termination status', terminationReason: 'termination reason',
  terminationDate: 'termination status', safetyPerformanceHistory: 'safety performance history',
  profilePhotoStoragePath: 'photo', profilePhotoUrl: 'photo',
  inspectorQualification: 'inspector qualification', customReminderExpirations: 'reminders',
};
function _t9677FieldLabels(keys) {
  const out = [];
  for (const k of keys || []) {
    const l = _T9677_FIELD_LABELS[k] || String(k).replace(/([a-z])([A-Z])/g, '$1 $2').toLowerCase();
    if (!out.includes(l)) out.push(l);
  }
  return out;
}
// Order-insensitive "same value" for the partner split. partnerDropUnchangedKeys
// compares objects with JSON.stringify, so a nested map whose keys come back from
// Firestore in a different order (notifications) or a Timestamp vs Date reads as a
// change — and would be reported to the partner as an edit they never made.
function _t9677Norm(v) {
  if (v === undefined || v === '' || v === null) return null;
  if (v && typeof v.toDate === 'function') { try { return { $t: v.toDate().getTime() }; } catch (_) { return null; } }
  if (v instanceof Date) return { $t: v.getTime() };
  if (Array.isArray(v)) return v.length ? v.map(_t9677Norm) : null;
  if (typeof v === 'object') {
    const o = {};
    for (const k of Object.keys(v).sort()) { const n = _t9677Norm(v[k]); if (n !== null) o[k] = n; }
    return Object.keys(o).length ? o : null;
  }
  return v;
}
function _t9677Same(a, b) { return JSON.stringify(_t9677Norm(a)) === JSON.stringify(_t9677Norm(b)); }
// For a key the partner may NOT write, it is never written either way — the only
// question is whether to tell them. A legacy doc missing a toggle the form renders at
// its default (absent active = on, absent smsOptIn = off) is not an edit they made.
const _T9677_DEFAULT_TRUE = new Set(['active', 'showInDispatch', 'showAllJobs']);
function _t9677ReportableChange(k, prevVal, nextVal) {
  let p = prevVal;
  if ((p === undefined || p === null) && typeof nextVal === 'boolean') p = _T9677_DEFAULT_TRUE.has(k);
  return !_t9677Same(p, nextVal);
}
// Split a partner's update into { allowed, refused, refusedLabels }. Unchanged keys and
// silent bookkeeping keys land in neither list.
function _t9677PartnerSplit(update, before) {
  const prev = before || {};
  const allowed = {};
  const refused = [];
  for (const k of Object.keys(update || {})) {
    if (_t9677Same(prev[k], update[k])) continue;
    if (_T9677_PARTNER_PEOPLE_KEYS.has(k) || _T9677_PARTNER_IDENTITY_KEYS.has(k)) allowed[k] = update[k];
    else if (_T9677_PARTNER_SILENT_KEYS.has(k)) continue;
    else if (_t9677ReportableChange(k, prev[k], update[k])) refused.push(k);
  }
  return { allowed, refused, refusedLabels: _t9677FieldLabels(refused) };
}
function _t9677RefusedMessage(labels, terminating) {
  const list = (labels || []).slice(0, 8);
  const what = list.length
    ? `These changes were not saved: ${list.join(', ')}${(labels || []).length > list.length ? ', and others' : ''}. As the compliance partner you can update compliance fields here; ask the carrier to change ${list.length === 1 ? 'that' : 'those'}.`
    : 'Some changes were not saved. As the compliance partner you can update compliance fields here; ask the carrier to make the other changes.';
  return what + (terminating ? ' The termination still goes ahead.' : '');
}

// TASK-9677 (2) — fill every blank answer in `sph` (the in-flight form record) from the
// stored record. Mutates and returns `sph`. capturedAt/capturedBy stay the new stamp.
const _T9677_SPH_ANSWER_KEYS = ['voluntary', 'eligibleForRehire', 'hadAccidents', 'accidentsDetail',
  'hadDrugAlcoholViolation', 'drugAlcoholDetail', 'returnToDutyCompleted', 'performanceSummary'];
// COD r5 (Domain): only answers captured during THIS employment stint count. A rehired
// driver's stored record from the earlier separation (capturedAt before the current
// hireDate) must be asked again, never silently carried into §391.23 answers. An undated
// (pre-TASK-1208) record is kept — its stint can't be told (COD final r2, Domain).
function _t9677MergeStoredSph(sph, stored, hireDate) {
  if (!sph || !stored || typeof stored !== 'object') return sph;
  const cap = _t9662YmdOf(stored.capturedAt);
  const hire = _t9662YmdOf(hireDate);
  if (hire && cap && cap < hire) return sph; // undated (pre-TASK-1208) records are kept
  for (const k of _T9677_SPH_ANSWER_KEYS) {
    const cur = sph[k];
    const old = stored[k];
    if ((cur == null || String(cur).trim() === '') && old != null && String(old).trim() !== '') sph[k] = old;
  }
  return sph;
}

function _t9662IsPermissionDenied(err) {
  const c = String((err && err.code) || '');
  return c === 'permission-denied' || c === 'functions/permission-denied' || /permission/i.test(String((err && err.message) || ''));
}

// TASK-1594 — the termination reason lives ONLY in tenants/{t}/peoplePii/{id}
// (read: admin | compliance manager | hr-directory), never on the broadcast
// people doc every driver-PIN session can list. The edit form hydrates it here
// and remembers what it loaded, so savePerson can tell a real edit from "this
// viewer was never allowed to see it" (state 'denied' → field locked, nothing
// sent). A not-yet-migrated person still shows the legacy parent value.
function _hydrateTerminationReason(el, person) {
  el.value = '';
  el.disabled = false;
  el.placeholder = 'Resigned, Terminated, etc.';
  el.removeAttribute('title');
  el.dataset.sepState = 'none';
  el.dataset.sepOriginal = '';
  el.dataset.sepPersonId = person?.id || '';
  const tenant = window.AppState?.tenant || 'demo';
  if (!person?.id || !(person.terminated === true || person.terminationDate)) return;
  el.dataset.sepState = 'loading';
  el.disabled = true; // COD: nothing typed before the read lands may be overwritten by it
  getDoc(doc(db, 'tenants', tenant, 'peoplePii', person.id)).then((snap) => {
    if (el.dataset.sepPersonId !== person.id) return; // modal re-opened on someone else
    const sib = snap.exists() ? (snap.data() || {}) : {};
    const v = typeof sib.terminationReason === 'string' ? sib.terminationReason : (person.terminationReason || '');
    el.value = v;
    el.dataset.sepOriginal = v;
    el.dataset.sepState = 'loaded';
    el.disabled = false;
  }).catch((err) => {
    if (el.dataset.sepPersonId !== person.id) return;
    el.dataset.sepState = 'denied';
    el.disabled = true;
    el.placeholder = 'Only admins and HR Directory users can see or edit the reason';
    el.title = el.placeholder;
    if (err?.code !== 'permission-denied') console.warn('[Roster] termination reason load failed:', err?.message || err);
  });
}

async function savePerson(e) {
  e.preventDefault();
  // TASK-9894 — an edit (not an add) by a view & upload only login is refused by the rules.
  if (document.getElementById('person-id')?.value && _rosterRefuseViewUploadOnly()) return;
  if (!window.validateRequiredFields(e.target)) return;

  // TASK-9378 COD — a photo still converting settles OUT HERE, not inside the
  // guard (its 20s hand-off would re-open Save mid-conversion → duplicate doc).
  if (!(await _settlePhotoBeforeSave(RosterState.personPhoto, 'savePerson', 'btn-save-person'))) return;
  return runGuardedSave('savePerson', async () => {
  const id = document.getElementById('person-id').value;
  // Unified role system — single dropdown + CDL checkbox for all accounts
  const roleSelect = document.getElementById('person-role-select');
  const roleVal = roleSelect?.value || '';
  if (!roleVal) {
    window.showToast?.('Please select a role', 'error');
    return;
  }
  const roles = [roleVal];
  const crewTypeVal = roleVal;
  // TASK-670 — explicit-value-wins. cdlLicensed governs the compliance portal's
  // CDL-only alerts (MVR/Clearinghouse/Annual Review). The roster form has no
  // cdlLicensed control, so a blind role-derive on every edit would silently
  // clobber whatever the compliance-portal CDL toggle set. Only derive from role
  // when no value is stored yet (undefined); preserve any existing boolean.
  const _existingPersonForCdl = id ? (RosterState.people.find(p => p.id === id) || {}) : {};
  const cdlLicensed = _existingPersonForCdl.cdlLicensed !== undefined
    ? _existingPersonForCdl.cdlLicensed
    : /cdl|driver/i.test(crewTypeVal);

  // TASK-1122 — compliance classification pick. Deliberately NOT written into
  // `data` (positionType is server-owned — the whole updateDoc/addDoc would be
  // rules-denied if it carried the field); applied post-save via the
  // setDriverClassification callable in _applyPositionTypePick.
  const pickedPositionType = _normalizePositionTypePick(document.getElementById('person-position-type')?.value);

  // TASK-1391 — classification is required when CREATING a person so the
  // unclassified backlog stops growing (an unclassified person defaults to the
  // full CDL checklist on the DQ file and an amber "Needs DOT Role" badge in
  // HR). EDIT deliberately still allows blank — legacy records must not get
  // force-guessed into a tier just to save an unrelated field.
  // Integrated-gate Domain catch: a HARD block contradicts the selectable
  // "Not classified yet" option and pressures a wrong tier (§382.305 drug-pool
  // mis-tiering is WORSE than unclassified). Confirm instead — explicit
  // choice, no forced misclassification; the amber badge + tile keep pressure.
  // TASK-1357 — ...but never prompt for a control the user could not see. On a
  // declared non-DOT tenant the classification select is gated off, so a blank
  // pick is the gate's doing, not an oversight the user can act on. Prompting
  // would strand them in a dialog naming a field that is not on their screen.
  if (!id && !pickedPositionType && RosterState._dotGate?.classification !== false) {
    // 🔴 LIVE HANG, fixed 2026-08-17. This wrapped showConfirm in a second
    // Promise and passed `() => res(true)` / `() => res(false)` as arguments 3
    // and 4 — but the real signature is showConfirm(title, message, options).
    // Argument 3 was read as an OPTIONS OBJECT (its function-ness ignored, only
    // `options.confirmText` etc. looked at), argument 4 was dropped entirely,
    // and showConfirm's OWN returned promise — the thing that actually resolves
    // true/false — was discarded. So neither callback ever fired: clicking
    // either button closed the dialog while `res` stayed unresolved, and
    // `await proceed` suspended savePerson FOREVER.
    //
    // The symptom was silent and total: the Save button sat on "Saving…"
    // disabled, nothing was written to Firestore, no error, no toast. It hit
    // EVERY new-person save that left the Compliance Classification dropdown at
    // its default — which is every save unless the user actively changes it —
    // so "adding a person just spins" was live for 11 days (introduced
    // 2026-08-06 by a9101d9d, which softened TASK-1391's synchronous hard block
    // into this async confirm). Editing an existing person was unaffected,
    // because this branch is gated on `!id`.
    //
    // Every other call site in the app already does `const ok = await
    // window.showConfirm(...)`. This one now matches.
    const proceed = window.showConfirm
      ? await window.showConfirm(
        'Save without a Compliance Classification?',
        'They’ll show as "Needs DOT Role" in HR until classified — classification sets which DOT items apply.',
        { confirmText: 'Save anyway', cancelText: 'Pick a classification' },
      )
      : true;
    if (!proceed) {
      const _ptGroup = document.getElementById('person-position-type-group');
      _ptGroup?.scrollIntoView({ block: 'center' });
      document.getElementById('person-position-type')?.focus();
      return;
    }
  }

  const _clamp = window.clampText || ((s, n) => s && s.length > n ? s.slice(0, n) : s || '');
  const data = {
    name: _clamp(capPersonName(document.getElementById('person-name').value.trim()), 100),
    email: document.getElementById('person-email').value.trim(),
    personalEmail: document.getElementById('person-personal-email').value.trim(),
    phone: document.getElementById('person-phone').value.trim(),
    roles: roles,
    role: roles[0], // Keep legacy field for backward compatibility
    crewType: crewTypeVal,
    cdlLicensed: cdlLicensed,
    updatedAt: new Date()
  };

  // Yard
  const personYardEl = document.getElementById('person-yard');
  if (personYardEl) {
    data.yardId = personYardEl.value || '';
    data.yardName = data.yardId ? getYardLabel(data.yardId) : '';
  }

  // TASK-9682 — crew nicknames ("Also goes by"). Only when the field was on
  // screen (Roster module or the owner) and only when the list CHANGED, so an
  // untouched save never carries the key (the rules gate it to those logins).
  let _aliasAudit = null;
  if (_rosterCanEditAliases() && document.getElementById('person-aliases')) {
    const _prevAliases = _cleanPersonAliases(id ? (RosterState.people.find(p => p.id === id) || {}).aliases : [], data.name);
    const _aliasRaw = document.getElementById('person-aliases').value;
    const _nextAliases = _cleanPersonAliases(_aliasRaw, data.name);
    // COD r2 (UX): say so when an entry was left out for looking like a phone number or email.
    if (String(_aliasRaw).split(/[,;\n]+/).some((x) => x.includes('@') || /\d(?:\D*\d){3}/.test(x))) {
      window.showToast?.('Left out of "Also goes by": an entry that looks like a phone number or email. Those go in the contact fields.', 'warning');
    }
    if (JSON.stringify(_prevAliases) !== JSON.stringify(_nextAliases)) {
      data.aliases = _nextAliases;
      _aliasAudit = { aliasesChanged: true, aliasesFrom: _prevAliases.join(', '), aliasesTo: _nextAliases.join(', ') };
    }
  }

  // TASK-607 — "Reports to" (direct supervisor) is now edited in the HR
  // Compliance editor (saveComplianceFields). savePerson no longer reads or
  // writes person.reportsTo; since this is a partial updateDoc(...data), the
  // stored value is preserved untouched when saving from the roster form.

  // TASK-258 §396.19 Inspector Qualifications — persist when set, clear when
  // cleared. Doc upload happens AFTER the save returns the personId (for new
  // people) so we can use it as the storage path component.
  try {
    // TASK-1357 — the gate record for this modal open (true = the control was
    // visible to the user). See _applyDotComplianceGates.
    const _gate = RosterState._dotGate || {};
    const iqTypeEl = document.getElementById('person-inspector-cert-type');
    const iqDateEl = document.getElementById('person-inspector-training-date');
    const iqNotesEl = document.getElementById('person-inspector-notes');
    const iqType = (iqTypeEl?.value || '').trim();
    const iqDate = (iqDateEl?.value || '').trim();
    const iqNotes = (iqNotesEl?.value || '').trim().slice(0, 200);
    const pending = window.__pendingInspectorDoc || {};
    const hasMeta = iqType || iqDate || iqNotes;
    // TASK-10086-D — a path-only doc counts as "has a doc" (keying on the URL
    // explicit-cleared a path-only doc on a save with no type/date/notes).
    // TASK-10086-D (audit 🟡12) — never re-write a Storage token URL. A legacy
    // URL-only doc is carried forward as the path the URL encodes (_p); only a
    // non-Storage link (no token) is kept as a URL. (No https check here: it cost
    // the cold-load budget, and the stored value predates this writer.)
    const _u = pending.existingUrl || null, _p = _u && window.StorageFile?.pathFromUrl(_u);
    const hasExistingDoc = (pending.existingPath || _u) && !pending.remove;
    if (hasMeta || hasExistingDoc) {
      data.inspectorQualification = {
        type: iqType || null,
        trainingDate: iqDate || null,
        notes: iqNotes || null,
        // Preserve existing doc unless user explicitly removed it. New
        // uploads land via a post-save Storage upload + Firestore merge.
        docUrl: pending.remove || _p ? null : _u,
        docPath: pending.remove ? null : (pending.existingPath || _p?.path || null),
        docName: pending.remove ? null : (pending.existingName || null),
      };
    // TASK-1357 — when the §396.19 section was gated OFF the user never saw it,
    // so its inputs read blank and this "explicit clear" would wipe a stored
    // inspectorQualification on the first unrelated edit. Omitting the key lets
    // the partial updateDoc preserve it. (The gate only ever hides an EMPTY
    // section, so this is belt-and-braces against a concurrent edit.)
    } else if (_gate.iq !== false) {
      data.inspectorQualification = null; // explicit clear
    }
  } catch (_) {}

  // TASK-812 follow-on — Driver Qualification (DL & CDL). SAME canonical keys as
  // Compliance saveDriverDetails / HR saveComplianceFields (one people/{id} doc,
  // three views — no drift, no shadow keys). The DQ section is prefilled on edit,
  // so it's a real editor: on an EDIT (existing id) we mirror the HR/Compliance
  // null-on-empty convention so a deliberately-cleared field actually clears.
  // On ADD (no id, fresh form) we OMIT blank keys instead of stamping nulls, so
  // a new person isn't seeded with explicit null DL fields. Either way a blank
  // never silently overwrites Compliance-entered DL/MVR data with junk.
  try {
    const _isEdit = !!id;
    const _dqVal = (elId) => {
      const el = document.getElementById(elId);
      return el ? (el.value || '').trim() : '';
    };
    const _dq = {
      dob: _dqVal('person-dq-dob'),
      dlState: _dqVal('person-dq-dl-state').toUpperCase(),
      dlNumber: _dqVal('person-dq-dl-number'),
      dlExpiryDate: _dqVal('person-dq-dl-expiry'),
      cdlClass: _dqVal('person-dq-cdl-class'),
      cdlEndorsements: _dqVal('person-dq-cdl-endorsements'),
      cdlRestrictions: _dqVal('person-dq-cdl-restrictions'), // TASK-9300 (5)
      cdlIssueDate: _dqVal('person-dq-cdl-issue'),
      cdlExpiryDate: _dqVal('person-dq-cdl-expiry'),
    };
    // TASK-1357 — a field the DOT gate hid is NOT "the user cleared it". The
    // null-on-edit convention is only correct for a control the user could
    // actually see. Hidden -> omit the key entirely; the partial updateDoc then
    // preserves whatever Compliance/HR stored.
    const _gate = RosterState._dotGate || {};
    const _cdlOnlyKeys = new Set(['cdlClass', 'cdlEndorsements', 'cdlRestrictions', 'cdlIssueDate', 'cdlExpiryDate']);
    const _gatedOff = (k) => _gate.dq === false || (_gate.cdlFields === false && _cdlOnlyKeys.has(k));
    // TASK-9359 (2nd COD) — a LOCKED identity input (vault not readable / still
    // loading) is omitted, never saved as a clear.
    const _idLockedEl = { dob: 'person-dq-dob', dlState: 'person-dq-dl-state', dlNumber: 'person-dq-dl-number', dlExpiryDate: 'person-dq-dl-expiry' };
    Object.keys(_dq).forEach(k => {
      if (_gatedOff(k)) return; // hidden control — leave the stored value alone
      if (_idLockedEl[k] && document.getElementById(_idLockedEl[k])?.disabled) return; // TASK-9359
      if (_dq[k]) data[k] = _dq[k];
      else if (_isEdit) data[k] = null; // deliberate clear on edit (matches HR/Compliance)
    });
  } catch (_) {}

  // Add notification prefs (nested structure for Cloud Functions compatibility)
  const tagCheckboxes = document.querySelectorAll('#person-notify-tags input[type="checkbox"]:checked');
  const selectedChannel = document.querySelector('input[name="preferred-channel"]:checked');
  const smsOptin = document.getElementById('person-sms-optin');
  const mutedEl = document.getElementById('person-muted');

  data.notifications = {
    tags: Array.from(tagCheckboxes).map(cb => cb.value).filter(v => v !== 'ALL'),
    preferredChannel: selectedChannel ? selectedChannel.value : 'both',
    sms: smsOptin ? smsOptin.checked : false,
    muted: mutedEl ? mutedEl.checked : false
  };

  // Also keep flat fields for backward compatibility
  data.notifyTags = data.notifications.tags;
  data.preferredChannel = data.notifications.preferredChannel;
  data.smsOptIn = data.notifications.sms;
  data.notifyMuted = data.notifications.muted;

  // TASK-779 (TCPA) — NEVER let a routine person-save re-enable SMS for someone
  // who legally opted out via carrier STOP. TASK-699 stamps `smsOptIn:false`
  // (+ smsOptOutAt / smsOptOutReason:"carrier_stop_21610") server-side when a
  // contact texts STOP. That is an AUTHORITATIVE legal opt-out — a distinct
  // concept from this form's generic "SMS Notifications" preference toggle. The
  // toggle defaults the checkbox from person.smsOptIn (so it renders OFF for an
  // opted-out person), but an admin can still flip it ON not realizing it's a
  // legal STOP — and `data.smsOptIn = data.notifications.sms` above would then
  // clobber the opt-out back to true, re-texting someone who said STOP (TCPA
  // violation). Guard it: if the stored person is in a carrier-STOP opt-out
  // state, force-preserve smsOptIn:false + its provenance regardless of the
  // toggle. Only the recipient texting START (server-side) or a dedicated admin
  // "clear opt-out" action may lift it — never this generic save. People who are
  // NOT opted out keep normal toggle behavior (the guard is a no-op for them).
  if (id) {
    const existingForOptOut = RosterState.people.find(p => p.id === id);
    // TASK-875 — only force-preserve for an ACTUAL carrier-STOP opt-out, identified
    // by its provenance (smsOptOutReason / smsOptOutAt, stamped server-side on STOP).
    // The old condition fired on ANY `smsOptIn === false`, which also traps a driver
    // who simply was NEVER opted in (default-off, no STOP) — making it impossible to
    // turn SMS on for them via the toggle (Malachi King, 2026-06-28). This now matches
    // the firestore.rules lock, which also keys on smsOptOutReason — the two layers
    // agreed on the policy but the client guard was over-broad.
    const hasRealOptOut = existingForOptOut &&
      (existingForOptOut.smsOptOutReason || existingForOptOut.smsOptOutAt);
    if (existingForOptOut && existingForOptOut.smsOptIn === false && hasRealOptOut) {
      data.smsOptIn = false;
      data.notifications.sms = false;
      // Preserve the legal-opt-out provenance so a save can't silently strip it.
      if (existingForOptOut.smsOptOutAt !== undefined) data.smsOptOutAt = existingForOptOut.smsOptOutAt;
      if (existingForOptOut.smsOptOutReason !== undefined) data.smsOptOutReason = existingForOptOut.smsOptOutReason;
      if (data.notifications.sms !== smsOptin?.checked) {
        console.warn(`[Roster] TASK-779: preserved carrier-STOP SMS opt-out for ${data.name || id} — generic save cannot re-enable SMS for an opted-out contact.`);
      }
    }
  }
  const activeEl = document.getElementById('person-active');
  data.active = activeEl ? activeEl.checked : true;
  // showInDispatch directly mirrors active — one toggle controls both
  data.showInDispatch = data.active;
  // Show all jobs toggle (driver PIN visibility)
  const showAllJobsEl = document.getElementById('person-show-all-jobs');
  data.showAllJobs = showAllJobsEl ? showAllJobsEl.checked : true;
  // Allow maintenance access via driver PIN
  const allowMaintenanceEl = document.getElementById('person-allow-maintenance');
  data.allowMaintenance = allowMaintenanceEl ? allowMaintenanceEl.checked : false;
  // Show on Driver Card — team contact directory opt-in (TASK-459, default OFF)
  const showOnDriverCardEl = document.getElementById('person-show-on-driver-card');
  data.showOnDriverCard = showOnDriverCardEl ? showOnDriverCardEl.checked : false;

  // Pay type and hourly wage managed by HR module only — not saved from Roster

  // TASK-9828 (2) — snapshot the form's notification state before the auto-mute
  // below, so a deactivation routed to the Deactivate confirm can save the rest of
  // the form without muting (the confirm does the mute).
  const _preMute9828 = {
    nMuted: data.notifications?.muted, nTags: data.notifications?.tags,
    notifyMuted: data.notifyMuted, notifyTags: data.notifyTags,
    hadNotifyMuted: 'notifyMuted' in data, hadNotifyTags: 'notifyTags' in data,
  };
  // Auto-mute and hide from dispatch when marking someone inactive
  if (data.active === false) {
    data.notifications.muted = true;
    data.notifications.tags = [];
    data.notifyMuted = true;
    data.notifyTags = [];
    data.showInDispatch = false;
  }

  // TASK-262 Slice 1B — Custom Reminders expirations.
  // Merge strategy (NOT overwrite) — only the keys for currently-active tenant
  // reminders are read from the modal inputs; archived-key history on the
  // person doc is preserved. Blank inputs DELETE that key (so admins can
  // "unset" a reminder for one driver) but don't touch other keys.
  // Stable selector: `input[data-custom-reminder-id]` inside the section
  // container. Section is hidden + empty when tenant has no active reminders,
  // so the querySelectorAll returns 0 and `customReminderExpirations` is
  // left untouched on the data payload.
  const crInputs = document.querySelectorAll('#person-custom-reminders-fields input[data-custom-reminder-id]');
  if (crInputs.length > 0) {
    const existingPerson = id ? (RosterState.people.find(p => p.id === id) || {}) : {};
    const merged = { ...(existingPerson.customReminderExpirations || {}) };
    crInputs.forEach(el => {
      const itemId = el.getAttribute('data-custom-reminder-id');
      if (!itemId) return;
      const v = (el.value || '').trim();
      // HTML5 date input enforces YYYY-MM-DD when typed via picker; manual
      // entry can still produce bad strings on older Safari etc. Belt-and-
      // suspenders validation prevents NaN-poisoned badges downstream.
      const isValidYmd = /^\d{4}-\d{2}-\d{2}$/.test(v);
      if (v && isValidYmd) {
        merged[itemId] = v;
      } else {
        // Blank or invalid → remove that key (per-driver opt-out path).
        delete merged[itemId];
      }
    });
    data.customReminderExpirations = merged;
  }

  // TASK-9844 — crew skills. Only when the Skills section was on screen; a
  // hidden section (feature off) omits the key so the stored skills survive.
  // TASK-9911 (COD r2) — only the rows the user CHANGED. Editing an existing
  // person (staff session): never put `skills` in the form write; the changes
  // are applied after it in a transaction on the person's CURRENT skills, so a
  // "Who has it" tick made meanwhile is never erased. A new person (no doc yet)
  // or a partner session (its own allowlisted write) keeps the in-form value.
  const _skillChanges = _collectPersonSkillChanges();
  let _pendingSkillChanges = null;
  if (_skillChanges) {
    const _partnerSkillSess = typeof window.isPartnerClientSession === 'function' && window.isPartnerClientSession();
    if (id && !_partnerSkillSess) _pendingSkillChanges = _skillChanges;
    else data.skills = _applySkillChanges(id ? (RosterState.people.find(p => p.id === id) || {}) : {}, _skillChanges);
  }

  // Terminated (no longer employed) — separate from active.
  // TASK-628: the §391.51 termination ACTION (stamp terminated + retention clock
  // + freeze the DQ-file snapshot + emit the DRIVER_TERMINATED compliance event)
  // is performed SERVER-SIDE by the `terminateDriver` callable, NOT written here.
  // So on a not-terminated → terminated TRANSITION we DEFER the terminate fields
  // and fire the callable after the normal save. SPH is still saved client-side
  // here (the snapshot reads it server-side). On un-terminate / no-change we keep
  // the legacy client-side behavior (the live doc stays re-hireable; the archive
  // is never touched on reactivation).
  const terminatedEl = document.getElementById('person-terminated');
  const wantsTerminated = terminatedEl ? terminatedEl.checked : false;
  const existingPersonForTerm = id ? (RosterState.people.find(p => p.id === id) || {}) : {};
  const wasTerminated = existingPersonForTerm.terminated === true;
  // A "terminate transition" requires an existing person (the callable needs a
  // personId) flipping from not-terminated → terminated.
  let pendingTerminate = null;
  let pendingReasonUpdate = null; // TASK-1594 — { reason } sent to setTerminationReason after the save
  let _createdPersonId1594 = null;

  if (wantsTerminated) {
    const tDate = document.getElementById('person-terminated-date')?.value || '';
    const termReason = document.getElementById('person-terminated-reason')?.value?.trim() || null;

    // 49 CFR 391.23(d) Safety Performance History — capture once at term time
    // so the carrier can respond to future-employer SPH inquiries (per
    // 391.23(g)) without scrambling. Required to retain for 3 years.
    const sph = {
      voluntary: document.getElementById('person-sph-voluntary')?.value || null,
      eligibleForRehire: document.getElementById('person-sph-rehire')?.value || null,
      hadAccidents: document.getElementById('person-sph-accidents')?.value || null,
      accidentsDetail: document.getElementById('person-sph-accidents-detail')?.value?.trim() || null,
      hadDrugAlcoholViolation: document.getElementById('person-sph-drug-alcohol')?.value || null,
      drugAlcoholDetail: document.getElementById('person-sph-drug-alcohol-detail')?.value?.trim() || null,
      returnToDutyCompleted: document.getElementById('person-sph-rtd-completed')?.value || null,
      performanceSummary: document.getElementById('person-sph-performance')?.value?.trim() || null,
      capturedAt: new Date().toISOString(),
      capturedBy: window.AppState?.user?.email || 'unknown',
    };
    // TASK-9677 (2) — merge with the answers already on file: a blank field in this
    // submission keeps the stored answer (a blank can never wipe one), and the
    // completeness check below sees the merged record, so an answer already on file
    // is not asked for again. Same merge the server applies (_terminateDriverCore).
    _t9677MergeStoredSph(sph, existingPersonForTerm.safetyPerformanceHistory, existingPersonForTerm.hireDate);
    // Only persist if at least one answer was provided — avoid stamping an
    // all-null doc on people terminated before this feature shipped.
    const hasAnyAnswer = Object.entries(sph).some(([k, v]) =>
      !['capturedAt', 'capturedBy'].includes(k) && v != null && v !== '');
    if (hasAnyAnswer) data.safetyPerformanceHistory = sph;

    // TASK-633 — if a report-based termination is already HELD for this person,
    // the only valid next steps are Complete or Cancel (in the pending panel).
    // Block a fresh terminate transition here so an immediate (non-FCRA) firing
    // can't bypass the in-flight hold.
    // TASK-633-FF — read the hold from the DENORMALIZED person.pendingSeparation
    // (from the people subscription) instead of the dropped bg listener.
    const _heldPerson = id ? (RosterState.people || []).find(p => p.id === id) : null;
    if (id && !wasTerminated && _sepAaFromPerson(_heldPerson)) {
      await window.showAlert?.('Termination already in progress', 'This driver has an FCRA termination on hold. Use "Complete termination" or "Cancel separation" in the No Longer Employed section instead of starting a new one.');
      return;
    }
    if (id && !wasTerminated) {
      // Transition → defer to the server callable. Do NOT write terminate
      // fields client-side (the callable stamps them + retention + snapshot).
      // TASK-633 — capture the FCRA trigger. When checked, this is a report-based
      // termination → route through initiateSeparationAdverseAction (hold +
      // Pre-AA) instead of the immediate terminateDriver.
      const fcraEl = document.getElementById('person-term-fcra');
      const isFcra = !!(fcraEl && fcraEl.checked);
      const fcraReportRef = isFcra ? (document.getElementById('person-term-fcra-report')?.value?.trim() || '') : '';

      // TASK-1208 — §391.23 SPH-completeness prompt. SOFT gate (TASK-428:
      // termination must never be trappable — "Complete later" always
      // proceeds). Fires BEFORE the FCRA/non-FCRA fork commits, and the FCRA
      // branch SKIPS the modal entirely: statutory Pre-Adverse timing never
      // waits on SPH paperwork — the server-side follow-up actionItem covers
      // those. Checklist + modal live in window.PersonSections (renderer
      // renders / host authorizes — §391.23 logic stays out of roster.js).
      let effLastDay = tDate || '';
      let _modalHireDate = '';   // TASK-9662 (A) — termination-time answers the partner path sends to the callable
      let _modalPosition = '';
      if (!isFcra && window.PersonSections?.sphTerminationChecklist) {
        const sphCheck = window.PersonSections.sphTerminationChecklist(existingPersonForTerm, {
          sph,
          position: data.position || data.crewType || data.role || '',
          lastDayWorked: tDate,
          terminationReason: termReason,
        });
        if (!sphCheck.complete) {
          const sphRes = await window.PersonSections.openSphCompletenessModal({
            personName: data.name || existingPersonForTerm.name || 'this driver',
            missing: sphCheck.missing,
          });
          if (!sphRes || sphRes.action === 'cancel') return; // back to the form — nothing saved
          if (sphRes.action === 'save') {
            const v = sphRes.values || {};
            // Merge modal answers into the SPH record + person fields. Mirror
            // the values back into the (still-open) form controls too, so a
            // later save failure leaves the answers visible, then rebuild the
            // persisted SPH object from the merged values.
            const _sync = (elId, val) => { const el = document.getElementById(elId); if (el && val) el.value = val; };
            if (v.hireDate && /^\d{4}-\d{2}-\d{2}$/.test(v.hireDate)) { data.hireDate = v.hireDate; _modalHireDate = v.hireDate; }
            if (v.position) { data.position = v.position.slice(0, 100); _modalPosition = data.position; }
            if (v.lastDayWorked && /^\d{4}-\d{2}-\d{2}$/.test(v.lastDayWorked)) {
              effLastDay = v.lastDayWorked;
              _sync('person-terminated-date', v.lastDayWorked);
            }
            if (v.sphVoluntary) { sph.voluntary = v.sphVoluntary; _sync('person-sph-voluntary', v.sphVoluntary); }
            if (v.sphHadAccidents) { sph.hadAccidents = v.sphHadAccidents; _sync('person-sph-accidents', v.sphHadAccidents); }
            if (v.sphAccidentsDetail) { sph.accidentsDetail = v.sphAccidentsDetail.slice(0, 1000); _sync('person-sph-accidents-detail', sph.accidentsDetail); }
            if (v.sphHadDrugAlcohol) { sph.hadDrugAlcoholViolation = v.sphHadDrugAlcohol; _sync('person-sph-drug-alcohol', v.sphHadDrugAlcohol); }
            if (v.sphDrugAlcoholDetail) { sph.drugAlcoholDetail = v.sphDrugAlcoholDetail.slice(0, 1000); _sync('person-sph-drug-alcohol-detail', sph.drugAlcoholDetail); }
            const nowHasAnswer = Object.entries(sph).some(([k2, v2]) =>
              !['capturedAt', 'capturedBy'].includes(k2) && v2 != null && v2 !== '');
            if (nowHasAnswer) data.safetyPerformanceHistory = sph;
          }
          // action 'later' → proceed unchanged; the terminate callable creates
          // the server-side sph_incomplete follow-up actionItem.
        }
      }
      // TASK-9662 (B) — the last day must be plausible BEFORE anything saves (the
      // server re-checks: year >= 2000, not before the hire date, at most today+90).
      const _ldErr = _t9662LastDayError(effLastDay, _modalHireDate || existingPersonForTerm.hireDate);
      if (_ldErr) { await window.showAlert?.('Check the last day worked', _ldErr); return; }
      pendingTerminate = {
        lastDayWorked: effLastDay, reason: termReason || '', isFcra, fcraReportRef,
        // TASK-9662 (A) — what a PARTNER session sends to terminateDriverFromPartner
        // instead of writing it onto the people doc (the rules arm it would hit is right).
        sph: data.safetyPerformanceHistory || null, hireDate: _modalHireDate, position: _modalPosition,
      };
      delete data.terminated;
      delete data.terminatedAt;
      delete data.terminationReason;
    } else {
      // Already terminated (editing a terminated person's SPH/reason) OR a brand-
      // new person flagged terminated on create (no server termination action —
      // rare; keep the legacy client write so the flag isn't lost). The retention
      // snapshot path is the standard transition above.
      data.terminated = true;
      if (tDate) data.terminatedAt = new Date(tDate);
      // TASK-1594 — the reason never goes onto the broadcast people doc. A real
      // change is sent to the setTerminationReason callable after the save; a
      // field this viewer could not load ('denied'/'loading') sends nothing.
      // firestore.rules refuses a non-null reason on people, so the anonymous
      // demo (no claim-bound member → the callable refuses it) simply does not
      // persist an after-the-fact reason edit.
      delete data.terminationReason;
      const _rEl = document.getElementById('person-terminated-reason');
      const _rState = _rEl?.dataset?.sepState || 'none';
      if ((window.AppState?.tenant || 'demo') !== 'demo' && _rState !== 'denied' && _rState !== 'loading' && (termReason || '') !== (_rEl?.dataset?.sepOriginal || '')) {
        pendingReasonUpdate = { reason: termReason || '' };
      }
    }
  } else {
    data.terminated = false;
    data.terminatedAt = null;
    data.terminationReason = null;
    // TASK-1516 — ALSO clear the legacy `terminationDate`. terminateDriver /
    // _terminateDriverCore stamp it for portal filters, and every termination
    // predicate treats it as terminated ON ITS OWN (`terminated === true ||
    // !!terminationDate` — onPersonWrite's wasTerm/nowTerm, the compliance
    // manager + partner roster filters, hr.js, the driver-portal gates).
    // Clearing only terminated/terminatedAt left a person terminated via the
    // callable and un-terminated from this form reading as STILL TERMINATED to
    // all of them, and — because nowTerm never flipped — emitting NO
    // DRIVER_REACTIVATED complianceEvent, so a compliance partner's Activity
    // Log never saw the reinstatement. Null (not deleteField) to match the
    // siblings above: every reader is a falsy/`||` check or a `String(x||'')`
    // format and nothing where()/orderBy()s the field, so null reads identically
    // to absent. Mirrors reactivateDriver's own terminationDate delete.
    data.terminationDate = null;
    // Don't clear safetyPerformanceHistory or the dqArchive snapshot on
    // un-terminate — the historical record stays in case the person is
    // re-terminated later, and FMCSA expects employers to retain prior SPH +
    // the §391.51 DQ file for 3 years regardless of reactivation.
  }

  // Un-terminate guardrail: if reactivating someone who was previously marked
  // No Longer Employed, check whether their PIN now collides with an active
  // driver at another tenant. Without this check, the global PIN search would
  // return "ambiguous" at next login and lock BOTH drivers out.
  if (id && !data.terminated) {
    const oldPerson = RosterState.people.find(p => p.id === id);
    const wasTerminated = !!(oldPerson?.terminated === true || oldPerson?.terminationDate);
    if (wasTerminated) {
      // TASK-1441 Phase 0b — the stored driver code now lives in the
      // `peopleSecure` vault, which no client can read, so the PIN can no longer
      // be assembled here. Hand the server the PERSON and let it resolve the
      // effective code itself (stored → clockPin → computed, vault-merged).
      // Building it client-side post-migration would silently degrade to the
      // COMPUTED code and stop flagging real collisions.
      {
        try {
          const tenantId = window.AppState?.tenant || 'demo';
          const checkFn = httpsCallable(functions, 'checkDriverPinAvailable');
          const collisionResult = await checkFn({ tenantId, resolveForPersonId: id, excludePersonId: id });
          if (collisionResult.data && collisionResult.data.ok === false) {
            console.warn('[Roster] PIN collision check returned not-ok on un-terminate:', collisionResult.data.error);
          }
          if (collisionResult.data?.ok && collisionResult.data.collidingTenantCount > 0) {
            const proceed = await window.showConfirm(
              'Reactivating creates a code conflict',
              `This driver's code is already in use by an active driver at another company. Reactivating this person will lock both drivers out at login with an "ambiguous code" error. Continue anyway? You can assign a new code from the driver's profile after save.`,
              { confirmText: 'Continue', cancelText: 'Cancel', danger: true }
            );
            if (!proceed) return;
          }
        } catch (collisionErr) {
          console.warn('[Roster] PIN collision check on un-terminate failed:', collisionErr.message);
        }
      }
    }
  }

  // Soft-duplicate + driver-code collision check on NEW people (TASK-1088).
  // The driver CODE (calculateDriverPin = name-initials + phone-last4) is a stronger
  // duplicate signal than name alone: two entries that resolve to the SAME code are
  // almost certainly the same person re-added, OR will collide at driver login with an
  // "ambiguous code" error. Warn (never block) before creating. Best-effort, purely
  // client-side against already-loaded RosterState.people; wrapped so it can never
  // break the save.
  if (!id && data.name) {
    // Rapid re-add guard (existing behavior).
    const lastSaved = window.__rosterLastSavedPerson;
    if (lastSaved && lastSaved.name === data.name && (Date.now() - lastSaved.ts) < 3000) {
      const proceed = confirm(`You just added "${data.name}" a moment ago. Are you sure you want to add another?`);
      if (!proceed) return;
    }
    try {
      const candidateCode = String(calculateDriverPin(data.name, data.phone) || '').toUpperCase();
      const newNameKey = data.name.trim().toLowerCase();
      // Mirror the server's canonical matchesDriverPin() predicate so a warning
      // here means the same thing a driver-login collision would: skip
      // inactive/terminated; match stored driverPin then clockPin; use the
      // calculated fallback ONLY when neither stored PIN exists (a stored PIN
      // equal to another's calculated code is not a login collision).
      //
      // ⚠️ TASK-1441 Phase 0b — `p.driverPin` is now VAULT-RESIDENT and absent
      // from every client-loaded person, so in practice this predicate resolves
      // via the computed fallback for everyone. That is a deliberate, bounded
      // degradation of a WARN-ONLY heuristic on the NEW-person path, not a
      // regression of the real gate: the AUTHORITATIVE cross-tenant check is
      // server-side and got STRONGER, because _plainPinCollidingTenantCount
      // merges the vault and therefore sees stored codes this loop cannot.
      // It runs on every code mint (resolveDriverPinCode) and on reactivation
      // (checkDriverPinAvailable + resolveForPersonId). Do NOT "fix" this by
      // re-adding a client read of driverPin — that is the leak this phase
      // closed. The `sameName` arm below is unaffected.
      const personMatchesCode = (p, code) => {
        if (!code) return false;
        if (p.active === false || p.terminated === true || p.terminationDate) return false;
        if (p.driverPin && String(p.driverPin).toUpperCase() === code) return true;
        if (p.clockPin && String(p.clockPin).toUpperCase() === code) return true;
        if (p.driverPin || p.clockPin) return false; // stored PIN set → no calc fallback
        return String(calculateDriverPin(p.name, p.phone) || '').toUpperCase() === code;
      };
      const dup = (RosterState.people || []).find(p => {
        if (!p || p.id === id) return false;
        if (p.active === false || p.terminated === true || p.terminationDate) return false; // active only
        const sameName = newNameKey.length > 0 && String(p.name || '').trim().toLowerCase() === newNameKey;
        const sameCode = personMatchesCode(p, candidateCode);
        return sameName || sameCode;
      });
      if (dup) {
        const dupName = dup.name || 'an existing person';
        const codeLine = candidateCode
          ? ` They share the driver code "${candidateCode}" — two active people with the same code hit an "ambiguous code" error at driver login.`
          : '';
        const proceed = await window.showConfirm(
          'Possible duplicate driver',
          `"${dupName}" is already on your roster with the same name or driver code.${codeLine} Add anyway?`,
          { confirmText: 'Add anyway', cancelText: 'Cancel', danger: true }
        );
        if (!proceed) return;
      }
    } catch (dupErr) {
      console.warn('[Roster] duplicate/code check failed (non-blocking):', dupErr?.message);
    }
  }

    // TASK-9828 (2) — ONE Inactive path. Turning Active OFF on an existing, active
    // person (not being marked No Longer Employed) no longer writes active:false
    // from this form, which hid + muted but left them on upcoming jobs and never
    // asked about their staff login. The rest of the form saves with the current
    // status, then the Deactivate confirm (deletePerson) opens: upcoming-job
    // cascade + mute + login question, the same as the Roster button. A partner
    // session is excluded (its status change goes through setDriverActive).
    let _deactivateAfterSave9828 = false;
    {
      const _partner9828 = typeof window.isPartnerClientSession === 'function' && window.isPartnerClientSession();
      const _prev9828 = id ? (RosterState.people || []).find(p => p.id === id) : null;
      if (id && !pendingTerminate && !_partner9828 && data.active === false
          && _prev9828 && _prev9828.active !== false && _prev9828.terminated !== true) {
        _deactivateAfterSave9828 = true;
        data.active = true;
        data.showInDispatch = true;
        if (data.notifications) {
          data.notifications.muted = _preMute9828.nMuted;
          data.notifications.tags = _preMute9828.nTags;
          if (data.notifications.muted === undefined) delete data.notifications.muted;
          if (data.notifications.tags === undefined) delete data.notifications.tags;
        }
        if (_preMute9828.hadNotifyMuted && _preMute9828.notifyMuted !== undefined) data.notifyMuted = _preMute9828.notifyMuted; else delete data.notifyMuted;
        if (_preMute9828.hadNotifyTags && _preMute9828.notifyTags !== undefined) data.notifyTags = _preMute9828.notifyTags; else delete data.notifyTags;
      }
    }

    const tenant = window.AppState?.tenant || 'demo';

    // TASK-340 Slice B — upload profile photo before Firestore write.
    // TASK-9234 — persist the STORAGE PATH, never the download URL. The URL is
    // a bearer credential and this doc is broadcast to every session. Clearing
    // profilePhotoUrl on every save means an edited legacy row self-heals off
    // the leaked URL (the path was derived from it in editPerson, so the photo
    // survives the save).
    const personPhotoPath = await uploadPersonPhoto(tenant);
    data.profilePhotoStoragePath = personPhotoPath || null;
    // 🔴 Only clear the URL once we actually HAVE a path to render from, or the
    // photo was explicitly removed. A legacy row whose URL we could not parse a
    // path out of yields no path — nulling the URL there would delete that
    // person's only photo reference on an unrelated field edit. Leave the field
    // out of `data` entirely in that case (COD Code Reviewer + Independent,
    // 2026-09-16); it is still gated behind a path we can resolve on the next
    // successful upload.
    const _photoRemoved = !RosterState.personPhoto?.pending && !RosterState.personPhoto?.existing;
    if (personPhotoPath || _photoRemoved) data.profilePhotoUrl = null;

    if (id) {
      // Update existing
      const oldPerson = RosterState.people.find(p => p.id === id);
      // TASK-9359 — the identity kit routes to the peopleIdentity vault in the
      // same atomic batch (never onto the broadcast doc); a viewer who cannot
      // know it has it dropped (no blank-over-hidden wipe).
      // TASK-9662 (A) — a compliance PARTNER inside the carrier ("Switch to DispatchDeck")
      // has no members doc; firestore.rules lets them change only a DOT field list on a
      // people doc. Writing the whole form 403'd every partner termination ("Save failed:
      // permission-denied") — the rule is right, the caller was wrong. In a partner session:
      // termination fields go to the partner callables below (terminateDriverFromPartner /
      // initiateSeparationAdverseAction — server-side SPH sanitize, actor stamp, §391.51),
      // and the rest of the form is narrowed to what actually changed. No rules widening.
      const _partnerSess = typeof window.isPartnerClientSession === 'function' && window.isPartnerClientSession();
      let _partnerEditDenied = false;
      let _partnerRefusedLabels = [];
      if (_partnerSess) {
        const _pData = { ...data };
        if (pendingTerminate) {
          delete _pData.safetyPerformanceHistory;
          if (pendingTerminate.hireDate) delete _pData.hireDate;
          if (pendingTerminate.position) delete _pData.position;
        }
        window.partnerDropUnchangedKeys?.(_pData, oldPerson);
        // TASK-9677 (1) — an explicit allowlist, not "whatever changed". Only keys the
        // partner people arm in firestore.rules lets a partner change are written; every
        // other CHANGED key is dropped and named back to the partner. Termination fields
        // are never in the list — they go only through terminateDriverFromPartner below.
        const _split = _t9677PartnerSplit(_pData, oldPerson);
        _partnerRefusedLabels = _split.refusedLabels;
        if (_split.refused.length) {
          _partnerEditDenied = true;
          console.warn('[Roster] partner session: fields outside the partner allowlist not saved', _split.refused);
        }
        if (Object.keys(_split.allowed).length) {
          try {
            await withWriteTimeout(window.PersonIdentity.updatePerson(tenant, id, _split.allowed, oldPerson || { id }), 15000, 'update person');
          } catch (pErr) {
            if (!_t9662IsPermissionDenied(pErr)) throw pErr;
            // Backstop: an allowlisted key whose VALUE the rules constrain (e.g. a
            // document path bound to this driver's folder). Same plain message.
            _partnerEditDenied = true;
            _partnerRefusedLabels = _partnerRefusedLabels.concat(_t9677FieldLabels(Object.keys(_split.allowed)));
            console.warn('[Roster] partner session: allowlisted edit refused by rules', Object.keys(_split.allowed));
          }
        }
      } else {
        await withWriteTimeout(window.PersonIdentity.updatePerson(tenant, id, data, oldPerson || { id }), 15000, 'update person');
        // TASK-9911 (COD r3) — a failed skills write keeps the form OPEN with the
        // ticks (runGuardedSave reports the error); Save again re-applies them.
        if (_pendingSkillChanges && !(await _persistPersonSkillChanges(tenant, id, _pendingSkillChanges))) {
          throw new Error('The person saved, but their skills did not. Press Save again.');
        }
      }
      if (_partnerEditDenied) {
        await window.showAlert?.('Some edits were not saved', _t9677RefusedMessage(_partnerRefusedLabels, !!pendingTerminate));
      }

      // TASK-1122 — classification changed in the form → sanctioned server
      // write. Only fires on a REAL change to a REAL tier (blank / unchanged
      // picks no-op inside the helper — an existing classification is never
      // clobbered by an untouched or blanked select).
      await _applyPositionTypePick(tenant, id, pickedPositionType, oldPerson?.positionType, data.name); // TASK-9806 — name for the CMV checks dialog

      // Cascade name change to active clock entries + ticket lines
      // TASK-9662 (COD) — never cascade an edit the rules refused (partner session).
      if (!_partnerEditDenied && oldPerson && data.name && oldPerson.name !== data.name) {
        try {
          const activeSnap = await getDocs(query(
            collection(db, 'tenants', tenant, 'clockEntries'),
            where('personId', '==', id),
            where('status', '==', 'active')
          ));
          if (!activeSnap.empty) {
            const batch = writeBatch(db);
            for (const entryDoc of activeSnap.docs) {
              batch.update(entryDoc.ref, { personName: data.name });
              // Also update the associated ticket line if present
              const tLineId = entryDoc.data().ticketLineId;
              const ticketId = entryDoc.data().ticketId;
              if (tLineId && ticketId) {
                const lineRef = doc(db, 'tenants', tenant, 'tickets', ticketId, 'ticketLines', tLineId);
                batch.update(lineRef, { personName: data.name });
              }
            }
            await batch.commit();
            console.log(`[Roster] Cascaded name change to ${activeSnap.size} active clock entry(s)`);
          }
        } catch (err) {
          console.warn('[Roster] Failed to cascade name to clock entries:', err);
        }
      }

      // TASK-1530 — carry the mute transition on UPDATE_PERSON (both fields:
      // nested `notifications.muted` + flat legacy `notifyMuted`), mirroring
      // smsOptInChanged. The 2026-08-12 incident was undiagnosable partly
      // because the audited per-person edit could not answer "was he muted?".
      // Everything is coerced to a boolean — Firestore addDoc throws on
      // undefined and logAudit's strip would silently drop the field (TASK-1479).
      // 2nd-COD Code Reviewer — TRUTHY, not === true: the shared send-path
      // predicate (isNotificationMuted) deliberately treats any truthy legacy
      // value as muted, so a strict-boolean audit here would misreport exactly
      // those legacy shapes as unmuted while the sender skips them.
      const _oldMuteEffective = !!(oldPerson?.notifyMuted || oldPerson?.notifications?.muted);
      const _newMuteEffective = !!(data.notifyMuted || data.notifications?.muted);
      // TASK-9662 (COD) — no UPDATE_PERSON row for an edit that did not persist.
      if (!_partnerEditDenied) window.logAudit?.('roster', 'UPDATE_PERSON', { personId: id, personName: data.name, nameChanged: oldPerson?.name !== data.name, oldName: oldPerson?.name !== data.name ? oldPerson?.name : undefined, terminated: data.terminated, activeChanged: oldPerson?.active !== data.active, smsOptIn: !!data.smsOptIn, smsOptInChanged: !!oldPerson?.smsOptIn !== !!data.smsOptIn, mutedChanged: _oldMuteEffective !== _newMuteEffective, notifyMutedFrom: oldPerson?.notifyMuted === true, notifyMutedTo: data.notifyMuted === true, notificationsMutedFrom: oldPerson?.notifications?.muted === true, notificationsMutedTo: data.notifications?.muted === true, ...(_aliasAudit || {}) });
      // TASK-607 — the PERSON_REPORTS_TO_SET audit row now fires from the HR
      // Compliance save (saveComplianceFields) where reportsTo is edited. It
      // intentionally does NOT fire here anymore — savePerson no longer reads
      // person.reportsTo, so it can't tell whether it changed.

      // TASK-628 — §391.51 termination action runs SERVER-SIDE after the live
      // person doc is saved (so the snapshot captures the just-saved SPH). The
      // callable stamps terminated/retention, freezes the DQ-file snapshot into
      // dqArchive, and emits the DRIVER_TERMINATED compliance event. A partner
      // who has taken exclusive control returns permission-denied → surface it.
      if (pendingTerminate && pendingTerminate.isFcra) {
        // TASK-633 — report-based termination → DO NOT terminate now. Send the
        // Pre-Adverse Action notice and HOLD. The driver stays active until the
        // wait window closes and an admin completes it.
        try {
          const initFn = httpsCallable(functions, 'initiateSeparationAdverseAction');
          const iRes = await initFn({
            tenantId: tenant,
            personId: id,
            reason: pendingTerminate.reason || undefined,
            lastDayWorked: pendingTerminate.lastDayWorked || undefined,
            externalReportRef: pendingTerminate.fcraReportRef || undefined,
          });
          const d = iRes?.data || {};
          if (d.ok) {
            let when = '';
            try {
              const dd = d.disputeDeadline?.toDate?.() || (d.disputeDeadline?._seconds ? new Date(d.disputeDeadline._seconds * 1000) : (d.disputeDeadline ? new Date(d.disputeDeadline) : null));
              if (dd && !isNaN(dd.getTime())) when = ` (until ${dd.toLocaleDateString()})`;
            } catch (_) {}
            window.showToast?.(`Pre-Adverse Action notice sent. Termination holds until the dispute window closes${when}.`, 'success');
          } else if (d.reason === 'no_report_on_file') {
            await window.showAlert?.('Report required', 'FCRA requires a copy of the report. No background check is on file in DispatchDeck — enter where the MVR/PSP report lives in the "Where is the report on file?" field, then save again.');
          } else if (d.reason === 'already_terminated') {
            window.showToast?.('Person updated', 'success');
          } else if (d.reason === 'separation_in_flight') {
            // TASK-633-FF — server-side re-initiation guard (mirrors the client guard).
            await window.showAlert?.('Termination already in progress', 'This driver already has an FCRA termination on hold. Use "Complete termination" or "Cancel separation" in the No Longer Employed section instead of starting a new one.');
          } else if (d.reason === 'report_copy_unavailable') {
            // TASK-9790 q09 — no copy of the report → no notice, no hold started; the server says why.
            await window.showAlert?.('Report copy required', d.message || 'The report PDF could not be attached, so the FCRA Pre-Adverse notice was NOT sent. Nothing was recorded.');
          } else if (d.reason === 'candidate_email_missing') {
            await window.showAlert?.('No email on file', 'This driver has no email address, so the FCRA Pre-Adverse notice could not be sent. Add an email and try again, or terminate without the FCRA hold if the decision was not report-based.');
          } else {
            await window.showAlert?.('Could not start FCRA hold', `Could not send the Pre-Adverse notice (${d.reason || 'unknown'}).`);
          }
          await _nleRevokeAfterSave(tenant, id, { terminated: d.reason === 'already_terminated' }); // TASK-10034 — a hold leaves them active
        } catch (initErr) {
          console.error('[Roster] initiateSeparationAdverseAction failed:', initErr);
          const msg = (initErr?.code === 'functions/permission-denied' || /permission/i.test(initErr?.message || ''))
            ? (initErr?.message || 'Your compliance partner manages terminations for this account.')
            : `Could not start the FCRA hold: ${initErr?.message || 'unknown error'}`;
          await window.showAlert?.('Could not start FCRA hold', msg);
        }
      } else if (pendingTerminate) {
        try {
          // TASK-9662 (A) — a partner session terminates through the partner callable
          // (terminateDriver needs an owner/admin member doc a partner never has).
          // Termination fields only; the server sanitizes the SPH answers.
          const termFn = httpsCallable(functions, _partnerSess ? 'terminateDriverFromPartner' : 'terminateDriver');
          const tRes = await termFn(_partnerSess ? {
            tenantId: tenant,
            personId: id,
            reason: pendingTerminate.reason || undefined,
            lastDayWorked: pendingTerminate.lastDayWorked || undefined,
            hireDate: pendingTerminate.hireDate || undefined,
            position: pendingTerminate.position || undefined,
            sph: pendingTerminate.sph || undefined,
          } : {
            tenantId: tenant,
            personId: id,
            reason: pendingTerminate.reason || undefined,
            lastDayWorked: pendingTerminate.lastDayWorked || undefined,
          });
          if (tRes?.data?.ok) {
            // TASK-931 Slice B — the server writes a DRIVER_TERMINATED
            // complianceEvent (partner-visible, forgery-proof) but NOTHING to
            // tenants/{tid}/auditLog, so "who marked this person as no longer
            // employed, and when" had no answer in the place the tech-support
            // runbook says to look — while the WRONG action (deactivate) has
            // logged ASSET_DEACTIVATE all along. Log the right one too.
            // Every value is coerced to a non-undefined primitive: logAudit
            // strips undefined (TASK-1479), so an undefined here would silently
            // vanish from the row rather than error.
            window.logAudit?.('roster', 'PERSON_TERMINATED', {
              entityType: 'person',
              entityId: id,
              personId: id,
              personName: data.name || '',
              unitId: data.name || null,
              source: 'roster-person-form',
              lastDayWorked: pendingTerminate.lastDayWorked || '',
              // TASK-9828 (6) — single-home rule: the reason lives ONLY in
              // peoplePii; the audit row records its length, never the text.
              reasonLength: String(pendingTerminate.reason || '').length,
              alreadyTerminated: tRes?.data?.alreadyTerminated === true,
            });
            window.showToast?.('Driver terminated — DQ file retained per §391.51', 'success');
            await _nleRevokeAfterSave(tenant, id, { terminated: true }); // TASK-10034 — only when ticked
          } else {
            window.showToast?.('Person updated', 'success');
          }
        } catch (termErr) {
          console.error('[Roster] terminateDriver callable failed:', termErr);
          const msg = (termErr?.code === 'functions/permission-denied' || /permission/i.test(termErr?.message || ''))
            ? (termErr?.message || 'Your compliance partner manages terminations for this account.')
            : `Termination failed: ${termErr?.message || 'unknown error'}`;
          await window.showAlert?.('Could not terminate driver', msg);
        }
      } else {
        // TASK-4874 follow-up — warn (never block) when the saved name + phone
        // leave this driver on a code the code-only login can't find.
        const _codeWarn = _driverCodeSaveWarning(id, data);
        window.showToast?.(_codeWarn ? `Person updated. ${_codeWarn}` : 'Person updated', _codeWarn ? 'warning' : 'success');
        // TASK-10034 — editing someone already No Longer Employed: revoke a ticked login.
        if (wantsTerminated && wasTerminated && !_partnerEditDenied) await _nleRevokeAfterSave(tenant, id, { terminated: true });
      }
    } else {
      // Create new
      data.createdAt = new Date();
      data.color = getRandomColor();
      // TASK-9359 — create via a batch so the identity kit lands in the
      // peopleIdentity vault (same batch when this session can write it,
      // otherwise right after commit), never on the broadcast people doc.
      const newRef = doc(collection(db, 'tenants', tenant, 'people'));
      const _idBatch9359 = writeBatch(db);
      const _idCreate9359 = window.PersonIdentity.addToCreateBatch(_idBatch9359, tenant, newRef, data);
      await withWriteTimeout(_idBatch9359.commit(), 15000, 'create person');
      await window.PersonIdentity.flushDeferred(tenant, newRef.id, _idCreate9359.deferred, _idCreate9359);
      window.logAudit?.('roster', 'CREATE_PERSON', { personId: newRef.id, personName: data.name, role: data.crewType || data.role, smsOptIn: !!data.smsOptIn, ...(_aliasAudit || {}) });
      // TASK-1122 — stamp the classification chosen at creation (server-owned
      // field → callable; non-fatal, see _applyPositionTypePick).
      await _applyPositionTypePick(tenant, newRef.id, pickedPositionType, '', data.name); // TASK-9806
      // TASK-4873 — a driver added with no phone gets no name-derived code any
      // more; say so at save time so the dispatcher isn't surprised later by a
      // driver standing at a truck with nothing to type (COD Domain/UX).
      const _newRole = String(data.crewType || data.role || (Array.isArray(data.roles) ? data.roles[0] : '') || '');
      const _newHasPhone = String(data.phone || '').replace(/\D/g, '').length >= 4;
      if (/driver|cdl/i.test(_newRole) && !_newHasPhone) {
        window.showToast?.('Person added. No phone on file — open them and use Driver Code › Show setup code so they can sign in.', 'success');
      } else if (/driver|cdl/i.test(_newRole)) {
        // TASK-4692 — a new driver WITH a phone gets no name+phone code either
        // (onPersonWrite). Say so now, or the dispatcher reads them the old
        // formula and the driver gets "PIN not found" at the truck.
        window.showToast?.('Person added. Their first sign-in is a setup code — open them › Driver Code › Text setup code. They pick their own PIN.', 'success');
      } else {
        window.showToast?.('Person added', 'success');
      }
      window.__rosterLastSavedPerson = { name: data.name, ts: Date.now() };
      _createdPersonId1594 = newRef.id; // TASK-1594 — the reason write needs the new id
      // Dispatch event for tutorial system
      window.dispatchEvent(new CustomEvent('roster:person-saved'));
      // After-save inspector-doc upload (TASK-258 §396.19) — runs for new
      // people once we have the personId from addDoc.
      await _uploadInspectorDocIfPending(tenant, newRef.id);
    }

    // Inspector doc upload for the edit path runs here too — same helper.
    if (id) {
      await _uploadInspectorDocIfPending(tenant, id);
    }

    // TASK-9710 — per-driver Add/Edit Job switch → tenant settings (Dispatch-gated).
    await _rosterSaveDriverJobEdit(tenant, id || _createdPersonId1594, data.name);

    // TASK-1594 — termination reason → peoplePii via the server (the only writer).
    // Non-fatal: the rest of the person already saved; say what did not.
    if (pendingReasonUpdate) {
      const _reasonPersonId = id || _createdPersonId1594;
      if (_reasonPersonId) {
        try {
          await httpsCallable(functions, 'setTerminationReason')({ tenantId: tenant, personId: _reasonPersonId, reason: pendingReasonUpdate.reason });
        } catch (rErr) {
          console.error('[Roster] setTerminationReason failed:', rErr);
          window.showToast?.(`Person saved, but the termination reason was not: ${rErr?.message || 'unknown error'}`, 'error');
        }
      }
    }

    closeModal('person');
    renderPeopleList();
    // TASK-9828 (2) — Active was switched off: finish through the one Inactive path.
    if (_deactivateAfterSave9828) {
      window.showToast?.('Saved. Confirm the deactivation to take them off upcoming jobs.', 'info');
      deletePerson(id);
    }
  }, { buttonId: 'btn-save-person', restoreLabel: 'Save' });
}

// TASK-9894 — a "View & upload only" login (members.viewUploadOnly) may ADD a
// person but may not change or remove an existing one: the people update and
// delete rules are isDispatcherOrHigher || (roster && !isViewUploadOnly). Such a
// login was still shown Save, Unmute All and Deactivate, clicked them, and got a
// raw "Missing or insufficient permissions" (Wolverine 2026-10-15: 6x Unmute
// All, 0 of 65). Mirror of those arms, viewUploadOnly only; the server still
// decides. Demo and a session with no members doc (owner / legacy) pass. Pure so
// the unit test executes it.
const ROSTER_VIEW_UPLOAD_ONLY_MSG = 'Your login is view & upload only — ask the owner or an admin to change this.';
function _rosterPeopleEditGate(memberAccess, isDemo) {
  if (isDemo === true) return true;
  const m = memberAccess;
  if (!m || m.viewUploadOnly !== true) return true;
  return ['owner', 'admin', 'dispatcher'].includes(m.role);
}
function _rosterCanEditPeople() {
  const S = window.AppState || {};
  return _rosterPeopleEditGate(S.memberAccess, S.isDemo === true || S.tenant === 'demo');
}
// TASK-9682 — "Also goes by" (person.aliases): nicknames the crew uses in
// texts, so Claude and the text-in assistant can find the person. Not private
// (it sits on the people doc every session reads, next to the name). Edited
// by a Roster-module login or the owner (tenants/{t}.ownerId) — modules, never
// roles; firestore.rules checks the same on update. Demo keeps the field.
function _rosterCanEditAliases() {
  const S = window.AppState || {};
  if (typeof window.isPartnerClientSession === 'function' && window.isPartnerClientSession()) return false;
  if (S.isDemo === true || S.tenant === 'demo') return true;
  const uid = auth?.currentUser?.uid;
  if (uid && S.tenantData?.ownerId === uid) return true;
  const m = S.memberAccess;
  return !!(m && Array.isArray(m.modules) && m.modules.includes('roster') && m.viewUploadOnly !== true && m.readOnly !== true
    && !['revoked', 'inactive', 'pending', 'suspended', 'removed'].includes(m.status));
}
// Same cleaning as functions/src/services/personAliasMatch.js cleanAliases
// (tests/task9682-person-aliases.unit.cjs runs both on the same inputs).
function _cleanPersonAliases(v, name) {
  const norm = (s) => String(s == null ? '' : s).toLowerCase().normalize('NFKD').replace(/[̀-ͯ]/g, '').replace(/[^a-z0-9]+/g, ' ').trim();
  const raw = Array.isArray(v) ? v : (typeof v === 'string' ? v.split(/[,;\n]+/) : []);
  const own = norm(name);
  const seen = new Set();
  const out = [];
  for (const x of raw) {
    if (typeof x !== 'string') continue;
    if (x.includes('@') || /\d(?:\D*\d){3}/.test(x)) continue; // never contact data (whole entry, before the cut)
    const s = x.replace(/[\u0000-\u001f\u007f]+/g, ' ').replace(/\s+/g, ' ').trim().slice(0, 40).trim();
    const k = norm(s);
    if (!k || k === own || seen.has(k)) continue;
    seen.add(k);
    out.push(s);
    if (out.length >= 10) break;
  }
  return out;
}
function _syncPersonAliasesField(person) {
  const grp = document.getElementById('person-aliases-group');
  const el = document.getElementById('person-aliases');
  if (!grp || !el) return;
  const show = _rosterCanEditAliases();
  grp.style.display = show ? '' : 'none';
  el.value = person ? _cleanPersonAliases(person.aliases, person.name).join(', ') : '';
}
function _syncPeopleViewOnlyNote() {
  const note = document.getElementById('roster-people-viewonly-note');
  if (note) note.style.display = _rosterCanEditPeople() ? 'none' : '';
}
// Edit Person: Save off + the note for a view & upload only login; Add Person
// (a create, which the rules allow) always gets Save back.
function _applyPersonViewOnlyLock(isEdit) {
  const locked = !!isEdit && !_rosterCanEditPeople();
  const btn = document.getElementById('btn-save-person');
  if (btn) { btn.disabled = locked; btn.style.opacity = locked ? '0.5' : ''; btn.style.cursor = locked ? 'not-allowed' : ''; }
  const note = document.getElementById('person-viewonly-note');
  if (note) note.style.display = locked ? '' : 'none';
}
function _rosterRefuseViewUploadOnly() {
  if (_rosterCanEditPeople()) return false;
  window.showToast?.(ROSTER_VIEW_UPLOAD_ONLY_MSG, 'error');
  return true;
}

function isAllMuted() {
  const activePeople = RosterState.people.filter(p => p.active !== false);
  if (activePeople.length === 0) return false;
  // TASK-1531 — shared fail-closed mute predicate (app.js window twin).
  return activePeople.every(p => window.isNotificationMuted(p));
}

function updateMuteButton() {
  const btn = document.getElementById('btn-mute-toggle');
  if (!btn) return;
  const muted = isAllMuted();
  const icon = btn.querySelector('.material-icons');
  const label = btn.querySelector('.toggle-label');
  if (icon) icon.textContent = muted ? 'notifications_active' : 'notifications_off';
  if (label) label.textContent = muted ? 'Unmute' : 'Mute';
  const muteHint = muted ? 'Unmute notifications for everyone' : 'Mute notifications for everyone';
  btn.setAttribute('data-tooltip', muteHint);
  btn.setAttribute('aria-label', muteHint);
  btn.removeAttribute('title');
  btn.classList.toggle('btn-muted-active', muted);
  // TASK-9894 — a view & upload only login can't write people: say so instead.
  const canEdit = _rosterCanEditPeople();
  btn.disabled = !canEdit;
  btn.style.opacity = canEdit ? '' : '0.5';
  btn.style.cursor = canEdit ? '' : 'not-allowed';
  if (!canEdit) {
    btn.setAttribute('data-tooltip', ROSTER_VIEW_UPLOAD_ONLY_MSG);
    btn.setAttribute('aria-label', `${muteHint}. ${ROSTER_VIEW_UPLOAD_ONLY_MSG}`);
  }

  // TASK-1532 — muted-count chip: the resulting state, plainly visible, so
  // "did that work?" is answerable by looking. Refreshed on every people-list
  // render (live onSnapshot), not just after the toggle.
  const chip = document.getElementById('roster-muted-chip');
  const chipCount = document.getElementById('roster-muted-chip-count');
  if (chip && chipCount) {
    const activePeople = RosterState.people.filter(p => p.active !== false);
    const mutedCount = activePeople.filter(p => window.isNotificationMuted(p)).length;
    if (mutedCount > 0) {
      chipCount.textContent = mutedCount === activePeople.length
        ? `All ${mutedCount} muted`
        : `${mutedCount} muted`;
      chip.style.display = 'inline-flex';
    } else {
      chip.style.display = 'none';
    }
  }
}

// TASK-459 — tenant-level toggle for the driver-side Team contact directory.
// Default OFF; when ON, the driver "Team" tile appears (and shows only people
// with showOnDriverCard===true). Persists to tenant.settings via the canonical
// admin-only _settingsPersist (driver sessions can read settings but the tenant
// doc update rule blocks driver writes).
function _syncDriverDirectoryButton() {
  const btn = document.getElementById('btn-team-directory-toggle');
  if (!btn) return;
  const on = window.AppState?.settings?.driverContactDirectory?.enabled === true;
  const label = btn.querySelector('.toggle-label');
  // TASK-4846 — "On" alone never said WHO was actually listed, which is how 7 of
  // 20 people sat outside the directory for months without anyone noticing. Count
  // the SAME population the driver modal renders (active, non-terminated) so the
  // denominator matches what a driver sees. Read-only — writes nothing, fetches
  // nothing, exposes no field the admin session doesn't already hold.
  // Suppressed until the people snapshot has landed, or the first paint reads a
  // scary "0 of 0" (COD Edge Case Hunter / Architect).
  let countText = '';
  let countTitle = '';
  if (on && typeof RosterState !== 'undefined' && RosterState._peopleLoaded) {
    const roster = RosterState.people || [];
    const eligible = roster.filter(p => p && p.active !== false && p.terminated !== true);
    const listed = eligible.filter(p => p.showOnDriverCard === true);
    countText = ` (${listed.length}/${eligible.length})`;
    countTitle = ` ${listed.length} of ${eligible.length} active people are listed.`;
  }
  if (label) label.textContent = on ? `Team Directory: On${countText}` : 'Team Directory: Off';
  const tip = `Show/hide the driver-side Team contact directory. Only people with 'Show on Driver Card' on appear.${countTitle}`;
  btn.setAttribute('data-tooltip', tip);
  btn.setAttribute('aria-label', tip);
  // DESIGN.md: an active toggle uses the brand-blue treatment (.active), NOT the
  // red .btn-muted-active state. That red is reserved for Mute, where "everyone
  // muted" is an alert/danger state. Team Directory On = feature enabled = blue.
  btn.classList.toggle('active', on);
}

async function toggleDriverDirectory() {
  const next = !(window.AppState?.settings?.driverContactDirectory?.enabled === true);
  const ok = await window._settingsPersist?.({ driverContactDirectory: { enabled: next } });
  if (ok) {
    _syncDriverDirectoryButton();
    window.showToast?.(`Team contact directory ${next ? 'enabled' : 'disabled'}`, 'success');
  }
}

// TASK-459 — driver-facing Team Contact Directory. Render logic lives here (its
// domain) and is invoked from the driver "Team" action tile in dispatch.js.
// PIN drivers resolve people from the jobboard→AppState.fleetPeople bridge
// (RosterState.people is admin-only); shows ONLY people with
// showOnDriverCard===true. escH on every interpolated value (phone is stored
// data — XSS surface).
function _resolveDriverPeople() {
  const fleet = (window.AppState && window.AppState.fleetPeople) || [];
  if (fleet.length) return fleet;
  return (typeof RosterState !== 'undefined' && RosterState.people) || [];
}

// Driver-directory display title (Chris ask 2026-06-04): prefer the HR Title
// (hrPosition → getPositionTitle) over the raw roster role. Built-in positions
// resolve in any session; when getPositionTitle returns the raw id (unknown /
// custom position not loaded in a PIN session) fall back to the crewType label.
function _driverDisplayTitle(p) {
  if (!p) return '';
  const hrt = window.HRModule?.getPositionTitle?.(p.hrPosition);
  if (hrt && hrt !== p.hrPosition) return hrt;
  const ct = p.crewType || p.role || (p.roles && p.roles[0]) || '';
  return ct ? (window.getTypeLabel ? window.getTypeLabel(ct, 'crewTypes') : ct) : '';
}

function openDriverContactDirectory() {
  // Defense in depth: self-gate on the tenant toggle so a direct/console call
  // can't bypass the feature flag even if the tile somehow rendered (Code
  // Reviewer COD).
  if (window.AppState?.settings?.driverContactDirectory?.enabled !== true) return;

  const modal = document.createElement('div');
  modal.className = 'modal';
  modal.id = 'roster-team-directory-modal';
  modal.innerHTML = `
    <div class="modal-content" style="max-width:480px;max-height:80dvh;overflow-y:auto;">
      <div class="modal-header">
        <h3><span class="material-icons" style="vertical-align:middle;margin-right:6px;">groups</span>Team</h3>
        <button class="btn-icon" onclick="document.getElementById('roster-team-directory-modal')?.remove()">
          <span class="material-icons">close</span>
        </button>
      </div>
      <div style="padding:12px 16px 8px;">
        <input type="search" id="team-directory-search" autocomplete="off" placeholder="Search by name or title…"
          oninput="window.RosterModule._renderTeamDirectoryList()"
          style="width:100%;padding:10px 12px;border:1px solid var(--border-color,#e2e8f0);border-radius:8px;font-size:16px;box-sizing:border-box;">
      </div>
      <div id="team-directory-list" style="padding:0;"></div>
    </div>
  `;
  document.body.appendChild(modal);
  _renderTeamDirectoryList();
}

// TASK-606: live client-side filter for the Team directory. Mirrors
// _renderCrmTeamPickerList — read the search input, re-render rows from the same
// source people array, with a separate "No matches" empty state. Row HTML +
// XSS-escaping (escH on name/title/phone + the doc-id onclick arg) is unchanged.
function _renderTeamDirectoryList() {
  // Defense in depth: same self-gate as the opener (a console call to this
  // re-render must not run when the feature is off).
  if (window.AppState?.settings?.driverContactDirectory?.enabled !== true) return;
  const list = document.getElementById('team-directory-list');
  if (!list) return;

  const escH = (s) => (s || '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
  const fmtPhone = (ph) => (window.formatPhoneDisplay ? window.formatPhoneDisplay(ph) : (ph || ''));
  const initials = (name) => {
    const parts = (name || '').trim().split(/\s+/).filter(Boolean);
    const i = ((parts[0] || '')[0] || '') + (parts.length > 1 ? (parts[parts.length - 1][0] || '') : '');
    return i.toUpperCase() || '?';
  };

  const q = (document.getElementById('team-directory-search')?.value || '').trim().toLowerCase();
  let people = _resolveDriverPeople()
    .filter(p => p.active !== false && p.terminated !== true && p.showOnDriverCard === true)
    .slice()
    .sort((a, b) => (a.name || '').localeCompare(b.name || ''));
  if (q) people = people.filter(p =>
    (p.name || '').toLowerCase().includes(q) || (_driverDisplayTitle(p) || '').toLowerCase().includes(q));

  if (people.length === 0) {
    list.innerHTML = `<div style="padding:32px 16px;text-align:center;color:var(--text-secondary,#475569);font-size:0.9rem;">${q ? 'No matches.' : 'No contacts to show'}</div>`;
    return;
  }

  list.innerHTML = people.map(p => {
    const ph = p.phone || '';
    const telHref = ph ? 'tel:' + ph.replace(/[^0-9+]/g, '') : '';
    const title = _driverDisplayTitle(p);
    // Tap the name/title → contact card. p.id is a Firestore doc id; escape both
    // single quotes (JS string literal) and HTML entities (double-quoted onclick
    // attr) — a doc id can technically contain " (only / is forbidden), so escH
    // closes the attribute-breakout XSS path (COD Adversary).
    const idArg = escH(String(p.id || '').replace(/'/g, "\\'"));
    return `
    <div class="vehicle-list-card" style="display:flex;align-items:center;gap:12px;padding:14px 16px;border-bottom:1px solid var(--border-color,#e2e8f0);">
      <div style="width:40px;height:40px;border-radius:50%;background:var(--brand-blue,#3B82F6);color:#fff;display:flex;align-items:center;justify-content:center;font-weight:700;font-size:0.9rem;flex-shrink:0;">${escH(initials(p.name))}</div>
      <button onclick="window.RosterModule.openDriverContactCard('${idArg}')" style="flex:1;min-width:0;text-align:left;background:none;border:none;padding:0;cursor:pointer;font:inherit;">
        <div style="font-weight:600;font-size:0.95rem;color:var(--text-primary,#0F172A);">${escH(p.name || '—')}</div>
        ${title ? `<div style="font-size:0.8rem;color:var(--text-secondary,#475569);">${escH(title)}</div>` : ''}
      </button>
      ${ph ? `<a href="${escH(telHref)}" style="display:flex;align-items:center;gap:6px;color:var(--brand-blue,#3B82F6);text-decoration:none;font-weight:600;font-size:0.85rem;white-space:nowrap;">
        <span class="material-icons" style="font-size:18px;">call</span>${escH(fmtPhone(ph))}</a>` : ''}
    </div>`;
  }).join('');
}

// TASK-459 follow-up (Chris ask 2026-06-04): tap a name in the Team directory to
// open that person's contact card — HR Title, work email (if present), phone.
// Same defense-in-depth gates as the directory: feature flag + showOnDriverCard.
function openDriverContactCard(personId) {
  if (window.AppState?.settings?.driverContactDirectory?.enabled !== true) return;

  const escH = (s) => (s || '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
  const fmtPhone = (ph) => (window.formatPhoneDisplay ? window.formatPhoneDisplay(ph) : (ph || ''));
  const initials = (name) => {
    const parts = (name || '').trim().split(/\s+/).filter(Boolean);
    const i = ((parts[0] || '')[0] || '') + (parts.length > 1 ? (parts[parts.length - 1][0] || '') : '');
    return i.toUpperCase() || '?';
  };

  const p = _resolveDriverPeople().find(x => String(x.id) === String(personId));
  // Only surface people the directory itself would show — never leak a hidden
  // person's email/phone via a crafted id.
  if (!p || p.showOnDriverCard !== true || p.active === false || p.terminated === true) return;

  const title = _driverDisplayTitle(p);
  const ph = p.phone || '';
  const telHref = ph ? 'tel:' + ph.replace(/[^0-9+]/g, '') : '';
  const email = (p.email || '').trim();
  const mailHref = email ? 'mailto:' + email : '';

  const contactRow = (icon, label, value, href) => `
    <a ${href ? `href="${escH(href)}"` : ''} style="display:flex;align-items:center;gap:12px;padding:14px 16px;border-top:1px solid var(--border-color,#e2e8f0);text-decoration:none;color:inherit;">
      <span class="material-icons" style="color:var(--brand-blue,#3B82F6);font-size:22px;flex-shrink:0;">${icon}</span>
      <div style="min-width:0;">
        <div style="font-size:0.72rem;text-transform:uppercase;letter-spacing:0.03em;color:var(--text-secondary,#475569);">${escH(label)}</div>
        <div style="font-weight:600;font-size:0.95rem;color:var(--brand-blue,#3B82F6);word-break:break-word;">${escH(value)}</div>
      </div>
    </a>`;

  const modal = document.createElement('div');
  modal.className = 'modal';
  modal.id = 'roster-contact-card-modal';
  modal.innerHTML = `
    <div class="modal-content" style="max-width:420px;max-height:80dvh;overflow-y:auto;">
      <div class="modal-header">
        <h3>Contact</h3>
        <button class="btn-icon" onclick="document.getElementById('roster-contact-card-modal')?.remove()">
          <span class="material-icons">close</span>
        </button>
      </div>
      <div style="display:flex;flex-direction:column;align-items:center;text-align:center;padding:20px 16px 16px;">
        <div style="width:72px;height:72px;border-radius:50%;background:var(--brand-blue,#3B82F6);color:#fff;display:flex;align-items:center;justify-content:center;font-weight:700;font-size:1.6rem;margin-bottom:12px;">${escH(initials(p.name))}</div>
        <div style="font-weight:700;font-size:1.15rem;color:var(--text-primary,#0F172A);">${escH(p.name || '—')}</div>
        ${title ? `<div style="font-size:0.9rem;color:var(--text-secondary,#475569);margin-top:2px;">${escH(title)}</div>` : ''}
      </div>
      <div style="padding:0 0 8px;">
        ${ph ? contactRow('call', 'Phone', fmtPhone(ph), telHref) : ''}
        ${email ? contactRow('email', 'Email', email, mailHref) : ''}
        ${!ph && !email ? '<div style="padding:24px 16px;text-align:center;color:var(--text-secondary,#475569);font-size:0.9rem;border-top:1px solid var(--border-color,#e2e8f0);">No contact info on file</div>' : ''}
      </div>
    </div>
  `;
  document.body.appendChild(modal);
}

async function toggleMuteAll() {
  if (_rosterRefuseViewUploadOnly()) return; // TASK-9894
  const tenant = window.AppState?.tenant || 'demo';

  // TASK-1532 — remember what the CACHE believed, for the audit row only. The
  // direction itself must NOT come from RosterState: a stale cache made this
  // button do the exact opposite of its label (the leading explanation for the
  // 2026-08-12 12:00:46 write that wiped the crew's mute state).
  const cachedAllMuted = isAllMuted();

  // Direction from a FRESH read at click time. If we can't read, we don't
  // guess — a bulk toggle pointed the wrong way is worse than a retry.
  let freshActive;
  try {
    const snap = await getDocs(collection(db, 'tenants', tenant, 'people'));
    freshActive = snap.docs.map(d => ({ id: d.id, ...d.data() })).filter(p => p.active !== false);
  } catch (err) {
    console.error('[Roster] Mute All: fresh roster read failed:', err);
    window.showToast?.("Couldn't verify the current mute state — check your connection and try again.", 'error');
    return;
  }

  if (freshActive.length === 0) {
    window.showToast?.('No active people', 'info');
    return;
  }

  const allMuted = freshActive.every(p => window.isNotificationMuted(p));
  const willMute = !allMuted;
  const action = willMute ? 'Mute' : 'Unmute';
  const n = freshActive.length;

  // TASK-1532 — the confirm names the direction and the consequence, because
  // the button label is state-derived and trivially misread. Centered
  // showConfirm per house pattern (Pitfall #6), native confirm as fallback.
  const title = `${action} all ${n} ${n === 1 ? 'person' : 'people'}?`;
  const message = willMute
    ? 'Nobody will receive job texts or any other notifications until you unmute them.'
    : `All ${n} active ${n === 1 ? 'person' : 'people'} will start receiving notifications again.`;
  const confirmed = typeof window.showConfirm === 'function'
    ? await window.showConfirm(title, message, {
        confirmText: `${action} all`,
        cancelText: 'Cancel',
        icon: willMute ? 'notifications_off' : 'notifications_active',
        iconColor: willMute ? '#DC2626' : '#16A34A',
      })
    : confirm(`${title} ${message}`);
  if (!confirmed) return;

  // TASK-1530 — ONE audit row per invocation (not per person). This bulk write
  // previously left NO trace and the pre-send state of the incident was
  // unrecoverable. `derivedFromIsAllMuted` records the value the direction was
  // ACTUALLY computed from (fresh read); `cachedIsAllMuted`/`cacheDisagreed`
  // record what the old stale-cache path would have done — exactly the bit we
  // couldn't answer on 2026-08-12. All values coerced — never undefined
  // (TASK-1479: Firestore addDoc throws on undefined).
  const _logMuteAllAudit = (succeeded, errorMsg) => {
    window.logAudit?.('roster', willMute ? 'MUTE_ALL' : 'UNMUTE_ALL', {
      direction: willMute ? 'mute' : 'unmute',
      derivedFromIsAllMuted: allMuted === true,
      cachedIsAllMuted: cachedAllMuted === true,
      cacheDisagreed: cachedAllMuted !== allMuted,
      attempted: n,
      succeeded: succeeded,
      partial: succeeded < n,
      error: errorMsg || null,
      personIds: freshActive.map(p => String(p.id)),
    });
  };

  // TASK-1532 — batched commit instead of 14 sequential updateDoc calls, so a
  // mid-loop failure can't leave the roster half-muted ("Muted 6, then error
  // occurred" was a state nobody could act on — and a plausible alternate
  // explanation for the incident). Chunks of 400 stay under Firestore's
  // 500-op batch cap; any realistic roster is a single all-or-nothing batch.
  let written = 0;
  try {
    for (let i = 0; i < freshActive.length; i += 400) {
      const chunk = freshActive.slice(i, i + 400);
      const batch = writeBatch(db);
      for (const person of chunk) {
        batch.update(doc(db, 'tenants', tenant, 'people', person.id), {
          notifyMuted: willMute,
          'notifications.muted': willMute,
          updatedAt: new Date()
        });
      }
      await batch.commit();
      written += chunk.length;
    }

    _logMuteAllAudit(written, null);
    window.showToast?.(`${action}d ${written} ${written === 1 ? 'person' : 'people'}`, 'success');
    updateMuteButton();
  } catch (error) {
    console.error(`[Roster] Error ${action.toLowerCase()}ing people:`, error);
    _logMuteAllAudit(written, String(error?.message || error).slice(0, 200));
    window.showToast?.(
      written === 0
        ? `${action} failed — nothing was changed. Try again.`
        : `${action}d ${written} of ${n}, then an error occurred — check the roster.`,
      'error'
    );
  }
}

// Keep legacy name for backwards compat
async function muteAllPeople() { return toggleMuteAll(); }

// TASK-358 (2026-05-26): cascade-revoke staff login when admin deactivates a
// person who has one. Without this, the soft-deactivated person retains their
// Firebase Auth session + members/{uid} doc + module access — they can still
// approve time-off, delete maintenance tasks, write hrForms, etc. (v1.15.821
// hasModule() rules made this gap more severe). Flow:
//   1. Look up any active members/{uid} doc(s) for this personId.
//   2. If none → existing flow unchanged.
//   3. If one or more → prompt "Also revoke their login?" (default Yes).
//      - Owner-account check short-circuits the flow with a toast (can't
//        self-deactivate the owner).
//   4. If admin confirms revoke: call revokeUserAccess for EACH active member
//      doc BEFORE the deactivate writes. Any revoke failure aborts the whole
//      flow (no person deactivate) so we never end up in a half-revoked state.
//   5. If admin confirms keep-login: deactivate person, write audit entry
//      PERSON_DEACTIVATED_LOGIN_KEPT so the gap is traceable.
// Anti-patterns: don't extract a shared cascade helper (one caller); revoke
// MUST come before deactivate (revoke-first atomicity); revoke ALL member docs
// not just the first (rare multi-doc case from re-invite producing new uid).
/**
 * TASK-931 Slice A — route from the deactivate confirm to the real separation
 * control. Closes the confirm, opens the person's edit form, and parks the eye
 * on the "No Longer Employed" section.
 *
 * Deliberately does NOT tick the checkbox or write anything: offboarding in
 * this product is a checklist the admin fills in (last day, reason, §391.23
 * SPH), not an action a button performs. This is navigation only.
 */
function _openTerminationFromDeactivate(personId) {
  closeModal('delete');
  editPerson(personId);
  // editPerson is synchronous for the DOM we need; defer one tick so the person
  // modal has settled before we scroll (same handoff pattern as
  // _pickCrmTeamPerson → showSetupStaffLoginModal).
  setTimeout(() => {
    const section = document.getElementById('person-terminated-section');
    if (!section || section.style.display === 'none') return;
    try { section.scrollIntoView({ behavior: 'smooth', block: 'center' }); }
    catch { try { section.scrollIntoView(); } catch {} }
    // Momentary outline so the section is findable in a long form.
    const prevOutline = section.style.outline;
    const prevOffset = section.style.outlineOffset;
    section.style.outline = '2px solid #B45309';
    section.style.outlineOffset = '4px';
    setTimeout(() => { section.style.outline = prevOutline; section.style.outlineOffset = prevOffset; }, 2400);
    try { document.getElementById('person-terminated')?.focus?.({ preventScroll: true }); } catch {}
  }, 60);
}

// TASK-9828 (P0 hotfix 2026-10-13) — Deactivate must NEVER touch past job history.
// Only a jobDay whose date is AFTER today, in the TENANT's timezone, is eligible for
// the cascade. Today and past days are kept (payroll, billing, DOT and wage-hour
// records reconcile against them). A missing / non-Timestamp date is skipped.
// Timezone resolution mirrors functions/src/notifications/crewAssignmentAlerts.js _tenantTz.
function _rosterTenantTz() {
  const t = window.AppState?.tenantData || {};
  const st = window.AppState?.settings || t.settings || {};
  return t.timezone || st.timezone || 'America/Chicago';
}
function _rosterDayKey(d, tz) {
  try {
    return new Intl.DateTimeFormat('en-CA', { timeZone: tz, year: 'numeric', month: '2-digit', day: '2-digit' }).format(d);
  } catch {
    return new Intl.DateTimeFormat('en-CA', { timeZone: 'America/Chicago', year: 'numeric', month: '2-digit', day: '2-digit' }).format(d);
  }
}
// Returns 'future' | 'past' (today or earlier) | 'invalid'.
function classifyJobDayForCascade(data, tz, todayKey) {
  const raw = data && data.date;
  if (!raw || typeof raw.toDate !== 'function') return 'invalid';
  let d;
  try { d = raw.toDate(); } catch { return 'invalid'; }
  if (!(d instanceof Date) || isNaN(d.getTime())) return 'invalid';
  return _rosterDayKey(d, tz) > todayKey ? 'future' : 'past';
}

// TASK-9828 STEP 2 — the ONE jobDay cascade behind Deactivate (person, vehicle,
// trailer). Touches only days AFTER today in the tenant timezone that are not
// cancelled, and removes the id only from the arrays that actually hold it.
// firestore.rules (jdCrewKeepOk) refuses a CREW-ONLY write on a day dated today or
// earlier — the backstop for stale pre-hotfix bundles, whose cascade wrote crew
// arrays alone. This client stamps updatedAt with every removal, so its writes are
// judged by its own date check (tenant timezone), never by the rules' proxy.
// ALL-OR-NOTHING: the removals go in write batches (each atomic), all stamped
// with ONE updatedAt. If a later batch fails, every day already committed is
// rolled back one by one (the id put back with arrayUnion) — each in its own
// transaction that restores only while updatedAt is still our stamp, so a
// dispatcher edit made in between (even between the read and the write) is
// never undone; every day is attempted even if one fails. The error is rethrown, so
// the caller never flips the person/unit inactive over a half-done cascade, and
// days that could not be restored are named in a toast + the console.
async function _cascadeOffFutureJobDays(tenant, id, keys) {
  const snap = await getDocs(collection(db, 'tenants', tenant, 'jobDays'));
  const tz = _rosterTenantTz();
  const todayKey = _rosterDayKey(new Date(), tz);
  const out = { cascaded: 0, skippedPastJobDays: 0, skippedInvalidDate: 0, skippedCancelled: 0 };
  const writes = [];
  snap.forEach(docSnap => {
    const data = docSnap.data() || {};
    const hit = keys.filter(k => Array.isArray(data[k]) && data[k].includes(id));
    if (!hit.length) return;
    const cls = classifyJobDayForCascade(data, tz, todayKey);
    if (cls === 'past') { out.skippedPastJobDays++; return; }
    if (cls === 'invalid') { out.skippedInvalidDate++; return; }
    if (data.status === 'cancelled') { out.skippedCancelled++; return; }
    writes.push({ ref: doc(db, 'tenants', tenant, 'jobDays', docSnap.id), keys: hit });
  });
  const CHUNK = 400;
  const stamp = Timestamp.now();
  const done = [];
  try {
    for (let i = 0; i < writes.length; i += CHUNK) {
      const part = writes.slice(i, i + CHUNK);
      const b = writeBatch(db);
      for (const w of part) {
        const upd = { updatedAt: stamp };
        w.keys.forEach(k => { upd[k] = arrayRemove(id); });
        b.update(w.ref, upd);
      }
      await b.commit();
      done.push(...part);
    }
  } catch (err) {
    const notRestored = [];  // { day, why }
    for (const w of done) {
      const day = String(w.ref?.id || w.ref);
      try {
        const restored = await runTransaction(db, async (tx) => {
          const snap = await tx.get(w.ref);
          const cur = snap.exists() ? (snap.data() || {}).updatedAt : null;
          if (!(cur && typeof cur.isEqual === 'function' && cur.isEqual(stamp))) return false;
          const upd = { updatedAt: Timestamp.now() };
          w.keys.forEach(k => { upd[k] = arrayUnion(id); });
          tx.update(w.ref, upd);
          return true;
        });
        if (!restored) notRestored.push({ day, why: 'edited since' });
      } catch (rbErr) {
        notRestored.push({ day, why: rbErr?.code || rbErr?.message || 'error' });
      }
    }
    if (notRestored.length) {
      console.error('[Roster] cascade rollback incomplete — these job days were NOT restored', id, notRestored);
      err.rollbackFailed = notRestored;
      window.showToast?.(`Deactivate failed, and ${notRestored.length} upcoming job day(s) could not be put back automatically. Check the board for this ${keys.includes('drivers') ? 'person' : 'unit'}.`, 'error');
    }
    throw err;
  }
  out.cascaded = writes.length;
  if (out.cascaded) console.log(`[Roster] Removed ${id} from ${out.cascaded} upcoming job day(s)`);
  return out;
}

async function deletePerson(id) {
  const person = RosterState.people.find(p => p.id === id);
  if (!person) return;
  if (_rosterRefuseViewUploadOnly()) return; // TASK-9894

  const tenant = window.AppState?.tenant || 'demo';

  // Lookup ALL member docs for this person (active + revoked, all roles). We
  // do this BEFORE showing the modal so we can:
  //   (a) detect an owner doc for the belt-and-suspenders guard below,
  //   (b) render login context + the "also revoke" question inline.
  // FAIL-CLOSED on lookup error (COD Code Reviewer flagged the fail-open):
  // this is a security-gap fix; silent fallback to deactivate-only would
  // defeat the whole point. Show error toast + bail so admin can retry or
  // revoke manually from the staff login card.
  let allMembers = [];
  try {
    const membersQ = query(
      collection(db, 'tenants', tenant, 'members'),
      where('personId', '==', id)
    );
    const membersSnap = await getDocs(membersQ);
    allMembers = membersSnap.docs.map(d => ({ uid: d.id, ...(d.data() || {}) }));
  } catch (err) {
    console.error('[Roster] deletePerson: members lookup failed — aborting (fail-closed)', err);
    window.showToast?.('Could not check staff login status — please retry.', 'error');
    return;
  }

  // Owner-account guard — belt-and-suspenders per COD Code Reviewer:
  //   (1) Email match against tenantData.ownerEmail (case-insensitive) — the
  //       common case where the founder's person doc has their email.
  //   (2) Any member doc with role === 'owner' linked to this personId —
  //       catches edge cases where tenantData isn't loaded yet OR the owner
  //       email is aliased/case-drifted from the person.email field.
  // revokeUserAccess rejects owner revoke server-side too, but the client
  // guard prevents a modal flash before the admin commits to anything.
  try {
    const ownerEmail = (window.AppState?.tenantData?.ownerEmail || '').toLowerCase();
    const personEmail = (person.email || '').toLowerCase();
    const hasOwnerMember = allMembers.some(m => m.role === 'owner');
    if ((ownerEmail && personEmail && ownerEmail === personEmail) || hasOwnerMember) {
      window.showToast?.("Can't deactivate the owner account.", 'error');
      return;
    }
  } catch {}

  // TASK-9828 — clocked in right now? Say so plainly; the shift is NOT closed
  // here (the office closes it). Best-effort: a lookup failure only drops the note.
  let clockedInHtml = '';
  try {
    const openSnap = await getDocs(query(
      collection(db, 'tenants', tenant, 'clockEntries'),
      where('personId', '==', id),
      where('status', '==', 'active')
    ));
    if (!openSnap.empty) {
      clockedInHtml = `<p id="deactivate-clocked-in-note" style="margin-top:10px;padding:8px 10px;background:#FEF3C7;border:1px solid #FDE68A;border-radius:6px;color:#7C2D12;text-align:left;">
        <strong>${escapeHtml(person.name)} is clocked in.</strong> Their shift stays open until the office closes it.</p>`;
    }
  } catch (clockErr) {
    console.warn('[Roster] deletePerson: open-shift lookup failed (note skipped)', clockErr?.message);
  }

  // Filter to active non-owner member docs for the cascade revoke.
  const activeMembers = allMembers.filter(m => (m.status || 'active') === 'active' && m.role !== 'owner');

  const hasActiveLogin = activeMembers.length > 0;
  // TASK-9389 S1 — revoking a login is the owner's / a "manage logins" holder's
  // call (revokeUserAccess refuses anyone else, and a refused revoke aborts the
  // whole deactivate). Anyone else deactivating keeps the login (radio preset +
  // revoke disabled) instead of meeting that refusal.
  const canRevokeHere = hasActiveLogin && _canManageStaffLogins() && activeMembers.every(m => _staffLoginViewerCanTouch(m, true));
  const moduleLabels = { '811': 'Locates (811)', billing: 'AR / AP', workforce: 'Workforce', bi: 'BI', jobboard: 'Job Board', hr: 'HR' };
  const loginContextHtml = hasActiveLogin ? (() => {
    const rows = activeMembers.map(m => {
      const mods = (m.modules || [])
        .map(s => moduleLabels[s] || (s.charAt(0).toUpperCase() + s.slice(1)))
        .join(', ') || 'No modules assigned';
      return `<div style="margin-top:6px;color:#166534;font-size:0.82rem;"><strong>${escapeHtml(m.email || '(no email)')}</strong> &mdash; ${escapeHtml(mods)}</div>`;
    }).join('');
    const plural = activeMembers.length > 1 ? `${activeMembers.length} active staff logins` : 'an active staff login';
    return `
      <div style="margin-top:14px;padding:10px 12px;background:#dcfce7;border:1px solid #16a34a;border-radius:6px;text-align:left;">
        <div style="display:flex;align-items:center;gap:8px;">
          <span class="material-icons" style="color:#15803d;font-size:18px;">verified_user</span>
          <strong style="color:#15803d;font-size:0.9rem;">This person has ${plural}</strong>
        </div>
        ${rows}
        <div style="margin-top:10px;display:flex;flex-direction:column;gap:6px;">
          <label style="display:flex;align-items:center;gap:8px;font-size:0.85rem;cursor:pointer;color:#166534;">
            <input type="radio" name="deactivate-login-choice" value="revoke" ${canRevokeHere ? 'checked' : 'disabled'}>
            <span><strong>Yes, revoke ${activeMembers.length > 1 ? 'their logins' : 'their login'}</strong> (default &mdash; they lose access immediately)</span>
          </label>
          <label style="display:flex;align-items:center;gap:8px;font-size:0.85rem;cursor:pointer;color:#166534;">
            <input type="radio" name="deactivate-login-choice" value="keep" ${canRevokeHere ? '' : 'checked'}>
            <span>Keep login active (temporary leave / seasonal layoff)</span>
          </label>
          ${canRevokeHere ? '' : '<div style="font-size:0.8rem;color:#166534;">Only the account owner, or someone the owner lets manage logins, can revoke this login. Ask them to revoke it.</div>'}
        </div>
      </div>`;
  })() : '';

  // TASK-931 Slice A — deactivate vs. "No Longer Employed". These are two
  // genuinely different actions and the confirm never said so, which is how
  // a quit employee got deactivated (2026-06-30: 140 jobDay assignments
  // cascaded off, no last day, no reason, no separation record — the person
  // still reads as "employed, temporarily not working").
  //   deactivate  = hide + pull off scheduled jobs + optional login revoke.
  //                 Reversible. Writes NO employment record.
  //   No Longer   = stamps last day + reason, captures §391.23 Safety
  //   Employed      Performance History, freezes the §391.51 DQ-file archive,
  //                 emits the partner-visible DRIVER_TERMINATED event.
  // Copy only + a route to the existing control. No new flow, and nothing here
  // performs a termination — the person still has to fill the section out.
  // TASK-10211 — the copy now lives in the LAZY views/rosterPersonDeactivate.js
  // (separationHtml, perf budget) next to the "Laid off, may return" box. The
  // GATE stays here — the host authorizes, the chunk only renders.
  const _lay = await _layoffMod();
  if (!_lay) { window.showToast?.('Could not open Deactivate. Check your connection and try again.', 'error'); return; }
  const separationHtml = person.terminated === true ? '' // already recorded — nothing to point at
    : _lay.separationHtml(person, id, _carrierCanTerminate() && _rosterCanTerminate(), !_carrierCanTerminate()); // TASK-9828 (6) — no setting dependency
  document.getElementById('delete-message').innerHTML = `
    <p>Are you sure you want to deactivate <strong>${escapeHtml(person.name)}</strong>?</p>
    <p class="form-hint" style="margin-top: 8px; color: var(--warning-color);">
      <span class="material-icons" style="font-size: 16px; vertical-align: middle;">warning</span>
      They will be removed from upcoming jobs (after today) and hidden from active lists. Today's and past jobs keep their history. You can reactivate them later.
    </p>${_lay.deactivateHtml(person)}${clockedInHtml}${separationHtml}${loginContextHtml}`;
  _lay.wireRecallHint(person);
  const confirmBtn = document.getElementById('btn-confirm-delete');
  confirmBtn.onclick = async () => {
    // Double-click guard (COD Edge Case Hunter): disable the button while
    // revokes + writes are in flight so a frantic re-click doesn't trigger
    // redundant API calls or a partial second pass.
    if (confirmBtn.dataset.inflight === '1') return;
    confirmBtn.dataset.inflight = '1';
    confirmBtn.disabled = true;
    try {
      const _lc = _lay.readChoice(); // TASK-10211 { r: 'laid_off'|null, d: recall date|null, n: note|null }
      // Read the admin's choice (only relevant if there was an active login).
      const revokeLogin = hasActiveLogin
        ? (document.querySelector('input[name="deactivate-login-choice"]:checked')?.value !== 'keep')
        : false;

      // Slice 2 — Cascade revoke BEFORE deactivate writes (atomicity). If any
      // revoke fails, abort the whole flow so we never end up with the login
      // revoked but the person still "active" (or vice-versa). Revoke ALL
      // active member docs (rare multi-doc case from re-invite producing a
      // new uid for the same personId).
      if (hasActiveLogin && revokeLogin) {
        try {
          const revokeFn = httpsCallable(functions, 'revokeUserAccess');
          // Sequential await so the first failure short-circuits the rest —
          // if member-doc #2's revoke fails, we don't want #3 to also fire.
          for (const m of activeMembers) {
            await revokeFn({ tenantId: tenant, uid: m.uid });
          }
        } catch (revokeErr) {
          console.error('[Roster] deletePerson: cascade revoke failed, aborting deactivate', revokeErr);
          const msg = revokeErr?.message || 'Failed to revoke login. Person was NOT deactivated.';
          window.showToast?.(msg.replace(/^internal:\s*/i, '').slice(0, 200), 'error');
          return;  // finally block re-enables the button
        }
      }

      // Remove person from upcoming (after today, not cancelled) job days only.
      const _cas = await _cascadeOffFutureJobDays(tenant, id, ['drivers', 'helpers']);

      try {
        window.logAudit?.('roster', 'ASSET_DEACTIVATE', {
          entityType: 'person',
          entityId: id,
          source: 'roster-delete-person',
          unitId: person?.name || null,
          redFlags: {
            cascadedJobDays: _cas.cascaded,
            activeUntil: new Date().toISOString().slice(0, 10),
            priorAssignmentCount: _cas.cascaded,
            skippedPastJobDays: _cas.skippedPastJobDays,
            skippedInvalidDate: _cas.skippedInvalidDate,
            skippedCancelled: _cas.skippedCancelled,
            cascadedLoginRevokes: (hasActiveLogin && revokeLogin) ? activeMembers.length : 0,
            loginKept: hasActiveLogin && !revokeLogin,
            layoff: _lc.r ? { recallBy: _lc.d || null, note: _lc.n || null } : null // TASK-10211 — the note lives only here
          }
        });
        // Soft warning entry so the security gap is traceable when an admin
        // chose to keep the login. Tech-support runbook can grep this.
        if (hasActiveLogin && !revokeLogin) {
          window.logAudit?.('roster', 'PERSON_DEACTIVATED_LOGIN_KEPT', {
            entityType: 'person',
            entityId: id,
            unitId: person?.name || null,
            redFlags: {
              memberUids: activeMembers.map(m => m.uid),
              memberEmails: activeMembers.map(m => m.email || null)
            }
          });
        }
      } catch {}
      // TASK-9828 (2) — the ONE Inactive write: hidden, muted, stamped. The Edit
      // form's Active toggle routes here too (savePerson → deletePerson), so both
      // entry points cascade AND mute. onPersonWrite revokes driver sessions.
      await updateDoc(doc(db, 'tenants', tenant, 'people', id), {
        // TASK-10211 — re-Deactivating an already-Inactive person (to add the layoff
        // label) keeps the date they actually went Inactive (Recall counts days from it).
        active: false, showInDispatch: false, deactivatedAt: (person.active === false && person.deactivatedAt) || Timestamp.now(),
        'notifications.muted': true, 'notifications.tags': [], notifyMuted: true, notifyTags: [],
        // TASK-10211 — a label on Inactive, not a new state; written (or cleared) on every Deactivate.
        inactiveReason: _lc.r || null, recallBy: _lc.d || null
      });
      const successMsg = (hasActiveLogin && revokeLogin)
        ? (activeMembers.length > 1 ? `Person deactivated, ${activeMembers.length} logins revoked` : 'Person deactivated, login revoked')
        : 'Person deactivated';
      window.showToast?.(successMsg, 'success');
      closeModal('delete');
    } catch (error) {
      console.error('[Roster] Error deactivating person:', error);
      window.showToast?.('Error deactivating person', 'error');
    } finally {
      // Release the double-click guard so a follow-up retry (or a different
      // person delete that reuses the same modal) isn't permanently blocked.
      confirmBtn.dataset.inflight = '0';
      confirmBtn.disabled = false;
    }
  };
  document.getElementById('modal-delete').classList.remove('hidden');
}

// TASK-10211 — the person Deactivate dialog's boxes (quit-or-let-go, "Laid off,
// may return") and the Recall dialog live in the LAZY views/rosterPersonDeactivate.js
// (perf budget, appColdLoadGzip).
// A failed load returns null: Deactivate and Recall then say so and change nothing.
let _layoffP = null;
const _layoffMod = () => _layoffP || (_layoffP = __ddChunkWrap(import('./rosterPersonDeactivate.js?v=10211002')).catch(() => (_layoffP = null)));
const recallPerson = (id) => _layoffMod().then(m => m ? m.openRecall(RosterState.people.find(x => x.id === id), { db, doc, runTransaction, canEdit: _rosterCanEditPeople() }) : window.showToast?.('Could not open Recall. Check your connection.', 'error'));

// ============================================
// CRUD OPERATIONS - TRUCKS
// ============================================
function showAddTruck() {
  document.getElementById('modal-truck-title').textContent = 'Add Vehicle';
  document.getElementById('form-truck').reset();
  document.getElementById('truck-id').value = '';
  _resetVehicleCitationsPanel();
  document.getElementById('truck-active').checked = true;
  document.getElementById('truck-show-in-dispatch').checked = true;
  // Default IFTA-registered for new trucks (most Class 8 tractors qualify)
  const iftaEl = document.getElementById('truck-ifta-registered');
  if (iftaEl) iftaEl.checked = true;
  const iftaYearEl = document.getElementById('truck-ifta-decal-year');
  if (iftaYearEl) iftaYearEl.value = '';
  // TASK-9339 — offer every type the fleet already uses, not just the registered list.
  populateTypeDropdown('truck-type', 'truckTypes', false, { inUse: RosterState.trucks });
  // Initialize with one empty axle field
  loadTruckAxles([]);
  // TASK-525 — reset Heavy Haul / Permit Specs (registered weight + per-axle table)
  const tRegWt = document.getElementById('truck-registered-weight');
  if (tRegWt) tRegWt.value = '';
  _loadPermitAxles('truck', []);
  _renderUnitRigsSummary('truck', null);
  _pgeoSync('truck', null); // TASK-9993
  const tHH = document.getElementById('truck-heavyhaul-section');
  if (tHH) tHH.open = false;
  // TASK-9267 — no nag badges on a brand-new, not-yet-saved vehicle.
  ['truck-dims-missing-badge', 'truck-hh-missing-badge'].forEach(elId => {
    const el = document.getElementById(elId);
    if (el) { el.style.display = 'none'; el.textContent = ''; }
  });
  // Initialize with one empty VIN field
  loadTruckVins([]);
  // Initialize with one empty plate field
  loadTruckPlates([]);
  // Clear all docs
  clearAllDocs('truck');
  // TASK-340 Slice A — reset photo state on "new truck" modal open.
  RosterState.truckPhoto = { pending: null, existing: null };
  _renderEquipmentPhotoPreview('truck');
  // Reset flag settings
  document.getElementById('truck-show-inspection-flag').checked = false;
  document.getElementById('truck-flag-days').value = '30';
  document.getElementById('truck-flag-options').style.display = 'none';
  document.getElementById('truck-reset-flag-btn').style.display = 'none';
  // Initialize empty inventory
  loadTruckInventory([], false);
  // Yard dropdown
  populateYardDropdown('truck-yard', false);
  // TASK-259 — default ownership=owned + hide the lease-detail block on add.
  const ownershipEl = document.getElementById('truck-ownership');
  if (ownershipEl) ownershipEl.value = 'owned';
  _toggleLeaseFieldsVisibility(false);
  _toggleLeaseStatementBtnEnabled();
  const leaseStmtCur = document.getElementById('truck-lease-statement-current');
  if (leaseStmtCur) leaseStmtCur.textContent = 'No statement on file yet — save the vehicle first to enable generation.';
  // TASK-1141 — no thread on an unsaved vehicle (no doc id yet)
  window.EquipmentNotes?.unmount('truck-notes-thread');
  window.Reminders?.unmount('truck-reminders'); // TASK-1550 — nothing to attach to yet
  _rentalLoad('truck', null); // TASK-9869
  const modal = document.getElementById('modal-truck');
  modal.querySelectorAll('.smart-warning').forEach(el => el.remove());
  modal.classList.remove('hidden');
  // Reset scroll position to top
  const modalBody = modal.querySelector('.modal-body');
  if (modalBody) modalBody.scrollTop = 0;
  window.attachAddressAutocomplete?.(document.getElementById('truck-inspection-location'));
}

function editTruck(id) {
  const truck = RosterState.trucks.find(t => t.id === id);
  if (!truck) return;
  _resetVehicleCitationsPanel();

  // TASK-1141 — per-unit notes thread under the legacy Notes field
  window.EquipmentNotes?.mount('truck-notes-thread', {
    kind: 'truck',
    id,
    unitLabel: window.getUnitDisplay ? window.getUnitDisplay(truck) : (truck.unit || ''),
  });
  // TASK-1550 — reminders on this vehicle (window.Reminders self-gates on roster|maintenance)
  window.Reminders?.mount('truck-reminders', {
    type: 'vehicle', id, label: window.getUnitDisplay ? window.getUnitDisplay(truck) : (truck.unit || ''),
  });

  document.getElementById('modal-truck-title').textContent = 'Edit Vehicle';
  document.getElementById('truck-id').value = id;
  document.getElementById('truck-unit').value = truck.unit || '';
  document.getElementById('truck-nickname').value = truck.nickname || '';
  // TASK-340 Slice A — seed photo state from existing truck.photoUrl.
  RosterState.truckPhoto = {
    pending: null,
    existing: truck.photoUrl ? { url: truck.photoUrl } : null,
  };
  _renderEquipmentPhotoPreview('truck');
  // TASK-9339 — in-use types are offered; `keep` still guarantees this truck's own
  // (legacy) type is selectable even before RosterState.trucks has loaded.
  populateTypeDropdown('truck-type', 'truckTypes', false, { inUse: RosterState.trucks, keep: truck.type });
  const truckTypeSelect = document.getElementById('truck-type');
  truckTypeSelect.value = truck.type || 'truck';
  document.getElementById('truck-make').value = truck.make || '';
  document.getElementById('truck-model').value = truck.model || '';
  document.getElementById('truck-year').value = truck.year || '';
  // VINs - load existing VINs (backward compat: single vin string → array)
  loadTruckVins(truck.vins || (truck.vin ? [truck.vin] : []));
  // Weight Ratings
  document.getElementById('truck-gvwr').value = truck.gvwr || '';
  document.getElementById('truck-gcwr').value = truck.gcwr || '';
  document.getElementById('truck-curb-weight').value = truck.curbWeight || '';
  document.getElementById('truck-payload-capacity').value = truck.payloadCapacity || '';
  document.getElementById('truck-gawr-front').value = truck.gawrFront || '';
  document.getElementById('truck-gawr-rear').value = truck.gawrRear || '';
  // TASK-512 — OS/OW dimensions + axle config
  document.getElementById('truck-length').value = truck.length || '';
  document.getElementById('truck-width').value = truck.width || '';
  document.getElementById('truck-height').value = truck.height || '';
  document.getElementById('truck-axle-count').value = truck.axleCount || '';
  document.getElementById('truck-axle-spacing').value = truck.axleSpacing || '';
  // TASK-525 — Heavy Haul / Permit Specs. Auto-open the section if data exists so it's visible on edit.
  const tRegWtEl = document.getElementById('truck-registered-weight');
  if (tRegWtEl) tRegWtEl.value = truck.registeredWeight || '';
  _loadPermitAxles('truck', truck.permitAxles || []);
  // TASK-1443 — read-only rig membership
  _renderUnitRigsSummary('truck', truck.id);
  _pgeoSync('truck', truck.id); // TASK-9993 — permit measurements, drawn when the section is open
  const tHHEl = document.getElementById('truck-heavyhaul-section');
  if (tHHEl) tHHEl.open = !!(truck.registeredWeight || (truck.permitAxles && truck.permitAxles.length));
  // TASK-9267 — badge the collapsed headers so a vehicle with no permit
  // specs is discoverable without opening either section.
  _updatePermitSpecsBadges('truck', truck);
  // Registration & Compliance — multi-plate support
  loadTruckPlates(truck.licensePlates || [], truck.licensePlate, truck.licensePlateState);
  document.getElementById('truck-reg-number').value = truck.registrationNumber || '';
  document.getElementById('truck-reg-expiry').value = truck.registrationNoExpiry === true ? '' : (truck.registrationExpiry || ''); // TASK-9814 follow-up: "Doesn't expire" wins
  document.getElementById('truck-reg-expiry').dataset.prevValue = truck.registrationExpiry || ''; // restored if the box is unticked
  document.getElementById('truck-reg-no-expiry').checked = truck.registrationNoExpiry === true; // TASK-9814
  window.RegNoExpiryGuard?.reset(document.getElementById('truck-reg-no-expiry')); // TASK-9935
  document.getElementById('truck-irp-expiry').value = truck.irpExpiry || ''; // TASK-9813
  // Insurance
  document.getElementById('truck-ins-company').value = truck.insuranceCompany || '';
  document.getElementById('truck-ins-policy').value = truck.insurancePolicy || '';
  document.getElementById('truck-ins-expiry').value = truck.insuranceExpiry || '';
  document.getElementById('truck-ins-phone').value = truck.insurancePhone || '';
  // Annual Inspection
  document.getElementById('truck-inspection-date').value = truck.inspectionDate || '';
  document.getElementById('truck-inspection-expiry').value = truck.inspectionExpiry || '';
  document.getElementById('truck-inspection-location').value = truck.inspectionLocation || '';
  // Engine/Drivetrain
  document.getElementById('truck-engine-make').value = truck.engineMake || '';
  document.getElementById('truck-engine-model').value = truck.engineModel || '';
  document.getElementById('truck-engine-serial').value = truck.engineSerial || '';
  document.getElementById('truck-transmission').value = truck.transmissionType || '';
  document.getElementById('truck-fuel').value = truck.fuelType || 'Diesel';
  // Tires
  document.getElementById('truck-tire-size-front').value = truck.tireSizeFront || '';
  document.getElementById('truck-tire-size-rear').value = truck.tireSizeRear || '';
  document.getElementById('truck-notes').value = truck.notes || '';
  // Axles - load existing axles
  loadTruckAxles(truck.axles || []);
  // Load all doc types
  loadAllDocs('truck', truck);
  loadGeneralDocs('truck', truck);
  // Load flag settings
  const showFlag = truck.showInspectionFlag || false;
  document.getElementById('truck-show-inspection-flag').checked = showFlag;
  document.getElementById('truck-flag-days').value = truck.inspectionFlagDays || '30';
  document.getElementById('truck-flag-options').style.display = showFlag ? 'flex' : 'none';
  // Show reset button if flag was dismissed
  document.getElementById('truck-reset-flag-btn').style.display = truck.inspectionFlagDismissed ? 'block' : 'none';
  // Load inventory if feature is enabled
  loadTruckInventory(truck.inventoryItems || [], truck.inventoryEnabled || false);
  // Yard dropdown
  populateYardDropdown('truck-yard', false);
  document.getElementById('truck-yard').value = truck.yardId || '';

  document.getElementById('truck-active').checked = truck.active !== false;
  document.getElementById('truck-show-in-dispatch').checked = truck.showInDispatch !== false;
  // IFTA — lazy default: missing field treated as true for trucks (matches compliance.js filter)
  const iftaEl = document.getElementById('truck-ifta-registered');
  if (iftaEl) iftaEl.checked = truck.iftaRegistered !== false;
  const iftaYearEl = document.getElementById('truck-ifta-decal-year');
  if (iftaYearEl) iftaYearEl.value = truck.iftaDecalYear || '';
  // TASK-259 — Part 376 ownership + lease fields.
  const ownershipEl = document.getElementById('truck-ownership');
  if (ownershipEl) ownershipEl.value = truck.ownership || 'owned';
  const leaseStartEl = document.getElementById('truck-lease-start');
  if (leaseStartEl) leaseStartEl.value = truck.leaseStart || '';
  const leaseEndEl = document.getElementById('truck-lease-end');
  if (leaseEndEl) leaseEndEl.value = truck.leaseEnd || '';
  const leaseOwnerEl = document.getElementById('truck-lease-owner');
  if (leaseOwnerEl) leaseOwnerEl.value = truck.leaseOwnerName || '';
  const leaseRetainEl = document.getElementById('truck-lease-retain-until');
  if (leaseRetainEl) leaseRetainEl.value = truck.leaseRetainUntil || '';
  const leaseRestrictionsEl = document.getElementById('truck-lease-commodity-restrictions');
  if (leaseRestrictionsEl) leaseRestrictionsEl.value = truck.leaseCommodityRestrictions || '';
  const leaseOrigAddrEl = document.getElementById('truck-lease-original-address');
  if (leaseOrigAddrEl) leaseOrigAddrEl.value = truck.leaseOriginalAddress || '';
  const leaseSummaryModeEl = document.getElementById('truck-lease-summary-mode');
  if (leaseSummaryModeEl) leaseSummaryModeEl.checked = !!truck.leaseStatementSummaryMode;
  _renderLeaseStatementCurrent(truck);
  _toggleLeaseFieldsVisibility((truck.ownership || 'owned') !== 'owned');
  _toggleLeaseStatementBtnEnabled();
  _rentalLoad('truck', truck); // TASK-9869
  // HOTFIX 2026-11-04 — reused modal: drop any Smart Assist banner computed from the
  // previous record's values (J-1361's notes were offered as Auto-fix on J-1355).
  window.SmartAssistant?.clearAll?.(document.getElementById('modal-truck'));
  document.getElementById('modal-truck').classList.remove('hidden');
  window.attachAddressAutocomplete?.(document.getElementById('truck-inspection-location'));
}

// TASK-259 — Render the "current lease statement on file" line inside the
// in-cab statement block on the truck-edit modal. Shows the last generated
// PDF (with a download link) and the timestamp, or a hint if none yet.
function _renderLeaseStatementCurrent(truck) {
  const el = document.getElementById('truck-lease-statement-current');
  if (!el) return;
  if (truck?.leaseStatementPdfUrl) {
    const when = truck.leaseStatementGeneratedAt
      ? new Date(truck.leaseStatementGeneratedAt?.toDate?.() || truck.leaseStatementGeneratedAt).toLocaleString()
      : '';
    el.innerHTML = `<a ${window.StorageFile.linkAttrs(truck, { urlKey: 'leaseStatementPdfUrl' })} style="color:var(--primary);text-decoration:underline;">
      <span class="material-icons" style="font-size:14px;vertical-align:middle;">picture_as_pdf</span>
      Current statement on file</a> ${when ? `&middot; generated ${escapeHtml(when)}` : ''}`;
  } else {
    el.textContent = 'No statement on file yet — click below to generate.';
  }
}

// TASK-259 — Show/hide the lease detail fields when ownership type changes.
function _toggleLeaseFieldsVisibility(show) {
  const wrap = document.getElementById('truck-lease-fields');
  if (wrap) wrap.style.display = show ? 'block' : 'none';
}

// TASK-259 — Enable/disable the "Generate §376.12 Statement" button based
// on whether the truck has been persisted yet. Per UX Lead: don't let the
// user click and then surface a "save first" error toast — gray the button
// out and show an inline hint instead.
function _toggleLeaseStatementBtnEnabled() {
  const btn = document.getElementById('truck-lease-statement-generate-btn');
  const hint = document.getElementById('truck-lease-statement-generate-hint');
  const truckId = document.getElementById('truck-id')?.value;
  const persisted = !!truckId;
  if (btn) { btn.disabled = !persisted; btn.style.opacity = persisted ? '1' : '0.55'; }
  if (hint) { hint.style.display = persisted ? 'none' : 'block'; }
}

// TASK-259 — Fired by the ownership <select> onchange handler.
function onTruckOwnershipChange() {
  const v = document.getElementById('truck-ownership')?.value || 'owned';
  _toggleLeaseFieldsVisibility(v !== 'owned');
}

// TASK-259 — Compute leaseRetainUntil = leaseEnd + 1 calendar year (§379
// App A item A.5(f): "Until expiration or termination plus 1 year"). Use
// setFullYear+1 rather than +365d so leap-year-bracketing leases retain a
// full calendar year (COD Code Reviewer catch). Read-only field —
// auto-recalcs whenever the user edits the lease end date.
function recalcLeaseRetainUntil() {
  const endVal = document.getElementById('truck-lease-end')?.value;
  const retainEl = document.getElementById('truck-lease-retain-until');
  if (!retainEl) return;
  if (!endVal) { retainEl.value = ''; return; }
  const end = new Date(endVal + 'T00:00:00');
  if (Number.isNaN(end.getTime())) { retainEl.value = ''; return; }
  const retain = new Date(end.getTime());
  retain.setFullYear(retain.getFullYear() + 1);
  // ISO slice on a date constructed at local midnight is safe for the
  // tenant-display field (no TZ shift across the date line).
  retainEl.value = retain.toISOString().slice(0, 10);
}

// TASK-259 — Call the generatePart376Statement Cloud Function and refresh
// the in-cab statement preview line. Requires the truck to already be saved
// (we need the doc id + the persisted carrier/DOT context). Disables the
// button while in flight; surfaces server errors via toast.
async function generatePart376Statement() {
  const truckId = document.getElementById('truck-id')?.value;
  if (!truckId) {
    window.showToast?.('Save the vehicle first, then generate the statement.', 'warning');
    return;
  }
  const tenant = window.AppState?.tenant;
  if (!tenant) {
    window.showToast?.('No tenant context.', 'error');
    return;
  }
  const summaryMode = !!document.getElementById('truck-lease-summary-mode')?.checked;
  const btn = document.getElementById('truck-lease-statement-generate-btn');
  const origHtml = btn?.innerHTML;
  if (btn) { btn.disabled = true; btn.innerHTML = '<span class="material-icons">hourglass_top</span> Generating…'; }
  try {
    const fn = httpsCallable(functions, 'generatePart376Statement');
    const res = await fn({ tenantId: tenant, truckId, summaryMode });
    const data = res?.data || res;
    if (!data?.ok) throw new Error(data?.error || 'Statement generation failed');
    window.showToast?.('§376.12 statement generated.', 'success');
    // Refresh the in-modal preview line. Reload truck doc from state if the
    // subscription already pushed it; otherwise patch the local truck object
    // optimistically so the link appears without a round-trip.
    const truck = RosterState.trucks.find(t => t.id === truckId) || {};
    truck.leaseStatementPdfUrl = data.url;
    truck.leaseStatementPdfPath = data.path;
    truck.leaseStatementGeneratedAt = new Date();
    _renderLeaseStatementCurrent(truck);
    window.logAudit?.('roster', 'LEASE_STATEMENT_GENERATED', {
      entityType: 'truck', entityId: truckId, summaryMode, source: 'client-trigger',
    });
  } catch (err) {
    console.error('[generatePart376Statement]', err);
    window.showToast?.(`Statement generation failed: ${err.message || err}`, 'error');
  } finally {
    if (btn) { btn.disabled = false; btn.innerHTML = origHtml; }
  }
}

// TASK-258 §396.19 — upload the staged Inspector Qualification doc (if any)
// to Storage and write its object PATH (never a token URL) onto the person doc. Handles three
// cases: (a) new file picked → upload + write, (b) user clicked Remove on an
// existing doc → delete + clear url, (c) no change → no-op. Silent on
// missing inputs; non-fatal on Storage errors (the metadata save already
// succeeded by the time this runs).
async function _uploadInspectorDocIfPending(tenant, personId) {
  if (!personId) return;
  try {
    const pending = window.__pendingInspectorDoc || {};
    const fileInput = document.getElementById('person-inspector-doc-file');
    const file = fileInput?.files?.[0] || null;

    // Case 2: explicit remove — delete from storage if we have the path and
    // clear the doc fields.
    // (A legacy URL-only doc's fields are cleared by savePerson's remove branch.)
    if (pending.remove && pending.existingPath) {
      try {
        const { deleteObject } = await __ddChunkWrap(import('../firebase-config.js?v=1868'));
        await deleteObject(ref(storage, pending.existingPath)).catch(() => {});
      } catch (_) {}
      await updateDoc(doc(db, 'tenants', tenant, 'people', personId), {
        'inspectorQualification.docUrl': null,
        'inspectorQualification.docPath': null,
        'inspectorQualification.docName': null,
      });
      return;
    }

    if (!file) return; // no-op: no new file picked

    // Validate size + type client-side (storage rule enforces server-side
    // but a 10MB pre-upload reject saves bandwidth + gives faster UX).
    if (file.size > 10 * 1024 * 1024) {
      window.showToast?.('Inspector doc too large (max 10MB)', 'error');
      return;
    }
    if (!/^application\/pdf|^image\//.test(file.type)) {
      window.showToast?.('Inspector doc must be a PDF or image', 'error');
      return;
    }

    const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g, '_').slice(0, 80);
    const stamped = `${Date.now()}_${safeName}`;
    const path = `tenants/${tenant}/inspectorQuals/${personId}/${stamped}`;
    const storageRef = ref(storage, path);
    await uploadBytes(storageRef, file, { contentType: file.type });
    // TASK-10086-D (audit 🟡12) — store the PATH only. A getDownloadURL token
    // link on the broadcast people doc let anyone who can read that doc open the
    // file past the module-gated inspectorQuals storage rule. Readers open by
    // docPath (StorageFile / the compliance byte-stream callables); a replace
    // also nulls a legacy docUrl so the old token stops being handed out.
    await updateDoc(doc(db, 'tenants', tenant, 'people', personId), {
      'inspectorQualification.docUrl': null,
      'inspectorQualification.docPath': path,
      'inspectorQualification.docName': file.name,
    });
  } catch (err) {
    console.error('[Inspector doc upload]', err);
    window.showToast?.('Inspector doc upload failed: ' + (err?.message || 'unknown'), 'error');
  }
}

// Module-scoped handler for the "Remove" button in the inspector-doc UI.
function _clearInspectorDoc() {
  const existingEl = document.getElementById('person-inspector-doc-existing');
  if (existingEl) existingEl.style.display = 'none';
  const fileInput = document.getElementById('person-inspector-doc-file');
  if (fileInput) fileInput.value = '';
  if (window.__pendingInspectorDoc) window.__pendingInspectorDoc.remove = true;
}

// TASK-258 sub-item — auto-calculate Annual Vehicle Inspection expiry from
// the inspection date if the user only filled in the date and left expiry
// blank. Per 49 CFR §396.17 the next inspection must be done within 12
// months of the last one — the form remains valid up to 14 months via the
// §396.23 sliding-window rule, but 12 months is the standard signal date.
// If the user explicitly entered an expiry (e.g. using the 14-month grace),
// we respect their input and don't overwrite.
function _aviAutoExpiry(inspectionDate, currentExpiry) {
  if (currentExpiry) return currentExpiry; // user-set takes precedence
  if (!inspectionDate) return null;
  try {
    const d = new Date(inspectionDate);
    if (isNaN(d.getTime())) return null;
    d.setMonth(d.getMonth() + 12);
    return d.toISOString().slice(0, 10); // YYYY-MM-DD
  } catch (_) {
    return null;
  }
}

// TASK-9433 — soft vehicle cap. Call after a save that left a vehicle ACTIVE
// that wasn't active before (created, or switched back on). `savedId` is that
// vehicle's doc id when known, so it is counted exactly once whether or not the
// live snapshot has picked it up yet. Warns only (window.noteVehicleSoftCap in
// app.js); never blocks the save.
function _vehicleSoftCapAfterSave(savedId) {
  try {
    const fleet = [...(RosterState.trucks || []), ...(RosterState.trailers || [])];
    const others = fleet.filter((v) => v && v.active !== false && (!savedId || v.id !== savedId)).length;
    window.noteVehicleSoftCap?.(others + 1);
  } catch (_) { /* a warning must never break a save */ }
}

async function saveTruck(e) {
  e?.preventDefault?.();
  const form = document.getElementById('form-truck');
  if (!window.validateRequiredFields(form)) return;
  // TASK-1444 — preset/import rows were STAMPED, not measured. Saying yes here
  // is the review; nothing regulatory gets persisted on convenience alone.
  if (!(await _paxleConfirmUnreviewed('truck'))) {
    document.getElementById('truck-heavyhaul-section')?.setAttribute('open', 'open');
    return;
  }

  // TASK-4529 — a unit/nickname that equals ANOTHER fleet asset's doc id would
  // shadow that asset in ref resolution (the doc-id pass wins). The server
  // trigger reverts such writes after the fact; block them HERE so the user
  // sees a clear error instead of a silently-reverted save. Keep in sync with
  // _fleetUnitCollisionDecision (functions/src/index.js).
  // 🔴 TASK-4718 — ONLY JUDGE A VALUE THAT CHANGED. The server has always had
  // `if (val === prev) continue;` and this half never did, so the two drifted
  // exactly where the comment above swears they are in sync. The cost was not
  // theoretical: trippin-trucking has a legacy truck whose DOC ID is "16"
  // (nickname Seagull, unit 09) and a different truck whose UNIT is "16"
  // (Peacock). Peacock's unit was already stored that way, so every save of
  // that vehicle was refused forever -- the vehicle was BRICKED. Alyse hit it
  // trying to attach insurance documents, an edit that never touched the unit
  // field at all. A pre-existing collision is data to migrate, not a reason to
  // lock someone out of an unrelated field.
  {
    const _selfId = document.getElementById('truck-id').value;
    const _fleetDocs = [...(RosterState.trucks || []), ...(RosterState.trailers || [])];
    const _collides = (v) => v && _fleetDocs.some(x => String(x.id) === v && x.id !== _selfId);
    const _self = _fleetDocs.find(x => String(x.id) === String(_selfId));
    const _prevOf = (f) => String(_self?.[f] ?? '').trim();
    const _bad = [
      ['unit', document.getElementById('truck-unit').value.trim()],
      ['nickname', document.getElementById('truck-nickname').value.trim()],
    ].filter(([f, v]) => v && v !== _prevOf(f))   // unchanged => the server skips it, so we do too
      .map(([, v]) => v)
      .find(_collides);
    if (_bad) {
      window.showToast?.(`"${_bad}" can't be used here — it matches another vehicle's internal ID, and jobs referencing that vehicle could resolve to this one. Pick a different value.`, 'error', null, 9000);
      return;
    }
  }
  // TASK-9378 COD — a photo still converting settles OUT HERE, not inside the
  // guard (its 20s hand-off would re-open Save mid-conversion → duplicate doc).
  if (!(await _settlePhotoBeforeSave(RosterState.truckPhoto, 'saveTruck', 'btn-save-truck'))) return;
  if (_regExpiryPromptBlocks('truck')) return; // TASK-9814 — soft prompt, "Save anyway" re-enters
  return runGuardedSave('saveTruck', async (markDone) => {
  const id = document.getElementById('truck-id').value;
  const _clamp = window.clampText || ((s, n) => s && s.length > n ? s.slice(0, n) : s || '');
  const data = {
    unit: _clamp(document.getElementById('truck-unit').value.trim(), 100),
    nickname: _clamp(document.getElementById('truck-nickname').value.trim(), 100),
    type: document.getElementById('truck-type').value,
    make: document.getElementById('truck-make').value.trim(),
    model: document.getElementById('truck-model').value.trim(),
    year: parseInt(document.getElementById('truck-year').value) || null,
    vins: getTruckVins(),
    vin: getTruckVins()[0] || '', // backward compat — first VIN as primary
    // Weight Ratings
    gvwr: parseInt(document.getElementById('truck-gvwr').value) || null,
    gcwr: parseInt(document.getElementById('truck-gcwr').value) || null,
    curbWeight: parseInt(document.getElementById('truck-curb-weight').value) || null,
    payloadCapacity: parseInt(document.getElementById('truck-payload-capacity').value) || null,
    gawrFront: parseInt(document.getElementById('truck-gawr-front').value) || null,
    gawrRear: parseInt(document.getElementById('truck-gawr-rear').value) || null,
    // TASK-512 — OS/OW dimensions (decimal ft) + axle config. Net-new, additive.
    length: parseFloat(document.getElementById('truck-length').value) || null,
    width: parseFloat(document.getElementById('truck-width').value) || null,
    height: parseFloat(document.getElementById('truck-height').value) || null,
    axleCount: parseInt(document.getElementById('truck-axle-count').value) || null,
    axleSpacing: document.getElementById('truck-axle-spacing').value.trim(),
    // TASK-525 — Heavy Haul / Permit Specs. registeredWeight = the PLATE/licensed
    // weight (distinct from gvwr/gcwr DOT ratings). permitAxles[] = this unit's OWN
    // axles for OS/OW filing (NO loaded weight; coupling gap lives on the rig template).
    registeredWeight: parseInt(document.getElementById('truck-registered-weight')?.value) || null,
    permitAxles: getPermitAxles('truck'),
    // Registration & Compliance — multi-plate support
    licensePlates: getTruckPlates(),
    licensePlate: getTruckPlates()[0]?.plate || '', // backward compat
    licensePlateState: getTruckPlates()[0]?.state || '', // backward compat
    registrationNumber: document.getElementById('truck-reg-number').value.trim(),
    // TASK-9814 follow-up: "Doesn't expire" clears the date (a stale date would keep alerting).
    registrationExpiry: document.getElementById('truck-reg-no-expiry')?.checked === true ? null : (document.getElementById('truck-reg-expiry').value || null),
    registrationNoExpiry: document.getElementById('truck-reg-no-expiry')?.checked === true, // TASK-9814
    irpExpiry: document.getElementById('truck-irp-expiry')?.value || null, // TASK-9813
    // Insurance
    insuranceCompany: document.getElementById('truck-ins-company').value.trim(),
    insurancePolicy: document.getElementById('truck-ins-policy').value.trim(),
    insuranceExpiry: document.getElementById('truck-ins-expiry').value || null,
    insurancePhone: document.getElementById('truck-ins-phone').value.trim(),
    // Annual Inspection — TASK-258 sub-item: auto-calc expiry from date if
    // the user only entered the date. 12-month rule per §396.17.
    inspectionDate: document.getElementById('truck-inspection-date').value || null,
    inspectionExpiry: _aviAutoExpiry(
      document.getElementById('truck-inspection-date').value || null,
      document.getElementById('truck-inspection-expiry').value || null,
    ),
    inspectionLocation: document.getElementById('truck-inspection-location').value.trim(),
    // Engine/Drivetrain
    engineMake: document.getElementById('truck-engine-make').value.trim(),
    engineModel: document.getElementById('truck-engine-model').value.trim(),
    engineSerial: document.getElementById('truck-engine-serial').value.trim(),
    transmissionType: document.getElementById('truck-transmission').value,
    fuelType: document.getElementById('truck-fuel').value,
    // Axles
    axles: getTruckAxles(),
    // Tires
    tireSizeFront: document.getElementById('truck-tire-size-front').value.trim(),
    tireSizeRear: document.getElementById('truck-tire-size-rear').value.trim(),
    notes: document.getElementById('truck-notes').value.trim(),
    // Inventory
    inventoryEnabled: document.getElementById('truck-inventory-enabled')?.checked ?? false,
    inventoryItems: getTruckInventory(),
    active: document.getElementById('truck-active')?.checked !== false,
    showInDispatch: document.getElementById('truck-show-in-dispatch')?.checked !== false,
    iftaRegistered: document.getElementById('truck-ifta-registered')?.checked !== false,
    iftaDecalYear: parseInt(document.getElementById('truck-ifta-decal-year')?.value) || null,
    // TASK-259 — Part 376 ownership + lease fields. Only persist lease detail
    // when ownership !== 'owned' (keeps owned-truck docs clean).
    ownership: document.getElementById('truck-ownership')?.value || 'owned',
    leaseStart: (document.getElementById('truck-ownership')?.value || 'owned') !== 'owned'
      ? (document.getElementById('truck-lease-start')?.value || null) : null,
    leaseEnd: (document.getElementById('truck-ownership')?.value || 'owned') !== 'owned'
      ? (document.getElementById('truck-lease-end')?.value || null) : null,
    leaseRetainUntil: (document.getElementById('truck-ownership')?.value || 'owned') !== 'owned'
      ? (document.getElementById('truck-lease-retain-until')?.value || null) : null,
    leaseOwnerName: (document.getElementById('truck-ownership')?.value || 'owned') !== 'owned'
      ? _clamp((document.getElementById('truck-lease-owner')?.value || '').trim(), 200) : '',
    leaseCommodityRestrictions: (document.getElementById('truck-ownership')?.value || 'owned') !== 'owned'
      ? _clamp((document.getElementById('truck-lease-commodity-restrictions')?.value || '').trim(), 300) : '',
    leaseOriginalAddress: (document.getElementById('truck-ownership')?.value || 'owned') !== 'owned'
      ? _clamp((document.getElementById('truck-lease-original-address')?.value || '').trim(), 300) : '',
    leaseStatementSummaryMode: !!document.getElementById('truck-lease-summary-mode')?.checked,
    updatedAt: new Date()
  };

  // Yard
  const truckYardEl = document.getElementById('truck-yard');
  if (truckYardEl) {
    data.yardId = truckYardEl.value || '';
    data.yardName = data.yardId ? getYardLabel(data.yardId) : '';
  }

  // Warn if year seems unreasonable
  const currentYear = new Date().getFullYear();
  if (data.year && data.year > currentYear + VEHICLE_SPEC_RANGES.year.maxYearsAhead) { // TASK-9715 shared bound
    const proceed = confirm(`Year ${data.year} is more than a year in the future. Did you mean ${currentYear} or ${currentYear + 1}?`);
    if (!proceed) return;
  }
  if (data.year && data.year < VEHICLE_SPEC_RANGES.year.min) { // TASK-9715 shared bound
    const proceed = confirm(`Year ${data.year} seems very old for a commercial vehicle. Continue anyway?`);
    if (!proceed) return;
  }

  // Soft-duplicate check: if user just created a truck with the same unit in the
  // last 3 seconds, confirm before creating another. This catches the "rapid tap
  // during a hang" scenario without blocking legitimate duplicates across yards.
  if (!id && data.unit) {
    const lastSaved = window.__rosterLastSavedTruck;
    if (lastSaved && lastSaved.unit === data.unit && (Date.now() - lastSaved.ts) < 3000) {
      const proceed = confirm(`You just created a vehicle with unit "${data.unit}" a moment ago. Are you sure you want to add another?`);
      if (!proceed) return;
    }
  }

    const tenant = window.AppState?.tenant || 'demo';

    // If type is "trailer", move to trailers collection
    if (data.type === 'trailer') {
      // Prepare trailer data (trailers have fewer fields)
      const trailerData = {
        unit: data.unit,
        nickname: data.nickname,
        type: 'flatbed', // Default trailer type, user can edit in trailers
        make: data.make,
        year: data.year,
        vin: data.vin,
        licensePlates: data.licensePlates,
        licensePlate: data.licensePlate,
        licensePlateState: data.licensePlateState,
        registrationExpiry: data.registrationExpiry,
        registrationNoExpiry: data.registrationNoExpiry, // TASK-9814
        inspectionDate: data.inspectionDate,
        inspectionExpiry: data.inspectionExpiry,
        notes: data.notes,
        active: data.active !== false,
        createdAt: new Date(),
        updatedAt: new Date()
      };

      // Add to trailers collection
      const _convNoExpBefore = id ? await _regNoExpiryBefore('trucks', id, RosterState.trucks.find(t => t.id === id)?.registrationNoExpiry) : false; // TASK-9935
      const _convRef = await withWriteTimeout(addDoc(collection(db, 'tenants', tenant, 'trailers'), trailerData), 15000, 'add trailer');
      // TASK-9935 — the flag moves with the unit; audit only if it changed on the way.
      _auditRegNoExpiry('trailer', _convRef?.id, trailerData.unit, _convNoExpBefore, trailerData.registrationNoExpiry);
      // TASK-9433 — a NEW vehicle filed as a trailer counts toward the cap
      // (a truck moved to trailers is the same vehicle — no change).
      if (!id && trailerData.active) _vehicleSoftCapAfterSave(_convRef?.id);

      // If editing existing truck, delete from trucks collection
      if (id) {
        await withWriteTimeout(deleteDoc(doc(db, 'tenants', tenant, 'trucks', id)), 15000, 'remove truck');
        window.showToast?.('Moved to Trailers', 'success');
      } else {
        window.showToast?.('Added to Trailers', 'success');
      }

      window.__rosterLastSavedTruck = { unit: data.unit, ts: Date.now() };
      closeModal('truck');
      return;
    }

    // Upload all pending docs for each type. TASK-259 — `lease` joins the
    // standard truck doc types and rides the same /tenants/{tid}/truck-docs/
    // Storage path so existing storage.rules apply unchanged.
    const docTypes = ['inspection', 'registration', 'insurance', 'lease', 'irp']; // TASK-9813 — + IRP cab card
    // Snapshot the pending count BEFORE upload so we can audit-log only when
    // a new lease was actually uploaded this save (existing+new is misleading).
    const _leasePendingCount = RosterState.truckDocs.lease.pending.length;
    for (const docType of docTypes) {
      const uploaded = await uploadPendingDocs('truck', docType, tenant);
      const existing = RosterState.truckDocs[docType].existing;
      data[`${docType}Docs`] = [...existing, ...uploaded];
    }

    // Upload general documents
    const generalUploaded = await uploadGeneralDocs('truck', tenant);
    data.documents = [...RosterState.truckGeneralDocs.existing, ...generalUploaded];

    // Save flag settings
    const showInspectionFlag = document.getElementById('truck-show-inspection-flag').checked;
    data.showInspectionFlag = showInspectionFlag;
    data.inspectionFlagDays = parseInt(document.getElementById('truck-flag-days').value) || 30;
    // Preserve dismissed state if not resetting
    const existingTruck = RosterState.trucks.find(t => t.id === id);
    if (existingTruck?.inspectionFlagDismissed && data.showInspectionFlag) {
      data.inspectionFlagDismissed = existingTruck.inspectionFlagDismissed;
    }

    // TASK-340 Slice A — upload photo before the Firestore write so we can
    // stamp the URL on the doc atomically. Returns existing URL if no new
    // file picked, null if user removed an existing photo (handled below
    // by writing null over the field).
    const truckPhotoUrl = await uploadTruckPhoto(tenant);
    data.photoUrl = truckPhotoUrl || null;

    // TASK-9869 — rented-in markers (no money) onto the unit doc.
    if (!_rentalApplyMarkers('truck', data)) return;

    // Normal truck save
    if (id) {
      const _noExpBefore = await _regNoExpiryBefore('trucks', id, existingTruck?.registrationNoExpiry); // TASK-9935
      await withWriteTimeout(updateDoc(doc(db, 'tenants', tenant, 'trucks', id), data), 15000, 'update truck');
      window.logAudit?.('roster', 'UPDATE_TRUCK', { truckId: id, unit: data.unit, name: data.name, activeChanged: existingTruck?.active !== data.active });
      _auditRegNoExpiry('truck', id, data.unit, _noExpBefore, data.registrationNoExpiry); // TASK-9935
      // TASK-259 — ownership-type change is regulatorily significant
      // (triggers/clears the Part 376 lease-on-file obligation). Audit it
      // separately for forensic visibility.
      if (existingTruck && existingTruck.ownership !== data.ownership) {
        window.logAudit?.('roster', 'TRUCK_OWNERSHIP_CHANGED', {
          entityType: 'truck', entityId: id, unit: data.unit,
          before: { ownership: existingTruck.ownership || 'owned' },
          after: { ownership: data.ownership },
        });
      }
      window.showToast?.('Truck updated', 'success');
      if (existingTruck && existingTruck.active === false && data.active) _vehicleSoftCapAfterSave(id); // TASK-9433
    } else {
      data.createdAt = new Date();
      const newRef = await withWriteTimeout(addDoc(collection(db, 'tenants', tenant, 'trucks'), data), 15000, 'create truck');
      window.logAudit?.('roster', 'CREATE_TRUCK', { truckId: newRef.id, unit: data.unit, name: data.name });
      _auditRegNoExpiry('truck', newRef.id, data.unit, false, data.registrationNoExpiry); // TASK-9935
      // TASK-259 — stash the new doc id so the LEASE_UPLOAD audit below can
      // reference the real truckId (not the unit string) on create flow.
      window.__rosterLastSavedTruckId = newRef.id;
      // TASK-259 — new truck created with a non-owned ownership type. Mark
      // it so the auditLog reflects the initial Part 376 obligation.
      if (data.ownership && data.ownership !== 'owned') {
        window.logAudit?.('roster', 'TRUCK_OWNERSHIP_CHANGED', {
          entityType: 'truck', entityId: newRef.id, unit: data.unit,
          before: { ownership: 'owned' }, after: { ownership: data.ownership },
        });
      }
      window.showToast?.('Truck added', 'success');
      if (data.active) _vehicleSoftCapAfterSave(newRef.id); // TASK-9433
      window.__rosterLastSavedTruck = { unit: data.unit, ts: Date.now() };
      // Dispatch event for tutorial system
      window.dispatchEvent(new CustomEvent('roster:truck-saved'));
    }

    // TASK-259 — log lease-PDF uploads after the save succeeds.
    // COD Code Reviewer fix: use the real truck doc id on create flow
    // (the new ref id), not the unit string, so auditLog rows can be
    // back-traced to the actual truck doc.
    if (_leasePendingCount > 0) {
      window.logAudit?.('roster', 'LEASE_UPLOAD', {
        entityType: 'truck',
        entityId: id || (window.__rosterLastSavedTruckId || null),
        unit: data.unit,
        uploadedCount: _leasePendingCount,
        totalOnFile: data.leaseDocs?.length || 0,
        ownership: data.ownership,
      });
    }

    // TASK-9869 — rate / terms / contract (owner or billing only).
    await _rentalSaveRecord('truck', id || window.__rosterLastSavedTruckId, data);

    // Clear all docs
    clearAllDocs('truck');
    clearGeneralDocs('truck');

    closeModal('truck');
    renderTrucksList();
  }, { buttonId: 'btn-save-truck', restoreLabel: 'Save' });
}

// ============================================
// TASK-9869 S1 — rented-IN equipment (a unit rented FROM a vendor)
// ============================================
// Not `isRental` (equipment rented OUT / the quantity ledger) and not the
// Part 376 lease fields (the compliance record). Two stores, on purpose:
//   - the unit doc (trucks/trailers/assets) carries the NON-money markers:
//     rentedIn, rentalVendor, rentalVendorUnit, rentalReturnBy. Driver-PIN
//     sessions read those collections, so nothing priced goes there. The
//     expiry list (get_fleet_expirations), the daily compliance alert and the
//     connector's find_crew_and_equipment read these markers.
//   - tenants/{t}/vehicleRentals/{kind}_{unitId} carries the rate, terms and
//     contract file PATHS; rules = owner or billing module only, and the
//     contract files sit under storage rental-contracts/ with the same gate.
// "Read terms from contract" (readRentalContract callable) only FILLS blank
// inputs, marked "Read from contract — check it"; nothing saves until Save.
const RENTAL_RATE_UNITS = [['day', 'per day'], ['week', 'per week'], ['28day', 'per 28 days'], ['month', 'per month']];
const RENTAL_MAX_FILES = 5;
// TASK-9880 — billed to the customer. Same modes / cap as opsReports.js
// OR_BILL_MODES / OR_MAX_MARKUP_PCT (the reader); company setting
// settings.rentalPassThrough, off by default.
const RENTAL_BILL_MODES = ['none', 'cost', 'markup', 'rate'];
const RENTAL_MAX_MARKUP_PCT = 1000;
const _rentalPassThroughOn = () => window.AppState?.settings?.rentalPassThrough === true;
const RENTAL_IDLE_DAYS = 3; // TASK-9879 — mirrors functions/src/services/rentalAlerts.js IDLE_DAYS
const RENTAL_MAX_BYTES = 7 * 1024 * 1024;
const RENTAL_DATE_RE = /^\d{4}-\d{2}-\d{2}$/;
const RentalState = { truck: null, trailer: null, asset: null };
let _rvP = null;
const _rentalEl = (id) => document.getElementById(id);
const _rentalClean = (v, n) => String(v == null ? '' : v).replace(/[\x00-\x1f\x7f]/g, ' ').replace(/\s+/g, ' ').trim().slice(0, n);

// Owner (ownerId) or a billing-module login. Modules, never roles. The rules
// decide again (vehicleRentals / rental-contracts); this only hides inputs a
// save would be refused on.
function _rentalCanSeeMoney() {
  const t = window.AppState?.tenant;
  if (!t || t === 'demo') return false;
  const uid = auth?.currentUser?.uid;
  if (uid && window.AppState?.tenantData?.ownerId === uid) return true;
  const m = window.AppState?.memberAccess;
  return !!(m && Array.isArray(m.modules) && m.modules.includes('billing') && m.viewUploadOnly !== true && m.readOnly !== true);
}

function _rentalTodayKey() {
  return new Date().toLocaleDateString('en-CA');
}

function _rentalBadge(u) {
  if (!u || u.rentedIn !== true) return '';
  const rb = typeof u.rentalReturnBy === 'string' && RENTAL_DATE_RE.test(u.rentalReturnBy) ? u.rentalReturnBy : '';
  const today = _rentalTodayKey();
  const past = !!rb && rb < today;
  const days = rb ? Math.round((Date.parse(rb + 'T12:00:00Z') - Date.parse(today + 'T12:00:00Z')) / 86400000) : null;
  const soon = !past && days != null && days <= 7;
  const style = past ? 'background:#FEE2E2;color:#991B1B;border-color:#FCA5A5;'
    : (soon ? 'background:#FEF3C7;color:#92400E;border-color:#FCD34D;' : 'background:#DBEAFE;color:#1E40AF;border-color:#93C5FD;');
  const vendor = u.rentalVendor ? ` · ${escapeHtml(_rentalClean(u.rentalVendor, 40))}` : '';
  // TASK-9879 — called off, waiting on the vendor's pickup; no date = open-ended.
  const co = typeof u.rentalCallOffDate === 'string' && RENTAL_DATE_RE.test(u.rentalCallOffDate) ? u.rentalCallOffDate : '';
  if (co) {
    return ` <span class="badge badge-yard" data-rental-badge="1" title="Called off ${co} — untick Rented in once the vendor picks it up" style="background:#FEF3C7;color:#92400E;border-color:#FCD34D;">Rented${vendor} · called off ${co.slice(5)}</span>`;
  }
  const when = rb ? (past ? ' · past return date' : ` · back ${rb.slice(5)}`) : ' · no return date';
  return ` <span class="badge badge-yard" data-rental-badge="1" title="Rented in from a vendor${rb ? ` — return by ${rb}` : ' — no return-by date on file'}" style="${style}">Rented${vendor}${when}</span>`;
}

function _rentalSectionHtml(p) {
  const opts = RENTAL_RATE_UNITS.map(([v, l]) => `<option value="${v}">${l}</option>`).join('');
  return `
          <!-- TASK-9869 — rented in from a vendor -->
          <details class="form-collapsible" id="${p}-rental-section">
            <summary class="form-collapsible-header">
              <span class="material-icons">key</span>
              <span>Rented in (from a vendor)</span>
              <span class="material-icons collapse-icon">expand_more</span>
            </summary>
            <div class="form-collapsible-content">
              <label class="checkbox-label" style="margin-bottom:4px;">
                <input type="checkbox" id="${p}-rented-in" onchange="window.RosterModule.rentalToggle('${p}')">
                <span>This unit is rented from a vendor</span>
              </label>
              <p class="form-hint">Not the Part 376 lease. Untick it when the vendor picks the unit up.</p>
              <div id="${p}-rental-fields" style="display:none;">
                <div class="form-row">
                  <div class="form-group">
                    <label for="${p}-rental-vendor">Vendor</label>
                    <select id="${p}-rental-vendor-pick" aria-label="Pick a CRM vendor" style="display:none;margin-bottom:6px;" onchange="window.RosterRentalVendor?.pick('${p}')"></select>
                    <input type="text" id="${p}-rental-vendor" maxlength="80" placeholder="Elite Rentals" oninput="window.RosterRentalVendor?.status('${p}')">
                    <div id="${p}-rental-vendor-crm" class="form-hint"></div>
                  </div>
                  <div class="form-group">
                    <label for="${p}-rental-vendor-unit">Vendor's unit #</label>
                    <input type="text" id="${p}-rental-vendor-unit" maxlength="60" placeholder="VT1017">
                  </div>
                </div>
                <div class="form-row">
                  <div class="form-group">
                    <label for="${p}-rental-return-by">Return by</label>
                    <input type="date" id="${p}-rental-return-by">
                  </div>
                  <div class="form-group">
                    <label for="${p}-rental-call-off">Called off on</label>
                    <input type="date" id="${p}-rental-call-off">
                  </div>
                </div>
                <p class="form-hint">People with the Dispatch or Compliance notification tag are warned before the return date, when the unit sits ${RENTAL_IDLE_DAYS} days with no job, before a 28-day or monthly period rolls, and when it was called off but is still ticked as rented in. With no return date they get a weekly reminder to add one.</p>
                <div id="${p}-rental-money" style="display:none;">
                  <div id="${p}-rental-prefill" role="status" style="display:none;margin:0 0 12px;padding:10px 12px;border:1px solid #FCD34D;background:#FFFBEB;color:#92400E;border-radius:var(--radius-md);font-size:0.875rem;"></div>
                  <div class="form-row">
                    <div class="form-group">
                      <label for="${p}-rental-rate">Rate ($)</label>
                      <input type="number" id="${p}-rental-rate" min="0" step="0.01" inputmode="decimal" placeholder="6250">
                    </div>
                    <div class="form-group">
                      <label for="${p}-rental-rate-unit">Per</label>
                      <select id="${p}-rental-rate-unit"><option value="">Pick one</option>${opts}</select>
                    </div>
                  </div>
                  <div class="form-row">
                    <div class="form-group">
                      <label for="${p}-rental-start">Rent start</label>
                      <input type="date" id="${p}-rental-start">
                    </div>
                    <div class="form-group">
                      <label for="${p}-rental-min-term">Minimum term</label>
                      <input type="text" id="${p}-rental-min-term" maxlength="60" placeholder="28 days">
                    </div>
                  </div>
                  <div class="form-row">
                    <div class="form-group">
                      <label for="${p}-rental-project">Project</label>
                      <input type="text" id="${p}-rental-project" maxlength="200" placeholder="Line 5">
                    </div>
                    <div class="form-group">
                      <label for="${p}-rental-contract-no">Contract #</label>
                      <input type="text" id="${p}-rental-contract-no" maxlength="60">
                    </div>
                  </div>
                  <div class="form-group">
                    <label for="${p}-rental-return-address">Return address</label>
                    <input type="text" id="${p}-rental-return-address" maxlength="300">
                  </div>
                  <!-- TASK-9880 — billed to the customer (company setting, off by default) -->
                  <div id="${p}-rental-bill" style="display:none;">
                    <div class="form-row">
                      <div class="form-group">
                        <label for="${p}-rental-bill-mode">Billed to the customer</label>
                        <select id="${p}-rental-bill-mode" onchange="window.RosterModule.rentalBillModeChange('${p}')">
                          <option value="">Not set</option>
                          <option value="cost">At cost (pass-through)</option>
                          <option value="markup">Cost + markup %</option>
                          <option value="rate">Our own rate</option>
                          <option value="none">Not billed to the customer</option>
                        </select>
                      </div>
                      <div class="form-group" id="${p}-rental-bill-markup-wrap" style="display:none;">
                        <label for="${p}-rental-bill-markup">Markup (%)</label>
                        <input type="number" id="${p}-rental-bill-markup" min="0" max="${RENTAL_MAX_MARKUP_PCT}" step="0.01" inputmode="decimal" placeholder="15">
                      </div>
                    </div>
                    <div class="form-row" id="${p}-rental-bill-rate-wrap" style="display:none;">
                      <div class="form-group">
                        <label for="${p}-rental-bill-rate">Customer rate ($)</label>
                        <input type="number" id="${p}-rental-bill-rate" min="0" step="0.01" inputmode="decimal" placeholder="7500">
                      </div>
                      <div class="form-group">
                        <label for="${p}-rental-bill-rate-unit">Per</label>
                        <select id="${p}-rental-bill-rate-unit"><option value="">Pick one</option>${opts}</select>
                      </div>
                    </div>
                    <p class="form-hint">What the customer pays for this rental, kept apart from the vendor's rate above. BI → Reports → Rental Register shows it next to the cost, by project. It does not put anything on an invoice.</p>
                  </div>
                  <p class="form-hint" id="${p}-rental-bill-off" style="display:none;">To record what the customer pays for this rental, turn on "Show what customers are billed for rentals" in BI → Reports → Rental Register.</p>
                  <div class="form-group">
                    <label>Rental contract</label>
                    <div class="doc-list" id="${p}-rental-files"></div>
                    <input type="file" id="${p}-rental-file-input" accept=".pdf,image/jpeg,image/png,image/webp" style="display:none" onchange="window.RosterModule.rentalPickFile(event, '${p}')">
                    <div style="display:flex;gap:8px;flex-wrap:wrap;">
                      <button type="button" class="btn btn-secondary btn-sm" onclick="document.getElementById('${p}-rental-file-input').click()"><span class="material-icons">add</span> Add contract</button>
                      <button type="button" class="btn btn-secondary btn-sm" id="${p}-rental-read" disabled onclick="window.RosterModule.rentalReadContract('${p}')"><span class="material-icons">auto_awesome</span> Read terms from contract</button>
                    </div>
                    <p class="form-hint">PDF or photo, up to 7MB. Smart Tools fills the empty boxes above; check every one before you save.</p>
                  </div>
                </div>
                <p class="form-hint" id="${p}-rental-money-hint" style="display:none;">The rate and the contract are visible to the owner and Billing logins only.</p>
              </div>
            </div>
          </details>`;
}

const RENTAL_MONEY_INPUTS = [
  ['rate', 'rental-rate', 'Rate'], ['rateUnit', 'rental-rate-unit', 'Per'], ['rentStart', 'rental-start', 'Rent start'],
  ['minimumTerm', 'rental-min-term', 'Minimum term'], ['project', 'rental-project', 'Project'],
  ['contractNumber', 'rental-contract-no', 'Contract #'], ['returnAddress', 'rental-return-address', 'Return address'],
];
const RENTAL_MARKER_INPUTS = [
  ['vendorName', 'rental-vendor', 'Vendor'], ['vendorUnit', 'rental-vendor-unit', "Vendor's unit #"], ['returnBy', 'rental-return-by', 'Return by'],
];

function rentalToggle(p) {
  const on = !!_rentalEl(`${p}-rented-in`)?.checked;
  const f = _rentalEl(`${p}-rental-fields`);
  if (f) f.style.display = on ? '' : 'none';
}

// TASK-9880 — show the markup or own-rate inputs for the picked bill mode.
function rentalBillModeChange(p) {
  const m = _rentalEl(`${p}-rental-bill-mode`)?.value || '';
  const mk = _rentalEl(`${p}-rental-bill-markup-wrap`);
  const rt = _rentalEl(`${p}-rental-bill-rate-wrap`);
  if (mk) mk.style.display = m === 'markup' ? '' : 'none';
  if (rt) rt.style.display = m === 'rate' ? '' : 'none';
}

// TASK-9880 — the bill fields to save, or { error } when a typed value is not a number.
// Only called with the setting on; off = the record's bill fields are left untouched.
function _rentalBillFields(p) {
  const raw = _rentalEl(`${p}-rental-bill-mode`)?.value || '';
  const mode = RENTAL_BILL_MODES.includes(raw) ? raw : null;
  const out = { billMode: mode, billMarkupPct: null, billRate: null, billRateUnit: null };
  if (mode === 'markup') {
    const pct = _rentalMoneyValue(_rentalEl(`${p}-rental-bill-markup`)?.value);
    if (pct === undefined || (pct != null && pct > RENTAL_MAX_MARKUP_PCT)) return { error: `the markup was not saved: type a percent from 0 to ${RENTAL_MAX_MARKUP_PCT}, like 15.` };
    out.billMarkupPct = pct;
  } else if (mode === 'rate') {
    const r = _rentalMoneyValue(_rentalEl(`${p}-rental-bill-rate`)?.value);
    if (r === undefined) return { error: 'the customer rate was not saved: type it as a number like 7500 or 7500.50.' };
    const u = _rentalEl(`${p}-rental-bill-rate-unit`)?.value || '';
    out.billRate = r;
    out.billRateUnit = RENTAL_RATE_UNITS.some(([v]) => v === u) ? u : null;
  }
  return out;
}

function _rentalClearPrefillMark(input) {
  input.style.background = '';
  input.style.borderColor = '';
}

async function _rentalLoad(p, unit) {
  // loaded: the record read finished (found or not). COD 10/15 Code Reviewer —
  // a save before the read lands, or after it failed, must not blank the rate
  // and contract list, so _rentalSaveRecord refuses until loaded is true.
  const st = { unitId: unit?.id || null, record: null, pending: [], prefilled: new Set(), wasOn: unit?.rentedIn === true, loaded: !unit?.id, v0: unit?.rentalVendor || '', vid0: unit?.rentalVendorId || null,
    wasCallOff: (unit?.rentedIn === true && RENTAL_DATE_RE.test(unit?.rentalCallOffDate || '')) ? unit.rentalCallOffDate : null };
  RentalState[p] = st;
  const cb = _rentalEl(`${p}-rented-in`);
  if (!cb) return;
  cb.checked = st.wasOn;
  _rentalEl(`${p}-rental-vendor`).value = unit?.rentalVendor || '';
  // TASK-10212 — CRM vendor pick-list + link state: the LAZY rosterRentalVendor.js.
  (_rvP = _rvP || __ddChunkWrap(import('./rosterRentalVendor.js?v=1')).catch((e) => { _rvP = null; console.warn('[Roster] rosterRentalVendor', e?.message); })).then((m) => m && RentalState[p] === st && m.fill(p, st, RosterState));
  _rentalEl(`${p}-rental-vendor-unit`).value = unit?.rentalVendorUnit || '';
  _rentalEl(`${p}-rental-return-by`).value = unit?.rentalReturnBy || '';
  const co = _rentalEl(`${p}-rental-call-off`);
  if (co) co.value = st.wasCallOff || '';
  for (const [, id] of RENTAL_MONEY_INPUTS) { const i = _rentalEl(`${p}-${id}`); if (i) i.value = ''; }
  for (const [, id] of RENTAL_MONEY_INPUTS.concat(RENTAL_MARKER_INPUTS)) { const i = _rentalEl(`${p}-${id}`); if (i) _rentalClearPrefillMark(i); }
  const banner = _rentalEl(`${p}-rental-prefill`);
  if (banner) { banner.style.display = 'none'; banner.innerHTML = ''; }
  const money = _rentalCanSeeMoney();
  _rentalEl(`${p}-rental-money`).style.display = money ? '' : 'none';
  _rentalEl(`${p}-rental-money-hint`).style.display = money ? 'none' : '';
  // TASK-9880 — billed-to-customer inputs: money viewers only, setting on only.
  const billBox = _rentalEl(`${p}-rental-bill`);
  if (billBox) billBox.style.display = money && _rentalPassThroughOn() ? '' : 'none';
  const billOff = _rentalEl(`${p}-rental-bill-off`); // COD UX 10/24 — say where the switch is
  if (billOff) billOff.style.display = money && !_rentalPassThroughOn() ? '' : 'none';
  for (const id of ['rental-bill-mode', 'rental-bill-markup', 'rental-bill-rate', 'rental-bill-rate-unit']) { const i = _rentalEl(`${p}-${id}`); if (i) i.value = ''; }
  rentalBillModeChange(p);
  const sec = _rentalEl(`${p}-rental-section`);
  if (sec) sec.open = st.wasOn;
  rentalToggle(p);
  _rentalRenderFiles(p);
  if (!money || !unit?.id) return;
  try {
    const snap = await getDoc(doc(db, 'tenants', window.AppState.tenant, 'vehicleRentals', `${p}_${unit.id}`));
    if (RentalState[p] !== st) return;
    st.loaded = true;
    if (!snap.exists()) return;
    st.record = snap.data() || {};
    for (const [k, id] of RENTAL_MONEY_INPUTS) {
      const i = _rentalEl(`${p}-${id}`);
      if (i && st.record[k] != null) i.value = String(st.record[k]);
    }
    // TASK-9880 — billed to the customer.
    const bm = RENTAL_BILL_MODES.includes(st.record.billMode) ? st.record.billMode : '';
    const setV = (id, v) => { const i = _rentalEl(`${p}-${id}`); if (i && v != null) i.value = String(v); };
    setV('rental-bill-mode', bm);
    setV('rental-bill-markup', st.record.billMarkupPct);
    setV('rental-bill-rate', st.record.billRate);
    setV('rental-bill-rate-unit', st.record.billRateUnit);
    rentalBillModeChange(p);
    _rentalRenderFiles(p);
  } catch (err) {
    console.warn('[Roster] rental record not loaded', err?.code || err?.message);
  }
}

function _rentalRenderFiles(p) {
  const st = RentalState[p];
  const box = _rentalEl(`${p}-rental-files`);
  if (!box || !st) return;
  const saved = Array.isArray(st.record?.contractFiles) ? st.record.contractFiles : [];
  const rows = saved.map((f, i) => `<div class="doc-item" style="display:flex;align-items:center;gap:8px;">
      <span class="material-icons" style="font-size:18px;">description</span>
      <button type="button" class="btn-link" style="flex:1;text-align:left;" onclick="window.RosterModule.rentalOpenFile('${p}', ${i})">${escapeHtml(_rentalClean(f && f.name, 120) || 'Contract')}</button>
    </div>`);
  st.pending.forEach((f, i) => rows.push(`<div class="doc-item" style="display:flex;align-items:center;gap:8px;">
      <span class="material-icons" style="font-size:18px;">upload_file</span>
      <span style="flex:1;color:var(--text-secondary);">${escapeHtml(_rentalClean(f.name, 120))} (saved when you press Save)</span>
      <button type="button" class="btn-icon" title="Remove" onclick="window.RosterModule.rentalRemovePending('${p}', ${i})"><span class="material-icons">close</span></button>
    </div>`));
  box.innerHTML = rows.join('');
  const readBtn = _rentalEl(`${p}-rental-read`);
  if (readBtn) readBtn.disabled = st.pending.length === 0;
}

function rentalPickFile(e, p) {
  const st = RentalState[p];
  const input = e.target;
  const files = Array.from(input.files || []);
  input.value = '';
  if (!st) return;
  const okTypes = ['application/pdf', 'image/jpeg', 'image/png', 'image/webp'];
  for (const f of files) {
    const have = (st.record?.contractFiles?.length || 0) + st.pending.length;
    if (have >= RENTAL_MAX_FILES) { window.showToast?.(`At most ${RENTAL_MAX_FILES} contract files per unit.`, 'error'); break; }
    if (!okTypes.includes(f.type)) { window.showToast?.('Add the contract as a PDF or a JPG / PNG / WebP photo.', 'error'); continue; }
    if (f.size > RENTAL_MAX_BYTES) { window.showToast?.('That file is over 7MB. Try a smaller scan.', 'error'); continue; }
    st.pending.push(f);
  }
  _rentalRenderFiles(p);
}

function rentalRemovePending(p, idx) {
  const st = RentalState[p];
  if (!st) return;
  st.pending.splice(idx, 1);
  _rentalRenderFiles(p);
}

async function rentalOpenFile(p, idx) {
  const f = RentalState[p]?.record?.contractFiles?.[idx];
  if (!f || typeof f.path !== 'string') return;
  try {
    const url = await getDownloadURL(ref(storage, f.path));
    window.open(url, '_blank', 'noopener');
  } catch (err) {
    window.showToast?.('That contract could not be opened. Only the owner and Billing logins can open rental contracts.', 'error');
  }
}

function _rentalFileToBase64(file) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(String(r.result || '').split(',')[1] || '');
    r.onerror = () => reject(r.error || new Error('read failed'));
    r.readAsDataURL(file);
  });
}

async function rentalReadContract(p) {
  const st = RentalState[p];
  const file = st?.pending?.[st.pending.length - 1];
  if (!file) { window.showToast?.('Add the contract first.', 'error'); return; }
  const btn = _rentalEl(`${p}-rental-read`);
  const label = btn ? btn.innerHTML : '';
  if (btn) { btn.disabled = true; btn.textContent = 'Reading…'; }
  try {
    const base64 = await _rentalFileToBase64(file);
    const res = await httpsCallable(functions, 'readRentalContract')({ unitKind: p, unitId: st.unitId || 'new', file: { base64, mimeType: file.type || '', name: String(file.name || '').slice(0, 120) } });
    if (RentalState[p] !== st) return;
    const out = res?.data || {};
    const f = out.fields || {};
    const filled = [];
    const kept = [];
    for (const [k, id, lbl] of RENTAL_MARKER_INPUTS.concat(RENTAL_MONEY_INPUTS)) {
      if (f[k] == null || f[k] === '') continue;
      const input = _rentalEl(`${p}-${id}`);
      if (!input) continue;
      const val = String(f[k]);
      const cur = String(input.value || '').trim();
      if (!cur) {
        input.value = val;
        input.style.background = '#FFFBEB';
        input.style.borderColor = '#F59E0B';
        input.addEventListener('input', () => _rentalClearPrefillMark(input), { once: true });
        st.prefilled.add(k);
        filled.push(lbl);
      } else if (cur !== val) {
        kept.push(`${lbl}: the contract says "${val}"; kept "${cur}"`);
      }
    }
    const cb = _rentalEl(`${p}-rented-in`);
    if (cb && !cb.checked && filled.length) { cb.checked = true; rentalToggle(p); }
    const banner = _rentalEl(`${p}-rental-prefill`);
    if (banner) {
      const lines = [];
      lines.push(`<strong>Read from contract — check it.</strong> ${filled.length ? `Filled: ${escapeHtml(filled.join(', '))}.` : 'Nothing new was filled.'}`);
      if (f.sourceText) lines.push(`Rate line read: "${escapeHtml(_rentalClean(f.sourceText, 160))}"`);
      (out.warnings || []).slice(0, 8).forEach((w) => lines.push(escapeHtml(_rentalClean(w, 200))));
      kept.slice(0, 8).forEach((w) => lines.push(escapeHtml(w)));
      if (!f.returnBy) lines.push('No return date was read. Open-ended rentals have none; type the date you plan to call it off so the warning can fire.');
      banner.innerHTML = lines.map((l) => `<div>${l}</div>`).join('');
      banner.style.display = '';
    }
  } catch (err) {
    window.showToast?.(err?.message || 'The contract could not be read. Type the terms in.', 'error');
  } finally {
    if (btn) { btn.innerHTML = label; btn.disabled = !(RentalState[p]?.pending?.length); }
  }
}

// Called inside each save BEFORE the unit write. Puts the non-money markers on
// `data`. Returns false (and toasts) when the form can't be saved as is.
function _rentalApplyMarkers(p, data) {
  const cb = _rentalEl(`${p}-rented-in`);
  const st = RentalState[p];
  if (!cb || !st) return true;
  const on = cb.checked;
  if (!on && !st.wasOn) return true; // never rented in: leave the unit doc untouched
  const vendor = _rentalClean(_rentalEl(`${p}-rental-vendor`).value, 80);
  const vunit = _rentalClean(_rentalEl(`${p}-rental-vendor-unit`).value, 60);
  const rb = _rentalEl(`${p}-rental-return-by`).value;
  if (on && !vendor) {
    window.showToast?.('Rented in: enter the vendor, or untick "This unit is rented from a vendor".', 'error');
    _rentalEl(`${p}-rental-section`).open = true;
    _rentalEl(`${p}-rental-vendor`).focus();
    return false;
  }
  const start = _rentalEl(`${p}-rental-start`)?.value || '';
  if (on && RENTAL_DATE_RE.test(rb) && RENTAL_DATE_RE.test(start) && rb < start) {
    window.showToast?.('Rented in: the return date is before the rent start.', 'error');
    return false;
  }
  const co = _rentalEl(`${p}-rental-call-off`)?.value || '';
  if (on && RENTAL_DATE_RE.test(co) && RENTAL_DATE_RE.test(start) && co < start) {
    window.showToast?.('Rented in: the call-off date is before the rent start.', 'error');
    return false;
  }
  data.rentedIn = on;
  data.rentalVendor = on ? vendor : null;
  // TASK-10212 — the CRM vendor id (this company's vendor list only); chunk not run = keep the old link while the name is unchanged.
  const rv = window.RosterRentalVendor?.idFor(p, vendor);
  data.rentalVendorId = on ? (rv !== undefined ? rv : (vendor === st.v0 ? st.vid0 : null)) : null;
  data.rentalVendorUnit = on ? (vunit || null) : null;
  data.rentalReturnBy = on && RENTAL_DATE_RE.test(rb) ? rb : null;
  // TASK-9879 — call-off date (drives the "pickup not confirmed" warning) and
  // the day it came on rent (anchors the idle warning when the rent start is
  // not on the money record). Neither is money; both clear when it goes back.
  data.rentalCallOffDate = on && RENTAL_DATE_RE.test(co) ? co : null;
  if (!on) data.rentalSince = null;
  else if (!st.wasOn) data.rentalSince = RENTAL_DATE_RE.test(start) && start <= _rentalTodayKey() ? start : _rentalTodayKey();
  return true;
}

function _rentalMoneyValue(v) {
  const s = String(v == null ? '' : v).trim();
  if (!s) return null;
  if (!/^\d+(\.\d{1,2})?$/.test(s)) return undefined;
  const n = Number(s);
  return Number.isFinite(n) && n <= 10000000 ? n : undefined;
}

// Called AFTER the unit write succeeded. Owner / billing only; anyone else's
// save keeps just the markers. A failure here never undoes the unit save.
async function _rentalSaveRecord(p, unitId, data) {
  const st = RentalState[p];
  if (!st || !unitId || !_rentalCanSeeMoney()) return;
  const on = data.rentedIn === true;
  if (!on && !st.wasOn && !st.record) return;
  const tenant = window.AppState?.tenant;
  const key = `${p}_${unitId}`;
  if (!st.loaded) {
    window.showToast?.('Unit saved. The rental rate and contract were not saved because the rental record had not finished loading. Open the unit again and save.', 'error');
    return;
  }
  try {
    const rate = _rentalMoneyValue(_rentalEl(`${p}-rental-rate`)?.value);
    if (rate === undefined) { window.showToast?.('Unit saved. The rental rate was not saved: type it as a number like 6250 or 6250.50.', 'error'); return; }
    // TASK-9880 — billed to the customer: written only while the company setting
    // is on (off = the record's bill fields stay as they were, merge below).
    const bill = _rentalPassThroughOn() ? _rentalBillFields(p) : null;
    if (bill && bill.error) { window.showToast?.(`Unit saved. The rental record was not saved: ${bill.error}`, 'error'); return; }
    const files = Array.isArray(st.record?.contractFiles) ? st.record.contractFiles.slice(0, RENTAL_MAX_FILES) : [];
    for (const f of st.pending) {
      if (files.length >= RENTAL_MAX_FILES) break;
      const safe = String(f.name || 'contract').replace(/[^A-Za-z0-9._-]+/g, '_').slice(-80);
      const path = `tenants/${tenant}/rental-contracts/${key}/${Date.now()}_${safe}`;
      await uploadBytes(ref(storage, path), f, { contentType: f.type || 'application/pdf' });
      files.push({ path, name: _rentalClean(f.name, 120), uploadedAt: new Date().toISOString() });
    }
    const val = (id, n) => _rentalClean(_rentalEl(`${p}-${id}`)?.value, n) || null;
    const rateUnit = _rentalEl(`${p}-rental-rate-unit`)?.value || null;
    const rentStart = _rentalEl(`${p}-rental-start`)?.value || '';
    const rec = {
      unitKind: p,
      unitId,
      unitLabel: _rentalClean(data.unit || data.nickname || data.name || '', 80),
      status: on ? 'on_rent' : 'returned',
      vendorName: on ? data.rentalVendor : (st.record?.vendorName ?? null),
      vendorId: on ? (data.rentalVendorId || null) : (st.record?.vendorId ?? null),
      vendorUnit: on ? data.rentalVendorUnit : (st.record?.vendorUnit ?? null),
      returnBy: on ? data.rentalReturnBy : (st.record?.returnBy ?? null),
      // TASK-9879 — mirrors of the unit markers (the unit doc is the source the
      // daily warning reads); pickup confirmed = the day it was unticked.
      callOffDate: on ? (data.rentalCallOffDate || null) : (st.record?.callOffDate ?? st.wasCallOff ?? null),
      pickupConfirmedDate: on ? null : (st.wasOn ? _rentalTodayKey() : (st.record?.pickupConfirmedDate ?? null)),
      rate,
      rateUnit: RENTAL_RATE_UNITS.some(([v]) => v === rateUnit) ? rateUnit : null,
      rentStart: RENTAL_DATE_RE.test(rentStart) ? rentStart : null,
      minimumTerm: val('rental-min-term', 60),
      project: val('rental-project', 200),
      contractNumber: val('rental-contract-no', 60),
      returnAddress: val('rental-return-address', 300),
      contractFiles: files,
      prefilledFromContract: Array.from(st.prefilled),
      ...(bill || {}),
      updatedBy: auth.currentUser.uid,
      updatedAt: Timestamp.now(),
      ...(on ? {} : { returnedAt: Timestamp.now() }),
    };
    await withWriteTimeout(setDoc(doc(db, 'tenants', tenant, 'vehicleRentals', key), rec, { merge: true }), 15000, 'save rental');
    // Audit: what changed, never the rate itself.
    window.logAudit?.('roster', 'RENTAL_RECORD_SAVED', {
      entityType: `${p}Rental`, entityId: unitId, unit: rec.unitLabel, status: rec.status,
      returnBy: rec.returnBy || null, rateChanged: (st.record?.rate ?? null) !== rec.rate,
      // TASK-9880 — whether the customer billing changed; never the amounts.
      billingChanged: !!bill && ['billMode', 'billMarkupPct', 'billRate', 'billRateUnit'].some((k) => (st.record?.[k] ?? null) !== rec[k]),
      filesAdded: st.pending.length, prefilled: rec.prefilledFromContract.length,
    });
  } catch (err) {
    console.error('[Roster] rental record save failed', err);
    window.showToast?.('Unit saved, but the rental record could not be saved. Open the unit and try again.', 'error');
  }
}

// ============================================
// TASK-9814 — registration expiry soft prompt + "No reg. expiry" badge
// ============================================
// A registration card on file with no expiry date and not "Doesn't expire" never
// alerts. The daily cron emails about it weekly (the real control); this is the
// in-form nudge. SOFT: it fires only when a NEW registration file is pending in
// this edit (never on legacy records, so an unrelated save of an old trailer is
// never blocked), and "Save anyway" always goes through.
const _regPromptBypass = { truck: false, trailer: false };

function _vehicleRegExpiryMissing(v) {
  if (!v) return false;
  const docs = Array.isArray(v.registrationDocs) ? v.registrationDocs : [];
  const exp = typeof v.registrationExpiry === 'string' ? v.registrationExpiry.trim() : v.registrationExpiry;
  return docs.length > 0 && !exp && v.registrationNoExpiry !== true;
}

function _noRegExpiryBadge(v) {
  if (!_vehicleRegExpiryMissing(v)) return '';
  return ' <span class="dvir-flag-label" title="A registration card is on file but no expiration date is entered, so no expiry alert can be sent" style="background:#FFFBEB;color:#92400E;border-color:#FDE68A;">No reg. expiry</span>';
}

// TASK-9814 follow-up — "Doesn't expire" disables the date field (and ticking it clears the date);
// unticking re-enables it. Called on toggle, after form.reset() (clearAllDocs) and on edit-load (loadAllDocs).
function _regNoExpirySync(prefix, clearOnTick) {
  const box = document.getElementById(`${prefix}-reg-no-expiry`);
  const dateEl = document.getElementById(`${prefix}-reg-expiry`);
  if (!box || !dateEl) return;
  if (box.checked && clearOnTick) { if (dateEl.value) dateEl.dataset.prevValue = dateEl.value; dateEl.value = ''; }
  // Unticking in the same edit puts back the date that was there (stashed on tick or on edit-load).
  if (!box.checked && clearOnTick && !dateEl.value && dateEl.dataset.prevValue) dateEl.value = dateEl.dataset.prevValue;
  dateEl.disabled = box.checked;
}

function regNoExpiryToggle(prefix) {
  _regNoExpirySync(prefix, true);
  regPromptHide(prefix);
}

function regPromptHide(prefix, resetBypass) {
  const el = document.getElementById(`${prefix}-reg-expiry-prompt`);
  if (el) { el.hidden = true; el.style.display = 'none'; }
  if (resetBypass) {
    _regPromptBypass[prefix] = false;
    window.RegNoExpiryGuard?.reset(document.getElementById(`${prefix}-reg-no-expiry`)); // TASK-9935 — no stale confirm on the next unit
  }
}

// TASK-9935 — what the "Doesn't expire" confirm weighs, read from the OPEN form
// (a GVWR typed a second ago counts) plus any IRP cab card on file or pending.
window.RegNoExpiryGuard?.register('roster-truck', () => {
  const v = (id) => document.getElementById(id)?.value || '';
  const irp = RosterState.truckDocs?.irp || {};
  return {
    gvwr: v('truck-gvwr'), gcwr: v('truck-gcwr'), registeredWeight: v('truck-registered-weight'),
    irpExpiry: v('truck-irp-expiry'), irpDocs: [...(irp.existing || []), ...(irp.pending || [])],
  };
});
window.RegNoExpiryGuard?.register('roster-trailer', () => {
  const v = (id) => document.getElementById(id)?.value || '';
  return { gvwr: v('trailer-gvwr'), registeredWeight: v('trailer-registered-weight') };
});

// TASK-9935 — a "Doesn't expire" flip silences every registration-expiry alert
// for the unit, so record who / old / new (logAudit stamps `user` + timestamp).
// A failed audit write is said out loud, never swallowed (COD Code Reviewer).
async function _auditRegNoExpiry(kind, id, unit, before, after) {
  if (!id || (before === true) === (after === true)) return;
  const ok = await window.logAudit?.('roster', 'REGISTRATION_NO_EXPIRY_CHANGED', {
    entityType: kind, entityId: id, unit: String(unit || ''), source: 'roster',
    before: { registrationNoExpiry: before === true }, after: { registrationNoExpiry: after === true },
  });
  if (ok === false) window.showToast?.('Saved, but the change log for "Doesn\'t expire" did not record. Tell your admin which unit you changed.', 'warning', null, 9000);
}

// TASK-9935 (COD) — the "before" for that audit comes from the doc as it is NOW,
// not RosterState: a flip made in another tab / by the compliance editor may not
// have reached this snapshot yet. Falls back to the snapshot if the read fails.
async function _regNoExpiryBefore(coll, id, cached) {
  if (!id) return false;
  try {
    const snap = await getDoc(doc(db, 'tenants', window.AppState?.tenant || 'demo', coll, id));
    return snap.exists() ? snap.data().registrationNoExpiry === true : cached === true;
  } catch (_) { return cached === true; }
}

// true = the prompt is showing and the save should stop here.
function _regExpiryPromptBlocks(prefix) {
  if (_regPromptBypass[prefix]) { _regPromptBypass[prefix] = false; return false; }
  const stateKey = prefix === 'truck' ? 'truckDocs' : 'trailerDocs';
  const pendingNew = RosterState[stateKey]?.registration?.pending?.length || 0;
  if (!pendingNew) return false;
  const dateEl = document.getElementById(`${prefix}-reg-expiry`);
  if (dateEl && dateEl.value) return false;
  if (document.getElementById(`${prefix}-reg-no-expiry`)?.checked) return false;
  const section = document.getElementById(`${prefix}-registration-section`);
  if (section) section.open = true;
  const el = document.getElementById(`${prefix}-reg-expiry-prompt`);
  if (el) { el.hidden = false; el.style.display = 'flex'; }
  if (dateEl) {
    try { dateEl.scrollIntoView({ block: 'center', behavior: 'smooth' }); } catch (_) { /* old browsers */ }
    dateEl.focus();
  }
  return true;
}

function saveAnywayRegPrompt(prefix) {
  _regPromptBypass[prefix] = true;
  regPromptHide(prefix);
  return prefix === 'truck' ? saveTruck() : saveTrailer();
}

// ============================================
// GENERALIZED DOCUMENT HANDLING
// ============================================

// Handle document upload for any prefix/docType
function handleDocUpload(e, prefix, docType) {
  const files = e.target.files;
  if (!files?.length) return;

  const stateKey = prefix === 'truck' ? 'truckDocs' : 'trailerDocs';
  const docState = RosterState[stateKey][docType];
  const maxDocs = 5;

  for (const file of files) {
    // Check limit
    if (docState.pending.length + docState.existing.length >= maxDocs) {
      window.showToast?.(`Maximum ${maxDocs} documents allowed`, 'error');
      break;
    }

    // Validate file type (include HEIC for iPhone photos)
    const validTypes = ['application/pdf', 'image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/heic', 'image/heif'];
    const imageExts = ['.jpg', '.jpeg', '.png', '.gif', '.webp', '.heic', '.heif', '.bmp', '.tiff', '.tif', '.pdf'];
    const nameLower = file.name.toLowerCase();
    if (!validTypes.includes(file.type) && !imageExts.some(ext => nameLower.endsWith(ext))) {
      window.showToast?.('Please upload PDF or image files only', 'error');
      continue;
    }

    // Validate file size (7.5MB max)
    if (file.size > 7.5 * 1024 * 1024) {
      window.showToast?.('File too large (max 7.5MB)', 'error');
      continue;
    }

    // Add to pending docs
    docState.pending.push({
      file,
      previewUrl: file.type.startsWith('image/') ? URL.createObjectURL(file) : null
    });
  }

  // Clear input for re-upload of same file
  e.target.value = '';
  renderDocs(prefix, docType);
  updateDocCount(prefix, docType);
}

// Render documents list for any prefix/docType
function renderDocs(prefix, docType) {
  const container = document.getElementById(`${prefix}-${docType}-doc-list`);
  if (!container) return;

  const stateKey = prefix === 'truck' ? 'truckDocs' : 'trailerDocs';
  const docState = RosterState[stateKey][docType];

  const existingHtml = docState.existing.map((doc, idx) => `
    <div class="doc-item uploaded">
      <span class="material-icons doc-icon">${doc.name?.endsWith('.pdf') ? 'picture_as_pdf' : 'image'}</span>
      <div class="doc-info">
        <a ${window.StorageFile.linkAttrs(doc)} class="doc-name">${escapeHtml(doc.name || 'Document')}</a>
        <span class="doc-status uploaded-status"><span class="material-icons" style="font-size: 14px; vertical-align: middle;">check_circle</span> Uploaded ${doc.uploadedAt ? new Date(doc.uploadedAt).toLocaleDateString() : ''}</span>
      </div>
      <button type="button" class="btn-icon btn-icon-danger" onclick="window.RosterModule.removeExistingDoc('${prefix}', '${docType}', ${idx})" title="Remove">
        <span class="material-icons">close</span>
      </button>
    </div>
  `).join('');

  const pendingHtml = docState.pending.map((doc, idx) => `
    <div class="doc-item pending">
      <span class="material-icons doc-icon">${doc.file.type === 'application/pdf' ? 'picture_as_pdf' : 'image'}</span>
      <div class="doc-info">
        <span class="doc-name">${escapeHtml(doc.file.name)}</span>
        <span class="doc-status pending-status">Uploads when you save</span>
      </div>
      <button type="button" class="btn-icon btn-icon-danger" onclick="window.RosterModule.removePendingDoc('${prefix}', '${docType}', ${idx})" title="Remove">
        <span class="material-icons">close</span>
      </button>
    </div>
  `).join('');

  container.innerHTML = existingHtml + pendingHtml;
}

// Update document count display
function updateDocCount(prefix, docType) {
  const stateKey = prefix === 'truck' ? 'truckDocs' : 'trailerDocs';
  const docState = RosterState[stateKey][docType];
  const count = docState.pending.length + docState.existing.length;
  const countEl = document.getElementById(`${prefix}-${docType}-count`);
  const uploadBtn = document.getElementById(`${prefix}-${docType}-upload-btn`);

  if (countEl) {
    countEl.textContent = `(${count}/5)`;
    countEl.classList.toggle('at-limit', count >= 5);
  }
  if (uploadBtn) {
    uploadBtn.disabled = count >= 5;
  }
}

// Remove pending doc
function removePendingDoc(prefix, docType, idx) {
  const stateKey = prefix === 'truck' ? 'truckDocs' : 'trailerDocs';
  const docState = RosterState[stateKey][docType];
  const doc = docState.pending[idx];
  if (doc?.previewUrl) {
    URL.revokeObjectURL(doc.previewUrl);
  }
  docState.pending.splice(idx, 1);
  renderDocs(prefix, docType);
  updateDocCount(prefix, docType);
}

// Remove existing doc
function removeExistingDoc(prefix, docType, idx) {
  const stateKey = prefix === 'truck' ? 'truckDocs' : 'trailerDocs';
  const docState = RosterState[stateKey][docType];
  docState.existing.splice(idx, 1);
  renderDocs(prefix, docType);
  updateDocCount(prefix, docType);
}

// Clear all docs for a prefix
function clearAllDocs(prefix) {
  regPromptHide(prefix, true); // TASK-9814
  _regNoExpirySync(prefix); // TASK-9814 follow-up — form.reset() unticks the box but leaves the date disabled
  { const d = document.getElementById(`${prefix}-reg-expiry`); if (d) d.dataset.prevValue = ''; }
  const stateKey = prefix === 'truck' ? 'truckDocs' : 'trailerDocs';
  // TASK-259 — trucks gain a 4th doc type (`lease`) for Part 376 lease PDFs.
  const docTypes = prefix === 'truck'
    ? ['inspection', 'registration', 'insurance', 'lease', 'irp']
    : ['inspection', 'registration', 'insurance'];
  for (const docType of docTypes) {
    // Revoke any preview URLs
    RosterState[stateKey][docType].pending.forEach(doc => {
      if (doc?.previewUrl) URL.revokeObjectURL(doc.previewUrl);
    });
    RosterState[stateKey][docType].pending = [];
    RosterState[stateKey][docType].existing = [];
    renderDocs(prefix, docType);
    updateDocCount(prefix, docType);
  }
}

// Load all docs from an entity into state
function loadAllDocs(prefix, entity) {
  regPromptHide(prefix, true); // TASK-9814
  _regNoExpirySync(prefix); // TASK-9814 follow-up
  const stateKey = prefix === 'truck' ? 'truckDocs' : 'trailerDocs';
  // TASK-259 — trucks gain a 4th doc type (`lease`) for Part 376 lease PDFs.
  const docTypes = prefix === 'truck'
    ? ['inspection', 'registration', 'insurance', 'lease', 'irp']
    : ['inspection', 'registration', 'insurance'];
  for (const docType of docTypes) {
    RosterState[stateKey][docType].pending = [];
    RosterState[stateKey][docType].existing = entity[`${docType}Docs`] || [];
    renderDocs(prefix, docType);
    updateDocCount(prefix, docType);
  }
}

// Upload pending docs and return array of uploaded doc metadata
async function uploadPendingDocs(prefix, docType, tenant) {
  const stateKey = prefix === 'truck' ? 'truckDocs' : 'trailerDocs';
  const docState = RosterState[stateKey][docType];
  const storagePath = prefix === 'truck' ? 'truck-docs' : 'trailer-docs';
  const uploadedDocs = [];

  for (const pending of docState.pending) {
    try {
      const path = `tenants/${tenant}/${storagePath}/${Date.now()}_${pending.file.name}`;
      const storageRef = ref(storage, path);
      await uploadBytes(storageRef, pending.file, { contentType: pending.file.type || 'application/octet-stream' });
      // TASK-10086-N/P (audit 🟠7) — PATH only, every slot: the vehicle doc is
      // readable by every driver session, and a getDownloadURL token link on it
      // opens the file past the driver door (_driverVehicleDocClasses) and
      // storage.rules. Every reader opens by path (StorageFile; Compliance tab;
      // the partner portal through getPartnerComplianceDocBytes).
      uploadedDocs.push({
        path,
        name: pending.file.name,
        uploadedAt: new Date().toISOString()
      });
    } catch (uploadErr) {
      console.error(`[Roster] Error uploading ${docType} doc:`, uploadErr);
    }
  }

  return uploadedDocs;
}

// Toggle flag options visibility
function toggleFlagOptions(prefix) {
  const checkbox = document.getElementById(`${prefix}-show-inspection-flag`);
  const options = document.getElementById(`${prefix}-flag-options`);
  if (options) {
    options.style.display = checkbox.checked ? 'flex' : 'none';
  }
}

// Reset inspection flag (clear dismissed state)
function resetInspectionFlag(prefix) {
  const id = document.getElementById(`${prefix}-id`)?.value;
  if (!id) return;

  const tenant = window.AppState?.tenant || 'demo';
  const collectionName = prefix === 'truck' ? 'trucks' : 'trailers';

  updateDoc(doc(db, 'tenants', tenant, collectionName, id), {
    inspectionFlagDismissed: null
  }).then(() => {
    window.showToast?.('Inspection flag reset', 'success');
    document.getElementById(`${prefix}-reset-flag-btn`).style.display = 'none';
  }).catch(err => {
    console.error('[Roster] Error resetting flag:', err);
    window.showToast?.('Error resetting flag', 'error');
  });
}

// Check if inspection flag should show
function shouldShowInspectionFlag(entity) {
  if (!entity.showInspectionFlag) return false;

  // If flag was dismissed, don't show
  if (entity.inspectionFlagDismissed) return false;

  // Flag if no inspection date is entered at all
  if (!entity.inspectionExpiry) return true;

  const expiryDate = new Date(entity.inspectionExpiry);
  const today = new Date();
  const daysUntilExpiry = Math.ceil((expiryDate - today) / (1000 * 60 * 60 * 24));
  const flagDays = entity.inspectionFlagDays || 30;

  return daysUntilExpiry <= flagDays;
}

// ============================================
// GENERAL DOCUMENTS (additional docs beyond inspection/registration/insurance)
// ============================================

const GENERAL_DOC_PRESETS = [
  { value: 'tero', label: 'TERO Documents' },
  { value: 'service-agreement', label: 'Service Agreement' },
  { value: 'permit', label: 'Permit' },
  { value: 'lease', label: 'Lease Agreement' },
  { value: 'warranty', label: 'Warranty' },
  { value: 'custom', label: 'Other (custom label)' }
];

// TASK-1297 — central prefix→state/storage maps so truck, trailer, and asset
// (equipment) all share the one generic-documents flow. 'asset' uploads to the
// dedicated tenants/{tid}/asset-docs/ Storage path (see storage.rules).
const GENERAL_DOC_STATE_KEYS = {
  truck: 'truckGeneralDocs',
  trailer: 'trailerGeneralDocs',
  asset: 'assetGeneralDocs'
};
const GENERAL_DOC_STORAGE_PATHS = {
  truck: 'truck-docs',
  trailer: 'trailer-docs',
  asset: 'asset-docs'
};

function showAddGeneralDoc(prefix) {
  const stateKey = GENERAL_DOC_STATE_KEYS[prefix] || 'trailerGeneralDocs';
  // "Show on Job Board" surfaces a doc on the driver-facing vehicle info card;
  // internal equipment/assets don't appear on the job board, so hide it there.
  const showJobBoardField = prefix !== 'asset';
  const total = RosterState[stateKey].pending.length + RosterState[stateKey].existing.length;
  if (total >= 20) {
    window.showToast?.('Maximum 20 documents allowed', 'error');
    return;
  }

  const today = new Date().toISOString().split('T')[0];
  const optionsHtml = GENERAL_DOC_PRESETS.map(p => `<option value="${p.value}">${p.label}</option>`).join('');

  const overlay = document.createElement('div');
  overlay.className = 'modal';
  overlay.id = 'modal-add-general-doc';
  overlay.onclick = (e) => { if (e.target === overlay) overlay.remove(); };
  overlay.innerHTML = `
    <div class="modal-content" style="max-width:420px;">
      <div class="modal-header">
        <h3><span class="material-icons">note_add</span> Add Document</h3>
        <button class="modal-close" onclick="document.getElementById('modal-add-general-doc').remove()">
          <span class="material-icons">close</span>
        </button>
      </div>
      <div class="modal-body" style="padding:16px;">
        <div class="form-group">
          <label>Document Type</label>
          <select id="general-doc-type" class="form-control" onchange="
            const custom = document.getElementById('general-doc-custom-label');
            custom.style.display = this.value === 'custom' ? 'block' : 'none';
          ">
            ${optionsHtml}
          </select>
        </div>
        <div class="form-group" id="general-doc-custom-label" style="display:none;">
          <label>Custom Label</label>
          <input type="text" class="form-control" id="general-doc-label" maxlength="50" placeholder="e.g. Oversize Load Permit">
        </div>
        <div class="form-group">
          <label>Expiry Date <span style="font-weight:normal;color:var(--text-muted);">(optional)</span></label>
          <input type="date" class="form-control" id="general-doc-expiry" min="${today}">
        </div>
        ${showJobBoardField ? `
        <div class="form-group" style="display:flex;align-items:center;gap:8px;">
          <input type="checkbox" id="general-doc-show-jobboard" checked>
          <label for="general-doc-show-jobboard" style="margin:0;">Show on Vehicle Info (Job Board)</label>
        </div>` : ''}
        <div class="form-group">
          <label>File</label>
          <div class="doc-upload-area">
            <input type="file" id="general-doc-file-input" accept="image/*,.pdf" style="display:none">
            <div id="general-doc-file-preview" style="font-size:0.85rem;color:var(--text-muted);margin-bottom:8px;">No file selected</div>
            <button type="button" class="btn btn-secondary btn-sm" onclick="document.getElementById('general-doc-file-input').click()">
              <span class="material-icons">upload_file</span> Choose File
            </button>
            <p class="form-hint">PDF or image, max 7.5MB</p>
          </div>
        </div>
      </div>
      <div class="form-actions">
        <button class="btn btn-secondary" onclick="document.getElementById('modal-add-general-doc').remove()">Cancel</button>
        <button class="btn btn-primary" id="general-doc-save-btn">
          <span class="material-icons">add</span> Add
        </button>
      </div>
    </div>
  `;
  document.body.appendChild(overlay);

  // File input preview
  const fileInput = document.getElementById('general-doc-file-input');
  const preview = document.getElementById('general-doc-file-preview');
  fileInput.onchange = () => {
    const f = fileInput.files[0];
    if (f) {
      preview.innerHTML = `<span class="material-icons" style="font-size:16px;vertical-align:middle;">${f.type === 'application/pdf' ? 'picture_as_pdf' : 'image'}</span> ${escapeHtml(f.name)} (${(f.size / 1024 / 1024).toFixed(1)}MB)`;
      preview.style.color = 'var(--text-primary)';
    }
  };

  // Save button
  document.getElementById('general-doc-save-btn').onclick = () => {
    const typeVal = document.getElementById('general-doc-type').value;
    const customLabel = document.getElementById('general-doc-label').value.trim();
    const expiryVal = document.getElementById('general-doc-expiry').value || null;
    // Checkbox is absent for assets (no job board surface) — default false there.
    const showJobBoardEl = document.getElementById('general-doc-show-jobboard');
    const showOnJobBoard = showJobBoardEl ? showJobBoardEl.checked : false;
    const file = fileInput.files[0];

    // Validate
    if (!file) { window.showToast?.('Please select a file', 'error'); return; }
    const validTypes = ['application/pdf', 'image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/heic', 'image/heif'];
    const imageExts = ['.jpg', '.jpeg', '.png', '.gif', '.webp', '.heic', '.heif', '.bmp', '.tiff', '.tif'];
    const nameLower = file.name?.toLowerCase() || '';
    const isValidType = validTypes.includes(file.type) || file.type.startsWith('image/') || imageExts.some(ext => nameLower.endsWith(ext));
    if (!isValidType) { window.showToast?.('PDF or image files only', 'error'); return; }
    if (file.size > 7.5 * 1024 * 1024) { window.showToast?.('File too large (max 7.5MB)', 'error'); return; }
    if (typeVal === 'custom' && !customLabel) { window.showToast?.('Enter a custom label', 'error'); return; }

    // Expiry date guardrail — can't be in the past
    if (expiryVal) {
      const expDate = new Date(expiryVal + 'T23:59:59');
      if (expDate < new Date()) {
        window.showToast?.('Expiry date cannot be in the past', 'error');
        return;
      }
    }

    const label = typeVal === 'custom' ? customLabel : GENERAL_DOC_PRESETS.find(p => p.value === typeVal)?.label || typeVal;

    // Add to pending state
    RosterState[stateKey].pending.push({
      file,
      previewUrl: file.type.startsWith('image/') ? URL.createObjectURL(file) : null,
      type: typeVal,
      label,
      expiresAt: expiryVal,
      showOnJobBoard
    });

    overlay.remove();
    renderGeneralDocs(prefix);
  };
}

function renderGeneralDocs(prefix) {
  const container = document.getElementById(`${prefix}-general-doc-list`);
  if (!container) return;

  const stateKey = GENERAL_DOC_STATE_KEYS[prefix] || 'trailerGeneralDocs';
  const state = RosterState[stateKey];

  const existingHtml = state.existing.map((d, idx) => `
    <div class="doc-item uploaded general-doc-item">
      <span class="material-icons doc-icon">${d.name?.endsWith('.pdf') ? 'picture_as_pdf' : 'image'}</span>
      <div class="doc-info" style="flex:1;">
        <div style="display:flex;align-items:center;gap:6px;flex-wrap:wrap;">
          <span class="general-doc-type-badge">${escapeHtml(d.label || d.type || 'Document')}</span>
          ${d.showOnJobBoard ? '<span class="material-icons" style="font-size:14px;color:var(--accent-teal);" title="Visible on Job Board">visibility</span>' : ''}
        </div>
        <a ${window.StorageFile.linkAttrs(d)} class="doc-name">${escapeHtml(d.name || 'Document')}</a>
        <div style="display:flex;gap:12px;flex-wrap:wrap;">
          <span class="doc-status uploaded-status"><span class="material-icons" style="font-size:14px;vertical-align:middle;">check_circle</span> ${d.uploadedAt ? new Date(d.uploadedAt).toLocaleDateString() : ''}</span>
          ${d.expiresAt ? `<span class="doc-status" style="color:${isGeneralDocExpiringSoon(d.expiresAt) ? '#ef4444' : 'var(--text-muted)'};">Expires: ${d.expiresAt}</span>` : ''}
        </div>
      </div>
      <button type="button" class="btn-icon btn-icon-danger" onclick="window.RosterModule.removeGeneralDoc('${prefix}', 'existing', ${idx})" title="Remove">
        <span class="material-icons">close</span>
      </button>
    </div>
  `).join('');

  const pendingHtml = state.pending.map((d, idx) => `
    <div class="doc-item pending general-doc-item">
      <span class="material-icons doc-icon">${d.file.type === 'application/pdf' ? 'picture_as_pdf' : 'image'}</span>
      <div class="doc-info" style="flex:1;">
        <div style="display:flex;align-items:center;gap:6px;flex-wrap:wrap;">
          <span class="general-doc-type-badge">${escapeHtml(d.label || d.type)}</span>
          ${d.showOnJobBoard ? '<span class="material-icons" style="font-size:14px;color:var(--accent-teal);" title="Visible on Job Board">visibility</span>' : ''}
        </div>
        <span class="doc-name">${escapeHtml(d.file.name)}</span>
        <div style="display:flex;gap:12px;flex-wrap:wrap;">
          <span class="doc-status pending-status">Uploads when you save</span>
          ${d.expiresAt ? `<span class="doc-status" style="color:var(--text-muted);">Expires: ${d.expiresAt}</span>` : ''}
        </div>
      </div>
      <button type="button" class="btn-icon btn-icon-danger" onclick="window.RosterModule.removeGeneralDoc('${prefix}', 'pending', ${idx})" title="Remove">
        <span class="material-icons">close</span>
      </button>
    </div>
  `).join('');

  container.innerHTML = existingHtml + pendingHtml;

  // Update count
  const total = state.existing.length + state.pending.length;
  const countEl = document.getElementById(`${prefix}-general-doc-count`);
  if (countEl) countEl.textContent = total > 0 ? `(${total})` : '';

  // Disable add button at limit
  const addBtn = document.getElementById(`${prefix}-add-general-doc-btn`);
  if (addBtn) addBtn.disabled = total >= 20;
}

function isGeneralDocExpiringSoon(dateStr) {
  if (!dateStr) return false;
  const expiry = new Date(dateStr);
  const today = new Date();
  const thirtyDays = 30 * 24 * 60 * 60 * 1000;
  return (expiry - today) < thirtyDays;
}

function removeGeneralDoc(prefix, which, idx) {
  const stateKey = GENERAL_DOC_STATE_KEYS[prefix] || 'trailerGeneralDocs';
  if (which === 'pending') {
    const d = RosterState[stateKey].pending[idx];
    if (d?.previewUrl) URL.revokeObjectURL(d.previewUrl);
    RosterState[stateKey].pending.splice(idx, 1);
  } else {
    RosterState[stateKey].existing.splice(idx, 1);
  }
  renderGeneralDocs(prefix);
}

async function uploadGeneralDocs(prefix, tenant) {
  const stateKey = GENERAL_DOC_STATE_KEYS[prefix] || 'trailerGeneralDocs';
  const storagePath = GENERAL_DOC_STORAGE_PATHS[prefix] || 'trailer-docs';
  const uploaded = [];

  for (const pending of RosterState[stateKey].pending) {
    try {
      const path = `tenants/${tenant}/${storagePath}/${Date.now()}_${pending.file.name}`;
      await uploadBytes(ref(storage, path), pending.file, { contentType: pending.file.type || 'application/octet-stream' });
      // TASK-10086-N (audit 🟠7) — PATH only, never a getDownloadURL token link:
      // documents[] carries leases, warranties, titles and loan papers on a doc
      // every driver session can read, and the link would open them past the
      // driver door's class check and storage.rules. Office readers open by path
      // (StorageFile); drivers through the getDriverVehicleDocBytes door.
      uploaded.push({
        path,
        name: pending.file.name,
        uploadedAt: new Date().toISOString(),
        type: pending.type,
        label: pending.label,
        expiresAt: pending.expiresAt || null,
        showOnJobBoard: pending.showOnJobBoard ?? true
      });
    } catch (err) {
      console.error(`[Roster] Error uploading general doc:`, err);
    }
  }

  return uploaded;
}

function loadGeneralDocs(prefix, entity) {
  const stateKey = GENERAL_DOC_STATE_KEYS[prefix] || 'trailerGeneralDocs';
  RosterState[stateKey].pending = [];
  RosterState[stateKey].existing = entity.documents || [];
  renderGeneralDocs(prefix);
}

function clearGeneralDocs(prefix) {
  const stateKey = GENERAL_DOC_STATE_KEYS[prefix] || 'trailerGeneralDocs';
  RosterState[stateKey].pending.forEach(d => { if (d?.previewUrl) URL.revokeObjectURL(d.previewUrl); });
  RosterState[stateKey].pending = [];
  RosterState[stateKey].existing = [];
  renderGeneralDocs(prefix);
}

// Legacy compatibility - keep old function names pointing to new ones
function handleTruckDocUpload(e) {
  handleDocUpload(e, 'truck', 'inspection');
}
function removePendingTruckDoc(idx) {
  removePendingDoc('truck', 'inspection', idx);
}
function removeExistingTruckDoc(idx) {
  removeExistingDoc('truck', 'inspection', idx);
}

// Legacy function that was referenced - now a noop
function renderTruckDocs() {
  // Replaced by renderDocs - this is a legacy stub
  renderTruckDocs();
}

// TASK-10172-A — Deactivate for a truck / trailer / asset (confirm, future-job
// cascade, audit, Sold / Retired + date) lives in the LAZY views/rosterUnitDeactivate.js
// (perf budget, appColdLoadGzip); these are the thin, always-loaded hooks.
let _unitDeactP = null;
function _unitDeactivate(fn, id) {
  if (!_unitDeactP) {
    _unitDeactP = __ddChunkWrap(import('./rosterUnitDeactivate.js?v=10302129')).then((m) => {
      m.bind({ db, doc, updateDoc, Timestamp, RosterState, escapeHtml, getUnitDisplay, assetDisplayLabel, closeModal, cascadeOffFutureJobDays: _cascadeOffFutureJobDays });
      return m;
    }).catch((e) => { _unitDeactP = null; console.warn('[Roster] rosterUnitDeactivate:', e?.message || e); window.showToast?.('Could not open Deactivate. Check your connection and try again.', 'error'); return null; });
  }
  return _unitDeactP.then((m) => m && m[fn](id));
}
function deleteTruck(id) { return _unitDeactivate('deactivateTruck', id); }

// ============================================
// CRUD OPERATIONS - TRAILERS
// ============================================
function showAddTrailer() {
  document.getElementById('modal-trailer-title').textContent = 'Add Trailer';
  document.getElementById('form-trailer').reset();
  document.getElementById('trailer-id').value = '';
  document.getElementById('trailer-active').checked = true;
  document.getElementById('trailer-show-in-dispatch').checked = true;
  // Trailers default to NOT IFTA-registered (most don't consume road-taxed fuel)
  const trailerIftaEl = document.getElementById('trailer-ifta-registered');
  if (trailerIftaEl) trailerIftaEl.checked = false;
  const trailerIftaYearEl = document.getElementById('trailer-ifta-decal-year');
  if (trailerIftaYearEl) trailerIftaYearEl.value = '';
  populateTypeDropdown('trailer-type', 'trailerTypes', false, { inUse: RosterState.trailers }); // TASK-9339
  // Initialize with one empty axle field
  loadTrailerAxles([]);
  // TASK-525 — reset Heavy Haul / Permit Specs
  const trRegWt = document.getElementById('trailer-registered-weight');
  if (trRegWt) trRegWt.value = '';
  _loadPermitAxles('trailer', []);
  _renderUnitRigsSummary('trailer', null);
  _pgeoSync('trailer', null); // TASK-9993
  const trHH = document.getElementById('trailer-heavyhaul-section');
  if (trHH) trHH.open = false;
  // TASK-9267 — no nag badges on a brand-new, not-yet-saved trailer.
  ['trailer-dims-missing-badge', 'trailer-hh-missing-badge'].forEach(elId => {
    const el = document.getElementById(elId);
    if (el) { el.style.display = 'none'; el.textContent = ''; }
  });
  // Initialize with one empty VIN field
  loadTrailerVins([]);
  // Initialize with one empty plate field
  loadTrailerPlates([]);
  // Clear all docs
  clearAllDocs('trailer');
  // TASK-340 Slice A — reset photo state on "new trailer" modal open.
  RosterState.trailerPhoto = { pending: null, existing: null };
  _renderEquipmentPhotoPreview('trailer');
  // Reset flag settings
  document.getElementById('trailer-show-inspection-flag').checked = false;
  document.getElementById('trailer-flag-days').value = '30';
  document.getElementById('trailer-flag-options').style.display = 'none';
  document.getElementById('trailer-reset-flag-btn').style.display = 'none';
  // Yard dropdown
  populateYardDropdown('trailer-yard', false);
  // TASK-1141 — no thread on an unsaved trailer (no doc id yet)
  window.EquipmentNotes?.unmount('trailer-notes-thread');
  _rentalLoad('trailer', null); // TASK-9869
  const modal = document.getElementById('modal-trailer');
  modal.querySelectorAll('.smart-warning').forEach(el => el.remove());
  modal.classList.remove('hidden');
  // Reset scroll position to top
  const modalBody = modal.querySelector('.modal-body');
  if (modalBody) modalBody.scrollTop = 0;
  window.attachAddressAutocomplete?.(document.getElementById('trailer-inspection-location'));
}

function editTrailer(id) {
  const trailer = RosterState.trailers.find(t => t.id === id);
  if (!trailer) return;

  // TASK-1141 — per-unit notes thread under the legacy Notes field
  window.EquipmentNotes?.mount('trailer-notes-thread', {
    kind: 'trailer',
    id,
    unitLabel: window.getUnitDisplay ? window.getUnitDisplay(trailer) : (trailer.unit || ''),
  });

  document.getElementById('modal-trailer-title').textContent = 'Edit Trailer';
  document.getElementById('trailer-id').value = id;
  document.getElementById('trailer-unit').value = trailer.unit || '';
  document.getElementById('trailer-nickname').value = trailer.nickname || '';
  // TASK-340 Slice A — seed photo state from existing trailer.photoUrl.
  RosterState.trailerPhoto = {
    pending: null,
    existing: trailer.photoUrl ? { url: trailer.photoUrl } : null,
  };
  _renderEquipmentPhotoPreview('trailer');
  populateTypeDropdown('trailer-type', 'trailerTypes', false, { inUse: RosterState.trailers, keep: trailer.type }); // TASK-9339
  const trailerTypeSelect = document.getElementById('trailer-type');
  trailerTypeSelect.value = trailer.type || 'flatbed';
  document.getElementById('trailer-make').value = trailer.make || '';
  document.getElementById('trailer-model').value = trailer.model || '';
  document.getElementById('trailer-year').value = trailer.year || '';
  // VINs - load existing VINs (backward compat: single vin string → array)
  loadTrailerVins(trailer.vins || (trailer.vin ? [trailer.vin] : []));
  // Weight Ratings
  document.getElementById('trailer-gvwr').value = trailer.gvwr || '';
  document.getElementById('trailer-unladen-weight').value = trailer.unladenWeight || '';
  document.getElementById('trailer-gawr').value = trailer.gawr || '';
  // TASK-512 — OS/OW dimensions + axle config
  document.getElementById('trailer-length').value = trailer.length || '';
  document.getElementById('trailer-width').value = trailer.width || '';
  document.getElementById('trailer-height').value = trailer.height || '';
  document.getElementById('trailer-axle-count').value = trailer.axleCount || '';
  document.getElementById('trailer-axle-spacing').value = trailer.axleSpacing || '';
  // TASK-525 — Heavy Haul / Permit Specs (load + auto-open if present)
  const trRegWtEl = document.getElementById('trailer-registered-weight');
  if (trRegWtEl) trRegWtEl.value = trailer.registeredWeight || '';
  _loadPermitAxles('trailer', trailer.permitAxles || []);
  // TASK-1443 — read-only rig membership
  _renderUnitRigsSummary('trailer', trailer.id);
  _pgeoSync('trailer', trailer.id); // TASK-9993
  const trHHEl = document.getElementById('trailer-heavyhaul-section');
  if (trHHEl) trHHEl.open = !!(trailer.registeredWeight || (trailer.permitAxles && trailer.permitAxles.length));
  // TASK-9267 — badge the collapsed headers so a trailer with no permit
  // specs is discoverable without opening either section.
  _updatePermitSpecsBadges('trailer', trailer);
  // Registration & Compliance — multi-plate support
  loadTrailerPlates(trailer.licensePlates || [], trailer.licensePlate, trailer.licensePlateState);
  document.getElementById('trailer-reg-number').value = trailer.registrationNumber || '';
  document.getElementById('trailer-reg-expiry').value = trailer.registrationNoExpiry === true ? '' : (trailer.registrationExpiry || ''); // TASK-9814 follow-up: "Doesn't expire" wins
  document.getElementById('trailer-reg-expiry').dataset.prevValue = trailer.registrationExpiry || ''; // restored if the box is unticked
  document.getElementById('trailer-reg-no-expiry').checked = trailer.registrationNoExpiry === true; // TASK-9814
  window.RegNoExpiryGuard?.reset(document.getElementById('trailer-reg-no-expiry')); // TASK-9935
  // Insurance
  document.getElementById('trailer-ins-company').value = trailer.insuranceCompany || '';
  document.getElementById('trailer-ins-policy').value = trailer.insurancePolicy || '';
  document.getElementById('trailer-ins-expiry').value = trailer.insuranceExpiry || '';
  document.getElementById('trailer-ins-phone').value = trailer.insurancePhone || '';
  // Annual Inspection
  document.getElementById('trailer-inspection-date').value = trailer.inspectionDate || '';
  document.getElementById('trailer-inspection-expiry').value = trailer.inspectionExpiry || '';
  document.getElementById('trailer-inspection-location').value = trailer.inspectionLocation || '';
  // Axles
  loadTrailerAxles(trailer.axles || []);
  document.getElementById('trailer-tire-size').value = trailer.tireSize || ''; // TASK-9731
  // Notes
  document.getElementById('trailer-notes').value = trailer.notes || '';
  // Load all doc types
  loadAllDocs('trailer', trailer);
  loadGeneralDocs('trailer', trailer);
  // Load flag settings
  const showFlag = trailer.showInspectionFlag || false;
  document.getElementById('trailer-show-inspection-flag').checked = showFlag;
  document.getElementById('trailer-flag-days').value = trailer.inspectionFlagDays || '30';
  document.getElementById('trailer-flag-options').style.display = showFlag ? 'flex' : 'none';
  // Show reset button if flag was dismissed
  document.getElementById('trailer-reset-flag-btn').style.display = trailer.inspectionFlagDismissed ? 'block' : 'none';
  // Yard dropdown
  populateYardDropdown('trailer-yard', false);
  document.getElementById('trailer-yard').value = trailer.yardId || '';

  document.getElementById('trailer-active').checked = trailer.active !== false;
  document.getElementById('trailer-show-in-dispatch').checked = trailer.showInDispatch !== false;
  // IFTA — trailers default false (missing field treated as false by compliance filter)
  const trailerIftaEl = document.getElementById('trailer-ifta-registered');
  if (trailerIftaEl) trailerIftaEl.checked = trailer.iftaRegistered === true;
  const trailerIftaYearEl = document.getElementById('trailer-ifta-decal-year');
  if (trailerIftaYearEl) trailerIftaYearEl.value = trailer.iftaDecalYear || '';
  _rentalLoad('trailer', trailer); // TASK-9869
  // HOTFIX 2026-11-04 — reused modal: drop any Smart Assist banner computed from the
  // previous record's values (J-1361's notes were offered as Auto-fix on J-1355).
  window.SmartAssistant?.clearAll?.(document.getElementById('modal-trailer'));
  document.getElementById('modal-trailer').classList.remove('hidden');
  window.attachAddressAutocomplete?.(document.getElementById('trailer-inspection-location'));
}

async function saveTrailer(e) {
  e.preventDefault();
  if (!window.validateRequiredFields(e.target)) return;
  // TASK-1444 — see saveTruck: stamped rows get acknowledged before they persist.
  if (!(await _paxleConfirmUnreviewed('trailer'))) {
    document.getElementById('trailer-heavyhaul-section')?.setAttribute('open', 'open');
    return;
  }

  // TASK-4529 — a unit/nickname that equals ANOTHER fleet asset's doc id would
  // shadow that asset in ref resolution (the doc-id pass wins). The server
  // trigger reverts such writes after the fact; block them HERE so the user
  // sees a clear error instead of a silently-reverted save. Keep in sync with
  // _fleetUnitCollisionDecision (functions/src/index.js).
  // 🔴 TASK-4718 — see the twin in saveTruck(). Only judge a value that CHANGED;
  // an already-stored collision must not lock the user out of unrelated fields.
  {
    const _selfId = document.getElementById('trailer-id').value;
    const _fleetDocs = [...(RosterState.trucks || []), ...(RosterState.trailers || [])];
    const _collides = (v) => v && _fleetDocs.some(x => String(x.id) === v && x.id !== _selfId);
    const _self = _fleetDocs.find(x => String(x.id) === String(_selfId));
    const _prevOf = (f) => String(_self?.[f] ?? '').trim();
    const _bad = [
      ['unit', document.getElementById('trailer-unit').value.trim()],
      ['nickname', document.getElementById('trailer-nickname').value.trim()],
    ].filter(([f, v]) => v && v !== _prevOf(f))   // unchanged => the server skips it, so we do too
      .map(([, v]) => v)
      .find(_collides);
    if (_bad) {
      window.showToast?.(`"${_bad}" can't be used here — it matches another vehicle's internal ID, and jobs referencing that vehicle could resolve to this one. Pick a different value.`, 'error', null, 9000);
      return;
    }
  }
  // TASK-9378 COD — a photo still converting settles OUT HERE, not inside the
  // guard (its 20s hand-off would re-open Save mid-conversion → duplicate doc).
  if (!(await _settlePhotoBeforeSave(RosterState.trailerPhoto, 'saveTrailer', 'btn-save-trailer'))) return;
  if (_regExpiryPromptBlocks('trailer')) return; // TASK-9814 — soft prompt, "Save anyway" re-enters
  return runGuardedSave('saveTrailer', async () => {
  const id = document.getElementById('trailer-id').value;
  const _clamp = window.clampText || ((s, n) => s && s.length > n ? s.slice(0, n) : s || '');
  const data = {
    unit: _clamp(document.getElementById('trailer-unit').value.trim(), 100),
    nickname: _clamp(document.getElementById('trailer-nickname').value.trim(), 100),
    type: document.getElementById('trailer-type').value,
    make: document.getElementById('trailer-make').value.trim(),
    model: document.getElementById('trailer-model').value.trim(),
    year: parseInt(document.getElementById('trailer-year').value) || null,
    vins: getTrailerVins(),
    vin: getTrailerVins()[0] || '', // backward compat — first VIN as primary
    // Weight Ratings
    gvwr: parseInt(document.getElementById('trailer-gvwr').value) || null,
    unladenWeight: parseInt(document.getElementById('trailer-unladen-weight').value) || null,
    gawr: parseInt(document.getElementById('trailer-gawr').value) || null,
    // TASK-512 — OS/OW dimensions (decimal ft) + axle config. Net-new, additive.
    length: parseFloat(document.getElementById('trailer-length').value) || null,
    width: parseFloat(document.getElementById('trailer-width').value) || null,
    height: parseFloat(document.getElementById('trailer-height').value) || null,
    axleCount: parseInt(document.getElementById('trailer-axle-count').value) || null,
    axleSpacing: document.getElementById('trailer-axle-spacing').value.trim(),
    // TASK-525 — Heavy Haul / Permit Specs (this trailer's OWN axles; no loaded weight)
    registeredWeight: parseInt(document.getElementById('trailer-registered-weight')?.value) || null,
    permitAxles: getPermitAxles('trailer'),
    // Registration & Compliance — multi-plate support
    licensePlates: getTrailerPlates(),
    licensePlate: getTrailerPlates()[0]?.plate || '', // backward compat
    licensePlateState: getTrailerPlates()[0]?.state || '', // backward compat
    registrationNumber: document.getElementById('trailer-reg-number').value.trim(),
    // TASK-9814 follow-up: "Doesn't expire" clears the date (a stale date would keep alerting).
    registrationExpiry: document.getElementById('trailer-reg-no-expiry')?.checked === true ? null : (document.getElementById('trailer-reg-expiry').value || null),
    registrationNoExpiry: document.getElementById('trailer-reg-no-expiry')?.checked === true, // TASK-9814
    // Insurance
    insuranceCompany: document.getElementById('trailer-ins-company').value.trim(),
    insurancePolicy: document.getElementById('trailer-ins-policy').value.trim(),
    insuranceExpiry: document.getElementById('trailer-ins-expiry').value || null,
    insurancePhone: document.getElementById('trailer-ins-phone').value.trim(),
    // Annual Inspection — TASK-258 sub-item: auto-calc expiry from date if
    // the user only entered the date. 12-month rule per §396.17.
    inspectionDate: document.getElementById('trailer-inspection-date').value || null,
    inspectionExpiry: _aviAutoExpiry(
      document.getElementById('trailer-inspection-date').value || null,
      document.getElementById('trailer-inspection-expiry').value || null,
    ),
    inspectionLocation: document.getElementById('trailer-inspection-location').value.trim(),
    // Axles
    axles: getTrailerAxles(),
    tireSize: _clamp(document.getElementById('trailer-tire-size').value.trim(), 40), // TASK-9731 — optional
    notes: document.getElementById('trailer-notes').value.trim(),
    active: document.getElementById('trailer-active')?.checked !== false,
    showInDispatch: document.getElementById('trailer-show-in-dispatch')?.checked !== false,
    iftaRegistered: document.getElementById('trailer-ifta-registered')?.checked === true,
    iftaDecalYear: parseInt(document.getElementById('trailer-ifta-decal-year')?.value) || null,
    updatedAt: new Date()
  };

  // Yard
  const trailerYardEl = document.getElementById('trailer-yard');
  if (trailerYardEl) {
    data.yardId = trailerYardEl.value || '';
    data.yardName = data.yardId ? getYardLabel(data.yardId) : '';
  }

  // Warn if year seems unreasonable
  const currentYear = new Date().getFullYear();
  if (data.year && data.year > currentYear + VEHICLE_SPEC_RANGES.year.maxYearsAhead) { // TASK-9715 shared bound
    const proceed = confirm(`Year ${data.year} is more than a year in the future. Did you mean ${currentYear} or ${currentYear + 1}?`);
    if (!proceed) return;
  }
  if (data.year && data.year < VEHICLE_SPEC_RANGES.year.min) { // TASK-9715 shared bound
    const proceed = confirm(`Year ${data.year} seems very old for a commercial trailer. Continue anyway?`);
    if (!proceed) return;
  }

  // Soft-duplicate check for new trailers
  if (!id && data.unit) {
    const lastSaved = window.__rosterLastSavedTrailer;
    if (lastSaved && lastSaved.unit === data.unit && (Date.now() - lastSaved.ts) < 3000) {
      const proceed = confirm(`You just added a trailer with unit "${data.unit}" a moment ago. Are you sure you want to add another?`);
      if (!proceed) return;
    }
  }

    const tenant = window.AppState?.tenant || 'demo';

    // Upload all pending docs for each type
    const docTypes = ['inspection', 'registration', 'insurance'];
    for (const docType of docTypes) {
      const uploaded = await uploadPendingDocs('trailer', docType, tenant);
      const existing = RosterState.trailerDocs[docType].existing;
      data[`${docType}Docs`] = [...existing, ...uploaded];
    }

    // Upload general documents
    const generalUploaded = await uploadGeneralDocs('trailer', tenant);
    data.documents = [...RosterState.trailerGeneralDocs.existing, ...generalUploaded];

    // Save flag settings
    const showInspectionFlag = document.getElementById('trailer-show-inspection-flag').checked;
    data.showInspectionFlag = showInspectionFlag;
    data.inspectionFlagDays = parseInt(document.getElementById('trailer-flag-days').value) || 30;
    // Preserve dismissed state if not resetting
    const existingTrailer = RosterState.trailers.find(t => t.id === id);
    if (existingTrailer?.inspectionFlagDismissed && data.showInspectionFlag) {
      data.inspectionFlagDismissed = existingTrailer.inspectionFlagDismissed;
    }

    // TASK-340 Slice A — trailer photo upload before write.
    const trailerPhotoUrl = await uploadTrailerPhoto(tenant);
    data.photoUrl = trailerPhotoUrl || null;

    // TASK-9869 — rented-in markers (no money) onto the unit doc.
    if (!_rentalApplyMarkers('trailer', data)) return;
    let _rentalUnitId = id;
    if (id) {
      const _noExpBefore = await _regNoExpiryBefore('trailers', id, existingTrailer?.registrationNoExpiry); // TASK-9935
      await withWriteTimeout(updateDoc(doc(db, 'tenants', tenant, 'trailers', id), data), 15000, 'update trailer');
      _auditRegNoExpiry('trailer', id, data.unit, _noExpBefore, data.registrationNoExpiry); // TASK-9935
      window.showToast?.('Trailer updated', 'success');
      if (existingTrailer && existingTrailer.active === false && data.active) _vehicleSoftCapAfterSave(id); // TASK-9433
    } else {
      data.createdAt = new Date();
      const newTrailerRef = await withWriteTimeout(addDoc(collection(db, 'tenants', tenant, 'trailers'), data), 15000, 'create trailer');
      _rentalUnitId = newTrailerRef?.id || null;
      _auditRegNoExpiry('trailer', newTrailerRef?.id, data.unit, false, data.registrationNoExpiry); // TASK-9935
      window.showToast?.('Trailer added', 'success');
      if (data.active) _vehicleSoftCapAfterSave(newTrailerRef?.id); // TASK-9433
      window.__rosterLastSavedTrailer = { unit: data.unit, ts: Date.now() };
    }

    await _rentalSaveRecord('trailer', _rentalUnitId, data); // TASK-9869

    // Clear all docs
    clearAllDocs('trailer');
    clearGeneralDocs('trailer');

    closeModal('trailer');
    renderTrailersList();
  }, { buttonId: 'btn-save-trailer', restoreLabel: 'Save' });
}

function deleteTrailer(id) { return _unitDeactivate('deactivateTrailer', id); }

// ============================================
// MODAL HELPERS
// ============================================
function closeModal(type) {
  document.getElementById(`modal-${type}`).classList.add('hidden');
  // TASK-1357 — drop the person modal's DOT gate record on close. savePerson
  // reads RosterState._dotGate to decide which keys to OMIT from its partial
  // updateDoc, so a record left over from a previous open would be a silent
  // "my edit didn't save". Today every open routes through showAddPerson /
  // editPerson, which both re-run the gate — this makes that invariant
  // structural instead of something a future call site has to remember.
  // Cleared (not falsified) so the absent-record path falls back to the full
  // legacy write behaviour.
  if (type === 'person') RosterState._dotGate = null;
}

// ============================================
// AXLE MANAGEMENT - TRUCKS
// ============================================
let truckAxleCount = 0;

function loadTruckAxles(axles) {
  const container = document.getElementById('truck-axles-container');
  if (!container) return;
  container.innerHTML = '';
  truckAxleCount = 0;
  if (axles && axles.length > 0) {
    axles.forEach(axle => addTruckAxle(axle));
  } else {
    // Start with one empty axle field
    addTruckAxle();
  }
}

function addTruckAxle(value = '') {
  const container = document.getElementById('truck-axles-container');
  if (!container) return;
  truckAxleCount++;
  const axleNum = truckAxleCount;
  const div = document.createElement('div');
  div.className = 'axle-input-row';
  div.id = `truck-axle-row-${axleNum}`;
  div.innerHTML = `
    <span class="axle-label">Axle ${axleNum}</span>
    <input type="text" class="truck-axle-input" placeholder="Serial number" value="${escapeHtml(value)}">
    <button type="button" class="btn-icon btn-icon-danger btn-sm" onclick="window.RosterModule.removeTruckAxle(${axleNum})" title="Remove">
      <span class="material-icons">close</span>
    </button>
  `;
  container.appendChild(div);
}

function removeTruckAxle(num) {
  const row = document.getElementById(`truck-axle-row-${num}`);
  if (row) row.remove();
  // Renumber remaining axles
  renumberTruckAxles();
}

function renumberTruckAxles() {
  const container = document.getElementById('truck-axles-container');
  if (!container) return;
  const rows = container.querySelectorAll('.axle-input-row');
  rows.forEach((row, idx) => {
    const label = row.querySelector('.axle-label');
    if (label) label.textContent = `Axle ${idx + 1}`;
  });
}

function getTruckAxles() {
  const inputs = document.querySelectorAll('.truck-axle-input');
  const axles = [];
  inputs.forEach(input => {
    const val = input.value.trim();
    if (val) axles.push(val);
  });
  return axles;
}

// ============================================
// AXLE MANAGEMENT - TRAILERS
// ============================================
let trailerAxleCount = 0;

function loadTrailerAxles(axles) {
  const container = document.getElementById('trailer-axles-container');
  if (!container) return;
  container.innerHTML = '';
  trailerAxleCount = 0;
  if (axles && axles.length > 0) {
    axles.forEach(axle => addTrailerAxle(axle));
  } else {
    // Start with one empty axle field
    addTrailerAxle();
  }
}

function addTrailerAxle(value = '') {
  const container = document.getElementById('trailer-axles-container');
  if (!container) return;
  trailerAxleCount++;
  const axleNum = trailerAxleCount;
  const div = document.createElement('div');
  div.className = 'axle-input-row';
  div.id = `trailer-axle-row-${axleNum}`;
  div.innerHTML = `
    <span class="axle-label">Axle ${axleNum}</span>
    <input type="text" class="trailer-axle-input" placeholder="Serial number" value="${escapeHtml(value)}">
    <button type="button" class="btn-icon btn-icon-danger btn-sm" onclick="window.RosterModule.removeTrailerAxle(${axleNum})" title="Remove">
      <span class="material-icons">close</span>
    </button>
  `;
  container.appendChild(div);
}

function removeTrailerAxle(num) {
  const row = document.getElementById(`trailer-axle-row-${num}`);
  if (row) row.remove();
  // Renumber remaining axles
  renumberTrailerAxles();
}

function renumberTrailerAxles() {
  const container = document.getElementById('trailer-axles-container');
  if (!container) return;
  const rows = container.querySelectorAll('.axle-input-row');
  rows.forEach((row, idx) => {
    const label = row.querySelector('.axle-label');
    if (label) label.textContent = `Axle ${idx + 1}`;
  });
}

function getTrailerAxles() {
  const inputs = document.querySelectorAll('.trailer-axle-input');
  const axles = [];
  inputs.forEach(input => {
    const val = input.value.trim();
    if (val) axles.push(val);
  });
  return axles;
}

// ============================================
// PERMIT-AXLE MANAGEMENT (TASK-525; fast capture TASK-1444)
// Per-axle permit config for OS/OW filing — DISTINCT from the maintenance axle
// SERIAL list above (truck.axles). Stored on a NEW field: truck/trailer.permitAxles[].
// Shape per row: { tires, tireWidth, trackWidth, spacingToNext, groupBreakAfter }.
// ⚠️ NO per-axle weight — rated capacity vs actual loaded (move-specific) ambiguity;
// loaded weights are collected per-request, never stored on the vehicle.
// ⚠️ This unit's OWN axles only; the tractor↔trailer coupling gap belongs on the
// rig template (combination-specific), never on a single vehicle.
//
// ──────────────────────────────────────────────────────────────────────────────
// TASK-1444 — why this got a grid. TASK-525's one-row-at-a-time form produced a
// fill rate of 0/26 vehicles in two months: a 9-axle truck is 9 rows × 5 fields
// typed by hand, and every downstream permit feature (1443/1445/1446/1447)
// prefills from that table. So: tab-between-cells, Enter-adds-a-row, paste a
// block straight out of Excel/Sheets, duplicate-the-previous-axle, presets, and
// an import path for a permit the carrier already filed once.
//
// 🔴 THE ONE INVARIANT EVERY FAST PATH OBEYS: **nothing here writes to Firestore.**
// Paste, preset and import all mutate VISIBLE DOM CELLS ONLY. The single write
// path stays `getPermitAxles()` → saveTruck/saveTrailer, so the server-side
// guards (`sanitizePermitAxles` / `_sanitizePermitAxleStr` prompt-injection
// hygiene, `_formulaGuard` on the CSV/e-mail sink in functions/src/index.js)
// keep covering every route by construction. Do not add a fast path that writes.
//
// 🔴 AND WE NEVER INVENT A NUMBER. This is a 23 USC 127 / weigh-station surface.
// A cell we cannot parse is left BLANK and flagged red — never defaulted to 0,
// never "best guess". A cell whose notation we converted (14' 6" → 14.5) is
// flagged amber with the original text in its tooltip. Preset/import rows land
// flagged "needs review" and the save path makes the user acknowledge them.
// ============================================

// Canonical column order. The paste engine maps clipboard columns onto this
// list starting at whichever cell has focus, so the order is load-bearing —
// it must stay identical to the stored permitAxles[] field order.
const PAXLE_COLS = ['tires', 'tireWidth', 'trackWidth', 'spacingToNext', 'groupBreakAfter'];
const PAXLE_COL_LABELS = { tires: 'Tires', tireWidth: 'Tire W', trackWidth: 'Track W', spacingToNext: 'To next (ft)', groupBreakAfter: 'Brk' };
// 20 = the server's own cap (`sanitizePermitAxles` slices to 20). Keeping the
// client cap identical means the user is told about the truncation HERE, where
// they can do something about it, instead of losing rows silently on write.
const PAXLE_MAX_ROWS = 20;
const PAXLE_TEXT_MAX = 20; // matches _sanitizePermitAxleStr(…, 20) server-side

const _paxleUndo = { truck: null, trailer: null };

// ───────────────────────────────────────────────────────── presets (DATA seam)
// Preset list is DATA, not logic — same national-scale seam as `jurisdictions`.
// `getAxlePresets()` is the ONE accessor: built-ins here, tenant overrides from
// `settings.axlePresets` (the customTypes pattern), and a Firestore-backed
// per-tenant collection would plug in here without touching a call site.
//
// Values come from the spec plus TITS's own filed LoadPass permits (steer 2×385mm
// / drives 4×275mm / 5'0" intra-group spacing / 8'6" track). Track width is left
// BLANK on trailer presets on purpose — the real filings show 8'6" AND 9'6" on
// the same trailer group, so there is no defensible default and we will not
// stamp one. Every preset leaves the LAST row's spacing blank as well: the gap
// to whatever comes next is not a property of this group.
const DEFAULT_AXLE_PRESETS = [
  { id: 'steer', label: 'Steer axle (2 tires, 385 mm)', appliesTo: 'truck',
    rows: [{ tires: 2, tireWidth: '385 mm', trackWidth: "8' 6\"", spacingToNext: null, groupBreakAfter: true }] },
  { id: 'drive-tandem', label: 'Tandem drive (2×4 tires, 275 mm, 5′0" spacing)', appliesTo: 'truck',
    rows: [
      { tires: 4, tireWidth: '275 mm', trackWidth: "8' 6\"", spacingToNext: 5, groupBreakAfter: false },
      { tires: 4, tireWidth: '275 mm', trackWidth: "8' 6\"", spacingToNext: null, groupBreakAfter: true }
    ] },
  { id: 'drive-tridem', label: 'Tridem drive (3×4 tires, 275 mm)', appliesTo: 'truck',
    rows: [
      { tires: 4, tireWidth: '275 mm', trackWidth: "8' 6\"", spacingToNext: 5, groupBreakAfter: false },
      { tires: 4, tireWidth: '275 mm', trackWidth: "8' 6\"", spacingToNext: 5, groupBreakAfter: false },
      { tires: 4, tireWidth: '275 mm', trackWidth: "8' 6\"", spacingToNext: null, groupBreakAfter: true }
    ] },
  { id: 'trailer-tandem', label: 'Tandem trailer (2×4 tires)', appliesTo: 'trailer',
    rows: [
      { tires: 4, tireWidth: '275 mm', trackWidth: '', spacingToNext: 5, groupBreakAfter: false },
      { tires: 4, tireWidth: '275 mm', trackWidth: '', spacingToNext: null, groupBreakAfter: true }
    ] },
  { id: 'trailer-tridem', label: 'Tri-axle trailer (3×4 tires)', appliesTo: 'trailer',
    rows: [
      { tires: 4, tireWidth: '275 mm', trackWidth: '', spacingToNext: 5, groupBreakAfter: false },
      { tires: 4, tireWidth: '275 mm', trackWidth: '', spacingToNext: 5, groupBreakAfter: false },
      { tires: 4, tireWidth: '275 mm', trackWidth: '', spacingToNext: null, groupBreakAfter: true }
    ] },
  { id: 'trailer-quad', label: 'Quad trailer (4×4 tires)', appliesTo: 'trailer',
    rows: [
      { tires: 4, tireWidth: '275 mm', trackWidth: '', spacingToNext: 5, groupBreakAfter: false },
      { tires: 4, tireWidth: '275 mm', trackWidth: '', spacingToNext: 5, groupBreakAfter: false },
      { tires: 4, tireWidth: '275 mm', trackWidth: '', spacingToNext: 5, groupBreakAfter: false },
      { tires: 4, tireWidth: '275 mm', trackWidth: '', spacingToNext: null, groupBreakAfter: true }
    ] },
  { id: 'booster', label: 'Booster (single axle, 4 tires)', appliesTo: 'trailer',
    rows: [{ tires: 4, tireWidth: '275 mm', trackWidth: '', spacingToNext: null, groupBreakAfter: true }] }
];

// Tenant presets are TENANT DATA — a raw-SDK write can put anything in them, and
// the label lands in markup while the rows land in a regulatory table. Coerce on
// READ (same stance as _rigStoredNum on the equipmentTemplates doc).
function _paxleSanitizePresetRow(r) {
  const o = (r && typeof r === 'object') ? r : {};
  const tires = Number(o.tires);
  const spacing = Number(o.spacingToNext);
  return {
    tires: Number.isFinite(tires) && tires >= 0 && tires <= 100 ? Math.trunc(tires) : null,
    tireWidth: String(o.tireWidth == null ? '' : o.tireWidth).replace(/[\u0000-\u001F\u007F]+/g, ' ').replace(/\s+/g, ' ').trim().slice(0, PAXLE_TEXT_MAX),
    trackWidth: String(o.trackWidth == null ? '' : o.trackWidth).replace(/[\u0000-\u001F\u007F]+/g, ' ').replace(/\s+/g, ' ').trim().slice(0, PAXLE_TEXT_MAX),
    spacingToNext: Number.isFinite(spacing) && spacing >= 0 ? Math.round(spacing * 100) / 100 : null,
    groupBreakAfter: o.groupBreakAfter === true
  };
}

function getAxlePresets(kind) {
  const custom = Array.isArray(window.AppState?.settings?.axlePresets) ? window.AppState.settings.axlePresets : [];
  const cleanedCustom = custom.slice(0, 50).map((p, i) => {
    const o = (p && typeof p === 'object') ? p : {};
    return {
      id: `custom-${i}`,
      label: String(o.label == null ? '' : o.label).replace(/[\u0000-\u001F\u007F]+/g, ' ').trim().slice(0, 80) || `Saved preset ${i + 1}`,
      appliesTo: (o.appliesTo === 'truck' || o.appliesTo === 'trailer') ? o.appliesTo : 'any',
      rows: (Array.isArray(o.rows) ? o.rows : []).slice(0, PAXLE_MAX_ROWS).map(_paxleSanitizePresetRow)
    };
  }).filter(p => p.rows.length);
  return DEFAULT_AXLE_PRESETS.concat(cleanedCustom)
    .filter(p => !p.appliesTo || p.appliesTo === 'any' || p.appliesTo === kind);
}

// ────────────────────────────────────────────────────────────── cell parsing
// Every parser returns { value, level, note }:
//   value  what goes IN the input (string, or boolean for the checkbox)
//   level  '' ok · 'warn' landed but look at it · 'bad' refused, cell left blank
//   note   plain-English reason, shown as the cell tooltip + in the banner
function _paxleOk(value) { return { value, level: '', note: '' }; }
function _paxleWarn(value, note) { return { value, level: 'warn', note }; }
function _paxleBad(blank, note) { return { value: blank, level: 'bad', note }; }

function _paxleClean(raw) {
  return String(raw == null ? '' : raw)
    .replace(/[\u0000-\u001F\u007F]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

// Feet. Accepts a plain decimal ("5", "14.5", "14.5 ft") AND the feet-inches
// notation every real permit is written in ("14' 6\"", "5'0\"", "14 ft 6 in").
// A conversion is a NOTATION change, not a guess — but it still gets surfaced,
// because 14' 6" reading back as 14.5 is exactly the kind of silent rewrite that
// puts a wrong axle spacing on a filing.
function _paxleParseFeet(s) {
  if (!s) return _paxleOk('');
  const t = s.replace(/,/g, '');
  const fi = t.match(/^(\d+(?:\.\d+)?)\s*(?:'|′|’|ft\.?|feet)\s*(?:(\d+(?:\.\d+)?)\s*(?:"|″|”|''|in\.?|inch(?:es)?)?)?$/i);
  if (fi) {
    const ft = Number(fi[1]);
    const inch = fi[2] === undefined ? 0 : Number(fi[2]);
    if (!Number.isFinite(ft) || !Number.isFinite(inch)) return _paxleBad('', `Couldn't read "${s}" as a distance — left blank.`);
    if (inch >= 12) return _paxleBad('', `"${s}" has ${inch} inches in it, which isn't a valid feet-and-inches measurement — left blank.`);
    const dec = Math.round((ft + inch / 12) * 100) / 100;
    if (dec > 100) return _paxleWarn(String(dec), `Converted "${s}" to ${dec} ft — that is a very long gap for one axle spacing, double-check it.`);
    return inch > 0
      ? _paxleWarn(String(dec), `Converted "${s}" to ${dec} ft.`)
      : { value: String(dec), level: '', note: `Read "${s}" as ${dec} ft.` };
  }
  const m = t.match(/^(-?\d+(?:\.\d+)?)\s*(?:'|′|’|ft\.?|feet)?$/i);
  if (!m) return _paxleBad('', `Couldn't read "${s}" as a number of feet — left blank so nobody files a made-up spacing.`);
  const n = Number(m[1]);
  if (!Number.isFinite(n)) return _paxleBad('', `Couldn't read "${s}" as a number — left blank.`);
  if (n < 0) return _paxleBad('', `"${s}" is negative — an axle spacing can't be, so it was left blank.`);
  const r = Math.round(n * 100) / 100;
  if (r > 100) return _paxleWarn(String(r), `${r} ft is a very long single axle spacing — double-check it.`);
  return _paxleOk(String(r));
}

function _paxleParseTires(s) {
  if (!s) return _paxleOk('');
  const t = s.replace(/,/g, '').replace(/\s*(?:tires?|tyres?)\.?$/i, '').trim();
  if (!/^\d+$/.test(t)) return _paxleBad('', `"${s}" isn't a whole number of tires — left blank.`);
  const n = Number(t);
  if (!Number.isFinite(n) || n > 100) return _paxleBad('', `"${s}" is not a believable tire count — left blank.`);
  if (n > 24) return _paxleWarn(String(n), `${n} tires on one axle — check that.`);
  return _paxleOk(String(n));
}

const _PAXLE_YES = new Set(['y', 'yes', 'true', 't', '1', 'x', 'break', 'brk', 'group break', 'groupbreak', '✓', '✔']);
const _PAXLE_NO = new Set(['', 'n', 'no', 'false', 'f', '0', '-', '—', 'none', 'na', 'n/a']);
function _paxleParseBool(s) {
  const l = s.toLowerCase();
  if (_PAXLE_YES.has(l)) return _paxleOk(true);
  if (_PAXLE_NO.has(l)) return _paxleOk(false);
  return _paxleBad(false, `Didn't recognise "${s}" as a yes/no for the group break — left unchecked, set it by hand.`);
}

function _paxleParseText(s) {
  if (!s) return _paxleOk('');
  const notes = [];
  let level = '';
  let v = s;
  if (v.length > PAXLE_TEXT_MAX) {
    v = v.slice(0, PAXLE_TEXT_MAX);
    level = 'warn';
    notes.push(`Trimmed to ${PAXLE_TEXT_MAX} characters ("${s}").`);
  }
  // Left exactly as typed — the CSV/e-mail sink quotes it server-side
  // (_formulaGuard). We flag it so a pasted formula can't slip by unnoticed;
  // rewriting the user's own text here would be the silent-coercion we forbid.
  if (/^[=+\-@]/.test(v)) {
    level = 'warn';
    notes.push('Starts with = + - or @, which spreadsheets read as a formula. Left exactly as pasted; exports quote it.');
  }
  // An export that writes "null"/"N/A" into an empty cell would otherwise put
  // that word on a permit as a tire width. Flagged, NOT deleted — we don't
  // silently drop something the user pasted, we make them look at it.
  if (/^(null|undefined|nan|n\/a|na|-{1,2}|#n\/a|#value!|#ref!)$/i.test(v)) {
    level = 'warn';
    notes.push(`This cell says "${v}" — that is usually an empty cell from an export, not a measurement. Clear it or replace it.`);
  }
  return { value: v, level, note: notes.join(' ') };
}

function _paxleParseCell(col, raw) {
  const s = _paxleClean(raw);
  if (col === 'tires') return _paxleParseTires(s);
  if (col === 'spacingToNext') return _paxleParseFeet(s);
  if (col === 'groupBreakAfter') return _paxleParseBool(s);
  return _paxleParseText(s);
}

// ───────────────────────────────────────────────────────── tabular parsing
// Split ONE line. Quote handling only kicks in when a field STARTS with a
// double quote — which is what Excel/Sheets emit — so an unquoted track width
// of 8' 6" doesn't open a quote that swallows the rest of the row.
function _paxleSplitLine(line, delim) {
  const out = [];
  const n = line.length;
  let i = 0;
  for (;;) {
    let cur = '';
    if (line[i] === '"') {
      i++;
      while (i < n) {
        if (line[i] === '"') {
          if (line[i + 1] === '"') { cur += '"'; i += 2; continue; }
          i++; break;
        }
        cur += line[i++];
      }
      while (i < n && line[i] !== delim) cur += line[i++]; // junk after the close quote is kept, not dropped
    } else {
      while (i < n && line[i] !== delim) cur += line[i++];
    }
    out.push(cur);
    if (i >= n) break;
    i++; // consume the delimiter
    if (i >= n) { out.push(''); break; } // trailing delimiter = a real empty last cell
  }
  return out;
}

const _PAXLE_HEADER_WORDS = /^(#|no\.?|axle|axles|tires?|tyres?|tire\s*w(idth)?|track\s*w(idth)?|width|spacing|space|to\s*next|gap|brk|break|group|group\s*break|weight|lbs?)$/i;
function _paxleLooksLikeHeader(cells) {
  const filled = cells.map(c => _paxleClean(c)).filter(Boolean);
  if (!filled.length) return false;
  if (filled.some(c => /^\d+(\.\d+)?$/.test(c))) return false; // a bare number = data, not a header
  return filled.filter(c => _PAXLE_HEADER_WORDS.test(c)).length >= Math.ceil(filled.length / 2);
}

/**
 * Turn a pasted block into a RAW string grid. Deliberately does NOT map cells to
 * fields — the target column depends on where the cursor is, so mapping happens
 * at apply time in _paxleApplyGrid().
 * @returns {{grid: string[][], issues: string[], delim: string}}
 */
function parseAxleBlock(text) {
  const issues = [];
  const raw = String(text == null ? '' : text).replace(/\r\n?/g, '\n');
  let lines = raw.split('\n');
  while (lines.length && !lines[0].trim()) lines.shift();
  while (lines.length && !lines[lines.length - 1].trim()) lines.pop();
  if (!lines.length) return { grid: [], issues: ['There was nothing to read in what you pasted.'], delim: '\t' };

  const delim = raw.includes('\t') ? '\t' : ',';
  let grid = lines.map(l => _paxleSplitLine(l, delim));

  if (grid.length > 1 && _paxleLooksLikeHeader(grid[0])) {
    issues.push(`Skipped what looks like a header row ("${grid[0].map(c => _paxleClean(c)).filter(Boolean).join(' / ').slice(0, 60)}").`);
    grid.shift();
  }
  const beforeBlank = grid.length;
  grid = grid.filter(cells => cells.some(c => _paxleClean(c) !== ''));
  if (grid.length < beforeBlank) issues.push(`Skipped ${beforeBlank - grid.length} blank row(s).`);

  if (grid.length > PAXLE_MAX_ROWS) {
    issues.push(`Only the first ${PAXLE_MAX_ROWS} rows were read — ${grid.length - PAXLE_MAX_ROWS} more were ignored (${PAXLE_MAX_ROWS} axles is the per-unit limit).`);
    grid = grid.slice(0, PAXLE_MAX_ROWS);
  }
  return { grid, issues, delim };
}

// ──────────────────────────────────────────── permit JSON import (a prior filing)
// A carrier who has ever pulled a permit already typed this once. Accepts the
// LoadPass/ND filing export shape ({ axles: [ { tires, tireWidth, trackWidth,
// spacing, weight, groupBreakAfter } ] }) or a bare array of the same.
//
// 🔴 `weight` is DROPPED ON PURPOSE and the user is told. A filed permit's axle
// weights are that MOVE's loaded weights; the vehicle record holds equipment
// configuration only. Copying them onto the truck would turn one trip's scale
// ticket into a standing declaration.
// 🔴 A filed permit's axle list is the WHOLE COMBINATION (unit 15 files 9 axles
// across tractor + trailer + booster). Only this unit's own axles belong here,
// which is why import rows start UNCHECKED.
function parsePermitJsonAxles(text) {
  const issues = [];
  let parsed;
  try {
    parsed = JSON.parse(text);
  } catch (e) {
    return { grid: [], issues: ['That looked like JSON but it did not parse. Paste the file contents exactly as they are, or paste plain rows instead.'], isPermit: false };
  }
  let arr = null;
  if (Array.isArray(parsed)) arr = parsed;
  else if (parsed && Array.isArray(parsed.axles)) arr = parsed.axles;
  else if (parsed && Array.isArray(parsed.permitAxles)) arr = parsed.permitAxles;
  if (!arr) return { grid: [], issues: ['No axle list found in that JSON — expected an "axles" array.'], isPermit: false };

  const label = (parsed && !Array.isArray(parsed) && parsed.permitNumber) ? String(parsed.permitNumber).slice(0, 40) : '';
  if (label) issues.push(`Read permit ${label}.`);
  if (arr.length > PAXLE_MAX_ROWS) {
    issues.push(`Only the first ${PAXLE_MAX_ROWS} axles were read — ${arr.length - PAXLE_MAX_ROWS} more were ignored.`);
    arr = arr.slice(0, PAXLE_MAX_ROWS);
  }
  let droppedWeights = 0;
  const grid = arr.map(a => {
    const o = (a && typeof a === 'object') ? a : {};
    if (o.weight !== undefined && o.weight !== null && String(o.weight).trim() !== '') droppedWeights++;
    const gb = o.groupBreakAfter === true || o.groupBreakAfter === 'true' ? 'y' : 'n';
    return [
      o.tires === undefined || o.tires === null ? '' : String(o.tires),
      o.tireWidth === undefined || o.tireWidth === null ? '' : String(o.tireWidth),
      o.trackWidth === undefined || o.trackWidth === null ? '' : String(o.trackWidth),
      o.spacing !== undefined && o.spacing !== null && String(o.spacing).trim() !== ''
        ? String(o.spacing)
        : (o.spacingToNext === undefined || o.spacingToNext === null ? '' : String(o.spacingToNext)),
      gb
    ];
  });
  if (droppedWeights) {
    issues.push(`Left out the per-axle weights on ${droppedWeights} row(s) on purpose — those were that trip's LOADED weights. The vehicle record holds equipment configuration; loaded weight is collected per move.`);
  }
  if (grid.length > 3) {
    issues.push('A filed permit lists the WHOLE combination — tractor, trailer and any booster. Tick only the axles that belong to this one unit. The trailer’s axles go on the trailer record, and the coupling gap belongs to the rig, not to either vehicle.');
  }
  return { grid, issues, isPermit: true };
}

// ─────────────────────────────────────────────────────────────── grid render
function _renderPermitAxleRow(kind, axle = {}, opts = {}) {
  const div = document.createElement('div');
  div.className = 'permit-axle-row paxle-row' + (opts.unreviewed ? ' paxle-row-unreviewed' : '');
  const gb = axle.groupBreakAfter === true;
  const v = k => (axle[k] === null || axle[k] === undefined ? '' : String(axle[k]));
  div.innerHTML = `
    <span class="axle-label paxle-idx"></span>
    <input type="number" class="paxle-cell ${kind}-paxle-tires" data-paxle-col="tires" placeholder="Tires" min="0" step="1" value="${window.escAttr(v('tires'))}" aria-label="Number of tires">
    <input type="text" class="paxle-cell ${kind}-paxle-tirewidth" data-paxle-col="tireWidth" placeholder="Tire W (385 mm)" maxlength="${PAXLE_TEXT_MAX}" value="${window.escAttr(axle.tireWidth || '')}" aria-label="Tire width">
    <input type="text" class="paxle-cell ${kind}-paxle-trackwidth" data-paxle-col="trackWidth" placeholder="Track W (8' 6&quot;)" maxlength="${PAXLE_TEXT_MAX}" value="${window.escAttr(axle.trackWidth || '')}" aria-label="Track width">
    <input type="number" class="paxle-cell ${kind}-paxle-spacing" data-paxle-col="spacingToNext" placeholder="To next (ft)" min="0" step="0.1" value="${window.escAttr(v('spacingToNext'))}" aria-label="Spacing to the next axle, in feet">
    <label class="paxle-brk" title="Group break after this axle">
      <input type="checkbox" class="paxle-cell ${kind}-paxle-groupbreak" data-paxle-col="groupBreakAfter" ${gb ? 'checked' : ''} aria-label="Group break after this axle">
      <span>Brk</span>
    </label>
    <span class="paxle-row-actions">
      <button type="button" class="btn-icon btn-sm" onclick="window.RosterModule._paxleDuplicateRow('${kind}', this)" title="Copy this axle into a new row below">
        <span class="material-icons">content_copy</span>
      </button>
      <button type="button" class="btn-icon btn-icon-danger btn-sm" onclick="window.RosterModule._paxleRemoveRow('${kind}', this)" title="Remove this axle">
        <span class="material-icons">close</span>
      </button>
    </span>
  `;
  return div;
}

function _paxleRows(kind) {
  const container = document.getElementById(`${kind}-permit-axles-container`);
  return container ? Array.from(container.querySelectorAll('.permit-axle-row')) : [];
}

function _renumberPermitAxles(kind) {
  _paxleRows(kind).forEach((row, idx) => {
    const label = row.querySelector('.axle-label');
    if (label) label.textContent = `A${idx + 1}`;
  });
  _paxleSyncChrome(kind);
}

// Header + empty-state + "needs review" count all live off the row list.
function _paxleSyncChrome(kind) {
  const rows = _paxleRows(kind);
  const head = document.getElementById(`${kind}-paxle-head`);
  if (head) head.style.display = rows.length ? '' : 'none';
  const empty = document.getElementById(`${kind}-paxle-empty`);
  if (empty) empty.style.display = rows.length ? 'none' : '';
  const count = document.getElementById(`${kind}-paxle-count`);
  if (count) count.textContent = rows.length ? `${rows.length} axle${rows.length === 1 ? '' : 's'}` : '';
  const unrev = _paxleUnreviewedCount(kind);
  const rev = document.getElementById(`${kind}-paxle-review`);
  if (rev) {
    rev.style.display = unrev ? '' : 'none';
    rev.textContent = unrev ? `${unrev} row${unrev === 1 ? '' : 's'} still need a look` : '';
  }
}

function _paxleUnreviewedCount(kind) {
  return _paxleRows(kind).filter(r => r.classList.contains('paxle-row-unreviewed')).length;
}

// TASK-9267 — the permit-specs sections only auto-open for vehicles that
// ALREADY have data, so the 11/14 (TITS) trucks missing everything never
// surface the editor. Badging the collapsed header makes the gap visible
// without changing the "collapsed by default for the 95%" intent from
// TASK-525. Client-render only — no schema/rules/server change.
// COD Blast-Radius (2026-09-22) — axleSpacing is dropped entirely from Pete's
// fleetContext server-side (functions/src/index.js askPermitPete, TASK-9267
// step 2): it's 0/14 trucks + 0/12 trailers filled and superseded by
// permitAxles[].spacingToNext. Leaving it in this badge's count would nag
// "1 field missing" forever for a field Pete no longer reads at all.
const _PERMIT_DIMS_FIELDS = ['length', 'width', 'height', 'axleCount'];

function _permitFieldMissing(v) {
  if (v === undefined || v === null) return true;
  if (typeof v === 'string') return v.trim() === '';
  if (typeof v === 'number') return Number.isNaN(v);
  return false;
}

function _updatePermitSpecsBadges(kind, unit) {
  const doc = unit || {};

  const missingDims = _PERMIT_DIMS_FIELDS.filter(f => _permitFieldMissing(doc[f]));
  const dimsBadge = document.getElementById(`${kind}-dims-missing-badge`);
  if (dimsBadge) {
    if (missingDims.length) {
      dimsBadge.style.display = '';
      dimsBadge.textContent = `${missingDims.length} field${missingDims.length === 1 ? '' : 's'} missing`;
    } else {
      dimsBadge.style.display = 'none';
      dimsBadge.textContent = '';
    }
  }

  // Harvest q11 — a trailer's plate weight is optional (see _permitReadiness).
  const missingWeight = kind !== 'trailer' && _permitFieldMissing(doc.registeredWeight);
  const missingAxles = !(Array.isArray(doc.permitAxles) && doc.permitAxles.length);
  const hhMissingCount = (missingWeight ? 1 : 0) + (missingAxles ? 1 : 0);
  const hhBadge = document.getElementById(`${kind}-hh-missing-badge`);
  if (hhBadge) {
    if (hhMissingCount) {
      hhBadge.style.display = '';
      hhBadge.textContent = missingAxles && missingWeight
        ? 'weight + axles missing'
        : (missingAxles ? 'axles missing' : 'weight missing');
    } else {
      hhBadge.style.display = 'none';
      hhBadge.textContent = '';
    }
  }
}

function _loadPermitAxles(kind, axles) {
  const container = document.getElementById(`${kind}-permit-axles-container`);
  if (!container) return;
  container.innerHTML = '';
  (Array.isArray(axles) ? axles : []).forEach(a => container.appendChild(_renderPermitAxleRow(kind, a)));
  _paxleUndo[kind] = null;
  _paxleClearBanner(kind);
  _paxleBindGrid(kind);
  _renumberPermitAxles(kind);
  const sel = document.getElementById(`${kind}-paxle-preset`);
  if (sel) {
    const presets = getAxlePresets(kind);
    sel.innerHTML = `<option value="">Add a common group…</option>` +
      presets.map(p => `<option value="${window.escAttr(p.id)}">${escapeHtml(p.label)}</option>`).join('');
    sel.value = '';
  }
}

// One delegated listener set per container, installed once.
function _paxleBindGrid(kind) {
  const container = document.getElementById(`${kind}-permit-axles-container`);
  if (!container || container.dataset.paxleBound === '1') return;
  container.dataset.paxleBound = '1';
  container.addEventListener('paste', e => _paxleOnPaste(kind, e));
  container.addEventListener('keydown', e => _paxleOnKeydown(kind, e));
  // Touching a cell IS the review, so it clears both the cell flag and the
  // row's "stamped, unread" state.
  const touched = e => {
    const cell = e.target.closest('.paxle-cell');
    if (!cell) return;
    cell.classList.remove('paxle-cell-warn', 'paxle-cell-bad');
    cell.removeAttribute('title');
    const row = cell.closest('.permit-axle-row');
    if (row && row.classList.contains('paxle-row-unreviewed')) {
      row.classList.remove('paxle-row-unreviewed');
      _paxleSyncChrome(kind);
    }
  };
  container.addEventListener('input', touched);
  container.addEventListener('change', touched);
}

function _paxleAddRow(kind, axle, opts) {
  const container = document.getElementById(`${kind}-permit-axles-container`);
  if (!container) return null;
  if (_paxleRows(kind).length >= PAXLE_MAX_ROWS) return null;
  const row = _renderPermitAxleRow(kind, axle || {}, opts || {});
  container.appendChild(row);
  return row;
}

function addTruckPermitAxle() { _paxleAddRow('truck'); _renumberPermitAxles('truck'); }
function addTrailerPermitAxle() { _paxleAddRow('trailer'); _renumberPermitAxles('trailer'); }

function _paxleRemoveRow(kind, btn) {
  const row = btn.closest('.permit-axle-row');
  if (row) row.remove();
  _renumberPermitAxles(kind);
}

// Read one row's cells back out as raw strings (what the inputs actually hold).
function _paxleReadRow(row, kind) {
  return {
    tires: row.querySelector(`.${kind}-paxle-tires`)?.value ?? '',
    tireWidth: row.querySelector(`.${kind}-paxle-tirewidth`)?.value ?? '',
    trackWidth: row.querySelector(`.${kind}-paxle-trackwidth`)?.value ?? '',
    spacingToNext: row.querySelector(`.${kind}-paxle-spacing`)?.value ?? '',
    groupBreakAfter: !!row.querySelector(`.${kind}-paxle-groupbreak`)?.checked
  };
}

// Drive and trailer groups repeat — this is the button that turns 9 rows into 3.
function _paxleDuplicateRow(kind, btn) {
  const row = btn.closest('.permit-axle-row');
  if (!row) return;
  if (_paxleRows(kind).length >= PAXLE_MAX_ROWS) {
    window.showToast?.(`${PAXLE_MAX_ROWS} axles is the limit for one unit`, 'error');
    return;
  }
  const clone = _renderPermitAxleRow(kind, _paxleReadRow(row, kind));
  row.parentNode.insertBefore(clone, row.nextSibling);
  _renumberPermitAxles(kind);
  clone.querySelector('.paxle-cell')?.focus();
}

function _paxleDuplicateLast(kind) {
  const rows = _paxleRows(kind);
  if (!rows.length) {
    // Nothing to copy yet — a blank row is the honest fallback.
    _paxleAddRow(kind);
    _renumberPermitAxles(kind);
    return;
  }
  const btn = rows[rows.length - 1].querySelector('.paxle-row-actions button');
  if (btn) _paxleDuplicateRow(kind, btn);
}

// ─────────────────────────────────────────────────────────── apply / undo
function _paxleSnapshot(kind) {
  _paxleUndo[kind] = _paxleRows(kind).map(r => Object.assign(_paxleReadRow(r, kind), {
    _unreviewed: r.classList.contains('paxle-row-unreviewed')
  }));
}

function _paxleUndoLast(kind) {
  const snap = _paxleUndo[kind];
  if (!snap) return;
  const container = document.getElementById(`${kind}-permit-axles-container`);
  if (!container) return;
  container.innerHTML = '';
  snap.forEach(r => container.appendChild(_renderPermitAxleRow(kind, r, { unreviewed: r._unreviewed })));
  _paxleUndo[kind] = null;
  _paxleClearBanner(kind);
  _renumberPermitAxles(kind);
  window.showToast?.('Put the axle table back the way it was', 'success');
}

function _paxleWriteCell(row, kind, col, parsed) {
  const sel = col === 'tires' ? `.${kind}-paxle-tires`
    : col === 'tireWidth' ? `.${kind}-paxle-tirewidth`
      : col === 'trackWidth' ? `.${kind}-paxle-trackwidth`
        : col === 'spacingToNext' ? `.${kind}-paxle-spacing`
          : `.${kind}-paxle-groupbreak`;
  const el = row.querySelector(sel);
  if (!el) return;
  if (col === 'groupBreakAfter') el.checked = parsed.value === true;
  else el.value = parsed.value;
  el.classList.remove('paxle-cell-warn', 'paxle-cell-bad');
  el.removeAttribute('title');
  if (parsed.level === 'bad') { el.classList.add('paxle-cell-bad'); el.title = parsed.note; }
  else if (parsed.level === 'warn') { el.classList.add('paxle-cell-warn'); el.title = parsed.note; }
  else if (parsed.note) el.title = parsed.note;
}

/**
 * Write a raw string grid into the visible cells, starting at (startRow, startCol).
 *
 * Overwrite semantics are deliberate: a pasted cell we could NOT read blanks the
 * target and flags it red rather than leaving whatever was there. Keeping a stale
 * value inside an otherwise-freshly-pasted row is the more dangerous outcome — it
 * reads as if it came from the paste. Blank-and-red is unmistakable, and Undo
 * puts the whole table back.
 */
function _paxleApplyGrid(kind, grid, startRow, startCol, opts) {
  const options = opts || {};
  const container = document.getElementById(`${kind}-permit-axles-container`);
  if (!container) return { issues: ['Could not find the axle table.'], bad: 0, warn: 0 };

  const issues = [];
  const notes = [];
  let bad = 0, warn = 0, ignoredCells = 0, ignoredRows = 0, added = 0, converted = 0;

  grid.forEach((cells, r) => {
    const idx = startRow + r;
    let row = _paxleRows(kind)[idx];
    if (!row) {
      row = _paxleAddRow(kind, {}, { unreviewed: !!options.unreviewed });
      if (!row) { ignoredRows++; return; }
      added++;
    } else if (options.unreviewed) {
      row.classList.add('paxle-row-unreviewed');
    }
    // Column counts that don't line up are REPORTED, never re-mapped. Guessing
    // that a 6-column paste "must" have a leading axle number is how a tire
    // width ends up declared as an axle spacing.
    const expected = PAXLE_COLS.length - startCol;
    if (cells.length !== expected) {
      issues.push(`A${idx + 1} brought ${cells.length} value${cells.length === 1 ? '' : 's'}, not ${expected}. Values fill left to right from where you pasted: ${PAXLE_COLS.slice(startCol).map(c => PAXLE_COL_LABELS[c]).join(' → ')}. Check each one landed in the column you meant.`);
    }
    cells.forEach((raw, c) => {
      const col = PAXLE_COLS[startCol + c];
      if (!col) { ignoredCells++; return; }
      const parsed = _paxleParseCell(col, raw);
      _paxleWriteCell(row, kind, col, parsed);
      if (parsed.level === 'bad') { bad++; notes.push(`A${idx + 1} ${PAXLE_COL_LABELS[col]}: ${parsed.note}`); }
      else if (parsed.level === 'warn') { warn++; notes.push(`A${idx + 1} ${PAXLE_COL_LABELS[col]}: ${parsed.note}`); }
      else if (parsed.note && col === 'spacingToNext') converted++;
    });
  });

  if (ignoredRows) issues.push(`${ignoredRows} row(s) did not fit — ${PAXLE_MAX_ROWS} axles is the limit for one unit.`);
  if (ignoredCells) issues.push(`${ignoredCells} value(s) fell past the last column and were dropped. Click the leftmost cell you want to fill before pasting.`);
  if (converted) issues.push(`${converted} spacing value(s) were written in feet-and-inches and are now decimal feet.`);

  _renumberPermitAxles(kind);
  return { issues: issues.concat(notes), bad, warn, added };
}

// ───────────────────────────────────────────────────────────────── banner
function _paxleClearBanner(kind) {
  const el = document.getElementById(`${kind}-paxle-banner`);
  if (el) { el.innerHTML = ''; el.style.display = 'none'; }
}

function _paxleShowBanner(kind, title, lines, tone, allowUndo) {
  const el = document.getElementById(`${kind}-paxle-banner`);
  if (!el) return;
  const cls = tone === 'bad' ? 'paxle-banner-bad' : tone === 'warn' ? 'paxle-banner-warn' : 'paxle-banner-ok';
  el.className = `paxle-banner ${cls}`;
  el.style.display = '';
  el.innerHTML = `
    <div class="paxle-banner-title">${escapeHtml(title)}</div>
    ${lines.length ? `<ul class="paxle-banner-list">${lines.slice(0, 25).map(l => `<li>${escapeHtml(l)}</li>`).join('')}${lines.length > 25 ? `<li>…and ${lines.length - 25} more.</li>` : ''}</ul>` : ''}
    ${allowUndo ? `<button type="button" class="btn btn-secondary btn-sm paxle-undo" onclick="window.RosterModule._paxleUndoLast('${kind}')">
      <span class="material-icons" style="font-size:14px;">undo</span> Undo that
    </button>` : ''}
  `;
}

function _paxleReport(kind, res, verb, allowUndo) {
  const tone = res.bad ? 'bad' : res.warn ? 'warn' : 'ok';
  const title = res.bad
    ? `${verb} — ${res.bad} value${res.bad === 1 ? '' : 's'} could not be read and were left blank. Fill them in before saving.`
    : res.warn
      ? `${verb} — ${res.warn} value${res.warn === 1 ? '' : 's'} need a look.`
      : `${verb}. Check the numbers, then Save.`;
  _paxleShowBanner(kind, title, res.issues, tone, allowUndo !== false);
}

// ───────────────────────────────────────────────────────── paste + keyboard
function _paxleOnPaste(kind, e) {
  const cell = e.target.closest && e.target.closest('.paxle-cell');
  if (!cell || cell.type === 'checkbox') return;
  const text = (e.clipboardData || window.clipboardData)?.getData('text/plain') || '';
  // Only intercept a BLOCK. A single value with no tab and no newline is an
  // ordinary paste into one field — leave the browser alone (and a lone comma
  // stays ambiguous with a thousands separator, so it is never a delimiter here;
  // the Paste/Import box handles comma-separated blocks explicitly).
  if (!text || !/[\t\n\r]/.test(text)) return;
  e.preventDefault();

  const { grid, issues } = parseAxleBlock(text);
  if (!grid.length) {
    _paxleShowBanner(kind, 'Nothing usable in that paste.', issues, 'bad', false);
    return;
  }
  const rows = _paxleRows(kind);
  const startRow = Math.max(0, rows.indexOf(cell.closest('.permit-axle-row')));
  const startCol = Math.max(0, PAXLE_COLS.indexOf(cell.dataset.paxleCol));

  _paxleSnapshot(kind);
  const res = _paxleApplyGrid(kind, grid, startRow, startCol, {});
  res.issues = issues.concat(res.issues);
  _paxleReport(kind, res, `Pasted ${grid.length} row${grid.length === 1 ? '' : 's'}`);
}

function _paxleOnKeydown(kind, e) {
  const cell = e.target.closest && e.target.closest('.paxle-cell');
  if (!cell) return;
  // Enter must never reach the form — it would submit the vehicle mid-entry.
  if (e.key === 'Enter') {
    e.preventDefault();
    const rows = _paxleRows(kind);
    const row = cell.closest('.permit-axle-row');
    const idx = rows.indexOf(row);
    const col = cell.dataset.paxleCol;
    let next = rows[idx + 1];
    if (!next) { next = _paxleAddRow(kind); _renumberPermitAxles(kind); }
    if (next) next.querySelector(`[data-paxle-col="${col}"]`)?.focus();
    return;
  }
  // Ctrl/Cmd+D — the spreadsheet reflex for "same as the one above".
  if ((e.ctrlKey || e.metaKey) && (e.key === 'd' || e.key === 'D')) {
    e.preventDefault();
    const rows = _paxleRows(kind);
    const row = cell.closest('.permit-axle-row');
    const idx = rows.indexOf(row);
    if (idx <= 0) return;
    const prev = _paxleReadRow(rows[idx - 1], kind);
    PAXLE_COLS.forEach(col => _paxleWriteCell(row, kind, col, _paxleOk(prev[col])));
    row.classList.remove('paxle-row-unreviewed');
    _paxleSyncChrome(kind);
    cell.focus();
  }
}

// ─────────────────────────────────────────────────────────────── presets
function applyAxlePreset(kind, presetId) {
  const sel = document.getElementById(`${kind}-paxle-preset`);
  if (sel) sel.value = '';
  if (!presetId) return;
  const preset = getAxlePresets(kind).find(p => p.id === presetId);
  if (!preset) return;
  _paxleSnapshot(kind);
  const grid = preset.rows.map(r => [
    r.tires === null || r.tires === undefined ? '' : String(r.tires),
    r.tireWidth || '',
    r.trackWidth || '',
    r.spacingToNext === null || r.spacingToNext === undefined ? '' : String(r.spacingToNext),
    r.groupBreakAfter ? 'y' : 'n'
  ]);
  // Appended at the end, flagged "needs review". A preset is a typing shortcut,
  // never an assertion about THIS unit — the save path makes the user say so.
  const res = _paxleApplyGrid(kind, grid, _paxleRows(kind).length, 0, { unreviewed: true });
  res.issues.unshift(`These came from the "${preset.label}" preset. They are a starting point, not a measurement of this unit — check every number against the vehicle.`);
  _paxleReport(kind, res, `Added ${grid.length} axle${grid.length === 1 ? '' : 's'}`);
}

// Collect the permit-axle rows into the canonical permitAxles[] shape. Numerics
// coerced; blank/all-empty rows dropped. This is the ONLY path from the grid to
// a Firestore write — paste/preset/import all stop at the DOM. (Server
// re-sanitizes on every read path.)
function getPermitAxles(kind) {
  const container = document.getElementById(`${kind}-permit-axles-container`);
  if (!container) return [];
  const out = [];
  container.querySelectorAll('.permit-axle-row').forEach(row => {
    const tiresRaw = row.querySelector(`.${kind}-paxle-tires`)?.value.trim() || '';
    const tireWidth = (row.querySelector(`.${kind}-paxle-tirewidth`)?.value || '').trim().slice(0, PAXLE_TEXT_MAX);
    const trackWidth = (row.querySelector(`.${kind}-paxle-trackwidth`)?.value || '').trim().slice(0, PAXLE_TEXT_MAX);
    const spacingRaw = row.querySelector(`.${kind}-paxle-spacing`)?.value.trim() || '';
    const groupBreakAfter = !!row.querySelector(`.${kind}-paxle-groupbreak`)?.checked;
    const tires = tiresRaw === '' ? null : (Number.isFinite(Number(tiresRaw)) ? Number(tiresRaw) : null);
    const spacingToNext = spacingRaw === '' ? null : (Number.isFinite(Number(spacingRaw)) ? Number(spacingRaw) : null);
    // Drop a fully-empty row.
    if (tires === null && !tireWidth && !trackWidth && spacingToNext === null && !groupBreakAfter) return;
    out.push({ tires, tireWidth, trackWidth, spacingToNext, groupBreakAfter });
  });
  return out.slice(0, PAXLE_MAX_ROWS);
}

/**
 * Save gate for preset/import rows. "Stamp then require review" — a preset must
 * never put a number on a regulatory record just because it was convenient.
 * Resolves true when it is OK to continue.
 *
 * Re-entrancy guarded: this runs BEFORE runGuardedSave() takes its in-flight
 * lock, so a second Enter/click while the dialog is up would otherwise stack a
 * second dialog and a second save. The duplicate submit is dropped instead
 * (COD Edge Case Hunter).
 */
const _paxleConfirmPending = new Set();

async function _paxleConfirmUnreviewed(kind) {
  const n = _paxleUnreviewedCount(kind);
  if (!n) return true;
  if (typeof window.showConfirm !== 'function') return true; // never block a save on a missing helper
  if (_paxleConfirmPending.has(kind)) return false;
  _paxleConfirmPending.add(kind);
  try {
    const rows = _paxleRows(kind)
      .map((r, i) => ({ r, i }))
      .filter(x => x.r.classList.contains('paxle-row-unreviewed'))
      .map(x => `A${x.i + 1}`)
      .join(', ');
    const ok = await window.showConfirm(
      'Check the axle rows that were filled in for you',
      `${rows} came from a preset or an import, not from this vehicle. Axle spacing and tire counts go on a permit filing — a wrong one is a citation, not a typo. Saving says you have checked them against the actual unit.`,
      { confirmText: 'I checked them — save', cancelText: 'Let me look' }
    );
    if (ok) {
      _paxleRows(kind).forEach(r => r.classList.remove('paxle-row-unreviewed'));
      _paxleSyncChrome(kind);
    }
    return !!ok;
  } finally {
    _paxleConfirmPending.delete(kind);
  }
}

// ───────────────────────────────────────────────── Paste / Import dialog
let _paxleImportParsed = null;

function showPaxleImport(kind) {
  ensureModalsMounted();
  _paxleImportParsed = null;
  const kindEl = document.getElementById('paxle-import-kind');
  if (kindEl) kindEl.value = kind;
  const ta = document.getElementById('paxle-import-text');
  if (ta) ta.value = '';
  const prev = document.getElementById('paxle-import-preview');
  if (prev) prev.innerHTML = '<div class="form-hint">Paste above, then press <strong>Read it</strong>. Nothing reaches the axle table until you insert the rows, and nothing is saved until you save the vehicle.</div>';
  const actions = document.getElementById('paxle-import-actions');
  if (actions) actions.style.display = 'none';
  document.getElementById('modal-paxle-import')?.classList.remove('hidden');
  setTimeout(() => ta?.focus(), 50);
}

function readPaxleImport() {
  const text = document.getElementById('paxle-import-text')?.value || '';
  const prev = document.getElementById('paxle-import-preview');
  const actions = document.getElementById('paxle-import-actions');
  if (!prev) return;
  if (!text.trim()) {
    prev.innerHTML = '<div class="paxle-banner paxle-banner-bad"><div class="paxle-banner-title">Nothing pasted yet.</div></div>';
    if (actions) actions.style.display = 'none';
    return;
  }
  const looksJson = /^[[{]/.test(text.trim());
  const parsed = looksJson ? parsePermitJsonAxles(text) : Object.assign(parseAxleBlock(text), { isPermit: false });
  _paxleImportParsed = parsed;

  if (!parsed.grid.length) {
    prev.innerHTML = `<div class="paxle-banner paxle-banner-bad">
      <div class="paxle-banner-title">Couldn't get any axle rows out of that.</div>
      <ul class="paxle-banner-list">${parsed.issues.map(i => `<li>${escapeHtml(i)}</li>`).join('')}</ul>
    </div>`;
    if (actions) actions.style.display = 'none';
    return;
  }

  // Row-level opt-in. A block the user assembled themselves starts ticked; a
  // filed permit starts UNTICKED, because its axle list spans the whole
  // combination and only this unit's own axles belong on this record.
  const checked = parsed.isPermit ? '' : ' checked';
  const body = parsed.grid.map((cells, i) => {
    const tds = PAXLE_COLS.map((col, c) => {
      const raw = cells[c] === undefined ? '' : cells[c];
      const p = _paxleParseCell(col, raw);
      const shown = col === 'groupBreakAfter' ? (p.value ? 'break' : '—') : (p.value === '' ? '—' : p.value);
      const changed = _paxleClean(raw) !== '' && String(shown) !== _paxleClean(raw);
      const cls = p.level === 'bad' ? 'paxle-prev-bad' : p.level === 'warn' ? 'paxle-prev-warn' : '';
      const orig = changed ? `<span class="paxle-prev-orig">was ${escapeHtml(_paxleClean(raw))}</span>` : '';
      return `<td class="${cls}" title="${window.escAttr(p.note || '')}">${escapeHtml(String(shown))}${orig}</td>`;
    }).join('');
    return `<tr>
      <td><input type="checkbox" class="paxle-import-pick" data-row="${i}"${checked}></td>
      <td class="paxle-prev-idx">${i + 1}</td>${tds}
    </tr>`;
  }).join('');

  prev.innerHTML = `
    ${parsed.issues.length ? `<div class="paxle-banner paxle-banner-warn">
      <div class="paxle-banner-title">Read this before you insert:</div>
      <ul class="paxle-banner-list">${parsed.issues.map(i => `<li>${escapeHtml(i)}</li>`).join('')}</ul>
    </div>` : ''}
    <div class="paxle-import-tools">
      <button type="button" class="btn btn-secondary btn-sm" onclick="window.RosterModule._paxleImportPickAll(true)">Tick all</button>
      <button type="button" class="btn btn-secondary btn-sm" onclick="window.RosterModule._paxleImportPickAll(false)">Tick none</button>
    </div>
    <div class="paxle-prev-wrap">
      <table class="paxle-prev">
        <thead><tr><th></th><th>#</th>${PAXLE_COLS.map(c => `<th>${escapeHtml(PAXLE_COL_LABELS[c])}</th>`).join('')}</tr></thead>
        <tbody>${body}</tbody>
      </table>
    </div>
    <p class="form-hint">Red = we could not read it and it will land blank. Amber = it landed but the notation changed or it looks unusual — hover for why. Everything inserted stays editable and is flagged for review until you have been through it.</p>
  `;
  if (actions) actions.style.display = '';
}

function _paxleImportPickAll(on) {
  document.querySelectorAll('.paxle-import-pick').forEach(cb => { cb.checked = !!on; });
}

function applyPaxleImport(mode) {
  const kind = document.getElementById('paxle-import-kind')?.value;
  if (!kind || !_paxleImportParsed) return;
  const picked = Array.from(document.querySelectorAll('.paxle-import-pick'))
    .filter(cb => cb.checked)
    .map(cb => _paxleImportParsed.grid[Number(cb.dataset.row)])
    .filter(Boolean);
  if (!picked.length) {
    window.showToast?.('Tick the axle rows you want first', 'error');
    return;
  }
  _paxleSnapshot(kind);
  if (mode === 'replace') {
    const container = document.getElementById(`${kind}-permit-axles-container`);
    if (container) container.innerHTML = '';
  }
  const start = _paxleRows(kind).length;
  const res = _paxleApplyGrid(kind, picked, start, 0, { unreviewed: true });
  res.issues = (_paxleImportParsed.issues || []).concat(res.issues);
  closeModal('paxle-import');
  _paxleReport(kind, res, `${mode === 'replace' ? 'Replaced the table with' : 'Added'} ${picked.length} axle${picked.length === 1 ? '' : 's'}`);
  document.getElementById(`${kind}-heavyhaul-section`)?.setAttribute('open', 'open');
}

// ═══════════════════════════════════════════════════════════════════════════
// FLEET PERMIT-READINESS (TASK-1444 item 3)
// The checklist that turns "somebody should fill this in" into a number that
// can hit zero. Read-only: it reports what is missing and links to the edit
// form. It never writes and never fills anything in on the user's behalf.
// ═══════════════════════════════════════════════════════════════════════════

// What a unit needs before its half of a permit packet can be assembled.
// `axleCount`/`axleSpacing` (the older free-text TASK-512 fields) are NOT
// required — permitAxles[] supersedes them and demanding both would be asking
// for the same data twice.
// `kind` is 'truck' | 'trailer'. Registered (plate) weight is REQUIRED for a
// truck and OPTIONAL for a trailer (Chris 2026-10-12, harvest q11): the plate
// weight normally rides on the tractor, so 0 of 13 TITS trailers carry one and
// demanding it meant a trailer could never read permit-ready. A trailer weight
// that IS on file still has to be plausible. Callers that omit `kind` get the
// truck rule (the stricter one).
function _permitReadiness(doc, kind = 'truck') {
  const missing = [];
  // TASK-9267 — a stored number outside the band askPermitPete's
  // reValidateVehicle trusts is NOT "ready": Pete refuses to copy it, so
  // counting it as filled would show a green chip on a unit Pete will still
  // ask about. Same bands as the quick-fill cells (window.PermitQuickFill).
  const PQF = window.PermitQuickFill;
  const scalar = (key, label, optional = false) => {
    if (!(Number(doc[key]) > 0)) {
      // Absent is fine when optional; a value that IS there but is not a
      // positive number (text, a negative) is still wrong (COD 10/12).
      const present = doc[key] !== null && doc[key] !== undefined && String(doc[key]).trim() !== '';
      if (!optional) missing.push(label); else if (present) missing.push(`${label} looks wrong`);
      return;
    }
    const cell = PQF ? PQF.parseCell(key, String(doc[key])) : { state: 'ok' };
    if (cell.state === 'bad') missing.push(`${label} looks wrong`);
  };
  scalar('registeredWeight', 'registered weight', kind === 'trailer');
  scalar('length', 'length');
  scalar('width', 'width');
  scalar('height', 'height');

  const axles = Array.isArray(doc.permitAxles) ? doc.permitAxles : [];
  if (!axles.length) {
    missing.push('axle table');
  } else {
    const holes = [];
    axles.forEach((a, i) => {
      const gaps = [];
      if (!(Number(a.tires) > 0)) gaps.push('tires');
      if (!a.tireWidth) gaps.push('tire width');
      if (!a.trackWidth) gaps.push('track width');
      // The last axle has nothing behind it, so no spacing is expected there.
      if (i < axles.length - 1 && !(Number(a.spacingToNext) > 0)) gaps.push('spacing');
      if (gaps.length) holes.push(`A${i + 1} (${gaps.join(', ')})`);
    });
    if (holes.length) missing.push(`axle detail: ${holes.slice(0, 6).join(', ')}${holes.length > 6 ? `, +${holes.length - 6} more` : ''}`);
  }
  return { ready: missing.length === 0, missing, axleCount: axles.length };
}

function showPermitReadiness() {
  ensureModalsMounted();
  renderPermitReadiness();
  document.getElementById('modal-permit-readiness').classList.remove('hidden');
}

// TASK-9267 — the screen used to be read-only: a list of what was missing and
// a "Fill in" button that opened the unit's FULL edit form with the permit
// sections collapsed. On TITS 2026-10-08 that left 0 of 14 trucks and 0 of 13
// trailers permit-ready. Each unit is now a card that takes the four numbers
// Pete reads (length, width, height, registered weight) right here, with one
// Save per card. The axle table stays in the full form (it is a grid, not a
// number) and "Axle table" opens the form straight at it.
// Rules for what a typed cell means: public/src/utils/permitQuickFill.js.
// Baselines = the stored values when the card was drawn; Save refuses any
// field that changed underneath it (another tab, another person).
const _pqfBaseline = new Map();
let _pqfBound = false;

function _pqfKey(kind, id) { return `${kind}:${id}`; }

function _pqfUnitDoc(kind, id) {
  const list = kind === 'truck' ? (RosterState.trucks || []) : (RosterState.trailers || []);
  return list.find(t => t.id === id) || null;
}

function _pqfChipsHtml(state) {
  return state.ready
    ? '<span class="paxle-ready-chip paxle-ready-yes">permit-ready</span>'
    : state.missing.map(m => `<span class="paxle-ready-chip paxle-ready-no">${escapeHtml(m)}</span>`).join('');
}

function _pqfAxleLine(doc, state) {
  const axles = Array.isArray(doc.permitAxles) ? doc.permitAxles : [];
  if (!axles.length) return 'Axle table: not entered';
  const gap = state.missing.find(m => m.startsWith('axle detail'));
  return `Axle table: ${axles.length} axle${axles.length === 1 ? '' : 's'}${gap ? ' — still has gaps' : ' — complete'}`;
}

function _pqfCardHtml(s) {
  const PQF = window.PermitQuickFill;
  const label = getUnitDisplay(s.doc);
  const base = PQF.baselineOf(s.doc);
  _pqfBaseline.set(_pqfKey(s.kind, s.doc.id), base);
  const idAttr = escapeHtml(s.doc.id);
  const cells = PQF.FIELDS.map(f => {
    const v = base[f.key];
    // Harvest q11 — a trailer's registered weight is optional: empty is not
    // "missing", so no red cell and no missing tag; just a quiet hint.
    const optional = f.key === 'registeredWeight' && s.kind === 'trailer';
    const missing = v === null && !optional;
    const inputId = `pqf-${s.kind}-${idAttr}-${f.key}`;
    // Placeholder is the UNIT only — never an example number, which reads
    // like a value at a glance (rule: nothing is filled in for the user).
    return `<div class="pqf-cell${missing ? ' pqf-cell-missing' : ''}" data-field="${f.key}">
      <label for="${inputId}">${escapeHtml(f.label)} <span class="pqf-unit">(${f.unit})</span>${missing ? ' <span class="pqf-tag">missing</span>' : (optional && v === null ? ' <span class="pqf-tag">optional</span>' : '')}</label>
      <input type="text" id="${inputId}" class="pqf-input" data-field="${f.key}" inputmode="${f.feet ? 'text' : 'numeric'}" autocomplete="off"
        value="${v === null ? '' : escapeHtml(String(v))}" placeholder="${f.unit}">
      <div class="pqf-err" aria-live="polite"></div>
    </div>`;
  }).join('');
  return `<div class="pqf-card${s.state.ready ? ' pqf-card-ready' : ''}" data-kind="${s.kind}" data-id="${idAttr}">
    <div class="pqf-card-head">
      <span class="pqf-card-name">
        <span class="material-icons">${s.kind === 'truck' ? 'local_shipping' : 'rv_hookup'}</span>${escapeHtml(label)}
      </span>
      <span class="paxle-ready-chips pqf-chips">${_pqfChipsHtml(s.state)}</span>
    </div>
    <div class="pqf-grid">${cells}</div>
    <div class="pqf-axles">
      <span class="pqf-axle-text">${escapeHtml(_pqfAxleLine(s.doc, s.state))}</span>
    </div>
    <div class="pqf-actions">
      <button type="button" class="btn btn-secondary pqf-btn" data-pqf-action="save">
        <span class="material-icons">save</span> Save
      </button>
      <button type="button" class="btn btn-secondary pqf-btn" data-pqf-action="axles">
        <span class="material-icons">tune</span> Axle table
      </button>
      <button type="button" class="btn btn-secondary pqf-btn" data-pqf-action="full">
        <span class="material-icons">edit</span> Full form
      </button>
      <span class="pqf-status" aria-live="polite"></span>
    </div>
  </div>`;
}

function _pqfScoreHtml(scored) {
  const ready = scored.filter(s => s.state.ready).length;
  const pct = Math.round((ready / scored.length) * 100);
  return `<div class="paxle-ready-score"><strong>${ready}</strong> of <strong>${scored.length}</strong> units permit-ready</div>
      <div class="paxle-ready-bar"><div class="paxle-ready-bar-fill" style="width:${pct}%;"></div></div>`;
}

function _pqfActiveUnits() {
  return []
    .concat((RosterState.trucks || []).filter(t => t.active !== false).map(t => ({ kind: 'truck', doc: t })))
    .concat((RosterState.trailers || []).filter(t => t.active !== false).map(t => ({ kind: 'trailer', doc: t })));
}

function renderPermitReadiness() {
  const body = document.getElementById('permit-readiness-body');
  if (!body) return;
  _pqfBind(body);
  _pqfBaseline.clear();

  const units = _pqfActiveUnits();
  if (!units.length) {
    body.innerHTML = `<div class="empty-state"><div class="empty-state-icon">📋</div>
      <p>No active vehicles or trailers yet. Add some on the Vehicles and Trailers tabs first.</p></div>`;
    return;
  }

  const scored = units.map(u => Object.assign({}, u, { state: _permitReadiness(u.doc, u.kind) }));
  // Not-ready first — this screen exists to be worked down, not admired.
  scored.sort((a, b) => (a.state.ready === b.state.ready)
    ? String(getUnitDisplay(a.doc)).localeCompare(String(getUnitDisplay(b.doc)), undefined, { numeric: true })
    : (a.state.ready ? 1 : -1));

  body.innerHTML = `
    <div class="paxle-ready-head" id="pqf-score">${_pqfScoreHtml(scored)}</div>
    <p class="form-hint">Type what is on the unit and tap <strong>Save</strong> on that card (feet: 8.5 or 8'6"). Nothing is filled in for you. Permit-ready means registered weight (trucks — optional for trailers, whose plate weight usually rides on the tractor), overall dimensions and a complete axle table are on file — a person still checks every permit, and loaded weights and the route are collected per move. This checks each unit's own record. Permit Pete does not stop for a missing length, width or height, but uses them to catch a declared size that is too small. A rig's coupling gaps are on the Rigs tab.</p>
    <div class="pqf-list">${scored.map(_pqfCardHtml).join('')}</div>`;
}

function _pqfRefreshScore() {
  const head = document.getElementById('pqf-score');
  if (!head) return;
  const scored = _pqfActiveUnits().map(u => Object.assign({}, u, { state: _permitReadiness(u.doc, u.kind) }));
  if (scored.length) head.innerHTML = _pqfScoreHtml(scored);
}

function _pqfBind(body) {
  if (_pqfBound) return;
  _pqfBound = true;
  body.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-pqf-action]');
    if (!btn) return;
    const card = btn.closest('.pqf-card');
    if (!card) return;
    const { kind, id } = card.dataset;
    const action = btn.dataset.pqfAction;
    if (action === 'save') _pqfSave(card);
    else if (action === 'axles') _permitReadinessEdit(kind, id, 'axles');
    else if (action === 'full') _permitReadinessEdit(kind, id);
  });
  // Clear a cell's error as soon as it is edited again.
  body.addEventListener('input', (e) => {
    const input = e.target.closest('.pqf-input');
    if (!input) return;
    const cell = input.closest('.pqf-cell');
    cell?.classList.remove('pqf-cell-bad');
    const err = cell?.querySelector('.pqf-err');
    if (err) err.textContent = '';
    const status = input.closest('.pqf-card')?.querySelector('.pqf-status');
    if (status) { status.textContent = ''; status.className = 'pqf-status'; }
  });
}

function _pqfSetStatus(card, text, tone) {
  const el = card.querySelector('.pqf-status');
  if (!el) return;
  el.textContent = text;
  el.className = `pqf-status${tone ? ` pqf-status-${tone}` : ''}`;
}

async function _pqfSave(card) {
  const PQF = window.PermitQuickFill;
  const { kind, id } = card.dataset;
  const btn = card.querySelector('[data-pqf-action="save"]');
  if (!PQF || !btn || btn.disabled) return;
  const key = _pqfKey(kind, id);
  const baseline = _pqfBaseline.get(key) || PQF.baselineOf(_pqfUnitDoc(kind, id));

  const inputs = {};
  card.querySelectorAll('.pqf-input').forEach(inp => { inputs[inp.dataset.field] = inp.value; });
  const res = PQF.buildPatch(baseline, inputs);

  card.querySelectorAll('.pqf-cell').forEach(cell => {
    const msg = res.errors[cell.dataset.field];
    cell.classList.toggle('pqf-cell-bad', !!msg);
    const err = cell.querySelector('.pqf-err');
    if (err) err.textContent = msg || '';
  });
  if (Object.keys(res.errors).length) {
    _pqfSetStatus(card, 'Not saved — fix the red box first.', 'bad');
    return;
  }
  const keys = Object.keys(res.patch);
  if (!keys.length) {
    _pqfSetStatus(card, 'Nothing changed.', '');
    return;
  }

  // Over-declare rule: going LOWER than what is saved is the dangerous
  // direction on a permit, so it is never a silent edit.
  if (res.lowered.length) {
    const unitLabel = getUnitDisplay(_pqfUnitDoc(kind, id) || {});
    const msg = `${unitLabel}: you are lowering ${res.lowered.map(l => l.text).join('; ')}. Permits are declared at or above the real number. Lower it anyway?`;
    const ok = window.showConfirm
      ? await window.showConfirm('Lower a saved number?', msg, { confirmText: 'Lower it', cancelText: 'Keep saved', danger: true })
      : confirm(msg);
    if (!ok) { _pqfSetStatus(card, 'Not saved.', ''); return; }
  }

  const tenant = window.AppState?.tenant || 'demo';
  const coll = kind === 'truck' ? 'trucks' : 'trailers';
  const label = btn.innerHTML;
  btn.disabled = true;
  btn.innerHTML = '<span class="material-icons">hourglass_top</span> Saving…';
  _pqfSetStatus(card, '', '');
  try {
    const ref = doc(db, 'tenants', tenant, coll, id);
    // Read-check-write in ONE transaction: a field that changed after this card
    // was drawn (another tab / person) is refused, never overwritten — and no
    // other writer can slip in between the check and the write.
    const outcome = await withWriteTimeout(runTransaction(db, async (tx) => {
      const fresh = await tx.get(ref);
      if (!fresh.exists()) return { gone: true };
      const stale = PQF.staleFields(baseline, fresh.data(), keys);
      if (stale.length) return { stale };
      tx.update(ref, Object.assign({}, res.patch, { updatedAt: new Date() }));
      return { ok: true };
    }), 15000, 'update unit');
    if (outcome.gone) {
      _pqfSetStatus(card, 'This unit was deleted. Close and reopen this screen.', 'bad');
      return;
    }
    if (outcome.stale) {
      const names = outcome.stale.map(k => (PQF.FIELDS.find(f => f.key === k) || {}).label || k).join(', ');
      _pqfSetStatus(card, `Not saved — ${names} changed since you opened this. Close and reopen to see the new number.`, 'bad');
      return;
    }
    const unitDoc = _pqfUnitDoc(kind, id) || {};
    // Old + new on every changed field — these govern what goes on a permit.
    window.logAudit?.('roster', kind === 'truck' ? 'UPDATE_TRUCK' : 'UPDATE_TRAILER', {
      [kind === 'truck' ? 'truckId' : 'trailerId']: id,
      unit: unitDoc.unit || '',
      source: 'permit-readiness-quick-fill',
      changes: res.changes,
    });
    const nextBase = Object.assign({}, baseline, res.patch);
    _pqfBaseline.set(key, nextBase);
    // Repaint this card's chips from the merged doc now; the roster snapshot
    // listener will catch RosterState up on its own.
    const merged = Object.assign({}, unitDoc, res.patch);
    const state = _permitReadiness(merged, kind);
    const chips = card.querySelector('.pqf-chips');
    if (chips) chips.innerHTML = _pqfChipsHtml(state);
    card.classList.toggle('pqf-card-ready', state.ready);
    card.querySelectorAll('.pqf-cell').forEach(cell => {
      if (nextBase[cell.dataset.field] !== null) {
        cell.classList.remove('pqf-cell-missing');
        cell.querySelector('.pqf-tag')?.remove();
      }
    });
    if (unitDoc.id) Object.assign(unitDoc, res.patch);
    _pqfRefreshScore();
    _pqfSetStatus(card, `Saved ${keys.length} field${keys.length === 1 ? '' : 's'} ✓`, 'ok');
  } catch (err) {
    console.error('[Roster] permit quick fill save failed:', err);
    const denied = /permission/i.test(String(err?.code || err?.message || ''));
    _pqfSetStatus(card, denied ? 'Not saved — your login cannot edit vehicles.' : 'Not saved — check your connection and try again.', 'bad');
  } finally {
    btn.disabled = false;
    btn.innerHTML = label;
  }
}

// `section` = 'axles' opens the form with Heavy Haul / Permit Specs expanded
// and scrolled into view. Both permit sections are always opened from here —
// the form's own default keeps them shut for a unit with no permit data, which
// is exactly the unit this screen sends you to.
function _permitReadinessEdit(kind, id, section) {
  closeModal('permit-readiness');
  const openSections = () => {
    const dims = document.getElementById(`${kind}-dimensions-section`);
    const hh = document.getElementById(`${kind}-heavyhaul-section`);
    if (dims) dims.open = true;
    if (hh) hh.open = true;
    const target = section === 'axles' ? hh : dims;
    target?.scrollIntoView({ block: 'start', behavior: 'smooth' });
  };
  if (kind === 'truck') { switchTab('trucks'); setTimeout(() => { editTruck(id); openSections(); }, 60); }
  else { switchTab('trailers'); setTimeout(() => { editTrailer(id); openSections(); }, 60); }
}

// ============================================================================
// RIG COMBINATIONS — tenants/{tid}/equipmentTemplates (TASK-1443)
// ----------------------------------------------------------------------------
// A rig is a tractor + one or more trailers/boosters run TOGETHER. The reason
// this collection exists at all: the sliding fifth wheel / kingpin setting and
// the sliding trailer tandems produce a distance (last drive axle → first
// trailer axle) that belongs to the COMBINATION, not to either vehicle. TITS's
// own filings prove it — same load, same dims, but unit 01 files 7 axles @
// 120,000 lb while unit 15 files 9 axles @ 155,000 lb, because 15 pulls a
// trailer + booster. Bake that gap onto the tractor and the Bridge Formula
// input (23 USC 127) is wrong the moment that tractor pulls a different
// trailer — a wrong axle-spacing declaration is permit-revocation /
// weigh-station-citation territory.
//
// INVARIANTS (do not "simplify" these away):
//  1. NOTHING here writes to truck.permitAxles / trailer.permitAxles. Those hold
//     each unit's OWN internal axles. Nor to truck.axles — that's the axle
//     SERIAL-number list used by maintenance, a completely different field.
//  2. `permitAxles` on the template is DERIVED. It is recomputed from the source
//     vehicles on every save and never hand-edited. `assembly.fingerprint` lets
//     the UI (and a downstream reader) notice a source vehicle changed since.
//  3. Per-axle LOADED weight never lands here — that's per-move.
//  4. The computed first→last axle distance is stored as `assembly.extremeSpacing`
//     and shown for reference. It is NOT written as `bridgeSpacing`: that is a
//     human attestation and must never be silently populated by a derived number.
// ============================================================================

function _rigNum(v) {
  if (v === null || v === undefined || v === '') return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

function _rigRound(n) {
  return n === null || n === undefined ? null : Math.round(n * 100) / 100;
}

// COD Code Reviewer ⚠️ — anything read back OUT of the equipmentTemplates doc and
// interpolated into innerHTML is attacker-controllable via a raw SDK write (the
// rules gate WHO writes, not WHAT). Numbers get coerced to real numbers here so a
// string payload can never survive into markup; strings go through escapeHtml.
function _rigStoredNum(v) {
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}
function _rigStoredCount(v) {
  const n = Math.trunc(Number(v));
  return Number.isFinite(n) && n >= 0 ? n : 0;
}

function _rigKindLabel(slug) {
  return (RIG_KINDS.find(k => k.slug === slug) || {}).label || (slug || '');
}

function _rigTruckById(id) {
  return RosterState.trucks.find(t => t.id === id) || null;
}
function _rigTrailerById(id) {
  return RosterState.trailers.find(t => t.id === id) || null;
}

/**
 * Assemble the CONTINUOUS axle sequence for a rig.
 *
 * truck.permitAxles[] → couplings[0].gap → trailer[0].permitAxles[] → couplings[1].gap → trailer[1]…
 *
 * The gap for a joint is written onto the LAST axle of the unit in FRONT of it
 * (spacingToNext), which is what makes the result one ordered list a permit desk
 * can read straight down. Each such axle is tagged `isCouplingGap: true` so a
 * reader can tell an inter-unit gap from a unit's own internal spacing, and
 * carries spacingToNextMin/Max when that joint slides.
 *
 * @param {object|null} truck  power unit doc
 * @param {Array} units  [{ trailerId, trailer, gap, gapMin, gapMax }] front→rear
 * @returns {{axles:Array, warnings:string[], incomplete:boolean, axleCount:number,
 *           extremeSpacing:number|null, extremeSpacingMin:number|null, extremeSpacingMax:number|null,
 *           hasSlider:boolean}}
 */
function assembleRigSequence(truck, units) {
  const axles = [];
  const warnings = [];

  const chain = [{ type: 'truck', id: truck ? truck.id : null, doc: truck, joint: null }];
  (units || []).forEach(u => chain.push({ type: 'trailer', id: u.trailerId || null, doc: u.trailer || null, joint: u }));

  let prevLabel = '';
  // Did the unit immediately in front contribute any axles? If it didn't, there
  // is no axle to hang this joint's gap on — and writing it onto the last axle
  // we happen to have would CLOBBER the previous joint's gap and silently
  // produce a shorter, wrong sequence. Broken chain gets a warning, not a guess.
  let prevUnitContributed = false;
  chain.forEach((link, i) => {
    const label = link.doc
      ? getUnitDisplay(link.doc)
      : (link.id ? `Missing ${link.type} (${link.id})` : `Unassigned ${link.type}`);

    if (!link.doc) {
      warnings.push(`${label} is not in the roster — its axles are missing from this sequence.`);
    }

    // Close the previous unit with this joint's gap BEFORE appending our axles.
    if (i > 0) {
      const j = link.joint || {};
      const prevAxle = prevUnitContributed && axles.length ? axles[axles.length - 1] : null;
      const jointName = i === 1 ? 'fifth wheel' : 'coupling';
      if (!prevAxle) {
        warnings.push(`Nothing in front of ${label} to measure the ${jointName} gap from — the sequence is broken.`);
      } else {
        prevAxle.isCouplingGap = true;
        prevAxle.spacingToNext = _rigRound(j.gap);
        prevAxle.spacingToNextMin = _rigRound(j.gapMin);
        prevAxle.spacingToNextMax = _rigRound(j.gapMax);
        if (prevAxle.spacingToNext === null) {
          warnings.push(`The ${jointName} gap between ${prevLabel} and ${label} is blank — the axle sequence has a hole in it.`);
        }
      }
    }

    const src = Array.isArray(link.doc && link.doc.permitAxles) ? link.doc.permitAxles : [];
    if (link.doc && src.length === 0) {
      warnings.push(`${label} has no per-axle permit configuration — fill in Heavy Haul / Permit Specs on that unit.`);
    }
    src.forEach((a, idx) => {
      axles.push({
        // canonical permitAxles contract (must stay first-class + identically named)
        tires: _rigNum(a.tires),
        tireWidth: (a.tireWidth || '').toString().slice(0, 20),
        trackWidth: (a.trackWidth || '').toString().slice(0, 20),
        spacingToNext: _rigRound(_rigNum(a.spacingToNext)),
        groupBreakAfter: a.groupBreakAfter === true,
        // provenance (additive)
        unitType: link.type,
        unitId: link.id,
        unitLabel: label,
        axleIndexOnUnit: idx + 1,
        isCouplingGap: false,
        spacingToNextMin: null,
        spacingToNextMax: null
      });
    });
    prevLabel = label;
    prevUnitContributed = src.length > 0;
  });

  // Nothing follows the last axle.
  if (axles.length) {
    const last = axles[axles.length - 1];
    last.spacingToNext = null;
    last.isCouplingGap = false;
    last.spacingToNextMin = null;
    last.spacingToNextMax = null;
  }

  // First→last axle distance. Reference only — never auto-attested.
  let sum = 0, sumMin = 0, sumMax = 0, complete = axles.length > 0, hasSlider = false;
  for (let i = 0; i < axles.length - 1; i++) {
    const a = axles[i];
    if (a.spacingToNext === null) { complete = false; break; }
    const lo = a.spacingToNextMin !== null && a.spacingToNextMin !== undefined ? a.spacingToNextMin : a.spacingToNext;
    const hi = a.spacingToNextMax !== null && a.spacingToNextMax !== undefined ? a.spacingToNextMax : a.spacingToNext;
    if (a.spacingToNextMin !== null || a.spacingToNextMax !== null) hasSlider = true;
    sum += a.spacingToNext;
    sumMin += lo;
    sumMax += hi;
  }
  if (axles.length > 1 && !complete) {
    warnings.push('At least one axle spacing is blank, so the overall axle spacing can\'t be computed.');
  }

  return {
    axles,
    warnings,
    incomplete: warnings.length > 0 || axles.length === 0,
    axleCount: axles.length,
    extremeSpacing: complete && axles.length > 1 ? _rigRound(sum) : null,
    extremeSpacingMin: complete && axles.length > 1 ? _rigRound(sumMin) : null,
    extremeSpacingMax: complete && axles.length > 1 ? _rigRound(sumMax) : null,
    hasSlider
  };
}

// Stable signature of an assembled sequence. Compare a freshly-recomputed one
// against the stored `assembly.fingerprint` to detect that a SOURCE VEHICLE was
// edited after this rig was last saved (derived data must never drift silently).
function _rigFingerprint(axles) {
  try {
    const s = v => (v === null || v === undefined ? '' : v);
    return JSON.stringify((axles || []).map(a => [
      a.unitId || '',
      // unitLabel is PERSISTED into every stored axle, so renaming a truck leaves
      // stale display strings riding onto a permit packet with no staleness badge
      // (blast-radius). Cover it here; better long-term is to stop persisting
      // display strings in a regulatory structure and re-resolve at render.
      a.unitLabel || '',
      s(a.tires),
      a.tireWidth || '',
      a.trackWidth || '',
      s(a.spacingToNext),
      a.groupBreakAfter ? 1 : 0,
      // COD Code Reviewer ⚠️ — min/max are template-owned today, so they can't
      // drift from a vehicle edit. Covered anyway so the check stays honest if a
      // future slider range ever becomes vehicle-derived.
      s(a.spacingToNextMin),
      s(a.spacingToNextMax)
    ]));
  } catch (_) {
    return '';
  }
}

// Re-derive a saved rig from the CURRENT vehicle records.
function _rigAssemblyFromDoc(rig) {
  const truck = _rigTruckById(rig.truckId);
  const units = (rig.trailerIds || []).map((tid, i) => {
    const c = (rig.couplings || [])[i] || {};
    return {
      trailerId: tid,
      trailer: _rigTrailerById(tid),
      gap: _rigNum(c.gap),
      gapMin: _rigNum(c.gapMin),
      gapMax: _rigNum(c.gapMax)
    };
  });
  return assembleRigSequence(truck, units);
}

function _rigIsStale(rig) {
  if (!rig || !rig.assembly || !rig.assembly.fingerprint) return false;
  // Vehicles still streaming in — don't cry stale on a half-loaded roster.
  if (!RosterState._trucksLoaded || !RosterState._trailersLoaded) return false;
  return _rigFingerprint(_rigAssemblyFromDoc(rig).axles) !== rig.assembly.fingerprint;
}

// ---------------------------------------------------------------- list render

function filterRigs() {
  RosterState.filters.rigs.search = (document.getElementById('search-rigs')?.value || '').trim();
  renderRigsList();
}

// TASK-10139 follow-up 3 — who may create / change / delete a rig. Mirror of
// the equipmentTemplates write arm etEditor in firestore.rules:
//   isTenantAdmin || isComplianceManager
//   || ((roster || compliance module) && !viewUploadOnly)
// so a View & upload only login is not shown Add Rig / Save / Delete only to
// get "Missing or insufficient permissions" (TASK-10139 S2 made create use
// etEditor too). The server still decides. Demo and a partner client session
// (isComplianceManager arm) pass. A session with NO members doc is refused:
// every other etEditor arm (isTenantAdmin, hasModule) reads that doc, so the
// rules deny it even for the owner by id (TASK-10139 left-open 2; it used to
// see Save and then hit a permission error). While the members read is still
// in flight (memberAccessPending) the buttons stay, so a real editor's first
// paint is not stripped. A revoked / inactive / pending / removed / suspended
// member is refused (the rules' mStOk on both the role and the module arm)
// unless they are the tenant owner by id. Pure so the unit test executes it.
const RIG_BLOCKED_STATUS = /^\s*(revoked|inactive|pending|removed|suspended)\s*$/i;
function _rigEditGate(memberAccess, isDemo, isPartner, isOwnerById, isPending) {
  if (isDemo === true || isPartner === true) return true;
  const m = memberAccess;
  if (!m) return isPending === true;
  if (isOwnerById !== true && RIG_BLOCKED_STATUS.test(String(m.status == null ? '' : m.status))) return false;
  if (['owner', 'admin'].includes(m.role)) return true; // rules' isTenantAdmin arm
  const mods = Array.isArray(m.modules) ? m.modules : [];
  return (mods.includes('roster') || mods.includes('compliance')) && m.viewUploadOnly !== true;
}
function _rigCanEdit() {
  const S = window.AppState || {};
  const partner = typeof window.isPartnerClientSession === 'function' && window.isPartnerClientSession() === true;
  const uid = auth?.currentUser?.uid;
  return _rigEditGate(S.memberAccess, S.isDemo === true || S.tenant === 'demo', partner, !!(uid && S.tenantData?.ownerId === uid), S.memberAccessPending === true);
}
// COD r2 (UX): a view-only open locks every field + the row / Add trailer
// buttons, so nobody types a change they cannot save. Cancel stays live.
function _rigFormLock(locked) {
  const form = document.getElementById('form-rig');
  if (!form || typeof form.querySelectorAll !== 'function') return;
  form.querySelectorAll('input, select, textarea, #rig-units-container button, #btn-add-rig-trailer')
    .forEach((el) => { el.disabled = locked === true; });
}
const RIG_VIEW_ONLY_MSG = 'Your login can view rigs but not add or change them — ask the owner or an admin.';

function renderRigsList() {
  const container = document.getElementById('list-rigs');
  if (!container) return;
  const addBtn = document.getElementById('btn-add-rig');
  if (addBtn) addBtn.style.display = _rigCanEdit() ? '' : 'none';
  const modal = document.getElementById('modal-rig');
  if (modal && !modal.classList.contains('hidden')) return;

  const search = (RosterState.filters.rigs.search || '').toLowerCase();
  let filtered = RosterState.rigs;
  if (search) {
    filtered = filtered.filter(r =>
      (r.name || '').toLowerCase().includes(search) ||
      (r.notes || '').toLowerCase().includes(search) ||
      _rigKindLabel(r.kind).toLowerCase().includes(search)
    );
  }

  if (RosterState.rigs.length === 0) {
    container.innerHTML = !RosterState._rigsLoaded
      ? `<div style="text-align:center;padding:var(--spacing-xl);">
           <div class="btn-spinner" style="width:32px;height:32px;margin:0 auto var(--spacing-md);"></div>
           <div style="color:var(--text-secondary);font-size:0.875rem;">Loading rigs…</div>
         </div>`
      : `<div class="empty-state">
           <div class="empty-state-icon">🚛</div>
           <div class="empty-state-title">No rigs yet</div>
           <div class="empty-state-text">A rig is a truck and the trailers it pulls, like "15 + 104 + booster". Save one so Permit Pete knows the gap between the truck's axles and the trailer's.</div>
           <div id="rig-suggest-empty" style="max-width:560px;margin:12px auto 0;"></div>
         </div>`;
    if (_rigCanEdit()) _pgeo('rigSuggest', 'rig-suggest-empty'); // TASK-10139 S4 item 1
    return;
  }

  if (filtered.length === 0) {
    container.innerHTML = `
      <div class="empty-state">
        <div class="empty-state-icon">🔍</div>
        <div class="empty-state-title">No matches</div>
        <div class="empty-state-text">Try adjusting your search.</div>
      </div>
    `;
    return;
  }

  container.innerHTML = filtered.map(rig => {
    const truck = _rigTruckById(rig.truckId);
    const parts = [truck ? getUnitDisplay(truck) : 'Missing vehicle']
      .concat((rig.trailerIds || []).map(tid => {
        const tr = _rigTrailerById(tid);
        return tr ? getUnitDisplay(tr) : 'Missing trailer';
      }));
    const axleCount = _rigStoredCount((rig.assembly && rig.assembly.axleCount) || (rig.permitAxles || []).length);
    // Fifth wheel ONLY (couplings[0]) — a multi-joint rig has more gaps than this.
    const gap = _rigStoredNum(rig.fifthWheelGap);
    const gapMin = _rigStoredNum(rig.fifthWheelGapMin);
    const gapMax = _rigStoredNum(rig.fifthWheelGapMax);
    const gapTxt = gap === null
      ? 'gap not set'
      : `${gap} ft fifth wheel${(gapMin !== null || gapMax !== null)
          ? ` (${gapMin !== null ? gapMin : gap}–${gapMax !== null ? gapMax : gap} ft)` : ''}`;
    const stale = _rigIsStale(rig);
    return `
    <div class="roster-card roster-card-clickable" data-id="${window.escAttr(rig.id)}" onclick="window.RosterModule.openRigEditor(this.dataset.id)">
      <div class="roster-card-icon"><span class="material-icons">link</span></div>
      <div class="roster-card-info">
        <div class="roster-card-name">
          ${escapeHtml(rig.name || 'Untitled rig')}
          ${rig.incomplete ? '<span class="inspection-due-flag" title="Axle sequence incomplete — not permit-ready"><span class="material-icons">warning</span></span>' : ''}
        </div>
        <div class="roster-card-meta">
          ${rig.kind ? `<span class="badge">${escapeHtml(_rigKindLabel(rig.kind))}</span>` : ''}
          <span>${escapeHtml(parts.join(' + '))}</span>
          <span>${axleCount} axle${axleCount === 1 ? '' : 's'}</span>
          <span>${escapeHtml(gapTxt)}</span>
        </div>
        ${stale ? `<div class="form-hint" style="color:var(--warning-color, #D97706);margin-top:2px;">
            <span class="material-icons" style="font-size:14px;vertical-align:text-bottom;">sync_problem</span>
            A source vehicle changed since this rig was saved — open and re-save to refresh the sequence.
          </div>` : ''}
        <div class="form-hint pgeo-rig-missing" data-rig-id="${window.escAttr(rig.id)}" style="display:none;"></div>
      </div>
      <span class="roster-card-chevron material-icons">chevron_right</span>
    </div>`;
  }).join('');
  _pgeo('fillRigMissing', container); // TASK-9993 — what each rig still needs for the axle placement
}

// ---------------------------------------------------------------- unit rows

// TASK-10139 S4 item 4 — unit number FIRST ("#104 — Stellar Flat Bed"): a long
// nickname used to push the number off the end of the picker.
function _rigOptionsHtml(list, selectedId, what) {
  return `<option value="">— Select a ${what} —</option>` + list
    .filter(t => t.active !== false || t.id === selectedId)
    .sort((a, b) => (a.unit || '').localeCompare(b.unit || '', undefined, { numeric: true }))
    .map(t => `<option value="${window.escAttr(t.id)}"${t.id === selectedId ? ' selected' : ''}>${escapeHtml(t.unit ? `#${t.unit}${t.nickname ? ` — ${t.nickname}` : ''}` : getUnitDisplay(t))}${(t.permitAxles || []).length ? '' : ' (no permit axles)'}</option>`)
    .join('');
}
function _rigTruckOptionsHtml(selectedId) { return _rigOptionsHtml(RosterState.trucks, selectedId, 'vehicle'); }
function _rigTrailerOptionsHtml(selectedId) { return _rigOptionsHtml(RosterState.trailers, selectedId, 'trailer'); }

function _renderRigUnitRow(preset) {
  const p = preset || {};
  const div = document.createElement('div');
  div.className = 'rig-unit-row';
  div.style.cssText = 'border:1px solid var(--border-light);border-radius:var(--radius-sm);padding:10px;margin-bottom:8px;background:var(--bg-subtle, transparent);';
  // TASK-10139 S4 item 8 — where the gap came from (the lazy chunk draws the picker).
  div.dataset.from = (p.src && p.src.from) || '';
  div.dataset.pn = (p.src && p.src.permitNo) || '';
  const gapIn = (cls, v, ph) => `<input type="text" inputmode="decimal" autocomplete="off" class="${cls}" placeholder="${ph}" value="${window.escAttr(v === null || v === undefined ? '' : String(v))}" oninput="window.RosterModule.refreshRigAssembly()" style="width:100%;">`;
  div.innerHTML = `
    <div style="display:flex;align-items:center;gap:8px;margin-bottom:8px;">
      <span class="rig-unit-label" style="font-size:0.75rem;font-weight:700;color:var(--text-secondary);white-space:nowrap;"></span>
      <select class="rig-unit-select" style="flex:1;min-width:0;" onchange="window.RosterModule.refreshRigAssembly()">${_rigTrailerOptionsHtml(p.trailerId || '')}</select>
      <button type="button" class="btn-icon btn-sm" title="Move up" onclick="window.RosterModule._moveRigUnit(this,-1)"><span class="material-icons">arrow_upward</span></button>
      <button type="button" class="btn-icon btn-sm" title="Move down" onclick="window.RosterModule._moveRigUnit(this,1)"><span class="material-icons">arrow_downward</span></button>
      <button type="button" class="btn-icon btn-icon-danger btn-sm" title="Remove" onclick="window.RosterModule._removeRigUnit(this)"><span class="material-icons">close</span></button>
    </div>
    <div class="rig-unit-joint-hint form-hint" style="margin:0 0 6px 0;"></div>
    <div style="display:grid;grid-template-columns:repeat(3,1fr);gap:8px;">
      <label style="font-size:0.75rem;color:var(--text-secondary);">Gap as it runs (ft-in)
        ${gapIn('rig-unit-gap', p.gap, 'ft-in')}
      </label>
      <label style="font-size:0.75rem;color:var(--text-secondary);">Slides to min
        ${gapIn('rig-unit-gap-min', p.gapMin, 'optional')}
      </label>
      <label style="font-size:0.75rem;color:var(--text-secondary);">Slides to max
        ${gapIn('rig-unit-gap-max', p.gapMax, 'optional')}
      </label>
    </div>
  `;
  return div;
}

function _renumberRigUnits() {
  const container = document.getElementById('rig-units-container');
  if (!container) return;
  container.querySelectorAll('.rig-unit-row').forEach((row, idx) => {
    const label = row.querySelector('.rig-unit-label');
    if (label) label.textContent = idx === 0 ? 'Trailer 1' : `Unit ${idx + 1}`;
    const hint = row.querySelector('.rig-unit-joint-hint');
    if (hint) {
      hint.textContent = idx === 0
        ? 'Gap 1 (fifth wheel): last drive axle of the truck to the first axle of this trailer. If the fifth wheel or tandems slide, give the range too.'
        : `Gap ${idx + 1}: last axle of the unit in front to the first axle of this one.`;
    }
    const g = row.querySelector('.rig-unit-gap');
    if (g) g.placeholder = idx === 0 ? 'e.g. 40\' 7"' : 'ft-in';
  });
}

function addRigTrailer(preset) {
  const container = document.getElementById('rig-units-container');
  if (!container) return;
  if (container.querySelectorAll('.rig-unit-row').length >= RIG_MAX_TRAILERS) {
    window.showToast?.(`A rig tops out at ${RIG_MAX_TRAILERS} trailers here.`, 'warning');
    return;
  }
  container.appendChild(_renderRigUnitRow(preset));
  _renumberRigUnits();
  refreshRigAssembly();
}

function _removeRigUnit(btn) {
  const row = btn.closest('.rig-unit-row');
  if (row) row.remove();
  _renumberRigUnits();
  refreshRigAssembly();
}

function _moveRigUnit(btn, dir) {
  const row = btn.closest('.rig-unit-row');
  if (!row) return;
  const sibling = dir < 0 ? row.previousElementSibling : row.nextElementSibling;
  if (!sibling) return;
  if (dir < 0) row.parentNode.insertBefore(row, sibling);
  else row.parentNode.insertBefore(sibling, row);
  _renumberRigUnits();
  refreshRigAssembly();
}

// Read the trailer rows straight out of the DOM, in visual order (same pattern
// as getPermitAxles). Blank gaps stay null — never defaulted to a number.
function _readRigUnitsFromDom() {
  const container = document.getElementById('rig-units-container');
  if (!container) return [];
  const out = [];
  container.querySelectorAll('.rig-unit-row').forEach(row => {
    const trailerId = row.querySelector('.rig-unit-select')?.value || '';
    const u = { trailerId, trailer: trailerId ? _rigTrailerById(trailerId) : null, from: row.dataset.from || '', permitNo: row.dataset.pn || '' };
    // TASK-10139 S4 item 3 — the box takes 40' 7" / 40 7 / 40.58; the lazy chunk
    // parsed it into dataset.ft for exactly this text (dataset.raw). Not parsed
    // yet: plain decimal only. Anything unreadable marks the row bad (saveRig
    // refuses), never a silent blank.
    for (const [k, c] of [['gap', '.rig-unit-gap'], ['gapMin', '.rig-unit-gap-min'], ['gapMax', '.rig-unit-gap-max']]) {
      const i = row.querySelector(c);
      const v = (i?.value || '').trim();
      const n = i && i.dataset.raw === v ? (i.dataset.ft === 'bad' ? NaN : _rigNum(i.dataset.ft)) : (/^\d+(\.\d+)?$/.test(v) ? Number(v) : (v ? NaN : null));
      if (Number.isNaN(n)) u.bad = true;
      u[k] = Number.isNaN(n) ? null : n;
    }
    out.push(u);
  });
  return out;
}

// ---------------------------------------------------------------- preview

// TASK-10139 S4 — the preview (axle table + the three-check status) and the
// gap parsing / name / source UI are drawn by the LAZY rig chunk
// (rosterPermitGeometry.js rigEditorRefresh). Returns its promise so saveRig
// can wait for the gap boxes to be parsed.
function refreshRigAssembly() {
  _renumberRigUnits();
  return _pgeo('rigEditorRefresh');
}

// ---------------------------------------------------------------- modal open

// TASK-4876 S3 — THE way into the rig editor, from Roster or any other module
// (Permit Pete): window.RosterModule.openRigEditor(rigId?, { draft, onSaved }).
// Same modal, same _rigEditGate, same saveRig. Outside Roster its listeners are
// down, so the lazy chunk reads trucks / trailers / rigs once first (fresh on
// every open, never a second snapshot). onSaved(rigId) runs after a save.
// Resolves true when the editor opened.
let _rigOnSaved = null;
let _rigOpenSeq = 0;
async function openRigEditor(rigId, opts) {
  const o = opts || {};
  const seq = ++_rigOpenSeq; // COD Code Reviewer: a later open supersedes one still priming
  if (!RosterState.unsubscribers.length && !(await _pgeo('rigEditorPrime'))) { if (seq === _rigOpenSeq) window.showToast?.('Could not load your vehicles and trailers. Try again.', 'error'); return false; }
  if (seq !== _rigOpenSeq) return false;
  if (rigId && !RosterState.rigs.some(r => r.id === rigId)) { window.showToast?.('That rig is no longer saved.', 'error'); return false; }
  if (rigId) editRig(rigId); else showAddRig(o.draft);
  const open = document.getElementById('modal-rig')?.classList.contains('hidden') === false;
  if (open && typeof o.onSaved === 'function') _rigOnSaved = o.onSaved;
  return open;
}

// `draft` (TASK-10139 S4 item 1) = { truckId, trailerIds } from a "You ran
// 15 + 103 on 116 days" suggestion: the units are picked, the gap is not.
function showAddRig(draft) {
  _rigOnSaved = null; // TASK-4876 S3: a hook belongs to the open that set it
  if (!_rigCanEdit()) { window.showToast?.(RIG_VIEW_ONLY_MSG, 'error'); return; }
  const d = draft && typeof draft.truckId === 'string' && Array.isArray(draft.trailerIds) ? draft : null;
  ensureModalsMounted();
  document.getElementById('modal-rig-title').textContent = 'Add Rig';
  document.getElementById('form-rig').reset();
  document.getElementById('rig-id').value = '';
  document.getElementById('rig-kind').value = 'tractor-trailer';
  document.getElementById('rig-truck').innerHTML = _rigTruckOptionsHtml(d ? d.truckId : '');
  document.getElementById('rig-units-container').innerHTML = '';
  const del = document.getElementById('btn-delete-rig');
  if (del) del.style.display = 'none';
  const save = document.getElementById('btn-save-rig');
  if (save) save.style.display = '';
  (d ? d.trailerIds.slice(0, RIG_MAX_TRAILERS) : ['']).forEach(t => addRigTrailer({ trailerId: t }));
  _rigFormLock(false);
  refreshRigAssembly();
  _pgeo('rigSuggest', 'rig-suggest');
  document.getElementById('modal-rig').classList.remove('hidden');
}

function editRig(id) {
  _rigOnSaved = null; // TASK-4876 S3
  const rig = RosterState.rigs.find(r => r.id === id);
  if (!rig) return;
  ensureModalsMounted();
  document.getElementById('modal-rig-title').textContent = 'Edit Rig';
  document.getElementById('form-rig').reset();
  document.getElementById('rig-id').value = rig.id;
  document.getElementById('rig-name').value = rig.name || '';
  document.getElementById('rig-kind').value = rig.kind || 'tractor-trailer';
  document.getElementById('rig-notes').value = rig.notes || '';
  document.getElementById('rig-overall-length').value = (rig.dims && rig.dims.overallL) || '';
  document.getElementById('rig-overall-width').value = (rig.dims && rig.dims.overallW) || '';
  document.getElementById('rig-overall-height').value = (rig.dims && rig.dims.overallH) || '';
  document.getElementById('rig-truck').innerHTML = _rigTruckOptionsHtml(rig.truckId || '');
  const container = document.getElementById('rig-units-container');
  container.innerHTML = '';
  (rig.trailerIds || []).forEach((tid, i) => {
    const c = (rig.couplings || [])[i] || {};
    // A saved source counts only while it still describes this gap (another
    // writer, e.g. propose_rig, may have changed the gap since).
    const s = (Array.isArray(rig.gapSources) ? rig.gapSources : [])[i];
    container.appendChild(_renderRigUnitRow({
      trailerId: tid,
      gap: _rigNum(c.gap),
      gapMin: _rigNum(c.gapMin),
      gapMax: _rigNum(c.gapMax),
      src: s && typeof s === 'object' && _rigNum(s.gap) !== null && _rigNum(s.gap) === _rigRound(_rigNum(c.gap)) ? s : null
    }));
  });
  // TASK-10139 follow-up 3 — a login the rules refuse opens the rig to LOOK at
  // it: no Save, no Delete (see _rigEditGate).
  const canEdit = _rigCanEdit();
  if (!canEdit) document.getElementById('modal-rig-title').textContent = 'Rig (view only)';
  const del = document.getElementById('btn-delete-rig');
  if (del) del.style.display = canEdit ? '' : 'none';
  const save = document.getElementById('btn-save-rig');
  if (save) save.style.display = canEdit ? '' : 'none';
  _renumberRigUnits();
  _rigFormLock(!canEdit);
  refreshRigAssembly();
  document.getElementById('modal-rig').classList.remove('hidden');
}

// ---------------------------------------------------------------- save/delete

async function saveRig(e) {
  if (e && e.preventDefault) e.preventDefault();
  if (!_rigCanEdit()) { window.showToast?.(RIG_VIEW_ONLY_MSG, 'error'); return; }
  const hook = _rigOnSaved; // TASK-4876 S3: bound to THIS open before any await (COD)
  await refreshRigAssembly(); // the lazy chunk parses every gap box first (S4)
  const form = document.getElementById('form-rig');
  if (form && window.validateRequiredFields && !window.validateRequiredFields(form)) return;

  const id = document.getElementById('rig-id').value;
  const _clamp = window.clampText || ((s, n) => (s && s.length > n ? s.slice(0, n) : s || ''));
  const name = _clamp((document.getElementById('rig-name').value || '').trim(), 100);
  const truckId = document.getElementById('rig-truck').value || '';
  const units = _readRigUnitsFromDom();
  if (units.some(u => u.bad)) { window.showToast?.('A gap box has something we can\'t read. Type feet like 40.5, 40\' 6" or 40 6.', 'error'); return; }

  if (!name) { window.showToast?.('Give the rig a name.', 'error'); return; }
  if (!truckId) { window.showToast?.('Pick the power unit for this rig.', 'error'); return; }
  // COD Code Reviewer ⚠️ (round 2) — a "rig" with no trailer has no coupling gap,
  // which is the entire reason this collection exists, and it would save clean
  // (incomplete:false) and read as permit-ready off a single vehicle's own axles.
  // A lone vehicle is a vehicle record, not a combination.
  if (units.length === 0) {
    window.showToast?.('A rig needs at least one trailer or booster — the coupling gap is what makes it a rig.', 'error');
    return;
  }

  // Every unit on a rig is a distinct piece of iron. A repeated id is a mis-click,
  // and it would double-count axles on a filing.
  const seen = new Set();
  for (const u of units) {
    if (!u.trailerId) { window.showToast?.('Every trailer row needs a trailer selected (or remove the row).', 'error'); return; }
    if (seen.has(u.trailerId)) {
      window.showToast?.('The same trailer is on this rig twice — a booster is its own unit, add it separately.', 'error');
      return;
    }
    seen.add(u.trailerId);
  }

  // Gap sanity. Blank is allowed (and flagged as not permit-ready); nonsense is not.
  for (let i = 0; i < units.length; i++) {
    const u = units[i];
    const where = i === 0 ? 'fifth wheel gap' : `coupling gap on unit ${i + 2}`;
    for (const [val, what] of [[u.gap, where], [u.gapMin, `${where} minimum`], [u.gapMax, `${where} maximum`]]) {
      if (val !== null && (val < 0 || val > RIG_GAP_MAX_FT)) {
        window.showToast?.(`The ${what} must be between 0 and ${RIG_GAP_MAX_FT} ft.`, 'error');
        return;
      }
    }
    if (u.gapMin !== null && u.gapMax !== null && u.gapMin > u.gapMax) {
      window.showToast?.(`The ${where} minimum is bigger than the maximum.`, 'error');
      return;
    }
    if (u.gap !== null && u.gapMin !== null && u.gap < u.gapMin) {
      window.showToast?.(`The ${where} is below the minimum you entered.`, 'error');
      return;
    }
    if (u.gap !== null && u.gapMax !== null && u.gap > u.gapMax) {
      window.showToast?.(`The ${where} is above the maximum you entered.`, 'error');
      return;
    }
  }

  const truck = _rigTruckById(truckId);
  const asm = assembleRigSequence(truck, units);

  if (asm.incomplete) {
    // showConfirm sets textContent (no pre-line), so this stays prose, not bullets.
    const ok = window.showConfirm
      ? await window.showConfirm(
          'Save an incomplete rig?',
          `This rig's axle sequence isn't complete. ${asm.warnings.join(' ')} It will be saved and marked NOT permit-ready — don't file from it until the gaps are filled in.`,
          { confirmText: 'Save anyway', icon: 'warning' }
        )
      : confirm('This rig\'s axle sequence is incomplete and will be marked not permit-ready. Save anyway?');
    if (!ok) return;
  }

  // TASK-10139 S4 item 8 — where each gap came from, [{ gap, from, permitNo }].
  // Pete never reads it; it is for the person checking the number.
  const gapSources = await _pgeo('rigSources', units);
  return runGuardedSave('saveRig', async () => {
    const tenant = window.AppState?.tenant || 'demo';
    const uid = auth?.currentUser?.uid || null;
    const first = units[0] || {};

    const data = {
      name,
      kind: document.getElementById('rig-kind').value || 'tractor-trailer',
      truckId,
      trailerIds: units.map(u => u.trailerId),
      couplings: units.map((u, i) => ({
        fromType: i === 0 ? 'truck' : 'trailer',
        fromId: i === 0 ? truckId : units[i - 1].trailerId,
        toType: 'trailer',
        toId: u.trailerId,
        gap: _rigRound(u.gap),
        gapMin: _rigRound(u.gapMin),
        gapMax: _rigRound(u.gapMax)
      })),
      // Scalar mirrors of couplings[0] — the tractor↔first-trailer FIFTH WHEEL and
      // nothing else. couplings[] stays the source of truth for every other joint;
      // see the schema block at the top of this file before reading these.
      fifthWheelGap: _rigRound(first.gap === undefined ? null : first.gap),
      fifthWheelGapMin: _rigRound(first.gapMin === undefined ? null : first.gapMin),
      fifthWheelGapMax: _rigRound(first.gapMax === undefined ? null : first.gapMax),
      ...(Array.isArray(gapSources) ? { gapSources } : {}),
      // DERIVED — recomputed here from the source vehicles on every single save.
      permitAxles: asm.axles,
      assembly: {
        // COD Adversary ⚠️ — `permitAxles` here is CLIENT-derived. The rules gate
        // WHO may write this doc, not WHAT the numbers say, and no rule can
        // recompute an axle sequence. So the provenance is stamped in the DATA,
        // not just in a comment: a server consumer that sees derivedBy:'client'
        // MUST re-derive from the validated truckId/trailerIds before anything
        // reaches a filing, exactly like the existing permitAxles re-fetch rule.
        // 🔴 NEVER BRANCH TRUST ON THIS FIELD (COD Adversary, round 2). It is
        // client-writable. rules pin it to the literal 'client' so it can't be
        // forged to 'server', but the only safe consumer ALWAYS re-derives —
        // "if derivedBy != 'client', skip recomputation" is the exact bug.
        derivedBy: 'client',
        fingerprint: _rigFingerprint(asm.axles),
        truckId,
        trailerIds: units.map(u => u.trailerId),
        axleCount: asm.axleCount,
        extremeSpacing: asm.extremeSpacing,
        extremeSpacingMin: asm.extremeSpacingMin,
        extremeSpacingMax: asm.extremeSpacingMax,
        computedAt: new Date()
      },
      incomplete: asm.incomplete,
      warnings: asm.warnings,
      dims: {
        overallL: _rigNum(document.getElementById('rig-overall-length').value),
        overallW: _rigNum(document.getElementById('rig-overall-width').value),
        overallH: _rigNum(document.getElementById('rig-overall-height').value)
      },
      notes: _clamp((document.getElementById('rig-notes').value || '').trim(), 1000),
      updatedAt: new Date(),
      updatedBy: uid
    };

    let savedId = id;
    if (id) {
      await withWriteTimeout(updateDoc(doc(db, 'tenants', tenant, 'equipmentTemplates', id), data), 15000, 'update rig');
      window.showToast?.('Rig updated', 'success');
    } else {
      data.createdAt = new Date();
      data.createdBy = uid;
      savedId = (await withWriteTimeout(addDoc(collection(db, 'tenants', tenant, 'equipmentTemplates'), data), 15000, 'create rig'))?.id || null;
      window.showToast?.('Rig saved', 'success');
    }

    try {
      window.logAudit?.('roster', id ? 'RIG_UPDATE' : 'RIG_CREATE', {
        entityType: 'equipmentTemplate',
        entityId: id || null,
        source: 'roster-save-rig',
        unitId: name,
        redFlags: {
          truckId,
          trailerIds: data.trailerIds,
          fifthWheelGap: data.fifthWheelGap,
          axleCount: asm.axleCount,
          incomplete: asm.incomplete
        }
      });
    } catch (_) { /* audit is best-effort, never blocks the save */ }

    if (_rigOnSaved === hook) _rigOnSaved = null; // a newer open keeps its own hook
    closeModal('rig');
    renderRigsList();
    if (hook && savedId) try { hook(savedId); } catch (err) { console.warn('[Roster] rig onSaved:', err?.message || err); }
  }, { buttonId: 'btn-save-rig', restoreLabel: 'Save' });
}

async function deleteRig() {
  if (!_rigCanEdit()) { window.showToast?.(RIG_VIEW_ONLY_MSG, 'error'); return; }
  const id = document.getElementById('rig-id')?.value;
  if (!id) return;
  const rig = RosterState.rigs.find(r => r.id === id);
  if (!rig) return;
  const ok = window.showConfirm
    ? await window.showConfirm(
        'Delete this rig?',
        `"${rig.name || 'Untitled rig'}" will be removed. The vehicles and trailers on it are untouched, and any permit request already built from it keeps its own copy of the axle sequence.`,
        { confirmText: 'Delete', icon: 'delete_forever', iconColor: '#DC2626' }
      )
    : confirm('Delete this rig?');
  if (!ok) return;
  try {
    const tenant = window.AppState?.tenant || 'demo';
    await withWriteTimeout(deleteDoc(doc(db, 'tenants', tenant, 'equipmentTemplates', id)), 15000, 'delete rig');
    try {
      window.logAudit?.('roster', 'RIG_DELETE', {
        entityType: 'equipmentTemplate', entityId: id, source: 'roster-delete-rig',
        unitId: rig.name || null, redFlags: { truckId: rig.truckId || null, trailerIds: rig.trailerIds || [] }
      });
    } catch (_) {}
    window.showToast?.('Rig deleted', 'success');
    closeModal('rig');
    renderRigsList();
  } catch (err) {
    console.error('[Roster] Error deleting rig:', err);
    window.showToast?.('Error deleting rig', 'error');
  }
}

// ------------------------------------------------- TASK-9993 permit measurements
// "Permit measurements" (vehicle.permitGeometry, what Permit Pete's axle placement
// reads) is the LAZY views/rosterPermitGeometry.js (cold-load budget): loaded only
// when a Heavy Haul section is open or the Rigs tab draws. The callable it uses
// (savePermitGeometry) re-checks Roster OR Compliance server-side.
let _pgeoP = null;
function _pgeo(fn, ...args) {
  if (!_pgeoP) _pgeoP = __ddChunkWrap(import('./rosterPermitGeometry.js?v=10304876')).catch((e) => { _pgeoP = null; console.warn('[Roster] rosterPermitGeometry:', e?.message || e); return null; });
  return _pgeoP.then((m) => m && m[fn]({ RosterState, escapeHtml, callSave: (p) => httpsCallable(functions, 'savePermitGeometry')(p), truckById: _rigTruckById, trailerById: _rigTrailerById,
    // TASK-10139 S4 rig editor: roster.js keeps the data path, the chunk draws.
    asm: assembleRigSequence, asmDoc: _rigAssemblyFromDoc, rows: _readRigUnitsFromDom, label: getUnitDisplay, pr: _permitReadiness, draft: showAddRig,
    // Board pairs (item 1): the last 12 months of job days, read once per session.
    read: (c) => getDocs(collection(db, 'tenants', window.AppState?.tenant || 'demo', c)).then(s => s.docs.map(x => ({ id: x.id, ...x.data() }))), // S3 prime
    days: () => getDocs(query(collection(db, 'tenants', window.AppState?.tenant || 'demo', 'jobDays'), where('date', '>=', Timestamp.fromMillis(Date.now() - 365 * 864e5)))).then(s => s.docs.map(x => x.data())) }, ...args));
}
function _pgeoSync(kind, unitId) {
  const el = document.getElementById(`${kind}-permit-geometry`);
  const sec = document.getElementById(`${kind}-heavyhaul-section`);
  if (!el || !sec) return;
  el.innerHTML = '';
  el.dataset.unitId = unitId || '';
  if (!sec.dataset.pgeo) { sec.dataset.pgeo = '1'; sec.addEventListener('toggle', () => sec.open && _pgeo('rosterDraw', kind)); }
  if (sec.open) _pgeo('rosterDraw', kind);
}

// ------------------------------------------------- read-only unit membership

// Read-only "which rigs is this unit on" block for the truck / trailer modals.
// Renders only — it never writes to the rig or to the vehicle.
function _renderUnitRigsSummary(kind, unitId) {
  const group = document.getElementById(`${kind}-rigs-summary-group`);
  const host = document.getElementById(`${kind}-rigs-summary`);
  if (!group || !host) return;
  if (!unitId) { group.style.display = 'none'; host.innerHTML = ''; return; }

  const mine = RosterState.rigs.filter(r =>
    kind === 'truck' ? r.truckId === unitId : (r.trailerIds || []).includes(unitId)
  );
  if (mine.length === 0) { group.style.display = 'none'; host.innerHTML = ''; return; }

  host.innerHTML = mine.map(rig => {
    const truck = _rigTruckById(rig.truckId);
    const parts = [truck ? getUnitDisplay(truck) : 'Missing vehicle']
      .concat((rig.trailerIds || []).map(tid => {
        const tr = _rigTrailerById(tid);
        return tr ? getUnitDisplay(tr) : 'Missing trailer';
      }));
    let role = 'Power unit';
    if (kind === 'trailer') {
      const pos = (rig.trailerIds || []).indexOf(unitId);
      role = pos === 0 ? 'Lead trailer' : `Unit ${pos + 2} (behind ${escapeHtml(parts[pos] || '')})`;
    }
    const gap = _rigStoredNum(rig.fifthWheelGap);  // fifth wheel only — see couplings[] for the rest
    const axleCount = _rigStoredCount((rig.assembly && rig.assembly.axleCount) || (rig.permitAxles || []).length);
    return `
      <div style="border:1px solid var(--border-light);border-radius:var(--radius-sm);padding:6px 8px;margin-bottom:6px;">
        <div style="font-weight:600;font-size:0.8rem;color:var(--text-primary);">
          ${escapeHtml(rig.name || 'Untitled rig')}
          ${rig.incomplete ? '<span style="color:var(--warning-color, #D97706);font-size:0.7rem;font-weight:700;margin-left:6px;">NOT PERMIT-READY</span>' : ''}
        </div>
        <div style="font-size:0.72rem;color:var(--text-secondary);">
          ${role} · ${escapeHtml(parts.join(' + '))} · ${axleCount} axle${axleCount === 1 ? '' : 's'}
          ${gap === null ? '' : ` · ${gap}′ fifth wheel`}
        </div>
      </div>`;
  }).join('') + `<p class="form-hint" style="margin-top:0;">Read-only. Coupling gaps belong to the combination — edit them on the Rigs tab, never here.</p>`;
  group.style.display = '';
}

// ============================================
// VIN MANAGEMENT - TRUCKS
// ============================================
let truckVinCount = 0;

function loadTruckVins(vins) {
  const container = document.getElementById('truck-vins-container');
  if (!container) return;
  container.innerHTML = '';
  truckVinCount = 0;
  if (vins && vins.length > 0) {
    vins.forEach(vin => addTruckVin(vin));
  } else {
    addTruckVin();
  }
}

function addTruckVin(value = '') {
  const container = document.getElementById('truck-vins-container');
  if (!container) return;
  truckVinCount++;
  const vinNum = truckVinCount;
  const div = document.createElement('div');
  div.className = 'axle-input-row';
  div.id = `truck-vin-row-${vinNum}`;
  div.innerHTML = `
    <span class="axle-label">VIN ${vinNum}</span>
    <input type="text" class="truck-vin-input" placeholder="1HGBH41JXMN109186" value="${escapeHtml(value)}">
    <button type="button" class="btn-icon btn-icon-danger btn-sm" onclick="window.RosterModule.removeTruckVin(${vinNum})" title="Remove">
      <span class="material-icons">close</span>
    </button>
  `;
  container.appendChild(div);
}

function removeTruckVin(num) {
  const row = document.getElementById(`truck-vin-row-${num}`);
  if (row) row.remove();
  renumberTruckVins();
}

function renumberTruckVins() {
  const container = document.getElementById('truck-vins-container');
  if (!container) return;
  const rows = container.querySelectorAll('.axle-input-row');
  rows.forEach((row, idx) => {
    const label = row.querySelector('.axle-label');
    if (label) label.textContent = `VIN ${idx + 1}`;
  });
}

function getTruckVins() {
  const inputs = document.querySelectorAll('.truck-vin-input');
  const vins = [];
  inputs.forEach(input => {
    const val = input.value.trim();
    if (val) vins.push(val);
  });
  return vins;
}

// ============================================
// VIN MANAGEMENT - TRAILERS
// ============================================
let trailerVinCount = 0;

function loadTrailerVins(vins) {
  const container = document.getElementById('trailer-vins-container');
  if (!container) return;
  container.innerHTML = '';
  trailerVinCount = 0;
  if (vins && vins.length > 0) {
    vins.forEach(vin => addTrailerVin(vin));
  } else {
    addTrailerVin();
  }
}

function addTrailerVin(value = '') {
  const container = document.getElementById('trailer-vins-container');
  if (!container) return;
  trailerVinCount++;
  const vinNum = trailerVinCount;
  const div = document.createElement('div');
  div.className = 'axle-input-row';
  div.id = `trailer-vin-row-${vinNum}`;
  div.innerHTML = `
    <span class="axle-label">VIN ${vinNum}</span>
    <input type="text" class="trailer-vin-input" placeholder="1JJV532D5KL456789" value="${escapeHtml(value)}">
    <button type="button" class="btn-icon btn-icon-danger btn-sm" onclick="window.RosterModule.removeTrailerVin(${vinNum})" title="Remove">
      <span class="material-icons">close</span>
    </button>
  `;
  container.appendChild(div);
}

function removeTrailerVin(num) {
  const row = document.getElementById(`trailer-vin-row-${num}`);
  if (row) row.remove();
  renumberTrailerVins();
}

function renumberTrailerVins() {
  const container = document.getElementById('trailer-vins-container');
  if (!container) return;
  const rows = container.querySelectorAll('.axle-input-row');
  rows.forEach((row, idx) => {
    const label = row.querySelector('.axle-label');
    if (label) label.textContent = `VIN ${idx + 1}`;
  });
}

function getTrailerVins() {
  const inputs = document.querySelectorAll('.trailer-vin-input');
  const vins = [];
  inputs.forEach(input => {
    const val = input.value.trim();
    if (val) vins.push(val);
  });
  return vins;
}

// ============================================
// LICENSE PLATE MANAGEMENT - TRUCKS
// ============================================
let truckPlateCount = 0;

function loadTruckPlates(plates, legacyPlate, legacyState) {
  const container = document.getElementById('truck-plates-container');
  if (!container) return;
  container.innerHTML = '';
  truckPlateCount = 0;
  if (plates && plates.length > 0) {
    plates.forEach(p => addTruckPlate(p.plate || '', p.state || ''));
  } else if (legacyPlate) {
    addTruckPlate(legacyPlate, legacyState || '');
  } else {
    addTruckPlate();
  }
  updatePlateButtonVisibility('truck');
}

function addTruckPlate(plate = '', state = '') {
  const container = document.getElementById('truck-plates-container');
  if (!container) return;
  truckPlateCount++;
  const num = truckPlateCount;
  const div = document.createElement('div');
  div.className = 'axle-input-row';
  div.id = `truck-plate-row-${num}`;
  div.innerHTML = `
    <input type="text" class="truck-plate-input" placeholder="ABC-1234" value="${escapeHtml(plate)}" style="flex:1;">
    <input type="text" class="truck-plate-state" placeholder="ST" value="${escapeHtml(state)}" maxlength="2" style="width:50px;text-align:center;text-transform:uppercase;">
    <button type="button" class="btn-icon btn-icon-danger btn-sm" onclick="window.RosterModule.removeTruckPlate(${num})" title="Remove">
      <span class="material-icons">close</span>
    </button>
  `;
  container.appendChild(div);
  updatePlateButtonVisibility('truck');
}

function removeTruckPlate(num) {
  const row = document.getElementById(`truck-plate-row-${num}`);
  if (row) row.remove();
  updatePlateButtonVisibility('truck');
}

function getTruckPlates() {
  const container = document.getElementById('truck-plates-container');
  if (!container) return [];
  const rows = container.querySelectorAll('.axle-input-row');
  const plates = [];
  rows.forEach(row => {
    const plate = row.querySelector('.truck-plate-input')?.value.trim();
    const state = row.querySelector('.truck-plate-state')?.value.trim().toUpperCase();
    if (plate) plates.push({ plate, state: state || '' });
  });
  return plates;
}

// ============================================
// LICENSE PLATE MANAGEMENT - TRAILERS
// ============================================
let trailerPlateCount = 0;

function loadTrailerPlates(plates, legacyPlate, legacyState) {
  const container = document.getElementById('trailer-plates-container');
  if (!container) return;
  container.innerHTML = '';
  trailerPlateCount = 0;
  if (plates && plates.length > 0) {
    plates.forEach(p => addTrailerPlate(p.plate || '', p.state || ''));
  } else if (legacyPlate) {
    addTrailerPlate(legacyPlate, legacyState || '');
  } else {
    addTrailerPlate();
  }
  updatePlateButtonVisibility('trailer');
}

function addTrailerPlate(plate = '', state = '') {
  const container = document.getElementById('trailer-plates-container');
  if (!container) return;
  trailerPlateCount++;
  const num = trailerPlateCount;
  const div = document.createElement('div');
  div.className = 'axle-input-row';
  div.id = `trailer-plate-row-${num}`;
  div.innerHTML = `
    <input type="text" class="trailer-plate-input" placeholder="TRL-1234" value="${escapeHtml(plate)}" style="flex:1;">
    <input type="text" class="trailer-plate-state" placeholder="ST" value="${escapeHtml(state)}" maxlength="2" style="width:50px;text-align:center;text-transform:uppercase;">
    <button type="button" class="btn-icon btn-icon-danger btn-sm" onclick="window.RosterModule.removeTrailerPlate(${num})" title="Remove">
      <span class="material-icons">close</span>
    </button>
  `;
  container.appendChild(div);
  updatePlateButtonVisibility('trailer');
}

function removeTrailerPlate(num) {
  const row = document.getElementById(`trailer-plate-row-${num}`);
  if (row) row.remove();
  updatePlateButtonVisibility('trailer');
}

function getTrailerPlates() {
  const container = document.getElementById('trailer-plates-container');
  if (!container) return [];
  const rows = container.querySelectorAll('.axle-input-row');
  const plates = [];
  rows.forEach(row => {
    const plate = row.querySelector('.trailer-plate-input')?.value.trim();
    const state = row.querySelector('.trailer-plate-state')?.value.trim().toUpperCase();
    if (plate) plates.push({ plate, state: state || '' });
  });
  return plates;
}

// Hide "Add Another" buttons when there's only 1 empty row
function updatePlateButtonVisibility(kind) {
  const btn = document.getElementById(`btn-add-${kind}-plate`);
  if (btn) {
    const container = document.getElementById(`${kind}-plates-container`);
    const rows = container?.querySelectorAll('.axle-input-row') || [];
    btn.style.display = rows.length >= 1 ? '' : 'none';
  }
}

// ============================================
// VEHICLE INVENTORY MANAGEMENT
// ============================================
const INVENTORY_CATEGORIES = [
  'Hand Tools',
  'Hoses & Connections',
  'Cam Fittings',
  'Cam Fitting Seals',
  'Spray Equipment',
  'Consumables & Maintenance',
  'Jobsite Tools',
  'Safety / Misc',
  'Other'
];

const INVENTORY_UNITS = [
  { value: 'ea', label: 'each' },
  { value: 'set', label: 'set' },
  { value: 'kit', label: 'kit' },
  { value: 'pack', label: 'pack' },
  { value: 'jug', label: 'jug' },
  { value: 'can', label: 'can' },
  { value: 'roll', label: 'roll' }
];

let inventoryItemCounter = 0;

function toggleInventorySection() {
  const content = document.getElementById('truck-inventory-content');
  const chevron = document.querySelector('#truck-inventory-section .section-chevron');
  if (content && chevron) {
    content.classList.toggle('collapsed');
    chevron.textContent = content.classList.contains('collapsed') ? 'expand_more' : 'expand_less';
  }
}

function showInventorySection() {
  const section = document.getElementById('truck-inventory-section');
  if (section && window.isVehicleInventoryEnabled?.()) {
    section.classList.remove('hidden');
  }
}

function hideInventorySection() {
  const section = document.getElementById('truck-inventory-section');
  if (section) {
    section.classList.add('hidden');
  }
}

function loadTruckInventory(items, inventoryEnabled = false) {
  const container = document.getElementById('truck-inventory-items');
  const enabledCheckbox = document.getElementById('truck-inventory-enabled');

  if (!container) return;

  // Set enabled state
  if (enabledCheckbox) {
    enabledCheckbox.checked = inventoryEnabled;
  }

  // Clear existing items
  container.innerHTML = '';
  inventoryItemCounter = 0;

  // Add each item
  if (items && items.length > 0) {
    items.forEach(item => addTruckInventoryItem(item));
  }

  // Show section if feature is enabled
  showInventorySection();
}

function addTruckInventoryItem(item = null) {
  const container = document.getElementById('truck-inventory-items');
  if (!container) return;

  inventoryItemCounter++;
  const idx = inventoryItemCounter;
  const itemId = item?.id || `inv-${Date.now()}-${idx}`;

  const categoryOptions = INVENTORY_CATEGORIES.map(cat =>
    `<option value="${cat}" ${item?.category === cat ? 'selected' : ''}>${cat}</option>`
  ).join('');

  const unitOptions = INVENTORY_UNITS.map(u =>
    `<option value="${u.value}" ${item?.unit === u.value ? 'selected' : ''}>${u.label}</option>`
  ).join('');

  const row = document.createElement('div');
  row.className = 'inventory-item-row';
  row.id = `inventory-item-${idx}`;
  row.dataset.itemId = itemId;
  row.innerHTML = `
    <div class="inventory-item-fields">
      <div class="form-group inventory-name-field">
        <input type="text" class="inv-name" placeholder="Item name (e.g., Post Pounder)" value="${escapeHtml(item?.name || '')}">
      </div>
      <div class="form-group inventory-category-field">
        <select class="inv-category">
          <option value="">Category...</option>
          ${categoryOptions}
        </select>
      </div>
      <div class="form-group inventory-qty-field">
        <input type="number" class="inv-expected-qty" placeholder="Qty" min="0" step="1" value="${item?.expectedQty || ''}">
      </div>
      <div class="form-group inventory-unit-field">
        <select class="inv-unit">
          ${unitOptions}
        </select>
      </div>
      <button type="button" class="btn-icon btn-icon-danger btn-sm" onclick="window.RosterModule.removeTruckInventoryItem(${idx})" title="Remove">
        <span class="material-icons">close</span>
      </button>
    </div>
  `;
  container.appendChild(row);
}

function removeTruckInventoryItem(idx) {
  const row = document.getElementById(`inventory-item-${idx}`);
  if (row) row.remove();
}

function getTruckInventory() {
  const container = document.getElementById('truck-inventory-items');
  if (!container) return [];

  const rows = container.querySelectorAll('.inventory-item-row');
  const items = [];

  rows.forEach(row => {
    const name = row.querySelector('.inv-name')?.value?.trim();
    const category = row.querySelector('.inv-category')?.value || '';
    const expectedQty = parseInt(row.querySelector('.inv-expected-qty')?.value) || 0;
    const unit = row.querySelector('.inv-unit')?.value || 'ea';

    // Only add items that have a name
    if (name) {
      items.push({
        id: row.dataset.itemId,
        name,
        category,
        expectedQty,
        unit
      });
    }
  });

  return items;
}

// ============================================
// UTILITIES
// ============================================
// TASK-862 — delegates to shared window.escapeHtml (sharedFormat.js). Escapes the
// identical set (&<>"', '->&#39;) for every string/falsy input; the shared helper
// also String()-coerces a non-string truthy input (former `str.replace` threw) —
// strictly safer, never a rendering regression. Hoisted wrapper, lookup at call time.
function escapeHtml(str) {
  return window.escapeHtml(str);
}

// HOTFIX (stored XSS) — onclick="fn('${…}')" is a JS string inside an attribute;
// escapeHtml's &#39; is decoded back to ' before the handler compiles, so a
// staff email like o'brien@x.com broke out. Delegates to sharedFormat.js
// escJsAttr (TASK-4838), looked up at call time (Pitfall #1).
function escJsAttr(str) {
  return window.escJsAttr(str);
}

// TASK-1460 — delegating wrapper for the shared person-name capitalizer
// (sharedFormat.js). window.* at CALL time, never a cross-?v= import
// (Pitfall #1). Falls back to the raw value if the helper is somehow
// absent, so a missing helper can never blank out a name on save.
function capPersonName(str) {
  return window.capitalizePersonName ? window.capitalizePersonName(str) : str;
}

function capitalizeFirst(str) {
  if (!str) return '';
  return str.charAt(0).toUpperCase() + str.slice(1);
}

// Format role badges for display (supports both legacy 'role' and new 'roles' array)
function formatRoleBadges(person) {
  const role = person.crewType || person.role || (person.roles && person.roles[0]) || '';
  const label = window.getTypeLabel?.(role, 'crewTypes') || capitalizeFirst(role) || 'Driver';
  return `<span class="badge badge-${escapeHtml(role)}">${escapeHtml(label)}</span>`;
}

function getInitials(name) {
  if (!name) return '?';
  return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
}

function getRandomColor() {
  const colors = ['#2563EB', '#059669', '#D97706', '#DC2626', '#7C3AED', '#0891B2'];
  return colors[Math.floor(Math.random() * colors.length)];
}

// ============================================
// DVIR SIGNATURE REQUEST
// ============================================

// State for signature request modal
let signatureRequestState = {
  personId: null,
  personName: null
};

async function showSignatureRequestModal() {
  const personId = document.getElementById('person-id').value;
  if (!personId) {
    window.showToast?.('Save the person first before requesting a signature', 'error');
    return;
  }

  const person = RosterState.people.find(p => p.id === personId);
  if (!person) {
    window.showToast?.('Person not found', 'error');
    return;
  }

  // Show loading state on button
  const btn = document.getElementById('btn-request-signature');
  if (btn) {
    btn.disabled = true;
    btn.innerHTML = '<span class="material-icons rotating">sync</span> Generating...';
  }

  try {
    const tenant = window.AppState?.tenant || 'demo';
    // TASK-1441 Phase 0a — the token is now minted SERVER-SIDE and stored in the
    // `peopleSecure` sibling (allow read, write: if false). It used to be minted
    // here and written straight onto the person doc — but `people` is broadcast
    // to every driver-PIN session, so a 24h bearer credential that authorizes
    // signing AS this person was readable by every coworker on a yard tablet.
    //
    // The callable applies the SAME gate this write had (requireRosterWriteAccess
    // mirrors the people-update rule exactly), so no role loses the button.
    // A denial now lands as a permission-denied HttpsError and is surfaced in the
    // catch below as a real message instead of a generic failure.
    //
    // TASK-1421 still applies server-side: the mint is crypto.randomUUID with no
    // Math.random fallback.
    const requestFn = httpsCallable(functions, 'requestPersonSignature');
    const { data: signatureResult } = await requestFn({ tenantId: tenant, personId });
    const token = signatureResult?.token;
    if (!token) throw new Error('Server did not return a signature token');

    // Build the signature URL
    const baseUrl = window.location.origin;
    const signUrl = `${baseUrl}/sign.html?token=${token}&tenant=${tenant}&person=${personId}`;

    // Store state for modal actions
    signatureRequestState = {
      personId,
      personName: person.name
    };

    // Show the modal
    showSignatureModal(person, signUrl);

  } catch (error) {
    console.error('[Roster] Error generating signature link:', error);
    // TASK-1441 Phase 0a — surface the SERVER's message. The mint moved into the
    // requestPersonSignature callable, so the realistic failures now are
    // permission-denied ("Roster access required to request a signature" for a
    // viewUploadOnly roster member) and not-found. Swallowing those into a flat
    // "Error generating signature link" is precisely the un-diagnosable toast the
    // migration plan called out; a denial must say WHY.
    const msg = error?.message && !/^internal$/i.test(error.message)
      ? `Error generating signature link: ${error.message}`
      : 'Error generating signature link';
    window.showToast?.(msg, 'error');
  } finally {
    // Restore button
    if (btn) {
      btn.disabled = false;
      btn.innerHTML = '<span class="material-icons">draw</span> Request Signature';
    }
  }
}

function showSignatureModal(person, signUrl) {
  // Create modal if it doesn't exist
  let modal = document.getElementById('modal-signature-request');
  if (!modal) {
    modal = document.createElement('div');
    modal.id = 'modal-signature-request';
    modal.className = 'modal hidden';
    document.body.appendChild(modal);
  }

  modal.innerHTML = `
    <div class="modal-content" style="max-width:420px;">
      <div class="modal-header">
        <h3><span class="material-icons">draw</span> Request Signature</h3>
        <button class="btn-icon" onclick="window.RosterModule.closeSignatureModal()">
          <span class="material-icons">close</span>
        </button>
      </div>
      <div class="modal-body" style="padding:var(--spacing-lg);">
        <p style="margin-bottom:var(--spacing-md);color:var(--text-secondary);">
          Send this link to <strong>${escapeHtml(person.name)}</strong> so they can provide their electronic signature for DVIR reports.
        </p>

        <div style="background:var(--bg-elevated);border-radius:var(--radius-md);padding:var(--spacing-md);margin-bottom:var(--spacing-md);">
          <label style="font-size:0.75rem;color:var(--text-muted);text-transform:uppercase;letter-spacing:0.5px;">Signature Link</label>
          <div style="display:flex;gap:8px;margin-top:4px;">
            <input type="text" id="signature-link-input" class="form-control" value="${escapeHtml(signUrl)}" readonly style="font-size:0.85rem;flex:1;">
            <button type="button" class="btn btn-secondary" onclick="window.RosterModule.copySignatureLink()" style="flex-shrink:0;">
              <span class="material-icons">content_copy</span>
            </button>
          </div>
          <p style="font-size:0.75rem;color:var(--text-muted);margin-top:4px;">Link expires in 24 hours</p>
        </div>

        <p style="font-size:0.85rem;color:var(--text-muted);margin:0;">
          <span class="material-icons" style="font-size:16px;vertical-align:middle;">info</span>
          Share via text message or email
        </p>
      </div>
      <div class="modal-footer">
        <button type="button" class="btn btn-secondary" onclick="window.RosterModule.closeSignatureModal()">Done</button>
      </div>
    </div>
  `;

  modal.classList.remove('hidden');

  // Close on backdrop click
  modal.onclick = (e) => {
    if (e.target === modal) closeSignatureModal();
  };
}

function closeSignatureModal() {
  const modal = document.getElementById('modal-signature-request');
  if (modal) modal.classList.add('hidden');
}

function copySignatureLink() {
  const input = document.getElementById('signature-link-input');
  if (input) {
    input.select();
    navigator.clipboard.writeText(input.value).then(() => {
      window.showToast?.('Link copied to clipboard!', 'success');
    }).catch(() => {
      // Fallback for older browsers
      document.execCommand('copy');
      window.showToast?.('Link copied!', 'success');
    });
  }
}

// Keep old function name for backward compatibility
async function requestSignature() {
  return showSignatureRequestModal();
}

// ============================================
// PIN SETUP REQUEST
// ============================================
// Calculate Driver Code from name + phone (phone optional)
// With phone: First 2 letters of first name + last 4 digits of phone (e.g., CH6711)
// Without phone: First 2 of first name + first 2 of last name (e.g., CHKJ)
// Single name only: First 4 letters (e.g., CHRI) — fallback
function calculateDriverPin(name, phone) {
  if (!name) return null;

  const parts = name.trim().split(/\s+/);
  const firstName = parts[0] || '';
  const lastName = parts.length > 1 ? parts[parts.length - 1] : '';

  // With phone: 2-letter + 4-digit format
  if (phone) {
    const letters = firstName.substring(0, 2).toUpperCase();
    if (letters.length < 2) return null;
    const digits = phone.replace(/\D/g, '');
    if (digits.length < 4) return null;
    return letters + digits.slice(-4);
  }

  // No phone, has last name: first 2 of first + first 2 of last
  if (lastName) {
    const fl = firstName.substring(0, 2).toUpperCase();
    const ll = lastName.substring(0, 2).toUpperCase();
    if (fl.length >= 2 && ll.length >= 1) return fl + ll;
  }

  // Single name fallback: first 4 letters
  const letters = firstName.substring(0, 4).toUpperCase();
  if (letters.length < 2) return null;
  return letters;
}

// ── TASK-4874 follow-up — codes the driver-PIN login can no longer FIND ─────
// Since TASK-4874, validateDriverPin's code-only (no QR / no tenant) login
// never scans every roster on a miss. It trims+uppercases what was typed
// (app.js), refuses anything outside 3–8 chars, refuses anything that is not
// A–Z/0–9 without looking it up, and finds a person ONLY through
// _indexedGlobalPinLookup: an exact `driverPin`/`clockPin` == UPPERCASE query,
// or a `name` range (name < ':' or the case forms of the code's first two
// characters, _computedPinNamePrefixes) whose hits are re-checked
// case-insensitively by matchesDriverPin. A code outside all of those is
// "PIN not found" even though it is on file. The QR / company-link login still
// scans one roster case-insensitively, so only the length arm is unusable.
// Mirrors functions/src/index.js on this base; the parity test
// tests/task4874-roster-driver-code-findable.unit.cjs runs this against the
// server's own sliced functions.
// Returns null (findable) or { unusable, reason } —
//   reason: 'too_short' | 'too_long' | 'symbol' | 'case' | 'name_prefix'.
// `opts.stored` = the code is a STORED driverPin/clockPin (exact-equality
// query applies), else it is name-derived (only the name range can find it).
function driverCodeLoginProblem(code, name, opts = {}) {
  if (code === null || code === undefined || code === '') return null;
  const raw = String(code);
  const typed = raw.trim().toUpperCase();
  if (typed.length < 3) return { unusable: true, reason: 'too_short' };
  if (typed.length > 8) return { unusable: true, reason: 'too_long' };
  if (!/^[A-Z0-9]+$/.test(typed)) return { unusable: false, reason: 'symbol' };
  if (opts.stored && raw === typed) return null;
  if (_nameInDriverPinLookupRange(name, typed)) return null;
  return { unusable: false, reason: opts.stored ? 'case' : 'name_prefix' };
}

// Would _indexedGlobalPinLookup's `name` range queries capture this person for
// `pin` (already trimmed + uppercased)? Same bounds as the server: name < ':'
// (digit / whitespace / symbol-leading raw names), plus the half-open
// [prefix, prefix-with-last-char+1) range per case form of the first two chars.
// A person with no string name is in no range (Firestore skips the field).
function _nameInDriverPinLookupRange(name, pin) {
  if (typeof name !== 'string') return false;
  if (name < ':') return true;
  const caseForms = (ch) => (/[A-Z]/.test(ch) ? [ch, ch.toLowerCase()] : [ch]);
  for (const a of caseForms(pin[0])) {
    for (const b of caseForms(pin[1])) {
      if (a < ':') continue;
      const prefix = a + b;
      const end = prefix.slice(0, -1) + String.fromCharCode(prefix.charCodeAt(prefix.length - 1) + 1);
      if (name >= prefix && name < end) return true;
    }
  }
  return false;
}

// The code resolveDriverPinCode would mint from a name + phone — a copy of the
// server's _deriveComputedPin (NOT calculateDriverPin above, which differs for
// short phones and 1-letter first names).
function _serverDerivedDriverCode(name, phone) {
  const n = String(name || '').trim();
  const parts = n.split(/\s+/);
  const firstName = parts[0] || '';
  const lastName = parts.length > 1 ? parts[parts.length - 1] : '';
  const digits = String(phone || '').replace(/\D/g, '');
  if (digits && digits.length >= 4) {
    const expected = firstName.substring(0, 2).toUpperCase() + digits.slice(-4);
    return expected.length === 6 ? expected : null;
  }
  if (!digits && lastName) {
    const expected = firstName.substring(0, 2).toUpperCase() + lastName.substring(0, 2).toUpperCase();
    return expected.length >= 3 ? expected : null;
  }
  if (!digits && !lastName && firstName.length >= 4) return firstName.substring(0, 4).toUpperCase();
  return null;
}

// One sentence per problem + the fix. Never repeats the code itself (the panel
// already shows it; a toast must not put a login code on screen twice).
function _driverCodeProblemText(problem, { regenerated = false } = {}) {
  if (!problem) return '';
  const what = regenerated ? 'A code made from this name' : 'This driver code';
  switch (problem.reason) {
    case 'too_short': return `${what} is under 3 characters, so it can't be typed at sign-in. Add a phone number and regenerate, or use a setup code.`;
    case 'too_long': return `${what} is over 8 characters, so it can't be typed at sign-in. Use a setup code.`;
    case 'symbol': return `${what} has a letter or symbol other than A–Z / 0–9 (an accent, apostrophe or space), so code-only sign-in can't find it. Use a setup code so the driver picks their own PIN.`;
    case 'case': return `${what} is stored with lowercase letters or spaces, so code-only sign-in can't find it. Add a phone number and regenerate, or use a setup code.`;
    case 'name_prefix': return `${what} starts with a character code-only sign-in can't match to the name. Use a setup code so the driver picks their own PIN.`;
    default: return '';
  }
}

function _driverCodeWarningHtml(text) {
  // Dark amber on pale amber — the same high-contrast pair as the shared-phone
  // advisory; never var(--text-muted) on a light surface.
  return `
    <div role="status" style="display:flex;gap:8px;align-items:flex-start;margin-top:8px;padding:10px 12px;background:#FFFBEB;border:1px solid #FDE68A;border-radius:var(--radius-sm);font-size:0.8125rem;line-height:1.45;color:#92400E;">
      <span class="material-icons" aria-hidden="true" style="font-size:18px;flex:0 0 auto;color:#B45309;">warning</span>
      <span>${escapeHtml(text)}</span>
    </div>`;
}

// personId → what the Driver Code panel last resolved: 'stored' | 'custom' |
// 'setup' | 'other'. Module-private, never the code itself.
const _driverPanelState = new Map();

// Person save: warn when this driver still signs in with a NAME-derived code
// (no stored code on file per the panel, no private PIN, no setup code
// pending) and the saved name + phone derive one the login cannot find.
// Returns the warning sentence or ''.
function _driverCodeSaveWarning(personId, data) {
  try {
    if (!personId || !data) return '';
    const role = String(data.crewType || data.role || (Array.isArray(data.roles) ? data.roles[0] : '') || '');
    if (!/driver|cdl/i.test(role)) return '';
    const prev = (RosterState.people || []).find(p => p.id === personId) || {};
    if (prev.driverPinCustomSet) return '';
    const state = _driverPanelState.get(personId);
    if (state === 'stored' || state === 'custom' || state === 'setup') return '';
    if (prev.clockPin) return _driverCodeProblemText(driverCodeLoginProblem(prev.clockPin, data.name, { stored: true }));
    // resolveDriverPinCode only mints a name code with 10+ phone digits.
    if (String(data.phone || '').replace(/\D/g, '').length < 10) return '';
    const code = _serverDerivedDriverCode(data.name, data.phone);
    return _driverCodeProblemText(driverCodeLoginProblem(code, data.name, { stored: false }), { regenerated: true });
  } catch (_) {
    return '';
  }
}

// Render the driver PIN section in the edit modal
//
// TASK-1441 Phase 0b — the plaintext driver code moved into the `peopleSecure`
// vault (`allow read, write: if false`), because `people` is broadcast to every
// session including driver PINs and Firestore cannot field-redact a read. A
// driver on a yard tablet could previously list every coworker's login PIN
// straight out of this collection and sign in as them.
//
// So this panel no longer reads `person.driverPin`, and no longer writes one
// back. The custom-PIN check, the stored-code read, the cross-tenant
// availability probe and the persist all happen in ONE `resolveDriverPinCode`
// round trip. That also closes a TOCTOU the old code had: it called
// checkDriverPinAvailable and then issued a SEPARATE updateDoc, so two
// dispatchers acting at once could both pass the check and both write.
// TASK-4692 — personId → "a real setup code is out" (true) / "only the system
// placeholder, nothing sent" (false), from the panel's last resolveDriverPinCode
// answer. Module-private on purpose: never on the person object (shared
// RosterState that a save path could spread back onto the broadcast doc).
const _driverSetupCodeIssued = new Map();

// TASK-10027 muted-tenant UI hints (Chris 10/22) — how the setup / reset code button will
// REALLY reach this driver, mirroring the server (_sendDriverSetupCodeCore):
//   internal no-contact company → nothing is sent (read a code aloud);
//   muted company               → email to the driver's own address, never a text;
//   live company                → STOP'd driver gets email, everyone else a text.
// Display only — the server decides. Returns { channel: 'sms'|'email'|null, reason }.
function _driverCodeSendPlan(person) {
  const p = person || {};
  const mode = (window.getTenantSendMode?.() || { mode: 'live' }).mode;
  const hasEmail = !!String(p.email || '').trim();
  if (mode === 'noContact') return { channel: null, reason: 'no_contact' };
  if (mode === 'muted') return hasEmail ? { channel: 'email', reason: null } : { channel: null, reason: 'muted_no_email' };
  // Same provenance test as the server (TASK-876): an unticked SMS box is not a STOP.
  const isOptedOut = p.smsOptIn === false && !!(p.smsOptOutReason || p.smsOptOutAt);
  if (isOptedOut) return hasEmail ? { channel: 'email', reason: null } : { channel: null, reason: 'opted_out_no_email' };
  const digits = String(p.phone || '').replace(/\D/g, '');
  return digits.length >= 10 ? { channel: 'sms', reason: null } : { channel: null, reason: 'no_phone' };
}

// Why the code can't be sent, in plain words (shown under the buttons and as the toast).
function _driverCodeNoSendText(reason) {
  switch (reason) {
    case 'no_contact': return 'This company is set to send nothing, so show a one-time code here and read it to them.';
    case 'muted_no_email': return 'Texts are paused for this company and this driver has no email on file, so show a one-time code here and read it to them.';
    case 'opted_out_no_email': return 'This driver opted out of texts and has no email on file, so show a one-time code here and read it to them.';
    default: return 'No phone on file, so show a one-time code here and read it to them.';
  }
}

// The "send a code" button, labelled with the channel it will actually use.
// kind: 'setup' | 'reset'. Returns '' when the code cannot be sent at all.
function _driverCodeSendButtonHtml(person, jsId, kind, extraStyle) {
  const plan = _driverCodeSendPlan(person);
  if (!plan.channel) return '';
  const email = plan.channel === 'email';
  const what = kind === 'reset' ? 'reset code' : 'setup code';
  const label = `${email ? 'Email' : 'Text'} ${what}`;
  const title = email
    ? `Email a one-time ${what} to the driver's own email address on file`
    : `Text a one-time ${what} to the driver's phone`;
  return `<button type="button" class="btn btn-secondary btn-sm" onclick="window.RosterModule.sendDriverBootstrapCode('${jsId}')" title="${title}"${extraStyle ? ` style="${extraStyle}"` : ''}>
          <span class="material-icons" style="font-size:16px;">${email ? 'email' : 'sms'}</span> ${label}
        </button>`;
}

function _driverCodeSendNotice() {
  return window.tenantSendNoticeHtml ? window.tenantSendNoticeHtml('driverCode') : '';
}

// The pause starting / ending / turning no-contact while a person is open
// redraws their Driver Code buttons (app.js fires this on a mode flip).
window.addEventListener('dd:tenant-send-mode-changed', () => {
  const el = document.getElementById('person-driver-pin');
  const modal = document.getElementById('modal-person');
  const personId = document.getElementById('person-id')?.value;
  if (!el || !personId || !modal || modal.classList.contains('hidden')) return;
  const person = RosterState.people.find((p) => p.id === personId);
  if (person) renderDriverPinSection(el, person);
});

function renderDriverPinSection(el, person) {
  if (!el) return;
  if (!person || !person.id) {
    el.innerHTML = `<span class="text-muted" style="font-size:0.85rem;">Save person first to generate code</span>`;
    return;
  }
  el.innerHTML = `<span class="text-muted" style="font-size:0.85rem; display:inline-flex; align-items:center;"><span class="loading-spinner-small"></span>Loading driver code…</span>`;
  // Fire-and-forget: every caller treats this as a render, not a promise.
  resolveAndRenderDriverPin(el, person, { regenerate: false, toast: false });
}

/**
 * Resolve the person's driver code server-side and paint the panel.
 * `regenerate: true` forces a fresh computed code (the Regenerate button).
 */
async function resolveAndRenderDriverPin(el, person, { regenerate = false, toast = false } = {}) {
  if (!el || !person?.id) return;
  const tenant = window.AppState?.tenant || 'demo';
  let res = null;
  try {
    const fn = httpsCallable(functions, 'resolveDriverPinCode');
    const out = await fn({ tenantId: tenant, personId: person.id, regenerate: !!regenerate });
    res = out?.data || {};
  } catch (err) {
    // Fail VISIBLY, never silently blank. A dispatcher who sees nothing assumes
    // the driver has no code and re-issues one by another route.
    console.warn('[Roster] resolveDriverPinCode failed:', err?.message || err);
    el.innerHTML = `<span class="text-muted" style="font-size:0.85rem;">Couldn't load the driver code — reopen this person to retry.</span>`;
    if (toast) window.showToast?.('Could not reach the server to set a code. Try again.', 'error');
    return;
  }

  // Keep the in-memory person in step so the rest of the modal agrees with the
  // server. Deliberately NOT caching `res.pin` onto the person object: nothing
  // reads it any more, it would put the plaintext code back into shared
  // RosterState that other views read, and a future save path that spreads the
  // person would then try to write `driverPin` back onto the broadcast doc — a
  // write the firestore.rules denylist now REJECTS, which would fail the whole
  // person save. The rendered value below uses `res.pin` directly.
  if (res.customSet) person.driverPinCustomSet = true;
  if (res.setupPending) _driverSetupCodeIssued.set(person.id, !!res.codeIssued);
  else _driverSetupCodeIssued.delete(person.id);
  // TASK-4874 follow-up — what the panel resolved, for savePerson's warning.
  _driverPanelState.set(person.id, res.customSet ? 'custom'
    : (res.ok && res.pin) ? 'stored'
      : (res.setupPending || res.needsSetupCode) ? 'setup' : 'other');
  // TASK-4873 — JS-string-in-attribute escaping for the new handlers below
  // (escapeHtml/escAttr do not make an onclick string literal safe).
  const _pinJsId = window.escJsAttr ? window.escJsAttr(person.id) : String(person.id || '');

  // ── Branch 1: the driver chose their own private PIN (one-way HMAC) ────────
  if (res.customSet) {
    const resetDot = (res.pinResetRequested || person.pinResetRequested)
      ? `<span title="The driver asked for a PIN reset, or their private PIN is their own name + phone code. Text them a reset code so they pick a new one." style="display:inline-flex;align-items:center;gap:4px;color:#F59E0B;font-size:0.8rem;font-weight:600;"><span style="width:8px;height:8px;border-radius:50%;background:#F59E0B;display:inline-block;"></span>Reset needed</span>`
      : '';
    const _resetPlan = _driverCodeSendPlan(person);
    const _resetBtn = _resetPlan.channel
      ? _driverCodeSendButtonHtml(person, _pinJsId, 'reset', 'margin-left:auto;')
      : `<button type="button" class="btn btn-secondary btn-sm" onclick="window.RosterModule.showDriverSetupCode('${_pinJsId}')" title="Show a one-time reset code to read to the driver" style="margin-left:auto;">
          <span class="material-icons" style="font-size:16px;">visibility</span> Show reset code
        </button>`;
    const _resetHelp = _resetPlan.channel === 'email'
      ? 'This driver chose their own private PIN — it can\'t be displayed. Email a reset code if they\'re locked out.'
      : _resetPlan.channel
        ? 'This driver chose their own private PIN — it can\'t be displayed. Text a reset code if they\'re locked out.'
        : `This driver chose their own private PIN — it can't be displayed. Locked out? ${_driverCodeNoSendText(_resetPlan.reason)}`;
    el.innerHTML = `${_driverCodeSendNotice()}
      <div class="driver-pin-box" style="flex-wrap:wrap;gap:8px;">
        <span style="display:inline-flex;align-items:center;gap:6px;color:var(--text-primary);font-size:0.9rem;font-weight:600;">
          <span class="material-icons" style="font-size:18px;color:#10B981;">lock</span>Private PIN set
        </span>
        ${resetDot}
        ${_resetBtn}
      </div>
      <p class="text-muted" style="font-size:0.78rem;margin:6px 0 0;">${escapeHtml(_resetHelp)}</p>
    `;
    return;
  }

  // ── Branch 2: a stored plaintext code we can show ─────────────────────────
  if (res.ok && res.pin) {
    // escAttr, not escapeHtml — this lands inside a quoted HTML ATTRIBUTE and
    // escapeHtml does not escape quotes (see sharedFormat.js).
    const pinAttr = window.escAttr ? window.escAttr(res.pin) : res.pin;
    const pinText = window.escapeHtml ? window.escapeHtml(res.pin) : res.pin;
    // TASK-10027 muted-tenant UI hints — on a muted / no-contact company the "Send" (this
    // code by text/email) and app-link sends are held by the server, so they are
    // not offered; the setup-code button says the channel it will really use.
    const _liveSends = (window.getTenantSendMode?.() || { mode: 'live' }).mode === 'live';
    const _storedPlan = _driverCodeSendPlan(person);
    const _storedHelp = _storedPlan.channel === 'email'
      ? 'When this driver next logs in, they\'ll be asked to set a private PIN. You can also email them a secure setup code now.'
      : _storedPlan.channel
        ? 'When this driver next logs in, they\'ll be asked to set a private PIN. You can also text them a secure setup code now.'
        : `When this driver next logs in, they'll be asked to set a private PIN. ${_driverCodeNoSendText(_storedPlan.reason)}`;
    el.innerHTML = `${_driverCodeSendNotice()}
      <div class="driver-pin-box" style="flex-wrap:wrap;gap:8px;">
        <span class="driver-pin-value">${pinText}</span>
        <button type="button" class="btn btn-copy-pin" onclick="window.RosterModule.copyDriverPin('${pinAttr}')" title="Copy Code">
          <span class="material-icons">content_copy</span> Copy
        </button>
        <button type="button" class="btn btn-secondary btn-sm" onclick="window.RosterModule.generateDriverPin()" title="Regenerate Code" style="margin-left:4px;">
          <span class="material-icons" style="font-size:16px;">refresh</span>
        </button>
      </div>
      <p class="text-muted" style="font-size:0.78rem;margin:6px 0 0;">${escapeHtml(_storedHelp)}</p>
      ${_driverCodeSendButtonHtml(person, _pinJsId, 'setup', 'margin-top:6px;')}
      ${_liveSends ? `<button type="button" class="btn btn-secondary btn-sm" onclick="window.RosterModule.sendDriverAppLink('${_pinJsId}')" title="Text the driver the app download links — does NOT reset their PIN" style="margin-top:6px;margin-left:4px;">
        <span class="material-icons" style="font-size:16px;">phone_iphone</span> Text app download link
      </button>` : ''}
      <button type="button" class="btn btn-secondary btn-sm" onclick="window.RosterModule.showDriverSetupCode('${_pinJsId}')" title="Show a one-time setup code to read to the driver (works with no phone on file)" style="margin-top:6px;margin-left:4px;">
        <span class="material-icons" style="font-size:16px;">visibility</span> Show setup code
      </button>
    `;
    // TASK-4874 follow-up — a stored code the code-only login cannot find
    // (legacy short / lowercase / accented code) says so, with the fix.
    const _codeProblem = driverCodeLoginProblem(res.pin, person.name, { stored: true });
    if (_codeProblem) el.insertAdjacentHTML('beforeend', _driverCodeWarningHtml(_driverCodeProblemText(_codeProblem)));
    if (toast) window.showToast?.(`Driver Code set: ${res.pin}`, _codeProblem ? 'warning' : 'success');
    return;
  }

  // ── Branch 2b (TASK-4873): this driver's first login is a one-time setup code ─
  // setupPending  — a setup code (or the system placeholder) is on file, which
  //                 switches any name/phone-derived code OFF at login, so there is
  //                 no code to show — showing one would hand out a dead code.
  // needsSetupCode — no phone on file: a name-only code is never issued any more.
  // Either way the fix is the same: give the driver a one-time setup code and
  // they pick their own PIN. Lead with the buttons (COD Domain/UX).
  if (res.setupPending || res.needsSetupCode) {
    const issued = !!(res.setupPending && res.codeIssued);
    // TASK-10027 muted-tenant UI hints — the button and the help line follow the channel the
    // server will really use (email on a muted company, nothing on a no-contact
    // one), not "has a phone". Was: a muted driver with email but no phone got no
    // button at all, and the button said "Text" when the code went by email.
    const plan = _driverCodeSendPlan(person);
    const headline = issued
      ? 'Setup code sent — waiting for the driver to pick their PIN'
      : 'No driver code yet — this driver picks their own PIN';
    const help = issued
      ? 'The driver enters the setup code on the Driver Code screen and chooses a private PIN. Sending or showing a new code cancels the old one.'
      : plan.channel === 'email'
        ? 'Email them a one-time setup code, or show one here and read it to them. They enter it on the Driver Code screen and choose a private PIN.'
        : plan.channel
          ? 'Text them a one-time setup code, or show one here and read it to them. They enter it on the Driver Code screen and choose a private PIN.'
          : `${_driverCodeNoSendText(plan.reason)} They enter it on the Driver Code screen and choose a private PIN.`;
    el.innerHTML = `${_driverCodeSendNotice()}
      <div class="driver-pin-box" style="flex-wrap:wrap;gap:8px;">
        <span style="display:inline-flex;align-items:center;gap:6px;color:var(--text-primary);font-size:0.9rem;font-weight:600;">
          <span class="material-icons" style="font-size:18px;color:#F59E0B;">vpn_key</span>${headline}
        </span>
      </div>
      <div style="display:flex;flex-wrap:wrap;gap:6px;margin-top:6px;">
        <button type="button" class="btn btn-secondary btn-sm" onclick="window.RosterModule.showDriverSetupCode('${_pinJsId}')" title="Show a one-time setup code to read to the driver">
          <span class="material-icons" style="font-size:16px;">visibility</span> Show setup code
        </button>
        ${_driverCodeSendButtonHtml(person, _pinJsId, 'setup')}
      </div>
      <p class="text-muted" style="font-size:0.78rem;margin:6px 0 0;">${escapeHtml(help)}</p>
    `;
    return;
  }

  // ── Branch 3: the computed code collides with another driver's code ───────
  // TASK-4873 — the fix is a setup code, not a name/phone edit (COD Code
  // Reviewer): the driver picks a private PIN, which setDriverPin guarantees is
  // unique. Wording stays generic — it never says WHO holds the code.
  if (res.error === 'pin_taken') {
    el.innerHTML = `${_driverCodeSendNotice()}
      <div class="driver-pin-box" style="flex-wrap:wrap; gap:8px;">
        <span style="color:var(--text-primary);font-size:0.9rem;font-weight:600;">This driver's code is already in use — give them a setup code instead</span>
      </div>
      <div style="display:flex;flex-wrap:wrap;gap:6px;margin-top:6px;">
        <button type="button" class="btn btn-secondary btn-sm" onclick="window.RosterModule.showDriverSetupCode('${_pinJsId}')" title="Show a one-time setup code to read to the driver">
          <span class="material-icons" style="font-size:16px;">visibility</span> Show setup code
        </button>
        ${_driverCodeSendButtonHtml(person, _pinJsId, 'setup')}
      </div>
      <p class="text-muted" style="font-size:0.78rem;margin:6px 0 0;">They enter the setup code on the Driver Code screen and choose a private PIN.</p>
    `;
    return;
  }

  // ── Branch 4: not enough to derive a code from ────────────────────────────
  el.innerHTML = `<span class="text-muted" style="font-size:0.85rem;">Add a name to generate code</span>`;
}

// Generate (or regenerate) a driver PIN. The availability check and the write
// are now a single server round trip (TASK-1441 Phase 0b) — the client no longer
// writes `driverPin` to the person doc at all.
async function generateDriverPin() {
  const personId = document.getElementById('person-id')?.value;
  if (!personId) { window.showToast?.('Save the person first', 'error'); return; }

  const person = RosterState.people.find(p => p.id === personId);
  if (!person) return;

  // TASK-4874 follow-up — Regenerate mints from the SAVED name + phone (only
  // with 10+ digits and SMS on). If that code is one the code-only login can't
  // find, it would replace a working code with a dead one: ask first.
  if (String(person.phone || '').replace(/\D/g, '').length >= 10 && person.smsOptIn !== false) {
    const _next = _serverDerivedDriverCode(person.name, person.phone);
    const _problem = driverCodeLoginProblem(_next, person.name, { stored: false });
    if (_problem) {
      const go = await window.showConfirm(
        'New code won’t work at sign-in',
        `${_driverCodeProblemText(_problem, { regenerated: true })} Regenerate anyway?`,
        { confirmText: 'Regenerate anyway', cancelText: 'Cancel' }
      );
      if (!go) return;
    }
  }

  const sectionEl = document.getElementById('person-driver-pin');
  if (sectionEl) {
    sectionEl.innerHTML = `<span class="text-muted" style="font-size:0.85rem; display:inline-flex; align-items:center;"><span class="loading-spinner-small"></span>Generating code…</span>`;
  }
  await resolveAndRenderDriverPin(sectionEl, person, { regenerate: true, toast: true });
}

// TASK-10098 — the old "Send" (texted/emailed the stored, usually name + phone
// derived code through the retired sendDriverPinNotification callable) is gone.
// Kept on window.RosterModule so a stale caller lands on the one-time setup-code
// flow (dispatch-module gated server-side) — never the stored code.
function sendDriverPinNotification(personId) {
  return sendDriverBootstrapCode(personId);
}

// TASK-PIN Slice 1 — text the driver a fresh one-time secure setup/reset code.
// Generates + stores a new bootstrap hash server-side and texts the plaintext;
// the dispatcher never sees the code (it's read straight to the driver by SMS).
async function sendDriverBootstrapCode(personId) {
  if (!personId) return;
  const person = RosterState.people.find(p => p.id === personId);
  if (!person) return;
  const tenant = window.AppState?.tenant;
  if (!tenant) { window.showToast?.('Tenant not loaded yet', 'error'); return; }

  // TASK-10098 (COD) — the server sends to the SAVED phone/email. If this
  // person's open edit form has a different phone or email, a code would go to
  // the old contact: ask for a save first (the retired Send flushed these; this
  // path never writes the person).
  if (document.getElementById('person-id')?.value === personId) {
    const livePhone = String(document.getElementById('person-phone')?.value ?? person.phone ?? '').replace(/\D/g, '');
    const liveEmail = String(document.getElementById('person-email')?.value ?? person.email ?? '').trim().toLowerCase();
    if (livePhone !== String(person.phone || '').replace(/\D/g, '') || liveEmail !== String(person.email || '').trim().toLowerCase()) {
      window.showToast?.('Save this person first — the phone or email was changed, and the code would go to the old one.', 'error');
      return;
    }
  }

  // TASK-831 (TCPA): a driver who texted STOP can't be texted — the server emails
  // the code instead when they have one. So gate on a usable channel: opted-out
  // needs an email; everyone else needs a phone. (Opted-out + no email → read them
  // a code from the Driver Code panel.)
  // TASK-10027 muted-tenant UI hints — one plan for the button label and this pre-check
  // (_driverCodeSendPlan mirrors the server). Was phone-first: a muted company's
  // driver with an email but no phone was refused here before the server, which
  // would have emailed them, was ever asked.
  const plan = _driverCodeSendPlan(person);
  if (!plan.channel) {
    window.showToast?.(plan.reason === 'no_phone'
      ? 'Add a phone number for this driver first, or use Show setup code and read it to them.'
      : _driverCodeNoSendText(plan.reason), 'error');
    return;
  }

  const sectionEl = document.getElementById('person-driver-pin');
  const btn = sectionEl?.querySelector('button[onclick*="sendDriverBootstrapCode"]');
  const originalHtml = btn?.innerHTML;
  if (btn) { btn.disabled = true; btn.innerHTML = `<span class="loading-spinner-small"></span>Sending…`; }

  try {
    const fn = httpsCallable(functions, 'sendDriverBootstrapCode');
    const { data } = await fn({ tenantId: tenant, personId });
    if (data?.ok) {
      const firstName = (person.name || '').trim().split(/\s+/)[0] || 'driver';
      const verb = data.channel === 'email' ? 'Emailed' : 'Texted';
      // Say WHY it went by email when the office expected a text.
      const why = data.channel === 'email' && (window.getTenantSendMode?.() || {}).mode === 'muted'
        ? ' (texts are paused for this company)' : '';
      window.showToast?.(`${verb} a secure setup code to ${firstName}${why}. They'll set their own private PIN.`, 'success');
      // Reflect pinResetRequested locally so the section re-renders with the dot.
      person.pinResetRequested = true;
      _driverSetupCodeIssued.set(personId, true); // TASK-4692
      if (sectionEl) renderDriverPinSection(sectionEl, person);
    } else {
      window.showToast?.('Could not send the setup code — try again.', 'error');
    }
  } catch (err) {
    console.error('[Roster] sendDriverBootstrapCode error:', err);
    window.showToast?.(err?.message || 'Could not send the setup code — try again.', 'error');
  } finally {
    if (btn) { btn.disabled = false; btn.innerHTML = originalHtml; }
  }
}

// ── TASK-9941 F S3 — Driver PINs (replaces TASK-4692's "Text setup codes") ──
// Who still has no private PIN, a per-company "Send setup codes", and the
// owner's yard-tablet cutover date. The panel lives in the LAZY
// rosterPinSetup.js; this is only the gate, the chip/filter and the loader.
// The status comes from getDriverPinRosterStatus: one word per person
// ('private' | 'setup_sent' | 'none') plus counts — never a PIN or a hash. The
// broadcast `people` doc's own flags are not trusted for this (the hash lives
// in the server-only vault). The send runs server-side in ONE callable
// (sendDriverSetupCodesBulk): owner or Roster / HR module, STOP list + credits
// meter checked, audited. Access = modules, never roles (Chris 9/24).
let _pinSetupLoad = null;
let _pinStatusCache = null; // { tenant, data|null, at }
const PIN_STATUS_RETRY_MS = 60 * 1000; // a failed read is retried after a minute, not on every render
let _pinStatusInflight = false;
let _noPinFilterOn = false;

function _canManageDriverPins() {
  const tenant = window.AppState?.tenant;
  if (!tenant) return false;
  return window.checkModuleAccess?.('roster', true) === true || window.checkModuleAccess?.('hr', true) === true;
}

function _loadPinSetupModule() {
  if (!_pinSetupLoad) {
    _pinSetupLoad = __ddChunkWrap(import('./rosterPinSetup.js?v=9941302')).catch((e) => {
      _pinSetupLoad = null;
      console.warn('[Roster] rosterPinSetup load failed:', e?.message || e);
      return null;
    });
  }
  return _pinSetupLoad;
}

function _pinStatusData() {
  return _pinStatusCache && _pinStatusCache.tenant === window.AppState?.tenant ? _pinStatusCache.data : null;
}

// Set of person ids with no private PIN while the chip filter is on, else null.
function _noPinFilterIds() {
  if (!_noPinFilterOn) return null;
  const d = _pinStatusData();
  if (!d || !d.people) return null;
  return new Set(Object.keys(d.people).filter((id) => d.people[id] !== 'private'));
}

function _paintDriverPinOffice() {
  const chip = document.getElementById('roster-nopin-chip');
  const countEl = document.getElementById('roster-nopin-chip-count');
  const banner = document.getElementById('roster-pin-cutover-banner');
  const d = _canManageDriverPins() ? _pinStatusData() : null;
  const n = d && d.counts ? (d.counts.none || 0) + (d.counts.setupSent || 0) : 0;
  if (chip && countEl) {
    if (d && (n > 0 || _noPinFilterOn)) {
      countEl.textContent = _noPinFilterOn ? `${n} no private PIN · show all` : `${n} no private PIN`;
      chip.style.display = 'inline-flex';
    } else {
      chip.style.display = 'none';
    }
  }
  if (banner) {
    const notice = d && d.cutover && d.cutover.notice;
    if (notice) {
      _loadPinSetupModule().then((mod) => {
        if (!mod) return;
        banner.innerHTML = mod.cutoverBannerHtml(d, escapeHtml);
        banner.style.display = '';
      });
    } else {
      banner.style.display = 'none';
      banner.innerHTML = '';
    }
  }
}

// Called on every people render; reads the server once per tenant per page load.
async function _syncDriverPinOffice(force = false) {
  const btn = document.getElementById('btn-roster-pin-setup');
  const ok = _canManageDriverPins() && !window.AppState?.isDemo && window.AppState?.tenant !== 'demo';
  if (btn) btn.style.display = ok ? '' : 'none';
  if (!ok) { _paintDriverPinOffice(); return; }
  const tenant = window.AppState.tenant;
  const fresh = _pinStatusCache && _pinStatusCache.tenant === tenant && (_pinStatusCache.data || Date.now() - _pinStatusCache.at < PIN_STATUS_RETRY_MS);
  if (!force && fresh) { _paintDriverPinOffice(); return; }
  if (_pinStatusInflight) return;
  _pinStatusInflight = true;
  try {
    const mod = await _loadPinSetupModule();
    if (!mod) return;
    let data = null;
    try { data = await mod.fetchPinStatus(tenant); } catch (_) { data = null; } // refused / not deployed: no chip
    _pinStatusCache = { tenant, data, at: Date.now() };
  } finally {
    _pinStatusInflight = false;
  }
  _paintDriverPinOffice();
  if (_noPinFilterOn) renderPeopleList();
}

function toggleNoPinFilter() {
  _noPinFilterOn = !_noPinFilterOn && !!_pinStatusData();
  renderPeopleList();
  _paintDriverPinOffice();
}

async function openDriverPins() {
  if (!_canManageDriverPins()) {
    window.showToast?.('Driver PINs need Roster or HR access.', 'error');
    return;
  }
  const tenant = window.AppState?.tenant;
  const mod = await _loadPinSetupModule();
  if (!mod) { window.showToast?.('Could not open Driver PINs. Reload and try again.', 'error'); return; }
  // Always a fresh read on open (COD r1): people added or set up since the page
  // loaded must show correctly in the send list.
  let data = null;
  try { data = await mod.fetchPinStatus(tenant); _pinStatusCache = { tenant, data, at: Date.now() }; } catch (e) {
    window.showToast?.(e?.code === 'functions/permission-denied' ? (e.message || 'Driver PINs need Roster or HR access.') : 'Could not load driver PINs. Try again in a moment.', 'error');
    return;
  }
  _paintDriverPinOffice();
  mod.openDriverPinsPanel({
    tenantId: tenant,
    people: RosterState.people,
    status: data,
    canSend: _rosterCanEditPeople(),
    escapeHtml,
    onChanged: (next) => {
      if (next) _pinStatusCache = { tenant, data: next, at: Date.now() };
      _paintDriverPinOffice();
      if (_noPinFilterOn) renderPeopleList();
    },
  });
}

// ── TASK-9768 slice 2b — "Setup answers are waiting" banner ────────────────
// A company setup link came back and nothing from it has been added yet. One
// summary-only read per tenant per page load (listSetupQuestionnaires
// {summaryOnly:true}: label + date only, no answers). Same gate as the Account
// card (owner or Roster module, server-enforced); any refusal = no banner.
let _setupqBannerCache = null; // { tenant, waiting }
const _SETUPQ_BANNER_DISMISS = 'dd_setupq_banner_dismissed';

function _setupqBannerHtml(waiting) {
  const esc = (v) => String(v == null ? '' : v).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
  const n = waiting.length;
  const first = waiting[0] || {};
  const who = n === 1 ? (first.label ? `${esc(first.label)} sent back the company setup link` : 'A company setup link came back') : `${n} company setup links came back`;
  return `<div style="display:flex;gap:10px;align-items:center;flex-wrap:wrap;background:#DBEAFE;border:1px solid #93C5FD;color:#1E3A8A;border-radius:8px;padding:10px 12px;margin:0 0 10px;">
    <span class="material-icons" aria-hidden="true">assignment</span>
    <span style="flex:1;min-width:180px;font-size:0.9rem;">${who}. Their people and vehicles are waiting for you to add.</span>
    <button type="button" class="btn btn-primary btn-sm" style="min-height:44px;" onclick="window.RosterModule.openSetupAnswers()">Review answers</button>
    <button type="button" class="btn btn-secondary btn-sm" style="min-height:44px;" onclick="window.RosterModule.dismissSetupAnswersBanner()" aria-label="Hide this for now">Not now</button>
  </div>`;
}

function _paintSetupAnswersBanner() {
  const el = document.getElementById('roster-setupq-banner');
  if (!el) return;
  let dismissed = false;
  try { dismissed = sessionStorage.getItem(_SETUPQ_BANNER_DISMISS) === '1'; } catch (_) { /* storage blocked */ }
  const w = _setupqBannerCache && _setupqBannerCache.tenant === window.AppState?.tenant ? _setupqBannerCache.waiting : [];
  if (dismissed || !w.length) { el.style.display = 'none'; el.innerHTML = ''; return; }
  el.innerHTML = _setupqBannerHtml(w);
  el.style.display = '';
}

async function _syncSetupAnswersBanner() {
  const tenant = window.AppState?.tenant;
  if (!tenant || tenant === 'demo' || window.AppState?.isDemo) return;
  if (window.checkModuleAccess?.('roster', true) !== true) return;
  if (_setupqBannerCache && _setupqBannerCache.tenant === tenant) { _paintSetupAnswersBanner(); return; }
  try {
    const res = await httpsCallable(functions, 'listSetupQuestionnaires')({ summaryOnly: true });
    const waiting = Array.isArray(res?.data?.waiting) ? res.data.waiting : [];
    _setupqBannerCache = { tenant, waiting };
  } catch (_) {
    _setupqBannerCache = { tenant, waiting: [] }; // not allowed / not deployed: no banner
  }
  _paintSetupAnswersBanner();
}

function openSetupAnswers() {
  _setupqBannerCache = null; // re-check when they come back to Roster
  if (typeof window.navigateToView === 'function') window.navigateToView('account');
  // The Account card mounts after its own render; scroll to it once it is there.
  let tries = 0;
  const t = setInterval(() => {
    const card = document.getElementById('account-setup-questionnaire');
    if (card || ++tries > 20) { clearInterval(t); card?.scrollIntoView({ behavior: 'smooth', block: 'start' }); }
  }, 150);
}

function dismissSetupAnswersBanner() {
  try { sessionStorage.setItem(_SETUPQ_BANNER_DISMISS, '1'); } catch (_) { /* storage blocked */ }
  _paintSetupAnswersBanner();
  const el = document.getElementById('roster-setupq-banner');
  if (el) { el.style.display = 'none'; el.innerHTML = ''; }
}

// TASK-4873 — show a one-time setup code ON SCREEN for the dispatcher to read to
// the driver (no phone on file, opted out of texts, or standing at the desk).
// generateDriverBootstrapCode stores only the hash and returns the plaintext
// ONCE; it is never cached on the person or in RosterState. Issuing a new code
// cancels any earlier one, so confirm before replacing a code already sent.
async function showDriverSetupCode(personId) {
  if (!personId) return;
  const person = RosterState.people.find(p => p.id === personId);
  if (!person) return;
  const tenant = window.AppState?.tenant;
  if (!tenant) { window.showToast?.('Tenant not loaded yet', 'error'); return; }
  // TASK-4692 — pinResetRequested is also raised for a new hire who has never
  // been sent anything (it stops Dispatch texts printing a dead code), so ask
  // the panel's last server answer first: only an ISSUED code is "replaced".
  const _issuedKnown = _driverSetupCodeIssued.has(personId);
  const _codeOut = _issuedKnown ? _driverSetupCodeIssued.get(personId) : !!person.pinResetRequested;
  if (_codeOut && window.showConfirm) {
    const ok = await window.showConfirm(
      'Replace the earlier setup code?',
      'A setup code was already sent to this driver. A new one cancels it — the old code will stop working.',
      { confirmText: 'Show new code', cancelText: 'Cancel' }
    );
    if (!ok) return;
  }
  const sectionEl = document.getElementById('person-driver-pin');
  const btn = sectionEl?.querySelector('button[onclick*="showDriverSetupCode"]');
  const originalHtml = btn?.innerHTML;
  if (btn) { btn.disabled = true; btn.innerHTML = `<span class="loading-spinner-small"></span>Creating…`; }
  try {
    const fn = httpsCallable(functions, 'generateDriverBootstrapCode');
    const { data } = await fn({ tenantId: tenant, personId });
    if (!data?.ok || !data.code) {
      window.showToast?.('Could not create a setup code — try again.', 'error');
      return;
    }
    person.pinResetRequested = true;
    _driverSetupCodeIssued.set(personId, true); // TASK-4692
    const firstName = (person.name || '').trim().split(/\s+/)[0] || 'the driver';
    const codeText = window.escapeHtml ? window.escapeHtml(data.code) : data.code;
    if (sectionEl) {
      sectionEl.innerHTML = `
        <div class="driver-pin-box" style="flex-direction:column;align-items:flex-start;gap:6px;">
          <span style="color:var(--text-primary);font-size:0.85rem;font-weight:600;">Read this setup code to ${window.escapeHtml ? window.escapeHtml(firstName) : firstName}:</span>
          <span class="driver-pin-value" style="font-size:1.75rem;letter-spacing:4px;">${codeText}</span>
        </div>
        <p class="text-muted" style="font-size:0.78rem;margin:6px 0 0;">It works once. They enter it on the Driver Code screen, then choose a private PIN. This code is not shown again — creating another one cancels this one.</p>
      `;
    }
  } catch (err) {
    console.error('[Roster] generateDriverBootstrapCode error:', err);
    window.showToast?.(err?.message || 'Could not create a setup code — try again.', 'error');
  } finally {
    if (btn && btn.isConnected) { btn.disabled = false; btn.innerHTML = originalHtml; }
  }
}

// TASK-1275 — text/email the driver just the native-app download links (no PIN reset).
async function sendDriverAppLink(personId) {
  if (!personId) return;
  // TASK-10027 muted-tenant UI hints — held by the server on a muted / no-contact company.
  if ((window.getTenantSendMode?.() || { mode: 'live' }).mode !== 'live') {
    window.showToast?.(window.tenantSendNoticeText?.('driverCode') || 'Sending is paused for this company.', 'error');
    return;
  }
  const person = RosterState.people.find(p => p.id === personId);
  if (!person) return;
  const tenant = window.AppState?.tenant;
  if (!tenant) { window.showToast?.('Tenant not loaded yet', 'error'); return; }

  const digits = String(person.phone || '').replace(/\D/g, '');
  const isOptedOut = person.smsOptIn === false;
  const hasEmail = !!String(person.email || '').trim();
  if (isOptedOut && !hasEmail) {
    window.showToast?.('This driver opted out of texts and has no email on file.', 'error');
    return;
  }
  if (!isOptedOut && digits.length < 10) {
    window.showToast?.('Add a phone number for this driver first, then text the app link.', 'error');
    return;
  }

  const sectionEl = document.getElementById('person-driver-pin');
  const btn = sectionEl?.querySelector('button[onclick*="sendDriverAppLink"]');
  const originalHtml = btn?.innerHTML;
  if (btn) { btn.disabled = true; btn.innerHTML = `<span class="loading-spinner-small"></span>Sending…`; }

  try {
    const fn = httpsCallable(functions, 'sendDriverAppLink');
    const { data } = await fn({ tenantId: tenant, personId });
    if (data?.ok) {
      const firstName = (person.name || '').trim().split(/\s+/)[0] || 'driver';
      const verb = data.channel === 'email' ? 'Emailed' : 'Texted';
      window.showToast?.(`${verb} the app download link to ${firstName}.`, 'success');
    } else {
      window.showToast?.('Could not send the app link — try again.', 'error');
    }
  } catch (err) {
    console.error('[Roster] sendDriverAppLink error:', err);
    window.showToast?.(err?.message || 'Could not send the app link — try again.', 'error');
  } finally {
    if (btn) { btn.disabled = false; btn.innerHTML = originalHtml; }
  }
}

function copyDriverPin(pin) {
  navigator.clipboard.writeText(pin).then(() => {
    window.showToast?.('Driver Code copied!', 'success');
  }).catch(() => {
    // Fallback
    const temp = document.createElement('input');
    temp.value = pin;
    document.body.appendChild(temp);
    temp.select();
    document.execCommand('copy');
    document.body.removeChild(temp);
    window.showToast?.('Driver Code copied!', 'success');
  });
}

// ============================================
// FILTER FUNCTIONS
// ============================================
function filterPeople() {
  const searchEl = document.getElementById('search-people');
  const roleEl = document.getElementById('filter-people-role');
  const yardEl = document.getElementById('filter-people-yard');
  const statusEl = document.getElementById('filter-people-status');
  RosterState.filters.people.search = searchEl?.value || '';
  RosterState.filters.people.role = roleEl?.value || 'all';
  RosterState.filters.people.yard = yardEl?.value || 'all';
  RosterState.filters.people.status = statusEl?.value || 'active';
  renderPeopleList();
}

// TASK-1476 — render (or clear) the person modal's reconciliation notice.
//
// `crewTypeOverride` lets the notice re-evaluate against whatever is CURRENTLY
// selected in the Role dropdown, so picking a different crew type makes the
// notice update live. It is a display recomputation ONLY — this function never
// writes, and savePerson does not call it. Nothing here can move `hrPosition`.
function _syncPersonRoleConflictNotice(person, crewTypeOverride) {
  const el = document.getElementById('person-role-conflict-notice');
  if (!el) return;
  if (!person || !person.hrPosition) { el.style.display = 'none'; el.innerHTML = ''; return; }

  // Spread the WHOLE person, then override crewType — a hand-built
  // {hrPosition, crewType} probe silently drops `cdlLicensed`, which the
  // driver-picker branch of the message depends on (caught by the E2E: a
  // CDL-licensed laborer was told they wouldn't show in the driver picker,
  // when cdlLicensed===true puts them there regardless). `role`/`roles` are
  // nulled so an empty override can't fall through to a legacy value.
  const probe = crewTypeOverride === undefined
    ? person
    : { ...person, crewType: crewTypeOverride, role: null, roles: null };
  const conflict = getRoleConflict(probe);
  if (!conflict) { el.style.display = 'none'; el.innerHTML = ''; return; }

  // Same accuracy rules as getRoleConflict's `summary` — see the comment there
  // before editing this. Crew type decides which RATE the hours price at and
  // whether reports count them as field crew; it does NOT decide whether they can
  // be assigned to a job at all.
  const affects = conflict.kind === 'hr-driver-crew-not'
    ? (conflict.cdlOverrides
      ? 'They still show in the driver picker because CDL Licensed is on, but their hours price at the rate above.'
      : 'They will not show in the driver picker when a job is assigned.')
    : conflict.crewHasRate
      ? `Crew type is what tickets price their hours against and what marks them as field crew in reporting${conflict.crewIsDriverish ? ', and it puts them in the driver picker on a job' : ''}.`
      : 'Crew type is what tickets price their hours against and what marks them as field crew in reporting — and this one carries no crew rate of its own.';

  el.innerHTML = `
    <div class="form-group" style="display:flex;gap:10px;align-items:flex-start;padding:10px 12px;margin-bottom:var(--spacing-md);background:#FFFBEB;border:1px solid #FDE68A;border-radius:var(--radius-md);">
      <span class="material-icons" aria-hidden="true" style="font-size:18px;color:#B45309;flex:0 0 auto;margin-top:1px;">rule</span>
      <div style="font-size:0.8125rem;line-height:1.45;color:#78350F;">
        <strong>Their HR title and their role disagree.</strong>
        HR has them as <strong>${escapeHtml(conflict.hrTitle)}</strong>; the role above is <strong>${escapeHtml(conflict.crewLabel)}</strong>.
        ${escapeHtml(affects)}
        Change the role here if it's wrong, or fix the title under HR &rsaquo; Employee &mdash; whichever one is out of date. Nothing is changed for you.
      </div>
    </div>`;
  el.style.display = '';
}

// TASK-1476 — keep the role-conflict chip honest on every people snapshot.
// Counts the WHOLE roster (not the filtered view) so the number doesn't change
// under the user as they search — the label says "to review", and clicking it
// widens the status filter to All so nothing is hidden behind "Active".
function _syncRoleConflictChip() {
  const chip = document.getElementById('roster-role-conflict-chip');
  const countEl = document.getElementById('roster-role-conflict-chip-count');
  if (!chip || !countEl) return;
  const n = countRoleConflicts();
  if (n > 0) {
    countEl.textContent = `${n} role ${n === 1 ? 'conflict' : 'conflicts'}`;
    chip.style.display = 'inline-flex';
  } else {
    chip.style.display = 'none';
  }
}

// TASK-1476 — one-tap "show me the conflicts". Widens employment status to All
// (a conflict on a terminated person still matters: their historical crew type
// is what billing and the DQ file read) and clears the search box so the chip's
// count and the resulting list always agree.
function showRoleConflicts() {
  const searchEl = document.getElementById('search-people');
  const roleEl = document.getElementById('filter-people-role');
  const statusEl = document.getElementById('filter-people-status');
  if (searchEl) searchEl.value = '';
  if (statusEl) statusEl.value = 'all';
  if (roleEl) {
    // The option only exists while a conflict exists; populateRoleFilterDropdown
    // adds it on every snapshot. Guard anyway rather than setting a value the
    // select would silently reject.
    if (!Array.from(roleEl.options).some(o => o.value === ROLE_CONFLICT_FILTER)) return;
    roleEl.value = ROLE_CONFLICT_FILTER;
  }
  RosterState.filters.people.search = '';
  RosterState.filters.people.status = 'all';
  RosterState.filters.people.role = ROLE_CONFLICT_FILTER;
  renderPeopleList();
}

// TASK-788 — one-tap "Show all" from the hidden-people hint. Flips the
// employment-status filter from "Active" to "All" (syncing the dropdown so the
// UI stays consistent) and re-renders, revealing inactive + terminated people.
function showAllPeople() {
  const statusEl = document.getElementById('filter-people-status');
  if (statusEl) statusEl.value = 'all';
  RosterState.filters.people.status = 'all';
  renderPeopleList();
}

function filterTrucks() {
  const searchEl = document.getElementById('search-trucks');
  const typeEl = document.getElementById('filter-trucks-type');
  const sortEl = document.getElementById('sort-trucks');
  const yardEl = document.getElementById('filter-trucks-yard');
  RosterState.filters.trucks.search = searchEl?.value || '';
  RosterState.filters.trucks.type = typeEl?.value || 'all';
  RosterState.filters.trucks.sort = sortEl?.value || 'unit';
  RosterState.filters.trucks.yard = yardEl?.value || 'all';
  renderTrucksList();
}

function filterTrailers() {
  const searchEl = document.getElementById('search-trailers');
  const typeEl = document.getElementById('filter-trailers-type');
  const sortEl = document.getElementById('sort-trailers');
  const yardEl = document.getElementById('filter-trailers-yard');
  RosterState.filters.trailers.search = searchEl?.value || '';
  RosterState.filters.trailers.type = typeEl?.value || 'all';
  RosterState.filters.trailers.sort = sortEl?.value || 'unit';
  RosterState.filters.trailers.yard = yardEl?.value || 'all';
  renderTrailersList();
}

function toggleInactive(section) {
  if (section === 'assets') RosterState.filters.assets.belowZero = false; // TASK-9985
  RosterState.filters[section].showInactive = !RosterState.filters[section].showInactive;

  // Update button appearance
  const btn = document.getElementById(`toggle-inactive-${section}`);
  if (btn) {
    btn.classList.toggle('active', RosterState.filters[section].showInactive);
    const label = btn.querySelector('.toggle-label');
    if (label) {
      label.textContent = RosterState.filters[section].showInactive ? 'Hide Inactive' : 'Show Inactive';
    }
  }

  // Re-render the appropriate list
  if (section === 'people') renderPeopleList();
  else if (section === 'trucks') renderTrucksList();
  else if (section === 'trailers') renderTrailersList();
  else if (section === 'assets') renderAssetsList();
}

// ============================================
// ASSETS/INVENTORY FUNCTIONS
// ============================================

// Order status stages for pizza tracker
const ORDER_STATUSES = ['pending', 'pick', 'prep', 'load', 'shipping', 'delivered'];
const ORDER_STATUS_LABELS = {
  pending: 'Pending',
  pick: 'Pick',
  prep: 'Prep',
  load: 'Load',
  shipping: 'Shipping',
  delivered: 'Delivered'
};

// TASK-9275 assetStockIsNegative + TASK-4810 rentalStockView: moved to
// functions/src/shared/assetRollup.js (TASK-9375 S2) — imported at the top.

// ============================================
// TASK-10007 — PINNED ASSET TOTALS (Guy 10/21: "this is how many Dura-Base I
// have, this is how many System 7 I have"). A star on each quantity-tracked
// asset card; starred assets show as an owned / out / available strip above
// the dispatch board and sort first (with a ★) in the Add Job asset picker.
//
// ONE list per company: tenants/{tid}.settings.pinnedAssetIds (<= 8 ids,
// firestore.rules t10007PinnedAssetsOk). Who may star: the owner or a member
// holding the Roster module (modules, never roles — same arms as
// _rosterCanEditAliases); the star is not rendered for anyone else, so there
// is no dead tap. Writes go through window._settingsPersist with arrayUnion /
// arrayRemove, never a whole-array overwrite, so two people starring at once
// both land. The strip's numbers come from dispatch.js rentalStockView — the
// same rule as this file's copy that the card prints (task4810 pins the two
// copies equal); nothing here re-totals stock.
// ============================================
const PINNED_ASSET_MAX = 8;

/** The stored pin list, cleaned: strings only, de-duplicated, order kept. PURE. */
function pinnedAssetIdsFrom(settings) {
  const raw = settings && Array.isArray(settings.pinnedAssetIds) ? settings.pinnedAssetIds : [];
  const out = [];
  for (const id of raw) {
    if (typeof id === 'string' && id && !out.includes(id)) out.push(id);
  }
  return out;
}

/** Only a quantity-tracked, active asset can be pinned (it has a count to show). PURE. */
function _pinnedAssetEligible(asset) {
  return !!asset && asset.trackingMode === 'quantity' && asset.active !== false;
}

/** Who may star / unstar. PURE (explicit args) so the unit test executes it.
 *  Owner (tenants/{tid}.ownerId) or a member with `roster`, not view/upload-only,
 *  read-only or suspended. Driver-PIN, driver-link and partner sessions never.
 *  The shared demo tenant keeps the star (demo parity). */
function _pinnedAssetStarGate({ tenantData, uid, memberAccess, tenant, isDemo, driverModePin, driverMode, isPublicAccess, partner } = {}) {
  if (driverModePin === true || driverMode === true || isPublicAccess === true || partner === true) return false;
  if (isDemo === true || tenant === 'demo') return true;
  const td = tenantData || {};
  if (td.ownerId && uid && td.ownerId === uid) return true;
  const m = memberAccess;
  if (!m) return false;
  if (['revoked', 'inactive', 'pending', 'suspended', 'removed'].includes(m.status)) return false;
  if (m.viewUploadOnly === true || m.readOnly === true) return false;
  return Array.isArray(m.modules) && m.modules.includes('roster');
}
function _canPinAssets() {
  const S = window.AppState || {};
  const partner = typeof window.isPartnerClientSession === 'function' && window.isPartnerClientSession() === true;
  return _pinnedAssetStarGate({ tenantData: S.tenantData, uid: auth?.currentUser?.uid || S.user?.uid, memberAccess: S.memberAccess,
    tenant: S.tenant, isDemo: S.isDemo, driverModePin: S.driverModePin, driverMode: S.driverMode, isPublicAccess: S.isPublicAccess, partner });
}

/**
 * What a star tap should write. PURE. `ids` = the stored list, `assets` = the
 * whole roster mirror (inactive included). Returns
 *   { action: 'remove' | 'add' | 'full' | 'ineligible', stale: [ids] }
 * `stale` = stored ids that no longer name a pinnable asset (deleted,
 * deactivated, switched to individual tracking). They are invisible on the
 * strip, so they must not hold one of the 8 slots: an add at the cap removes
 * them first. 'full' = 8 live pins already.
 */
function planPinnedAssetToggle(ids, assets, assetId) {
  const list = Array.isArray(ids) ? ids : [];
  const pool = Array.isArray(assets) ? assets : [];
  if (list.includes(assetId)) return { action: 'remove', stale: [] };
  const asset = pool.find(a => a && a.id === assetId);
  if (!_pinnedAssetEligible(asset)) return { action: 'ineligible', stale: [] };
  const stale = list.filter(id => !_pinnedAssetEligible(pool.find(a => a && a.id === id)));
  const live = list.length - stale.length;
  if (live >= PINNED_ASSET_MAX) return { action: 'full', stale: [] };
  return { action: 'add', stale: list.length >= PINNED_ASSET_MAX ? stale : [] };
}

const _pinnedAssetBusy = new Set();
// COD r1 (Code): taps run ONE AT A TIME, so each reads the list the previous
// tap left on AppState. Two quick taps on different stars used to read the
// same stale list, and the second put back a list without the first.
let _pinnedAssetQueue = Promise.resolve();
function togglePinnedAsset(assetId) {
  if (!assetId || _pinnedAssetBusy.has(assetId)) return _pinnedAssetQueue;
  _pinnedAssetBusy.add(assetId);
  // COD r3 (Code): bound to the company it was tapped in — a queued or
  // half-done tap must never write into a company switched to meanwhile.
  const tid = window.AppState?.tenant || null;
  _pinnedAssetQueue = _pinnedAssetQueue.then(() => _togglePinnedAssetNow(assetId, tid))
    .catch((e) => console.warn('[Roster] pin toggle failed:', e?.message || e))
    .finally(() => _pinnedAssetBusy.delete(assetId));
  return _pinnedAssetQueue;
}
async function _togglePinnedAssetNow(assetId, tid) {
  const sameTenant = () => !!tid && window.AppState?.tenant === tid;
  if (!sameTenant()) return;
  if (!_canPinAssets()) { window.showToast?.('Your login can see pinned assets but not change them.', 'info'); return; }
  if (typeof window._settingsPersist !== 'function') { window.showToast?.('Settings are still loading — try again in a moment.', 'error'); return; }
  const before = pinnedAssetIdsFrom(window.AppState?.settings);
  const plan = planPinnedAssetToggle(before, RosterState.assets, assetId);
  if (plan.action === 'full') { window.showToast?.(`Max ${PINNED_ASSET_MAX} pinned — unstar one first.`, 'info'); return; }
  if (plan.action === 'ineligible') { window.showToast?.('Only items counted by quantity can be pinned.', 'info'); return; }
  let ok = false;
  if (plan.action === 'remove') {
    ok = await window._settingsPersist({ pinnedAssetIds: arrayRemove(assetId) });
  } else {
    ok = plan.stale.length ? await window._settingsPersist({ pinnedAssetIds: arrayRemove(...plan.stale) }) : true;
    if (ok && !sameTenant()) return; // switched company between the two writes
    if (ok) ok = await window._settingsPersist({ pinnedAssetIds: arrayUnion(assetId) });
  }
  if (!sameTenant()) return; // switched company during the write: nothing local to update
  // _settingsPersist Object.assign()s the patch into AppState.settings, which
  // leaves a FieldValue sentinel there — and the tenant snapshot (with our write
  // and anyone else's) has usually already landed by then. COD r2 (Code): so
  // read the doc back (the live listener keeps it cached) and use THAT list —
  // never one rebuilt from `before`, which would erase another session's star
  // locally, or bring back stale ids a half-done write already removed.
  let next = before.filter(id => !plan.stale.includes(id));
  if (plan.action === 'remove') next = next.filter(id => id !== assetId);
  else if (!next.includes(assetId)) next = next.concat(assetId);
  try {
    const snap = await getDoc(doc(db, 'tenants', tid));
    if (snap.exists()) next = pinnedAssetIdsFrom(snap.data()?.settings);
    else if (!ok) next = before;
  } catch (_) {
    if (!ok) next = before; // could not re-read: keep what we knew
  }
  if (!sameTenant()) return;
  if (window.AppState?.settings) window.AppState.settings.pinnedAssetIds = next;
  try { window.dispatchEvent(new CustomEvent('dd:pinned-assets-changed')); } catch (_) { /* repaint below still runs */ }
  if (!ok) return; // _settingsPersist already toasted the failure
  const asset = RosterState.assets.find(a => a && a.id === assetId);
  window.logAudit?.('settings', plan.action === 'remove' ? 'UNPIN_ASSET' : 'PIN_ASSET', {
    assetId, assetName: asset?.name || null, pinnedFrom: before, pinnedTo: next,
  });
  window.showToast?.(plan.action === 'remove' ? 'Unpinned from the dispatch board' : 'Pinned to the dispatch board', 'success');
}

/** The star on an asset card: a button for someone who may star, a quiet
 *  marker for anyone else when it is pinned, nothing otherwise. */
function _pinnedAssetStarHtml(asset, pinnedIds, canPin) {
  if (!_pinnedAssetEligible(asset)) return '';
  const on = pinnedIds.includes(asset.id);
  const base = 'position:absolute;right:8px;bottom:8px;z-index:3;width:40px;height:40px;border-radius:50%;display:flex;align-items:center;justify-content:center;background:rgba(255,255,255,0.94);box-shadow:0 1px 4px rgba(15,23,42,0.22);';
  const icon = `<span class="material-icons" style="font-size:24px;color:${on ? '#B45309' : '#475569'};">${on ? 'star' : 'star_border'}</span>`;
  if (!canPin) {
    return on ? `<span class="asset-pin-star is-on" style="${base}" title="Pinned to the dispatch board" aria-label="Pinned to the dispatch board">${icon}</span>` : '';
  }
  const jsId = window.escJsAttr ? window.escJsAttr(asset.id || '') : String(asset.id || '');
  const label = on ? 'Unpin from the dispatch board' : 'Pin to the dispatch board';
  return `<button type="button" class="asset-pin-star${on ? ' is-on' : ''}" data-asset-id="${escapeHtml(asset.id || '')}" style="${base}border:none;padding:0;cursor:pointer;" onclick="event.stopPropagation(); window.RosterModule.togglePinnedAsset('${jsId}')" title="${label}" aria-label="${label}" aria-pressed="${on ? 'true' : 'false'}">${icon}</button>`;
}

// A star here or in another session (app.js's tenant snapshot fires this too).
window.addEventListener('dd:pinned-assets-changed', () => {
  if (RosterState._assetsLoaded && document.getElementById('list-assets')) renderAssetsList();
});

// ============================================
// TASK-9369 — ASSETS "GROUP BY BRANCH" (Guy / Split Rock ask, 2026-09-24)
// ============================================
// A branch IS a yard (settings.yards). Rollups are computed on READ from the
// live assets snapshot — nothing is stored on the asset doc (TASK-9375
// constraint). Utilization is per branch and per SKU, never per person.
//
// Semantics (verified dispatch.js buildInventoryPreview / bi.js, do not
// re-derive): `quantity` = TOTAL owned, `deployedQty` = out on rental,
// available = quantity − deployedQty, so % out = deployedQty / quantity.
// Chris's spoken "deployed / (on hand + deployed)" would double-count.

// (ASSETS_GROUPBY_LS_KEY / ASSETS_FORMER_YARD_KEY are declared beside YARD_TYPES —
// RosterState's initializer calls _loadAssetsGroupBy() at module load, and a
// const declared down here would still be in its temporal dead zone then.)

function _loadAssetsGroupBy() {
  try { return localStorage.getItem(ASSETS_GROUPBY_LS_KEY) === 'branches' ? 'branches' : 'items'; } catch (_) { return 'items'; }
}

/** The mode that actually renders: Branches needs ≥1 yard, otherwise Items. */
function _assetsGroupByEffective() {
  return getYardsList().length > 0 && RosterState.filters.assets.groupBy === 'branches' ? 'branches' : 'items';
}

// TASK-9369 computeAssetBranchRollups: moved to functions/src/shared/assetRollup.js
// (TASK-9375 S2) — imported at the top.

/**
 * TASK-4800 follow-up — Roster asset order without a Firestore orderBy (which
 * drops docs missing the field). PURE. Same order the old partNumber orderBy
 * gave (code-unit string order) for docs WITH a part number; docs without one
 * follow, by name, then id — so they are always shown, never dropped.
 */
function _assetPartNo(a) {
  const p = a && a.partNumber;
  return (p === null || p === undefined) ? '' : String(p).trim();
}
function sortRosterAssets(list) {
  const cmp = (x, y) => (x < y ? -1 : x > y ? 1 : 0);
  return (list || []).slice().sort((a, b) => {
    const pa = _assetPartNo(a), pb = _assetPartNo(b);
    if (!pa !== !pb) return pa ? -1 : 1;
    if (pa) { const c = cmp(pa, pb); if (c) return c; }
    const n = String(a?.name || '').toLowerCase().localeCompare(String(b?.name || '').toLowerCase());
    return n || cmp(String(a?.id || ''), String(b?.id || ''));
  });
}

// findYardTransferTarget: moved to functions/src/shared/assetRollup.js
// (TASK-9375 S2) — imported at the top.

/**
 * One plain-text label for an asset in confirms, modal read-only fields and
 * toasts: "PN — Name", or "Name (no part #)" when it has no part number. PURE;
 * callers escape it for HTML. Replaces `${asset.partNumber} — ${asset.name}`,
 * which printed "undefined — Name" for the 53 docs with no part number.
 */
function assetDisplayLabel(asset) {
  const pn = _assetPartNo(asset);
  const name = String(asset?.name || '').trim();
  if (pn && name) return `${pn} — ${name}`;
  if (pn) return pn;
  return `${name || 'Unnamed asset'} (no part #)`;
}

/**
 * Whole-company per-SKU utilization. A yard transfer SPLITS one SKU into one
 * asset doc per yard keyed by exact-match `partNumber` (executeYardTransfer), so
 * "Helical Piers at A" and "at B" are two docs. PURE. Returns a Map:
 *   partNumber → { docs, yards, total, out, denominator, pctOut }
 * Only active quantity-mode docs with a partNumber take part.
 */
function computeAssetSkuTotals(assets) {
  const map = new Map();
  for (const a of (Array.isArray(assets) ? assets : [])) {
    if (!a || a.active === false || a.trackingMode !== 'quantity' || !a.partNumber) continue;
    let s = map.get(a.partNumber);
    if (!s) { s = { docs: 0, yards: new Set(), total: 0, out: 0, denominator: 0, pctOut: null, drift: false }; map.set(a.partNumber, s); }
    s.docs++;
    s.yards.add(a.yardId || '');
    const qty = Number(a.quantity) || 0;
    s.total += qty;
    if (a.quantityType === 'rental') {
      const rv = rentalStockView(a); // TASK-4810 — out capped per doc, excess flagged
      s.out += rv.outCounted; s.denominator += qty;
      if (rv.drift) s.drift = true;
    }
  }
  for (const s of map.values()) {
    s.pctOut = s.denominator > 0 ? Math.min(100, Math.round((s.out / s.denominator) * 100)) : null;
  }
  return map;
}

/** Muted meta line under a split SKU: "SKU total 81 · 3 branches · 12% out". '' when the SKU lives in one doc. */
function _assetSkuTotalLine(asset, skuTotals) {
  const s = asset && asset.partNumber && skuTotals ? skuTotals.get(asset.partNumber) : null;
  if (!s || s.docs < 2) return '';
  const n = s.yards.size;
  const parts = [`SKU total ${s.total}`, `${n} ${n === 1 ? 'branch' : 'branches'}`];
  if (s.pctOut !== null) parts.push(`${s.pctOut}% out`);
  if (s.drift) parts.push('needs recount'); // TASK-4810 — the % above counts each doc only up to what it owns
  return `<div class="asset-card-sku-total" style="margin-top:4px;font-size:0.72rem;color:var(--text-secondary);" title="Company-wide across every branch holding this part number">${escapeHtml(parts.join(' · '))}</div>`;
}

// ============================================================================
// TASK-4800 S3 — Assets › "On site": the stranded-equipment view.
//
// Sunbelt Phase 1's acceptance criterion is "zero stranded assets — deployed
// count matches physical reality on spot-check audit", and a spot check happens
// AT A SITE. Dispatch › Maps already draws one badge per site (TASK-4800 S2);
// this is the same derivation laid out for the person who owns the inventory:
// every site holding rental units, oldest first, with how long it has been
// there. ONE planner (window.DispatchModule.planEquipmentOnSite — Pitfall #1,
// never a cross-import), so the two surfaces can never disagree.
//
// Data, all read-only and all collections a Roster member already reads:
//   assetTransactions — one getDocs per switch-on, re-taken after 2 minutes
//   jobs              — one getDoc per job the plan asks for, cached per tenant,
//                       ceiling 200 a session / 80 a round, failures retried
//                       only after a pause (the map's own discipline)
//   projects          — a pin only for a job with no located stop; from the
//                       Projects module's memory, else one getDoc (ceiling 50)
// Nothing is written. Counter/ledger drift is SHOWN (count check), never fixed.
// ============================================================================
const ONSITE_TXN_TTL_MS = 2 * 60 * 1000;
const ONSITE_JOB_FETCH_CAP = 200;
const ONSITE_JOB_FETCH_BATCH = 80;
const ONSITE_PROJECT_FETCH_CAP = 50;
const ONSITE_MAX_ROUNDS = 3;
// Fallbacks only — the live values come from window.DispatchModule so the
// colours match the map's badges exactly.
const ONSITE_AGE_COLORS_FALLBACK = { fresh: '#047857', aging: '#B45309', stale: '#B91C1C', unknown: '#6B7280' };

// RosterState has no tenantId field (it is never assigned) — the tenant comes
// from AppState, mapped exactly as subscribeToData maps it.
function _onsiteTenant() {
  const raw = window.AppState?.tenant || null;
  return raw === 'demo-tenant' ? 'demo' : raw;
}

function _onsiteState() {
  const tid = _onsiteTenant();
  if (!RosterState._onsite || RosterState._onsite.tid !== tid) {
    // Per tenant: a tenant switch must never paint the previous tenant's sites.
    RosterState._onsite = { tid, on: false, age: 'all', state: 'idle', txns: null, assets: null, txnsAt: 0, jobs: {}, projects: {}, loading: false, gen: 0 };
  }
  return RosterState._onsite;
}

function _assetIsRental(a) {
  return !!a && (a.quantityType === 'rental' || a.isRental === true);
}

/** Show the On Site toggle once there is a rental to locate; keep it in step with state. */
function _syncAssetsOnSiteUi() {
  const st = _onsiteState();
  const btn = document.getElementById('toggle-assets-onsite');
  // st.assets = On Site's own whole-collection read, once it has loaded.
  const has = RosterState.assets.some(_assetIsRental) || (st.assets || []).some(_assetIsRental);
  if (!has && st.on) st.on = false;
  if (!btn) return;
  btn.style.display = has ? '' : 'none';
  btn.classList.toggle('active', st.on);
  btn.setAttribute('aria-pressed', st.on ? 'true' : 'false');
  // One view at a time: while On Site is showing, Items/Branches read as off.
  // (_syncAssetsGroupByUi restores them when On Site is turned off.)
  if (st.on) {
    ['assets-groupby-items', 'assets-groupby-branches'].forEach(id => {
      const b = document.getElementById(id);
      if (b) { b.classList.remove('active'); b.setAttribute('aria-pressed', 'false'); }
    });
  }
}

function toggleAssetsOnSite() {
  RosterState.filters.assets.belowZero = false; // TASK-9985
  const st = _onsiteState();
  st.on = !st.on;
  if (st.on) {
    // Fresh numbers every time it is opened — a deploy made a minute ago must show.
    // The last read stays on screen while the fresh one loads.
    st.state = st.txns ? 'stale' : 'idle';
    RosterState._assetsBranchDrill = false;
  }
  _syncAssetsGroupByUi();
  _syncAssetsOnSiteUi();
  renderAssetsList();
}

function retryAssetsOnSite() {
  const st = _onsiteState();
  if (st.loading) return;
  st.state = 'idle';
  renderAssetsList();
}

function setAssetsOnSiteAge(v) {
  const st = _onsiteState();
  st.age = (v === '7' || v === '14') ? v : 'all';
  renderAssetsList();
}

function openAssetsOnSiteMap() {
  if (typeof window.DispatchModule?.openEquipmentMap !== 'function') {
    window.showToast?.('The map is still loading — try again in a moment.', 'info');
    return;
  }
  window.DispatchModule.openEquipmentMap();
}

/**
 * Run the shared planner over what has been read so far. null = planner not
 * available. `caches` lets the loader plan against the caches it is filling
 * while the screen keeps showing the last complete read (no flicker to "not
 * loaded" on a refresh).
 */
function _assetsOnSitePlan(caches) {
  const st = _onsiteState();
  const plan = window.DispatchModule?.planEquipmentOnSite;
  if (typeof plan !== 'function') return null;
  const c = caches || st;
  const jobsById = new Map();
  const missing = new Set();
  Object.entries(c.jobs).forEach(([id, j]) => {
    if (j === null) missing.add(id);
    else if (j && !j.__failedAt) jobsById.set(id, j);
  });
  const projectsById = new Map();
  (window.ProjectsModule?.getState?.()?.projects || []).forEach(p => { if (p && p.id) projectsById.set(p.id, p); });
  Object.entries(c.projects).forEach(([id, p]) => { if (p) projectsById.set(id, p); });
  return plan({ assets: c.assets || RosterState.assets, transactions: c.txns, jobsById, projectsById, missingJobIds: missing });
}

/**
 * Read the ledger, then the jobs (and project pins) the plan asks for, a round
 * at a time. Re-renders when something landed. Safe to call on every render:
 * it no-ops while a load is in flight or the data is fresh.
 */
async function _loadAssetsOnSite() {
  const st = _onsiteState();
  const tid = st.tid;
  if (!tid || st.loading) return;
  // COD (Code + Domain, convergent): a failed read repaints, and the repaint
  // calls back in here — without this the failure looped reads forever on a
  // denied rule or an offline tablet. Failed stays failed until Try again or
  // the toggle is switched on again (both reset the state).
  if (st.state === 'failed') return;
  const fresh = st.state === 'loaded' && st.txns && (Date.now() - st.txnsAt) < ONSITE_TXN_TTL_MS;
  if (fresh) return;
  st.loading = true;
  const gen = ++st.gen;
  const live = () => RosterState._onsite === st && st.gen === gen && _onsiteTenant() === tid;
  const repaint = () => { if (live() && st.on && document.getElementById('list-assets')) renderAssetsList(); };
  // Every read goes into FRESH caches (COD Code lane: a job's rentalLedger
  // cached from the last open would outlive a return). The screen keeps the
  // last complete read until this one is whole, then the caches swap — so a
  // refresh never flickers lots to "not loaded", and a first open shows the
  // spinner until the locations are known rather than half an answer.
  const work = { txns: null, assets: null, jobs: {}, projects: {} };
  let ok = false;
  try {
    try {
      // On Site reads the WHOLE asset collection itself — the same set the
      // Dispatch map plans over. (The Roster list used to subscribe with
      // orderBy('partNumber'), which dropped the 53 docs with no part number;
      // that is fixed, but this read stays self-contained and fresh per open.)
      const [snap, aSnap] = await Promise.all([
        getDocs(collection(db, 'tenants', tid, 'assetTransactions')),
        getDocs(collection(db, 'tenants', tid, 'assets')),
      ]);
      if (!live()) return;
      work.txns = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      work.assets = aSnap.docs.map(d => ({ id: d.id, ...d.data() }));
    } catch (e) {
      if (!live()) return;
      console.warn('[Roster on-site] assetTransactions read failed — rental locations not shown', e?.code || e?.message || e);
      // Remembered as FAILED, never as an empty ledger: "nothing out" would be a lie.
      st.txns = null;
      st.state = 'failed';
      return;
    }
    const has = (id) => Object.prototype.hasOwnProperty.call(work.jobs, id);
    for (let round = 0; round < ONSITE_MAX_ROUNDS; round++) {
      const plan = _assetsOnSitePlan(work);
      if (!plan) break;
      const room = Math.max(0, ONSITE_JOB_FETCH_CAP - Object.keys(work.jobs).length);
      const want = plan.jobsNeeded.filter(id => !has(id)).slice(0, Math.min(room, ONSITE_JOB_FETCH_BATCH));
      if (!want.length) break;
      let landed = 0;
      await Promise.all(want.map(async id => {
        try {
          const s = await getDoc(doc(db, 'tenants', tid, 'jobs', id));
          if (!live()) return;
          work.jobs[id] = s.exists() ? { id: s.id, ...s.data() } : null;   // null = the job is gone
          landed++;
        } catch (e) {
          if (!live()) return;
          // Not cached as gone: the lot stays "not loaded" (said on screen) and
          // the next open retries it.
          console.warn('[Roster on-site] job read failed — will retry on the next open', id, e?.code || e?.message || e);
          work.jobs[id] = { id, __failedAt: Date.now() };
        }
      }));
      if (!live()) return;
      if (!landed) break;
    }
    // Project pins — only for a job that has no located stop of its own.
    const plan = _assetsOnSitePlan(work);
    if (plan) {
      const known = new Set((window.ProjectsModule?.getState?.()?.projects || []).map(p => p && p.id));
      const pids = [...new Set(plan.unplotted.filter(d => d.reason === 'no-location' && d.projectId).map(d => d.projectId))]
        .filter(pid => !known.has(pid))
        .slice(0, ONSITE_PROJECT_FETCH_CAP);
      await Promise.all(pids.map(async pid => {
        try {
          const s = await getDoc(doc(db, 'tenants', tid, 'projects', pid));
          if (live()) work.projects[pid] = s.exists() ? { id: s.id, ...s.data() } : null;
        } catch (_) {
          if (live()) work.projects[pid] = null;   // no pin, so the lot stays "no location" — honest
        }
      }));
      if (!live()) return;
    }
    st.txns = work.txns;
    st.assets = work.assets;
    st.jobs = work.jobs;
    st.projects = work.projects;
    st.txnsAt = Date.now();
    st.state = 'loaded';
    ok = true;
  } finally {
    if (RosterState._onsite === st && st.gen === gen) st.loading = false;
    if (ok || st.state === 'failed') repaint();
  }
}

/**
 * PURE (vm-tested). Plan deployments → one card per job site, filtered and
 * sorted for the On site view.
 *   plan      planEquipmentOnSite() output
 *   opts      { assetIds: Set|null (search/category/yard), age: 'all'|'7'|'14',
 *               freshDays, staleDays }
 * Returns { cards, counts: { all, over7, over14 }, units, sites }.
 * A card's age is its OLDEST lot. Unknown-age cards appear under All only —
 * "stranded" is a claim, and a lot with no date cannot support it. Unplotted
 * lots (no location / deleted job / job not loaded) are cards too, flagged,
 * so the list is the complete truth even where the map has nothing to pin.
 */
function buildAssetsOnSiteCards(plan, opts) {
  const o = opts || {};
  const freshDays = Number.isFinite(o.freshDays) ? o.freshDays : 7;
  const staleDays = Number.isFinite(o.staleDays) ? o.staleDays : 14;
  const ids = (o.assetIds && typeof o.assetIds.has === 'function') ? o.assetIds : null;
  const bucket = (d) => (d == null ? 'unknown' : d < freshDays ? 'fresh' : d <= staleDays ? 'aging' : 'stale');
  const byJob = new Map();
  for (const d of ((plan && plan.deployments) || [])) {
    if (!d || d.kind !== 'rental') continue;
    if (ids && !ids.has(d.assetId)) continue;
    const key = d.jobId || '';
    let c = byJob.get(key);
    if (!c) {
      c = { jobId: d.jobId, title: d.title, jobNumber: d.jobNumber, customer: d.customer || '', locName: d.locName || '',
        locSource: d.locSource || null, reason: d.reason || null, items: [], units: 0, pending: 0, oldestMs: null };
      byJob.set(key, c);
    }
    c.items.push(d);
    c.units += Number(d.qty) || 0;
    c.pending += Number(d.pending) || 0;
    if (d.sinceKnown && d.ageDays != null && (c.oldestAge == null || d.ageDays > c.oldestAge)) { c.oldestAge = d.ageDays; c.oldestMs = d.sinceMs; }
  }
  const all = [...byJob.values()].map(c => {
    c.items.sort((a, b) => (b.ageDays ?? -1) - (a.ageDays ?? -1) || String(a.label).localeCompare(String(b.label)));
    const ageDays = c.oldestAge == null ? null : c.oldestAge;
    return { ...c, ageDays, ageBucket: bucket(ageDays) };
  });
  const counts = {
    all: all.length,
    over7: all.filter(c => c.ageDays != null && c.ageDays >= freshDays).length,
    over14: all.filter(c => c.ageDays != null && c.ageDays > staleDays).length,
  };
  const min = o.age === '14' ? staleDays + 1 : o.age === '7' ? freshDays : null;
  const cards = all
    .filter(c => min == null || (c.ageDays != null && c.ageDays >= min))
    .sort((a, b) => (b.ageDays ?? -1) - (a.ageDays ?? -1)
      || (a.reason ? 1 : 0) - (b.reason ? 1 : 0)
      || b.units - a.units
      || String(a.title).localeCompare(String(b.title)));
  return {
    cards, counts,
    units: cards.reduce((n, c) => n + c.units, 0),
    sites: cards.filter(c => !c.reason).length,
  };
}

function _onsiteReasonText(reason) {
  switch (reason) {
    case 'deleted-job': return 'The job these were sent out on no longer exists — still counted as out.';
    case 'job-not-loaded': return 'Older job not loaded yet — still counted as out.';
    case 'no-location': return 'This job has no destination with a location, so it is not on the map.';
    default: return '';
  }
}

function _onsiteSinceText(d) {
  if (!d.sinceKnown || d.ageDays == null) return 'date unknown';
  const when = d.sinceISO ? new Date(d.sinceISO + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '';
  const days = d.ageDays === 0 ? 'today' : `${d.ageDays} day${d.ageDays !== 1 ? 's' : ''}`;
  return when ? `since ${when} · ${days}` : days;
}

/** The On site list. `assetIds` = the search/category/yard filters, applied per asset. */
function _renderAssetsOnSite(assetIds) {
  const st = _onsiteState();
  const fmt = (n) => Number(n || 0).toLocaleString('en-US');
  const DM = window.DispatchModule || {};
  const colors = DM.equipmentAgeColors || ONSITE_AGE_COLORS_FALLBACK;
  const days = DM.equipmentAgeDays || { fresh: 7, stale: 14 };
  const full = 'grid-column:1 / -1;';
  if (st.state === 'failed') {
    return `<div class="onsite-state" role="alert" style="${full}padding:12px 14px;border:1px solid #FECACA;border-left:4px solid #DC2626;border-radius:8px;background:#FEF2F2;color:#991B1B;font-size:0.875rem;">
      <strong>Couldn't read the rental records for this login.</strong> Where your equipment is can't be shown right now — this is "unknown", not "nothing out". Try again in a minute, or ask an admin.
      <div style="margin-top:8px;"><button type="button" class="btn btn-secondary" style="min-height:44px;" onclick="window.RosterModule.retryAssetsOnSite()">Try again</button></div>
    </div>`;
  }
  const plan = _assetsOnSitePlan();
  if (!plan) {
    return `<div class="onsite-state" style="${full}padding:12px 14px;color:var(--text-secondary);font-size:0.875rem;">Still loading — try again in a moment.</div>`;
  }
  if (!st.txns) {
    return `<div class="onsite-state" style="${full}text-align:center;padding:var(--spacing-xl);">
      <div class="btn-spinner" style="width:32px;height:32px;margin:0 auto var(--spacing-md);"></div>
      <div style="color:var(--text-secondary);font-size:0.875rem;">Finding where your rental equipment is…</div>
    </div>`;
  }
  const view = buildAssetsOnSiteCards(plan, { assetIds, age: st.age, freshDays: days.fresh, staleDays: days.stale });
  const canMap = window.checkModuleAccess?.('dispatch', true) === true;
  const chip = (v, label, n) => {
    const on = st.age === v;
    return `<button type="button" class="btn-toggle-inactive${on ? ' active' : ''}" aria-pressed="${on ? 'true' : 'false'}" style="min-height:40px;" data-onsite-age="${v}" onclick="window.RosterModule.setAssetsOnSiteAge('${v}')"><span class="toggle-label">${label} (${fmt(n)})</span></button>`;
  };
  const header = `
    <div class="onsite-header" style="${full}display:flex;flex-direction:column;gap:8px;padding:12px 14px;border:1px solid var(--border-light);border-radius:var(--radius-lg);background:var(--bg-card);box-shadow:0 1px 2px rgba(0,0,0,0.06), 0 1px 3px rgba(0,0,0,0.1);">
      <div style="display:flex;align-items:center;justify-content:space-between;gap:10px;flex-wrap:wrap;">
        <div style="min-width:0;">
          <div class="onsite-summary" style="font-size:1rem;font-weight:700;color:var(--text-primary);">${fmt(view.units)} unit${view.units !== 1 ? 's' : ''} out at ${fmt(view.cards.length)} job${view.cards.length !== 1 ? 's' : ''}${view.counts.over14 ? ` · <span style="color:${colors.stale};">${fmt(view.counts.over14)} over ${days.stale} days</span>` : ''}</div>
          <div style="font-size:0.8rem;color:var(--text-secondary);margin-top:2px;">Oldest first. From the schedule and the rental records, not GPS.</div>
        </div>
        ${canMap ? `<button type="button" class="btn btn-secondary" id="btn-onsite-map" style="min-height:44px;" onclick="window.RosterModule.openAssetsOnSiteMap()"><span class="material-icons">map</span> View on map</button>` : ''}
      </div>
      <div role="group" aria-label="How long on site" style="display:flex;flex-wrap:wrap;gap:6px;">
        ${chip('all', 'All', view.counts.all)}${chip('7', `${days.fresh}+ days`, view.counts.over7)}${chip('14', `Over ${days.stale} days`, view.counts.over14)}
      </div>
    </div>`;
  const badge = (c) => {
    const bg = colors[c.ageBucket] || colors.unknown;
    const d = c.ageDays == null ? '?' : `${c.ageDays}d`;
    return `<span class="onsite-badge" style="display:inline-block;flex-shrink:0;background:${bg};color:#fff;font-weight:700;font-size:0.85rem;border-radius:var(--radius-sm);padding:4px 8px;font-family:'Courier New', Courier, monospace;" title="Units | days since the oldest lot here was sent out — from the rental records, not GPS">${fmt(c.units)} | ${d}</span>`;
  };
  const card = (c) => `
    <div class="onsite-card" data-onsite-job="${escapeHtml(c.jobId || '')}" style="display:flex;flex-direction:column;gap:6px;padding:12px 14px;border:1px solid var(--border-light);border-left:4px ${c.reason ? 'dashed var(--border-dark)' : `solid ${colors[c.ageBucket] || colors.unknown}`};border-radius:var(--radius-lg);background:var(--bg-card);box-shadow:0 1px 2px rgba(0,0,0,0.06), 0 1px 3px rgba(0,0,0,0.1);min-width:0;">
      <div style="display:flex;align-items:flex-start;justify-content:space-between;gap:10px;">
        <div style="min-width:0;">
          <div style="font-weight:600;color:var(--text-primary);font-size:0.95rem;overflow-wrap:anywhere;">${c.jobNumber != null ? `#${escapeHtml(String(c.jobNumber))} ` : ''}${escapeHtml(c.title || 'Untitled Job')}</div>
          ${c.customer ? `<div style="font-size:0.8rem;color:var(--text-secondary);overflow-wrap:anywhere;">${escapeHtml(c.customer)}</div>` : ''}
        </div>
        ${badge(c)}
      </div>
      ${c.reason
        ? `<div style="font-size:0.8rem;color:#92400E;font-weight:600;">${escapeHtml(_onsiteReasonText(c.reason))}</div>`
        : (c.locName ? `<div style="font-size:0.8rem;color:var(--text-secondary);overflow-wrap:anywhere;"><span class="material-icons" style="font-size:15px;vertical-align:-3px;">place</span> ${escapeHtml(c.locName)}${c.locSource === 'project' ? ' (project location)' : ''}</div>` : '')}
      <div style="display:flex;flex-direction:column;gap:3px;border-top:1px solid var(--border-light);padding-top:6px;">
        ${c.items.map(d => `<div style="display:flex;justify-content:space-between;gap:8px;font-size:0.82rem;color:var(--text-primary);">
          <span style="min-width:0;overflow-wrap:anywhere;"><strong>${escapeHtml(d.label)}</strong> × ${fmt(d.qty)}${d.pending ? ` <span style="color:#92400E;font-weight:600;">(${fmt(d.pending)} awaiting return inspection)</span>` : ''}</span>
          <span style="flex-shrink:0;color:var(--text-secondary);">${escapeHtml(_onsiteSinceText(d))}</span>
        </div>`).join('')}
      </div>
    </div>`;
  const ids = (assetIds && typeof assetIds.has === 'function') ? assetIds : null;
  const recon = (plan.recon || []).filter(r => !ids || ids.has(r.assetId));
  const reconHtml = !recon.length ? '' : `
    <div class="onsite-recon" style="${full}margin-top:4px;padding:12px 14px;border:1px solid var(--border-light);border-radius:var(--radius-lg);background:var(--bg-card);">
      <div style="font-size:0.9rem;font-weight:700;color:var(--text-primary);">Count check — does the out count match the sites?</div>
      <div style="font-size:0.8rem;color:var(--text-secondary);margin:4px 0 8px;">"Out" is the count DispatchDeck bills and plans with. "At sites" is what the records put at a job above. "Off by" is the gap — usually a return or a cancelled deploy that never wrote a record. Check it before a spot count.</div>
      ${recon.map(r => `<div class="onsite-recon-row" style="display:flex;flex-wrap:wrap;gap:4px 14px;padding:6px 0;border-top:1px solid var(--border-light);font-size:0.82rem;color:var(--text-primary);">
        <span style="font-weight:600;flex-basis:100%;overflow-wrap:anywhere;">${escapeHtml(r.label)}${r.overStock ? ` <span style="color:#991B1B;font-weight:700;">— more out than owned (${fmt(r.deployedQty)} of ${fmt(r.quantity)})</span>` : ''}</span>
        <span>Out: <strong>${fmt(r.deployedQty)}</strong></span><span>At sites: <strong>${fmt(r.placed + r.unplaced)}</strong></span>
        <span style="${r.delta !== 0 ? 'color:#991B1B;font-weight:700;' : ''}">Off by: ${r.delta > 0 ? '+' : ''}${fmt(r.delta)}</span>
      </div>`).join('')}
    </div>`;
  const notes = [];
  if (plan.unclassifiedRows) notes.push(`${fmt(plan.unclassifiedRows)} rental record${plan.unclassifiedRows !== 1 ? 's are' : ' is'} neither a send-out nor a return, so ${plan.unclassifiedRows !== 1 ? 'they were' : 'it was'} counted but not placed.`);
  if (plan.deployments.some(d => d.basis === 'transactions')) notes.push('For jobs from before per-job rental tracking, a return is matched to that item’s open lots — the return’s own job first, then the same customer, then the oldest. That matching is an assumption; the totals per item are not.');
  const notesHtml = notes.length ? `<div class="onsite-notes" style="${full}font-size:0.78rem;color:var(--text-secondary);line-height:1.4;">${notes.map(escapeHtml).join(' ')}</div>` : '';
  // COD Domain lane — a partial read must never pass for a whole one. A job
  // that could not be read (ceiling hit, or the read failed) leaves its lots
  // unlocated AND makes return matching / ledger detection less exact, so a
  // clean-looking "Off by 0" would be overclaiming. Say so, above the list.
  // COD Code lane: count every job the plan still NEEDS, not just the ones
  // holding a lot — an unread "Mat Removal" job changes which lot its return
  // releases while every surviving lot can still look cleanly placed.
  const notLoadedJobs = new Set([...(plan.jobsNeeded || []), ...plan.deployments.filter(d => d.reason === 'job-not-loaded').map(d => d.jobId)]).size;
  const partialHtml = !notLoadedJobs ? '' : `<div class="onsite-partial" role="status" style="${full}padding:10px 12px;border:1px solid #FDE68A;border-left:4px solid #D97706;border-radius:8px;background:#FFFBEB;color:#92400E;font-size:0.82rem;">
      <strong>${fmt(notLoadedJobs)} job${notLoadedJobs !== 1 ? 's' : ''} couldn't be loaded</strong>, so ${notLoadedJobs !== 1 ? 'their' : 'its'} equipment is listed without a location and the count check below may be incomplete. Turn On Site off and on to try again.
    </div>`;
  const empty = view.cards.length ? '' : `<div class="onsite-empty" style="${full}padding:16px;text-align:center;color:var(--text-secondary);font-size:0.875rem;">${view.counts.all ? 'Nothing has been out that long.' : 'No rental equipment is out on a job right now.'}</div>`;
  return header + partialHtml + view.cards.map(card).join('') + empty + reconHtml + notesHtml;
}

/** Sync the Items|Branches control + the Add Branch button to state. Safe off-view. */
function _syncAssetsGroupByUi() {
  const hasYards = getYardsList().length > 0;
  const mode = _assetsGroupByEffective();
  const group = document.getElementById('assets-groupby');
  if (group) group.style.display = hasYards ? 'inline-flex' : 'none';
  const itemsBtn = document.getElementById('assets-groupby-items');
  const branchesBtn = document.getElementById('assets-groupby-branches');
  if (itemsBtn) { itemsBtn.classList.toggle('active', mode === 'items'); itemsBtn.setAttribute('aria-pressed', mode === 'items' ? 'true' : 'false'); }
  if (branchesBtn) { branchesBtn.classList.toggle('active', mode === 'branches'); branchesBtn.setAttribute('aria-pressed', mode === 'branches' ? 'true' : 'false'); }
  const addBranch = document.getElementById('btn-add-branch');
  if (addBranch) addBranch.style.display = (!hasYards || mode === 'branches') ? '' : 'none';
}

/** Items | Branches click. Persists the preference per device. */
function setAssetsGroupBy(mode) {
  RosterState.filters.assets.belowZero = false; // TASK-9985
  const next = mode === 'branches' ? 'branches' : 'items';
  RosterState.filters.assets.groupBy = next;
  try { localStorage.setItem(ASSETS_GROUPBY_LS_KEY, next); } catch (_) { /* private mode */ }
  if (next === 'branches') RosterState._assetsBranchDrill = false;
  _onsiteState().on = false;   // TASK-4800 S3 — picking Items/Branches leaves On site
  _syncAssetsGroupByUi();
  renderAssetsList();
}

/**
 * Branch card click → Items mode filtered to that yard, with the breadcrumb.
 * key: yard slug | '' (Unassigned) | ASSETS_FORMER_YARD_KEY. Reuses the existing
 * yard filter, so search / +− / edit behave exactly as in a hand-picked filter.
 * The <select> is kept in step with state on purpose: filterAssets() reads the
 * DOM on the next keystroke, and a missing/unequal option would wipe the drill.
 */
function drillAssetsBranch(key) {
  const yardKey = key === ASSETS_FORMER_YARD_KEY ? ASSETS_FORMER_YARD_KEY : String(key || '');
  const select = document.getElementById('filter-assets-yard');
  if (select) {
    if (yardKey === ASSETS_FORMER_YARD_KEY && !Array.from(select.options).some(o => o.value === yardKey)) {
      // TASK-9548 slice 2 — under its own header, after "Facilities", so it
      // reads as a bucket of things that LEFT the facility list, not as one.
      const grp = document.createElement('optgroup');
      grp.label = 'No longer in settings';
      const opt = document.createElement('option');
      opt.value = yardKey;
      opt.textContent = 'Former facility';
      grp.appendChild(opt);
      select.appendChild(grp);
    }
    select.value = yardKey;
  }
  RosterState.filters.assets.yard = yardKey;
  RosterState.filters.assets.belowZero = false; // TASK-9985
  // Navigation, not a preference: the persisted groupBy stays 'branches' so the
  // next visit reopens on the rollup; only this render is Items.
  RosterState.filters.assets.groupBy = 'items';
  RosterState._assetsBranchDrill = true;
  _onsiteState().on = false;   // TASK-4800 S3
  _syncAssetsGroupByUi();
  renderAssetsList();
}

/** "Add Branch" (Assets header) → the existing facility modal, type preselected to Branch. */
function addBranchFromAssets() {
  addYardInline('', null, 'branch');
}

/** Human label for the Items-mode breadcrumb / a branch bucket key. */
function _assetsYardKeyLabel(key) {
  if (key === '') return 'Unassigned';
  if (key === ASSETS_FORMER_YARD_KEY) return 'Former facility';
  return getLiveYardLabel(key) || getYardLabel(key);
}

/**
 * TASK-9509 — is asset `a` inside the Items-mode yard view `key`? The SAME three
 * cases the drill filter uses, so a banner's "in this branch" count can never
 * disagree with the cards under it: '' = Unassigned (no yardId),
 * ASSETS_FORMER_YARD_KEY = tagged with a slug that left settings.yards, else
 * an exact slug match. `knownSlugs` = Set of current settings.yards slugs.
 */
function _assetInYardView(a, key, knownSlugs) {
  if (!a) return false;
  if (key === ASSETS_FORMER_YARD_KEY) return !!a.yardId && !(knownSlugs && knownSlugs.has(a.yardId));
  if (key === '') return !a.yardId;
  return a.yardId === key;
}

/**
 * TASK-9509 — the below-zero / needs-recount banners are company-wide ON
 * PURPOSE (a search or a drill must never hide the thing to go fix). When
 * drilled into one branch they say how many of those items are in it, so a
 * clean branch does not read as broken. `scope` = null (no drill) or
 * { key, label, known }. Returns '' when there is no scope.
 */
function _assetBannerScopeLine(list, scope) {
  if (!scope || !Array.isArray(list) || list.length === 0) return '';
  const here = list.filter(a => _assetInYardView(a, scope.key, scope.known)).length;
  const elsewhere = list.length - here;
  return `<div class="asset-banner-scope" style="margin-top:4px;font-weight:600;">${here} ${here === 1 ? 'item' : 'items'} in ${escapeHtml(scope.label || 'this branch')} &middot; ${elsewhere} elsewhere</div>`;
}

/** TASK-9509 — job titles for deployed individual items. Map jobId → title
 *  ('' = unreadable/untitled, null = fetch in flight). Lives on RosterState so a
 *  re-render never refetches. OWNED BY ONE TENANT (COD): a tenant switch drops
 *  the map, so a title can never render under another tenant's asset. */
function _assetJobTitleCache() {
  const tenant = window.AppState?.tenant || '';
  if (!(RosterState._assetJobTitles instanceof Map) || RosterState._assetJobTitlesTenant !== tenant) {
    RosterState._assetJobTitles = new Map();
    RosterState._assetJobTitlesTenant = tenant;
  }
  return RosterState._assetJobTitles;
}

/**
 * TASK-9509 — status line for an INDIVIDUALLY tracked item (light tower, shower
 * trailer). It used to show only its serial, so a unit out on a job looked like
 * it was in the yard. Deployed → "Out — {job}" (just "Out on a job" until the
 * title resolves, or if this user can't read jobs). A rental in the yard →
 * "Available" ONLY on an explicit 'available' status (COD Domain: an
 * out-of-service / missing status must never read as bookable). Anything else
 * → nothing (today's card).
 */
function _assetIndividualStatusLine(asset) {
  if (!asset || asset.trackingMode === 'quantity') return '';
  const status = String(asset.deploymentStatus || '').toLowerCase();
  if (status !== 'deployed') {
    return (asset.isRental === true && status === 'available')
      ? '<div class="asset-card-out-line" style="margin-top:4px;font-size:0.72rem;color:var(--text-secondary);">Available</div>'
      : '';
  }
  const title = asset.deployedToJobId ? _assetJobTitleCache().get(asset.deployedToJobId) : '';
  const text = title ? `Out &mdash; ${escapeHtml(title)}` : 'Out on a job';
  // One line, ellipsized — a long job name must not grow the card (COD UX).
  return `<div class="asset-card-out-line" style="margin-top:4px;font-size:0.72rem;font-weight:600;color:var(--text-primary);white-space:nowrap;overflow:hidden;text-overflow:ellipsis;"${title ? ` title="${escapeHtml(title)}"` : ''}>${text}</div>`;
}

/** TASK-9509 — fetch the titles of jobs holding deployed individual items, once
 *  per job id per session, then re-render. Permission/network errors leave the
 *  card at "Out on a job" — never throw out of a render. */
async function _resolveAssetJobTitles(jobIds) {
  const cache = _assetJobTitleCache();
  const todo = Array.from(new Set((jobIds || []).filter(id => id && !cache.has(id))));
  if (todo.length === 0) return;
  const tenant = window.AppState?.tenant;
  if (!tenant) return;
  todo.forEach(id => cache.set(id, null));
  let changed = false;
  await Promise.all(todo.map(async (id) => {
    try {
      const snap = await getDoc(doc(db, 'tenants', tenant, 'jobs', id));
      const j = snap.exists() ? (snap.data() || {}) : {};
      const t = String(j.title || j.name || j.jobNumber || '').trim();
      cache.set(id, t);
      if (t) changed = true;
    } catch (e) {
      cache.set(id, '');
    }
  }));
  // Tenant switched mid-flight → this map was dropped; its results are stale.
  if (_assetJobTitleCache() !== cache) return;
  if (changed) renderAssetsList();
}

function _renderAssetBranchCards(rollups) {
  return rollups.map(b => {
    const jsKey = window.escJsAttr ? window.escJsAttr(b.key) : String(b.key || '');
    const typeSlug = b.yard ? (b.yard.type || 'yard') : (b.unassigned ? '' : 'former');
    const typeLabel = b.unassigned ? 'No branch' : (b.former ? 'Removed from settings' : (getYardTypeLabel(typeSlug) || 'Yard'));
    const icon = b.unassigned ? 'location_off' : (b.former ? 'history' : getYardTypeIcon(typeSlug));
    const pct = b.pctOut === null ? '—' : `${b.pctOut}%`;
    const driftChip = b.drift
      ? `<span class="asset-card-flag" style="position:static;display:block;width:max-content;max-width:100%;box-sizing:border-box;margin:4px 0 0;white-space:normal;overflow-wrap:anywhere;line-height:1.2;font-size:0.58rem;background:#FEF3C7;color:#92400E;border:1px solid #FCD34D;" title="${escapeHtml(`${Number(b.driftUnits || 0).toLocaleString('en-US')} more out than owned on at least one item — a return was not logged, or a quantity was edited after deploying. Out and Available count each item only up to what it owns until it is recounted.`)}">needs recount</span>`
      : '';
    const hue = b.unassigned ? 210 : (b.former ? 0 : assetCategoryHue(`yard:${b.key}`));
    const address = b.yard && b.yard.address ? `<span class="asset-card-yard"><span class="material-icons">place</span>${escapeHtml(b.yard.address)}</span>` : '';
    // TASK-9509 — a label never bleeds into the next column (it did: "AVAILABLE% OUT").
    // Stats are a FIXED 2×2 grid: auto-fit(minmax(72px)) was measured on live
    // cards (268px desktop, 340px phone) laying out 3+1 with "% OUT" orphaned.
    const stat = (label, value, extra) => `
          <div class="asset-stat-copy" style="min-width:0;">
            <div class="asset-stat-label" style="white-space:nowrap;overflow:hidden;text-overflow:ellipsis;">${label}</div>
            <div class="asset-stat-value" style="font-size:1.05rem;">${value}${extra || ''}</div>
          </div>`;
    return `
    <div class="asset-card asset-branch-card ${b.negative ? 'low-stock' : ''}" data-branch-key="${escapeHtml(b.key)}" style="--asset-hue:${hue};" onclick="window.RosterModule.drillAssetsBranch('${jsKey}')" title="Show this branch's items">
      <div class="asset-card-visual">
        <span class="material-icons asset-card-glyph">${icon}</span>
        <span class="asset-card-cat">${escapeHtml(typeLabel)}</span>
      </div>
      <div class="asset-card-main">
        <div class="asset-card-name">${escapeHtml(b.label)}</div>
        <div class="asset-card-meta">
          <span class="asset-card-sku">${b.items} item${b.items === 1 ? '' : 's'}</span>
          ${address}
        </div>
      </div>
      <div class="asset-card-stat ${b.negative ? 'is-low' : ''}" style="display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:6px 12px;align-items:start;">
        ${stat('Total', b.total)}
        ${stat('Out', b.out)}
        ${stat('Available', b.available)}
        ${stat('% out', pct, driftChip)}
      </div>
    </div>`;
  }).join('');
}

function renderAssetsList() {
  const container = document.getElementById('list-assets');
  if (!container) return;
  if (typeof _syncReceiveStockButton === 'function') _syncReceiveStockButton(); // TASK-9723 (guarded: suites lift this function out alone)
  const assetModal = document.getElementById('modal-asset');
  const receiptModal = document.getElementById('modal-inventory-receipt');
  const adjustModal = document.getElementById('modal-inventory-adjustment');
  const transferModal = document.getElementById('modal-yard-transfer');
  if ((assetModal && !assetModal.classList.contains('hidden')) ||
      (receiptModal && !receiptModal.classList.contains('hidden')) ||
      (adjustModal && !adjustModal.classList.contains('hidden')) ||
      (transferModal && !transferModal.classList.contains('hidden'))) return;

  const { search, category, showInactive } = RosterState.filters.assets;
  let filtered = RosterState.assets;
  // TASK-9985 — opened from the Roster badge: show exactly the items the badge
  // counted (negativeStockAssets), nothing else. Dropped once nothing is below
  // zero, so a stale filter never shows an empty list.
  if (RosterState.filters.assets.belowZero && RosterState._assetsLoaded && negativeStockAssets(RosterState.assets).length === 0) {
    RosterState.filters.assets.belowZero = false;
  }
  const belowZero = !!RosterState.filters.assets.belowZero;

  // Filter inactive unless showInactive is true
  if (!showInactive) {
    filtered = filtered.filter(a => a.active !== false);
  }

  if (search) {
    const searchLower = search.trim().toLowerCase();
    filtered = filtered.filter(a =>
      a.partNumber?.toLowerCase().includes(searchLower) ||
      a.name?.toLowerCase().includes(searchLower)
    );
  }
  if (category !== 'all') {
    filtered = filtered.filter(a => a.category === category);
  }
  const assetYardFilter = RosterState.filters.assets.yard;
  if (assetYardFilter !== 'all') {
    // TASK-9369 — '' = Unassigned, ASSETS_FORMER_YARD_KEY = every asset still
    // tagged with a slug that left settings.yards, else the exact slug.
    // TASK-9509 — one predicate shared with the banners' "in this branch" count.
    const knownYards = new Set(getYardsList().map(y => y.slug));
    filtered = filtered.filter(a => _assetInYardView(a, assetYardFilter, knownYards));
  }
  if (belowZero) filtered = negativeStockAssets(RosterState.assets); // TASK-9985 — overrides the toolbar filters

  // TASK-9369 — Branches mode: one rollup card per yard, computed off the
  // UNFILTERED active roster (same principle as the below-zero banner — a stale
  // search must never change a branch total or hide the rollup), and rendered
  // BEFORE the empty-results returns below for the same reason.
  const hasYards = getYardsList().length > 0;
  const groupMode = _assetsGroupByEffective();
  // TASK-9509 — when Items mode is narrowed to one branch, the company-wide
  // banners add "N items in {branch} · M elsewhere". Branches mode = no scope.
  const branchScope = (assetYardFilter !== 'all' && groupMode !== 'branches')
    ? { key: assetYardFilter, label: _assetsYardKeyLabel(assetYardFilter), known: new Set(getYardsList().map(y => y.slug)) }
    : null;
  // (the Branches render itself sits below, right after negBanner is built)
  // Items mode breadcrumb after a branch-card drill (or a member's home branch):
  // "← All branches · Branch A". Back restores Branches mode.
  const crumb = (!belowZero && hasYards && RosterState._assetsBranchDrill && assetYardFilter !== 'all') ? `
    <div class="asset-branch-crumb" style="grid-column:1 / -1;display:flex;align-items:center;gap:8px;flex-wrap:wrap;font-size:0.85rem;color:var(--text-secondary);">
      <button type="button" class="btn btn-secondary" style="padding:4px 10px;font-size:0.8rem;" onclick="window.RosterModule.setAssetsGroupBy('branches')">
        <span class="material-icons" style="font-size:16px;vertical-align:-3px;">arrow_back</span> All branches
      </button>
      <span aria-hidden="true">·</span>
      <strong style="color:var(--text-primary);">${escapeHtml(_assetsYardKeyLabel(assetYardFilter))}</strong>
    </div>` : '';
  // Per-SKU company line only makes sense once stock can be split across yards;
  // a zero-yard tenant's grid stays exactly today's.
  const skuTotals = hasYards ? computeAssetSkuTotals(RosterState.assets) : null;

  // TASK-9275 — a negative count is a RECORD, not a toast. Both write paths
  // (billing.js saveReviewTicket, dispatch.js saveJob/saveAssignment) warn and
  // then write anyway, on purpose: a crew really can consume stock nobody booked
  // in, and refusing the write would strand a biller mid-ticket the way TASK-428
  // stranded drivers mid-clock-out. That is the correct call — but it left the
  // only signal as a toast on one person's screen at one instant. The card tint
  // and the tab badge have flagged this since be6320f9; what was missing is the
  // one place that NAMES the offending items, so "1" on a badge becomes findable.
  //
  // Built off the UNFILTERED roster on purpose: a search string or a category
  // filter must not be able to hide the thing you are being told to go fix.
  const negAssets = negativeStockAssets(RosterState.assets); // TASK-9985 — shared with the badge
  const negBanner = negAssets.length === 0 ? '' : `
    <div class="asset-negative-banner" style="grid-column:1 / -1;margin:0 0 var(--spacing-sm);padding:10px 12px;border:1px solid #FECACA;border-left:4px solid #DC2626;border-radius:8px;background:#FEF2F2;color:#991B1B;font-size:0.82rem;">
      <div style="display:flex;align-items:center;gap:6px;font-weight:700;">
        <span class="material-icons" style="font-size:18px;">inventory_2</span>
        ${negAssets.length} item${negAssets.length === 1 ? '' : 's'} below zero
      </div>
      ${_assetBannerScopeLine(negAssets, branchScope)}
      <div style="margin-top:4px;">
        ${negAssets.map(a => `<strong>${escapeHtml(a.name || a.partNumber || 'Asset')}</strong> (${Number(a.quantity || 0)})`).join(', ')}
        &mdash; more has been consumed than was ever booked in. Receive the missing stock, or adjust the count so it reads true.
      </div>
    </div>`;

  // TASK-4810 — rentals with more OUT than OWNED. The true count is unknown,
  // so name them (unfiltered, same as the below-zero banner) rather than let a
  // picker print "Available: -2168". Amber: a count to check, not an outage.
  const driftAssets = RosterState.assets.filter(a => a.active !== false && rentalStockView(a)?.drift);
  const _fmtN = (n) => Number(n || 0).toLocaleString('en-US');
  const driftBanner = driftAssets.length === 0 ? '' : `
    <div class="asset-drift-banner" style="grid-column:1 / -1;margin:0 0 var(--spacing-sm);padding:10px 12px;border:1px solid #FCD34D;border-left:4px solid #D97706;border-radius:8px;background:#FFFBEB;color:#92400E;font-size:0.82rem;">
      <div style="display:flex;align-items:center;gap:6px;font-weight:700;">
        <span class="material-icons" style="font-size:18px;">rule</span>
        ${driftAssets.length} rental${driftAssets.length === 1 ? '' : 's'} need${driftAssets.length === 1 ? 's' : ''} a recount
      </div>
      ${_assetBannerScopeLine(driftAssets, branchScope)}
      <div style="margin-top:4px;">
        ${driftAssets.map(a => { const v = rentalStockView(a); return `<strong>${escapeHtml(a.name || a.partNumber || 'Asset')}</strong> (${_fmtN(v.out)} out, ${_fmtN(v.owned)} owned)`; }).join(', ')}
        &mdash; more is out than you own, so what's available can't be known. A return was missed or a quantity was edited after deploying. Count what's in the yard, then correct the total or the out count.
      </div>
    </div>`;
  // Prepended AFTER each innerHTML write (not concatenated into it) so the
  // TASK-9275 / 9369 banner literals stay exactly as their suites pin them.
  // TASK-10015 — the owner's "Recount from live jobs" bar rides the same
  // prepend (below the banners), so no pinned banner literal changes.
  const _recountBar = typeof _rentalRecountBarHtml === 'function' ? _rentalRecountBarHtml() : ''; // guarded: suites lift this function out alone
  const _prependDriftBanner = () => {
    if (_recountBar) container.insertAdjacentHTML('afterbegin', _recountBar);
    if (driftBanner) container.insertAdjacentHTML('afterbegin', driftBanner);
  };

  // TASK-4800 S3 — On site: where the rental equipment is sitting, oldest
  // first. Takes the grid over from Items/Branches while it is on. The search /
  // category / yard filters narrow it PER ASSET (inactive included: a retired
  // SKU with units still in the field is still out there).
  _syncAssetsOnSiteUi();
  if (!belowZero && _onsiteState().on && RosterState._assetsLoaded) {
    let ids = null;
    if ((search && search.trim()) || category !== 'all' || assetYardFilter !== 'all') {
      const sl = (search || '').trim().toLowerCase();
      const knownYards = new Set(getYardsList().map(y => y.slug));
      ids = new Set((_onsiteState().assets || RosterState.assets).filter(a =>
        (!sl || a.partNumber?.toLowerCase().includes(sl) || a.name?.toLowerCase().includes(sl))
        && (category === 'all' || a.category === category)
        && (assetYardFilter === 'all' || _assetInYardView(a, assetYardFilter, knownYards))
      ).map(a => a.id));
    }
    container.innerHTML = _renderAssetsOnSite(ids);
    _prependDriftBanner();
    _loadAssetsOnSite();
    return;
  }

  if (!belowZero && groupMode === 'branches' && RosterState._assetsLoaded) {
    const rollups = computeAssetBranchRollups(RosterState.assets, getYardsList());
    const filtersActive = !!(search && search.trim()) || category !== 'all';
    const filterNote = !filtersActive ? '' : `
      <div class="asset-branch-note" style="grid-column:1 / -1;font-size:0.8rem;color:var(--text-secondary);padding:2px 4px;">
        <span class="material-icons" style="font-size:15px;vertical-align:-3px;">info</span>
        Branch totals are company-wide — the search box and category filter apply to Items, not here.
      </div>`;
    container.innerHTML = negBanner + filterNote + _renderAssetBranchCards(rollups);
    _prependDriftBanner();
    return;
  }

  if (RosterState.assets.length === 0) {
    if (!RosterState._assetsLoaded) {
      container.innerHTML = `
        <div style="text-align:center;padding:var(--spacing-xl);">
          <div class="btn-spinner" style="width:32px;height:32px;margin:0 auto var(--spacing-md);"></div>
          <div style="color:var(--text-secondary);font-size:0.875rem;">Loading assets…</div>
        </div>
      `;
    } else {
      container.innerHTML = `
        <div class="empty-state">
          <div class="empty-state-icon">📦</div>
          <div class="empty-state-title">No assets yet</div>
          <div class="empty-state-text">Add equipment, tools, or inventory to track.</div>
        </div>
      `;
    }
    return;
  }

  if (filtered.length === 0) {
    container.innerHTML = negBanner + `
      <div class="empty-state">
        <div class="empty-state-icon">🔍</div>
        <div class="empty-state-title">No matches</div>
        <div class="empty-state-text">Try adjusting your search or filter.</div>
      </div>
    `;
    _prependDriftBanner(); // TASK-4810
    // TASK-9369 — breadcrumb sits above everything (incl. the below-zero banner)
    if (crumb) container.insertAdjacentHTML('afterbegin', crumb);
    return;
  }

  // TASK-10007 (typeof: suites lift renderAssetsList out alone, same as _syncReceiveStockButton)
  const _pinIds = typeof pinnedAssetIdsFrom === 'function' ? pinnedAssetIdsFrom(window.AppState?.settings) : [];
  const _canPin = typeof _canPinAssets === 'function' && _canPinAssets();
  container.innerHTML = negBanner + filtered.map(asset => {
    const jsId = window.escJsAttr ? window.escJsAttr(asset.id || '') : String(asset.id || '');
    const catLabel = getTypeLabel(asset.category, 'assetCategories');
    const isQty = asset.trackingMode === 'quantity';
    const qty = asset.quantity || 0;
    const low = assetStockIsNegative(asset);
    const rsv = rentalStockView(asset); // TASK-4810
    // TASK-9509 — `quantity` is everything owned, out or not. "On hand" beside
    // "178 out · 287 available" read as in-the-yard; a rental says Owned.
    const qtyLabel = rsv ? 'Owned' : 'Total';
    const yardLabel = asset.yardId
      ? (getLiveYardLabel(asset.yardId) || asset.yardName || getYardLabel(asset.yardId))
      : '';
    return `
    <div class="asset-card ${asset.active === false ? 'inactive' : ''} ${low ? 'low-stock' : ''}" style="--asset-hue:${assetCategoryHue(asset.category)};" onclick="window.RosterModule.editAsset('${jsId}')">
      <div class="asset-card-visual">
        ${asset.photoUrl
          ? `<img class="asset-card-photo" src="${escapeHtml(asset.photoUrl)}" alt="${escapeHtml(asset.name || '')}" loading="lazy">`
          : `<span class="material-icons asset-card-glyph">${assetCategoryIcon(asset.category, catLabel)}</span>`}
        ${catLabel ? `<span class="asset-card-cat">${escapeHtml(catLabel)}</span>` : ''}
        ${asset.active === false ? '<span class="asset-card-flag">Inactive</span>' : ''}
        <button class="asset-delete-btn" onclick="event.stopPropagation(); window.RosterModule.deleteAsset('${jsId}')" title="Deactivate" aria-label="Deactivate asset">
          <span class="material-icons">delete</span>
        </button>
        ${typeof _pinnedAssetStarHtml === 'function' ? _pinnedAssetStarHtml(asset, _pinIds, _canPin) : ''}
      </div>
      <div class="asset-card-main">
        <div class="asset-card-name">${escapeHtml(asset.name || (_assetPartNo(asset) ? '' : 'Unnamed asset'))}${_rentalBadge(asset)}</div>
        <div class="asset-card-meta">
          ${_assetPartNo(asset)
            ? `<span class="asset-card-sku">${escapeHtml(_assetPartNo(asset))}</span>`
            : `<span class="asset-card-sku asset-card-sku--none" title="This record has no part number" style="font-style:italic;color:var(--text-secondary);">No part #</span>`}
          ${yardLabel ? `<span class="asset-card-yard"><span class="material-icons">place</span>${escapeHtml(yardLabel)}</span>` : ''}
        </div>
        ${_assetSkuTotalLine(asset, skuTotals)}
        ${rsv && rsv.out > 0 ? `<div class="asset-card-out-line" style="margin-top:4px;font-size:0.72rem;color:${rsv.drift ? '#92400E' : 'var(--text-secondary)'};${rsv.drift ? 'font-weight:600;' : ''}">${rsv.drift
          ? `&#9888; Needs recount: ${_fmtN(rsv.out)} out, ${_fmtN(rsv.owned)} owned`
          : `${_fmtN(rsv.out)} out &middot; ${_fmtN(rsv.available)} available`}</div>` : ''}
        ${_assetIndividualStatusLine(asset)}
        ${typeof _assetParBadge === 'function' ? _assetParBadge(asset) : ''}
        ${asset.stockTracked === true ? `<div class="asset-card-tracked-line" style="margin-top:4px;font-size:0.72rem;color:var(--text-secondary);">${_isRosterStockKey(asset) ? '<span class="material-icons" style="font-size:13px;vertical-align:-2px;">inventory</span> Counted from jobs and field tickets' : '<span class="material-icons" style="font-size:13px;vertical-align:-2px;">shield</span> Counted from PPE sign-outs'}</div>
        <div class="asset-card-stock-actions" style="display:flex;gap:6px;flex-wrap:wrap;margin-top:6px;">
          <button type="button" class="btn btn-secondary btn-sm asset-stock-count-btn" style="min-height:48px;height:48px;flex:1 1 0;" onclick="event.stopPropagation(); window.RosterModule.openTrackedStockCount('${jsId}')"><span class="material-icons" style="font-size:16px;">fact_check</span> Count now</button>
          <button type="button" class="btn btn-secondary btn-sm asset-stock-history-btn" style="min-height:48px;height:48px;flex:1 1 0;" onclick="event.stopPropagation(); window.RosterModule.openTrackedStockHistory('${jsId}')"><span class="material-icons" style="font-size:16px;">history</span> History</button>
        </div>` : ''}
      </div>
      <div class="asset-card-stat ${low ? 'is-low' : ''} ${isQty ? '' : 'is-serial'}">
        <div class="asset-stat-copy">
          <div class="asset-stat-label">${isQty ? (low ? 'Below zero' : qtyLabel) : 'Serial'}</div>
          <div class="asset-stat-value">${isQty ? qty : escapeHtml(asset.serialNumber || 'N/A')}</div>
        </div>
        ${isQty && asset.consumable ? `
          <div class="asset-inv-buttons">
            <button class="asset-inv-btn asset-inv-add" onclick="event.stopPropagation(); window.RosterModule.showReceiptModal('${jsId}')" title="Add inventory" aria-label="Add inventory">
              <span class="material-icons">add</span>
            </button>
            <button class="asset-inv-btn asset-inv-adjust" onclick="event.stopPropagation(); window.RosterModule.showAdjustmentModal('${jsId}')" title="Adjust inventory" aria-label="Adjust inventory">
              <span class="material-icons">remove</span>
            </button>
          </div>` : ''}
      </div>
    </div>`;
  }).join('');
  _prependDriftBanner(); // TASK-4810
  // TASK-9369 — breadcrumb sits above everything (incl. the below-zero banner)
  if (crumb) container.insertAdjacentHTML('afterbegin', crumb);
  if (belowZero) container.insertAdjacentHTML('afterbegin', _assetsBelowZeroChipHtml(filtered.length)); // TASK-9985
  // TASK-9509 — name the job a deployed individual item is out on (async, once per job).
  const _deployedJobIds = filtered
    .filter(a => a.trackingMode !== 'quantity' && String(a.deploymentStatus || '').toLowerCase() === 'deployed' && a.deployedToJobId)
    .map(a => a.deployedToJobId);
  if (_deployedJobIds.length) _resolveAssetJobTitles(_deployedJobIds).catch(() => {});
}

/** Stable hue (0-359) for an asset category slug. Categories are tenant-defined,
 *  so the card tint is derived instead of mapped — every custom category still
 *  gets its own consistent colour without anyone registering one. */
function assetCategoryHue(slug) {
  const s = String(slug || 'other');
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) % 360;
  return h;
}

/** Glyph for the card's visual tile when an asset has no photo — matched on the
 *  slug AND the label so a renamed/custom category still lands on something
 *  recognisable. Falls back to the generic inventory box. */
function assetCategoryIcon(slug, label) {
  const s = `${slug || ''} ${label || ''}`.toLowerCase();
  if (/pipe|pier|casing|tubular|rod|pile/.test(s)) return 'straighten';
  if (/safety|ppe|gear|harness/.test(s)) return 'health_and_safety';
  if (/tool/.test(s)) return 'build';
  if (/part|component|spare/.test(s)) return 'settings';
  if (/equip|machine|hoe|loader|dozer|excavat/.test(s)) return 'precision_manufacturing';
  if (/fluid|oil|chem|fuel|water/.test(s)) return 'water_drop';
  if (/tank|vessel|drum/.test(s)) return 'propane_tank';
  if (/mat|board|plank|timber/.test(s)) return 'view_agenda';
  if (/hose|line|cable|chain|strap/.test(s)) return 'cable';
  if (/valve|fitting|flange/.test(s)) return 'plumbing';
  return 'inventory_2';
}

function getCategoryLabel(category) {
  const labels = {
    drill_pipe: 'Drill Pipe',
    equipment: 'Equipment',
    tool: 'Tool',
    other: 'Other'
  };
  return labels[category] || category;
}

// TASK-9720 — a stock-tracked item (HR → Safety Equipment → Track stock) has a
// SERVER-maintained count (the PPE ledger). firestore.rules refuses any client
// change to its quantity, so the form shows the count read-only, never sends
// it, and points at the two ways it does change: Receive / Adjust here (server
// callable) and PPE sign-outs in HR.
function _applyStockTrackedAssetForm(asset) {
  const tracked = !!(asset && asset.stockTracked === true);
  const q = document.getElementById('asset-quantity');
  if (q) q.readOnly = tracked;
  ['asset-tracking-mode', 'asset-quantity-type'].forEach((elId) => {
    const el = document.getElementById(elId);
    if (el) el.disabled = tracked;
  });
  let hint = document.getElementById('asset-stock-tracked-hint');
  if (tracked && q) {
    if (!hint) {
      hint = document.createElement('div');
      hint.id = 'asset-stock-tracked-hint';
      hint.className = 'form-hint';
      hint.style.cssText = 'margin-top:4px;color:var(--text-secondary);';
      q.insertAdjacentElement('afterend', hint);
    }
    hint.textContent = _isRosterStockKey(asset)
      ? 'Counted from jobs and field tickets. Change it with Count now, Receive or Adjust on the item card.'
      : 'Counted from PPE sign-outs. Change it with Count now, Receive or Adjust on the item card.';
  } else if (hint) {
    hint.remove();
  }
  // TASK-9722 — the mismatch strip (taken / received / returned / counted / on
  // hand) + History, read from the server ledger by the shared HR renderer.
  let box = document.getElementById('asset-stock-ledger-box');
  if (tracked && q && asset && asset.id) {
    if (!box) {
      box = document.createElement('div');
      box.id = 'asset-stock-ledger-box';
      box.style.cssText = 'margin-top:8px;';
      (hint || q).insertAdjacentElement('afterend', box);
    }
    const jsId = window.escJsAttr ? window.escJsAttr(asset.id) : String(asset.id);
    box.innerHTML = `<div id="asset-stock-strip"></div>
      <div style="display:flex;gap:8px;flex-wrap:wrap;margin-top:8px;">
        <button type="button" class="btn btn-secondary" style="min-height:48px;height:48px;flex:1 1 140px;" onclick="window.RosterModule.openTrackedStockCount('${jsId}')"><span class="material-icons" style="font-size:18px;">fact_check</span> Count now</button>
        <button type="button" class="btn btn-secondary" style="min-height:48px;height:48px;flex:1 1 140px;" onclick="window.RosterModule.openTrackedStockHistory('${jsId}')"><span class="material-icons" style="font-size:18px;">history</span> History</button>
        <button type="button" class="btn btn-secondary" style="min-height:48px;height:48px;flex:1 1 140px;" onclick="window.RosterModule.openSupplyParSheet('${jsId}')"><span class="material-icons" style="font-size:18px;">low_priority</span> <span id="asset-par-btn-label">${(() => { const p = _supplyParInfo(asset); return p ? `Reorder at ${escapeHtml(String(p.par))}` : 'Reorder level'; })()}</span></button>
        ${_isRosterStockKey(asset) ? `<button type="button" class="btn btn-secondary" style="min-height:48px;height:48px;flex:1 1 140px;" onclick="window.RosterModule.stopRosterStockTracking('${jsId}')"><span class="material-icons" style="font-size:18px;">block</span> Stop counting</button>` : ''}
      </div>`;
    window.HRModule?.renderSupplyStripInto?.(document.getElementById('asset-stock-strip'), asset.id);
  } else if (box) {
    box.remove();
  }
  // TASK-9730 — "Track stock" on a saved, untracked Roster consumable that is
  // not Safety Gear (that one is counted from its PPE item in HR). The server
  // (setPpeStockTracking scope 'asset', roster module or owner) opens the
  // ledger; from then on jobs and field tickets move the count, not this form.
  const offer = document.getElementById('asset-stock-track-box');
  if (q && _canOfferRosterStockTracking(asset)) {
    __ddChunkWrap(import('./rosterStockTrack.js?v=9730002')).then(m => m.renderTrackOffer(q, asset)).catch(() => {}); // lazy (cold-load budget)
  } else if (offer) {
    offer.remove();
  }
}

// TASK-9730 — a stock count opened from Roster (not from an HR PPE item) has the
// key ASSET:<id> (functions/src/services/supplyLedger.js assetStockKey).
function _isRosterStockKey(asset) {
  return !!(asset && asset.stockTracked === true && typeof asset.ppeTypeKey === 'string' && asset.ppeTypeKey.startsWith('ASSET:'));
}
function _canOfferRosterStockTracking(asset) {
  return !!(asset && asset.id && asset.stockTracked !== true && asset.active !== false
    && (asset.trackingMode || 'quantity') === 'quantity'
    && (asset.quantityType === 'consumable' || asset.consumable === true)
    && asset.quantityType !== 'rental' && asset.isRental !== true
    && asset.category !== 'safety_gear');
}
// The call itself lives in the LAZY views/rosterStockTrack.js (cold-load budget).
async function _rosterStockTrack(assetId, fn, ok) {
  const asset = RosterState.assets.find(a => a.id === assetId);
  if (!asset || !ok(asset)) return;
  const reapply = () => { if (document.getElementById('asset-id')?.value === assetId) _applyStockTrackedAssetForm(asset); };
  try {
    await (await __ddChunkWrap(import('./rosterStockTrack.js?v=9730002')))[fn](asset, reapply);
  } catch (e) {
    window.showToast?.('Could not load this just now. Reload and try again.', 'error');
  }
}
function startRosterStockTracking(assetId) { return _rosterStockTrack(assetId, 'startRosterStockTracking', _canOfferRosterStockTracking); }
function stopRosterStockTracking(assetId) { return _rosterStockTrack(assetId, 'stopRosterStockTracking', _isRosterStockKey); }

// TASK-9728 (S6) — SERIALIZED PPE. A gas monitor (an individual asset in
// Safety Gear) shows who has it now and its sign-out history in its edit form.
// The sign-outs are HR's safetyEquipment rows, which a Roster-only login cannot
// read and the asset doc must not copy (every driver PIN session reads assets),
// so the card asks getSerialPpeCustody (roster|hr, tenant from the claim).
// Read-only here: the unit is issued and returned from HR → the person's card →
// Issue Equipment. Never shows "nobody has it" on a failed read.
function _applySerialPpeAssetForm(asset) {
  let box = document.getElementById('asset-serial-ppe-box');
  const show = !!(asset && asset.id && asset.trackingMode === 'individual' && asset.category === 'safety_gear');
  if (!show) { if (box) box.remove(); return; }
  const anchor = document.getElementById('asset-serial-group');
  if (!anchor) return;
  if (!box) {
    box = document.createElement('div');
    box.id = 'asset-serial-ppe-box';
    box.style.cssText = 'margin:4px 0 14px;padding:10px 12px;border:1px solid var(--border-light);border-radius:10px;background:var(--bg-secondary);';
    anchor.insertAdjacentElement('afterend', box);
  }
  box.setAttribute('data-asset-id', asset.id);
  box.innerHTML = `
    <div style="font-weight:700;font-size:0.9rem;color:var(--text-primary);display:flex;align-items:center;gap:6px;"><span class="material-icons" style="font-size:18px;">badge</span> Who has it</div>
    <div id="asset-serial-ppe-body" style="font-size:0.85rem;color:var(--text-secondary);margin-top:6px;">Checking&hellip;</div>`;
  _loadSerialPpeCustody(asset.id);
}

// TASK-9730 — the "Who has it" read + render lives in the LAZY
// views/rosterSerialPpe.js (cold-load budget). A failed chunk load says so in
// the box; it never shows "nobody has it".
async function _loadSerialPpeCustody(assetId) {
  try {
    await (await __ddChunkWrap(import('./rosterSerialPpe.js?v=9730001'))).loadSerialPpeCustody(assetId, escapeHtml);
  } catch (e) {
    const b = document.getElementById('asset-serial-ppe-box');
    const el = b && b.getAttribute('data-asset-id') === assetId ? document.getElementById('asset-serial-ppe-body') : null;
    if (el) el.textContent = 'Could not check who has it just now. Close and reopen to try again.';
  }
}

// TASK-9722 — Count now / History for a stock-tracked item. ONE implementation
// lives in hr.js (window.HRModule — never a cross-?v= import, Pitfall #1); the
// host passes what this screen allows, and recordSupplyStock decides again
// (roster or hr module, owner via ownerId).
function openTrackedStockCount(assetId) {
  if (!window.HRModule?.openSupplyCountSheet) { window.showToast?.('Counting is not available just now. Reload and try again.', 'error'); return; }
  window.HRModule.openSupplyCountSheet(assetId, {
    canManage: true,
    onDone: (r) => {
      const a = RosterState.assets.find(x => x.id === assetId);
      if (a && r && typeof r.balance === 'number') a.quantity = r.balance;
      const strip = document.getElementById('asset-stock-strip');
      if (strip) window.HRModule.renderSupplyStripInto(strip, assetId);
    },
  });
}
function openTrackedStockHistory(assetId) {
  if (!window.HRModule?.openSupplyHistory) { window.showToast?.('History is not available just now. Reload and try again.', 'error'); return; }
  window.HRModule.openSupplyHistory(assetId, { canManage: true });
}

// TASK-9720 — Receive / Adjust on a stock-tracked item go through the server
// (the ledger row and the count move together, in one transaction). One request
// id per open dialog, so a retried tap after a network blip is not counted twice.
function _stockMoveRequestId() {
  if (!RosterState._stockMoveReqId) {
    RosterState._stockMoveReqId = 'r' + Date.now().toString(36) + Math.random().toString(36).slice(2, 10);
  }
  return RosterState._stockMoveReqId;
}
async function _recordTrackedStockMove(asset, kind, qty, reason) {
  const fn = httpsCallable(functions, 'recordSupplyStock');
  const res = await fn({ assetId: asset.id, kind, qty, reason: reason || '', requestId: _stockMoveRequestId() });
  RosterState._stockMoveReqId = null;
  const bal = res && res.data ? res.data.balance : undefined;
  if (typeof bal === 'number') asset.quantity = bal;
  return res && res.data;
}

// =============================================================================
// TASK-9723 — PPE inventory S3: FAST RECEIVE + PAR LEVELS (quantity only).
// =============================================================================
// Receive: one form lists every stock-tracked item; type (or scan) what came in
// and Save once. ONE server call (receiveSupplies) writes every ledger row, moves
// every count and writes one audit row, all in one transaction (the sheets:
// views/supplyReceive.js, lazily loaded; it keeps the receive id while a
// Save's outcome is unknown, so a delivery is never counted twice).
// Par: the reorder level (and optional fill-to) on a stock-tracked item, set
// through setSupplyParLevel (roster module or owner; audited old -> new). The
// "below par" rule is window.PpeReport.alertFlags / reorderQty — the same
// functions the daily inventory email and the connector use.
// No cost anywhere here (TASK-9724 / TASK-9575 slice D own supply cost).
function _stockTrackedItems() {
  return (RosterState.assets || []).filter(a => a && a.active !== false && a.stockTracked === true);
}
function _supplyParInfo(asset) {
  const P = window.PpeReport;
  if (!P || typeof P.alertFlags !== 'function' || !asset) return null;
  const par = Number(asset.parLevel);
  if (asset.parLevel == null || !Number.isInteger(par) || par < 1) return null;
  const f = P.alertFlags(asset);
  const reorder = typeof P.reorderQty === 'function' ? P.reorderQty(asset) : 0;
  return { par, target: Number.isInteger(Number(asset.parTarget)) && Number(asset.parTarget) >= par ? Number(asset.parTarget) : null, belowPar: !!f.belowPar, reorder };
}
// The badge on an asset card (Roster > Assets). '' when no par / not below it.
function _assetParBadge(asset) {
  if (!asset || asset.trackingMode === 'individual') return '';
  const p = _supplyParInfo(asset);
  if (!p || !p.belowPar) return '';
  const qty = Number(asset.quantity) || 0;
  return `<div class="asset-card-par-line" style="margin-top:6px;display:inline-flex;align-items:center;gap:4px;padding:3px 8px;border-radius:999px;background:#FFFBEB;border:1px solid #FCD34D;color:#92400E;font-size:0.74rem;font-weight:700;">
    <span class="material-icons" style="font-size:14px;">trending_down</span> Below par: ${escapeHtml(String(qty))} of ${escapeHtml(String(p.par))}${p.reorder > 0 ? ` &middot; order ${escapeHtml(String(p.reorder))}` : ''}
  </div>`;
}
function _syncReceiveStockButton() {
  const btn = document.getElementById('btn-receive-stock');
  if (!btn) return;
  const vuo = window.AppState?.memberAccess?.viewUploadOnly === true;
  const items = _stockTrackedItems();
  btn.style.display = (!vuo && items.length) ? '' : 'none';
  const low = items.filter(a => (_supplyParInfo(a) || {}).belowPar).length;
  const lbl = btn.querySelector('.receive-low-count');
  if (lbl) lbl.textContent = low ? ` (${low} low)` : '';
}

// The Receive and Reorder level sheets live in views/supplyReceive.js, loaded
// on first use (cold-load budget, scripts/perf-budgets.cjs). It never touches
// RosterState itself: everything it needs comes through this host.
const _supplyHost = {
  assets: () => RosterState.assets || [],
  costState: () => _supplyCostState(), // TASK-9724
  items: () => _stockTrackedItems(),
  parInfo: (a) => _supplyParInfo(a),
  yardLabel: (a) => (a && a.yardId ? (getLiveYardLabel(a.yardId) || a.yardName || getYardLabel(a.yardId)) : ''),
  multiYard: () => new Set(_stockTrackedItems().map(a => a.yardId || '')).size > 1,
  rerender: () => renderAssetsList(),
};
async function _supplySheets() {
  return __ddChunkWrap(import('./supplyReceive.js?v=9724002')); // TASK-9724 unit cost on receive; S2: switch moved to Settings
}
async function openFastReceive() {
  try { await (await _supplySheets()).openFastReceive(_supplyHost); } catch (err) {
    console.error('[Roster] Receive failed to open:', err);
    window.showToast?.('Could not open Receive. Reload the page and try again.', 'error');
  }
}
async function openSupplyParSheet(assetId) {
  try { (await _supplySheets()).openSupplyParSheet(_supplyHost, assetId); } catch (err) {
    console.error('[Roster] Reorder level failed to open:', err);
    window.showToast?.('Could not open Reorder level. Reload the page and try again.', 'error');
  }
}

// TASK-9720 S1 #2 — the HR PPE item (safetyEquipmentTypes) that matches an
// asset name: trimmed, case-insensitive (the PPE report's grouping key).
async function _ppeTypesByKey() {
  const tenant = window.AppState?.tenant;
  const snap = await getDocs(collection(db, 'tenants', tenant, 'safetyEquipmentTypes'));
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
}
const _ppeKey = (v) => String(v == null ? '' : v).replace(/\s+/g, ' ').trim().toLowerCase();

// Returns 'link' (link / create — the server does it), 'decline' (not
// issuable), or 'cancel' (back to the form). Never blocks a save on a read error.
async function _askSafetyGearLink(assetId, name) {
  let types = [];
  try { types = await _ppeTypesByKey(); } catch (e) { console.warn('[Roster] PPE item check failed:', e?.message || e); return 'link'; }
  const t = types.find(x => _ppeKey(x.name) === _ppeKey(name));
  if (!t) return 'link'; // a new PPE item named after this one is made for it
  const linked = t.linkedAssetId || t.stockAssetId || '';
  if (linked && linked !== assetId) {
    window.showToast?.(`HR > PPE already has "${t.name}" for another Roster item. This one is saved, and PPE keeps the first one.`, 'info');
    return 'decline';
  }
  if (linked && linked === assetId && t.active !== false) return 'link';
  if (typeof window.showConfirm !== 'function') return confirm(`Link to the existing PPE item "${t.name}" in HR?`) ? 'link' : 'decline';
  const ok = await window.showConfirm('Same item as in PPE?',
    `HR > PPE already has "${t.name}". Is this the same thing? Yes keeps signing it out in PPE and this Roster item as one item.`,
    { confirmText: 'Yes, same item', cancelText: 'No, keep separate' });
  return ok ? 'link' : 'decline';
}

// Moving an item OUT of Safety Gear: 'keep' | 'remove' | 'cancel'.
async function _askKeepSafetyGearIssuable(asset) {
  const ok = typeof window.showConfirm === 'function'
    ? await window.showConfirm('Still sign it out as PPE?',
      `"${asset.name || 'This item'}" is leaving Safety Gear. Can people still sign it out in HR > PPE?`,
      { confirmText: 'Yes, keep it', cancelText: 'No, remove it' })
    : confirm('Still sign it out as PPE?');
  if (ok) return 'keep';
  try {
    const types = await _ppeTypesByKey();
    const t = types.find(x => x.linkedAssetId === asset.id) || types.find(x => _ppeKey(x.name) === _ppeKey(asset.name) && (x.linkedAssetId === asset.id || x.stockAssetId === asset.id));
    if (t && t.active !== false) {
      await updateDoc(doc(db, 'tenants', window.AppState?.tenant, 'safetyEquipmentTypes', t.id), { active: false, updatedAt: Timestamp.now() });
    }
  } catch (e) {
    console.error('[Roster] could not take the item out of PPE:', e);
    window.showToast?.('Saved, but it could not be taken out of HR > PPE. Turn it off in HR > Safety Equipment > Types.', 'error');
  }
  return 'remove';
}

// ============================================
// TASK-9575 slice D — COST HOME (tenants/{tid}/assetCosts)
// ============================================
// Cost no longer lives on the asset doc (every member AND driver can read
// that). It lives in the server-only cost home, readable by the owner
// (tenants/{tid}.ownerId) or a member with the BI or Billing module — the same
// test firestore.rules applies. Purchase price / current value are changed by
// the owner or Billing through setAssetValuation (audited). A login without
// cost access never sees the Valuation block, and roster.js never writes a cost
// field onto an asset.
function _assetCostAccess() {
  const st = window.AppState || {};
  const none = { see: false, edit: false };
  if (st.driverMode || st.isPublicAccess || st.driverModePin || st.isDemo || st.tenant === 'demo') return none;
  const uid = st.user?.uid || null;
  const isOwner = !!(uid && st.tenantData?.ownerId && st.tenantData.ownerId === uid);
  if (isOwner) return { see: true, edit: true, owner: true, viewOnly: false };
  const ma = st.memberAccess || null;
  const mods = ma && Array.isArray(ma.modules) ? ma.modules : [];
  const billing = mods.includes('billing');
  const viewOnly = !!(ma && (ma.viewUploadOnly === true || ma.readOnly === true));
  return { see: billing || mods.includes('bi'), edit: billing && !viewOnly, owner: false, viewOnly };
}

// TASK-9724 — the owner's "Track supply cost" switch lives in the cost home
// (assetCosts/settings): only a login that may see cost can read it, so a
// login without cost access never learns whether cost is tracked. Any read
// failure = off (never show a cost field we could not confirm).
async function _supplyCostState() {
  const acc = _assetCostAccess();
  if (!acc.see) return Object.assign({ on: false }, acc);
  try {
    const snap = await getDoc(doc(db, 'tenants', window.AppState?.tenant, 'assetCosts', 'settings'));
    return Object.assign({ on: !!(snap.exists() && snap.data()?.trackCost === true) }, acc);
  } catch (e) {
    console.warn('[Roster] cost switch read failed:', e?.code || e?.message || e);
    return Object.assign({ on: false, readFailed: true }, acc);
  }
}

const _fmtCost = (n) => (typeof n === 'number' && isFinite(n))
  ? '$' + n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 4 })
  : 'unknown';

// TASK-9724 S2 — the owner's "Track supply cost" switch lives in Settings →
// Company (#settings-supply-cost-group, index.html). app.js calls this when
// Settings opens. Hidden for everyone but the owner (nothing cost-related
// renders for anyone else; the flip is audited like any setting change); the
// button goes straight to
// setSupplyCostTracking (owner-only, audited old -> new) — it is NOT part of
// Save Settings and takes effect at once. Receive (supplyReceive.js) and the
// asset card only READ the switch.
async function paintSupplyCostSetting() {
  const group = document.getElementById('settings-supply-cost-group');
  if (!group) return;
  const acc = _assetCostAccess();
  if (!acc.owner) { group.style.display = 'none'; return; }
  const state = document.getElementById('settings-supply-cost-state');
  const btn = document.getElementById('settings-supply-cost-btn');
  const hint = document.getElementById('settings-supply-cost-hint');
  if (!state || !btn || !hint) return;
  group.style.display = '';
  state.textContent = 'checking…';
  btn.disabled = true;
  const cs = await _supplyCostState();
  if (cs.readFailed) {
    state.textContent = 'could not be read just now';
    hint.textContent = 'Try again in a moment.';
    btn.textContent = 'Retry';
    btn.disabled = false;
    btn.onclick = () => paintSupplyCostSetting();
    return;
  }
  const on = !!cs.on;
  state.textContent = on ? 'On' : 'Off';
  btn.textContent = on ? 'Turn off' : 'Turn on';
  hint.textContent = on
    ? 'Prices typed on a receipt and the cost of each sign-out (at the item’s average unit cost when it is booked) are kept for you, BI and Billing only — a usage report, never a second cost on your books. Turning it off stops new cost records; what was recorded stays.'
    : 'Off: supply counts only, no prices anywhere. Only you (the account owner) can turn it on. Changes are audited.';
  btn.disabled = false;
  btn.onclick = async () => {
    if (btn.disabled) return;
    btn.disabled = true;
    try {
      await httpsCallable(functions, 'setSupplyCostTracking')({ trackCost: !on });
      window.showToast?.(!on ? 'Supply cost tracking is on.' : 'Supply cost tracking is off. Counts work as before.', 'success');
    } catch (err) {
      console.error('[Roster] setSupplyCostTracking failed:', err?.code, err?.message);
      window.showToast?.(err?.message || 'Could not change the setting just now. Try again.', 'error');
    }
    paintSupplyCostSetting();
  };
}

// Fill the Valuation block for one asset (null = a new asset). Hidden unless
// the login may see cost; read-only unless it may edit. A cost doc that is not
// there yet falls back to what an unmigrated asset still carries.
async function _loadAssetValuation(asset) {
  const block = document.getElementById('asset-valuation-block');
  const pp = document.getElementById('asset-purchase-price');
  const cv = document.getElementById('asset-current-value');
  const sum = document.getElementById('asset-cost-summary');
  if (!block || !pp || !cv) return;
  pp.value = ''; cv.value = '';
  if (sum) sum.textContent = '';
  RosterState._valuationLoaded = null;
  const avgGroup = document.getElementById('asset-avg-cost-group');
  const avgIn = document.getElementById('asset-avg-unit-cost');
  if (avgGroup) avgGroup.style.display = 'none';
  if (avgIn) avgIn.value = '';
  _resetAssetBilling(); // TASK-9725 S2 UI — the Billing section below Valuation
  const acc = _assetCostAccess();
  block.style.display = acc.see ? '' : 'none';
  pp.disabled = !acc.edit; cv.disabled = !acc.edit;
  if (!acc.see) return;
  if (!asset) { RosterState._valuationLoaded = { assetId: null, purchasePrice: null, currentValue: null }; return; }
  let item = null;
  let costState = null;
  let billCfg = null;
  try {
    const [snap, cs, billSnap] = await Promise.all([
      getDoc(doc(db, 'tenants', window.AppState?.tenant, 'assetCosts', `item_${asset.id}`)),
      _supplyCostState(),
      getDoc(doc(db, 'tenants', window.AppState?.tenant, 'assetCosts', 'ppe_billing')), // TASK-9725
    ]);
    item = snap.exists() ? snap.data() : null;
    costState = cs;
    const bi = billSnap.exists() ? (billSnap.data()?.items || {})[asset.id] : null;
    billCfg = bi && bi.mode === 'customer' ? { mode: 'customer', itemCode: String(bi.itemCode || '') } : { mode: 'company', itemCode: '' };
  } catch (e) {
    console.warn('[Roster] cost read failed:', e?.code || e?.message || e);
    if (sum) sum.textContent = 'Cost could not be loaded just now.';
    pp.disabled = true; cv.disabled = true; // never save over a value we could not read
    return;
  }
  // The modal may have moved on to another asset while we read.
  if (document.getElementById('asset-id')?.value !== asset.id) return;
  const src = item || asset; // unmigrated asset: legacy fields, until the migration runs
  const num = (v) => (typeof v === 'number' && isFinite(v) ? v : null);
  const loaded = { assetId: asset.id, purchasePrice: num(src.purchasePrice), currentValue: num(src.currentValue) };
  RosterState._valuationLoaded = loaded;
  // TASK-9725 — Bill to customer (supply items). Billing / owner edit; BI reads.
  // S2 UI: its own Billing section (_fillAssetBilling), Item Code picker + hint.
  _fillAssetBilling(asset, acc, billCfg, loaded);
  pp.value = loaded.purchasePrice == null ? '' : loaded.purchasePrice;
  cv.value = loaded.currentValue == null ? '' : loaded.currentValue;
  if (sum && (asset.trackingMode || 'quantity') === 'quantity') {
    const avg = num(src.avgUnitCost);
    const recv = num(src.totalReceived);
    sum.textContent = avg == null
      ? 'Average unit cost: unknown (no costed receipts yet)'
      : `Average unit cost: ${_fmtCost(avg)}${recv ? ` (from ${recv.toLocaleString('en-US')} received)` : ''}`;
    // TASK-9724 — while supply cost is tracked: the on-hand value, and the
    // average (what each sign-out is costed at) is editable by the owner /
    // Billing through setAssetValuation (audited old -> new in the cost home).
    if (costState && costState.on && avgGroup && avgIn) {
      avgGroup.style.display = '';
      avgIn.disabled = !acc.edit;
      avgIn.value = avg == null ? '' : avg;
      loaded.avgUnitCost = avg;
      const onHand = Number(asset.quantity) || 0;
      if (avg != null && asset.stockTracked === true) sum.textContent += ` · On hand value: ${_fmtCost(Math.round(avg * Math.max(0, onHand) * 100) / 100)}`;
    }
  }
}

// =============================================================================
// TASK-9725 S2 UI — the Billing section on a supply item. The picker, hint and
// Track-stock gate live in the LAZY views/rosterSupplyBilling.js (integ/2100,
// perf budget appColdLoadGzip); the setting is still saved by
// _saveAssetValuation through setSupplyBillMode exactly as before (same ids:
// asset-bill-mode / asset-bill-item-code / asset-bill-mode-group). The module
// sets loaded.bill only once the inputs are filled, so a save before it lands
// leaves the billing choice alone. Deps are handed over, never imported.
// =============================================================================
function _resetAssetBilling() {
  const block = document.getElementById('asset-billing-block');
  if (block) block.style.display = 'none';
  const hint = document.getElementById('asset-bill-hint');
  if (hint) hint.textContent = '';
  RosterState._assetBillingCtx = null;
}
let _supplyBillMod = null;
let _supplyBillLoad = null;
function _supplyBillDeps() { return { RosterState, db, getDocs, collection, escapeHtml }; }
function _loadSupplyBillMod() {
  if (_supplyBillMod) return Promise.resolve(_supplyBillMod);
  if (!_supplyBillLoad) _supplyBillLoad = __ddChunkWrap(import('./rosterSupplyBilling.js?v=9725202')).then((m) => (_supplyBillMod = m)).catch((e) => { _supplyBillLoad = null; console.warn('[Roster] rosterSupplyBilling load failed:', e?.message || e); return null; });
  return _supplyBillLoad;
}
function _fillAssetBilling(asset, acc, billCfg, loaded) {
  if (!billCfg || !asset || !loaded) return;
  if ((asset.trackingMode || 'quantity') !== 'quantity') return; // serialized gear is never billed per sign-out
  _loadSupplyBillMod().then((m) => {
    if (!m || document.getElementById('asset-id')?.value !== asset.id) return; // modal moved on while we loaded
    m.fillAssetBilling(asset, acc, billCfg, loaded, _supplyBillDeps());
  });
}
function onAssetBillModeChange() { _loadSupplyBillMod().then((m) => { if (m) m.onAssetBillModeChange(_supplyBillDeps()); }); }
function onAssetBillCodeInput() { _loadSupplyBillMod().then((m) => { if (m) m.onAssetBillCodeInput(_supplyBillDeps()); }); }
function onAssetBillCodeChange() { _loadSupplyBillMod().then((m) => { if (m) m.onAssetBillCodeChange(_supplyBillDeps()); }); }

// After the asset doc saved: send a changed purchase price / current value to
// the server. Never blocks the asset save; says so if the value did not save.
async function _saveAssetValuation(assetId) {
  const acc = _assetCostAccess();
  const loaded = RosterState._valuationLoaded;
  if (!acc.edit || !assetId || !loaded || (loaded.assetId && loaded.assetId !== assetId)) return;
  const read = (id) => { const v = document.getElementById(id)?.value; return v === '' || v == null ? null : parseFloat(v); };
  const want = { purchasePrice: read('asset-purchase-price'), currentValue: read('asset-current-value') };
  // TASK-9724 — only when the average field was shown (switch on) and loaded.
  if (Object.prototype.hasOwnProperty.call(loaded, 'avgUnitCost') && document.getElementById('asset-avg-cost-group')?.style.display !== 'none') {
    want.avgUnitCost = read('asset-avg-unit-cost');
  }
  const args = { assetId };
  for (const k of Object.keys(want)) {
    const v = want[k];
    if (v != null && (!isFinite(v) || v < 0)) { window.showToast?.('Asset saved. The value was not saved: enter dollar amounts of 0 or more.', 'error'); return; }
    if (v !== loaded[k]) args[k] = v;
  }
  // TASK-9725 — Bill to customer: its own audited callable, only when changed.
  if (loaded.bill && document.getElementById('asset-bill-mode-group')?.style.display !== 'none') {
    const mode = document.getElementById('asset-bill-mode')?.value === 'customer' ? 'customer' : 'company';
    const itemCode = mode === 'customer' ? String(document.getElementById('asset-bill-item-code')?.value || '').trim() : '';
    if (mode !== loaded.bill.mode || itemCode !== loaded.bill.itemCode) {
      try {
        await httpsCallable(functions, 'setSupplyBillMode')({ assetId, mode, itemCode });
        if (mode === 'customer' && !itemCode) window.showToast?.('Saved. With no Price Book item code, billed lines show "not in Price Book" until one is set.', 'info');
      } catch (e) {
        console.error('[Roster] setSupplyBillMode failed:', e);
        window.showToast?.(`Asset saved. The billing setting was not saved: ${e?.message || 'try again'}.`, 'error');
      }
    }
  }
  if (Object.keys(args).length === 1) return;
  try {
    await httpsCallable(functions, 'setAssetValuation')(args);
  } catch (e) {
    console.error('[Roster] setAssetValuation failed:', e);
    window.showToast?.(`Asset saved. The value was not saved: ${e?.message || 'try again'}.`, 'error');
  }
}

function showAddAsset() {
  document.getElementById('modal-asset-title').textContent = 'Add Asset';
  document.getElementById('form-asset').reset();
  document.getElementById('asset-id').value = '';
  document.getElementById('asset-quantity').min = 0; // TASK-9263 (editAsset may lower it)
  document.getElementById('asset-active').checked = true;
  document.getElementById('asset-show-in-dispatch').checked = true;
  populateTypeDropdown('asset-category', 'assetCategories', false, { inUse: RosterState.assets }); // TASK-9339
  document.getElementById('asset-tracking-mode').value = 'quantity';
  document.getElementById('asset-quantity-type').value = 'standard';
  document.getElementById('asset-unit-weight').value = '';
  // Reset rental fields
  document.getElementById('asset-is-rental').checked = false;
  document.getElementById('asset-require-return-inspection').checked = false;
  toggleTrackingMode();
  _applyStockTrackedAssetForm(null); // TASK-9720
  _applySerialPpeAssetForm(null); // TASK-9728
  _loadAssetValuation(null); // TASK-9575 slice D
  // Reset hours tracking
  document.getElementById('asset-hours-enabled').checked = false;
  document.getElementById('asset-current-hours').value = '';
  document.getElementById('asset-pm-interval').value = '';
  document.getElementById('asset-last-pm-hours').value = '';
  toggleAssetHoursFields();
  // Yard dropdown
  populateYardDropdown('asset-yard', false);
  // Reset photo state
  if (RosterState.assetPhoto.pending?.previewUrl) {
    URL.revokeObjectURL(RosterState.assetPhoto.pending.previewUrl);
  }
  RosterState.assetPhoto = { pending: null, existing: null };
  renderAssetPhotoPreview();
  // TASK-1297 — reset general documents for a fresh asset
  clearGeneralDocs('asset');
  // Hide transfer button for new assets
  const _addTransBtn = document.getElementById('btn-asset-transfer');
  if (_addTransBtn) _addTransBtn.style.display = 'none';
  // TASK-1141 — no thread on an unsaved asset (no doc id yet)
  window.EquipmentNotes?.unmount('asset-notes-thread');
  _rentalLoad('asset', null); // TASK-9869
  const modal = document.getElementById('modal-asset');
  // Clear any lingering Smart Assistant warnings
  modal.querySelectorAll('.smart-warning').forEach(el => el.remove());
  modal.classList.remove('hidden');
  // Reset scroll position to top
  const modalBody = modal.querySelector('.modal-body');
  if (modalBody) modalBody.scrollTop = 0;
}

function editAsset(id) {
  const asset = RosterState.assets.find(a => a.id === id);
  if (!asset) return;

  // TASK-1141 — per-unit notes thread under the legacy Notes field
  window.EquipmentNotes?.mount('asset-notes-thread', {
    kind: 'asset',
    id,
    unitLabel: asset.name || '',
  });

  document.getElementById('modal-asset-title').textContent = 'Edit Asset';
  document.getElementById('asset-id').value = id;
  RosterState.assetFormBase = { id, quantity: asset.quantity, active: asset.active !== false }; // TASK-9375 S4b
  document.getElementById('asset-part-number').value = asset.partNumber || '';
  document.getElementById('asset-name').value = asset.name || '';
  populateTypeDropdown('asset-category', 'assetCategories', false, { inUse: RosterState.assets, keep: asset.category }); // TASK-9339
  const assetCatSelect = document.getElementById('asset-category');
  assetCatSelect.value = asset.category || 'other';
  document.getElementById('asset-tracking-mode').value = asset.trackingMode || 'quantity';
  document.getElementById('asset-quantity-type').value = asset.quantityType || (asset.consumable ? 'consumable' : 'standard');
  document.getElementById('asset-unit-weight').value = asset.unitWeight || '';
  // TASK-9263 S2 — a stored 0 loads as 0 (was blank, so Save refused an untouched field).
  document.getElementById('asset-quantity').value = asset.quantity ?? '';
  // COD r1: min="0" let one spinner step snap a stored -17 to 0 (a real edit that wipes it).
  document.getElementById('asset-quantity').min = Math.min(0, Number(asset.quantity) || 0);
  document.getElementById('asset-serial-number').value = asset.serialNumber || '';
  document.getElementById('asset-manufacturer').value = asset.manufacturer || '';
  document.getElementById('asset-notes').value = asset.notes || '';
  // TASK-9575 slice D — cost comes from the cost home, only for owner / BI / Billing.
  _loadAssetValuation(asset);
  document.getElementById('asset-active').checked = asset.active !== false;
  document.getElementById('asset-show-in-dispatch').checked = asset.showInDispatch !== false;

  // Yard dropdown
  populateYardDropdown('asset-yard', false);
  document.getElementById('asset-yard').value = asset.yardId || '';

  // Load existing photo
  if (RosterState.assetPhoto.pending?.previewUrl) {
    URL.revokeObjectURL(RosterState.assetPhoto.pending.previewUrl);
  }
  RosterState.assetPhoto = {
    pending: null,
    existing: asset.photoUrl ? { url: asset.photoUrl } : null
  };
  renderAssetPhotoPreview();

  // TASK-1297 — load existing general documents
  loadGeneralDocs('asset', asset);

  // Rental fields
  document.getElementById('asset-is-rental').checked = asset.isRental || false;
  document.getElementById('asset-require-return-inspection').checked = asset.requireReturnInspection || false;

  toggleTrackingMode();
  _applyStockTrackedAssetForm(asset); // TASK-9720
  _applySerialPpeAssetForm(asset); // TASK-9728

  // Populate hours tracking fields
  const hoursEnabled = asset.hoursTracking?.enabled || false;
  document.getElementById('asset-hours-enabled').checked = hoursEnabled;
  document.getElementById('asset-current-hours').value = asset.hoursTracking?.currentHours || '';
  document.getElementById('asset-pm-interval').value = asset.hoursTracking?.pmInterval || '';
  document.getElementById('asset-last-pm-hours').value = asset.hoursTracking?.lastPmHours || '';
  toggleAssetHoursFields();

  // Show transfer button for quantity-based assets when yards exist
  const _transBtn = document.getElementById('btn-asset-transfer');
  if (_transBtn) {
    const _hasYards = getYardsList().length > 0;
    _transBtn.style.display = (_hasYards && (asset.trackingMode === 'quantity')) ? '' : 'none';
  }

  _rentalLoad('asset', asset); // TASK-9869
  // HOTFIX 2026-11-04 — reused modal: drop any Smart Assist banner computed from the
  // previous record's values (J-1361's notes were offered as Auto-fix on J-1355).
  window.SmartAssistant?.clearAll?.(document.getElementById('modal-asset'));
  document.getElementById('modal-asset').classList.remove('hidden');
}

// TASK-9375 S4b — the edit form holds `quantity` / `active` as they were when
// it OPENED. Sent back absolute from a stale tab, they would re-add units an org
// transfer (or another tab, a job, a ticket) moved out since, or re-activate the
// copy an org transfer left inactive. So, per field: unchanged from the form's
// opening value -> not sent at all; changed -> written in a transaction that
// re-reads the doc and refuses ("changed — reload") when the stored value moved
// since the form opened (a stored value already equal to the form's is a no-op).
// Returns false when refused (nothing written). Every other field saves as before.
const _assetFormVal = (k, v) => (k === 'active' ? v !== false : v == null ? null : (Number(v) || 0)); // null (individual) != 0
async function _updateAssetFromForm(tenant, id, data) {
  const ref = doc(db, 'tenants', tenant, 'assets', id);
  const base = RosterState.assetFormBase && RosterState.assetFormBase.id === id ? RosterState.assetFormBase : null;
  const keys = base ? ['quantity', 'active'].filter((k) => k in data) : [];
  const changed = keys.filter((k) => _assetFormVal(k, data[k]) !== _assetFormVal(k, base[k]));
  for (const k of keys) if (!changed.includes(k)) delete data[k];
  // TASK-10067 item 2 — a count typed on a CONSUMABLE goes through the server
  // (rules refuse a client quantity change on one): the same "changed since you
  // opened it" refusal, plus a ledger row. The rest of the form then saves below.
  let _srvQty;
  if (changed.includes('quantity') && ((RosterState.assets || []).find((a) => a.id === id) || {}).consumable === true) {
    let res;
    try {
      // One request id per open form (COD r2): a Save retried after a lost answer replays, never re-applies.
      if (!base._qtyReqId) base._qtyReqId = 'f' + Date.now().toString(36) + Math.random().toString(36).slice(2, 10);
      res = await withWriteTimeout(httpsCallable(functions, 'adjustAssetQuantity')({ kind: 'edit', assetId: id, from: _assetFormVal('quantity', base.quantity), to: _assetFormVal('quantity', data.quantity), tenantId: tenant, requestId: base._qtyReqId }), 15000, 'update count');
    } catch (err) {
      if (!/^functions\/(failed-precondition|not-found|invalid-argument)$/.test(String(err?.code || ''))) throw err;
      window.showToast?.(err.message, 'error');
      return false;
    }
    _srvQty = res && res.data ? res.data.balance : data.quantity;
    changed.splice(changed.indexOf('quantity'), 1);
    delete data.quantity;
  }
  if (!changed.length) {
    await withWriteTimeout(updateDoc(ref, data), 15000, 'update asset');
    if (_srvQty !== undefined) data.quantity = _srvQty; // the caller's optimistic copy
    return true;
  }
  const r = await withWriteTimeout(runTransaction(db, async (tx) => { // may retry: never mutates `data`
    const snap = await tx.get(ref);
    if (!snap.exists()) return { refused: 'This item was deleted. Close the form and reload.' };
    const cur = snap.data();
    // TASK-10067 item 2 — it became a consumable since this tab loaded: rules refuse the client count write
    if (changed.includes('quantity') && cur.consumable === true) return { refused: 'This item\'s count or status changed since you opened it. Close and reopen it to see the new numbers, then save again.' };
    // rules refuse this (org-left copy stays inactive): say why instead of a bare permission error
    if (data.active === true && changed.includes('active') && cur.orgTransferredTo) return { refused: 'This item was moved to another branch. Only a transfer back from that branch can make it active here again.' };
    const out = { ...data };
    for (const k of changed) {
      if (_assetFormVal(k, cur[k]) === _assetFormVal(k, out[k])) delete out[k];
      else if (_assetFormVal(k, cur[k]) !== _assetFormVal(k, base[k])) return { refused: 'This item\'s count or status changed since you opened it. Close and reopen it to see the new numbers, then save again.' };
    }
    tx.update(ref, out);
    return { out };
  }), 15000, 'update asset');
  if (r.refused) { window.showToast?.(r.refused, 'error'); return false; }
  for (const k of changed) if (!(k in r.out)) delete data[k];
  if (_srvQty !== undefined) data.quantity = _srvQty;
  return true;
}

async function saveAsset(e) {
  e.preventDefault();
  if (!window.validateRequiredFields(e.target)) return;

  // TASK-9378 COD — a photo still converting settles OUT HERE, not inside the
  // guard (its 20s hand-off would re-open Save mid-conversion → duplicate doc).
  if (!(await _settlePhotoBeforeSave(RosterState.assetPhoto, 'saveAsset', 'btn-save-asset'))) return;
  return runGuardedSave('saveAsset', async () => {
  const id = document.getElementById('asset-id').value;
  const trackingMode = document.getElementById('asset-tracking-mode').value;
  // TASK-9720 — a stock-tracked item's count is server-maintained; its
  // quantity box is read-only and is never validated or sent from here.
  const _stockTracked = !!(id && (RosterState.assets.find(a => a.id === id) || {}).stockTracked === true);

  // Validate required fields based on tracking mode
  if (trackingMode === 'quantity' && !_stockTracked) {
    // TASK-9263 S2 — a count already on the item (0, or below zero after jobs
    // and tickets drew it down) is not bad input: an unchanged Quantity is not
    // even sent (_updateAssetFromForm), so the name / yard / photo / notes save.
    // Refusals name the field and the reason.
    const qtyErr = _assetQtyFormError(document.getElementById('asset-quantity').value, id ? RosterState.assetFormBase : null, id);
    if (qtyErr) {
      window.showToast?.(qtyErr, 'error');
      return;
    }
  } else {
    const serial = document.getElementById('asset-serial-number').value.trim();
    if (!serial) {
      window.showToast?.('Please enter a serial number', 'error');
      return;
    }
  }

  const _clamp = window.clampText || ((s, n) => s && s.length > n ? s.slice(0, n) : s || '');
  const data = {
    partNumber: _clamp(document.getElementById('asset-part-number').value.trim(), 100),
    name: _clamp(document.getElementById('asset-name').value.trim(), 100),
    category: document.getElementById('asset-category').value,
    trackingMode: trackingMode,
    manufacturer: document.getElementById('asset-manufacturer').value.trim(),
    notes: document.getElementById('asset-notes').value.trim(),
    // TASK-9575 slice D — NO purchasePrice / currentValue here: firestore.rules
    // refuse a cost field on an asset doc. _saveAssetValuation sends them.
    active: document.getElementById('asset-active').checked,
    showInDispatch: document.getElementById('asset-show-in-dispatch')?.checked !== false,
    updatedAt: Timestamp.now()
  };

  // Yard
  const assetYardEl = document.getElementById('asset-yard');
  if (assetYardEl) {
    data.yardId = assetYardEl.value || '';
    data.yardName = data.yardId ? getYardLabel(data.yardId) : '';
  }

  // Unit weight (applies to all tracking modes)
  data.unitWeight = parseInt(document.getElementById('asset-unit-weight').value) || null;

  // Rental options (applies to both tracking modes)
  data.requireReturnInspection = document.getElementById('asset-require-return-inspection')?.checked || false;

  // Set tracking-specific fields
  if (trackingMode === 'quantity') {
    const quantityType = document.getElementById('asset-quantity-type').value || 'standard';
    data.quantity = parseInt(document.getElementById('asset-quantity').value) || 0;
    data.quantityType = quantityType;
    data.consumable = quantityType === 'consumable'; // backward compat
    data.serialNumber = null;
    data.isRental = false;
    // Init deployedQty for new rental assets
    if (quantityType === 'rental' && !id) {
      data.deployedQty = 0;
    }
  } else {
    data.serialNumber = document.getElementById('asset-serial-number').value.trim();
    data.quantityType = null;
    data.consumable = false;
    data.quantity = null;
    data.isRental = document.getElementById('asset-is-rental')?.checked || false;
    // Init deployment status for new individual rental assets
    if (data.isRental && !id) {
      data.deploymentStatus = 'available';
      data.deployedToJobId = null;
    }
  }

  // Hours tracking
  const hoursEnabled = document.getElementById('asset-hours-enabled').checked;
  if (hoursEnabled) {
    data.hoursTracking = {
      enabled: true,
      currentHours: parseFloat(document.getElementById('asset-current-hours').value) || 0,
      pmInterval: parseInt(document.getElementById('asset-pm-interval').value) || 250,
      lastPmHours: parseFloat(document.getElementById('asset-last-pm-hours').value) || 0
    };
  } else {
    data.hoursTracking = { enabled: false };
  }

  // TASK-9720 — never send the count (or anything that would re-shape the
  // item) for a stock-tracked asset: rules refuse a client quantity change on
  // it, and the ledger is the only writer. Name, yard, photo, notes still save.
  if (_stockTracked) {
    delete data.quantity;
    delete data.quantityType;
    delete data.consumable;
    delete data.trackingMode;
    delete data.serialNumber;
    delete data.isRental;
  }

  // TASK-9720 S1 #2 — Safety Gear ↔ HR > PPE, both ways. A Safety Gear item
  // becomes an issuable PPE item in HR, linked to this asset (the server does
  // the linking: onSafetyGearAssetWrite). Here we only ask the two questions a
  // person has to answer: link to a same-named PPE item that already exists
  // (never a duplicate), and — when moving OUT of Safety Gear — keep it issuable?
  const _gearPrev = id ? (RosterState.assets.find(a => a.id === id) || null) : null;
  const _gearNow = data.category === 'safety_gear';
  const _gearWas = !!(_gearPrev && _gearPrev.category === 'safety_gear');
  if (_gearNow && (!_gearWas || (_gearPrev && _gearPrev.name !== data.name) || (_gearPrev && _gearPrev.ppeIssuable === false))) {
    const _ans = await _askSafetyGearLink(id, data.name);
    if (_ans === 'cancel') return;
    data.ppeIssuable = _ans !== 'decline';
  } else if (_gearWas && !_gearNow && _gearPrev.ppeIssuable !== false) {
    const _keep = await _askKeepSafetyGearIssuable(_gearPrev);
    if (_keep === 'cancel') return;
    if (_keep === 'remove') data.ppeIssuable = false;
  }

  // Soft-duplicate check for new assets
  if (!id && data.name) {
    const lastSaved = window.__rosterLastSavedAsset;
    if (lastSaved && lastSaved.name === data.name && (Date.now() - lastSaved.ts) < 3000) {
      const proceed = confirm(`You just added an asset named "${data.name}" a moment ago. Are you sure you want to add another?`);
      if (!proceed) return;
    }
  }

    const tenant = window.AppState?.tenant || 'demo';

    // Upload photo if pending
    const photoUrl = await uploadAssetPhoto(tenant);
    data.photoUrl = photoUrl;

    // TASK-1297 — upload general documents (equipment/asset docs) and persist
    // the documents[] array on the asset doc (mirrors truck/trailer save).
    const generalUploaded = await uploadGeneralDocs('asset', tenant);
    data.documents = [...RosterState.assetGeneralDocs.existing, ...generalUploaded];

    // TASK-9869 — rented-in markers (no money) onto the asset doc.
    if (!_rentalApplyMarkers('asset', data)) return;
    let _rentalUnitId = id;
    let _savedId = id; // TASK-9575 slice D valuation target
    if (id) {
      if (!(await _updateAssetFromForm(tenant, id, data))) return; // TASK-9375 S4b
      window.showToast?.('Asset updated', 'success');
    } else {
      data.createdAt = Timestamp.now();
      const newAssetRef = await withWriteTimeout(addDoc(collection(db, 'tenants', tenant, 'assets'), data), 15000, 'create asset');
      _rentalUnitId = newAssetRef?.id || null;
      _savedId = _rentalUnitId;
      window.showToast?.('Asset added', 'success');
      window.__rosterLastSavedAsset = { name: data.name, ts: Date.now() };
    }
    await _saveAssetValuation(_savedId); // TASK-9575 slice D

    await _rentalSaveRecord('asset', _rentalUnitId, data); // TASK-9869

    // Optimistically update local state so card refreshes immediately
    if (id) {
      const idx = RosterState.assets.findIndex(a => a.id === id);
      if (idx !== -1) Object.assign(RosterState.assets[idx], data);
    }
    closeModal('asset');
    renderAssetsList();
  }, { buttonId: 'btn-save-asset', restoreLabel: 'Save' });
}

// TASK-9263 S2 — PURE. The Quantity box's refusal text, or '' when it may save.
// A negative may only stay as the item's stored count (the form's opening value);
// typing a new negative is refused — stock going out is recorded with Adjust.
function _assetQtyFormError(raw, base, id) {
  const txt = String(raw ?? '').trim();
  const n = Number(txt);
  if (!txt || !Number.isFinite(n)) return 'Quantity is blank (0 is fine).';
  if (n < 0) {
    const stored = base && id && base.id === id && base.quantity != null ? Number(base.quantity) : NaN;
    if (n !== stored) return 'Quantity can\'t be typed below 0. Use Adjust for items going out.';
  }
  return '';
}

function deleteAsset(id) { return _unitDeactivate('deactivateAsset', id); }

function toggleTrackingMode() {
  const mode = document.getElementById('asset-tracking-mode').value;
  const qtyGroup = document.getElementById('asset-quantity-group');
  const serialGroup = document.getElementById('asset-serial-group');
  const qtyTypeGroup = document.getElementById('asset-quantity-type-group');

  const indRentalGroup = document.getElementById('asset-individual-rental-group');
  if (mode === 'quantity') {
    qtyGroup.classList.remove('hidden');
    serialGroup.classList.add('hidden');
    if (qtyTypeGroup) qtyTypeGroup.classList.remove('hidden');
    if (indRentalGroup) indRentalGroup.classList.add('hidden');
    document.getElementById('asset-quantity').required = true;
    document.getElementById('asset-serial-number').required = false;
  } else {
    qtyGroup.classList.add('hidden');
    serialGroup.classList.remove('hidden');
    if (qtyTypeGroup) qtyTypeGroup.classList.add('hidden');
    if (indRentalGroup) indRentalGroup.classList.remove('hidden');
    document.getElementById('asset-quantity').required = false;
    document.getElementById('asset-serial-number').required = true;
  }
  updateAssetHoursVisibility();
}

function updateAssetHoursVisibility() {
  const trackingMode = document.getElementById('asset-tracking-mode')?.value || 'quantity';
  const qtyType = document.getElementById('asset-quantity-type')?.value || 'standard';
  const isIndividualRental = document.getElementById('asset-is-rental')?.checked || false;
  const hoursSection = document.getElementById('asset-hours-section');
  if (hoursSection) {
    hoursSection.style.display = qtyType === 'consumable' ? 'none' : '';
  }
  // Show rental options when quantityType is 'rental' OR individual asset with isRental checked
  const rentalOptions = document.getElementById('asset-rental-options');
  if (rentalOptions) {
    const showRental = (trackingMode === 'quantity' && qtyType === 'rental') || (trackingMode === 'individual' && isIndividualRental);
    rentalOptions.classList.toggle('hidden', !showRental);
  }
}

function toggleAssetHoursFields() {
  const enabled = document.getElementById('asset-hours-enabled').checked;
  const fields = document.getElementById('asset-hours-fields');
  if (enabled) {
    fields.classList.remove('hidden');
  } else {
    fields.classList.add('hidden');
  }
}

// ============================================
// ASSET PHOTO UPLOAD
// ============================================
// ── TASK-9302 — one photo gate for every roster photo selector ──────────────
// TITS 2026-09-10: "Truck pictures won't upload in vehicle profile." The four
// selectors below (truck/trailer via _handleEquipmentPhotoSelect, person,
// asset) each hard-coded ['image/jpeg','image/png','image/webp'] and toasted a
// reject for anything else — which threw out image/heic + image/heif (iPhone
// "Keep Originals", Samsung HEIF) AND any file whose `type` is EMPTY (desktop
// Chrome/Edge hand '' for a .heic with no HEIF codec; some Android pickers hand
// '' for everything). Meanwhile the uploaders right below each already carry a
// HEIC/HEIF branch (by extension) the selector could never reach, and
// storage.rules isImage() already admits image/heic. Selector and uploader now
// agree: a file is a photo if its MIME type is one we upload, or — when the
// browser gives us NO type — its extension is one we upload. SVG stays out
// (stored-XSS, TASK-684). GIF/BMP/TIFF were kept out until TASK-9402 (below). Nothing beyond
// image types is admitted here.
//
// TASK-9402 — the <input accept> hints were jpeg/png/webp ON PURPOSE until
// 2026-09-28: iOS Safari transcodes HEIC → JPEG for an input that does not list
// image/heic, which was the only HEIC fix we had. Chris opened them on 9/28
// ("yes open it up") to `image/*,.heic,.heif`: HEIC→JPEG conversion now runs on
// every pick (TASK-9378 _acceptPhotoIntoSlot), and the narrow hint hid .heic
// files entirely from Windows and Android pickers. Any image is admitted here
// EXCEPT SVG (stored-XSS, TASK-684); a format this browser can't draw (TIFF on
// Chrome) is refused at pick time with PHOTO_REJECT_MSG, and every upload is
// still re-encoded to a ≤1200px JPEG by processPhoto.
const PHOTO_ACCEPTED_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/heic', 'image/heif'];
const PHOTO_ACCEPTED_EXTS = ['.jpg', '.jpeg', '.png', '.webp', '.heic', '.heif', '.gif', '.bmp', '.avif', '.tif', '.tiff'];
const PHOTO_REJECT_MSG = 'That file type cannot be used as a photo. Pick a JPG or PNG instead.';
function isAcceptedPhotoFile(file) {
  if (!file) return false;
  const type = String(file.type || '').toLowerCase();
  if (type) return PHOTO_ACCEPTED_TYPES.includes(type) || (type.startsWith('image/') && !type.includes('svg'));
  const name = String(file.name || '').toLowerCase();
  return PHOTO_ACCEPTED_EXTS.some(ext => name.endsWith(ext));
}

// TASK-9378 — iOS / some Android pickers hand a photo over as '' or
// application/octet-stream with a name that proves nothing. When the MIME/ext
// gate says no and the browser gave no real type, the magic bytes decide:
// HEIC/JPEG/PNG/WebP are admitted, anything else (SVG, PDF, unknown) is not.
async function acceptPhotoFile(file) {
  if (!file) return false;
  if (isAcceptedPhotoFile(file)) return true;
  const type = String(file.type || '').toLowerCase();
  if (type && type !== 'application/octet-stream') return false;
  const kind = await sniffFileKind(file);
  return kind === 'heic' || kind === 'jpeg' || kind === 'png' || kind === 'webp';
}

// TASK-9378 — the one path from a picked file to a slot's `pending`. A HEIC the
// browser can't draw (Windows Chrome/Firefox) is converted to JPEG HERE, before
// the preview, so the thumbnail the user sees is the file that gets uploaded.
// A failed conversion toasts an actionable message and leaves the slot as it
// was — a raw HEIC is never parked for upload. `slot.converting` lets the
// uploader wait out a conversion still in flight when Save is clicked, and lets
// a later pick / a Remove supersede an earlier one.
// opts.maxBytes — optional raw-size cap (the asset selector keeps its 10MB).
async function _acceptPhotoIntoSlot(slot, file, render, opts = {}) {
  if (!slot || !file) return false;
  if (opts.maxBytes && typeof file.size === 'number' && file.size > opts.maxBytes) {
    window.showToast?.(`Image file too large (max ${Math.round(opts.maxBytes / (1024 * 1024))}MB)`, 'error');
    return false;
  }
  const job = (async () => {
    if (!(await acceptPhotoFile(file))) {
      window.showToast?.(PHOTO_REJECT_MSG, 'error');
      return false;
    }
    let usable;
    let converted = false;
    try {
      ({ file: usable, converted } = await normalizePhotoForUpload(file, {
        onConvertStart: () => window.showToast?.('Converting iPhone photo…', 'info'),
      }));
    } catch (err) {
      console.warn('[Roster] photo conversion failed:', err?.cause || err);
      if (slot.converting === job) {
        window.showToast?.(err?.userMessage || 'Could not read that photo — pick a JPG or PNG instead', 'error');
      }
      return false;
    }
    // TASK-9402 — the picker takes any image now. Anything that isn't a JPEG,
    // PNG or WebP must be drawable HERE (processPhoto re-encodes it through a
    // canvas at Save), or it is refused now instead of failing at Save.
    if (!converted && !['jpeg', 'png', 'webp'].includes(await sniffFileKind(usable))) {
      if (!(await canBrowserDecode(usable))) {
        if (slot.converting === job) window.showToast?.(PHOTO_REJECT_MSG, 'error');
        return false;
      }
    }
    if (slot.converting !== job) return false;   // superseded by a newer pick or a Remove
    if (slot.pending?.previewUrl) {
      try { URL.revokeObjectURL(slot.pending.previewUrl); } catch (_) {}
    }
    slot.pending = { file: usable, previewUrl: URL.createObjectURL(usable) };
    render();
    return true;
  })();
  slot.converting = job;
  try {
    return await job;
  } finally {
    if (slot.converting === job) slot.converting = null;
  }
}

function handleAssetPhoto(e) {
  const file = e.target.files?.[0];
  if (!file) return;
  e.target.value = '';
  // Asset photos kept imageProcessor.validateImageFile's 10MB raw cap; the type
  // half of that check is a superset of the shared gate, so only the cap stays.
  return _acceptPhotoIntoSlot(RosterState.assetPhoto, file, renderAssetPhotoPreview, { maxBytes: 10 * 1024 * 1024 });
}

// TASK-9378 COD — wait until the LATEST pick has settled. If photo B replaces
// photo A while we are waiting on A, slot.converting now points at B, so loop
// until the job we just awaited is still the current one (or none is running).
// Bounded: each conversion is itself bounded (load + convert timeouts), and a
// runaway re-pick loop stops after 20 rounds.
async function _waitForLatestPhoto(slot) {
  for (let i = 0; i < 20 && slot?.converting; i++) {
    const job = slot.converting;
    try { await job; } catch (_) {}
    if (!slot.converting || slot.converting === job) break;
  }
}

// TASK-9378 COD — settle a running conversion BEFORE runGuardedSave. The guard
// releases its button + in-flight lock after 20s on the assumption that a
// Firestore write is what's slow; a cold 3MB converter fetch + conversion can
// outlast that, and a second Save then ran another addDoc (duplicate truck /
// person / asset). So the wait happens out here: the Save button is disabled
// and reads "Converting photo…", and a second Save (Enter key, double tap)
// while we wait is refused rather than queued. Returns false when refused.
const _photoSettleWaits = new Set();
async function _settlePhotoBeforeSave(slot, key, buttonId) {
  if (_photoSettleWaits.has(key)) {
    window.showToast?.('Still converting the photo — Save will finish on its own', 'info');
    return false;
  }
  if (!slot?.converting) return true;
  _photoSettleWaits.add(key);
  const btn = buttonId && typeof document !== 'undefined' ? document.getElementById(buttonId) : null;
  const prevHtml = btn ? btn.innerHTML : null;
  const prevDisabled = btn ? btn.disabled : null;
  if (btn) {
    btn.disabled = true;
    btn.innerHTML = '<span class="btn-spinner"></span> Converting photo…';
  }
  try {
    await _waitForLatestPhoto(slot);
    return true;
  } finally {
    _photoSettleWaits.delete(key);
    if (btn) {
      btn.disabled = !!prevDisabled;
      btn.innerHTML = prevHtml;
    }
  }
}

// TASK-9378 — every roster uploader goes through this: wait out an in-flight
// conversion, make sure what we hold is drawable (a HEIC that somehow reached
// `pending` is converted now, never uploaded raw), then compress to JPEG.
async function _preparePendingPhotoBlob(slot, processOpts) {
  await _waitForLatestPhoto(slot);
  const pending = slot?.pending;
  if (!pending) return null;
  const { file } = await normalizePhotoForUpload(pending.file);
  return processPhoto(file, processOpts);
}

function removeAssetPhoto() {
  RosterState.assetPhoto.converting = null;
  if (RosterState.assetPhoto.pending?.previewUrl) {
    URL.revokeObjectURL(RosterState.assetPhoto.pending.previewUrl);
  }
  RosterState.assetPhoto.pending = null;
  RosterState.assetPhoto.existing = null;
  renderAssetPhotoPreview();
}

function renderAssetPhotoPreview() {
  const preview = document.getElementById('asset-photo-preview');
  const btn = document.getElementById('asset-photo-btn');
  if (!preview) return;

  const photo = RosterState.assetPhoto.pending || RosterState.assetPhoto.existing;
  if (photo) {
    const src = photo.previewUrl || photo.url;
    preview.innerHTML = `
      <div class="asset-photo-thumb">
        <img src="${src}" alt="Asset photo">
        <button type="button" class="btn-icon btn-icon-danger asset-photo-remove"
          onclick="window.RosterModule.removeAssetPhoto()" title="Remove photo">
          <span class="material-icons">close</span>
        </button>
      </div>
    `;
    preview.classList.remove('hidden');
    btn.innerHTML = '<span class="material-icons">swap_horiz</span> Change Photo';
  } else {
    preview.innerHTML = '';
    preview.classList.add('hidden');
    btn.innerHTML = '<span class="material-icons">add_a_photo</span> Add Photo';
  }
}

// TASK-340 Slice A — shared photo upload helper, parameterized by `kind`.
// Used by trucks and trailers (and could absorb asset later as a refactor,
// but leaving uploadAssetPhoto untouched to bound blast radius).
// kind: 'truck' | 'trailer' — controls state key + storage subpath.
async function _uploadEquipmentPhoto(tenant, kind) {
  const stateKey = kind === 'truck' ? 'truckPhoto' : 'trailerPhoto';
  const subPath = kind === 'truck' ? 'truck-photos' : 'trailer-photos';
  const slot = RosterState[stateKey];
  if (!slot?.pending && !slot?.converting) return slot?.existing?.url || null;
  try {
    // TASK-9378 — always a JPEG: a HEIC is converted (or refused), never uploaded raw.
    const blob = await _preparePendingPhotoBlob(slot, { maxDimension: 1200, quality: 0.8, maxSizeKB: 500 });
    if (!blob) return slot?.existing?.url || null;
    const fileName = `${Date.now()}_${kind}.jpg`;
    const storageRef = ref(storage, `tenants/${tenant}/${subPath}/${fileName}`);
    await uploadBytes(storageRef, blob, { contentType: 'image/jpeg' });
    return await getDownloadURL(storageRef);
  } catch (err) {
    console.error(`[Roster] Error uploading ${kind} photo:`, err);
    window.showToast?.(err?.userMessage ? `${err.userMessage} (${kind} saved without photo)` : `Photo upload failed — ${kind} saved without photo`, 'error');
    return slot?.existing?.url || null;
  }
}

function _handleEquipmentPhotoSelect(e, kind) {
  const file = e.target.files?.[0];
  if (!file) return;
  e.target.value = '';
  const stateKey = kind === 'truck' ? 'truckPhoto' : 'trailerPhoto';
  return _acceptPhotoIntoSlot(RosterState[stateKey], file, () => _renderEquipmentPhotoPreview(kind));
}

function _removeEquipmentPhoto(kind) {
  const stateKey = kind === 'truck' ? 'truckPhoto' : 'trailerPhoto';
  RosterState[stateKey].converting = null;
  if (RosterState[stateKey].pending?.previewUrl) {
    URL.revokeObjectURL(RosterState[stateKey].pending.previewUrl);
  }
  RosterState[stateKey].pending = null;
  RosterState[stateKey].existing = null;
  _renderEquipmentPhotoPreview(kind);
}

function _renderEquipmentPhotoPreview(kind) {
  const preview = document.getElementById(`${kind}-photo-preview`);
  if (!preview) return;
  const stateKey = kind === 'truck' ? 'truckPhoto' : 'trailerPhoto';
  const photo = RosterState[stateKey].pending || RosterState[stateKey].existing;
  if (photo) {
    const src = photo.previewUrl || photo.url;
    preview.innerHTML = `
      <div class="asset-photo-thumb">
        <img src="${src}" alt="${kind} photo">
        <button type="button" class="btn-icon btn-icon-danger asset-photo-remove"
          onclick="window.RosterModule.remove${kind === 'truck' ? 'Truck' : 'Trailer'}Photo()" title="Remove photo">
          <span class="material-icons">close</span>
        </button>
      </div>`;
    preview.classList.remove('hidden');
  } else {
    preview.innerHTML = '';
    preview.classList.add('hidden');
  }
}

// Public wrappers — surface-specific so existing onclick / onchange attribute
// patterns can call them directly without passing the `kind` argument.
async function uploadTruckPhoto(tenant) { return _uploadEquipmentPhoto(tenant, 'truck'); }
async function uploadTrailerPhoto(tenant) { return _uploadEquipmentPhoto(tenant, 'trailer'); }
function handleTruckPhoto(e) { return _handleEquipmentPhotoSelect(e, 'truck'); }
function handleTrailerPhoto(e) { return _handleEquipmentPhotoSelect(e, 'trailer'); }
function removeTruckPhoto() { return _removeEquipmentPhoto('truck'); }
function removeTrailerPhoto() { return _removeEquipmentPhoto('trailer'); }

// ── TASK-9234 follow-up — person photos resolve from a STORAGE PATH ─────────
// A Firebase download URL carries its own access token in the query string. It
// is a BEARER CREDENTIAL, not a path: anyone holding the string fetches the
// object with no Authorization header at all. Persisted onto a `people` doc it
// rides the whole-collection broadcast (jobboard.js:971) into every session,
// including driver-PIN kiosks — proven end to end on TASK-9234.
//
// So nothing here ever renders from a persisted URL. We resolve the storage
// path through the SDK at display time, which (a) keeps the credential out of
// Firestore and (b) makes a token rotation SAFE — after a rotation the resolver
// simply picks up the new token, whereas a persisted URL would be dead.
//
// Legacy rows carry only `profilePhotoUrl`. The PATH inside that URL is not the
// secret (the token is), so we derive the path from it and resolve that. That
// derivation is what lets rotation happen with ZERO backfill — see
// personPhotoStoragePath below.
const _personPhotoUrlCache = new Map();   // storagePath -> Promise<string|null>

// Pull the object path out of a firebasestorage.googleapis.com download URL.
// Returns null for anything else (a `*Url` field is client-writable, so treat
// it as untrusted — we never hand an arbitrary string to ref()).
function storagePathFromDownloadUrl(url) {
  if (!url || typeof url !== 'string') return null;
  if (!/^https:\/\/firebasestorage\.googleapis\.com\//i.test(url)) return null;
  try {
    const encoded = new URL(url).pathname.split('/o/')[1];
    if (!encoded) return null;
    const path = decodeURIComponent(encoded);
    if (!path || path.includes('..')) return null;
    return path;
  } catch (_) { return null; }
}

// The path a person's avatar should resolve from. Prefers the stored path;
// falls back to deriving it from the legacy URL so un-backfilled rows still
// render through the SDK (and therefore survive a token rotation).
function personPhotoStoragePath(person) {
  if (!person) return null;
  if (person.profilePhotoStoragePath) return person.profilePhotoStoragePath;
  return storagePathFromDownloadUrl(person.profilePhotoUrl);
}

// Storage paths whose last resolve was refused by the rules. A denial is NOT a
// transient miss: falling back to the persisted bearer URL after one would
// defeat the very read-gate that just said no (the rule viewComplianceDoc
// documents at hr.js — "NEVER fall back to legacyUrl on an ERROR"). We remember
// the denial so the fail-soft path can stay closed for that object.
const _personPhotoDenied = new Set();

// Memoized getDownloadURL. Resolves to null (never throws) so a denied or
// missing object degrades to initials instead of a broken image. A failure is
// evicted from the cache so a transient error retries on the next render.
function resolvePersonPhotoUrl(storagePath) {
  if (!storagePath) return Promise.resolve(null);
  if (!_personPhotoUrlCache.has(storagePath)) {
    let p;
    try {
      // ref() throws SYNCHRONOUSLY on a malformed path — outside any .catch().
      p = getDownloadURL(ref(storage, storagePath)).catch((err) => {
        const code = err?.code || '';
        if (code === 'storage/unauthorized') _personPhotoDenied.add(storagePath);
        console.warn('[Roster] person photo resolve failed:', storagePath, code || err?.message);
        _personPhotoUrlCache.delete(storagePath);
        return null;
      });
    } catch (err) {
      console.warn('[Roster] person photo ref() rejected the path:', storagePath, err?.message);
      return Promise.resolve(null);
    }
    _personPhotoUrlCache.set(storagePath, p);
  }
  return _personPhotoUrlCache.get(storagePath);
}

// Drop a resolved URL so the next render re-resolves it. Called when the
// browser fails to LOAD an image we just handed it — which is exactly what a
// token rotation looks like to a tab that was already open: the cached URL is
// syntactically fine and permanently dead, and without this eviction the
// avatars stay initials until a full reload.
function forgetPersonPhotoUrl(storagePath) {
  if (storagePath) _personPhotoUrlCache.delete(storagePath);
}

// Does this person have an avatar worth trying to paint?
function personHasPhoto(person) {
  return !!(personPhotoStoragePath(person) || person?.profilePhotoUrl);
}

// Markup for the inside of an avatar circle. Initials render IMMEDIATELY and
// the <img> stays hidden until it actually loads — so the resolve round-trip
// shows today's initials avatar rather than an empty circle, and a failed
// resolve leaves the initials in place rather than a broken-image glyph.
// `photoClass` differs per surface (roster card vs HR card) so each keeps its
// own existing CSS.
function personAvatarInnerHtml(person, photoClass, initials) {
  // Initials are first letters of a user-supplied name — escaped here, where
  // the old inline markup interpolated them raw.
  const safeInitials = escapeHtml(initials == null ? '' : String(initials));
  if (!personHasPhoto(person)) return safeInitials;
  const path = personPhotoStoragePath(person) || '';
  // Legacy fail-soft ONLY: used if the SDK resolve comes back empty.
  const legacy = /^https:\/\//i.test(person.profilePhotoUrl || '') ? person.profilePhotoUrl : '';
  // 🔴 NO `loading="lazy"` here, deliberately. A lazy image that is
  // `display:none` is never in the viewport, so the browser never loads it —
  // and we only reveal it on `load`. That is a deadlock: the avatar stays
  // hidden forever and no error is raised anywhere. Measured in the browser
  // 2026-09-16 (every avatar had a resolved src and naturalWidth 0). These are
  // 48px circles fetched only for rows we actually rendered, so eager is fine.
  return `<span data-person-photo-initials>${safeInitials}</span>`
    + `<img class="${escapeHtml(photoClass)}" alt="${escapeHtml(person.name || '')}"`
    + ` style="display:none"`
    + ` data-person-photo-path="${escapeHtml(path)}"`
    + ` data-person-photo-legacy="${escapeHtml(legacy)}">`;
}

// Resolve + paint every pending avatar under `root`. Idempotent — an <img>
// already handled is skipped, so a re-render is cheap and a partial render
// can't double-fetch. Exposed on window.RosterModule for hr.js (Pitfall #1 —
// never a cross-module ?v= import).
function hydratePersonPhotos(root) {
  const scope = root || document;
  const imgs = scope.querySelectorAll('img[data-person-photo-path]:not([data-person-photo-done])');
  return Promise.all(Array.from(imgs).map(async (img) => {
    img.setAttribute('data-person-photo-done', '1');
    const path = img.getAttribute('data-person-photo-path') || '';
    const legacy = img.getAttribute('data-person-photo-legacy') || '';
    let src = path ? await resolvePersonPhotoUrl(path) : null;
    let usedLegacy = false;
    // Fail soft: an un-resolvable path (offline, object missing, an old row we
    // could not parse a path out of) still paints from the URL we already had,
    // so a working screen never goes blank.
    // 🔴 But NOT after a REFUSAL. `storage/unauthorized` means the rules just
    // denied this viewer, and painting the bearer URL would walk straight
    // around the gate that said no — the same carve-out hr.js's
    // viewComplianceDoc spells out. Denied ⇒ initials, full stop.
    if (!src && legacy && !(path && _personPhotoDenied.has(path))) {
      src = legacy;
      usedLegacy = true;
    }
    if (!src || !img.isConnected) { img.removeAttribute('data-person-photo-done'); return; }
    img.onload = () => {
      const avatar = img.parentElement;
      if (avatar) {
        avatar.classList.add('has-photo');
        const ini = avatar.querySelector('[data-person-photo-initials]');
        if (ini) ini.style.display = 'none';
      }
      img.style.display = 'block';
    };
    img.onerror = () => {
      img.style.display = 'none';
      // A resolved URL that won't load is a dead token — drop it so the next
      // render re-resolves instead of re-painting the corpse. This is the
      // rotation case for a tab that was already open.
      if (!usedLegacy && path) {
        forgetPersonPhotoUrl(path);
        img.removeAttribute('data-person-photo-done');
      }
    };
    img.src = src;
  }));
}

// TASK-340 Slice B — person profile photo (separate from DL image to dodge
// the PII risk of surfacing license data on every roster card). Same shape
// as equipment photo. Storage: tenants/{tid}/person-photos/{ts}_person.jpg.
// TASK-9234 — returns the STORAGE PATH, not a download URL. The caller writes
// that path to Firestore; the URL is resolved at display time.
async function uploadPersonPhoto(tenant) {
  const slot = RosterState.personPhoto;
  if (!slot?.pending && !slot?.converting) return slot?.existing?.storagePath || null;
  try {
    // Smaller dimension cap than equipment — profile shots typically render at 48px.
    // TASK-9378 — always a JPEG: a HEIC is converted (or refused), never uploaded raw.
    const blob = await _preparePendingPhotoBlob(slot, { maxDimension: 600, quality: 0.85, maxSizeKB: 200 });
    if (!blob) return slot?.existing?.storagePath || null;
    const fileName = `${Date.now()}_person.jpg`;
    const storagePath = `tenants/${tenant}/person-photos/${fileName}`;
    const storageRef = ref(storage, storagePath);
    await uploadBytes(storageRef, blob, { contentType: 'image/jpeg' });
    return storagePath;
  } catch (err) {
    console.error('[Roster] Person photo upload error:', err);
    window.showToast?.(err?.userMessage ? `${err.userMessage} (person saved without photo)` : 'Photo upload failed — person saved without photo', 'error');
    return RosterState.personPhoto?.existing?.storagePath || null;
  }
}

function handlePersonPhoto(e) {
  const file = e.target.files?.[0];
  if (!file) return;
  e.target.value = '';
  return _acceptPhotoIntoSlot(RosterState.personPhoto, file, renderPersonPhotoPreview);
}

function removePersonPhoto() {
  RosterState.personPhoto.converting = null;
  if (RosterState.personPhoto.pending?.previewUrl) {
    URL.revokeObjectURL(RosterState.personPhoto.pending.previewUrl);
  }
  RosterState.personPhoto.pending = null;
  RosterState.personPhoto.existing = null;
  renderPersonPhotoPreview();
}

function renderPersonPhotoPreview() {
  const preview = document.getElementById('person-photo-preview');
  if (!preview) return;
  const photo = RosterState.personPhoto.pending || RosterState.personPhoto.existing;
  if (photo) {
    // TASK-9234 — src is ASSIGNED, never interpolated: a pending blob: preview
    // is local, and a saved photo is resolved from its storage path at display
    // time (never from the persisted bearer URL).
    preview.innerHTML = `
      <div class="asset-photo-thumb">
        <img id="person-photo-preview-img" alt="Profile photo">
        <button type="button" class="btn-icon btn-icon-danger asset-photo-remove"
          onclick="window.RosterModule.removePersonPhoto()" title="Remove photo">
          <span class="material-icons">close</span>
        </button>
      </div>`;
    const img = preview.querySelector('#person-photo-preview-img');
    if (img) {
      if (photo.previewUrl) {
        img.src = photo.previewUrl;
      } else {
        resolvePersonPhotoUrl(photo.storagePath).then((url) => {
          // Fail soft to the legacy URL so an old row still shows its photo.
          // 🔴 But NOT after a REFUSAL — same carve-out hydratePersonPhotos makes
          // twenty lines up, and the twin this preview was missing (COD Code
          // Reviewer + Independent, integrated-diff review 2026-09-16). A
          // `storage/unauthorized` means the rules just denied this viewer;
          // painting the bearer URL anyway walks straight around the gate that
          // said no, which is the exact reliance this whole change removes.
          const denied = photo.storagePath && _personPhotoDenied.has(photo.storagePath);
          const src = url || (denied ? '' : photo.url);
          if (src && img.isConnected) img.src = src;
        });
      }
    }
    preview.classList.remove('hidden');
  } else {
    preview.innerHTML = '';
    preview.classList.add('hidden');
  }
}

async function uploadAssetPhoto(tenant) {
  const slot = RosterState.assetPhoto;
  if (!slot?.pending && !slot?.converting) return slot?.existing?.url || null;

  try {
    // Compress via imageProcessor (strips EXIF, resizes to 1200px max).
    // TASK-9378 — always a JPEG: a HEIC is converted (or refused), never uploaded raw.
    const blob = await _preparePendingPhotoBlob(slot, { maxDimension: 1200, quality: 0.8, maxSizeKB: 500 });
    if (!blob) return slot?.existing?.url || null;
    const fileName = `${Date.now()}_asset.jpg`;
    const storageRef = ref(storage, `tenants/${tenant}/asset-photos/${fileName}`);
    await uploadBytes(storageRef, blob, { contentType: 'image/jpeg' });
    return await getDownloadURL(storageRef);
  } catch (err) {
    console.error('[Roster] Error uploading asset photo:', err);
    window.showToast?.(err?.userMessage ? `${err.userMessage} (asset saved without photo)` : 'Photo upload failed — asset saved without photo', 'error');
    return RosterState.assetPhoto.existing?.url || null;
  }
}

function filterAssets() {
  RosterState.filters.assets.belowZero = false; // TASK-9985 — a hand-made filter replaces the badge filter
  const searchEl = document.getElementById('search-assets');
  const categoryEl = document.getElementById('filter-assets-category');
  const yardEl = document.getElementById('filter-assets-yard');
  RosterState.filters.assets.search = searchEl?.value || '';
  RosterState.filters.assets.category = categoryEl?.value || 'all';
  // TASK-9369 — preserve '' (Unassigned). The old `yardEl.value || 'all'` turned
  // the Unassigned option into "no filter" on the very next keystroke, so a
  // drill into the Unassigned card was wiped by typing in search. The dropdown
  // has no options at all until the tenant has a yard (refreshYardFilters), and
  // an option-less <select> reads as '' too — that case must stay 'all'.
  RosterState.filters.assets.yard = (yardEl && yardEl.options.length > 0) ? yardEl.value : 'all';
  renderAssetsList();
}

// ============================================
// TASK-9985 — Roster badge says what it counts + opens exactly those items
// ============================================
// ONE list for the badge count, the below-zero banner and the badge's filtered
// grid (TASK-9275 predicate: same as the card tint).
function negativeStockAssets(assets) {
  return (assets || []).filter(a => a.active !== false && assetStockIsNegative(a));
}

function assetBelowZeroLabel(n) {
  return n === 1 ? '1 stock item is below zero' : `${n} stock items are below zero`;
}

function _assetsBelowZeroChipHtml(count) {
  return `<div class="asset-belowzero-chip" style="grid-column:1 / -1;display:flex;align-items:center;gap:8px;flex-wrap:wrap;max-width:100%;padding:6px 12px;background:#FEF2F2;border:1px solid #FECACA;border-radius:var(--radius-md);font-size:0.8rem;color:#991B1B;">
      <span class="material-icons" style="font-size:16px;" aria-hidden="true">filter_alt</span>
      <span style="font-weight:600;min-width:0;overflow-wrap:break-word;">Showing ${Number(count) || 0} — ${count === 1 ? 'item' : 'items'} below zero</span>
      <button type="button" onclick="window.RosterModule.clearBelowZeroAssets()" style="display:inline-flex;align-items:center;gap:4px;min-height:32px;border:1px solid #FECACA;background:#fff;border-radius:999px;padding:2px 12px;font-size:0.75rem;font-weight:600;color:#991B1B;cursor:pointer;white-space:nowrap;">✕ Show all</button>
    </div>`;
}

// Assets tab filtered to exactly the badge's items (other asset filters are
// overridden while it is on, so the list length == the badge count).
function showBelowZeroAssets() {
  // switchTab FIRST: on a member's first Assets open it applies the home-branch
  // default through drillAssetsBranch, which clears belowZero (COD r1).
  switchTab('assets');
  RosterState.filters.assets.belowZero = true;
  renderAssetsList();
  document.getElementById('panel-assets')?.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
}

function clearBelowZeroAssets() {
  RosterState.filters.assets.belowZero = false;
  renderAssetsList();
}

function _assetBadgeTap(e) {
  e.preventDefault();
  e.stopPropagation();
  const n = negativeStockAssets(RosterState.assets).length;
  if (n > 0) window.showToast?.(`${assetBelowZeroLabel(n)}. Showing them now.`, 'info');
  document.getElementById('mobile-more-menu')?.classList.add('hidden');
  if (/^#\/roster\b/.test(window.location.hash || '') && document.getElementById('list-assets')) {
    showBelowZeroAssets();
    return;
  }
  if (window.AppState) window.AppState.pendingRosterFilter = 'belowZero';
  window.location.hash = '#/roster';
}

// Drained from the assets snapshot (initRoster re-renders the tabs on entry).
function consumePendingBelowZero() {
  if (window.AppState?.pendingRosterFilter !== 'belowZero' || !document.getElementById('list-assets')) return;
  window.AppState.pendingRosterFilter = null;
  showBelowZeroAssets();
}

function updateAssetAlertBadges() {
  // TASK-9275 — same predicate as the card tint and the list banner.
  // TASK-9985 — and the same list the badge opens (negativeStockAssets).
  const negCount = negativeStockAssets(RosterState.assets).length;
  const badges = [
    document.getElementById('assets-alert-badge'),
    document.getElementById('roster-asset-badge'),
    document.getElementById('roster-asset-badge-mobile')
  ];
  badges.forEach(b => {
    if (!b) return;
    if (negCount > 0) {
      const label = assetBelowZeroLabel(negCount);
      b.textContent = negCount;
      b.title = `${label} — tap to see them`;
      b.setAttribute('aria-label', `${label}. Tap to see them.`);
      b.setAttribute('role', 'button');
      b.setAttribute('tabindex', '0');
      b.setAttribute('data-badge-tap', '1');
      b.classList.remove('hidden');
      if (!b.dataset.badgeBound) {
        b.dataset.badgeBound = '1';
        b.addEventListener('click', _assetBadgeTap);
        b.addEventListener('keydown', (ev) => { if (ev.key === 'Enter' || ev.key === ' ') _assetBadgeTap(ev); });
      }
    } else {
      b.classList.add('hidden');
      ['title', 'aria-label', 'role', 'tabindex'].forEach(a => b.removeAttribute(a));
    }
  });
}

// ============================================
// INVENTORY RECEIPT & ADJUSTMENT FUNCTIONS
// ============================================

function showReceiptModal(assetId) {
  const asset = RosterState.assets.find(a => a.id === assetId);
  if (!asset) return;

  document.getElementById('receipt-asset-id').value = assetId;
  document.getElementById('receipt-asset-name').value = `${assetDisplayLabel(asset)} (Current: ${asset.quantity || 0})`;
  document.getElementById('receipt-qty').value = '';
  document.getElementById('receipt-unit-cost').value = '';
  document.getElementById('receipt-tax').value = '0';
  document.getElementById('receipt-total').value = '$0.00';
  document.getElementById('receipt-vendor').value = '';
  document.getElementById('receipt-po').value = '';
  document.getElementById('receipt-notes').value = '';
  document.getElementById('receipt-date').value = new Date().toISOString().split('T')[0];
  document.getElementById('receipt-file').value = '';

  RosterState._stockMoveReqId = null; // TASK-9720 — fresh request id per dialog
  // Pro gate: hide cost/vendor/receipt fields for non-pro
  const isPro = typeof window.isProAccount === 'function' && window.isProAccount();
  const proFields = document.getElementById('receipt-pro-fields');
  // TASK-9720 — a stock-tracked (PPE) item takes a count only; supply cost is a
  // later, owner-only switch (TASK-9724). Hide the cost block rather than take
  // numbers that would be dropped.
  if (proFields && asset.stockTracked === true) {
    proFields.style.display = 'none';
  } else if (proFields) {
    if (isPro) {
      proFields.style.display = '';
      proFields.style.opacity = '';
      proFields.style.pointerEvents = '';
      // Remove any existing upgrade banner
      const existing = proFields.querySelector('.inv-upgrade-banner');
      if (existing) existing.remove();
    } else {
      proFields.style.display = '';
      proFields.style.opacity = '0.4';
      proFields.style.pointerEvents = 'none';
      // Add upgrade banner if not already there
      if (!proFields.querySelector('.inv-upgrade-banner')) {
        const banner = document.createElement('div');
        banner.className = 'inv-upgrade-banner';
        banner.style.cssText = 'text-align:center;padding:12px;background:linear-gradient(135deg,#EFF6FF,#DBEAFE);border:1px solid #BFDBFE;border-radius:8px;margin-bottom:12px;cursor:pointer;';
        banner.innerHTML = '<span class="material-icons" style="font-size:18px;vertical-align:middle;color:var(--brand-blue);">star</span> <strong style="color:var(--brand-blue);">Upgrade to Pro</strong> <span style="color:#1E40AF;font-size:13px;">to track costs, vendors & receipts</span>';
        banner.onclick = () => { window.showFeatureUpgradeModal?.('clockbilling', true); };
        proFields.prepend(banner);
      }
    }
  }

  // Load vendors for Pro accounts
  if (isPro) {
    loadVendorsForReceipt();
  }

  document.getElementById('modal-inventory-receipt').classList.remove('hidden');
}

async function loadVendorsForReceipt() {
  const select = document.getElementById('receipt-vendor');
  if (!select) return;
  const tenant = window.AppState?.tenant;
  if (!tenant) return;

  try {
    const snap = await getDocs(collection(db, 'tenants', tenant, 'crmCustomers'));
    const vendors = snap.docs.map(d => ({ id: d.id, ...d.data() }))
      .filter(c => c.type === 'vendor' || c.type === 'subcontractor' || !c.type)
      .sort((a, b) => (a.name || '').localeCompare(b.name || ''));

    select.innerHTML = '<option value="">— None —</option>' +
      vendors.map(v => `<option value="${v.id}" data-name="${escapeHtml(v.name || '')}">${escapeHtml(v.name || 'Unnamed')}</option>`).join('');
  } catch (err) {
    console.warn('[Roster] Error loading vendors:', err);
  }
}

function calcReceiptTotal() {
  const qty = parseFloat(document.getElementById('receipt-qty')?.value) || 0;
  const unitCost = parseFloat(document.getElementById('receipt-unit-cost')?.value) || 0;
  const tax = parseFloat(document.getElementById('receipt-tax')?.value) || 0;
  const total = (qty * unitCost) + tax;
  const totalEl = document.getElementById('receipt-total');
  if (totalEl) totalEl.value = `$${total.toFixed(2)}`;
}

async function saveReceipt(e) {
  e.preventDefault();
  if (!window.validateRequiredFields(e.target)) return;
  const assetId = document.getElementById('receipt-asset-id').value;
  const asset = RosterState.assets.find(a => a.id === assetId);
  if (!asset) return;

  const qty = parseInt(document.getElementById('receipt-qty').value);
  if (!qty || qty < 1) {
    window.showToast?.('Quantity must be at least 1', 'error');
    return;
  }

  const btn = document.getElementById('btn-save-receipt');
  btn.disabled = true;
  btn.innerHTML = '<span class="btn-spinner"></span> Saving...';

  // TASK-9720 — a stock-tracked item is received through the server ledger
  // (count + immutable row in one transaction). No cost on it yet (TASK-9724).
  if (asset.stockTracked === true) {
    try {
      const r = await _recordTrackedStockMove(asset, 'receive', qty, document.getElementById('receipt-notes').value.trim());
      window.showToast?.(`Added ${qty} to ${asset.name || asset.partNumber}${r && typeof r.balance === 'number' ? ` — on hand ${r.balance}` : ''}`, 'success');
      closeModal('inventory-receipt');
    } catch (err) {
      console.error('[Roster] Error receiving tracked stock:', err);
      window.showToast?.(err?.message || 'Error saving receipt', 'error');
    } finally {
      btn.disabled = false;
      btn.innerHTML = '<span class="material-icons">add_circle</span> Add Inventory';
    }
    return;
  }

  const isPro = typeof window.isProAccount === 'function' && window.isProAccount();
  const unitCost = isPro ? (parseFloat(document.getElementById('receipt-unit-cost').value) || 0) : 0;
  const tax = isPro ? (parseFloat(document.getElementById('receipt-tax').value) || 0) : 0;
  const vendorSelect = document.getElementById('receipt-vendor');
  const vendorId = isPro ? (vendorSelect?.value || '') : '';
  const vendorName = isPro ? (vendorSelect?.selectedOptions[0]?.dataset?.name || '') : '';
  const poNumber = isPro ? (document.getElementById('receipt-po').value.trim()) : '';
  const dateReceived = document.getElementById('receipt-date').value || new Date().toISOString().split('T')[0];
  const notes = document.getElementById('receipt-notes').value.trim();
  const fileInput = document.getElementById('receipt-file');

  const tenant = window.AppState?.tenant;

  try {
    // Upload receipt file if provided (Pro only)
    let receiptUrl = null;
    let receiptPath = null;
    if (isPro && fileInput?.files?.[0]) {
      const file = fileInput.files[0];
      const storagePath = `tenants/${tenant}/photos/assets/${assetId}/receipts/${Date.now()}_${file.name}`;
      const storageRef = ref(storage, storagePath);
      await uploadBytes(storageRef, file, { contentType: file.type || 'application/octet-stream' });
      receiptUrl = await getDownloadURL(storageRef);
      receiptPath = storagePath;
    }

    // TASK-9575 slice D — the receipt goes through the SERVER: the ledger row,
    // the new count and (when a unit cost was typed) the item's average land in
    // ONE transaction, from values read inside it. The cost half goes to the
    // owner / BI / Billing-only cost home — never onto the asset doc or the
    // ledger row (both are readable by every driver session).
    const cost = isPro ? {
      unitCost: unitCost || null,
      tax: tax || null,
      vendorId, vendorName, poNumber,
      receiptPath: receiptPath || '',
      dateReceived,
    } : null;
    const res = await httpsCallable(functions, 'recordSupplyStock')({
      assetId, kind: 'receive', qty, reason: notes, dateReceived, cost, requestId: _stockMoveRequestId(),
    });
    RosterState._stockMoveReqId = null;
    const bal = res && res.data ? res.data.balance : undefined;
    if (typeof bal === 'number') asset.quantity = bal;

    window.showToast?.(`Added ${qty} to ${asset.name || asset.partNumber}`, 'success');
    closeModal('inventory-receipt');
  } catch (err) {
    console.error('[Roster] Error saving receipt:', err);
    window.showToast?.(err?.message || 'Error saving receipt', 'error');
  } finally {
    btn.disabled = false;
    btn.innerHTML = '<span class="material-icons">add_circle</span> Add Inventory';
  }
}

function showAdjustmentModal(assetId) {
  const asset = RosterState.assets.find(a => a.id === assetId);
  if (!asset) return;

  document.getElementById('adjust-asset-id').value = assetId;
  document.getElementById('adjust-asset-name').value = `${assetDisplayLabel(asset)} (Current: ${asset.quantity || 0})`;
  document.getElementById('adjust-qty').value = '';
  document.getElementById('adjust-reason').value = '';
  document.getElementById('adjust-notes').value = '';
  RosterState._stockMoveReqId = null; // TASK-9720 — fresh request id per dialog

  document.getElementById('modal-inventory-adjustment').classList.remove('hidden');
}

// TASK-10067 item 2 — what to say when adjustAssetQuantity fails: offline (the
// call never reached the server, nothing changed) / the server's own refusal
// sentence (deleted, changed since this opened, not enough left) / a fallback.
function _qtyCallMsg(err, offline, fallback) {
  const c = String(err?.code || '');
  const msg = String(err?.message || '').trim();
  if ((typeof navigator !== 'undefined' && navigator.onLine === false) || c === 'functions/unavailable' || c === 'functions/deadline-exceeded'
      || (c === 'functions/internal' && /^internal$/i.test(msg))) return offline;
  return ['functions/failed-precondition', 'functions/not-found', 'functions/invalid-argument', 'functions/permission-denied'].includes(c) && msg ? msg : fallback;
}

async function saveAdjustment(e) {
  e.preventDefault();
  if (!window.validateRequiredFields(e.target)) return;
  const assetId = document.getElementById('adjust-asset-id').value;
  const asset = RosterState.assets.find(a => a.id === assetId);
  if (!asset) return;

  const qty = parseInt(document.getElementById('adjust-qty').value);
  if (!qty || qty === 0) {
    window.showToast?.('Quantity cannot be zero', 'error');
    return;
  }
  const reason = document.getElementById('adjust-reason').value;
  if (!reason) {
    window.showToast?.('Please select a reason', 'error');
    return;
  }

  const btn = document.getElementById('btn-save-adjustment');
  btn.disabled = true;
  btn.innerHTML = '<span class="btn-spinner"></span> Saving...';

  const notes = document.getElementById('adjust-notes').value.trim();

  // TASK-9720 — a stock-tracked item is adjusted through the server ledger.
  if (asset.stockTracked === true) {
    try {
      const r = await _recordTrackedStockMove(asset, 'adjust', qty, notes ? `${reason} — ${notes}` : reason);
      const verb = qty > 0 ? 'Added' : 'Removed';
      window.showToast?.(`${verb} ${Math.abs(qty)} — ${asset.name || asset.partNumber}${r && typeof r.balance === 'number' ? ` (on hand ${r.balance})` : ''}`, 'success');
      closeModal('inventory-adjustment');
    } catch (err) {
      console.error('[Roster] Error adjusting tracked stock:', err);
      window.showToast?.(err?.message || 'Error saving adjustment', 'error');
    } finally {
      btn.disabled = false;
      btn.innerHTML = '<span class="material-icons">tune</span> Apply Adjustment';
    }
    return;
  }

  try {
    // TASK-10067 — adjust from the STORED count (this tab's copy can be behind a
    // move to another branch or another tab's deduct), ledger row in the same
    // commit. TASK-10067 item 2 — on the server (adjustAssetQuantity): rules
    // refuse a client count change on a consumable.
    // requestId: one per dialog (COD r1) — a retry after a lost response replays, never adjusts twice.
    const res = await httpsCallable(functions, 'adjustAssetQuantity')({ kind: 'adjust', assetId, delta: qty, reason, note: notes, tenantId: window.AppState?.tenant, requestId: _stockMoveRequestId() });
    RosterState._stockMoveReqId = null;
    const bal = res && res.data ? res.data.balance : undefined;
    if (typeof bal === 'number') asset.quantity = bal;

    const verb = qty > 0 ? 'Added' : 'Removed';
    window.showToast?.(`${verb} ${Math.abs(qty)} — ${asset.name || asset.partNumber} (${reason})`, 'success');
    closeModal('inventory-adjustment');
  } catch (err) {
    console.error('[Roster] Error saving adjustment:', err);
    window.showToast?.(_qtyCallMsg(err, 'No connection. Reconnect and tap Apply again — it will not count twice.', 'Error saving adjustment'), 'error');
  } finally {
    btn.disabled = false;
    btn.innerHTML = '<span class="material-icons">tune</span> Apply Adjustment';
  }
}

// ============================================
// YARD TRANSFER
// ============================================

function showYardTransferModal() {
  const assetId = document.getElementById('asset-id').value;
  const asset = RosterState.assets.find(a => a.id === assetId);
  if (!asset) return;

  document.getElementById('transfer-asset-id').value = assetId;
  document.getElementById('transfer-asset-name').value = `${assetDisplayLabel(asset)} (Qty: ${asset.quantity || 0})`;
  document.getElementById('transfer-from-yard').value = asset.yardId ? getYardLabel(asset.yardId) : 'Unassigned';
  document.getElementById('transfer-qty').value = '';
  document.getElementById('transfer-notes').value = '';
  RosterState._stockMoveReqId = null; // TASK-10067 item 2 — fresh request id per dialog
  // TASK-9369 — show what can actually move (quantity − deployedQty on rentals).
  const transferAvail = _assetTransferableQty(asset);
  const deployedOut = asset.quantityType === 'rental' ? (Number(asset.deployedQty) || 0) : 0;
  document.getElementById('transfer-available').textContent = deployedOut > 0
    ? `Available to transfer: ${transferAvail} (${deployedOut} out on rental)`
    : `Available: ${transferAvail}`;

  // Populate destination yards (exclude current yard)
  const toSelect = document.getElementById('transfer-to-yard');
  toSelect.innerHTML = '<option value="">Select...</option>';
  const yards = getYardsList();
  yards.forEach(y => {
    if (y.slug !== asset.yardId) {
      const opt = document.createElement('option');
      opt.value = y.slug;
      opt.textContent = y.label;
      toSelect.appendChild(opt);
    }
  });
  // Add "Unassigned" if asset is currently in a yard
  if (asset.yardId) {
    const opt = document.createElement('option');
    opt.value = '__unassigned__';
    opt.textContent = 'Unassigned (no yard)';
    toSelect.appendChild(opt);
  }

  document.getElementById('modal-yard-transfer').classList.remove('hidden');
}

// TASK-9369 _assetTransferableQty: moved to functions/src/shared/assetRollup.js
// (TASK-9375 S2) — imported at the top.

async function executeYardTransfer(e) {
  e.preventDefault();
  if (!window.validateRequiredFields(e.target)) return;
  const assetId = document.getElementById('transfer-asset-id').value;
  const asset = RosterState.assets.find(a => a.id === assetId);
  if (!asset) return;

  const toYardSlug = document.getElementById('transfer-to-yard').value;
  const qty = parseInt(document.getElementById('transfer-qty').value);
  const notes = document.getElementById('transfer-notes').value.trim();

  // TASK-9720 — a stock-tracked item's count is server-maintained and a yard
  // transfer writes it from the browser (two docs, client math). Moving tracked
  // stock between yards is not built yet (per-yard counts, TASK-9718); say so
  // instead of a raw permission error.
  if (asset.stockTracked === true) {
    window.showToast?.('This item\'s count comes from PPE sign-outs, so it can\'t be moved between yards yet. Change the yard on the item instead.', 'error');
    return;
  }

  if (!toYardSlug) {
    window.showToast?.('Select a destination yard', 'error');
    return;
  }
  const currentYardId = asset.yardId || '';
  if ((toYardSlug === '__unassigned__' ? '' : toYardSlug) === currentYardId) {
    window.showToast?.('Asset is already in that yard', 'error');
    return;
  }
  if (!qty || qty <= 0) {
    window.showToast?.('Enter a valid quantity', 'error');
    return;
  }
  // TASK-9369 (COD Edge Case #2) — a rental's `deployedQty` stays on the SOURCE
  // doc; only `quantity` moves. Transferring all 40 of a rental with 10 out
  // would leave the source at Out 10 on a Total of 0. Block a transfer that
  // exceeds what is actually on the shelf (quantity − deployedQty) — same intent
  // as decrementDeployedQtyFloored: never cross zero.
  const transferAvail = _assetTransferableQty(asset);
  if (qty > transferAvail) {
    const deployed = asset.quantityType === 'rental' ? (Number(asset.deployedQty) || 0) : 0;
    window.showToast?.(deployed > 0
      ? `Only ${transferAvail} available to transfer — ${deployed} of ${asset.quantity || 0} ${deployed === 1 ? 'is' : 'are'} out on rental`
      : `Only ${transferAvail} available to transfer`, 'error');
    return;
  }

  const btn = document.getElementById('btn-execute-transfer');
  btn.disabled = true;
  btn.innerHTML = '<span class="btn-spinner"></span> Transferring...';

  const tenant = window.AppState?.tenant;
  if (!tenant) { window.showToast?.('No tenant found', 'error'); return; }
  const toYardId = toYardSlug === '__unassigned__' ? '' : toYardSlug;
  const toYardName = toYardId ? getYardLabel(toYardId) : '';
  const fromYardName = asset.yardId ? getYardLabel(asset.yardId) : 'Unassigned';

  try {
    // Find existing asset with same partNumber in destination yard.
    // TASK-4800 follow-up: docs with NO part number are now listed (the old
    // orderBy('partNumber') hid them), and `undefined === undefined` would have
    // merged one into ANY other part-less doc in the destination yard. A
    // part-less item only merges into a part-less doc with the same name.
    const target = findYardTransferTarget(RosterState.assets, asset, assetId, toYardId);

    // TASK-10067 — ONE transaction re-reads the source (and the destination
    // doc, if any) and moves from the STORED counts: this tab's copy can be
    // behind a move to another branch, a dispatch deduct or another tab.
    // TASK-10067 item 2 — on the server (adjustAssetQuantity,
    // functions/src/services/assetQuantity.js), with the same refusals: the item
    // deleted / now stock-tracked / edited (identity below) since this opened,
    // more than is on the shelf NOW, or the destination match changed. The
    // destination is the doc this tab matched (or a new one); the server
    // re-checks it is still the same item in that yard.
    const res = await httpsCallable(functions, 'adjustAssetQuantity')({
      kind: 'transfer', assetId, qty, toYardId, toYardName, fromYardName, note: notes, tenantId: tenant,
      destId: target ? target.id : null, requestId: _stockMoveRequestId(), // one per dialog: a retry never moves twice
      expect: { partNumber: asset.partNumber ?? '', name: asset.name || '', yardId: asset.yardId || '', quantityType: asset.quantityType || 'standard', trackingMode: asset.trackingMode || '' },
    });
    RosterState._stockMoveReqId = null;
    const newSourceQty = res && res.data ? res.data.balance : undefined;

    window.showToast?.(`Transferred ${qty} × ${assetDisplayLabel(asset)} → ${toYardName || 'Unassigned'}`, 'success');
    closeModal('yard-transfer');
    if (typeof newSourceQty === 'number') {
      // Update the qty field in the still-open asset modal
      document.getElementById('asset-quantity').value = newSourceQty;
      // Optimistically update local state
      asset.quantity = newSourceQty;
    }
  } catch (err) {
    console.error('[Roster] Error transferring:', err);
    window.showToast?.(_qtyCallMsg(err, 'No connection. Reconnect and tap Transfer again — it will not move twice.', 'Error transferring inventory'), 'error');
  } finally {
    btn.disabled = false;
    btn.innerHTML = '<span class="material-icons">swap_horiz</span> Transfer';
  }
}

// ============================================
// ORDER MANAGEMENT FUNCTIONS
// ============================================

function openOrderManagement() {
  // Gate behind billing package for non-demo, non-pro accounts
  if (!window.AppState?.isDemo && typeof window.isProAccount === 'function' && !window.isProAccount()) {
    window.showFeatureUpgradeModal('clockbilling', true);
    return;
  }
  RosterState.orderManagementOpen = true;
  document.getElementById('order-management-overlay').classList.remove('hidden');
  renderOrdersList();
  populateOrderCustomerFilter();
}

function closeOrderManagement() {
  RosterState.orderManagementOpen = false;
  document.getElementById('order-management-overlay').classList.add('hidden');
}

function renderOrdersList() {
  const container = document.getElementById('list-orders');
  if (!container) return;
  const detailsModal = document.getElementById('modal-order-details');
  const linkModal = document.getElementById('modal-order-link');
  if ((detailsModal && !detailsModal.classList.contains('hidden')) ||
      (linkModal && !linkModal.classList.contains('hidden'))) return;

  const { search, customer, status } = RosterState.filters.orders;
  let filtered = RosterState.orders;

  if (search) {
    const searchLower = search.trim().toLowerCase();
    filtered = filtered.filter(o =>
      o.poNumber?.toLowerCase().includes(searchLower) ||
      o.customerName?.toLowerCase().includes(searchLower)
    );
  }
  if (customer !== 'all') {
    filtered = filtered.filter(o => o.customerId === customer);
  }
  if (status !== 'all') {
    filtered = filtered.filter(o => o.status === status);
  }

  if (RosterState.orders.length === 0) {
    container.innerHTML = `
      <div class="empty-state">
        <div class="empty-state-icon">📋</div>
        <div class="empty-state-title">No orders yet</div>
        <div class="empty-state-text">Generate a request link and share it with customers.</div>
      </div>
    `;
    return;
  }

  if (filtered.length === 0) {
    container.innerHTML = `
      <div class="empty-state">
        <div class="empty-state-icon">🔍</div>
        <div class="empty-state-title">No matches</div>
        <div class="empty-state-text">Try adjusting your search or filter.</div>
      </div>
    `;
    return;
  }

  container.innerHTML = filtered.map(order => {
    const statusIndex = ORDER_STATUSES.indexOf(order.status);
    const requestedDate = order.requestedDate?.toDate?.() || new Date(order.requestedDate);
    const dateStr = requestedDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    const timeStr = order.requestedTime || '';
    const itemsSummary = (order.items || []).slice(0, 2).map(i => `${i.quantity}x ${i.name}`).join(', ');
    const moreItems = (order.items || []).length > 2 ? ` +${order.items.length - 2} more` : '';

    return `
      <div class="order-card" onclick="window.RosterModule.showOrderDetails('${escJsAttr(order.id)}')">
        <div class="order-card-header">
          <span class="order-customer-name">${escapeHtml(order.customerName || 'Unknown')}</span>
          <span class="order-po">PO: ${escapeHtml(order.poNumber || 'N/A')}</span>
          <span class="status-badge status-badge-${escapeHtml(order.status)}">${escapeHtml(ORDER_STATUS_LABELS[order.status] || order.status)}</span>
        </div>
        <div class="order-card-items">${escapeHtml(itemsSummary)}${moreItems}</div>
        <div class="order-card-date">
          <span class="material-icons">schedule</span> Requested: ${dateStr} ${escapeHtml(timeStr)}
        </div>
        <div class="pizza-tracker-mini">
          ${ORDER_STATUSES.map((s, i) => `
            <span class="tracker-dot ${i <= statusIndex ? 'active' : ''} ${s === order.status ? 'current' : ''}"></span>
            ${i < ORDER_STATUSES.length - 1 ? '<span class="tracker-line ' + (i < statusIndex ? 'active' : '') + '"></span>' : ''}
          `).join('')}
        </div>
      </div>
    `;
  }).join('');
}

function filterOrders() {
  const searchEl = document.getElementById('search-orders');
  const customerEl = document.getElementById('filter-orders-customer');
  const statusEl = document.getElementById('filter-orders-status');
  RosterState.filters.orders.search = searchEl?.value || '';
  RosterState.filters.orders.customer = customerEl?.value || 'all';
  RosterState.filters.orders.status = statusEl?.value || 'all';
  renderOrdersList();
}

function populateOrderCustomerFilter() {
  const select = document.getElementById('filter-orders-customer');
  if (!select) return;

  // Get unique customers from orders
  const customerIds = [...new Set(RosterState.orders.map(o => o.customerId).filter(Boolean))];
  const currentValue = select.value;

  select.innerHTML = '<option value="all">All Customers</option>' +
    RosterState.customers
      .filter(c => customerIds.includes(c.id))
      .map(c => `<option value="${c.id}">${escapeHtml(c.name)}</option>`)
      .join('');

  select.value = currentValue || 'all';
}

function populateOrderLinkCustomerDropdown() {
  const select = document.getElementById('order-link-customer');
  if (!select) return;

  // P0 2026-10-06 — customers only (QBO vendors share the collection).
  // TASK-9548 — the builder drops vendors. Decision 2026-10-10: an order link is
  // for an outside customer, so Shop/Office (internal time) are not offered.
  select.innerHTML = '<option value="">Any customer can use this link</option>' +
    window.PickerGroups.customerOptionsHtml(RosterState.customers || [], { nameOf: c => c.name || '', excludeInternal: true });
}

function showGenerateOrderLink() {
  populateOrderLinkCustomerDropdown();
  document.getElementById('order-link-customer').value = '';
  document.getElementById('order-link-expiry').value = 'never';
  document.getElementById('order-link-result-group').classList.add('hidden');
  document.getElementById('btn-generate-link').classList.remove('hidden');
  document.getElementById('modal-order-link').classList.remove('hidden');
}

async function generateOrderRequestLink() {
  const customerId = document.getElementById('order-link-customer').value;
  const expiry = document.getElementById('order-link-expiry').value;
  const btn = document.getElementById('btn-generate-link');

  btn.disabled = true;
  btn.innerHTML = '<span class="material-icons rotating">sync</span> Generating...';

  try {
    const tenant = window.AppState?.tenant || 'demo';
    // TASK-1421 — same as the signature token above: no silent Math.random()
    // fallback for a bearer credential, especially one whose expiry can be 'never'.
    const token = window.mintAccessTokenId('drv');

    // Calculate expiry
    let expiresAt = null;
    if (expiry !== 'never') {
      const now = new Date();
      if (expiry === '24h') now.setHours(now.getHours() + 24);
      else if (expiry === '7d') now.setDate(now.getDate() + 7);
      else if (expiry === '30d') now.setDate(now.getDate() + 30);
      expiresAt = Timestamp.fromDate(now);
    }

    const tokenData = {
      tenantId: tenant,
      type: 'order_request',
      customerId: customerId || null,
      expiresAt: expiresAt,
      createdAt: Timestamp.now(),
      createdBy: auth.currentUser?.email || 'unknown'
    };

    await setDoc(doc(db, 'accessTokens', token), tokenData);

    // Generate URL
    const baseUrl = window.location.origin;
    const orderUrl = `${baseUrl}/?order=${token}`;

    document.getElementById('order-link-url').value = orderUrl;
    document.getElementById('order-link-result-group').classList.remove('hidden');
    btn.classList.add('hidden');

    window.showToast?.('Request link generated', 'success');
  } catch (error) {
    console.error('[Roster] Error generating order link:', error);
    window.showToast?.('Error generating link', 'error');
  } finally {
    btn.disabled = false;
    btn.innerHTML = '<span class="material-icons">link</span> Generate Link';
  }
}

function copyOrderLink() {
  const input = document.getElementById('order-link-url');
  if (input) {
    input.select();
    navigator.clipboard.writeText(input.value).then(() => {
      window.showToast?.('Link copied to clipboard!', 'success');
    }).catch(() => {
      document.execCommand('copy');
      window.showToast?.('Link copied!', 'success');
    });
  }
}

function showOrderDetails(orderId) {
  const order = RosterState.orders.find(o => o.id === orderId);
  if (!order) return;

  document.getElementById('modal-order-title').textContent = `Order: ${order.poNumber || 'No PO'}`;

  const requestedDate = order.requestedDate?.toDate?.() || new Date(order.requestedDate);
  const dateStr = requestedDate.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' });
  const createdAt = order.createdAt?.toDate?.() || new Date(order.createdAt);
  const createdStr = createdAt.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' });

  // Order details content
  const detailsHtml = `
    <div class="order-details-grid">
      <div class="order-detail-item">
        <span class="detail-label">Customer</span>
        <span class="detail-value">${escapeHtml(order.customerName || 'Unknown')}</span>
      </div>
      <div class="order-detail-item">
        <span class="detail-label">PO Number</span>
        <span class="detail-value">${escapeHtml(order.poNumber || 'N/A')}</span>
      </div>
      <div class="order-detail-item">
        <span class="detail-label">Requested Date</span>
        <span class="detail-value">${dateStr} ${escapeHtml(order.requestedTime || '')}</span>
      </div>
      <div class="order-detail-item">
        <span class="detail-label">Location</span>
        <span class="detail-value">${escapeHtml(order.location || 'N/A')}</span>
      </div>
      ${/^https?:\/\//i.test(order.googleMapsUrl) ? `
        <div class="order-detail-item">
          <span class="detail-label">Map</span>
          <a href="${escapeHtml(order.googleMapsUrl)}" target="_blank" class="detail-value link">
            <span class="material-icons">map</span> Open in Maps
          </a>
        </div>
      ` : ''}
      ${order.notes ? `
        <div class="order-detail-item full-width">
          <span class="detail-label">Notes</span>
          <span class="detail-value">${escapeHtml(order.notes)}</span>
        </div>
      ` : ''}
      <div class="order-detail-item">
        <span class="detail-label">Created</span>
        <span class="detail-value">${createdStr} via ${order.createdVia === 'customer_link' ? 'Customer Link' : 'Admin'}</span>
      </div>
    </div>

    <h4 style="margin-top: var(--spacing-lg);">Items Ordered</h4>
    <div class="order-items-list">
      ${(order.items || []).map(item => `
        <div class="order-item-row">
          <span class="item-qty">${escapeHtml(String(item.quantity))}x</span>
          <span class="item-name">${escapeHtml(item.partNumber)} - ${escapeHtml(item.name)}</span>
        </div>
      `).join('') || '<p class="empty-state-small" style="color:var(--text-secondary);padding:8px 0;"><span class="material-icons" style="font-size:18px;vertical-align:middle;margin-right:4px;opacity:0.55;">inventory_2</span>No items in this order</p>'}
    </div>
  `;

  document.getElementById('order-details-content').innerHTML = detailsHtml;

  // Pizza tracker
  const statusIndex = ORDER_STATUSES.indexOf(order.status);
  const trackerHtml = ORDER_STATUSES.map((s, i) => `
    <div class="tracker-stage ${i <= statusIndex ? 'completed' : ''} ${s === order.status ? 'current' : ''}">
      <div class="tracker-dot-large"></div>
      <span class="tracker-label">${ORDER_STATUS_LABELS[s]}</span>
    </div>
    ${i < ORDER_STATUSES.length - 1 ? '<div class="tracker-connector ' + (i < statusIndex ? 'completed' : '') + '"></div>' : ''}
  `).join('');

  document.getElementById('order-pizza-tracker').innerHTML = trackerHtml;

  // Status action buttons
  const actionsHtml = ORDER_STATUSES.map((s, i) => {
    const isCurrent = s === order.status;
    const isPast = i < statusIndex;
    const isNext = i === statusIndex + 1;
    return `
      <button class="btn ${isCurrent ? 'btn-primary' : isNext ? 'btn-secondary' : 'btn-ghost'}"
        ${isCurrent ? 'disabled' : ''}
        onclick="window.RosterModule.updateOrderStatus('${escJsAttr(orderId)}', '${s}')">
        ${isCurrent ? '<span class="material-icons">check</span>' : ''}
        ${ORDER_STATUS_LABELS[s]}
      </button>
    `;
  }).join('');

  document.getElementById('order-status-actions').innerHTML = actionsHtml;

  // Status history
  const historyHtml = (order.statusHistory || [])
    .slice()
    .reverse()
    .map(h => {
      const ts = h.timestamp?.toDate?.() || new Date(h.timestamp);
      const tsStr = ts.toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
      return `
        <div class="status-history-item">
          <span class="status-badge status-badge-${escapeHtml(h.status)}">${escapeHtml(ORDER_STATUS_LABELS[h.status] || h.status)}</span>
          <span class="history-time">${tsStr}</span>
          <span class="history-by">by ${escapeHtml(h.updatedBy || 'Unknown')}</span>
        </div>
      `;
    }).join('') || '<p class="text-muted">No history yet</p>';

  document.getElementById('order-status-history').innerHTML = historyHtml;

  // Delivery ticket section (shown when delivered)
  const ticketSection = document.getElementById('order-delivery-ticket-section');
  const viewTicketBtn = document.getElementById('btn-view-delivery-ticket');

  if (order.status === 'delivered') {
    ticketSection.classList.remove('hidden');
    viewTicketBtn.classList.remove('hidden');
    viewTicketBtn.dataset.orderId = orderId;

    // Check if signature exists
    if (order.signatureUrl) {
      document.getElementById('delivery-ticket-status').innerHTML = `
        <div class="signature-status signed">
          <span class="material-icons">verified</span>
          <span>Signed by ${escapeHtml(order.signedBy || 'Customer')} on ${
            order.signedAt?.toDate?.().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' }) || 'Unknown date'
          }</span>
        </div>
      `;
    } else {
      document.getElementById('delivery-ticket-status').innerHTML = `
        <div class="signature-status pending">
          <span class="material-icons">pending</span>
          <span>Awaiting signature</span>
          <button class="btn btn-sm btn-secondary" onclick="window.RosterModule.generateSignatureLink('${escJsAttr(orderId)}')">
            <span class="material-icons">link</span> Generate Signature Link
          </button>
        </div>
      `;
    }
  } else {
    ticketSection.classList.add('hidden');
    viewTicketBtn.classList.add('hidden');
  }

  document.getElementById('modal-order-details').classList.remove('hidden');
}

async function updateOrderStatus(orderId, newStatus) {
  try {
    const tenant = window.AppState?.tenant || 'demo';
    const order = RosterState.orders.find(o => o.id === orderId);
    if (!order) return;

    const historyEntry = {
      status: newStatus,
      timestamp: Timestamp.now(),
      updatedBy: auth.currentUser?.email || 'unknown'
    };

    await updateDoc(doc(db, 'tenants', tenant, 'orders', orderId), {
      status: newStatus,
      statusHistory: arrayUnion(historyEntry),
      updatedAt: Timestamp.now()
    });

    window.showToast?.(`Order status updated to ${ORDER_STATUS_LABELS[newStatus]}`, 'success');

    // Refresh the modal
    showOrderDetails(orderId);
  } catch (error) {
    console.error('[Roster] Error updating order status:', error);
    window.showToast?.('Error updating order status', 'error');
  }
}

// ============================================
// DELIVERY TICKET & SIGNATURE FUNCTIONS
// ============================================
let currentTicketOrderId = null;

function viewDeliveryTicket() {
  const orderId = document.getElementById('btn-view-delivery-ticket')?.dataset?.orderId;
  if (!orderId) return;

  currentTicketOrderId = orderId;
  const order = RosterState.orders.find(o => o.id === orderId);
  if (!order) return;

  const tenant = window.AppState?.tenant || 'demo';
  const tenantData = window.AppState?.tenantData || {};
  const companyName = tenantData.name || 'DispatchDeck';

  const requestedDate = order.requestedDate?.toDate?.() || new Date(order.requestedDate);
  const dateStr = requestedDate.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' });
  const deliveredAt = order.statusHistory?.find(h => h.status === 'delivered')?.timestamp;
  const deliveredStr = deliveredAt?.toDate?.()?.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' }) || 'N/A';

  const ticketHtml = `
    <div class="delivery-ticket" id="printable-ticket">
      <div class="ticket-header">
        <h2>${escapeHtml(companyName)}</h2>
        <h3>Delivery Ticket</h3>
        <div class="ticket-number">Order: ${escapeHtml(order.poNumber || order.id.slice(0, 8).toUpperCase())}</div>
      </div>

      <div class="ticket-info-grid">
        <div class="ticket-info-item">
          <span class="ticket-label">Customer</span>
          <span class="ticket-value">${escapeHtml(order.customerName || 'N/A')}</span>
        </div>
        <div class="ticket-info-item">
          <span class="ticket-label">PO Number</span>
          <span class="ticket-value">${escapeHtml(order.poNumber || 'N/A')}</span>
        </div>
        <div class="ticket-info-item">
          <span class="ticket-label">Requested Date</span>
          <span class="ticket-value">${dateStr} ${escapeHtml(order.requestedTime || '')}</span>
        </div>
        <div class="ticket-info-item">
          <span class="ticket-label">Delivered</span>
          <span class="ticket-value">${deliveredStr}</span>
        </div>
        <div class="ticket-info-item full-width">
          <span class="ticket-label">Location</span>
          <span class="ticket-value">${escapeHtml(order.location || 'N/A')}</span>
        </div>
      </div>

      <div class="ticket-items">
        <h4>Items Delivered</h4>
        <table class="ticket-items-table">
          <thead>
            <tr>
              <th>Qty</th>
              <th>Part #</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            ${(order.items || []).map(item => `
              <tr>
                <td>${escapeHtml(String(item.quantity))}</td>
                <td>${escapeHtml(item.partNumber)}</td>
                <td>${escapeHtml(item.name)}</td>
              </tr>
            `).join('')}
          </tbody>
        </table>
      </div>

      ${order.notes ? `
        <div class="ticket-notes">
          <h4>Notes</h4>
          <p>${escapeHtml(order.notes)}</p>
        </div>
      ` : ''}

      <div class="ticket-signature-section">
        <h4>Received By</h4>
        ${order.signatureUrl ? `
          <div class="signature-display">
            <img src="${escapeHtml(order.signatureUrl)}" alt="Signature" class="signature-image">
            <div class="signature-name">${escapeHtml(order.signedBy || 'Customer')}</div>
            <div class="signature-date">${
              order.signedAt?.toDate?.()?.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' }) || ''
            }</div>
          </div>
        ` : `
          <div class="signature-pending">
            <p class="text-muted">Signature pending</p>
            <div class="signature-line"></div>
            <span class="signature-label">Customer Signature</span>
          </div>
        `}
      </div>
    </div>
  `;

  document.getElementById('delivery-ticket-content').innerHTML = ticketHtml;

  // Show/hide print button based on signature status
  const printBtn = document.getElementById('btn-print-ticket');
  if (printBtn) {
    printBtn.style.display = order.signatureUrl ? 'inline-flex' : 'none';
  }

  document.getElementById('modal-delivery-ticket').classList.remove('hidden');
}

function printDeliveryTicket() {
  const ticketContent = document.getElementById('printable-ticket');
  if (!ticketContent) return;

  const printWindow = window.open('', '_blank');
  if (!printWindow) { window.showToast?.('Popup blocked. Allow popups for this site to print.', 'error'); return; }
  printWindow.document.write(`
    <!DOCTYPE html>
    <html>
    <head>
      <title>Delivery Ticket</title>
      <style>
        body { font-family: Arial, sans-serif; padding: 20px; }
        .delivery-ticket { max-width: 800px; margin: 0 auto; }
        .ticket-header { text-align: center; border-bottom: 2px solid #333; padding-bottom: 15px; margin-bottom: 20px; }
        .ticket-header h2 { margin: 0 0 5px 0; }
        .ticket-header h3 { margin: 0; color: #666; }
        .ticket-number { margin-top: 10px; font-size: 14px; color: #888; }
        .ticket-info-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 15px; margin-bottom: 25px; }
        .ticket-info-item { display: flex; flex-direction: column; }
        .ticket-info-item.full-width { grid-column: span 2; }
        .ticket-label { font-size: 12px; color: #666; text-transform: uppercase; }
        .ticket-value { font-size: 16px; font-weight: 500; margin-top: 3px; }
        .ticket-items h4 { border-bottom: 1px solid #ddd; padding-bottom: 8px; }
        .ticket-items-table { width: 100%; border-collapse: collapse; margin-bottom: 25px; }
        .ticket-items-table th, .ticket-items-table td { padding: 10px; text-align: left; border-bottom: 1px solid #eee; }
        .ticket-items-table th { background: #f5f5f5; font-weight: 600; }
        .ticket-notes { margin-bottom: 25px; }
        .ticket-notes h4 { margin-bottom: 10px; }
        .ticket-signature-section { margin-top: 40px; padding-top: 20px; border-top: 1px solid #ddd; }
        .signature-display { text-align: center; }
        .signature-image { max-width: 300px; max-height: 100px; border-bottom: 1px solid #333; }
        .signature-name { margin-top: 5px; font-weight: 500; }
        .signature-date { font-size: 12px; color: #666; }
        .signature-pending { text-align: center; }
        .signature-line { width: 300px; border-bottom: 1px solid #333; margin: 50px auto 5px; }
        .signature-label { font-size: 12px; color: #666; }
        @media print { body { padding: 0; } }
      </style>
    </head>
    <body>
      ${ticketContent.outerHTML}
    </body>
    </html>
  `);
  printWindow.document.close();
  printWindow.print();
}

async function generateSignatureLink(orderId) {
  try {
    const tenant = window.AppState?.tenant || 'demo';
    const order = RosterState.orders.find(o => o.id === orderId);
    if (!order) return;

    // Generate a unique token for signature
    // TASK-1421 — CSPRNG-random instead of sig_<orderId>_<timestamp>. This one
    // is a never-expiring delivery-signature link, so a guessable id let a
    // stranger sign for a delivery. orderId is read from the token doc's field.
    const token = window.mintAccessTokenId('sig');

    // Save token to accessTokens collection
    await setDoc(doc(db, 'accessTokens', token), {
      tenantId: tenant,
      type: 'delivery_signature',
      orderId: orderId,
      createdAt: Timestamp.now(),
      createdBy: auth.currentUser?.email || 'unknown',
      expiresAt: null, // Never expires
      used: false
    });

    // Update order with signature token
    await updateDoc(doc(db, 'tenants', tenant, 'orders', orderId), {
      signatureToken: token,
      updatedAt: Timestamp.now()
    });

    // Build the signature link
    const baseUrl = window.location.origin;
    const signatureUrl = `${baseUrl}?sign=${token}`;

    // Show the link
    document.getElementById('signature-link').value = signatureUrl;
    document.getElementById('modal-signature-request').classList.remove('hidden');

    window.showToast?.('Signature link generated', 'success');
  } catch (error) {
    console.error('[Roster] Error generating signature link:', error);
    window.showToast?.('Error generating signature link', 'error');
  }
}

function copyDeliverySignatureLink() {
  const linkInput = document.getElementById('signature-link');
  if (!linkInput) return;

  navigator.clipboard.writeText(linkInput.value).then(() => {
    window.showToast?.('Link copied to clipboard', 'success');
  }).catch(() => {
    // Fallback for older browsers
    linkInput.select();
    document.execCommand('copy');
    window.showToast?.('Link copied to clipboard', 'success');
  });
}

function showCustomerList() {
  const container = document.getElementById('customer-list-content');
  if (!container) return;
  // P0 2026-10-06 — customers only (QBO vendors share the collection).
  const customers = window.BillableCustomer.billableCustomers(RosterState.customers || []);

  if (customers.length === 0) {
    container.innerHTML = `
      <div class="empty-state">
        <div class="empty-state-icon">🏢</div>
        <div class="empty-state-title">No customers yet</div>
        <div class="empty-state-text">Add customers in the Dispatch module first.</div>
      </div>
    `;
  } else {
    container.innerHTML = `
      <div class="customer-list-grid">
        ${customers.map(c => `
          <div class="customer-list-item">
            <span class="material-icons">business</span>
            <span class="customer-name">${escapeHtml(c.name)}</span>
          </div>
        `).join('')}
      </div>
    `;
  }

  document.getElementById('modal-customer-list').classList.remove('hidden');
}

// ============================================
// DVIR ACCOUNTABILITY FLAGS
// ============================================

// TASK-10193 — a per-truck reminder row (no driver paired; sent to the crew
// lead or the office) carries driverName "" — never render a blank title.
function _dvirFlagWho(f) {
  if (f && f.driverName) return f.driverName;
  if (f && f.pairedNotPicked) return "Paired driver didn't pick it at clock-in";
  return f && f.recipientMode === 'lead' && f.notifyPersonName
    ? `No driver paired (sent to ${f.notifyPersonName})`
    : 'No driver paired';
}

function showDvirFlags(entityId, type) {
  const flags = type === 'driver'
    ? RosterState.dvirFlags.filter(f => f.driverId === entityId)
    : RosterState.dvirFlags.filter(f => f.truckId === entityId);

  if (flags.length === 0) return;

  const title = type === 'driver'
    ? `Missing DVIRs - ${flags[0].driverName}`
    : `Missing DVIRs - Unit ${flags[0].truckUnit}`;

  const itemsHtml = flags.map(f => {
    const flaggedAt = f.flaggedAt?.toDate?.() || (f.flaggedAt ? new Date(f.flaggedAt) : null);
    const hoursAgo = flaggedAt && !isNaN(flaggedAt.getTime()) ? Math.round((Date.now() - flaggedAt.getTime()) / (1000 * 60 * 60)) : null;
    const timeLabel = hoursAgo !== null ? (hoursAgo < 24 ? `${hoursAgo}h ago` : `${Math.round(hoursAgo / 24)}d ago`) : '';
    // Resolve full truck display name (with nickname if available)
    const truck = RosterState.trucks.find(t => t.id === f.truckId);
    const truckDisplay = truck && window.getUnitDisplay ? window.getUnitDisplay(truck) : (f.truckUnit || '?');
    return `
      <div class="dvir-flag-item" style="display:flex;align-items:center;gap:8px;">
        <div style="flex:1;">
          <div class="dvir-flag-item-title">${escapeHtml(_dvirFlagWho(f))}</div>
          <div class="dvir-flag-item-meta">
            <span class="material-icons" style="font-size:13px;vertical-align:middle;margin-right:2px;">local_shipping</span> ${escapeHtml(truckDisplay)}
            ${f.jobTitle ? ` &bull; ${escapeHtml(f.jobTitle)}` : ''}
            ${f.date ? `<br>Date: ${f.date}` : ''}
            ${timeLabel ? ` &bull; Flagged ${timeLabel}` : ''}
          </div>
        </div>
        <button onclick="window.RosterModule.dismissDvirFlag('${f.id}')" title="Dismiss flag"
          style="flex-shrink:0;background:#EF4444;color:#fff;border:none;border-radius:6px;padding:6px 12px;font-size:0.8rem;cursor:pointer;display:flex;align-items:center;gap:4px;">
          <span class="material-icons" style="font-size:16px;">close</span> Dismiss
        </button>
      </div>
    `;
  }).join('');

  // Create modal overlay
  const overlay = document.createElement('div');
  overlay.className = 'dvir-flag-modal-overlay';
  overlay.onclick = (e) => { if (e.target === overlay) overlay.remove(); };
  overlay.innerHTML = `
    <div class="dvir-flag-modal">
      <h3><span class="material-icons">warning</span> ${escapeHtml(title)}</h3>
      ${itemsHtml}
      <button class="btn btn-secondary" style="width:100%;margin-top:var(--spacing-md);" onclick="this.closest('.dvir-flag-modal-overlay').remove()">Close</button>
    </div>
  `;
  document.body.appendChild(overlay);
}

async function dismissDvirFlag(flagId) {
  try {
    const tenant = window.AppState?.tenant || 'demo';
    await updateDoc(doc(db, 'tenants', tenant, 'dvirAccountability', flagId), {
      status: 'dismissed',
      dismissedAt: new Date(),
    });
    window.showToast?.('Flag dismissed — still visible in HR Directory', 'success');
    // Close the modal
    const overlay = document.querySelector('.dvir-flag-modal-overlay');
    if (overlay) overlay.remove();
  } catch (error) {
    console.error('[Roster] Error dismissing DVIR flag:', error);
    window.showToast?.('Error dismissing flag', 'error');
  }
}

// ============================================
// DVIR MISSED COUNTER RESET
// ============================================

async function resetMissedDvirCount(personId) {
  try {
    const tenant = window.AppState?.tenant || 'demo';
    await updateDoc(doc(db, 'tenants', tenant, 'people', personId), {
      missedDvirCount: 0,
      lastMissedDvirDate: null,
    });
    // Update the UI immediately
    const statsEl = document.getElementById('person-dvir-stats-content');
    if (statsEl) statsEl.innerHTML = '<span class="dvir-stats-none">None</span>';
    window.showToast?.('Missed DVIR counter reset', 'success');
  } catch (error) {
    console.error('[Roster] Error resetting missed DVIR count:', error);
    window.showToast?.('Error resetting counter', 'error');
  }
}

// ============================================
// JSA ACCOUNTABILITY FLAGS
// ============================================

// TASK-9263 (perf slim) — showJsaFlags / dismissJsaFlag removed: no caller since
// 9ead73755 moved the JSA badges to Compliance (compliance.js owns dismiss).

// ============================================
// JSA MISSED COUNTER RESET
// ============================================

async function resetMissedJsaCount(personId) {
  try {
    const tenant = window.AppState?.tenant || 'demo';
    await updateDoc(doc(db, 'tenants', tenant, 'people', personId), {
      missedJsaCount: 0,
      lastMissedJsaDate: null,
    });
    const statsEl = document.getElementById('person-jsa-stats-content');
    if (statsEl) statsEl.innerHTML = '<span class="dvir-stats-none">None</span>';
    window.showToast?.('Missed JSA counter reset', 'success');
  } catch (error) {
    console.error('[Roster] Error resetting missed JSA count:', error);
    window.showToast?.('Error resetting counter', 'error');
  }
}

// ============================================
// INLINE TYPE CREATION
// ============================================

/**
 * Show inline input row below a type dropdown for creating a custom type.
 * @param {string} kind - 'truckTypes' | 'trailerTypes' | 'assetCategories'
 * @param {string} selectId - ID of the <select> element
 */
function showAddTypeInline(kind, selectId) {
  const select = document.getElementById(selectId);
  if (!select) return;
  const wrapper = select.closest('.select-with-add') || select.parentElement;

  // Don't add duplicate prompt
  if (wrapper.parentElement.querySelector('.inline-type-prompt')) return;

  // Get existing custom types for this kind
  const customTypes = window.AppState?.settings?.customTypes?.[kind] || [];

  const prompt = document.createElement('div');
  prompt.className = 'inline-type-prompt';

  // Build custom types list (deletable) + add input
  let customListHTML = '';
  if (customTypes.length > 0) {
    customListHTML = `<div class="custom-type-list">
      <span class="custom-type-list-label">Custom types:</span>
      ${customTypes.map(t => `
        <div class="custom-type-chip" data-slug="${escapeHtml(t.slug)}">
          <span>${escapeHtml(t.label)}</span>
          <button type="button" class="btn-remove-type" title="Remove ${escapeHtml(t.label)}">
            <span class="material-icons">close</span>
          </button>
        </div>
      `).join('')}
    </div>`;
  }

  prompt.innerHTML = `
    <div style="display:flex;justify-content:flex-end;">
      <button type="button" class="btn-close-type-prompt" title="Close" style="background:none;border:none;cursor:pointer;padding:2px 4px;color:var(--text-secondary,#475569);">
        <span class="material-icons" style="font-size:18px;">close</span>
      </button>
    </div>
    ${customListHTML}
    <div class="inline-type-form">
      <input type="text" class="inline-type-input" placeholder="e.g. Pump Truck" maxlength="50" autocomplete="off">
      <button type="button" class="btn btn-sm btn-primary btn-inline-save" style="padding:6px 14px;font-size:0.8rem;white-space:nowrap;">
        <span class="material-icons" style="font-size:16px;">add</span> Add
      </button>
    </div>
  `;

  wrapper.parentElement.appendChild(prompt);

  const input = prompt.querySelector('.inline-type-input');
  input.focus();

  const save = () => saveNewTypeInline(kind, selectId, prompt);

  prompt.querySelector('.btn-inline-save').addEventListener('click', save);
  prompt.querySelector('.btn-close-type-prompt').addEventListener('click', () => prompt.remove());
  input.addEventListener('keydown', (e) => {
    if (e.key === 'Enter') { e.preventDefault(); save(); }
    if (e.key === 'Escape') prompt.remove();
  });

  // Wire up delete buttons on custom type chips
  prompt.querySelectorAll('.btn-remove-type').forEach(btn => {
    btn.addEventListener('click', () => {
      const chip = btn.closest('.custom-type-chip');
      const slug = chip.dataset.slug;
      const label = chip.querySelector('span').textContent;
      deleteCustomType(kind, slug, label, selectId, prompt);
    });
  });
}

/**
 * Save a new custom type from the inline input.
 */
async function saveNewTypeInline(kind, selectId, promptEl) {
  const input = promptEl.querySelector('.inline-type-input');
  const _clamp = window.clampText || ((s, n) => s && s.length > n ? s.slice(0, n) : s || '');
  const label = _clamp(input.value.trim(), 50);
  if (!label) {
    window.showToast?.('Enter a name for the new type', 'error');
    input.focus();
    return;
  }

  // Slugify: lowercase, replace spaces/special chars with underscore
  const slug = label.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '');
  if (!slug) {
    window.showToast?.('Invalid name', 'error');
    return;
  }

  // Check for duplicates in merged list
  // TASK-10198 — also refuse a near-duplicate ("Dump Truck" beside a stored
  // `dumptruck`): case, spaces, _ and - and a plural s don't count. The key
  // lives in the lazy rosterTypeDrift.js; if it can't load, exact match only.
  const dup = getTypeList(kind).find(t => t.slug === slug) || (await _tdLoad())?.typeDupOf(slug, getTypeListWithInUse(kind, _inUseItemsFor(kind)));
  if (dup) {
    window.showToast?.(dup.slug === slug ? 'That type already exists' : `Same as "${dup.label}"`, 'error');
    input.focus();
    return;
  }

  // Enforce 25 custom types per category limit
  const currentList = window.AppState?.settings?.customTypes?.[kind] || [];
  if (currentList.length >= 25) {
    window.showToast?.('Maximum 25 custom types per category', 'error');
    return;
  }

  // Save to Firestore tenant settings
  try {
    const tenant = window.AppState?.tenant || 'demo';
    const currentCustomTypes = window.AppState?.settings?.customTypes || {};
    const kindList = [...(currentCustomTypes[kind] || []), { slug, label }];
    const updatedCustomTypes = { ...currentCustomTypes, [kind]: kindList };

    // TASK-9337 -- write ONLY this kind. Sending the whole customTypes map
    // from AppState round-tripped every OTHER kind through a possibly-stale
    // snapshot (TASK-9255 class: it could revert billing's apCategories), and
    // the module-scoped rule now refuses a roster member moving any kind
    // roster does not own.
    await updateDoc(doc(db, 'tenants', tenant), {
      [`settings.customTypes.${kind}`]: kindList
    });

    // Update AppState
    if (!window.AppState.settings) window.AppState.settings = {};
    window.AppState.settings.customTypes = updatedCustomTypes;

    // Refresh the dropdown and select the new type (TASK-9339: keep in-use types on offer)
    populateTypeDropdown(selectId, kind, false, { inUse: _inUseItemsFor(kind) });
    document.getElementById(selectId).value = slug;

    // Also refresh any visible filter dropdowns
    refreshFilterDropdowns(kind);

    promptEl.remove();
    window.showToast?.(`"${label}" added`, 'success');
  } catch (error) {
    console.error('[Roster] Error saving custom type:', error);
    window.showToast?.('Error saving custom type', 'error');
  }
}

/**
 * Delete a custom type. Removes from Firestore settings.
 * Vehicles with this type will still display the raw slug as a label fallback.
 */
async function deleteCustomType(kind, slug, label, selectId, promptEl) {
  // Check if any vehicles/trailers/assets are currently using this type
  let inUseCount = 0;
  if (kind === 'truckTypes') {
    inUseCount = RosterState.trucks.filter(t => (t.type || '').toLowerCase() === slug).length;
  } else if (kind === 'trailerTypes') {
    inUseCount = RosterState.trailers.filter(t => (t.type || '').toLowerCase() === slug).length;
  } else if (kind === 'assetCategories') {
    inUseCount = RosterState.assets.filter(a => (a.category || '').toLowerCase() === slug).length;
  }

  if (inUseCount > 0) {
    const itemWord = kind === 'assetCategories' ? 'asset' : (kind === 'trailerTypes' ? 'trailer' : 'vehicle');
    window.showToast?.(`Can't remove "${label}" — ${inUseCount} ${itemWord}${inUseCount > 1 ? 's' : ''} still using it`, 'error');
    return;
  }

  if (!confirm(`Remove "${label}"?`)) return;

  try {
    const tenant = window.AppState?.tenant || 'demo';
    const currentCustomTypes = window.AppState?.settings?.customTypes || {};
    const kindList = (currentCustomTypes[kind] || []).filter(t => t.slug !== slug);
    const updatedCustomTypes = { ...currentCustomTypes, [kind]: kindList };

    // TASK-9337 -- write ONLY this kind. Sending the whole customTypes map
    // from AppState round-tripped every OTHER kind through a possibly-stale
    // snapshot (TASK-9255 class: it could revert billing's apCategories), and
    // the module-scoped rule now refuses a roster member moving any kind
    // roster does not own.
    await updateDoc(doc(db, 'tenants', tenant), {
      [`settings.customTypes.${kind}`]: kindList
    });

    // Update AppState
    if (!window.AppState.settings) window.AppState.settings = {};
    window.AppState.settings.customTypes = updatedCustomTypes;

    // Refresh the dropdown. TASK-9339 — keep in-use types on offer and keep what the
    // form had selected (a rebuild used to drop an edited record's legacy type, so
    // a save would silently move it), unless that selection IS the removed type.
    const prevSel = document.getElementById(selectId)?.value;
    populateTypeDropdown(selectId, kind, false, {
      inUse: _inUseItemsFor(kind),
      keep: prevSel && prevSel !== slug ? prevSel : undefined,
    });
    if (prevSel && prevSel !== slug && document.getElementById(selectId)) document.getElementById(selectId).value = prevSel;
    refreshFilterDropdowns(kind);

    // Remove the chip from the inline prompt
    const chip = promptEl.querySelector(`.custom-type-chip[data-slug="${slug}"]`);
    if (chip) chip.remove();

    // If no more custom types, remove the list container
    const list = promptEl.querySelector('.custom-type-list');
    if (list && !list.querySelector('.custom-type-chip')) list.remove();

    window.showToast?.(`"${label}" removed`, 'success');
  } catch (error) {
    console.error('[Roster] Error deleting custom type:', error);
    window.showToast?.('Error removing type', 'error');
  }
}

/**
 * Refresh filter dropdowns after adding a new custom type.
 */
function refreshFilterDropdowns(kind) {
  if (kind === 'truckTypes') {
    populateTypeFilterDropdown('filter-trucks-type', 'truckTypes');
  } else if (kind === 'trailerTypes') {
    populateTypeFilterDropdown('filter-trailers-type', 'trailerTypes');
  } else if (kind === 'assetCategories') {
    populateTypeFilterDropdown('filter-assets-category', 'assetCategories');
  }
}

// ============================================
// PRINT ROSTER LIST — lazy (views/rosterPrintList.js, TASK-9911 M3 cold-load
// slim). The Print button lives inside the dialog, so by the time it is tapped
// the module is loaded and printRosterList runs in the same tap (window.open
// keeps its user gesture; no await before it).
// ============================================
let _printListLoad = null;
let _printListMod = null;
function _loadPrintList() {
  if (!_printListLoad) {
    _printListLoad = __ddChunkWrap(import('./rosterPrintList.js?v=9911003')).then((m) => (_printListMod = m)).catch((e) => {
      _printListLoad = null;
      throw e;
    });
  }
  return _printListLoad;
}
function showPrintListModal() {
  _loadPrintList().then((m) => m.showPrintListModal()).catch((e) => {
    console.warn('[Roster] rosterPrintList load failed', e);
    window.showToast?.('Could not open Print. Check your connection and try again.', 'error');
  });
}
function printRosterList() {
  const deps = { RosterState, getLiveYardLabel, getYardLabel, showToast: (...a) => window.showToast?.(...a) };
  if (_printListMod) { _printListMod.printRosterList(deps); return; }
  _loadPrintList().then((m) => m.printRosterList(deps)).catch(() => window.showToast?.('Could not print. Check your connection and try again.', 'error'));
}

// ============================================
// YARD FILTER & TRANSFER FUNCTIONS
// ============================================

/** Show/hide yard filter dropdowns based on whether tenant has yards configured */
function refreshYardFilters() {
  const yards = getYardsList();
  const hasYards = yards.length > 0;
  const filterIds = ['filter-people-yard', 'filter-trucks-yard', 'filter-trailers-yard', 'filter-assets-yard'];
  for (const id of filterIds) {
    const el = document.getElementById(id);
    if (el) {
      el.style.display = hasYards ? '' : 'none';
      if (hasYards) {
        populateYardDropdown(id, true);
      }
    }
  }
  // TASK-9369 — the Items|Branches control + Add Branch follow the same gate.
  // Also reached from app.js subscribeTenantSettings when ANOTHER session
  // changes settings.yards (COD Edge Case #5): a tenant that just gained its
  // first yard must see the toggle, and a Branches view must gain/lose a card,
  // without a reload — so re-render the assets grid when it is on screen.
  _syncAssetsGroupByUi();
  if (RosterState._assetsLoaded && document.getElementById('list-assets')) renderAssetsList();
}

/** Transfer entity to a different yard — uses writeBatch for atomicity */
async function transferToYard(entityType, entityId) {
  const entityMap = {
    people: { list: RosterState.people, label: 'person', nameKey: 'name' },
    trucks: { list: RosterState.trucks, label: 'vehicle', nameKey: 'unit' },
    trailers: { list: RosterState.trailers, label: 'trailer', nameKey: 'unit' },
    assets: { list: RosterState.assets, label: 'asset', nameKey: 'name' }
  };
  const meta = entityMap[entityType];
  if (!meta) return;
  if (entityType === 'people' && _rosterRefuseViewUploadOnly()) return; // TASK-9894
  const entity = meta.list.find(e => e.id === entityId);
  if (!entity) return;

  const yards = getYardsList();
  if (yards.length === 0) {
    window.showToast?.('No yards configured. Add yards first.', 'error');
    return;
  }

  // Build transfer modal
  let modal = document.getElementById('modal-transfer-yard');
  if (!modal) {
    modal = document.createElement('div');
    modal.id = 'modal-transfer-yard';
    modal.className = 'modal hidden';
    document.body.appendChild(modal);
  }

  const entityLabel = entityType === 'people'
    ? escapeHtml(entity.name)
    : escapeHtml(getUnitDisplay(entity) || entity.name || entity.partNumber);
  const fromYard = entity.yardId ? getYardLabel(entity.yardId) : 'Unassigned';

  modal.innerHTML = `
    <div class="modal-content modal-small" style="text-align:center;padding:var(--spacing-xl);">
      <div style="display:flex;flex-direction:column;align-items:center;gap:var(--spacing-sm);margin-bottom:var(--spacing-md);">
        <span class="material-icons" style="font-size:48px;color:#7C3AED;">swap_horiz</span>
        <h3 style="font-size:1.25rem;font-weight:600;margin:0;">Transfer ${meta.label}</h3>
      </div>
      <p style="color:var(--text-secondary);margin-bottom:var(--spacing-sm);line-height:1.5;">
        <strong>${entityLabel}</strong><br>
        Current: <span class="badge badge-yard">${escapeHtml(fromYard)}</span>
      </p>
      <div class="form-group" style="text-align:left;margin-bottom:var(--spacing-md);">
        <label for="transfer-dest-yard" style="font-weight:600;font-size:0.85rem;">Destination Yard</label>
        <select id="transfer-dest-yard" class="form-control"></select>
      </div>
      <div class="form-group" style="text-align:left;margin-bottom:var(--spacing-lg);">
        <label for="transfer-notes" style="font-weight:600;font-size:0.85rem;">Notes (optional)</label>
        <input type="text" id="transfer-notes" class="form-control" placeholder="Reason for transfer...">
      </div>
      <div style="display:flex;gap:var(--spacing-md);justify-content:center;">
        <button class="btn btn-secondary" onclick="document.getElementById('modal-transfer-yard').classList.add('hidden')" style="flex:1;max-width:140px;">Cancel</button>
        <button class="btn btn-primary" id="btn-confirm-transfer" style="flex:1;max-width:140px;">
          <span class="material-icons" style="font-size:16px;">swap_horiz</span> Transfer
        </button>
      </div>
    </div>`;

  // Populate dest dropdown. TASK-9929 FU — the current yard is listed but
  // disabled ("(current)") so it reads as where the unit is, not a missing yard.
  const destSelect = modal.querySelector('#transfer-dest-yard');
  destSelect.innerHTML = '';
  const curYard = entity.yardId || '';
  const addOpt = (value, label) => {
    const opt = document.createElement('option');
    opt.value = value;
    opt.textContent = value === curYard ? `${label} (current)` : label;
    opt.disabled = value === curYard;
    destSelect.appendChild(opt);
  };
  addOpt('', 'Unassigned (remove from yard)');
  for (const y of yards) addOpt(y.slug, y.label);
  const firstOpen = Array.from(destSelect.options).find(o => !o.disabled);
  if (firstOpen) destSelect.value = firstOpen.value;

  modal.classList.remove('hidden');

  // Wire up confirm
  modal.querySelector('#btn-confirm-transfer').onclick = async () => {
    const toYardId = destSelect.value;
    const toYardName = toYardId ? getYardLabel(toYardId) : '';
    const notes = modal.querySelector('#transfer-notes').value.trim();
    const tenant = window.AppState?.tenant || 'demo';
    // TASK-9929 — "Unassigned" on a unit with no yard is not a move; the rules
    // refuse a transfer row that does not change the yard, so say it plainly.
    if (toYardId === (entity.yardId || '')) {
      modal.classList.add('hidden');
      window.showToast?.(`Already ${toYardName ? 'at ' + toYardName : 'unassigned'}`, 'info');
      return;
    }

    try {
      const batch = writeBatch(db);

      // Update entity doc
      const collName = entityType;
      batch.update(doc(db, 'tenants', tenant, collName, entityId), {
        yardId: toYardId,
        yardName: toYardName,
        updatedAt: new Date()
      });

      // Create audit log entry
      const transferRef = doc(collection(db, 'tenants', tenant, 'yardTransfers'));
      batch.set(transferRef, {
        entityType,
        entityId,
        entityLabel: entity.name || entity.unit || entity.partNumber || entityId,
        // TASK-9929 FU (COD) — the rule compares this to the stored yardId; a stored
        // null must be sent as null (no writer sets one today; 0 live docs 2026-10-17).
        fromYardId: entity.yardId === null ? null : (entity.yardId || ''),
        fromYardName: entity.yardName || (entity.yardId ? getYardLabel(entity.yardId) : ''),
        toYardId: toYardId,
        toYardName: toYardName,
        transferredBy: window.AppState?.user?.email || 'unknown',
        // TASK-9929 — firestore.rules pins this to the caller (yardTransfers),
        // fromYardId to the unit's yard before the batch, and the time to the server's.
        transferredByUid: auth.currentUser?.uid || null,
        transferredAt: serverTimestamp(),
        notes: notes || null
      });

      await batch.commit();

      // Update local state
      entity.yardId = toYardId;
      entity.yardName = toYardName;

      modal.classList.add('hidden');
      window.showToast?.(`Transferred to ${toYardName || 'Unassigned'}`, 'success');

      // Re-render
      if (entityType === 'people') renderPeopleList();
      else if (entityType === 'trucks') renderTrucksList();
      else if (entityType === 'trailers') renderTrailersList();
      else if (entityType === 'assets') renderAssetsList();
    } catch (err) {
      console.error('[Roster] Transfer error:', err);
      // TASK-9929 FU — the rule refuses a row whose fromYardId is stale (moved in another tab).
      window.showToast?.(err?.code === 'permission-denied'
        ? 'Not transferred — this may have been moved since the list loaded. Refresh and try again.'
        : 'Error transferring', 'error');
    }
  };
}

// ============================================
// STAFF LOGIN (TASK-319 slice 5)
// Multi-user staff login — admin sends an invite, invitee gets their own
// Firebase Auth identity + members/{uid} doc with explicit module access.
// Mirrors the slice-2 sendUserInvite callable contract.
// ============================================
const STAFF_LOGIN_ROLE_PRESETS = {
  dispatcher: { role: 'dispatcher', modules: ['dispatch', 'roster'] },
  mechanic:   { role: 'mechanic',   modules: ['maintenance', 'jobboard'] },
  employee:   { role: 'staff',      modules: ['clock'] },  // TASK-370 — vanilla employee self-service (clock in/out, own hours, own time-off). NOT workforce (=manage-others, TASK-360 split). NOT hr (admin approver).
  // TASK-1489 / TASK-1500 — outside compliance manager. Modules transcribed from
  // the matrix Kolsen wrote himself (_featureRequests/PdREZYbcDvwdsZ7MQ5AC):
  //   workforce   — "access to Workforce to obtain the total OSHA hours"
  //   dispatch    — "reviewer access here to monitor for hours of service violations"
  //   maintenance — "access to inspections & Maintenance"
  //   roster      — "access to Roster, but restricted from sensitive personnel info"
  //   hr          — "access to HR, but restricted from sensitive personnel info"
  //   compliance  — the reason they're here at all
  // Deliberately ABSENT: `billing` ("we don't need access to AR/AP"), `bi` ("we
  // don't need access to BI"), and — critically — `hr-directory`, which is the
  // SSN / W-4 / I-9 grant. "Sensitive information stays locked down" is the
  // admin-gated READ boundary (peopleTax / isTenantAdmin), which is why the role
  // stays `staff`: an outside consultant must never trip isTenantAdmin().
  complianceManager: {
    role: 'staff',
    modules: ['compliance', 'roster', 'hr', 'maintenance', 'workforce', 'dispatch'],
    // Kolsen asked for REVIEWER access (no job creation, no crew notifications).
    // viewUploadOnly is the existing capability that expresses that, enforced in
    // firestore.rules via isViewUploadOnly() — not a UI-only flag.
    viewUploadOnly: true,
    label: 'Compliance Manager',
  },
  // TASK-4710 — 'projects' joins the full catalog (14). The dispatcher preset
  // deliberately does NOT gain it: `dispatch` already carries full project
  // access in firestore.rules, and an edit-mode save re-derives role from an
  // EXACT preset match — adding a key there would silently demote every
  // existing dispatcher to 'staff' on their next no-op Edit Access save.
  // TASK-9619 — 'notes' joins the full catalog (15). Same shape as 4710: an
  // existing admin holding 14 is kept admin on a no-op Edit Access save by the
  // currentRole guard in _staffLoginRoleFor; the dispatcher preset stays as is.
  // TASK-9778 — 'receipts' joins the full catalog (16), same shape as 9619:
  // the currentRole guard keeps an existing admin admin on a no-op save.
  // TASK-9823 — 'calls' joins the full catalog (17), same shape again.
  admin:      { role: 'admin',      modules: ['dispatch', 'roster', 'maintenance', 'hr', 'hr-directory', 'crm', 'clock', 'billing', 'workforce', 'bi', 'compliance', 'jobboard', '811-permits', 'projects', 'notes', 'receipts', 'calls'] },
};
const STAFF_LOGIN_STAFF_SOFT_CAP = 50;

// TASK-366 — Single source of truth for the custom-module checkbox grid. Used
// by both the Set-up-login (invite) modal AND the Edit Access (edit) modal so
// the two flows share an identical module list. The role-preset rail above is
// also shared via _staffLoginRolePresetRailHtml() below. mode suffix keeps the
// `input` names + ids distinct so the two modals don't share radio groups.
const STAFF_LOGIN_MODULE_OPTIONS = [
  { value: 'dispatch',     label: 'Dispatch' },
  { value: 'roster',       label: 'Roster' },
  { value: 'maintenance',  label: 'Maintenance' },
  { value: 'hr',           label: 'HR' },
  { value: 'hr-directory', label: 'HR Directory' },
  { value: 'crm',          label: 'CRM' },
  { value: 'clock',        label: 'Clock' },
  { value: 'billing',      label: 'AR / AP Billing' },
  { value: 'workforce',    label: 'Workforce' },
  { value: 'bi',           label: 'BI' },
  { value: 'compliance',   label: 'Compliance' },
  { value: 'jobboard',     label: 'Job Board' },
  { value: '811-permits',  label: 'Locates (811)' },
  // TASK-4710 — grants the module; WHICH projects (viewer/member/manager on
  // each) is set per project in projects/{id}.access, by dispatch.
  { value: 'projects',     label: 'Projects' },
  // TASK-9619 — the notes orb by the logo + @mention sharing. Off until ticked.
  { value: 'notes',        label: 'Notes' },
  // TASK-9778 — upload / match / approve receipts and submitted expenses,
  // WITHOUT invoices, AR/AP bills, revenue, the price book or payroll. The
  // narrow office grant (e.g. someone who only does receipts). AR / AP Billing
  // already includes everything this gives.
  { value: 'receipts',     label: 'Receipts & Expenses' },
  // TASK-9823 — recorded calls to CRM contacts (spends SMS credits; the
  // recordings are sensitive). Off until ticked; the owner always has it.
  { value: 'calls',        label: 'Calls' },
];

// TASK-608 — modules that expose sensitive data (PII / FCRA consumer reports /
// payroll / financials). Checking any of these surfaces the inline soft warning.
//   hr / hr-directory — SSN, W-4, I-9, pay, discipline
//   compliance        — MVR / PSP / drug-test (FCRA consumer reports)
//   workforce         — payroll CSVs (PII-dense)
//   billing / bi      — financials
//   receipts          — company spend, card last 4 (TASK-9778)
const STAFF_LOGIN_SENSITIVE_MODULES = new Set(['hr', 'hr-directory', 'compliance', 'workforce', 'billing', 'bi', 'receipts', 'calls']); // TASK-9823: call recordings

// TASK-608 — derive members/{uid}.role from the chosen module set. role is a
// behind-the-scenes write tier, NOT module-count-derived (COD Codex: role
// 'admin' trips isTenantAdmin() broadly). Rule: the role is whatever the last
// quick-fill set (tracked in a hidden field); a hand-picked custom grant is
// always 'staff'.
//
// TASK-1389 (Chris 2026-08-06) — DELIBERATE REVERSAL of the TASK-608 default
// for the FULL module set. Old rule: the Admin quick-fill was the ONLY path to
// role 'admin'; hand-picking all 13 modules silently left role 'staff' —
// which is how Chris's own login ended up staff-with-all-13 and lost the
// admin-gated assistant FAB with no explanation. His call: "the point of
// giving full access would be to be admin status." New rule (COD-unanimous:
// explicit choice, never a silent side-effect): when the checked set exactly
// equals the Admin preset (all 13), an EXPLICIT amber "grant Admin status?"
// toggle appears (pre-checked — full access defaults to admin) and IT decides
// admin vs staff. Unchecking it is the legit "see-all, manage-nothing" staff
// grant. Non-full hand-picked sets are unchanged: always 'staff'.
function _staffLoginRoleFor(mode, modules) {
  // TASK-1389 — full module set: the explicit Admin toggle is the sole
  // authority (both the Admin quick-fill and a hand-picked full grid land
  // here, so the two paths converge on one derivation point).
  if (_staffLoginIsFullAccessSet(modules)) {
    return document.getElementById(`staff-login-admin-toggle-${mode}`)?.checked ? 'admin' : 'staff';
  }
  // Integrated-gate (Code Reviewer + Independent, converging): LEGACY-ADMIN
  // DEMOTION TRAP — an existing admin whose stored modules ≠ today's 13-module
  // preset (granted before newer modules joined it) must NOT be silently
  // demoted to staff by a no-op Edit Access save. On EDIT, the member's actual
  // current role keeps admin unless the operator explicitly unchecks the
  // (visible, seeded) Admin toggle. TASK-608's derive-from-modules rule stands
  // for everyone else.
  if (mode === 'edit') {
    const cur = document.getElementById('staff-login-edit-uid')?.dataset.currentRole;
    const tgl = document.getElementById('staff-login-admin-toggle-edit');
    if (cur === 'admin' && (!tgl || tgl.checked)) return 'admin';
  }
  const presetRole = document.getElementById(`staff-login-role-${mode}`)?.value || '';
  // Only honor a preset role if the current checkbox set STILL matches that
  // preset exactly — otherwise the admin edited the grid by hand → 'staff'.
  for (const [, cfg] of Object.entries(STAFF_LOGIN_ROLE_PRESETS)) {
    if (cfg.role !== presetRole) continue;
    const a = cfg.modules.slice().sort();
    const b = (modules || []).slice().sort();
    if (a.length === b.length && a.every((m, i) => m === b[i])) return cfg.role;
  }
  return 'staff';
}

// TASK-1389 — true when the module set exactly equals the Admin preset's
// full set (all 13). Order-insensitive.
function _staffLoginIsFullAccessSet(modules) {
  const a = STAFF_LOGIN_ROLE_PRESETS.admin.modules.slice().sort();
  const b = (modules || []).slice().sort();
  return a.length === b.length && a.every((m, i) => m === b[i]);
}

// TASK-1389 slice 2 — login-tier chip HTML. Shared by the person-card
// Active-login row and the Edit Access modal header so the member's actual
// members/{uid}.role is visible somewhere (its invisibility was the whole
// incident). Owner never renders in the staff-login section (filtered out),
// but the label is mapped for completeness.
const STAFF_LOGIN_TIER_LABELS = {
  owner: 'Owner',
  admin: 'Admin tier',
  dispatcher: 'Dispatcher',
  mechanic: 'Mechanic',
  staff: 'Staff tier',
};
function _staffLoginTierChipHtml(role) {
  const r = role || 'staff';
  const label = STAFF_LOGIN_TIER_LABELS[r] || (r.charAt(0).toUpperCase() + r.slice(1));
  const isAdminTier = (r === 'admin' || r === 'owner');
  const bg = isAdminTier ? '#fef3c7' : '#f1f5f9';
  const border = isAdminTier ? '#f59e0b' : '#cbd5e1';
  const color = isAdminTier ? '#92400e' : '#475569';
  return `<span class="staff-login-tier-chip" style="display:inline-block;padding:1px 8px;border:1px solid ${border};border-radius:9999px;background:${bg};color:${color};font-size:0.7rem;font-weight:600;vertical-align:middle;white-space:nowrap;">${escapeHtml(label)}</span>`;
}

// TASK-366 — Role-preset rail + custom-module grid HTML helper. Shared between
// invite + edit modals. mode is 'invite' or 'edit' and is used as a suffix on
// input names + ids + classes so the two modals' radio groups don't collide
// if both happen to be mounted to the document body simultaneously.
// TASK-608 — MODULE-FIRST staff-login modal (Chris 2026-06-15). Mirrors access
// PINs (app.js — pure {modules:[...]}). The MODULE CHECKBOX GRID leads; role
// presets are optional ONE-TAP quick-fill chips, NOT the gate. A "View & upload
// only" capability toggle (members/{uid}.viewUploadOnly, enforced in
// firestore.rules) and the sensitive-module soft warning live below the grid.
//
// `mode` is 'invite' or 'edit'; it suffixes input names/ids/classes so the two
// modals can coexist in the DOM without colliding.
//
// SENSITIVE_MODULES — checking any of these reveals the inline soft warning
// (PII / FCRA consumer-reports / payroll). hr-directory keeps the legacy SSN
// copy; the broader set adds hr/billing/bi/compliance/workforce.
function _staffLoginRoleAndModuleGridHtml(mode) {
  return `
    <div class="form-group">
      <label>Modules <span style="font-weight:400;color:var(--text-muted);font-size:0.85rem;">— what this person can access</span></label>
      <div class="staff-login-module-grid" id="staff-login-custom-modules-${mode}" style="display:grid;grid-template-columns:1fr 1fr;gap:6px;">
        ${STAFF_LOGIN_MODULE_OPTIONS.map(m => `
          <!-- TASK-9994: the Locates (811) grant is hidden (not removed: a saved grant round-trips) while the company has 811 off. -->
          <label id="staff-login-module-opt-${mode}-${m.value}" style="display:${m.value === '811-permits' && window.AppState?.settings?.enable811 !== true && window.AppState?.isDemo !== true ? 'none' : 'flex'};align-items:center;gap:6px;cursor:pointer;font-weight:400;">
            <input type="checkbox" class="staff-login-custom-module-${mode}" value="${m.value}"> ${m.label}
          </label>
        `).join('')}
      </div>
      <div id="staff-login-modules-empty-${mode}" style="display:none;margin-top:6px;font-size:0.8rem;color:var(--status-danger,#dc2626);">
        Pick at least one module — or use a quick-fill below.
      </div>
      <!-- TASK-1489 — shown only in personless mode. Clock and Job Board are
           self-service, personId-scoped views ("MY hours", "MY schedule"). With no
           roster person there is nothing for them to scope TO, so the server
           rejects them on a personless invite and we hide them here so the admin
           never meets that error. -->
      <div id="staff-login-modules-personless-note-${mode}" style="display:none;margin-top:6px;font-size:0.8rem;color:var(--text-secondary);">
        Clock and Job Board aren't offered for an outside user — they're personal
        self-service views that need an employee record. Grant <strong>Workforce</strong>
        for timeclock data and <strong>Dispatch</strong> for the schedule instead.
      </div>
    </div>
    <div class="form-group">
      <input type="hidden" id="staff-login-role-${mode}" value="">
      <label style="font-weight:400;color:var(--text-muted);font-size:0.85rem;">Quick-fill (optional)</label>
      <div style="display:flex;flex-wrap:wrap;gap:6px;">
        <button type="button" class="btn btn-xs btn-secondary staff-login-quickfill-${mode}" data-preset="dispatcher">Dispatcher</button>
        <button type="button" class="btn btn-xs btn-secondary staff-login-quickfill-${mode}" data-preset="mechanic">Mechanic</button>
        <button type="button" class="btn btn-xs btn-secondary staff-login-quickfill-${mode}" id="staff-login-quickfill-${mode}-employee" data-preset="employee">Employee</button>
        <button type="button" class="btn btn-xs btn-secondary staff-login-quickfill-${mode}" data-preset="complianceManager">Compliance Manager</button>
        <button type="button" class="btn btn-xs btn-secondary staff-login-quickfill-${mode}" data-preset="admin">Admin (all)</button>
      </div>
    </div>
    <div id="staff-login-admin-block-${mode}" style="display:none;align-items:flex-start;gap:8px;padding:10px 12px;margin-top:4px;border:1px solid #f59e0b;border-radius:6px;background:#fef3c7;color:#92400e;font-size:0.85rem;line-height:1.4;">
      <span class="material-icons" style="font-size:1.1rem;color:#b45309;flex-shrink:0;">admin_panel_settings</span>
      <label style="display:flex;align-items:flex-start;gap:8px;cursor:pointer;font-weight:400;margin:0;flex:1;">
        <input type="checkbox" id="staff-login-admin-toggle-${mode}" style="margin-top:2px;flex-shrink:0;">
        <span><strong>Full access — grant Admin status?</strong><br>
        <span style="font-size:0.8rem;">Admin adds: delete records and view SSN / tax data. (Managing logins is its own switch below, set by the account owner.) Uncheck to keep a Staff login that can see every module but not administer them.</span></span>
      </label>
    </div>
    <div class="form-group" style="margin-top:4px;">
      <label style="display:flex;align-items:flex-start;gap:8px;cursor:pointer;font-weight:400;">
        <input type="checkbox" id="staff-login-viewupload-${mode}" style="margin-top:2px;">
        <span><strong>View &amp; upload only</strong> (no edit/delete)<br><span style="color:var(--text-secondary);font-size:0.85rem;">This person can open their modules, add new records and upload files. They <strong>can't</strong> change or delete anything already saved — no saving a person's details or text/notification settings, no Mute/Unmute All, no Dispatch settings. Leave this off for anyone who manages people or settings. (A Dispatcher or Admin login can still edit people, and an Admin can still change Dispatch settings.)</span></span>
      </label>
      <div id="staff-login-viewupload-workforce-note-${mode}" style="display:none;margin-top:6px;font-size:0.8rem;color:#92400e;background:#fef3c7;border:1px solid #f59e0b;border-radius:6px;padding:8px 10px;line-height:1.35;">
        Heads up — Workforce (timeclock) edits stay enabled even with this on, so payroll/clock corrections aren't blocked. The restriction applies to your other modules.
      </div>
    </div>
    <!-- TASK-9986 S1 — the owner's per-person "can change Billing settings" switch
         (members.settingsModules). Shown only while AR / AP (billing) is ticked;
         disabled while View & upload only is on; only the OWNER can change it
         (server + rules refuse anyone else). Off by default on a new invite. -->
    <div class="form-group" id="staff-login-settings-group-${mode}" style="display:none;margin-top:4px;">
      <label style="display:flex;align-items:flex-start;gap:8px;cursor:pointer;font-weight:400;">
        <input type="checkbox" id="staff-login-settings-billing-${mode}" style="margin-top:2px;">
        <span><strong>Can change Billing settings</strong><br><span style="color:var(--text-secondary);font-size:0.85rem;">Tax, ticket and invoice options, QuickBooks setup, and switches that change pay, like ticket time edits updating the time clock. Off: they can see these settings but not change them.</span></span>
      </label>
      <div id="staff-login-settings-note-${mode}" style="display:none;margin-top:6px;font-size:0.8rem;color:var(--text-secondary);"></div>
    </div>
    <!-- TASK-9389 S1 — the owner's per-person "can manage logins" switch
         (members.manageLogins). Only the OWNER can change it (server + rules
         refuse anyone else); disabled while View & upload only is on; off by
         default on a new invite; always an explicit tick (no chip sets it). -->
    <div class="form-group" id="staff-login-manage-group-${mode}" style="margin-top:4px;">
      <label style="display:flex;align-items:flex-start;gap:8px;cursor:pointer;font-weight:400;">
        <input type="checkbox" id="staff-login-manage-logins-${mode}" style="margin-top:2px;">
        <span><strong>Can manage logins</strong><br><span style="color:var(--text-secondary);font-size:0.85rem;">Invite people and change or revoke their logins, only for modules this person has. They can't change your access or let anyone change settings.</span></span>
      </label>
      <div id="staff-login-manage-note-${mode}" style="display:none;margin-top:6px;font-size:0.8rem;color:var(--text-secondary);"></div>
    </div>
    <div id="staff-login-sensitive-warning-${mode}" style="display:none;align-items:flex-start;gap:8px;padding:10px 12px;margin-top:4px;border:1px solid var(--status-danger,#dc2626);border-radius:6px;background:#fef2f2;color:#991b1b;font-size:0.85rem;line-height:1.35;">
      <span class="material-icons" style="font-size:1.1rem;color:var(--status-danger,#dc2626);">warning</span>
      <span><strong>Heads up — this grants access to sensitive data:</strong> employee records (SSN / W-4 / I-9 / pay / discipline), background-check results (MVR / PSP — FCRA consumer reports), payroll exports, and billing/financials. Grant only to people who need it.</span>
    </div>
    <div class="form-group" id="staff-login-crm-role-group-${mode}" style="display:none;margin-top:4px;">
      <label>CRM level</label>
      <div style="display:flex;flex-direction:column;gap:6px;">
        <label style="display:flex;align-items:flex-start;gap:8px;cursor:pointer;font-weight:400;padding:8px 12px;border:1px solid var(--border-light);border-radius:6px;">
          <input type="radio" name="staff-login-crm-role-${mode}" value="rep" checked style="margin-top:2px;">
          <span><strong>Rep</strong><br><span style="color:var(--text-muted);font-size:0.85rem;">Sees only their own assigned + unassigned customers</span></span>
        </label>
        <label style="display:flex;align-items:flex-start;gap:8px;cursor:pointer;font-weight:400;padding:8px 12px;border:1px solid var(--border-light);border-radius:6px;">
          <input type="radio" name="staff-login-crm-role-${mode}" value="supervisor" style="margin-top:2px;">
          <span><strong>Supervisor</strong><br><span style="color:var(--text-muted);font-size:0.85rem;">Sees the whole pipeline + every rep</span></span>
        </label>
      </div>
    </div>
  `;
}

// TASK-349 — current pending invite for the open person card. Tracked in
// module-state so the Resend/Revoke onclick handlers can find the token + email
// without re-querying. Cleared on modal close + on each _renderStaffLoginSection
// call (then re-populated by the async fetch).
let _staffLoginPendingInvite = null;

function _formatStaffInviteRelative(ms) {
  if (!ms) return '';
  const diff = Date.now() - ms;
  const min = Math.floor(diff / 60000);
  if (min < 1) return 'just now';
  if (min < 60) return `${min} min ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr} hr ago`;
  const days = Math.floor(hr / 24);
  if (days === 1) return 'yesterday';
  if (days < 7) return `${days} days ago`;
  return new Date(ms).toLocaleDateString();
}

function _formatStaffInviteExpiry(ms) {
  if (!ms) return '';
  const diff = ms - Date.now();
  if (diff <= 0) return 'expired';
  const days = Math.ceil(diff / 86400000);
  if (days === 1) return 'tomorrow';
  if (days < 7) return `in ${days} days`;
  return new Date(ms).toLocaleDateString();
}

// TASK-349 polish 2026-05-22 -- format a history-row ms timestamp as
// "MMM D, YYYY" (e.g. "May 22, 2026"). Empty string for null/0.
function _formatStaffInviteHistoryDate(ms) {
  if (!ms) return '';
  try {
    return new Date(ms).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  } catch (_e) {
    return new Date(ms).toLocaleDateString();
  }
}

// TASK-349 polish 2026-05-22 -- append a muted history breadcrumb under the
// "Set up login" CTA when historical (revoked/expired) tokens exist for this
// person. Skipped silently if history is empty.
//
// Status -> sentence mapping:
//   revoked         -> "Last invite revoked May 22, 2026 by alice@..."
//   revoked_member  -> "Access revoked May 22, 2026 by alice@..."  (was accepted, then kicked)
//   expired         -> "Earlier invite expired May 15, 2026"
function _renderStaffLoginHistoryLine(statusEl, history, historyTotal) {
  if (!statusEl || !Array.isArray(history) || history.length === 0) return;

  const rows = history.map((h, idx) => {
    const dateStr = _formatStaffInviteHistoryDate(h.statusAtMs);
    const safeBy = h.revokedByEmail ? escapeHtml(h.revokedByEmail) : '';
    const byClause = safeBy ? ` by ${safeBy}` : '';
    const isFirst = idx === 0;
    let sentence;
    if (h.status === 'revoked_member') {
      sentence = isFirst
        ? `Access revoked ${dateStr}${byClause}`
        : `Earlier access revoked ${dateStr}${byClause}`;
    } else if (h.status === 'revoked') {
      sentence = isFirst
        ? `Last invite revoked ${dateStr}${byClause}`
        : `Earlier invite revoked ${dateStr}${byClause}`;
    } else if (h.status === 'expired') {
      sentence = isFirst && history.length === 1
        ? `Last invite expired ${dateStr}`
        : `Earlier invite expired ${dateStr}`;
    } else {
      sentence = `Earlier invite ${escapeHtml(h.status || 'inactive')} ${dateStr}`;
    }
    return `<div>${sentence}</div>`;
  }).join('');

  const extra = Math.max(0, (historyTotal || 0) - history.length);
  const extraPill = extra > 0
    ? `<span class="staff-login-history-more" style="display:inline-block;margin-top:4px;padding:1px 8px;background:var(--bg-secondary,#f1f5f9);border-radius:9999px;font-size:0.7rem;color:var(--text-secondary,#64748b);">+${extra} earlier</span>`
    : '';

  const html = `
    <div class="staff-login-history" style="margin-top:8px;display:flex;align-items:flex-start;gap:6px;font-size:0.78rem;color:var(--text-secondary,#64748b);line-height:1.4;">
      <span class="material-icons" style="font-size:14px;color:var(--text-secondary,#64748b);flex-shrink:0;margin-top:1px;">history</span>
      <div>
        ${rows}
        ${extraPill}
      </div>
    </div>
  `;
  statusEl.insertAdjacentHTML('beforeend', html);
}

// ============================================
// TASK-1585 — staff-login authority gate (the Roster half of TASK-1577)
// ============================================
// TASK-1577 closed this escalation in Settings (Access PINs + Team Access). The
// SAME three callables — sendUserInvite / updateMemberAccess / revokeUserAccess —
// are reachable from the person card's staff-login section, which had no
// authority check at all. _canInviteUsers() reads a missing memberAccess as
// "legacy single-login owner = allowed" and a PIN session HAS no memberAccess,
// so a dispatch-only PIN that happened to carry the `roster` module could mint a
// full-access staff login by clicking. No devtools required.
//
// ⚠️ Roster staff logins run on TWO authority models, not one, and the gate has
// to honour both or it becomes the TASK-428 trap:
//   PATH A — PIN model. Kiosk / shared-tablet sessions on the tenant's single
//     Firebase login. Authority = the Primary PIN; assertCanManageAccessPins()
//     (app.js, TASK-1577) owns it and is server-truth.
//   PATH B — TASK-319 identity model. A real login with members/{uid}.role.
//     Owners and legacy admins reach Roster with NO PIN at all (checkModuleAccess
//     returns true on the owner / legacy-admin bypass unless the device is
//     locked), and a scoped admin member is NEVER PIN-challenged (TASK-1528).
//     assertCanManageAccessPins() returns FALSE for every one of them on a tenant
//     that has PINs — right in Settings, where non-owner members can't even open
//     the modal (hideUpgradeBadges hides it), and wrong here. Path B is for them.
// Either path grants. Neither alone covers the legitimate flows.
//
// 🔴 One exclusion, or path B re-opens the hole path A just closed: an owner or
// legacy admin who DELIBERATELY device-locked this tablet (TASK-719) has opted
// their session into PIN scoping — checkModuleAccess routes them back through
// the PIN system. That is the shared-yard-tablet case this whole bug came from,
// so their member doc must not vouch for whoever typed a PIN into it; path A has
// to speak for them. A SCOPED member is untouched by the exclusion: a PIN can't
// change which uid they are signed in as, and the lock flag is per-tenant
// localStorage that outlives the owner who set it.
// TASK-9389 S1 — path B is the OWNER (tenants.ownerId) or a member the owner
// lets manage logins (members.manageLogins, server-written), never a role.
const STAFF_LOGIN_REFUSAL_MSG = 'Only the account owner, someone the owner lets manage logins, or the Primary PIN holder can manage staff logins.';

// TASK-1585 — concurrency guard for the async authority gate in
// submitStaffLoginInvite. Scoped to the await window only; see the comment there
// for why this is not done by disabling the send button.
let _staffLoginGateInFlight = false;

function _refuseStaffLoginManagement() {
  window.showToast?.(STAFF_LOGIN_REFUSAL_MSG, 'error');
}

// TASK-9389 S1 — the TASK-719 device lock now applies to the OWNER by ownerId
// only (see _staffLoginMemberCanManage). A legacy role-admin never manages
// logins without the owner's switch, so the old role-shaped lock test is gone.

// Sync tier — decides what to RENDER. Reads AppState, which is a mutable global,
// so it is advisory only: a wrong answer here paints a panel, never a write.
// window.* not import (Pitfall #1 — app.js carries a ?v= buster).
function _canManageStaffLogins() {
  if (window.canManageAccessPins?.() === true) return true; // path A
  const st = window.AppState || {};                         // path B
  return _staffLoginMemberCanManage(st.user?.uid || null, st.tenantData?.ownerId || null, st.memberAccess || null);
}

// TASK-9389 S1 — path B's ONE predicate, the client twin of the server's
// _memberManagerDecision: the owner by tenants.ownerId (unless they
// device-locked this tablet — then path A speaks for them, TASK-719), or an
// active, non-readOnly, non-view-only member holding the owner's "manage logins"
// switch. members.role is never read. Pure (unit-tested).
function _staffLoginMemberCanManage(uid, ownerId, member) {
  if (uid && ownerId && uid === ownerId) return window.isOwnerDeviceLocked?.() !== true;
  const m = member;
  if (!m) return false;
  if (typeof m.status === 'string' && ['revoked', 'inactive', 'pending', 'removed', 'suspended'].includes(m.status.trim().toLowerCase())) return false;
  if (m.accessToken || m.linkTypes) return false;
  if (m.readOnly === true || m.viewUploadOnly === true) return false;
  return m.manageLogins === true;
}

// Async tier — the WRITE gate. Both paths re-derive authority from the SERVER,
// never from AppState, and fail CLOSED on a read error (same contract as
// assertCanManageAccessPins).
async function _assertCanManageStaffLogins() {
  if ((await window.assertCanManageAccessPins?.()) === true) return true; // path A
  return _assertStaffLoginMemberAuthority();                             // path B
}

async function _assertStaffLoginMemberAuthority() {
  const tenant = window.AppState?.tenant;
  const uid = window.AppState?.user?.uid || auth?.currentUser?.uid || null;
  if (!tenant || !uid) return false;
  try {
    // TASK-9389 S1 — re-derived from the server docs: tenants/{tid}.ownerId (an
    // owner with no members doc now passes — the callables admit them too) and
    // the caller's own members doc (its manageLogins switch).
    const [tSnap, mSnap] = await Promise.all([
      getDoc(doc(db, 'tenants', tenant)),
      getDoc(doc(db, 'tenants', tenant, 'members', uid)),
    ]);
    const ownerId = tSnap.exists() ? (tSnap.data() || {}).ownerId || null : null;
    return _staffLoginMemberCanManage(uid, ownerId, mSnap.exists() ? (mSnap.data() || {}) : null);
  } catch (err) {
    // Fail CLOSED. Recovery from a shared device is the lock pill (clearPinAccess
    // → re-challenge with the Primary PIN), same as TASK-1577.
    console.warn('[StaffLogin] member authority re-check failed, refusing:', err?.message);
    return false;
  }
}

async function _renderStaffLoginSection(person) {
  const section = document.getElementById('person-staff-login-section');
  const group = document.getElementById('person-staff-login-group');
  const statusEl = document.getElementById('person-staff-login-status');
  const setupBtn = document.getElementById('btn-setup-staff-login');
  if (!section || !group || !statusEl) return;

  // Reset pending-invite tracking on every render
  _staffLoginPendingInvite = null;

  // Only show for saved people (need a personId for the callable)
  if (!person || !person.id) {
    section.style.display = 'none';
    group.style.display = 'none';
    return;
  }

  // Hide on demo tenant — server-side requireTenantAdmin would reject anyway,
  // and the demo seed doesn't include any invite plumbing.
  const tenant = window.AppState?.tenant;
  if (!tenant || tenant === 'demo') {
    section.style.display = 'none';
    group.style.display = 'none';
    return;
  }

  // TASK-1585 — hide the staff-login SECTION only, never the person card. A
  // dispatcher still opens, edits and saves the person exactly as before; only
  // Set up login / Edit access / Revoke disappear. Gating the whole card is the
  // TASK-428 trap and the reason TASK-1577 picked Option B. Bonus: an
  // unauthorized session no longer pays the members query + the listPendingInvites
  // cold start either.
  if (!_canManageStaffLogins()) {
    section.style.display = 'none';
    group.style.display = 'none';
    return;
  }

  section.style.display = '';
  group.style.display = '';

  // Reset to default CTA. Show a neutral "Checking…" hint instead of asserting
  // "No staff login yet" before we know — pre-2026-05-28, we lied here and
  // the admin saw a 2-4s flash of wrong state when an active login existed
  // (listPendingInvites callable has no minInstances; cold-start dominates).
  if (setupBtn) setupBtn.style.display = '';
  statusEl.innerHTML = '<span class="text-muted" style="font-size:0.85rem;opacity:0.65;">Checking…</span>';

  // Capture the personId we're rendering for so a late-arriving response from
  // a stale open doesn't clobber a fresh modal that opened a different person.
  const renderingForPersonId = person.id;

  try {
    // Fast path FIRST: Firestore members query (~200ms) is the source of truth
    // for the active-login state per the comment at the activeMember branch
    // below. Skip the slow listPendingInvites callable (2-4s cold start) entirely
    // when an active member is found — they win over invites anyway.
    const membersQ = query(
      collection(db, 'tenants', tenant, 'members'),
      where('personId', '==', renderingForPersonId)
    );
    const membersSnap = await getDocs(membersQ);
    // Bail if the user closed/switched person while the query was in flight
    if (document.getElementById('person-id')?.value !== renderingForPersonId) return;

    // Active member doc wins over pending/no-invite — single source of truth.
    const activeMember = membersSnap.docs
      .map(d => ({ uid: d.id, ...(d.data() || {}) }))
      .find(m => (m.status || 'active') === 'active' && m.role !== 'owner');
    if (activeMember) {
      if (setupBtn) setupBtn.style.display = 'none';
      const moduleLabels = { '811': 'Locates (811)', billing: 'AR / AP', workforce: 'Workforce', bi: 'BI', jobboard: 'Job Board', hr: 'HR' };
      const mods = (activeMember.modules || [])
        .map(m => moduleLabels[m] || (m.charAt(0).toUpperCase() + m.slice(1)))
        .join(', ') || 'No modules assigned';
      const safeEmail = escapeHtml(activeMember.email || '');
      const safeUid = escapeHtml(activeMember.uid);
      statusEl.innerHTML = `
        <div class="staff-login-active" style="padding:10px 12px;background:#dcfce7;border:1px solid #16a34a;border-radius:6px;">
          <div style="display:flex;align-items:center;gap:8px;flex-wrap:wrap;">
            <span class="material-icons" style="color:#15803d;font-size:18px;">verified_user</span>
            <strong style="color:#15803d;font-size:0.9rem;">Active login</strong>
            ${_staffLoginTierChipHtml(activeMember.role || 'staff')}
          </div>
          <div style="margin-top:4px;color:#166534;font-size:0.82rem;">${safeEmail}</div>
          <div style="margin-top:2px;color:#166534;font-size:0.75rem;">Modules: ${escapeHtml(mods)}</div>
          ${(activeMember.modules || []).includes('billing') ? `<div style="margin-top:2px;color:#166534;font-size:0.75rem;">${(Array.isArray(activeMember.settingsModules) && activeMember.settingsModules.includes('billing') && activeMember.viewUploadOnly !== true) ? 'Can change Billing settings' : "Can't change Billing settings"}</div>` : ''}
          ${activeMember.manageLogins === true ? '<div style="margin-top:2px;color:#166534;font-size:0.75rem;">Can manage logins</div>' : ''}
          <div style="margin-top:8px;display:flex;gap:8px;flex-wrap:wrap;">
            ${_staffLoginViewerCanTouch(activeMember, false) ? `<button type="button" class="btn btn-xs btn-secondary" onclick="window.RosterModule.editStaffLoginAccess('${escJsAttr(activeMember.uid)}', '${escJsAttr(activeMember.email || '')}')" title="Change which modules this person can access">
              <span class="material-icons" style="font-size:14px;">tune</span> Edit access
            </button>` : ''}
            ${_staffLoginViewerCanTouch(activeMember, true) ? `<button type="button" class="btn btn-xs btn-danger-outline" onclick="window.RosterModule.revokeStaffLoginMember('${escJsAttr(activeMember.uid)}', '${escJsAttr(activeMember.email || '')}')" title="Revoke this staff login">
              <span class="material-icons" style="font-size:14px;">block</span> Revoke access
            </button>` : ''}
            ${(_staffLoginViewerCanTouch(activeMember, false) && _staffLoginViewerCanTouch(activeMember, true)) ? '' : '<span style="color:#166534;font-size:0.75rem;align-self:center;">Only the account owner can change some of this login.</span>'}
          </div>
        </div>
      `;
      return;
    }

    // No active member — NOW pay the cold-start cost on listPendingInvites to
    // distinguish pending-invite from no-login-at-all. Status remains "Checking…"
    // during this fetch; admin sees an honest hint instead of false "No staff login yet".
    const fn = httpsCallable(functions, 'listPendingInvites');
    const { data } = await fn({
      tenantId: tenant,
      personId: renderingForPersonId,
      includeHistory: true,  // TASK-349 polish 2026-05-22: empty-state breadcrumb
      historyLimit: 3,
    });
    // Bail if the user closed/switched person while the callable was in flight
    if (document.getElementById('person-id')?.value !== renderingForPersonId) return;

    const invite = Array.isArray(data?.invites) ? data.invites[0] : null;
    if (!invite) {
      // No pending invite -- render the empty state and any history breadcrumb.
      statusEl.innerHTML = '<span class="text-muted" style="font-size:0.85rem;">No staff login yet</span>';
      _renderStaffLoginHistoryLine(statusEl, data?.history || [], data?.historyTotal || 0);
      return;
    }

    _staffLoginPendingInvite = invite;
    if (setupBtn) setupBtn.style.display = 'none';

    const safeEmail = escapeHtml(invite.email || '');
    const safeToken = escapeHtml(invite.token || '');
    const sentRel = _formatStaffInviteRelative(invite.createdAtMs);
    const expiresRel = _formatStaffInviteExpiry(invite.expiresAtMs);
    const resendCountSuffix = invite.resendCount > 0
      ? ` &middot; resent ${invite.resendCount}×`
      : '';
    // TASK-4851 D -- last send outcome + "not accepted yet" nudge, from a lazy
    // chunk (cold-load budget). A failed load just leaves the card as before.
    let deliveryHtml = '';
    try {
      const m = await __ddChunkWrap(import('./rosterInviteDelivery.js?v=48510002'));
      deliveryHtml = m.inviteDeliveryHtml(invite, Date.now(), escapeHtml);
    } catch (e) { console.warn('[Roster] rosterInviteDelivery:', e?.message || e); }
    if (document.getElementById('person-id')?.value !== renderingForPersonId) return;

    statusEl.innerHTML = `
      <div class="staff-login-pending" style="padding:10px 12px;background:#fef3c7;border:1px solid #f59e0b;border-radius:6px;">
        <div style="display:flex;align-items:center;gap:8px;flex-wrap:wrap;">
          <span class="material-icons" style="color:#92400e;font-size:18px;">mail_outline</span>
          <strong style="color:#92400e;font-size:0.9rem;">${invite.token ? `Invite sent to ${safeEmail}` : "Invite pending"}</strong>
        </div>
        <div style="margin-top:4px;color:#92400e;font-size:0.78rem;">
          Sent ${sentRel} &middot; expires ${expiresRel}${resendCountSuffix}
        </div>
        ${deliveryHtml}
        ${invite.token ? '' : '<div style="margin-top:6px;color:#92400e;font-size:0.78rem;">Only the account owner can resend or revoke this invite.</div>'}
        <div style="margin-top:8px;display:${invite.token ? 'flex' : 'none'};gap:8px;flex-wrap:wrap;">
          <button type="button" class="btn btn-xs btn-secondary" onclick="window.RosterModule.resendStaffLoginInvite('${escJsAttr(invite.token || '')}')" title="Resend invite link">
            <span class="material-icons" style="font-size:14px;">send</span> Resend
          </button>
          <button type="button" class="btn btn-xs btn-danger-outline" onclick="window.RosterModule.revokeStaffLoginInvite('${escJsAttr(invite.token || '')}', '${escJsAttr(invite.email || '')}')" title="Revoke this pending invite">
            <span class="material-icons" style="font-size:14px;">block</span> Revoke
          </button>
        </div>
      </div>
    `;
  } catch (err) {
    // Non-fatal: clear the "Checking…" hint and fall back to the safe default
    // empty state so the admin sees an actionable CTA. Common reasons: callable
    // not yet deployed, network blip, members query rules denial. We don't show
    // a scary banner because the admin can still re-send via the Set-up-login
    // CTA (server detects the existing token and the duplicate-email check in
    // sendUserInvite will only fail if they've already accepted — pending
    // tokens don't block re-send today).
    // TASK-10094 — but never claim there is no login here: we do not know. A
    // scoped admin whose members read is denied saw that on people who DO have
    // a login, one click from a duplicate invite. The CTA stays (same flows).
    if (statusEl && document.getElementById('person-id')?.value === renderingForPersonId) {
      statusEl.innerHTML = '<span class="staff-login-unknown" style="font-size:0.85rem;color:var(--text-primary);">Couldn\'t check whether this person already has a login. Close and reopen the card to try again before setting one up.</span>';
    }
    console.warn('[StaffLogin] members/listPendingInvites lookup failed (non-fatal):', err?.message);
  }
}

// Double-click guard for resend/revoke — token-keyed so two different invites
// can't share an in-flight lock (TASK-349 COD Edge Case 1 catch).
const _staffLoginInviteInFlight = new Set();

// TASK-4531 — in-flight feedback for callable-backed row actions (revoke /
// resend). The initiating button — matched by its own inline onclick — disables
// and shows a spinner until the callable returns; before this, the admin
// confirmed the dialog and NOTHING changed for the round-trip. (Duplicated in
// app.js for Team Access — Pitfall #1 forbids cross-imports under ?v= busters.)
function _setRowActionBusy(match, busy) {
  document.querySelectorAll(`button[onclick*="${match}"]`).forEach(b => {
    b.disabled = busy;
    if (busy) {
      b.dataset.prevHtml = b.innerHTML;
      b.innerHTML = '<span class="material-icons spin" style="font-size:14px;vertical-align:middle;">autorenew</span>';
    } else if (b.dataset.prevHtml) {
      b.innerHTML = b.dataset.prevHtml;
      delete b.dataset.prevHtml;
    }
  });
}

async function resendStaffLoginInvite(token) {
  if (!await _assertCanManageStaffLogins()) { _refuseStaffLoginManagement(); return; } // TASK-1585
  const tenant = window.AppState?.tenant;
  if (!tenant || !token) return;
  if (_staffLoginInviteInFlight.has(`resend:${token}`)) return;

  const invite = _staffLoginPendingInvite;
  if (!invite || invite.token !== token) {
    window.showToast?.('Invite state stale — close and reopen the person.', 'error');
    return;
  }

  _staffLoginInviteInFlight.add(`resend:${token}`);
  _setRowActionBusy(`resendStaffLoginInvite('${token}'`, true); // TASK-4531
  try {
    const fn = httpsCallable(functions, 'resendUserInvite');
    // TASK-4840 — the server now ALWAYS emails on resend (the token always
    // carries an address). Report the real channels rather than a bare
    // "Invite resent": the whole bug was an operator believing a resend had
    // gone somewhere it hadn't.
    const { data: resendData } = await fn({ tenantId: tenant, token });
    // Branch on BOTH flags. Saying "resent by email" when the send returned
    // {ok:false} is the same false confidence this task exists to remove — COD
    // pass 2 caught it still living here after the server side was fixed.
    // A text WITHOUT the email is the dangerous half-success (the texted link
    // often won't open on an iPhone) — warn, never green (COD, integrated diff).
    // TASK-10027 muted-tenant UI hints — the shared outcome wording names the pause.
    const _out = window.inviteResendOutcome ? window.inviteResendOutcome(resendData) : null;
    if (_out) {
      window.showToast?.(_out.text, _out.type);
    } else if (resendData?.emailSent) {
      window.showToast?.(`Invite resent by email${resendData?.smsSent ? ' + text' : ''}`, 'success');
    } else if (resendData?.smsSent) {
      window.showToast?.("Resent by text only — the email did not send, and a texted login link often won't open on an iPhone. Check the email address.", 'warning');
    } else {
      window.showToast?.('Resend failed — nothing was delivered. Check the email address.', 'error');
    }
    // Refresh the pending-invite banner so the resentCount + expiresAt update
    const personId = document.getElementById('person-id')?.value;
    const person = RosterState.people.find(p => p.id === personId);
    if (person) _renderStaffLoginSection(person);
  } catch (err) {
    console.error('[StaffLogin] resendUserInvite error:', err);
    const msg = err?.message || 'Resend failed — try again.';
    window.showToast?.(msg.replace(/^internal:\s*/i, '').slice(0, 140), 'error');
  } finally {
    _staffLoginInviteInFlight.delete(`resend:${token}`);
    _setRowActionBusy(`resendStaffLoginInvite('${token}'`, false);
  }
}

async function revokeStaffLoginInvite(token, email) {
  if (!await _assertCanManageStaffLogins()) { _refuseStaffLoginManagement(); return; } // TASK-1585
  const tenant = window.AppState?.tenant;
  if (!tenant || !token) return;
  if (_staffLoginInviteInFlight.has(`revoke:${token}`)) return;

  const invite = _staffLoginPendingInvite;
  if (!invite || invite.token !== token) {
    window.showToast?.('Invite state stale — close and reopen the person.', 'error');
    return;
  }

  const confirmed = await window.showConfirm?.(
    'Revoke this pending invite?',
    `The invite link sent to ${email || 'this email'} will stop working. You can send a fresh invite afterward.`,
    { confirmText: 'Revoke invite', danger: true }
  );
  if (!confirmed) return;

  _staffLoginInviteInFlight.add(`revoke:${token}`);
  _setRowActionBusy(`revokeStaffLoginInvite('${token}'`, true); // TASK-4531
  try {
    const fn = httpsCallable(functions, 'revokeUserAccess');
    await fn({ tenantId: tenant, token });
    window.showToast?.('Invite revoked', 'success');
    // Re-render: the listPendingInvites callable filters `revoked:true` so the
    // banner disappears and the Set-up-login CTA returns.
    const personId = document.getElementById('person-id')?.value;
    const person = RosterState.people.find(p => p.id === personId);
    if (person) _renderStaffLoginSection(person);
  } catch (err) {
    console.error('[StaffLogin] revokeUserAccess (token) error:', err);
    const msg = err?.message || 'Revoke failed — try again.';
    window.showToast?.(msg.replace(/^internal:\s*/i, '').slice(0, 140), 'error');
  } finally {
    _staffLoginInviteInFlight.delete(`revoke:${token}`);
    _setRowActionBusy(`revokeStaffLoginInvite('${token}'`, false);
  }
}

// Revoke an active (accepted) staff login by member uid. Distinct from
// revokeStaffLoginInvite above, which only kills a pending invite token.
async function revokeStaffLoginMember(uid, email) {
  if (!await _assertCanManageStaffLogins()) { _refuseStaffLoginManagement(); return; } // TASK-1585
  const tenant = window.AppState?.tenant;
  if (!tenant || !uid) return;
  if (_staffLoginInviteInFlight.has(`revoke-member:${uid}`)) return;

  const confirmed = await window.showConfirm?.(
    'Revoke this staff login?',
    `${email || 'This user'} will lose access immediately. You can re-invite them later from this same screen.`,
    { confirmText: 'Revoke access', danger: true }
  );
  if (!confirmed) return;

  _staffLoginInviteInFlight.add(`revoke-member:${uid}`);
  _setRowActionBusy(`revokeStaffLoginMember('${uid}'`, true); // TASK-4531
  try {
    const fn = httpsCallable(functions, 'revokeUserAccess');
    await fn({ tenantId: tenant, uid });
    window.showToast?.('Staff login revoked', 'success');
    const personId = document.getElementById('person-id')?.value;
    const person = RosterState.people.find(p => p.id === personId);
    if (person) _renderStaffLoginSection(person);
  } catch (err) {
    console.error('[StaffLogin] revokeUserAccess (member) error:', err);
    const msg = err?.message || 'Revoke failed — try again.';
    window.showToast?.(msg.replace(/^internal:\s*/i, '').slice(0, 140), 'error');
  } finally {
    _staffLoginInviteInFlight.delete(`revoke-member:${uid}`);
    _setRowActionBusy(`revokeStaffLoginMember('${uid}'`, false);
  }
}

// ============================================
// TASK-366 — Edit Access modal for accepted members
// ============================================
// Opens the new #modal-staff-login-edit modal, pre-fills with the member's
// current modules + role, and on submit calls the new updateMemberAccess
// callable. M1 (memberAccess onSnapshot in app.js) keeps live sessions in sync
// without a reload; the Roster card itself re-renders via _renderStaffLoginSection
// on close.
//
// M3 — Double-click guard via btn.dataset.inflight + the same
// _staffLoginInviteInFlight set used for resend/revoke (key 'edit-member:<uid>').
//
// M6 — Two modals NOT one. The role-rail + module-grid HTML is shared via the
// _staffLoginRoleAndModuleGridHtml('edit') helper. Inputs use the -edit suffix
// so the two modals can coexist in the DOM without colliding radio groups.

async function editStaffLoginAccess(uid, email) {
  // TASK-1585 — also the entry point app.js editTeamMemberAccess bridges into.
  // That caller already asserts path A; this re-assert is what makes the Roster
  // entry point (which had nothing) match it, and it is a superset, so the
  // Settings bridge is unchanged for anyone it lets through today.
  if (!await _assertCanManageStaffLogins()) { _refuseStaffLoginManagement(); return; }
  const tenant = window.AppState?.tenant;
  if (!tenant || !uid) return;
  const modal = document.getElementById('modal-staff-login-edit');
  if (!modal) return;

  // Resolve the open person card (for the friendly name in the modal header)
  const personId = document.getElementById('person-id')?.value;
  const person = personId ? RosterState.people.find(p => p.id === personId) : null;

  // Fetch the current members/{uid} doc so the modal opens pre-filled with the
  // member's existing modules + role. Use getDoc here (one-shot) — onSnapshot
  // is only useful for AppState.memberAccess (the current user's own doc).
  let memberData = null;
  try {
    const memberSnap = await getDoc(doc(db, 'tenants', tenant, 'members', uid));
    if (!memberSnap.exists()) {
      window.showToast?.('Member not found — refresh and try again.', 'error');
      return;
    }
    memberData = memberSnap.data() || {};
  } catch (err) {
    console.error('[StaffLogin] member doc load failed:', err);
    window.showToast?.('Could not load access details — try again.', 'error');
    return;
  }

  // Reset error/empty banners
  const errEl = document.getElementById('staff-login-edit-error');
  if (errEl) errEl.style.display = 'none';
  const emptyHint = document.getElementById('staff-login-edit-empty-hint');
  if (emptyHint) emptyHint.style.display = 'none';

  // Pre-fill header + hidden uid field
  const displayName = person?.name || memberData.displayName || 'this person';
  document.getElementById('staff-login-edit-uid').value = uid;
  document.getElementById('staff-login-edit-person-name').textContent = displayName;
  document.getElementById('staff-login-edit-email').textContent = email || memberData.email || '';
  // TASK-1389 slice 2 — surface the member's CURRENT tier in the modal header
  // (members/{uid}.role was invisible everywhere — that invisibility is the
  // whole incident). escapeHtml on the name because we switch to innerHTML.
  document.getElementById('staff-login-edit-title').innerHTML =
    `Edit access for ${escapeHtml(displayName)} ${_staffLoginTierChipHtml(memberData.role || 'staff')}`;

  // TASK-608 — MODULE-FIRST prefill. Pre-check the granted module boxes, set
  // the View & upload only toggle from the member doc, and remember the member's
  // current role in the hidden field so a no-op save preserves an admin's role.
  const currentModules = Array.isArray(memberData.modules) ? memberData.modules.slice() : [];
  const currentRole = memberData.role || 'staff';
  document.querySelectorAll('.staff-login-custom-module-edit').forEach(cb => {
    cb.checked = currentModules.includes(cb.value);
  });
  // TASK-1489 — mirror the invite grid's restriction for an intentionally
  // personless member: updateMemberAccess rejects clock/jobboard for them, so
  // hide those boxes here too instead of letting an admin check an invisible-cause
  // error. Explicit flag only (matching the server), so a legacy unlinked member
  // who already holds one of these keeps it visible and editable.
  _applyPersonlessModuleGrid('edit', memberData.personless === true);
  const vuoBox = document.getElementById('staff-login-viewupload-edit');
  if (vuoBox) vuoBox.checked = memberData.viewUploadOnly === true;
  // TASK-9986 S1 — the stored "can change Billing settings" switch.
  const settingsBoxEdit = document.getElementById('staff-login-settings-billing-edit');
  if (settingsBoxEdit) settingsBoxEdit.checked = Array.isArray(memberData.settingsModules) && memberData.settingsModules.includes('billing');
  const manageBoxEdit = document.getElementById('staff-login-manage-logins-edit'); // TASK-9389 S1
  if (manageBoxEdit) manageBoxEdit.checked = memberData.manageLogins === true;
  // TASK-9369 — Home branch picker (default Assets filter). Only when the tenant
  // has facilities; a slug that no longer exists is surfaced, not silently kept.
  _populateHomeBranchPicker('edit', memberData.yardId);
  // Seed the hidden role field with the member's CURRENT role so _staffLoginRoleFor
  // keeps it if the grid is left unchanged (it only matches when modules still
  // equal that preset's set — a hand-edit reverts it to 'staff' via onchange).
  const roleField = document.getElementById('staff-login-role-edit');
  if (roleField) roleField.value = currentRole;
  // TASK-1389 slice 3 — stash the target's CURRENT role on the hidden uid field
  // so submitStaffLoginEdit can detect a self-demotion (the hidden role field
  // above is cleared by any hand-edit, so it can't serve as the "before" state).
  document.getElementById('staff-login-edit-uid').dataset.currentRole = currentRole;
  // TASK-1389 — reset the Admin-status block from any prior open, then seed it
  // from the member's CURRENT state when their granted set is already the full
  // 13 (COD Code Reviewer catch on the real diff): opening the modal is not a
  // choice, so the toggle must MIRROR reality at open — admin → checked (no-op
  // save keeps 'admin', no downgrade surprise), staff-with-all-13 → unchecked
  // (a no-op save must NOT silently promote; the visible amber block + tier
  // chip invite the explicit promote). The pre-checked default (full access =
  // admin) still applies to every IN-SESSION reveal — hand-checking the 13th
  // box or the Admin quick-fill — via _updateStaffLoginAdminToggle's
  // hidden→visible transition.
  const editAdminBlock = document.getElementById('staff-login-admin-block-edit');
  const editAdminToggle = document.getElementById('staff-login-admin-toggle-edit');
  if (editAdminBlock) { editAdminBlock.dataset.visible = '0'; editAdminBlock.style.display = 'none'; }
  if (editAdminToggle) editAdminToggle.checked = false;
  // TASK-4710 (COD Independent + Edge Case, 🔴) — a CURRENT admin is seeded
  // checked even when their stored modules are not today's full preset. The
  // admin preset grew to 14 ('projects'); every pre-4710 admin holds 13, so
  // without this a no-op Edit Access save fell through _staffLoginRoleFor to
  // 'staff' — a silent demotion (the toggle was shown but left unchecked).
  if (_staffLoginIsFullAccessSet(currentModules) || currentRole === 'admin') {
    // Mark visible BEFORE the wiring repaint so the transition pre-check is
    // skipped and the seeded state below survives the initial paint.
    if (editAdminBlock) editAdminBlock.dataset.visible = '1';
    if (editAdminToggle) editAdminToggle.checked = (currentRole === 'admin');
  }

  // Wire all module-first handlers (checkboxes, quick-fill, view-upload) + paint.
  _wireStaffLoginGrid('edit');

  // Reset the Save button's inflight guard from any stale prior open
  const saveBtn = document.getElementById('btn-staff-login-edit-save');
  if (saveBtn) {
    saveBtn.dataset.inflight = '0';
    saveBtn.disabled = false;
    saveBtn.innerHTML = `<span class="material-icons">check</span> Update access`;
  }
  _updateEditAccessSaveButton();

  modal.classList.remove('hidden');
}

// TASK-366/608 — M2 client-side empty-modules guard. Module-first: 0 modules
// checked → disable Save + show the inline hint directing the admin to use
// Revoke Access. Server still rejects empty arrays as defense in depth.
function _updateEditAccessSaveButton() {
  const saveBtn = document.getElementById('btn-staff-login-edit-save');
  const emptyHint = document.getElementById('staff-login-edit-empty-hint');
  if (!saveBtn || !emptyHint) return;
  if (_staffLoginCheckedModules('edit').length === 0) {
    saveBtn.disabled = true;
    emptyHint.style.display = '';
    return;
  }
  saveBtn.disabled = false;
  emptyHint.style.display = 'none';
}

function closeStaffLoginEditModal() {
  document.getElementById('modal-staff-login-edit')?.classList.add('hidden');
  // TASK-1489 — this modal can now also be opened from Settings → Team Access
  // (editTeamMemberAccess), which is the only management surface a personless
  // member has. Refresh that list so an edited grant shows immediately. Gated on
  // the active view so the Roster path doesn't pay for a read it can't see.
  if (window.AppState?.currentView === 'settings' && typeof window.renderTeamAccessList === 'function') {
    window.renderTeamAccessList();
    return;
  }
  // Reflect the (possibly) updated modules in the Active-login card pill list.
  const personId = document.getElementById('person-id')?.value;
  if (personId) {
    const person = RosterState.people.find(p => p.id === personId);
    if (person) _renderStaffLoginSection(person);
  }
}

async function submitStaffLoginEdit(e) {
  e.preventDefault();
  const tenant = window.AppState?.tenant;
  const uid = document.getElementById('staff-login-edit-uid').value;
  if (!tenant || !uid) {
    _showStaffLoginEditError('Missing tenant or member — close and reopen the card.');
    return;
  }

  // TASK-1585 — server-truth re-assert immediately before the write. The modal
  // could have been opened while this session still had authority (or by a
  // console call straight into the submit handler), so the open-time check is
  // not the boundary. Error lands in the modal's own error strip, where the
  // admin is already looking, not in a toast behind it.
  if (!await _assertCanManageStaffLogins()) {
    _showStaffLoginEditError(STAFF_LOGIN_REFUSAL_MSG);
    return;
  }

  const saveBtn = document.getElementById('btn-staff-login-edit-save');
  // M3 — double-click guard via dataset.inflight (per spec). The shared
  // _staffLoginInviteInFlight set also tracks this key so any other concurrent
  // path (e.g. someone wiring the same handler elsewhere) can't double-fire.
  if (saveBtn?.dataset.inflight === '1') return;
  if (_staffLoginInviteInFlight.has(`edit-member:${uid}`)) return;

  // TASK-608 — MODULE-FIRST: modules come straight from the checkbox grid; role
  // is derived (preset role only if the set still matches, else 'staff'). The
  // View & upload only capability flag is read from its own toggle.
  const modules = _staffLoginCheckedModules('edit');
  if (modules.length === 0) {
    // M2 — defense in depth. The Save button is disabled when this is true, but
    // a stray submit (e.g. Enter key) still hits here.
    _showStaffLoginEditError('To remove all access, use Revoke Access instead — this flow is for adjusting modules.');
    return;
  }
  const role = _staffLoginRoleFor('edit', modules);
  const viewUploadOnly = !!document.getElementById('staff-login-viewupload-edit')?.checked;

  // TASK-1389 slice 3 — self-demotion confirm. If the admin is editing their
  // OWN login and the outgoing role is LOWER than their current one, make the
  // consequence explicit before the write (memberAccess onSnapshot applies it
  // to their live session instantly — admin features vanish on save). Server
  // unchanged: self-edit stays allowed; the owner is the recovery anchor.
  const _tierRank = { owner: 4, admin: 3, dispatcher: 2, mechanic: 2, staff: 1 };
  const currentRoleBefore = document.getElementById('staff-login-edit-uid')?.dataset.currentRole || 'staff';
  const isSelfEdit = !!window.AppState?.user?.uid && uid === window.AppState.user.uid;
  if (isSelfEdit && (_tierRank[role] || 1) < (_tierRank[currentRoleBefore] || 1)) {
    const confirmed = await window.showConfirm?.(
      'Lower your own access?',
      "You're lowering your own access — you'll lose admin features immediately. Only the owner or another admin can restore them.",
      { confirmText: 'Lower my access', danger: true }
    );
    if (!confirmed) return;
  }

  if (saveBtn) {
    saveBtn.dataset.inflight = '1';
    saveBtn.disabled = true;
    saveBtn.innerHTML = `<span class="loading-spinner-small"></span> Updating…`;
  }
  _staffLoginInviteInFlight.add(`edit-member:${uid}`);

  try {
    const fn = httpsCallable(functions, 'updateMemberAccess');
    const payload = { tenantId: tenant, uid, modules, role, viewUploadOnly };
    // TASK-9986 S1 — owner only; omitted for anyone else (server keeps the stored grant).
    const settingsModulesEdit = _staffLoginSettingsPayload('edit', modules, viewUploadOnly);
    if (settingsModulesEdit !== undefined) payload.settingsModules = settingsModulesEdit;
    const manageLoginsEdit = _staffLoginManagePayload('edit', viewUploadOnly); // TASK-9389 S1 — owner only
    if (manageLoginsEdit !== undefined) payload.manageLogins = manageLoginsEdit;
    // TASK-9369 — send yardId ONLY when the picker was shown (tenant has
    // facilities). Absent = the callable leaves the field alone; '' clears it.
    const homeBranch = _readHomeBranchPicker('edit');
    if (homeBranch !== undefined) payload.yardId = homeBranch;
    await fn(payload);
    window.showToast?.('Access updated', 'success');
    closeStaffLoginEditModal();
  } catch (err) {
    console.error('[StaffLogin] updateMemberAccess error:', err);
    const msg = err?.message || 'Update failed — try again.';
    _showStaffLoginEditError(msg.replace(/^internal:\s*/i, '').slice(0, 180));
  } finally {
    if (saveBtn) {
      saveBtn.dataset.inflight = '0';
      saveBtn.disabled = false;
      saveBtn.innerHTML = `<span class="material-icons">check</span> Update access`;
    }
    _staffLoginInviteInFlight.delete(`edit-member:${uid}`);
  }
}

// TASK-9369 — Home branch picker helpers (Team Access editor).
// members/{uid}.yardId is a DEFAULT FILTER for Roster → Assets, never a boundary.
function _populateHomeBranchPicker(mode, currentSlug) {
  const wrap = document.getElementById(`staff-login-homebranch-wrap-${mode}`);
  const select = document.getElementById(`staff-login-homebranch-${mode}`);
  const note = document.getElementById(`staff-login-homebranch-note-${mode}`);
  if (!wrap || !select) return;
  const yards = getYardsList();
  if (yards.length === 0) { wrap.style.display = 'none'; select.innerHTML = ''; return; }
  wrap.style.display = '';
  select.innerHTML = '';
  const none = document.createElement('option');
  none.value = '';
  none.textContent = 'No home branch — Assets opens on all branches';
  select.appendChild(none);
  yards.forEach(y => {
    const opt = document.createElement('option');
    opt.value = y.slug;
    const typeLabel = getYardTypeLabel(y.type);
    opt.textContent = typeLabel && y.type !== 'yard' ? `${y.label} · ${typeLabel}` : y.label;
    select.appendChild(opt);
  });
  const slug = typeof currentSlug === 'string' ? currentSlug : '';
  const exists = !!slug && yards.some(y => y.slug === slug);
  select.value = exists ? slug : '';
  if (note) {
    if (slug && !exists) {
      note.textContent = `Was set to "${slug}", a facility that no longer exists — saving clears it (Assets already opens on all branches for them).`;
      note.style.display = '';
    } else {
      note.textContent = '';
      note.style.display = 'none';
    }
  }
}

/** undefined = picker not shown (don't send); '' = clear; slug = set. */
function _readHomeBranchPicker(mode) {
  const wrap = document.getElementById(`staff-login-homebranch-wrap-${mode}`);
  const select = document.getElementById(`staff-login-homebranch-${mode}`);
  if (!wrap || !select || wrap.style.display === 'none' || getYardsList().length === 0) return undefined;
  return select.value || '';
}

function _showStaffLoginEditError(msg) {
  const el = document.getElementById('staff-login-edit-error');
  if (!el) return;
  el.textContent = msg;
  el.style.display = '';
}

// ============================================
// TASK-1489 / TASK-1500 — PERSONLESS ("Invite User") entry point.
// Same modal, same module grid, same callable — the ONLY difference is that no
// roster person is involved, so `personless: true` goes to sendUserInvite and no
// `tenants/{tid}/people` doc is ever created. That absence is the whole feature:
// the people roster renders `tenants/{tid}/people` exclusively (verified — the
// `members` collection is never merged into a roster row), so an invited outside
// user CANNOT appear there, in HR, on the schedule, or in payroll. They surface
// only in Settings → Team Access, which is uid-keyed and personId-independent.
// ============================================

// TASK-1489 — modules that are meaningless (and mildly dangerous) without a
// roster person, so the personless grid hides them and the server rejects them:
//   clock    — self-service "MY hours / clock MYSELF in". With no personId the
//              view renders an "unlinked" dead end, AND `clock` is one of the two
//              modules that unlock createMemberExpense. (Its no-linked-personId
//              branch USED to fall back to a CLIENT-supplied personId — TASK-1544
//              closed that: personId is now claim-bound for ALL members, unlinked
//              members file with personId:null. Blocking `clock` here is still
//              right because the view itself is a dead end without a person.)
//   jobboard — "MY daily schedule". jobDayVisibleToCurrentDriver() returns TRUE
//              for everything when driverPersonId is falsy (that's the intended
//              office/dispatcher behavior), so a personless jobboard grant would
//              quietly show the entire tenant schedule instead of a scoped board.
// Nothing legitimate is blocked: neither module can function without an employee
// record. Workforce (all timeclock data) and Dispatch (the whole schedule) are
// the correct grants for an outside reviewer and remain available.
const STAFF_LOGIN_PERSONLESS_BLOCKED_MODULES = ['clock', 'jobboard'];

// TASK-1489 — client-side admin gate for the Invite User button. Mirrors the
// owner/admin/legacy-single-login pattern used elsewhere in the SPA
// (app.js _appqCanManageScreening). Cosmetic only: sendUserInvite enforces
// requireTenantAdmin server-side, which is the actual boundary.
function _canInviteUsers() {
  // 🔴 TASK-1585 — authority FIRST. The `!ma` line below reads a missing
  // memberAccess as "legacy single-login owner = allowed", and a PIN session has
  // NO memberAccess — so without this term a dispatch-only PIN carrying the
  // `roster` module walked straight into the invite modal by clicking. Sync tier;
  // the write gate is _assertCanManageStaffLogins() in submitStaffLoginInvite.
  // TASK-9389 S1 — that gate IS the authority now (owner by ownerId, a
  // "manage logins" holder, or the Primary PIN); no role read.
  return _canManageStaffLogins();
}

function _syncInviteUserButtonVisibility(_retries = 8) {
  const btn = document.getElementById('btn-roster-invite-user');
  if (!btn) return;
  // TASK-595 pattern — while the member doc is still loading, memberAccess is null,
  // which _canInviteUsers() reads as "legacy owner = allowed". Stay hidden and
  // re-check rather than flashing an admin-only button at a staff member.
  if (window.AppState?.memberAccessPending === true) {
    btn.style.display = 'none';
    if (_retries > 0) setTimeout(() => _syncInviteUserButtonVisibility(_retries - 1), 250);
    return;
  }
  // Hidden on the demo tenant for the same reason the person-card "Set up login"
  // section is (_renderStaffLoginSection): requireTenantAdmin rejects the anon
  // demo session server-side, so the button could only ever produce an error.
  const tenant = window.AppState?.tenant;
  const allowed = _canInviteUsers() && !!tenant && tenant !== 'demo';
  btn.style.display = allowed ? '' : 'none';
}

// TASK-1489 — flip the shared invite modal between roster-person and personless
// presentation. Called by BOTH open paths so neither can inherit the other's
// state (a stale `personless` flag would send the wrong contract).
function _applyInviteModalPersonlessMode(personless) {
  const flag = document.getElementById('staff-login-invite-personless');
  if (flag) flag.value = personless ? '1' : '';

  const introPerson = document.getElementById('staff-login-invite-intro-person');
  if (introPerson) introPerson.style.display = personless ? 'none' : '';
  const introPersonless = document.getElementById('staff-login-invite-intro-personless');
  if (introPersonless) introPersonless.style.display = personless ? '' : 'none';
  const nameGroup = document.getElementById('staff-login-invite-name-group');
  if (nameGroup) nameGroup.style.display = personless ? '' : 'none';

  // No person doc ⇒ no phone on file ⇒ email is the only delivery channel.
  // Hide the whole group rather than disabling radios, and force `email` so a
  // stale `sms`/`both` selection from a prior open can't be submitted (the
  // server rejects sms/both with no phone).
  const deliveryGroup = document.getElementById('staff-login-invite-delivery-group');
  if (deliveryGroup) deliveryGroup.style.display = personless ? 'none' : '';
  if (personless) {
    document.querySelectorAll('input[name="staff-login-delivery"]').forEach(r => {
      r.checked = r.value === 'email';
    });
  }

  _applyPersonlessModuleGrid('invite', personless);

  const nameInput = document.getElementById('staff-login-invite-name');
  if (nameInput && personless) nameInput.value = '';
}

// TASK-1489 — hide/unhide the personId-scoped module checkboxes for a given grid.
// Shared by the invite modal AND the Edit Access modal: the server blocks
// clock/jobboard for a personless member on BOTH sendUserInvite and
// updateMemberAccess, so both grids must hide them or an admin meets an error
// whose cause is an invisible checkbox.
function _applyPersonlessModuleGrid(mode, personless) {
  // Single source of truth for "is THIS grid in personless mode", read by the
  // quick-fill handler (which must strip blocked modules) in both modals.
  const grid = document.getElementById(`staff-login-custom-modules-${mode}`);
  if (grid) grid.dataset.personless = personless ? '1' : '';
  // Unchecking matters as much as hiding: a hidden-but-checked box still rides
  // along into the payload (the grid is read by :checked selector).
  STAFF_LOGIN_PERSONLESS_BLOCKED_MODULES.forEach(slug => {
    const label = document.getElementById(`staff-login-module-opt-${mode}-${slug}`);
    if (label) label.style.display = personless ? 'none' : '';
    if (personless) {
      const cb = document.querySelector(`.staff-login-custom-module-${mode}[value="${slug}"]`);
      if (cb) cb.checked = false;
    }
  });
  const note = document.getElementById(`staff-login-modules-personless-note-${mode}`);
  if (note) note.style.display = personless ? '' : 'none';

  // The Employee quick-fill IS ['clock'] — after the personless strip it would
  // select nothing at all. Hide it rather than offering a chip that does nothing.
  const employeeChip = document.getElementById(`staff-login-quickfill-${mode}-employee`);
  if (employeeChip) employeeChip.style.display = personless ? 'none' : '';
}

// TASK-1489 / TASK-1500 — open the invite modal with NO person behind it.
// `opts.preset` optionally pre-applies a STAFF_LOGIN_ROLE_PRESETS key (the
// Roster button passes 'complianceManager', which is Kolsen's own matrix).
function showInviteUserModal(opts) {
  ensureModalsMounted();
  const modal = document.getElementById('modal-staff-login-invite');
  if (!modal) return;
  const _tenant = window.AppState?.tenant;
  if (!_tenant) {
    window.showToast?.('Tenant not loaded yet — refresh and try again.', 'error');
    return;
  }
  if (_tenant === 'demo') {
    window.showToast?.('Inviting users isn\'t available in the demo.', 'info');
    return;
  }
  // Client-side courtesy gate. The button is hidden for non-admins, but this
  // catches a direct window.RosterModule call; the server is the real boundary.
  if (!_canInviteUsers()) {
    window.showToast?.('Only an owner or admin can invite users.', 'error');
    return;
  }

  // Reset modal to form state (mirrors showSetupStaffLoginModal).
  document.getElementById('staff-login-invite-form-body').style.display = '';
  document.getElementById('staff-login-invite-success').style.display = 'none';
  document.getElementById('staff-login-invite-actions').style.display = '';
  document.getElementById('staff-login-invite-success-actions').style.display = 'none';
  document.getElementById('staff-login-invite-error').style.display = 'none';
  document.getElementById('staff-login-cap-banner').style.display = 'none';

  // No person → clear the person-scoped fields so a previously-open person
  // card can't leak its id/name/email into this invite.
  document.getElementById('staff-login-invite-person-id').value = '';
  document.getElementById('staff-login-invite-email').value = '';
  const personNameEl = document.getElementById('staff-login-invite-person-name');
  if (personNameEl) personNameEl.textContent = '';
  document.getElementById('staff-login-invite-title').textContent = 'Invite a user';

  const presetKey = (opts && opts.preset) || 'complianceManager';
  const preset = STAFF_LOGIN_ROLE_PRESETS[presetKey] || STAFF_LOGIN_ROLE_PRESETS.complianceManager;
  const presetSet = new Set(preset.modules);
  document.querySelectorAll('.staff-login-custom-module-invite').forEach(cb => {
    cb.checked = presetSet.has(cb.value);
  });
  const inviteRoleField = document.getElementById('staff-login-role-invite');
  if (inviteRoleField) inviteRoleField.value = preset.role;
  const inviteVuo = document.getElementById('staff-login-viewupload-invite');
  if (inviteVuo) inviteVuo.checked = preset.viewUploadOnly === true;
  const inviteSettingsBox = document.getElementById('staff-login-settings-billing-invite'); // TASK-9986 S1 — off by default
  if (inviteSettingsBox) inviteSettingsBox.checked = false;
  const inviteManageBox = document.getElementById('staff-login-manage-logins-invite'); // TASK-9389 S1 — off by default
  if (inviteManageBox) inviteManageBox.checked = false;

  // TASK-1389 — reset the Admin-status block so a prior open can't leak a
  // checked toggle (the personless preset is never the full set, so it stays
  // hidden; _wireStaffLoginGrid repaints below).
  const inviteAdminBlock = document.getElementById('staff-login-admin-block-invite');
  const inviteAdminToggle = document.getElementById('staff-login-admin-toggle-invite');
  if (inviteAdminBlock) { inviteAdminBlock.dataset.visible = '0'; inviteAdminBlock.style.display = 'none'; }
  if (inviteAdminToggle) inviteAdminToggle.checked = false;
  document.querySelectorAll('input[name="staff-login-crm-role-invite"]').forEach(r => {
    r.checked = r.value === 'rep';
  });

  // AFTER the preset is applied, so a preset that includes a blocked module gets
  // it unchecked rather than smuggled through hidden.
  _applyInviteModalPersonlessMode(true);

  _wireStaffLoginGrid('invite');
  window.paintTenantSendNotices?.(modal); // TASK-10027 muted-tenant UI hints
  modal.classList.remove('hidden');
  document.getElementById('staff-login-invite-name')?.focus();
  _checkStaffLoginCap();
}

function showSetupStaffLoginModal(opts) {
  const modal = document.getElementById('modal-staff-login-invite');
  if (!modal) return;
  // TASK-1585 — the Set-up-login CTA is already hidden with the rest of the
  // section for sessions that can't manage logins; this catches a direct
  // window.RosterModule call and the CRM "Invite to CRM Team" launch, which
  // reaches this modal without ever rendering the person card's section.
  if (!_canManageStaffLogins()) {
    window.showToast?.(STAFF_LOGIN_REFUSAL_MSG, 'error');
    return;
  }
  // TASK-375 — when launched from CRM ("Invite to CRM Team"), pre-select the
  // Custom preset with ONLY the `crm` module checked (CRM-only baseline —
  // supervisor does NOT imply broader app modules) and reveal the CRM-level
  // selector. Plain "Set up login" callers pass nothing → dispatcher default.
  const crmPreset = !!(opts && opts.crmPreset);

  // The personId comes from the open person modal (hidden field)
  const personId = document.getElementById('person-id')?.value;
  if (!personId) {
    window.showToast?.('Save the person first, then set up their login.', 'error');
    return;
  }
  const person = RosterState.people.find(p => p.id === personId);
  if (!person) {
    window.showToast?.('Person not found — refresh and try again.', 'error');
    return;
  }

  // Reset modal to form state
  document.getElementById('staff-login-invite-form-body').style.display = '';
  document.getElementById('staff-login-invite-success').style.display = 'none';
  document.getElementById('staff-login-invite-actions').style.display = '';
  document.getElementById('staff-login-invite-success-actions').style.display = 'none';
  document.getElementById('staff-login-invite-error').style.display = 'none';
  document.getElementById('staff-login-cap-banner').style.display = 'none';

  // Pre-fill fields
  document.getElementById('staff-login-invite-person-id').value = personId;
  document.getElementById('staff-login-invite-person-name').textContent = person.name || 'this person';
  document.getElementById('staff-login-invite-title').textContent = `Set up login for ${person.name || 'this person'}`;
  document.getElementById('staff-login-invite-email').value = (person.email || '').trim();

  // TASK-608 — MODULE-FIRST reset. Default grid = Dispatcher's modules (the old
  // default preset) so a plain "Set up login" still lands on a sensible grant;
  // a CRM launch (TASK-375) checks ONLY `crm`. The hidden role field carries the
  // matching preset role so a no-edit send keeps it (dispatcher/staff).
  const defaultModules = crmPreset ? ['crm'] : [...STAFF_LOGIN_ROLE_PRESETS.dispatcher.modules];
  const defaultModuleSet = new Set(defaultModules);
  document.querySelectorAll('.staff-login-custom-module-invite').forEach(cb => {
    cb.checked = defaultModuleSet.has(cb.value);
  });
  const inviteRoleField = document.getElementById('staff-login-role-invite');
  if (inviteRoleField) inviteRoleField.value = crmPreset ? 'staff' : STAFF_LOGIN_ROLE_PRESETS.dispatcher.role;
  // Reset View & upload only to off (least restriction = default).
  const inviteVuo = document.getElementById('staff-login-viewupload-invite');
  if (inviteVuo) inviteVuo.checked = false;
  const inviteSettingsBox = document.getElementById('staff-login-settings-billing-invite'); // TASK-9986 S1 — off by default
  if (inviteSettingsBox) inviteSettingsBox.checked = false;
  const inviteManageBox = document.getElementById('staff-login-manage-logins-invite'); // TASK-9389 S1 — off by default
  if (inviteManageBox) inviteManageBox.checked = false;
  // TASK-1389 — reset the Admin-status block so a prior open's state can't
  // leak. _wireStaffLoginGrid below repaints; the default dispatcher grid is
  // not the full set, so the block stays hidden until all 13 are checked.
  const inviteAdminBlock = document.getElementById('staff-login-admin-block-invite');
  const inviteAdminToggle = document.getElementById('staff-login-admin-toggle-invite');
  if (inviteAdminBlock) { inviteAdminBlock.dataset.visible = '0'; inviteAdminBlock.style.display = 'none'; }
  if (inviteAdminToggle) inviteAdminToggle.checked = false;
  // Reset CRM-level selector to Rep (safe default — least privilege).
  document.querySelectorAll('input[name="staff-login-crm-role-invite"]').forEach(r => {
    r.checked = r.value === 'rep';
  });

  // Phone handling for the Email + Text option
  const phoneHint = document.getElementById('staff-login-phone-hint');
  const phoneMissingHint = document.getElementById('staff-login-phone-missing-hint');
  const phoneDisplay = document.getElementById('staff-login-invite-phone-display');
  const prefHint = document.getElementById('staff-login-delivery-pref-hint');
  const bothRadio = document.getElementById('staff-login-delivery-both');
  const bothLabel = document.getElementById('staff-login-delivery-both-label');
  if (person.phone) {
    phoneDisplay.textContent = person.phone;
    phoneHint.style.display = 'none'; // hidden until the user picks Email + Text
    phoneMissingHint.style.display = 'none';
    bothRadio.disabled = false;
    if (bothLabel) bothLabel.style.opacity = '1';
  } else {
    phoneHint.style.display = 'none';
    phoneMissingHint.style.display = '';
    bothRadio.disabled = true;
    if (bothLabel) bothLabel.style.opacity = '0.5';
  }

  // TASK-4840 — preselect the delivery channel from the person's OWN stated
  // preference, and say out loud which one it is. Before this, the radios were
  // hard-reset to "Email only" on every open and `preferredChannel` was read
  // exactly once in this whole file (the person-edit form) — never here. So the
  // operator was choosing blind: Lacey picked Text for Alyse Hymer, whose record
  // says she prefers email, and no email was ever sent (2026-09-09).
  // A `sms`/`both` preference maps to **Email + Text**, never text alone — see
  // the comment on the radio markup for why text-only is gone.
  const prefRaw = person.notifications?.preferredChannel || person.preferredChannel || '';
  const wantsText = (prefRaw === 'sms' || prefRaw === 'both') && !!person.phone;
  document.querySelectorAll('input[name="staff-login-delivery"]').forEach(r => {
    r.checked = r.value === (wantsText ? 'both' : 'email');
  });
  // The change handler below only fires on user interaction, so paint the
  // "text goes to <number>" line here for the preselected state too.
  phoneHint.style.display = wantsText ? '' : 'none';
  if (prefHint) {
    const firstName = escapeHtml((person.name || 'They').split(' ')[0]);
    if (prefRaw === 'email') {
      prefHint.innerHTML = `${firstName} prefers <strong>email</strong>.`;
      prefHint.style.display = '';
    } else if (prefRaw === 'sms' || prefRaw === 'both') {
      prefHint.innerHTML = person.phone
        ? `${firstName} prefers <strong>text</strong> — the invite is emailed too, because a texted login link often won't open.`
        : `${firstName} prefers <strong>text</strong>, but has no phone on file — sending by email.`;
      prefHint.style.display = '';
    } else {
      prefHint.innerHTML = '';
      prefHint.style.display = 'none';
    }
  }

  // TASK-1489 — this is the ROSTER-PERSON path: explicitly clear personless mode
  // so a prior "Invite User" open can't leave the flag set (which would send
  // personless:true WITH a personId and get rejected) or leave Clock/Job Board
  // hidden on a normal employee invite.
  _applyInviteModalPersonlessMode(false);

  // TASK-608 — wire module-first handlers (checkboxes, quick-fill, view-upload)
  // + initial paint of sensitive warning / CRM-role visibility / empty hint.
  _wireStaffLoginGrid('invite');
  // Wire delivery-channel change handler
  document.querySelectorAll('input[name="staff-login-delivery"]').forEach(r => {
    r.onchange = () => {
      const v = r.checked ? r.value : null;
      if (v === 'both' && person.phone) {
        phoneHint.style.display = '';
      } else {
        phoneHint.style.display = 'none';
      }
    };
  });

  window.paintTenantSendNotices?.(modal); // TASK-10027 muted-tenant UI hints
  modal.classList.remove('hidden');

  // Async cap check — non-blocking. Reads tenants/{tid}/members count.
  _checkStaffLoginCap();
}

// TASK-608 — read the currently-checked module slugs for a modal.
// TASK-815 — normalize the hr-directory→hr dependency at read time (defense in
// depth): hr-directory is the SSN grant and can't exist without base hr. If the
// grid ever yields hr-directory without hr (e.g. a programmatic check that
// skipped the onchange handler), inject hr so the SAVED grant is always
// consistent with the rule. hr alone is left untouched (the safe default).
function _staffLoginCheckedModules(mode) {
  const mods = Array.from(document.querySelectorAll(`.staff-login-custom-module-${mode}:checked`)).map(cb => cb.value);
  if (mods.includes('hr-directory') && !mods.includes('hr')) mods.push('hr');
  return mods;
}

// TASK-608 — module-first wiring. Binds checkbox + quick-fill + view-upload
// handlers once per modal open, and paints the dependent UI (sensitive warning,
// CRM-level selector, empty hint, view-upload workforce note). Idempotent:
// onX assignment replaces any prior handler.
function _wireStaffLoginGrid(mode) {
  // Module checkboxes — any toggle = hand-edit → clear preset role + repaint.
  document.querySelectorAll(`.staff-login-custom-module-${mode}`).forEach(cb => {
    cb.onchange = () => {
      const roleField = document.getElementById(`staff-login-role-${mode}`);
      if (roleField) roleField.value = ''; // hand-edited → role becomes 'staff'
      // TASK-815 (Chris 2026-06-25) — hr-directory REQUIRES hr; hr may stand
      // alone. hr-directory is the SSN/W-4/I-9 grant, so it can't exist without
      // the base HR module. Checking HR Directory auto-checks HR; unchecking HR
      // unchecks HR Directory. HR alone never forces Directory (the safe default
      // = HR without employee-records/SSN). Mirrors the PIN modal (app.js).
      if (cb.value === 'hr-directory' && cb.checked) {
        const hrCb = document.querySelector(`.staff-login-custom-module-${mode}[value="hr"]`);
        if (hrCb) hrCb.checked = true;
      } else if (cb.value === 'hr' && !cb.checked) {
        const dirCb = document.querySelector(`.staff-login-custom-module-${mode}[value="hr-directory"]`);
        if (dirCb) dirCb.checked = false;
      }
      _onStaffLoginModulesChanged(mode);
    };
  });
  // Quick-fill chips — set the checkbox set + remember the preset role.
  document.querySelectorAll(`.staff-login-quickfill-${mode}`).forEach(btn => {
    btn.onclick = () => {
      const cfg = STAFF_LOGIN_ROLE_PRESETS[btn.dataset.preset];
      if (!cfg) return;
      // TASK-1489 — in personless mode, strip the modules that require an employee
      // record. Without this, clicking "Admin (all)" or "Employee" would re-check
      // the HIDDEN clock/jobboard boxes and smuggle them into the payload (the
      // server rejects them, so the admin would just meet an error they can't see
      // the cause of, because the offending checkbox is invisible).
      const personlessNow =
        document.getElementById(`staff-login-custom-modules-${mode}`)?.dataset.personless === '1';
      const set = new Set(
        personlessNow
          ? cfg.modules.filter(m => !STAFF_LOGIN_PERSONLESS_BLOCKED_MODULES.includes(m))
          : cfg.modules
      );
      // TASK-9994 COD r1 — with 811 off its box is hidden: a quick-fill never
      // changes it (no new hidden grant; an existing grant is kept as is).
      const off811 = window.AppState?.settings?.enable811 !== true && window.AppState?.isDemo !== true;
      document.querySelectorAll(`.staff-login-custom-module-${mode}`).forEach(cb => {
        if (off811 && cb.value === '811-permits') return;
        if (cb.dataset.capLocked === '1') return; // TASK-9389 S1 — outside the manager's own modules
        cb.checked = set.has(cb.value);
      });
      const roleField = document.getElementById(`staff-login-role-${mode}`);
      if (roleField) roleField.value = cfg.role;
      // TASK-1489 — a preset may declare viewUploadOnly (Compliance Manager =
      // reviewer access). SET-ONLY, never clear: if the operator hand-checked
      // "View & upload only" and then clicks a preset that doesn't declare it,
      // leaving the restriction on errs toward LESS access. Silently dropping a
      // restriction on a chip click would be an invisible over-grant.
      if (cfg.viewUploadOnly === true) {
        const vuoCb = document.getElementById(`staff-login-viewupload-${mode}`);
        if (vuoCb) vuoCb.checked = true;
      }
      // TASK-1389 — the Admin quick-fill converges on the explicit toggle:
      // force it CHECKED even if the block was already visible with the toggle
      // hand-unchecked (clicking the chip labeled "Admin" is an explicit ask).
      if (btn.dataset.preset === 'admin') {
        const adminToggle = document.getElementById(`staff-login-admin-toggle-${mode}`);
        if (adminToggle) adminToggle.checked = true;
      }
      // TASK-9986 S1 (Chris 2026-10-20 Q1) — Admin (all) ticks the settings
      // switch; the other chips clear it. Owner only (the box is disabled otherwise).
      const settingsBox = document.getElementById(`staff-login-settings-billing-${mode}`);
      if (settingsBox && _staffLoginViewerIsOwner()) settingsBox.checked = btn.dataset.preset === 'admin';
      _onStaffLoginModulesChanged(mode);
    };
  });
  // View & upload only toggle — repaint the FLSA workforce note.
  const vuo = document.getElementById(`staff-login-viewupload-${mode}`);
  if (vuo) vuo.onchange = () => _onStaffLoginModulesChanged(mode);
  _applyStaffLoginModuleCap(mode); // TASK-9389 S1
  _onStaffLoginModulesChanged(mode);
}

// TASK-608 — repaint everything that depends on the current module selection.
function _onStaffLoginModulesChanged(mode) {
  _updateStaffLoginSensitiveWarning(mode);
  _updateStaffLoginSettingsGrant(mode); // TASK-9986 S1
  _updateStaffLoginManageGrant(mode); // TASK-9389 S1
  _updateStaffLoginCrmRoleVisibility(mode);
  _updateStaffLoginViewUploadNote(mode);
  _updateStaffLoginAdminToggle(mode);
  if (mode === 'edit' && typeof _updateEditAccessSaveButton === 'function') {
    _updateEditAccessSaveButton();
  }
  if (mode === 'invite') {
    const hint = document.getElementById(`staff-login-modules-empty-${mode}`);
    if (hint) hint.style.display = _staffLoginCheckedModules(mode).length === 0 ? '' : 'none';
  }
}

// TASK-9986 S1 — only the OWNER (tenants.ownerId, never a role) may grant or
// remove the "can change Billing settings" switch. Demo may (UI parity).
function _staffLoginViewerIsOwner() {
  const st = window.AppState || {};
  if (st.isDemo || st.tenant === 'demo') return true;
  const uid = st.user?.uid || null;
  return !!uid && st.tenantData?.ownerId === uid;
}
function _updateStaffLoginSettingsGrant(mode) {
  const group = document.getElementById(`staff-login-settings-group-${mode}`);
  const box = document.getElementById(`staff-login-settings-billing-${mode}`);
  const note = document.getElementById(`staff-login-settings-note-${mode}`);
  if (!group || !box) return;
  const hasBilling = _staffLoginCheckedModules(mode).includes('billing');
  group.style.display = hasBilling ? '' : 'none';
  const vuo = !!document.getElementById(`staff-login-viewupload-${mode}`)?.checked;
  const owner = _staffLoginViewerIsOwner();
  box.disabled = vuo || !owner;
  let msg = '';
  if (!owner) msg = 'Only the account owner can change this.';
  else if (vuo) msg = 'View & upload only is on, so this login cannot change settings.';
  if (note) { note.textContent = msg; note.style.display = msg ? '' : 'none'; }
}
// TASK-9389 S1 — the "can manage logins" switch: owner-only, like the settings
// switch above. View & upload only forces it off.
function _updateStaffLoginManageGrant(mode) {
  const box = document.getElementById(`staff-login-manage-logins-${mode}`);
  const note = document.getElementById(`staff-login-manage-note-${mode}`);
  if (!box) return;
  const vuo = !!document.getElementById(`staff-login-viewupload-${mode}`)?.checked;
  const owner = _staffLoginViewerIsOwner();
  box.disabled = vuo || !owner;
  if (vuo && owner) box.checked = false;
  let msg = '';
  if (!owner) msg = 'Only the account owner can change this.';
  else if (vuo) msg = 'View & upload only is on, so this login cannot manage logins.';
  if (note) { note.textContent = msg; note.style.display = msg ? '' : 'none'; }
}
// Owner sends true/false (untick removes it); anyone else omits it and the
// server keeps the stored value.
function _staffLoginManagePayload(mode, viewUploadOnly) {
  if (!_staffLoginViewerIsOwner()) return undefined;
  return !!document.getElementById(`staff-login-manage-logins-${mode}`)?.checked && !viewUploadOnly;
}
// TASK-9389 S1 — a manager (not the owner) may give or take away only modules
// they hold: null = no cap (owner / demo), else the viewer's own modules.
function _staffLoginViewerModuleCap() {
  if (_staffLoginViewerIsOwner()) return null;
  const ma = window.AppState?.memberAccess;
  return Array.isArray(ma?.modules) ? ma.modules.slice() : [];
}
// Role tiers (server twin _memberRoleRank): a holder sets / touches only roles
// at or below their own.
function _staffLoginRoleRank(role) {
  return ({ admin: 3, dispatcher: 2, mechanic: 1 })[typeof role === 'string' ? role : ''] || 0;
}
function _staffLoginViewerRoleRank() {
  return _staffLoginViewerIsOwner() ? 99 : _staffLoginRoleRank(window.AppState?.memberAccess?.role);
}
// Admin only from the owner or an admin who manages logins (server twin in
// sendUserInvite / updateMemberAccess).
function _staffLoginViewerCanGrantAdmin() {
  return _staffLoginViewerIsOwner() || window.AppState?.memberAccess?.role === 'admin';
}
// Lock the module boxes outside the cap (their state is kept: the server
// refuses adding OR removing a module the manager does not hold).
function _applyStaffLoginModuleCap(mode) {
  const cap = _staffLoginViewerModuleCap();
  document.querySelectorAll(`.staff-login-custom-module-${mode}`).forEach(cb => {
    const locked = !!cap && !cap.includes(cb.value);
    cb.disabled = locked;
    cb.dataset.capLocked = locked ? '1' : '0';
    const label = cb.closest ? cb.closest('label') : null;
    if (label) label.title = locked ? "You don't have this module, so you can't give or take it away." : '';
  });
  // Quick-fill chips whose preset role is above the viewer's tier are disabled.
  const rank = _staffLoginViewerRoleRank();
  document.querySelectorAll(`.staff-login-quickfill-${mode}`).forEach(btn => {
    const cfg = STAFF_LOGIN_ROLE_PRESETS[btn.dataset.preset];
    const over = !!cfg && _staffLoginRoleRank(cfg.role) > rank;
    btn.disabled = over;
    btn.title = over ? 'Only the account owner, or someone with at least this role, can give it.' : '';
  });
}
// May the viewer change (forRevoke false) / revoke (true) this member? Client
// twin of _memberManagerTargetRefusal + the revoke cap. Hides buttons only.
function _staffLoginViewerCanTouch(member, forRevoke) {
  const st = window.AppState || {};
  const uid = st.user?.uid || null;
  const m = member || {};
  if (m.uid === uid || m.role === 'owner' || (st.tenantData?.ownerId && m.uid === st.tenantData.ownerId)) return false;
  if (_staffLoginViewerIsOwner()) return true;
  if (m.manageLogins === true) return false;
  if (_staffLoginRoleRank(m.role) > _staffLoginViewerRoleRank()) return false;
  if (forRevoke) {
    const cap = _staffLoginViewerModuleCap() || [];
    if ((m.modules || []).some(x => !cap.includes(x))) return false;
    if (Array.isArray(m.settingsModules) && m.settingsModules.length) return false;
  }
  return true;
}
// What the submit sends: the owner always sends the list (so unticking removes
// the grant); anyone else omits it, and the server keeps the stored value.
function _staffLoginSettingsPayload(mode, modules, viewUploadOnly) {
  if (!_staffLoginViewerIsOwner()) return undefined;
  const box = document.getElementById(`staff-login-settings-billing-${mode}`);
  return (box?.checked && modules.includes('billing') && !viewUploadOnly) ? ['billing'] : [];
}

// TASK-375 — the CRM-level (rep/supervisor) selector is only relevant when the
// grant actually includes the `crm` module.
function _updateStaffLoginCrmRoleVisibility(mode) {
  const group = document.getElementById(`staff-login-crm-role-group-${mode}`);
  if (!group) return;
  group.style.display = _staffLoginCheckedModules(mode).includes('crm') ? '' : 'none';
}

// TASK-608 — show the inline RED soft warning whenever ANY checked module is in
// the sensitive set (PII / FCRA consumer reports / payroll / financials).
// Supersedes the hr-directory-only TASK-455/2026-06-08 trigger.
function _updateStaffLoginSensitiveWarning(mode) {
  const warn = document.getElementById(`staff-login-sensitive-warning-${mode}`);
  if (!warn) return;
  const exposes = _staffLoginCheckedModules(mode).some(m => STAFF_LOGIN_SENSITIVE_MODULES.has(m));
  warn.style.display = exposes ? 'flex' : 'none';
}

// TASK-1389 — show the amber "grant Admin status?" block only when the checked
// set exactly equals the full Admin preset. On each hidden→visible transition
// the toggle PRE-CHECKS (Chris's default: full access = admin); while the block
// stays visible the admin's explicit un/check is preserved across repaints
// (e.g. toggling View & upload only must not re-check it). Hiding clears both
// so a stale checked toggle can never leak into a later reveal.
function _updateStaffLoginAdminToggle(mode) {
  const block = document.getElementById(`staff-login-admin-block-${mode}`);
  const toggle = document.getElementById(`staff-login-admin-toggle-${mode}`);
  if (!block || !toggle) return;
  const isFull = _staffLoginIsFullAccessSet(_staffLoginCheckedModules(mode));
  if (isFull) {
    if (block.dataset.visible !== '1') {
      block.dataset.visible = '1';
      toggle.checked = true; // TASK-1389 default: full access = admin
    }
    block.style.display = 'flex';
  } else if (mode === 'edit'
    && document.getElementById('staff-login-edit-uid')?.dataset.currentRole === 'admin') {
    // Integrated-gate: a CURRENT admin whose stored modules ≠ today's preset
    // (legacy grant) keeps the toggle VISIBLE and untouched — unchecking it is
    // the explicit demotion path; hiding+unchecking here was the silent
    // demotion trap (_staffLoginRoleFor's edit branch reads this toggle).
    block.dataset.visible = '1';
    block.style.display = 'flex';
  } else {
    block.dataset.visible = '0';
    block.style.display = 'none';
    toggle.checked = false;
  }
  // TASK-9389 S1 — only the owner or an admin who manages logins can make an admin.
  if (!_staffLoginViewerCanGrantAdmin()) { toggle.checked = false; toggle.disabled = true; } else { toggle.disabled = false; }
}

// TASK-608 — when View & upload only is checked AND workforce is granted, note
// that timeclock edits stay enabled (FLSA wage-hour carve-out in the rules).
function _updateStaffLoginViewUploadNote(mode) {
  const note = document.getElementById(`staff-login-viewupload-workforce-note-${mode}`);
  if (!note) return;
  const vuo = document.getElementById(`staff-login-viewupload-${mode}`)?.checked;
  const hasWorkforce = _staffLoginCheckedModules(mode).includes('workforce');
  note.style.display = (vuo && hasWorkforce) ? '' : 'none';
}

async function _checkStaffLoginCap() {
  const tenant = window.AppState?.tenant;
  if (!tenant) return;
  const banner = document.getElementById('staff-login-cap-banner');
  const sendBtn = document.getElementById('btn-staff-login-invite-send');
  if (!banner) return;
  try {
    const snap = await getDocs(collection(db, 'tenants', tenant, 'members'));
    const active = snap.docs.filter(d => {
      const m = d.data() || {};
      return (m.status || 'active') === 'active';
    }).length;
    if (active >= STAFF_LOGIN_STAFF_SOFT_CAP) {
      banner.style.display = '';
      banner.style.background = '#fee2e2';
      banner.style.color = '#991b1b';
      banner.textContent = `At cap: ${active} / ${STAFF_LOGIN_STAFF_SOFT_CAP} active staff logins — revoke an unused login from Settings → Team Access before adding more.`;
      if (sendBtn) sendBtn.disabled = true;
    } else if (active >= 45) {
      banner.style.display = '';
      banner.style.background = '#fef3c7';
      banner.style.color = '#92400e';
      banner.textContent = `Approaching cap: ${active} / ${STAFF_LOGIN_STAFF_SOFT_CAP} active staff logins.`;
      if (sendBtn) sendBtn.disabled = false;
    } else {
      banner.style.display = 'none';
      if (sendBtn) sendBtn.disabled = false;
    }
  } catch (err) {
    // Non-fatal — server enforces cap as a soft warn-log, not a throw.
    console.warn('[StaffLogin] cap check failed:', err?.message);
  }
}

function closeStaffLoginInviteModal() {
  document.getElementById('modal-staff-login-invite')?.classList.add('hidden');
  // TASK-1489 — clear the personless flag on close so the next open starts from a
  // known state regardless of which entry point ran last.
  const personlessFlag = document.getElementById('staff-login-invite-personless');
  const wasPersonless = personlessFlag?.value === '1';
  if (personlessFlag) personlessFlag.value = '';
  if (wasPersonless) {
    // No person card to re-render (that's the point). Settings → Team Access is
    // the ONLY surface a personless invite appears on, so refresh it when that's
    // the active view. Gated on currentView, NOT on the element existing —
    // #team-access-list is in the static shell, so an existence check would fire
    // a Firestore read + callable on every close from the Roster too.
    if (window.AppState?.currentView === 'settings' && typeof window.renderTeamAccessList === 'function') {
      window.renderTeamAccessList();
    }
    return;
  }
  // TASK-349 — if an invite was just sent, the person card should now show the
  // pending banner instead of the Set-up-login CTA. Re-render the staff login
  // section so the new state is reflected without requiring a card close+reopen.
  const personId = document.getElementById('person-id')?.value;
  if (personId) {
    const person = RosterState.people.find(p => p.id === personId);
    if (person) _renderStaffLoginSection(person);
  }
}

async function submitStaffLoginInvite(e) {
  e.preventDefault();
  const tenant = window.AppState?.tenant;
  if (!tenant) {
    window.showToast?.('Tenant not loaded yet — refresh and try again.', 'error');
    return;
  }
  // TASK-1585 — server-truth gate on the sendUserInvite write. Covers BOTH open
  // paths (person-card "Set up login" and the personless "Invite User" modal),
  // which is why it lives here and not only in the two show* functions.
  //
  // The flag guards the AWAIT WINDOW THIS GATE INTRODUCED, and nothing more.
  // `sendUserInvite` has no pending-invite dedupe, so two clicks would mint two
  // invite tokens for the same person — and this gate awaits a Firestore read,
  // turning a tight synchronous window into a round-trip-sized one. Flagged by the
  // independent COD on this diff.
  //
  // Deliberately NOT done by disabling the send button here: seven validation
  // early-returns sit between this point and where the button is disabled below,
  // so an early disable would strand the button dead after any validation error —
  // a worse bug than the one being fixed. The pre-existing synchronous window
  // between validation and send is untouched; that is the existing code's call,
  // not a regression from this change.
  if (_staffLoginGateInFlight) return;
  _staffLoginGateInFlight = true;
  let _gateOk;
  try {
    _gateOk = await _assertCanManageStaffLogins();
  } finally {
    _staffLoginGateInFlight = false;
  }
  if (!_gateOk) {
    _showStaffLoginError(STAFF_LOGIN_REFUSAL_MSG);
    return;
  }
  // TASK-1489 — two contracts, one modal. Personless = no roster person exists,
  // so skip every person lookup and force email delivery.
  const personless = document.getElementById('staff-login-invite-personless')?.value === '1';
  const personId = document.getElementById('staff-login-invite-person-id').value;
  let person = null;
  let inviteeName = '';
  if (personless) {
    if (personId) {
      // Should be impossible (both open paths clear the other's fields) — but a
      // mixed payload is exactly what the server rejects, so fail loudly here.
      _showStaffLoginError('Invite state is mixed up — close and reopen the modal.');
      return;
    }
    inviteeName = (document.getElementById('staff-login-invite-name')?.value || '').trim().slice(0, 80);
  } else {
    if (!personId) {
      window.showToast?.('Missing person — close and reopen the modal.', 'error');
      return;
    }
    person = RosterState.people.find(p => p.id === personId);
    if (!person) {
      window.showToast?.('Person not found — refresh and try again.', 'error');
      return;
    }
  }
  const email = (document.getElementById('staff-login-invite-email').value || '').trim().toLowerCase();
  if (!email || !email.includes('@')) {
    _showStaffLoginError('Enter a valid email address.');
    return;
  }
  const deliveryChannel = personless
    ? 'email'
    : (document.querySelector('input[name="staff-login-delivery"]:checked')?.value || 'email');
  if (!personless && deliveryChannel === 'both' && !person.phone) {
    _showStaffLoginError('No phone on file — pick Email only or add a phone to the person first.');
    return;
  }

  // TASK-608 — MODULE-FIRST: modules from the checkbox grid; role derived (preset
  // role only if the set still matches a preset, else 'staff'); the View & upload
  // only capability flag read from its toggle.
  const modules = _staffLoginCheckedModules('invite');
  if (modules.length === 0) {
    _showStaffLoginError('Pick at least one module — or use a quick-fill above.');
    return;
  }
  // TASK-1489 — belt-and-suspenders: the personless grid hides + unchecks these,
  // but a quick-fill fired after the mode was applied (or a console poke) could
  // re-check one. Catch it here with a readable message instead of shipping a
  // payload the server will bounce as 'invalid-argument'.
  if (personless) {
    const blocked = modules.filter(m => STAFF_LOGIN_PERSONLESS_BLOCKED_MODULES.includes(m));
    if (blocked.length > 0) {
      _showStaffLoginError('Clock and Job Board need an employee record — uncheck them, or add this person to the roster instead.');
      return;
    }
  }
  const role = _staffLoginRoleFor('invite', modules);
  const viewUploadOnly = !!document.getElementById('staff-login-viewupload-invite')?.checked;

  // TASK-375 — CRM level (rep/supervisor) only meaningful when the grant
  // includes the `crm` module. Read the selector; default rep (least privilege).
  let crmRole = null;
  if (modules.includes('crm')) {
    crmRole = document.querySelector('input[name="staff-login-crm-role-invite"]:checked')?.value === 'supervisor'
      ? 'supervisor' : 'rep';
  }

  const sendBtn = document.getElementById('btn-staff-login-invite-send');
  const originalHtml = sendBtn?.innerHTML;
  if (sendBtn) {
    sendBtn.disabled = true;
    sendBtn.innerHTML = `<span class="loading-spinner-small"></span> Sending…`;
  }

  try {
    const fn = httpsCallable(functions, 'sendUserInvite');
    // TASK-1489 — personless payload omits personId entirely and carries the
    // explicit intent flag + the operator-typed name (server re-sanitizes it).
    const payload = personless
      ? {
        tenantId: tenant,
        personless: true,
        inviteeName,
        email,
        phone: null,
        modules,
        role,
        viewUploadOnly,
        crmRole,
        deliveryChannel: 'email',
      }
      : {
        tenantId: tenant,
        personId,
        email,
        phone: (deliveryChannel === 'both' || deliveryChannel === 'sms') ? (person.phone || null) : null,
        modules,
        role,
        viewUploadOnly, // TASK-608 — capability flag, persisted on the invite token
        crmRole, // TASK-375 — null unless crm is granted
        deliveryChannel,
      };
    // TASK-9986 S1 — the owner's "can change Billing settings" switch; only sent
    // when it grants something (the server refuses a grant from a non-owner).
    const settingsModulesInvite = _staffLoginSettingsPayload('invite', modules, viewUploadOnly);
    if (settingsModulesInvite && settingsModulesInvite.length) payload.settingsModules = settingsModulesInvite;
    // TASK-9389 S1 — only sent when it grants (the server refuses it from a non-owner).
    if (_staffLoginManagePayload('invite', viewUploadOnly) === true) payload.manageLogins = true;
    const { data } = await fn(payload);

    // Success — swap to success state
    document.getElementById('staff-login-invite-form-body').style.display = 'none';
    document.getElementById('staff-login-invite-actions').style.display = 'none';
    document.getElementById('staff-login-invite-success').style.display = '';
    document.getElementById('staff-login-invite-success-actions').style.display = '';

    const detail = document.getElementById('staff-login-invite-success-detail');
    const channels = [];
    if (data?.emailDelivered) channels.push('email');
    if (data?.smsDelivered) channels.push('SMS');
    const safePersonName = escapeHtml(
      personless ? (inviteeName || 'They') : (person.name || 'this person')
    );
    const safeEmail = escapeHtml(email);
    // TASK-10027 muted-tenant UI hints — say WHY a channel did not go.
    const _sendMode = (window.getTenantSendMode?.() || { mode: 'live' }).mode;
    if (channels.length > 0) {
      detail.innerHTML = `Sent to <strong>${safeEmail}</strong> via ${channels.join(' + ')}. ${safePersonName} can use the link to set up their login.`
        + (personless ? ` <br><span style="font-size:0.8rem;">They won't appear on your roster — manage or revoke them from Settings &rarr; Team Access.</span>` : '');
      if (_sendMode === 'muted' && deliveryChannel === 'both' && !data?.smsDelivered) {
        detail.innerHTML += ` <br><span style="font-size:0.8rem;">The text was not sent because texts are paused for this company.</span>`;
      }
    } else if (_sendMode === 'noContact') {
      detail.innerHTML = `Invite created for <strong>${safeEmail}</strong>, but nothing was sent: this company is set to send nothing to anyone right now.`;
    } else if (_sendMode === 'muted') {
      detail.innerHTML = `Invite created for <strong>${safeEmail}</strong>, but the email did not send. Texts are paused for this company and invite emails are limited to 20 a day. Check the address, or resend tomorrow from Settings → Team Access.`;
    } else {
      // Channel attempted but delivery failed — server returned ok with delivery flags false.
      detail.innerHTML = `Invite created for <strong>${safeEmail}</strong>, but delivery failed. Resend from Settings → Team Access.`;
    }
    // TASK-4840 - a text that landed while the email did NOT is the dangerous
    // half-success: the recipient holds a link their phone may refuse to open
    // and there is no working copy anywhere. Say so, instead of reporting a
    // green "Sent via SMS" and letting the operator walk away satisfied.
    if (data?.smsDelivered && !data?.emailDelivered) {
      detail.innerHTML += ` <br><span style="font-size:0.8rem;color:#991b1b;">The email did not send &mdash; and a texted login link often won't open on an iPhone. Fix the address and resend before counting this done.</span>`;
    }

    document.getElementById('staff-login-invite-success-existing').style.display = data?.alreadyHasAccount ? '' : 'none';

    // TASK-9891 follow-up — the length this invite was actually minted with
    // (sendUserInvite returns linkLabel from the owner's Settings → Security
    // setting). An older server with no label keeps the static wording above.
    const lengthEl = document.getElementById('staff-login-invite-success-length');
    const linkLabel = typeof data?.linkLabel === 'string' && /^\d+ (hours?|days)$/.test(data.linkLabel) ? data.linkLabel : '';
    if (lengthEl) {
      // Always set it: the modal is reused, so never leave the last invite's length.
      lengthEl.textContent = linkLabel
        ? `The invite link works for ${linkLabel}. The account owner sets this in Settings → Security. You can resend or revoke from Settings → Team Access.`
        : 'The invite link works for 7 days unless the account owner changed it in Settings → Security. You can resend or revoke from Settings → Team Access.';
    }

  } catch (err) {
    console.error('[StaffLogin] sendUserInvite error:', err);
    const msg = err?.message || 'Send failed — try again.';
    _showStaffLoginError(msg);
  } finally {
    if (sendBtn) {
      sendBtn.disabled = false;
      sendBtn.innerHTML = originalHtml;
    }
  }
}

// ============================================
// TASK-375 — "Invite to CRM Team" entry point (called from CRM Manage Team).
// Thin layer over the EXISTING staff-login invite flow: pick (or create) a
// roster person, then open the shared invite modal pre-set to CRM. Every CRM
// team member is a roster person with a sub-user login (Chris-locked decision).
// ============================================
function openCrmTeamInvite() {
  // Hop to Roster so the person list + person card + invite modal machinery are
  // mounted and subscribed (subscribeToData populates RosterState.people). This
  // is the deep-link the task calls for; the picker opens over the Roster view.
  if (typeof window.navigateToView === 'function' && window.AppState?.currentView !== 'roster') {
    window.navigateToView('roster');
  }
  // Wait for roster people to load (cold open) before showing the picker.
  let waited = 0;
  const tick = () => {
    if (RosterState._peopleLoaded || waited >= 5000) {
      _showCrmTeamPersonPicker();
      return;
    }
    waited += 150;
    setTimeout(tick, 150);
  };
  tick();
}

function _showCrmTeamPersonPicker() {
  document.getElementById('crm-team-person-picker')?.remove();
  ensureModalsMounted();
  const modal = document.createElement('div');
  modal.id = 'crm-team-person-picker';
  modal.className = 'modal';
  modal.style.zIndex = '9998';
  modal.innerHTML = `
    <div class="modal-content modal-form-narrow">
      <div class="modal-header">
        <h3>Invite to CRM Team</h3>
        <button class="btn-icon" onclick="document.getElementById('crm-team-person-picker')?.remove();"><span class="material-icons">close</span></button>
      </div>
      <div class="modal-body" style="max-height:70vh;max-height:70dvh;overflow-y:auto;">
        <p style="font-size:0.9rem;color:var(--text-secondary);margin:0 0 var(--spacing-md) 0;">Pick the person to invite, or add a new one. They'll get an email invite to set up their own login with CRM access.</p>
        <input type="text" id="crm-team-person-search" placeholder="Search people…" style="width:100%;padding:8px;border:1px solid var(--border-color);border-radius:6px;margin-bottom:12px;" oninput="window.RosterModule._renderCrmTeamPickerList()">
        <div id="crm-team-person-list" style="display:flex;flex-direction:column;gap:6px;"></div>
      </div>
      <div class="form-actions">
        <button type="button" class="btn btn-secondary" onclick="window.RosterModule._createPersonForCrmInvite()">
          <span class="material-icons">person_add</span> Add a new person
        </button>
      </div>
    </div>
  `;
  document.body.appendChild(modal);
  modal.classList.remove('hidden');
  _renderCrmTeamPickerList();
}

function _renderCrmTeamPickerList() {
  const list = document.getElementById('crm-team-person-list');
  if (!list) return;
  const q = (document.getElementById('crm-team-person-search')?.value || '').trim().toLowerCase();
  // Active people only — don't invite terminated/inactive staff to CRM.
  let people = (RosterState.people || []).filter(p => p.active !== false && (p.terminated !== true));
  if (q) people = people.filter(p => (p.name || '').toLowerCase().includes(q) || (p.email || '').toLowerCase().includes(q));
  if (people.length === 0) {
    list.innerHTML = `<div style="padding:16px;text-align:center;color:var(--text-secondary);background:var(--bg-secondary,#f8fafc);border-radius:6px;">${q ? 'No matches.' : 'No people on the roster yet — add one below.'}</div>`;
    return;
  }
  list.innerHTML = people.slice(0, 50).map(p => `
    <button type="button" class="btn btn-secondary" style="justify-content:flex-start;text-align:left;width:100%;" onclick="window.RosterModule._pickCrmTeamPerson('${escapeHtml(p.id)}')">
      <span class="material-icons" style="font-size:18px;">person</span>
      <span style="margin-left:6px;"><strong>${escapeHtml(p.name || '(no name)')}</strong>${p.email ? `<br><span style="font-size:0.8rem;color:var(--text-muted);">${escapeHtml(p.email)}</span>` : ''}</span>
    </button>
  `).join('');
}

function _pickCrmTeamPerson(personId) {
  const person = (RosterState.people || []).find(p => p.id === personId);
  if (!person) { window.showToast?.('Person not found — refresh and try again.', 'error'); return; }
  document.getElementById('crm-team-person-picker')?.remove();
  // Open the person card (sets #person-id + renders the staff-login section),
  // then open the shared invite modal pre-configured for CRM.
  editPerson(personId);
  // editPerson is synchronous for the fields we need (#person-id). Defer one
  // tick so the person modal's DOM is settled before mounting the invite modal.
  setTimeout(() => showSetupStaffLoginModal({ crmPreset: true }), 50);
}

function _createPersonForCrmInvite() {
  document.getElementById('crm-team-person-picker')?.remove();
  // Open the standard Add Person modal. Once saved, the manager re-opens
  // "Invite to CRM Team" (or uses Set up login on the new card with CRM). We
  // surface a hint so they know the next step. Reusing showAddPerson keeps the
  // create-roster-person flow single-sourced (no parallel mini-form).
  showAddPerson();
  window.showToast?.('Save the new person, then use "Set up login" → CRM, or Invite to CRM Team again.', 'info');
}

function _showStaffLoginError(msg) {
  const el = document.getElementById('staff-login-invite-error');
  if (!el) return;
  el.textContent = msg;
  el.style.display = '';
}

// ============================================
// EXPOSE TO WINDOW FOR ONCLICK HANDLERS
// ============================================
// ── TASK-10015 — "Recount from live jobs" (owner only) ──────────────────────
// Recomputes every quantity-tracked rental's out-count from the live jobs, with
// the SAME shared math the board uses (functions recountRentalsFromJobs runs
// shared/rentalLedgerMath.js). Preview first (asset · now · recount · the jobs
// behind the difference), writes only on the owner's confirm, and only the
// assets the owner previewed — refused if anything moved since. Modules, not
// roles: owner via tenants.ownerId (the callable decides again, server-side).
function _rentalRecountIsOwner() {
  const t = window.AppState?.tenant;
  if (!t || t === 'demo') return false;
  const uid = auth?.currentUser?.uid;
  return !!(uid && window.AppState?.tenantData?.ownerId === uid);
}

function _rentalRecountBarHtml() {
  if (!_rentalRecountIsOwner()) return '';
  const any = (RosterState.assets || []).some(a => a && a.active !== false && a.trackingMode === 'quantity' && (a.quantityType === 'rental' || a.isRental));
  if (!any) return '';
  return `
    <div class="asset-recount-bar" style="grid-column:1 / -1;display:flex;align-items:center;justify-content:space-between;gap:8px;flex-wrap:wrap;margin:0 0 var(--spacing-sm);padding:8px 12px;border:1px solid var(--border-color, #E2E8F0);border-radius:8px;background:#F8FAFC;color:var(--text-primary);font-size:0.82rem;">
      <span>Rental out-counts off? Recount them from your live jobs.</span>
      <button type="button" class="btn btn-secondary" id="btn-rental-recount" style="padding:4px 10px;font-size:0.8rem;" onclick="window.RosterModule.openRentalRecount()">
        <span class="material-icons" style="font-size:16px;vertical-align:-3px;">fact_check</span> Recount from live jobs
      </button>
    </div>`;
}

let _rentalRecountPlan = null;

function _rentalRecountOverlay() {
  let overlay = document.getElementById('modal-rental-recount');
  if (!overlay) {
    overlay = document.createElement('div');
    overlay.id = 'modal-rental-recount';
    overlay.className = 'modal';
    document.body.appendChild(overlay);
    overlay.onclick = (e) => { if (e.target === overlay) overlay.classList.add('hidden'); };
  }
  overlay.classList.remove('hidden');
  return overlay;
}

function _rentalRecountShell(bodyHtml, footHtml) {
  return `
    <div class="modal-content modal-fullscreen" style="max-width:760px;margin:auto;height:auto;max-height:100vh;max-height:100dvh;display:flex;flex-direction:column;border-radius:var(--radius-xl);overflow:hidden;background:#fff;">
      <div style="padding:14px 18px;background:linear-gradient(135deg, var(--brand-blue) 0%, #2563EB 100%);color:#fff;display:flex;justify-content:space-between;align-items:center;">
        <h3 style="margin:0;font-size:1.05rem;font-weight:700;"><span class="material-icons" style="font-size:20px;vertical-align:text-bottom;">fact_check</span> Recount from live jobs</h3>
        <button class="btn-icon" aria-label="Close" onclick="document.getElementById('modal-rental-recount').classList.add('hidden')" style="color:#fff;"><span class="material-icons">close</span></button>
      </div>
      <div id="rental-recount-body" style="padding:14px 18px;overflow-y:auto;flex:1;color:var(--text-primary);font-size:0.85rem;">${bodyHtml}</div>
      <div style="padding:12px 18px;border-top:1px solid #E2E8F0;display:flex;gap:8px;justify-content:flex-end;flex-wrap:wrap;">${footHtml}</div>
    </div>`;
}

function _rentalRecountRowsHtml(plans) {
  const fmt = (n) => Number(n || 0).toLocaleString('en-US');
  const changed = plans.filter(p => p.changed);
  if (!changed.length) {
    return `<p style="margin:0;"><strong>Everything matches.</strong> Every rental's out-count already equals what your live jobs hold (${plans.length} checked). Nothing to change.</p>`;
  }
  return `
    <p style="margin:0 0 10px;">Each job's rental record is rebuilt from its live days: what it has out, minus pickups that have happened. A pickup booked for a later day still counts as out until that day. Jobs from before rental records were kept are not recounted. <strong>A yard count beats this number</strong> — if the yard says different, untick the item and type the yard count on it instead.</p>
    ${changed.map(p => `
      <div class="rental-recount-asset" data-asset-id="${escapeHtml(p.assetId)}" style="border:1px solid #E2E8F0;border-radius:8px;padding:10px 12px;margin-bottom:10px;">
        <div style="display:flex;justify-content:space-between;gap:8px;flex-wrap:wrap;font-weight:700;">
          <label style="display:flex;align-items:center;gap:6px;cursor:pointer;"><input type="checkbox" class="rr-pick" data-asset-id="${escapeHtml(p.assetId)}"${(p.overOwned || p.negative || p.ledgerScanOk === false) ? '' : ' checked'}${p.ledgerScanOk === false ? ' disabled' : ''} style="width:18px;height:18px;"> ${escapeHtml(p.name || 'Rental')}</label>
          <span>Out now <span class="rr-current">${fmt(p.current)}</span> &rarr; recount <span class="rr-recomputed">${fmt(p.recomputed)}</span> <span style="font-weight:400;color:var(--text-secondary);">of ${fmt(p.owned)} owned</span></span>
        </div>
        ${p.overOwned ? '<div style="margin-top:4px;color:#B91C1C;font-weight:600;">More out than owned — get a yard count.</div>' : ''}
        ${p.negative ? '<div style="margin-top:4px;color:#B91C1C;font-weight:600;">More picked up than ever went out — get a yard count.</div>' : ''}
        ${p.ledgerScanOk === false ? '<div style="margin-top:4px;color:#B91C1C;font-weight:600;">Couldn\'t list every job holding this — it won\'t be changed.</div>' : ''}
        <ul style="margin:6px 0 0;padding-left:18px;">
          ${p.rows.map(r => `<li>${escapeHtml(r.title || 'Untitled job')}: ${fmt(r.before)} &rarr; ${fmt(r.after)} <span style="color:var(--text-secondary);">— ${escapeHtml(r.why || '')}</span></li>`).join('')}
          ${p.moreRows ? `<li style="color:var(--text-secondary);">…and ${fmt(p.moreRows)} more job(s)</li>` : ''}
        </ul>
        ${(p.older && p.older.length) ? `<div style="margin-top:4px;color:var(--text-secondary);">Not recounted: ${fmt(p.older.length + (p.moreOlder || 0))} older job${(p.older.length + (p.moreOlder || 0)) === 1 ? '' : 's'} from before rental records were kept (${p.older.slice(0, 5).map(o => escapeHtml(o.title || 'Untitled job')).join(', ')}${(p.older.length + (p.moreOlder || 0)) > 5 ? ', …' : ''}). Their share of the count stays as it is — a yard count settles those.</div>` : ''}
      </div>`).join('')}`;
}

async function openRentalRecount() {
  if (!_rentalRecountIsOwner()) { window.showToast?.('Only the account owner can recount rentals.', 'error'); return; }
  const overlay = _rentalRecountOverlay();
  overlay.innerHTML = _rentalRecountShell('<p style="margin:0;">Checking every rental against your live jobs…</p>',
    `<button class="btn btn-secondary" onclick="document.getElementById('modal-rental-recount').classList.add('hidden')">Close</button>`);
  _rentalRecountPlan = null;
  try {
    const fn = httpsCallable(functions, 'recountRentalsFromJobs');
    const res = await fn({ mode: 'preview' });
    const plans = (res && res.data && Array.isArray(res.data.plans)) ? res.data.plans : [];
    _rentalRecountPlan = plans;
    const applicable = plans.filter(p => p.changed && p.ledgerScanOk !== false);
    overlay.innerHTML = _rentalRecountShell(_rentalRecountRowsHtml(plans), `
      <button class="btn btn-secondary" onclick="document.getElementById('modal-rental-recount').classList.add('hidden')">${applicable.length ? 'Cancel' : 'Close'}</button>
      ${applicable.length ? `<button class="btn btn-primary" id="btn-rental-recount-apply" onclick="window.RosterModule.applyRentalRecount()"><span class="material-icons" style="font-size:16px;vertical-align:-3px;">check</span> Apply ticked recounts</button>` : ''}`);
  } catch (e) {
    console.warn('[Roster] TASK-10015 recount preview failed', e?.code || e?.message || e);
    overlay.innerHTML = _rentalRecountShell(`<p style="margin:0;color:#B91C1C;">Couldn't run the recount: ${escapeHtml(e?.message || 'try again')}. Nothing was changed.</p>`,
      `<button class="btn btn-secondary" onclick="document.getElementById('modal-rental-recount').classList.add('hidden')">Close</button>`);
  }
}

async function applyRentalRecount() {
  const plans = Array.isArray(_rentalRecountPlan) ? _rentalRecountPlan : [];
  // Only the items the owner left ticked (an untick = "the yard count wins").
  const picked = new Set(Array.from(document.querySelectorAll('#modal-rental-recount .rr-pick'))
    .filter(cb => cb.checked && !cb.disabled).map(cb => cb.getAttribute('data-asset-id')));
  const expect = {};
  for (const p of plans) if (p.changed && p.ledgerScanOk !== false && p.fingerprint && picked.has(p.assetId)) expect[p.assetId] = p.fingerprint;
  if (!Object.keys(expect).length) { window.showToast?.('Tick at least one item to recount.', 'info'); return; }
  const btn = document.getElementById('btn-rental-recount-apply');
  if (btn) { btn.disabled = true; btn.textContent = 'Applying…'; }
  try {
    const fn = httpsCallable(functions, 'recountRentalsFromJobs');
    const res = await fn({ mode: 'apply', expect });
    const results = (res && res.data && Array.isArray(res.data.results)) ? res.data.results : [];
    const done = results.filter(r => r.changed);
    const stale = results.filter(r => r.error);
    const fmt = (n) => Number(n || 0).toLocaleString('en-US');
    const overlay = _rentalRecountOverlay();
    overlay.innerHTML = _rentalRecountShell(`
      ${done.length ? `<p style="margin:0 0 8px;"><strong>Recounted ${done.length}:</strong></p><ul style="margin:0 0 8px;padding-left:18px;">${done.map(r => `<li>${escapeHtml(r.name || r.assetId)}: ${fmt(r.from)} &rarr; ${fmt(r.to)}</li>`).join('')}</ul>` : ''}
      ${stale.length ? `<p style="margin:0;color:#B45309;">${stale.length} not changed: ${stale.map(r => escapeHtml(r.message || r.assetId)).join(' · ')}</p>` : ''}
      ${!done.length && !stale.length ? '<p style="margin:0;">Nothing needed changing.</p>' : ''}`,
      `<button class="btn btn-primary" onclick="document.getElementById('modal-rental-recount').classList.add('hidden')">Done</button>`);
    _rentalRecountPlan = null;
    window.showToast?.(done.length ? `Recounted ${done.length} rental${done.length === 1 ? '' : 's'} from live jobs` : 'Rental counts unchanged', done.length ? 'success' : 'info');
  } catch (e) {
    console.warn('[Roster] TASK-10015 recount apply failed', e?.code || e?.message || e);
    if (btn) { btn.disabled = false; btn.textContent = 'Apply'; }
    window.showToast?.(`Couldn't apply the recount: ${e?.message || 'try again'}. Nothing was changed.`, 'error');
  }
}


window.RosterModule = {
  ensureModalsMounted,
  showBelowZeroAssets,     // TASK-9985 — Roster badge → items below zero
  clearBelowZeroAssets,    // TASK-9985
  togglePinnedAsset,       // TASK-10007 — star an asset onto the dispatch board strip
  openRentalRecount,       // TASK-10015 — owner: recount rental out-counts from live jobs (preview)
  applyRentalRecount,      // TASK-10015 — owner: apply the previewed recount
  openTrackedStockCount,   // TASK-9722 — Count now on a stock-tracked item
  openTrackedStockHistory, // TASK-9722 — item History (server ledger)
  startRosterStockTracking, stopRosterStockTracking, // TASK-9730 — Track stock on a Roster consumable
  openFastReceive,         // TASK-9723 — receive many counted items in one form
  openSupplyParSheet,      // TASK-9723 — reorder level / fill-to on a counted item
  paintSupplyCostSetting,  // TASK-9724 S2 — the owner's Track supply cost switch in Settings → Company
  supplyCostState: _supplyCostState, // TASK-9724 S2 — { see, edit, owner, on } for hr.js History (read-only)
  getTypeList,
  showAddPerson,
  editPerson,
  jumpToPersonSection, // TASK-9686 — scroll the person modal to a titled section
  savePerson,
  showCrewSkills,  // TASK-9844 — Roster → Skills
  showSkillsAndCerts, // TASK-9911 M2 — the one Skills & certifications screen (HR → Safety opens it too)
  closeCrewSkills, // TASK-9844
  completeSeparation, // TASK-633 — finalize a held FCRA termination
  cancelSeparation,   // TASK-633 — cancel a held FCRA termination
  _clearInspectorDoc,
  // TASK-1357 — the single client-side "is this tenant a DOT-regulated motor
  // carrier?" predicate. Exposed on window so the Settings toggle that writes
  // tenants/{tid}.isDotCarrier, and any other surface needing the same answer
  // (hr.js / compliance.js), read ONE definition instead of forking a second.
  // Fails open to true — see the block comment on the helper.
  tenantIsDotCarrier: _tenantIsDotCarrier,
  deletePerson,
  _openTerminationFromDeactivate, // TASK-931 Slice A — deactivate confirm → No Longer Employed
  recallPerson, // TASK-10211
  _nleRemoveFutureJobs, // TASK-10034 — "still open" list: off future job days (one tap)
  _nleSetRevoke,        // TASK-10034 — "revoke this login when I save" checkbox
  _nleCheckNow,         // TASK-10034 — load the list for someone already No Longer Employed
  muteAllPeople,
  toggleMuteAll,
  toggleDriverDirectory,
  openDriverContactDirectory,
  _renderTeamDirectoryList,
  openDriverContactCard,
  showAddTruck,
  editTruck,
  saveTruck,
  deleteTruck,
  // TASK-259 — Part 376 ownership + lease handlers exposed to truck-edit modal.
  onTruckOwnershipChange,
  rentalToggle,        // TASK-9869 — rented-in section
  rentalBillModeChange, // TASK-9880 — billed to the customer
  rentalPickFile,
  rentalRemovePending,
  rentalOpenFile,
  rentalReadContract,
  recalcLeaseRetainUntil,
  generatePart376Statement,
  showAddTrailer,
  editTrailer,
  saveTrailer,
  deleteTrailer,
  closeModal,
  requestSignature,
  showSignatureRequestModal,
  closeSignatureModal,
  copySignatureLink,
  copyDriverPin,
  generateDriverPin,
  sendDriverPinNotification,
  sendDriverBootstrapCode,
  showDriverSetupCode, // TASK-4873
  openDriverPins, // TASK-9941 F S3
  toggleNoPinFilter, // TASK-9941 F S3
  openSetupAnswers, dismissSetupAnswersBanner, // TASK-9768 slice 2b
  sendDriverAppLink,
  // Staff Login (TASK-319 slice 5)
  showSetupStaffLoginModal,
  closeStaffLoginInviteModal,
  submitStaffLoginInvite,
  // TASK-1489 / TASK-1500 — personless scoped invite ("Invite User")
  showInviteUserModal,
  canInviteUsers: _canInviteUsers, // TASK-9753 — Settings → Add team member shows/hides on this same gate
  // TASK-375 — Invite to CRM Team (thin layer over staff-login invite)
  openCrmTeamInvite,
  _renderCrmTeamPickerList,
  _pickCrmTeamPerson,
  _createPersonForCrmInvite,
  // Pending invite actions (TASK-349)
  resendStaffLoginInvite,
  revokeStaffLoginInvite,
  revokeStaffLoginMember,
  // TASK-366 — Edit Access modal (no re-invite required)
  editStaffLoginAccess,
  closeStaffLoginEditModal,
  submitStaffLoginEdit,
  filterPeople,
  showAllPeople,
  // TASK-1476 — role-conflict detector. READ-ONLY: getRoleConflict/
  // countRoleConflicts are pure comparisons that write nothing. Exported so HR /
  // compliance can reuse the ONE predicate via `window.RosterModule.*` at call
  // time (never a cross-import with a ?v= buster — Pitfall #1). If you find
  // yourself calling these from a save path, stop: that is the derivation COD
  // rejected.
  getRoleConflict,
  countRoleConflicts,
  showRoleConflicts,
  filterTrucks,
  filterTrailers,
  toggleInactive,
  // Axle management
  addTruckAxle,
  removeTruckAxle,
  addTrailerAxle,
  removeTrailerAxle,
  // Permit-axle management (TASK-525) + fast capture grid (TASK-1444)
  addTruckPermitAxle,
  addTrailerPermitAxle,
  _renumberPermitAxles,
  _paxleRemoveRow,
  _paxleDuplicateRow,
  _paxleDuplicateLast,
  _paxleUndoLast,
  applyAxlePreset,
  getAxlePresets,
  showPaxleImport,
  readPaxleImport,
  applyPaxleImport,
  _paxleImportPickAll,
  // Fleet permit-readiness checklist (TASK-1444 item 3)
  showPermitReadiness,
  _permitReadinessEdit,
  // Parsers exported for verification/regression use. They are PURE — none of
  // them touch Firestore, and none of them are a write path.
  parseAxleBlock,
  parsePermitJsonAxles,
  // Rig combinations (TASK-1443) — equipmentTemplates
  openRigEditor,  // TASK-4876 S3 — the one rig-editor entry point (Roster + Permit Pete)
  showAddRig,
  editRig,
  saveRig,
  deleteRig,
  filterRigs,
  addRigTrailer,
  refreshRigAssembly,
  _removeRigUnit,
  _moveRigUnit,
  // Exported for CLIENT consumers (window.RosterModule is an established API -
  // app.js, crm.js, compliance.js, dispatch.js already use it; never cross-import
  // with a ?v= buster, Pitfall #1).
  //
  // 🔴 IT DOES NOT MAKE THIS RULE LIVE IN ONE PLACE, and do not write that it does.
  // A SECOND assembly implementation already ships inside submitPermitRequest
  // (functions/src/index.js) and Cloud Functions cannot reach window.*. The two
  // DISAGREE today: the server handles a single trailer only (no chain, no
  // booster), deliberately synthesizes NO coupling gap (so the truck's last drive
  // axle keeps whatever spacingToNext sits on the vehicle record, while this
  // client version OVERWRITES that same slot with the joint gap), emits a
  // different per-axle shape, and caps at 20/vehicle + 30 assembled vs 250 here.
  // Same truck, same trailer, two different DECLARED AXLE SPACINGS depending on
  // which path builds the filing - on a permit-revocation surface.
  // TASK-1445 must reconcile them or diverge them explicitly, and must re-derive
  // server-side rather than trusting anything stored here.
  assembleRigSequence,
  // VIN management
  addTruckVin,
  removeTruckVin,
  addTrailerVin,
  removeTrailerVin,
  // Plate management
  addTruckPlate,
  removeTruckPlate,
  addTrailerPlate,
  removeTrailerPlate,
  // Vehicle inventory management
  toggleInventorySection,
  addTruckInventoryItem,
  removeTruckInventoryItem,
  // Document management (generalized)
  handleDocUpload,
  regPromptHide, // TASK-9814
  regNoExpiryToggle, // TASK-9814 follow-up
  saveAnywayRegPrompt, // TASK-9814
  removePendingDoc,
  removeExistingDoc,
  // General documents
  showAddGeneralDoc,
  removeGeneralDoc,
  // Flag management
  toggleFlagOptions,
  resetInspectionFlag,
  // Legacy truck docs (backwards compatibility)
  handleTruckDocUpload,
  removePendingTruckDoc,
  removeExistingTruckDoc,
  // Assets/Inventory
  showAddAsset,
  onAssetBillModeChange, onAssetBillCodeInput, onAssetBillCodeChange, // TASK-9725 S2 UI — Billing section on a supply item
  // TASK-9369 — Assets "Group by branch"
  setAssetsGroupBy,
  drillAssetsBranch,
  // TASK-4800 S3 — Assets › On site (stranded equipment, oldest first)
  toggleAssetsOnSite,
  setAssetsOnSiteAge,
  retryAssetsOnSite,
  openAssetsOnSiteMap,
  buildAssetsOnSiteCards,   // PURE — exposed for tests
  addBranchFromAssets,
  editAsset,
  saveAsset,
  deleteAsset,
  filterAssets,
  toggleTrackingMode,
  toggleAssetHoursFields,
  updateAssetHoursVisibility,
  handleAssetPhoto,
  removeAssetPhoto,
  // TASK-340 Slice A — truck + trailer photo handlers
  handleTruckPhoto,
  removeTruckPhoto,
  handleTrailerPhoto,
  removeTrailerPhoto,
  // TASK-340 Slice B — person profile photo handlers
  handlePersonPhoto,
  removePersonPhoto,
  // TASK-9234 — person avatars resolve from a storage path at display time.
  // Shared with hr.js via window.* (Pitfall #1 — never a cross-?v= import).
  personAvatarInnerHtml,
  personHasPhoto,
  personPhotoStoragePath,
  resolvePersonPhotoUrl,
  hydratePersonPhotos,
  // Inventory receipt/adjustment
  showReceiptModal,
  saveReceipt,
  calcReceiptTotal,
  showAdjustmentModal,
  saveAdjustment,
  // Yard Transfer
  showYardTransferModal,
  executeYardTransfer,
  // Order Management
  openOrderManagement,
  closeOrderManagement,
  filterOrders,
  showGenerateOrderLink,
  generateOrderRequestLink,
  copyOrderLink,
  showOrderDetails,
  updateOrderStatus,
  showCustomerList,
  // Delivery ticket functions
  viewDeliveryTicket,
  printDeliveryTicket,
  generateSignatureLink,
  copyDeliverySignatureLink,
  // DVIR Accountability
  showDvirFlags,
  dismissDvirFlag,
  resetMissedDvirCount,
  // JSA Accountability
  resetMissedJsaCount,
  // Custom type management
  showAddTypeInline,
  // Notifications
  toggleNotifyDropdown,
  // Yards
  // TASK-1142 — getYardsList/getYardLabel/getYardTypeLabel exposed for
  // cross-module consumers (pretrip.js return-inspection dropdown was calling
  // window.RosterModule.getYardsList which was never exported → always []).
  getYardsList,
  getYardLabel,
  getYardTypeLabel,
  getLiveYardLabel,
  addYardInline,
  saveNewYard,
  transferToYard,
  refreshYardFilters,
  // TASK-1142 — "Your Facilities" manager (rename / re-address / retype / remove)
  manageFacilities,
  deleteYard,
  // Print
  showPrintListModal,
  printRosterList,
  // State helpers
  getPeopleCount: () => RosterState.people.length,
  getUnitDisplay,
  // Citations
  loadVehicleCitations
};

// Load citations for a vehicle (lazy, on details toggle)
async function loadVehicleCitations() {
  const container = document.getElementById('truck-citations-list');
  if (!container) return;

  const truckId = document.getElementById('truck-id')?.value;
  // Loaded-for-THIS-vehicle, not a bare flag: the modal is reused, and a bare
  // 'true' would show the last vehicle's reports on the next one.
  if (truckId && container.dataset.loaded === truckId) return;
  if (!truckId) {
    container.innerHTML = '<div style="color:var(--text-muted);font-size:0.8rem;">No vehicle selected.</div>';
    return;
  }

  container.innerHTML = '<div style="color:var(--text-muted);font-size:0.8rem;">Loading...</div>';

  try {
    // TASK-4800 follow-up — this read `RosterState.tenantId`, which nothing
    // ever assigns, so collection() threw on an undefined path segment and
    // every vehicle showed "Error loading citations." Second, independent
    // cause: where(vehicleId)+orderBy(createdAt) needs a composite index that
    // does not exist (probed live 2026-10-03: FAILED_PRECONDITION). So: the
    // tenant the rest of Roster uses, an equality-only query, sorted here.
    const tenant = _rosterTenantId();
    if (!tenant) throw new Error('no tenant');
    const snap = await getDocs(
      query(collection(db, 'tenants', tenant, 'citations'), where('vehicleId', '==', truckId))
    );
    // Stale guard: the modal may have moved to another vehicle meanwhile.
    if (document.getElementById('truck-id')?.value !== truckId) return;

    container.dataset.loaded = truckId;

    if (snap.empty) {
      container.innerHTML = '<div style="color:var(--text-secondary);font-size:0.8rem;padding:4px 0;">No exam reports for this vehicle.</div>';
      return;
    }

    const esc = (v) => escapeHtml(v === null || v === undefined ? '' : String(v));
    container.innerHTML = sortCitationsNewestFirst(snap.docs.map(d => d.data())).map(c => {
      const dateStr = c.inspectionDate ? new Date(c.inspectionDate + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : 'N/A';
      const oos = (c.vehicleOOS || c.driverOOS) ? '<span style="background:#FEE2E2;color:#991B1B;padding:1px 6px;border-radius:4px;font-size:0.65rem;font-weight:700;">OOS</span>' : '';
      const statusColor = c.repairStatus === 'completed' ? '#059669' : c.repairStatus === 'in-progress' ? '#D97706' : '#DC2626';
      return `
        <div style="padding:8px 0;border-bottom:1px solid var(--border-light);display:flex;align-items:center;justify-content:space-between;">
          <div>
            <span style="font-weight:600;color:var(--text-primary);">${esc(dateStr)}</span>
            <span style="color:var(--text-secondary);margin-left:6px;">${esc(c.driverName || 'Unknown')}</span>
            ${oos}
            ${c.reportNumber ? `<span style="color:var(--text-secondary);margin-left:4px;font-size:0.75rem;">#${esc(c.reportNumber)}</span>` : ''}
          </div>
          <span style="font-size:0.7rem;font-weight:600;color:${statusColor};text-transform:uppercase;">${esc(c.repairStatus || 'pending')}</span>
        </div>
      `;
    }).join('');
  } catch (err) {
    console.error('[Roster] Error loading vehicle citations:', err);
    // Same stale guard as the success path (COD): a late failure for vehicle A
    // must not overwrite vehicle B's panel.
    if (document.getElementById('truck-id')?.value !== truckId) return;
    container.innerHTML = '<div style="color:var(--text-secondary);font-size:0.8rem;">Couldn\'t load exam reports. Close and reopen this vehicle to retry.</div>';
  }
}

// Called when the (reused) vehicle modal opens: collapse the exam-report panel
// and forget what it loaded, so it never shows the previous vehicle's reports.
function _resetVehicleCitationsPanel() {
  const list = document.getElementById('truck-citations-list');
  if (list) { delete list.dataset.loaded; list.textContent = 'Click to load...'; }
  const sec = document.getElementById('truck-citations-section');
  if (sec) sec.open = false;
}

// Roster's tenant id for one-off reads. `RosterState.tenantId` is never
// assigned — use the app's current tenant, mapped like subscribeToData does.
function _rosterTenantId() {
  const raw = window.AppState?.tenant || null;
  return raw === 'demo-tenant' ? 'demo' : raw;
}

// Newest first by createdAt (Timestamp | Date | ISO/ms), falling back to
// inspectionDate (YYYY-MM-DD); undated last. PURE.
function sortCitationsNewestFirst(list) {
  const ms = (c) => {
    const t = c && c.createdAt;
    let v = t && typeof t.toMillis === 'function' ? t.toMillis()
      : (t && typeof t.seconds === 'number') ? t.seconds * 1000
      : t instanceof Date ? t.getTime()
      : (typeof t === 'string' || typeof t === 'number') ? new Date(t).getTime() : NaN;
    if (!Number.isFinite(v) && c && c.inspectionDate) v = new Date(c.inspectionDate + 'T00:00:00').getTime();
    return Number.isFinite(v) ? v : -Infinity;
  };
  return (list || []).slice().sort((a, b) => { const x = ms(a), y = ms(b); return x === y ? 0 : (x < y ? 1 : -1); });
}

export default { initRoster };

function __ddChunkWrap(p){var h=globalThis.__ddChunk;return typeof h==="function"?h(p):p}
